import { useState, useEffect, useRef, useMemo, useCallback, useId, createContext, useContext, Component } from "react";

// ── Internal modules ───────────────────────────────────────────────
// safeStorage + jgaErrorReport extracted to ./lib (Jun 19, 2026).
// Stamps system extracted to ./stamps (Jun 19, 2026 — Phase B Step 1).
import { jgaErrorReport, safeStorage } from "./lib/safeStorage";
import {
  StampIllustration,
  SUGGESTED_STAMPS,
  STAMP_STORAGE_KEY,
  getStamps,
  setStampState,
  updateStampMeta,
  clearStamp,
} from "./stamps";
import {
  PostcardIllustration,
  POSTCARDS_CATALOG,
  MISSING_POSTCARD_FACTS,
  destNameToPostcardId,
  computePostcardDerivation,
  getFunFactForDest,
  getPostcardIdForDest,
  POSTCARD_STORAGE_KEY,
  getPostcards,
  setPostcardState,
  updatePostcardMeta,
  clearPostcard,
} from "./postcards";

import {
  ThemeContext, useTheme,
  BOOKING_OPEN_DATE, getMinTravelDate, addDaysISO, daysBetweenISO,
  applyDepartChange, applyReturnChange, isPreLaunch,
  DESTINATIONS, DEST_COORDS, LIMITED_SERVICE_DOMESTIC, MAJOR_AIRPORTS,
  distanceMiles, getDomesticBookingWindowMultiplier, estimateFlightPrice,
  SafetyBadge, TrustBand, Label,
  formatTravelers, cardStyle, lightCard,
} from "./shared";




// ── JGAErrorBoundary — React render-error catcher ──────────────────
// Without this, any uncaught exception during render shows the user a
// blank white page. With this, they see a graceful "something broke"
// screen with a refresh button. Errors are reported through the
// global reporter so we get telemetry on render crashes.
//
// Class component (not hooks) because componentDidCatch is the only
// way React lets us catch render errors.
class JGAErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMsg: error?.message || "Unknown error" };
  }
  componentDidCatch(error, info) {
    jgaErrorReport(error, "react.render", { componentStack: info?.componentStack || null });
  }
  handleReset = () => {
    this.setState({ hasError: false, errorMsg: null });
  };
  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    // Graceful fallback — readable, branded, with two actions:
    // refresh (most likely fix) and try again (re-render attempt
    // without full reload, useful for transient errors).
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 24px",
        background: "#FAFAF5",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, color: "#C8741D", textTransform: "uppercase", fontWeight: 800, marginBottom: 14 }}>
            Something went wrong
          </div>
          <h1 style={{ margin: "0 0 12px", fontSize: 28, fontWeight: 900, color: "#1B3A5C", lineHeight: 1.2, letterSpacing: "-0.02em" }}>
            We hit a small turbulence
          </h1>
          <p style={{ fontSize: 14, color: "rgba(27,58,92,0.7)", lineHeight: 1.55, marginBottom: 24 }}>
            Something unexpected happened. Refreshing the page should sort it out — your saves and trips are safe.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={this.handleReload}
              style={{
                padding: "11px 22px", borderRadius: 12, border: "none",
                background: "#1B3A5C", color: "#FAFAF5",
                fontSize: 13, fontWeight: 800, cursor: "pointer",
                fontFamily: "inherit", letterSpacing: 0.3,
              }}>
              Refresh the page
            </button>
            <button onClick={this.handleReset}
              style={{
                padding: "11px 22px", borderRadius: 12,
                border: "1px solid rgba(27,58,92,0.25)",
                background: "transparent", color: "#1B3A5C",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                fontFamily: "inherit",
              }}>
              Try again
            </button>
          </div>
          {/* Dev-only error detail. Hidden in production builds where
             NODE_ENV is "production"; visible during dev for debugging. */}
          {typeof process !== "undefined" && process?.env?.NODE_ENV !== "production" && this.state.errorMsg && (
            <details style={{ marginTop: 28, textAlign: "left", fontSize: 11, color: "rgba(27,58,92,0.5)" }}>
              <summary style={{ cursor: "pointer", fontWeight: 700 }}>Error details (dev only)</summary>
              <pre style={{ marginTop: 8, padding: 12, background: "rgba(27,58,92,0.05)", borderRadius: 8, overflow: "auto", fontSize: 10 }}>
                {this.state.errorMsg}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}


// ════════════════════════════════════════════════════════════════════
// CONTEXTUAL HINTS — first-time guidance for new users
// ════════════════════════════════════════════════════════════════════
// Single localStorage key tracks which hints a user has dismissed.
// Hints fire on first visit to each surface, with two important rules:
//   1. If the user already has data on that surface, skip the hint
//      (we don't patronize returning users with familiar surfaces)
//   2. Once dismissed, never show again — including if they navigate
//      away mid-hint
//
// Two visual treatments:
//   - HintCard: floating card for navigation surfaces (Trips, Boards,
//     Journal, Explore). Free-floating at top of scroll area, 500ms
//     delayed entry, "Onward" dismiss button.
//   - InlineHint: subtle text-only treatment for capability hints
//     (e.g., "Tip: this saved to Trips → Itineraries")
//
// Reset for testing: append ?hints=reset to any URL to clear all
// dismissals. Useful when shipping hint copy updates.
// ════════════════════════════════════════════════════════════════════

const HINTS_STORAGE_KEY = "jga-hints-dismissed-v1";

// Read current dismissals. Returns [] on any failure (corrupted
// storage, parse error, no quota). Always-safe accessor.
function getDismissedHints() {
  const data = safeStorage.getJSON(HINTS_STORAGE_KEY, []);
  return Array.isArray(data) ? data : [];
}

// Persist a dismissal. No-op if the hint was already dismissed.
function dismissHint(hintId) {
  const dismissed = getDismissedHints();
  if (dismissed.includes(hintId)) return;
  dismissed.push(hintId);
  safeStorage.setJSON(HINTS_STORAGE_KEY, dismissed);
}

// Should this hint render? Two gates:
//   - hasExistingData: caller passes true if the user already has
//     meaningful state on this surface (skip the hint — they don't
//     need an intro)
//   - dismissed: has the user clicked "Onward" on this hint before?
function shouldShowHint(hintId, hasExistingData = false) {
  if (hasExistingData) return false;
  return !getDismissedHints().includes(hintId);
}

// Reset everything. Used by the URL parameter and the Profile-
// settings "Show me hints again" affordance.
function resetAllHints() {
  safeStorage.setJSON(HINTS_STORAGE_KEY, []);
}

// URL parameter handling — runs once at module load.
// Append ?hints=reset to any URL → clears all dismissals + logs.
// Useful for re-running hints after a copy update or for testing.
if (typeof window !== "undefined") {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("hints") === "reset") {
      resetAllHints();
      console.log("[JGA hints] All hints reset via URL parameter");
    }
  } catch (e) {
    jgaErrorReport(e, "hints.urlParam");
  }
}


// ── HintCard ───────────────────────────────────────────────────────
// Card-style hint for navigation surfaces. Free-floating at the top
// of the scroll area, 500ms delayed entry, accent color from the
// surface (passed via prop), "Onward" dismiss button. Persists
// dismissal via dismissHint().
function HintCard({ id, title, body, accentColor = "#4A8CC4", shouldShow }) {
  const [visible, setVisible] = useState(false);
  const [render, setRender] = useState(shouldShow);

  useEffect(() => {
    if (!shouldShow) return;
    // 500ms delay so the page settles visually before the hint slides in
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, [shouldShow]);

  const handleDismiss = () => {
    setVisible(false);
    // Mark dismissed immediately so navigating away doesn't re-show
    dismissHint(id);
    // Remove from DOM after fade-out completes
    setTimeout(() => setRender(false), 220);
  };

  if (!render) return null;

  return (
    <div
      style={{
        background: "#FAFAF5",
        border: `1px solid ${accentColor}33`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 12,
        padding: "14px 16px",
        margin: "0 0 16px 0",
        boxShadow: "0 2px 8px rgba(27,58,92,0.06)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-8px)",
        transition: "opacity 0.2s ease, transform 0.2s ease",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{
        fontSize: 13,
        fontWeight: 800,
        color: "#1B3A5C",
        letterSpacing: 0.1,
      }}>
        {title}
      </div>
      <div style={{
        fontSize: 12.5,
        color: "rgba(27,58,92,0.7)",
        lineHeight: 1.5,
      }}>
        {body}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
        <button
          onClick={handleDismiss}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: `1px solid ${accentColor}44`,
            background: "transparent",
            color: accentColor,
            fontSize: 11.5,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "inherit",
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          Onward
        </button>
      </div>
    </div>
  );
}


// ── InlineHint ─────────────────────────────────────────────────────
// Subtle text-only treatment for capability callouts. No card, no
// dismiss button — just a one-line tip that shows once, then
// auto-dismisses on view. Used for "Tip: ..." style guidance after
// the user does something for the first time.
function InlineHint({ id, body, shouldShow }) {
  const [render, setRender] = useState(shouldShow);

  useEffect(() => {
    if (!shouldShow) return;
    // Auto-dismiss the moment it renders — it's a passive tip, not
    // an interactive element. User reads it; it's marked seen.
    dismissHint(id);
  }, [shouldShow, id]);

  if (!render) return null;

  return (
    <div
      style={{
        fontSize: 11.5,
        color: "rgba(27,58,92,0.6)",
        fontStyle: "italic",
        padding: "6px 0",
        lineHeight: 1.5,
      }}
    >
      💡 {body}
    </div>
  );
}


// ── Theme context ─────────────────────────────────────────────────


// ── Dashboard context & rotation ─────────────────────────────────
// Determines which dashboard section is "lead" based on day/time,
// plus a contextual badge + copy. Spotify-style — the home feed
// reorganizes itself based on when you're opening it.
function getDashboardContext() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const hour = now.getHours();

  // Sunday evening (6pm onward) — pre-week planning energy
  if (day === 0 && hour >= 18) {
    return {
      id: "sundayEvening",
      lead: "mood",
      badge: "🌙 Sunday plan-aheads",
      copy: "Plan something to look forward to.",
      tintColor: "rgba(150,140,200,0.05)",
    };
  }

  // Friday afternoon (2pm) → Sunday afternoon (before 6pm) — weekend
  // Weekend mood-quiz badge removed: identity-driven dashboard makes the
  // 'What kind of weekend feels right?' prompt redundant. If reintroducing,
  // restore the {id:"weekend", lead:"mood", badge:"Weekend mode", ...} object.

  // Monday–Thursday morning (5am–noon) — escape-fantasy hours
  if (day >= 1 && day <= 4 && hour >= 5 && hour < 12) {
    return {
      id: "weekdayMorning",
      lead: "mood",
      badge: "Need an escape?",
      copy: "Pick a vibe. We'll find the trip.",
      tintColor: "rgba(74,140,196,0.05)",
    };
  }

  // Default — mood-led
  return {
    id: "default",
    lead: "mood",
    badge: null,
    copy: null,
    tintColor: null,
  };
}

// Returns "winter" | "spring" | "summer" | "fall" (Northern Hemisphere)
function getSeason() {
  const m = new Date().getMonth(); // 0=Jan ... 11=Dec
  if (m === 11 || m === 0 || m === 1) return "winter";
  if (m >= 2 && m <= 4) return "spring";
  if (m >= 5 && m <= 7) return "summer";
  return "fall";
}

// Subtle dashboard background tint per season
const SEASONAL_TINTS = {
  winter: { dark: "radial-gradient(ellipse at top, rgba(125,180,220,0.06) 0%, transparent 55%)", light: "radial-gradient(ellipse at top, rgba(125,180,220,0.10) 0%, transparent 55%)" },
  spring: { dark: "radial-gradient(ellipse at top, rgba(180,220,160,0.05) 0%, transparent 55%)", light: "radial-gradient(ellipse at top, rgba(180,220,160,0.10) 0%, transparent 55%)" },
  summer: { dark: "radial-gradient(ellipse at top, rgba(245,200,140,0.06) 0%, transparent 55%)", light: "radial-gradient(ellipse at top, rgba(245,200,140,0.12) 0%, transparent 55%)" },
  fall:   { dark: "radial-gradient(ellipse at top, rgba(220,150,100,0.06) 0%, transparent 55%)", light: "radial-gradient(ellipse at top, rgba(220,150,100,0.12) 0%, transparent 55%)" },
};

// Maps the dashboard context to flex-order values for each section.
// Even spacing (2,4,6,8) leaves odd slots free for the contextual badge
// to sit just above the lead section.
function getSectionOrder(contextId) {
  // Dashboard section order — question-driven IA (Jun 2026 restructure).
  // Five section headers ("Who am I" / "Where should I go" / "What have I
  // collected" / "What else might surprise me" / "Worth Knowing") group
  // related surfaces. 4-step spacing between sections leaves room for the
  // dynamic lead-badge (leadOrder - 1) without colliding with header orders.
  //
  // The contextId parameter is preserved so context-aware ordering can
  // return later without changing this function's call sites.
  return {
    // ── Q1: Who am I ──
    whoAmIHeader:    -25,
    identity:        -20,   // Travel Identity card
    style:           -16,   // Setup bar (Style/Budget/Airport/Region)
    edit:            -16,   // Edit panels (same source-code position)
    dashboard:       -12,   // "Your Travel Dashboard" greeting
    stats:           -8,    // Trip count strip
    aura:            -4,    // Travel Aura This Month
    // ── Q2: Where should I go ──
    whereShouldIGoHeader: 0,
    mood:            4,     // Trips For You (Mood Match)
    // ── Q3: What have I collected ──
    whatHaveICollectedHeader: 8,
    postcards:       12,    // Postcard Wall
    stamps:          16,    // Stamp Wall
    // ── Q4: What else might surprise me ──
    whatElseHeader:  20,
    swipe:           24,    // Swipe Deck
    detour:          28,    // Detour / Escape Mode
    // ── Q5: Worth Knowing ──
    worthKnowingHeader: 32,
    factRotator:     36,    // Travel Facts rotator (now first)
    worthKnowing:    38,    // Around the world.. (renamed)
    blog:            42,    // Blog (new dropdown)
    forTravelAgents: 46,    // For Travel Agents (new dropdown, CTA → waitlist)
  };
}

// ─────────────────────────────────────────────────────────────────
// STAMPS — Drop 1
// ─────────────────────────────────────────────────────────────────
// Stamps are a polymorphic marker the user collects, with two states:
// SOMEDAY (aspirational) and VISITED (retrospective). They live on the
// dashboard as a horizontal "Stamp Wall" strip above Mood Match.
//
// Drop 1 scope:
//   - 6 hardcoded suggested stamps (Cherry Blossoms, Northern Lights,
//     Pyramids, Whale Sharks, Maldives, Salar de Uyuni)
//   - Two save states per card: tap stamp-icon = SOMEDAY, tap check = VISITED
//   - Suggested state (faded + dashed border + "Suggested" pill) until saved
//   - Storage: localStorage only ("jga-stamps" key) — mirrors Boards pattern.
//     Backend table coming when Phase 4 (Boards 3-column) ships.
//
// Deferred to later drops:
//   - "+Add" tile (Phase 3 — custom stamps)
//   - Postmarks (Brand, WonderSeal, VisitedCancellation) — only render on
//     Boards detail view per spec § 6A. Not at dashboard size.
//   - Long-press, date pickers, stamp icons on destination cards
//   - Other 24 SVGs from the asset library
//   - Themed-fallback components for custom stamps
//
// See: stamps-handoff.md for the full spec.

// Fisher-Yates shuffle. Returns a new array — does not mutate input.
// Used to randomize the order of suggested stamps per session.
function shuffleArray(arr) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}


// ── MILESTONE OPTIONS ─────────────────────────────────────────
// The 9 occasion options surfaced in the milestone question
// (TMA destination detail card + SM setup form). Spec source: the
// Quiz & Booking Flow Additions handoff (June 2026). Captured per-trip,
// not per-user — same user plans a honeymoon, a random weekend, and a
// parents-anniversary trip at different times.
//
// "just-because" is a first-class answer ("no special context"), distinct
// from skip ("user didn't engage with the question"). Both are valid
// signals; skip is preserved so downstream logic can distinguish
// "explicitly no occasion" from "we never asked / they didn't say".
const MILESTONE_OPTIONS = [
  { value: "just-because",      emoji: "🎉", label: "Just because" },
  { value: "honeymoon",         emoji: "💍", label: "Honeymoon" },
  { value: "anniversary",       emoji: "💑", label: "Anniversary" },
  { value: "milestone-birthday",emoji: "🎂", label: "Milestone birthday" },
  { value: "graduation",        emoji: "🎓", label: "Graduation" },
  { value: "family-reunion",    emoji: "👨‍👩‍👧", label: "Family reunion" },
  { value: "reset-moment",      emoji: "💔", label: "Reset moment" },
  { value: "gift-for-someone",  emoji: "🎁", label: "Gift / surprise" },
  { value: "other",             emoji: "✨", label: "Something else" },
];

// Storage key for TMA milestones (keyed by destination ID, persists across
// session so user can navigate away from a destination and back without
// losing context). SM milestone is session-only — Surprise Me preferences
// reset on every new spin.
const TMA_MILESTONES_KEY = "jga-tma-milestones-v1";

function getTmaMilestones() {
  try {
    const raw = localStorage.getItem(TMA_MILESTONES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch { return {}; }
}

function setTmaMilestone(destId, milestone) {
  if (!destId) return;
  const all = getTmaMilestones();
  all[destId] = { ...milestone, updatedAt: Date.now() };
  try { localStorage.setItem(TMA_MILESTONES_KEY, JSON.stringify(all)); } catch {}
}

// Convenience: return a human-readable label for a milestone value.
// Used in waitlist confirmation copy, journal display, and (later)
// admin dashboard rows. Returns null for skipped/unset milestones.
function milestoneLabel(value) {
  if (!value || value === "skipped") return null;
  const opt = MILESTONE_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : null;
}

// ── TRAVEL AURA SYSTEM ───────────────────────────────────────
// Aura is JGA's monthly "soft reading" of the user — distinct from
// Travel Identity (the moat). Identity is who they ARE; Aura is what
// they're currently leaning toward, based on the past 30 days of
// searches, saves, journal entries, trips added, and identity vibes.
//
// MIGRATED June 2026: moved off the home page (where it competed
// with Identity for the highest-traffic surface) into MoodBoard,
// where it lives alongside other personalization affordances.
// Repainted in the window-seat palette per the rebrand handoff —
// no warm rust or terracotta on the card chrome.
//
// FORMING ENGINE: each month a user's Aura starts at 0% and builds
// toward 100% as they engage. Quiz completion gives a baseline 25%;
// the rest comes from saves, trips, entries, and searches. Weights
// are capped per dimension so no single behavior carries the whole
// reading. A mid-month signup sees a "preview" Aura that forms over
// their first partial month; on the 1st they get their first full
// reading. An inactive user's Aura "falls" — their forming percentage
// stays low across months, which can trigger backend re-engagement.

// Single-vibe auras — first match in user's top vibes wins.
const SIMPLE_AURA_MAP = {
  beach:     { label: "Beach Seeker",      emoji: "🌊", desc: "You're drawn to water, warmth, and the sound of waves." },
  romance:   { label: "Romantic Soul",     emoji: "💫", desc: "Connection and beauty are at the heart of your travel." },
  wellness:  { label: "Restoration Mode",  emoji: "🌸", desc: "You're craving stillness, space, and deep rest." },
  luxury:    { label: "Elevated Taste",    emoji: "✨", desc: "You want the finer things — and you've earned them." },
  adventure: { label: "Edge Energy",       emoji: "⚡", desc: "Your aura says: push limits, find the wild." },
  culture:   { label: "Deep Diver",        emoji: "🏛️", desc: "You want to understand places, not just visit them." },
  food:      { label: "Flavour Chaser",    emoji: "🍜", desc: "Every trip is built around what you'll eat next." },
  nature:    { label: "Earth Energy",      emoji: "🌿", desc: "You need green spaces, open skies, and silence." },
};

// Compound auras — require TWO matching vibes. First match wins,
// so the order matters. Most distinctive first.
const COMPOUND_AURAS = [
  { match: ["nightlife", "food"],     label: "Neon",         emoji: "🌃", desc: "After dark, after expectations. Cities that buzz." },
  { match: ["nightlife", "romance"],  label: "Night Owl",    emoji: "🌙", desc: "Cities that come alive after midnight. Late, lit, lived-in." },
  { match: ["nightlife", "culture"],  label: "Chaos",        emoji: "⚡", desc: "Dense, loud, unfiltered. Travel that doesn't apologize." },
  { match: ["history", "romance"],    label: "Lost Era",     emoji: "🎞️", desc: "Time-warped places. Worn paint, slow tobacco, hand-painted signs." },
  { match: ["wellness", "nature"],    label: "Soft Ritual",  emoji: "🌾", desc: "Mornings made for tea. Evenings for stars. Pace for breath." },
  { match: ["food", "culture"],       label: "Slow Feast",   emoji: "🥖", desc: "Markets, family kitchens, the stories behind the table." },
  { match: ["adventure", "nature"],   label: "Open Country", emoji: "🏔️", desc: "Wide skies, big walks, weather you respect." },
  { match: ["beach", "romance"],      label: "Salt & Light", emoji: "🌅", desc: "Long horizons, warm hands, the day stretched golden." },
  { match: ["luxury", "wellness"],    label: "Quiet Luxury", emoji: "🛁", desc: "A robe, a view, a closed door. Privacy as the indulgence." },
];

// Compute aura from a flat array of vibe tags. Compound match first,
// fall back to first single match. Returns { label, emoji, desc, key }
// or null if nothing matches.
function computeAuraForVibes(vibes) {
  const set = new Set((vibes || []).filter(Boolean));
  for (const compound of COMPOUND_AURAS) {
    if (compound.match.every(v => set.has(v))) {
      return { key: compound.label, label: compound.label, emoji: compound.emoji, desc: compound.desc };
    }
  }
  for (const v of vibes || []) {
    if (SIMPLE_AURA_MAP[v]) {
      return { key: v, ...SIMPLE_AURA_MAP[v] };
    }
  }
  return null;
}

// ── Aura forming engine ──
// Weights match Cas-approved spec (June 2026):
//   identity quiz   25% baseline
//   stamps (someday) 3% each, cap 18%
//   postcards (dreaming) 3% each, cap 18%
//   trips added      5% each, cap 20%
//   journal entries  5% each, cap 15%
//   searches run     1% each, cap 4%
// Total max = 100%. A user who only completes the quiz stalls at 25%
// until they engage — that becomes the "falling aura" trigger.
function computeAuraPercent({ hasIdentity = false, somedayStampCount = 0, dreamingPostcardCount = 0, tripCount = 0, entryCount = 0, searchCount = 0 } = {}) {
  let pct = 0;
  if (hasIdentity) pct += 25;
  pct += Math.min(somedayStampCount * 3, 18);
  pct += Math.min(dreamingPostcardCount * 3, 18);
  pct += Math.min(tripCount * 5, 20);
  pct += Math.min(entryCount * 5, 15);
  pct += Math.min(searchCount * 1, 4);
  return Math.min(100, Math.max(0, Math.round(pct)));
}

// Contextual copy by tier. Hidden at 100% — once formed, the aura
// stands on its own and doesn't need a prompt.
function auraFormingCopy(pct) {
  if (pct >= 100) return null;
  if (pct >= 71)  return "Almost there — one more month finishes the picture.";
  if (pct >= 41)  return "Halfway formed. We're getting a read.";
  if (pct >= 25)  return "Your aura is taking shape. Keep exploring.";
  return "Take the identity quiz to start your reading.";
}

// ── Aura monthly archive ──
const AURA_ARCHIVE_KEY = "jga-aura-archive-v1";
const AURA_SIGNUP_MONTH_KEY = "jga-aura-signup-month-v1";
const AURA_SEARCH_COUNT_KEY = "jga-aura-search-count-v1"; // { "YYYY-MM": n }

// Returns current year-month as "YYYY-MM".
function currentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function previousYearMonth(yyyyMm) {
  const [y, m] = yyyyMm.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Human label for archive headers. "June 2026" / "July 2026" / etc.
function yearMonthLabel(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return "";
  const [y, m] = yyyyMm.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function getAuraArchive() {
  try {
    const raw = localStorage.getItem(AURA_ARCHIVE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch { return {}; }
}

function setAuraArchive(archive) {
  try { localStorage.setItem(AURA_ARCHIVE_KEY, JSON.stringify(archive)); } catch {}
}

// Lazy-record the signup month the first time aura is computed.
// Used to distinguish "preview" mid-month signups from full months.
function getAuraSignupMonth() {
  try {
    const stored = localStorage.getItem(AURA_SIGNUP_MONTH_KEY);
    if (stored && /^\d{4}-\d{2}$/.test(stored)) return stored;
    const cur = currentYearMonth();
    localStorage.setItem(AURA_SIGNUP_MONTH_KEY, cur);
    return cur;
  } catch { return currentYearMonth(); }
}

// Search counter — incremented on every TMA spin + SM run.
// Stored as a per-month map so old months don't inflate current readings.
function getMonthlySearchCount(yyyyMm) {
  try {
    const raw = localStorage.getItem(AURA_SEARCH_COUNT_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed[yyyyMm] || 0) : 0;
  } catch { return 0; }
}

function recordSearch() {
  try {
    const raw = localStorage.getItem(AURA_SEARCH_COUNT_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const cur = currentYearMonth();
    parsed[cur] = (parsed[cur] || 0) + 1;
    localStorage.setItem(AURA_SEARCH_COUNT_KEY, JSON.stringify(parsed));
  } catch {}
}

// On every AccountModal mount, check whether any previous months
// need to be archived. Archives lock the aura value + percentage at
// that month's end-state. Idempotent — re-archiving the same month
// is a no-op.
function maybeArchivePreviousMonths(currentStats) {
  const archive = getAuraArchive();
  const cur = currentYearMonth();
  const signup = getAuraSignupMonth();
  // Only archive months that are strictly BEFORE the current one
  // and AT OR AFTER the signup month.
  let cursor = previousYearMonth(cur);
  let safety = 24; // never walk back more than 2 years
  while (cursor >= signup && safety-- > 0) {
    if (!archive[cursor]) {
      // Snapshot using the user's *current* stats as a proxy. We don't
      // have time-series engagement data; the archive captures what the
      // user looks like at the moment we cross the month boundary, which
      // is the honest read available without a backend.
      const aura = computeAuraForVibes(currentStats.vibes || []);
      const pct = computeAuraPercent(currentStats);
      archive[cursor] = {
        auraKey: aura ? aura.key : null,
        auraLabel: aura ? aura.label : null,
        auraEmoji: aura ? aura.emoji : null,
        auraDesc: aura ? aura.desc : null,
        percent: pct,
        vibes: (currentStats.vibes || []).slice(0, 3),
        archivedAt: Date.now(),
      };
    }
    cursor = previousYearMonth(cursor);
  }
  setAuraArchive(archive);
  return archive;
}

// ── Suggested stamp catalog for Drop 1 ──
// Each entry: id, country, title, illustration component. The illustration
// component fills a 280×110 viewBox and uses palette tokens from the
// editorial scene palette (see § 9 in handoff).
//
// To add a stamp: add an entry here AND a corresponding StampIllustration
// component below. The illustration renders inside the .stamp-card-illo slot.

// ── STAMP CATALOG ─────────────────────────────────────────────
// 108 entries powering the Stamps catalog: 12 original SVG-illustrated
// experiences (preserved) + 96 destinations seeded for the onboarding
// seeding flow. New entries render with a generic placeholder stamp
// until per-destination SVG illustrations catch up.
//
// IDs aligned with POSTCARDS_CATALOG where overlapping destinations exist,
// so cross-catalog lookups ("do I have a postcard for the place I stamped?")
// resolve naturally.
//
// Shape: { id, title, country, name, emoji, region, lat, lng,
//          hasIllustration, isChipSuggestion }

// ── Stamp illustration components ──
// Each returns an SVG <svg class="scene" viewBox="0 0 280 110">. The
// viewBox is locked across all illustrations so card chrome stays
// consistent. Foreground band on every scene is the signature device.
//
// Sourced from the Stamps SVG asset library (companion file). Drop 1
// includes 6 of 30 shipped illustrations. The other 24 will arrive
// as Drop 2 along with the dedicated Stamps detail page.


// ── WonderSeal ───────────────────────────────────────────────────
// Decorative circular postmark that overlays the illustration when a
// stamp is part of the Seven Wonders canon (set by `canon: "wonder"`
// in the catalog entry). Burgundy ink on the parent's surface.
//
// IMPORTANT: useId() generates a unique path id per instance — without
// this, multiple WonderSeals on the same page would share the same
// `id` attribute and textPath would target whichever DOM element won
// the lookup race, breaking the curved text on all but one seal.
function WonderSeal({ size = 28 }) {
  const reactId = useId();
  const pathId = `wonder-seal-path-${reactId.replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" aria-hidden="true" style={{ pointerEvents: "none" }}>
      <circle cx="30" cy="30" r="26" fill="none" stroke="#8B3A2F" strokeWidth="1" opacity="0.85"/>
      <circle cx="30" cy="30" r="21" fill="none" stroke="#8B3A2F" strokeWidth="0.6" opacity="0.7"/>
      <path id={pathId} d="M 30 30 m -19 0 a 19 19 0 1 1 38 0 a 19 19 0 1 1 -38 0" fill="none"/>
      <text fontSize="6.2" fill="#8B3A2F" opacity="0.95" letterSpacing="1.5" fontFamily="DM Sans, sans-serif" fontWeight="500">
        <textPath href={`#${pathId}`}>★ WONDER · 1 of 7 · ★</textPath>
      </text>
      <polygon points="30,18 32,24 38,24 33,28 35,34 30,30 25,34 27,28 22,24 28,24" fill="#8B3A2F" opacity="0.9"/>
      <text x="30" y="44" textAnchor="middle" fontSize="5" fill="#8B3A2F" fontFamily="DM Sans, sans-serif" letterSpacing="0.8" fontWeight="500" opacity="0.9">VII</text>
    </svg>
  );
}

// Small postage-stamp icon for the SOMEDAY save button.
// Filled = saved, outline = unsaved. Sized to ~14×14px on cards.
function StampSaveIcon({ filled, color = "#1B3A5C" }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="0.5"
        fill={filled ? color : "none"}
        stroke={color}
        strokeWidth="1.5"
        strokeDasharray="0.7 1.4"/>
      {filled && (
        <circle cx="12" cy="12" r="3" fill="none" stroke="#FAFAF5" strokeWidth="1.2"/>
      )}
    </svg>
  );
}

// Checkmark in a circle for the VISITED save button.
function StampCheckIcon({ filled, color = "#1B3A5C" }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9"
        fill={filled ? color : "none"}
        stroke={color}
        strokeWidth="1.5"/>
      <path d="M 8 12 L 11 15 L 16 9"
        stroke={filled ? "#FAFAF5" : color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"/>
    </svg>
  );
}


// ── TravelFactsDropdown ─────────────────────────────────────────
// Expandable section for the Explore page that surfaces the funFact
// fields from POSTCARDS_CATALOG. Restored Jun 19, 2026 — facts had
// existed per-postcard but weren't reachable from anywhere users
// browse before. Pattern: collapsed accordion → expand reveals a
// native <select> of destinations (grouped by region) + the fact
// for whichever destination is selected. Starts with a random pick
// so first-open feels lively, not blank.
function TravelFactsDropdown({ catalog }) {
  const { textPrimary, textMuted, textDim, darkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const withFacts = useMemo(() => catalog.filter(c => c.funFact), [catalog]);
  const grouped = useMemo(() => {
    const g = {};
    for (const c of withFacts) {
      const r = c.region || "Other";
      if (!g[r]) g[r] = [];
      g[r].push(c);
    }
    // Sort regions alphabetically, destinations within alphabetical
    return Object.keys(g).sort().map(r => ({
      region: r,
      items: g[r].sort((a, b) => (a.destName || a.title || "").localeCompare(b.destName || b.title || "")),
    }));
  }, [withFacts]);
  // Flat ordered list used for prev/next stepping
  const flatList = useMemo(() => grouped.flatMap(g => g.items), [grouped]);
  const [selectedId, setSelectedId] = useState(() => {
    if (withFacts.length === 0) return "";
    return withFacts[Math.floor(Math.random() * withFacts.length)].id;
  });
  const selected = withFacts.find(c => c.id === selectedId);
  const selectedIndex = flatList.findIndex(c => c.id === selectedId);
  // autoRotate stays on until any user interaction (dropdown change OR
  // arrow click). Once paused, doesn't resume until the user closes and
  // reopens the accordion — predictable behavior beats clever resume.
  const [autoRotate, setAutoRotate] = useState(true);

  // Auto-rotate every 9 seconds while accordion is open AND user hasn't
  // overridden. Random pick (avoiding immediate repeat) so the experience
  // feels lively. 9s is long enough to read the typical 1–2 sentence fact.
  useEffect(() => {
    if (!expanded || !autoRotate || withFacts.length < 2) return;
    const tick = setInterval(() => {
      setSelectedId(prev => {
        if (withFacts.length < 2) return prev;
        let next;
        do {
          next = withFacts[Math.floor(Math.random() * withFacts.length)].id;
        } while (next === prev);
        return next;
      });
    }, 9000);
    return () => clearInterval(tick);
  }, [expanded, autoRotate, withFacts]);

  // Reset autoRotate fresh on each open — user might want a different
  // experience the second time they engage with this feature.
  useEffect(() => {
    if (expanded) setAutoRotate(true);
  }, [expanded]);

  const goPrev = () => {
    if (flatList.length < 2) return;
    setAutoRotate(false);
    const prev = (selectedIndex - 1 + flatList.length) % flatList.length;
    setSelectedId(flatList[prev].id);
  };
  const goNext = () => {
    if (flatList.length < 2) return;
    setAutoRotate(false);
    const next = (selectedIndex + 1) % flatList.length;
    setSelectedId(flatList[next].id);
  };

  if (withFacts.length === 0) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <button onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 16px", borderRadius: 14, border: "none",
          background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
          color: textPrimary, fontSize: 14, fontWeight: 700, cursor: "pointer",
          fontFamily: "inherit", letterSpacing: 0.2, textAlign: "left",
        }}
        aria-expanded={expanded}>
        <span>Did you know? <span style={{ color: textDim, fontWeight: 500, fontSize: 12 }}>· travel facts</span></span>
        <span aria-hidden="true" style={{ color: textDim, fontSize: 14, transition: "transform 0.15s", transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}>›</span>
      </button>
      {expanded && (
        <div style={{
          marginTop: 8,
          padding: "16px 16px 18px",
          borderRadius: 14,
          border: `1px solid ${darkMode ? "rgba(27,58,92,0.084)" : "rgba(0,0,0,0.06)"}`,
          background: darkMode ? "rgba(27,58,92,0.030)" : "rgba(250,250,245,0.5)",
        }}>
          <label style={{ display: "block", fontSize: 10, letterSpacing: 1.6, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
            Pick a destination
          </label>
          <select value={selectedId} onChange={e => { setAutoRotate(false); setSelectedId(e.target.value); }}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.12)"}`,
              background: darkMode ? "rgba(27,58,92,0.060)" : "#FAFAF5",
              color: textPrimary, fontSize: 14, fontFamily: "inherit",
              cursor: "pointer", marginBottom: 14, appearance: "menulist",
            }}>
            {grouped.map(g => (
              <optgroup key={g.region} label={g.region}>
                {g.items.map(c => (
                  <option key={c.id} value={c.id}>{c.destName || c.title}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {selected && (
            <div style={{
              padding: "14px 16px",
              borderRadius: 12,
              background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(232,154,60,0.06)",
              borderLeft: `3px solid #E89A3C`,
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C8741D", textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>
                {selected.destName || selected.title}
              </div>
              <div style={{ fontSize: 14, color: textPrimary, lineHeight: 1.55, fontStyle: "italic" }}>
                "{selected.funFact}"
              </div>
            </div>
          )}
          {/* Prev/next arrows — let users step manually. Clicking either
             pauses auto-rotation. Position counter doubles as wayfinding. */}
          {flatList.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <button onClick={goPrev}
                aria-label="Previous fact"
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.1)"}`,
                  background: "transparent", color: textPrimary,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                <span aria-hidden="true">‹</span> Prev
              </button>
              <div style={{ fontSize: 10, color: textDim, letterSpacing: 1, fontWeight: 600 }}>
                {selectedIndex + 1} of {flatList.length}
                {autoRotate && (
                  <span style={{ marginLeft: 6, color: "#E89A3C", fontStyle: "italic", letterSpacing: 0.5 }}>· auto</span>
                )}
              </div>
              <button onClick={goNext}
                aria-label="Next fact"
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.1)"}`,
                  background: "transparent", color: textPrimary,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                Next <span aria-hidden="true">›</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BoardHeroGrid — 2×2 illustration grid for each Board card ──
// Phase 1 Boards redesign (Jun 19, 2026). Shows the 4 most recently saved
// destinations as a postcard-illustration grid above the card footer.
// Empty slots get a quiet sky-light placeholder so sparse boards look
// "waiting" rather than "broken." Reuses PostcardIllustration — no new
// art system.
function getBoardRecentDestNames(boardId, items) {
  // Read save events from the recent-saves sidecar (capped at 50 entries).
  // For older saves outside the cap, fall back to board.items array order
  // (items are appended on add, so .reverse() gives newest-first).
  let log = [];
  try { log = JSON.parse(localStorage.getItem("jga-recent-saves-v1") || "[]"); } catch (e) {}
  const boardSaves = log
    .filter(e => e.boardId === boardId)
    .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
    .map(e => e.destName);
  const seen = new Set(boardSaves);
  const result = [...boardSaves];
  // Fallback for entries the sidecar didn't capture
  for (const name of [...(items || [])].reverse()) {
    if (!seen.has(name)) { seen.add(name); result.push(name); }
  }
  return result.slice(0, 4);
}
function BoardHeroPlaceholder() {
  // Sky-light bg + small globe icon. Subtle — should read as "open slot,"
  // not "broken." Spec called for ~24px globe at 25% opacity navy.
  return (
    <div style={{
      background: "#D6E8F5",
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: 0, // grid children need this to honor parent height
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="rgba(27,58,92,0.25)" strokeWidth="1.4" fill="none"/>
        <ellipse cx="12" cy="12" rx="4" ry="9" stroke="rgba(27,58,92,0.25)" strokeWidth="1" fill="none"/>
        <line x1="3" y1="12" x2="21" y2="12" stroke="rgba(27,58,92,0.25)" strokeWidth="1"/>
      </svg>
    </div>
  );
}
function BoardHeroGrid({ board }) {
  const items = board?.items || [];
  const recents = getBoardRecentDestNames(board?.id, items);
  // Pad to 4 slots so the grid is always 2×2 (per spec).
  const slots = [recents[0], recents[1], recents[2], recents[3]];
  return (
    <div style={{
      width: "100%",
      height: 120,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      gap: 1,
      background: "rgba(27,58,92,0.08)", // shows through as 1px divider
      borderTopLeftRadius: 13,  // card has borderRadius 14 + 1px border = 13 inner
      borderTopRightRadius: 13,
      overflow: "hidden",
    }}>
      {slots.map((name, i) => {
        if (!name) return <BoardHeroPlaceholder key={`slot-${i}`} />;
        // Match by destName OR title — POSTCARDS_CATALOG entries use either
        const cat = POSTCARDS_CATALOG.find(c => c.destName === name || c.title === name);
        if (!cat) return <BoardHeroPlaceholder key={`slot-${i}`} />;
        // Wrap PostcardIllustration in a clipping container with a cream
        // backstop so the illustration's transparent edges (if any) read
        // against the card aesthetic rather than the gap color.
        return (
          <div key={`slot-${i}`} style={{ background: "#FAFAF5", overflow: "hidden", minHeight: 0 }}>
            <PostcardIllustration id={cat.id} />
          </div>
        );
      })}
    </div>
  );
}
const COUNTRY_FLAGS = {
  // UK + sub-nations
  "uk": "🇬🇧", "united kingdom": "🇬🇧", "great britain": "🇬🇧", "britain": "🇬🇧",
  "england": "🇬🇧", "scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿", "wales": "🏴󠁧󠁢󠁷󠁬󠁳󠁿", "northern ireland": "🇬🇧",
  // Europe
  "france": "🇫🇷", "italy": "🇮🇹", "spain": "🇪🇸", "portugal": "🇵🇹", "germany": "🇩🇪",
  "netherlands": "🇳🇱", "holland": "🇳🇱", "greece": "🇬🇷", "iceland": "🇮🇸", "norway": "🇳🇴",
  "sweden": "🇸🇪", "denmark": "🇩🇰", "finland": "🇫🇮", "austria": "🇦🇹", "switzerland": "🇨🇭",
  "belgium": "🇧🇪", "ireland": "🇮🇪", "poland": "🇵🇱", "czech republic": "🇨🇿", "czechia": "🇨🇿",
  "hungary": "🇭🇺", "croatia": "🇭🇷", "romania": "🇷🇴", "bulgaria": "🇧🇬", "slovenia": "🇸🇮",
  "slovakia": "🇸🇰", "serbia": "🇷🇸", "ukraine": "🇺🇦", "russia": "🇷🇺", "turkey": "🇹🇷",
  "malta": "🇲🇹", "luxembourg": "🇱🇺", "estonia": "🇪🇪", "latvia": "🇱🇻", "lithuania": "🇱🇹",
  "monaco": "🇲🇨",
  // Asia
  "japan": "🇯🇵", "china": "🇨🇳", "south korea": "🇰🇷", "korea": "🇰🇷", "north korea": "🇰🇵",
  "thailand": "🇹🇭", "vietnam": "🇻🇳", "indonesia": "🇮🇩", "philippines": "🇵🇭", "malaysia": "🇲🇾",
  "singapore": "🇸🇬", "taiwan": "🇹🇼", "india": "🇮🇳", "nepal": "🇳🇵", "bhutan": "🇧🇹",
  "cambodia": "🇰🇭", "laos": "🇱🇦", "myanmar": "🇲🇲", "burma": "🇲🇲", "sri lanka": "🇱🇰",
  "maldives": "🇲🇻", "pakistan": "🇵🇰", "bangladesh": "🇧🇩", "mongolia": "🇲🇳", "hong kong": "🇭🇰",
  "macau": "🇲🇴",
  // Middle East
  "uae": "🇦🇪", "united arab emirates": "🇦🇪", "saudi arabia": "🇸🇦", "qatar": "🇶🇦",
  "kuwait": "🇰🇼", "bahrain": "🇧🇭", "oman": "🇴🇲", "jordan": "🇯🇴", "lebanon": "🇱🇧",
  "israel": "🇮🇱", "iran": "🇮🇷", "iraq": "🇮🇶", "yemen": "🇾🇪", "syria": "🇸🇾",
  // Africa
  "egypt": "🇪🇬", "morocco": "🇲🇦", "south africa": "🇿🇦", "kenya": "🇰🇪", "tanzania": "🇹🇿",
  "ethiopia": "🇪🇹", "nigeria": "🇳🇬", "ghana": "🇬🇭", "rwanda": "🇷🇼", "uganda": "🇺🇬",
  "senegal": "🇸🇳", "tunisia": "🇹🇳", "algeria": "🇩🇿", "namibia": "🇳🇦", "botswana": "🇧🇼",
  "zimbabwe": "🇿🇼", "zambia": "🇿🇲", "madagascar": "🇲🇬", "mauritius": "🇲🇺", "seychelles": "🇸🇨",
  // Oceania
  "australia": "🇦🇺", "new zealand": "🇳🇿", "fiji": "🇫🇯", "samoa": "🇼🇸", "tonga": "🇹🇴",
  "papua new guinea": "🇵🇬",
  // Americas
  "usa": "🇺🇸", "united states": "🇺🇸", "us": "🇺🇸", "america": "🇺🇸",
  "canada": "🇨🇦", "mexico": "🇲🇽",
  "argentina": "🇦🇷", "brazil": "🇧🇷", "chile": "🇨🇱", "colombia": "🇨🇴", "peru": "🇵🇪",
  "ecuador": "🇪🇨", "uruguay": "🇺🇾", "paraguay": "🇵🇾", "bolivia": "🇧🇴", "venezuela": "🇻🇪",
  "cuba": "🇨🇺", "jamaica": "🇯🇲", "dominican republic": "🇩🇴", "puerto rico": "🇵🇷",
  "bahamas": "🇧🇸", "barbados": "🇧🇧", "costa rica": "🇨🇷", "panama": "🇵🇦", "guatemala": "🇬🇹",
};

// Common city → country inference, for when a user types just a city name.
// Covers cities in DESTINATIONS plus a few obvious extras so "London" or
// "Edinburgh" don't fall back to globe.
const CITY_TO_COUNTRY = {
  "london": "uk", "manchester": "uk", "edinburgh": "scotland", "glasgow": "scotland",
  "liverpool": "uk", "birmingham": "uk", "oxford": "uk", "cambridge": "uk", "bristol": "uk",
  "york": "uk", "bath": "uk", "brighton": "uk", "leeds": "uk", "cardiff": "wales",
  "belfast": "northern ireland", "dublin": "ireland",
  // Other commonly-typed cities not in DESTINATIONS
  "berlin": "germany", "munich": "germany", "hamburg": "germany", "frankfurt": "germany",
  "amsterdam": "netherlands", "rotterdam": "netherlands",
  "madrid": "spain", "seville": "spain", "valencia": "spain", "granada": "spain",
  "florence": "italy", "venice": "italy", "milan": "italy", "naples": "italy",
  "zurich": "switzerland", "geneva": "switzerland",
  "vienna": "austria", "salzburg": "austria",
  "stockholm": "sweden", "oslo": "norway", "helsinki": "finland",
  "shanghai": "china", "beijing": "china", "hong kong": "hong kong",
  "mumbai": "india", "delhi": "india", "jaipur": "india", "goa": "india",
  "istanbul": "turkey",
  "phuket": "thailand",
  "ho chi minh city": "vietnam", "hanoi": "vietnam", "saigon": "vietnam",
  "havana": "cuba",
  "rio de janeiro": "brazil", "rio": "brazil", "sao paulo": "brazil",
  "lima": "peru", "cusco": "peru",
  "quito": "ecuador",
  "panama city": "panama",
  "san juan": "puerto rico",
};

// Resolve a flag emoji for a freeform destination string like "London, UK"
// or "England" or just "Edinburgh". Returns null if no match — caller can
// decide on a fallback (🌍 etc).
function resolveFlagEmoji(input) {
  if (!input || typeof input !== "string") return null;
  const raw = input.toLowerCase().trim();
  // 1. Exact country match
  if (COUNTRY_FLAGS[raw]) return COUNTRY_FLAGS[raw];
  // 2. "City, Country" form — try the part after the last comma
  if (raw.includes(",")) {
    const parts = raw.split(",").map(s => s.trim());
    for (let i = parts.length - 1; i >= 0; i--) {
      if (COUNTRY_FLAGS[parts[i]]) return COUNTRY_FLAGS[parts[i]];
      if (CITY_TO_COUNTRY[parts[i]] && COUNTRY_FLAGS[CITY_TO_COUNTRY[parts[i]]]) {
        return COUNTRY_FLAGS[CITY_TO_COUNTRY[parts[i]]];
      }
    }
  }
  // 3. City-only — look up via city→country
  if (CITY_TO_COUNTRY[raw] && COUNTRY_FLAGS[CITY_TO_COUNTRY[raw]]) {
    return COUNTRY_FLAGS[CITY_TO_COUNTRY[raw]];
  }
  // 4. Substring match — country name appears anywhere in input
  for (const country of Object.keys(COUNTRY_FLAGS)) {
    if (raw.includes(country)) return COUNTRY_FLAGS[country];
  }
  return null;
}

// ── Monthly travel blog ──────────────────────────────────────────
// "Best Places to Travel in {Month}" — one entry per month, keyed 0-11.
// getActiveBlogMonth() picks which to show: the current month normally,
// but rolls to NEXT month once we're within 7 days of month-end (people
// plan next month's travel in the final week). Auto-rotates forever —
// no manual edits needed.
const MONTHLY_BLOG = {
  0: { title: "Best Places to Travel in January", subtitle: "New year, fresh escapes — chase winter sun or counter-season summer down south.", picks: [
    { rank: 1, emoji: "🇹🇭", name: "Bangkok", region: "Asia", why: "Peak cool-dry season — comfortable days, buzzing markets, and post-holiday value.", budget: "From $700", tag: "Culture" },
    { rank: 2, emoji: "🇦🇷", name: "Patagonia", region: "Americas", why: "Southern-hemisphere summer means long days for hiking and glaciers.", budget: "From $1,800", tag: "Adventure" },
    { rank: 3, emoji: "🇦🇪", name: "Dubai", region: "Middle East", why: "Ideal 24°C weather before the summer heat — desert and city at their best.", budget: "From $1,600", tag: "Luxury" },
    { rank: 4, emoji: "🇳🇿", name: "Queenstown", region: "Oceania", why: "Summer in the Southern Alps — lakes, trails, and adventure season peaks.", budget: "From $1,700", tag: "Adventure" },
    { rank: 5, emoji: "🇲🇽", name: "Tulum", region: "Americas", why: "Dry, warm, and beachy — escape winter on the Caribbean coast.", budget: "From $1,000", tag: "Beach" },
  ]},
  1: { title: "Best Places to Travel in February", subtitle: "Shoulder-season warmth and romance — fewer crowds, softer prices.", picks: [
    { rank: 1, emoji: "🇲🇽", name: "Mexico City", region: "Americas", why: "Dry, mild, and buzzing — world-class food without the summer heat.", budget: "From $800", tag: "Food" },
    { rank: 2, emoji: "🇿🇦", name: "Cape Town", region: "Africa", why: "Late summer — beaches, wine country, and Table Mountain clear.", budget: "From $1,200", tag: "Nature" },
    { rank: 3, emoji: "🇯🇵", name: "Tokyo", region: "Asia", why: "Crisp winter days, plum blossoms beginning, and fewer tourists.", budget: "From $1,400", tag: "Culture" },
    { rank: 4, emoji: "🇵🇹", name: "Lisbon", region: "Europe", why: "Mild winter, low prices, and the city to yourself before spring crowds.", budget: "From $800", tag: "Culture" },
    { rank: 5, emoji: "🇨🇴", name: "Cartagena", region: "Americas", why: "Dry season on the Caribbean — colonial color and warm beaches.", budget: "From $800", tag: "Beach" },
  ]},
  2: { title: "Best Places to Travel in March", subtitle: "Spring awakens — blossoms, shoulder-season deals, and warming days.", picks: [
    { rank: 1, emoji: "🇯🇵", name: "Kyoto", region: "Asia", why: "Cherry blossom season begins — book early, it's the year's highlight.", budget: "From $1,200", tag: "Culture" },
    { rank: 2, emoji: "🇪🇸", name: "Barcelona", region: "Europe", why: "Warm spring days, Gaudí without the crowds, and pre-summer prices.", budget: "From $1,000", tag: "Culture" },
    { rank: 3, emoji: "🇲🇦", name: "Marrakech", region: "Africa", why: "Comfortable temperatures for medinas, gardens, and desert excursions.", budget: "From $700", tag: "Culture" },
    { rank: 4, emoji: "🇺🇸", name: "New Orleans", region: "Americas", why: "Festival season, mild weather, and that unmistakable spring energy.", budget: "From $800", tag: "Culture" },
    { rank: 5, emoji: "🇯🇴", name: "Amman", region: "Middle East", why: "Perfect hiking weather for Petra and Wadi Rum before the heat.", budget: "From $900", tag: "Adventure" },
  ]},
  3: { title: "Best Places to Travel in April", subtitle: "Full spring — gardens, mild weather, and pre-summer shoulder deals.", picks: [
    { rank: 1, emoji: "🇯🇵", name: "Kyoto", region: "Asia", why: "Late cherry blossoms and temple gardens at their most stunning.", budget: "From $1,200", tag: "Culture" },
    { rank: 2, emoji: "🇬🇷", name: "Athens", region: "Europe", why: "Warm but not crowded — ruins and island ferries before peak season.", budget: "From $900", tag: "Culture" },
    { rank: 3, emoji: "🇨🇿", name: "Prague", region: "Europe", why: "Spring blooms, gentle weather, and the old town before summer fills it.", budget: "From $800", tag: "Culture" },
    { rank: 4, emoji: "🇺🇸", name: "Charleston", region: "Americas", why: "Gardens in bloom, perfect temperatures, Southern charm.", budget: "From $900", tag: "Culture" },
    { rank: 5, emoji: "🇻🇳", name: "Hoi An", region: "Asia", why: "Dry and warm — lantern-lit old town before the summer heat.", budget: "From $600", tag: "Food" },
  ]},
  4: { title: "Best Places to Travel in May", subtitle: "May is one of the best months to travel — spring weather, fewer crowds, lower prices before peak summer.", picks: [
    { rank: 1, emoji: "🇯🇵", name: "Tokyo", region: "Asia", why: "Cherry blossom season wraps up, leaving green parks and cool, clear days.", budget: "From $1,400", tag: "Culture" },
    { rank: 2, emoji: "🇬🇷", name: "Santorini", region: "Europe", why: "Peak season hasn't hit yet — perfect weather, quieter caldera, better prices.", budget: "From $1,300", tag: "Beach" },
    { rank: 3, emoji: "🇲🇽", name: "Mexico City", region: "Americas", why: "Dry season, 25°C days, and world-class food scene at its best.", budget: "From $800", tag: "Food" },
    { rank: 4, emoji: "🇵🇹", name: "Porto", region: "Europe", why: "Mild riverside spring, port-wine cellars, and far fewer crowds than summer.", budget: "From $700", tag: "Food" },
    { rank: 5, emoji: "🇲🇦", name: "Marrakech", region: "Africa", why: "Warm but not scorching — ideal for medinas, gardens, and mountain day trips.", budget: "From $700", tag: "Culture" },
  ]},
  5: { title: "Best Places to Travel in June", subtitle: "Summer opens up — long days, festival season, and the Mediterranean at its best before July crowds peak.", picks: [
    { rank: 1, emoji: "🇮🇹", name: "Amalfi Coast", region: "Europe", why: "Warm seas and lemon groves before the August crush — early summer is the sweet spot.", budget: "From $1,400", tag: "Beach" },
    { rank: 2, emoji: "🇮🇸", name: "Reykjavik", region: "Europe", why: "Midnight sun, accessible highlands, and puffin season in full swing.", budget: "From $1,500", tag: "Nature" },
    { rank: 3, emoji: "🇬🇷", name: "Santorini", region: "Europe", why: "Caldera sunsets and warm Aegean swims before peak-season prices hit.", budget: "From $1,400", tag: "Beach" },
    { rank: 4, emoji: "🇨🇦", name: "Vancouver", region: "Americas", why: "Mild, dry, and stunning — mountains and ocean at their early-summer best.", budget: "From $1,200", tag: "Nature" },
    { rank: 5, emoji: "🇦🇷", name: "Buenos Aires", region: "Americas", why: "Counter-season city escape — tango, steak, and crisp winter-clear days.", budget: "From $900", tag: "Culture" },
  ]},
  6: { title: "Best Places to Travel in July", subtitle: "Peak summer — head north for cool air and endless daylight, or counter-season south.", picks: [
    { rank: 1, emoji: "🇮🇸", name: "Reykjavik", region: "Europe", why: "The warmest, brightest stretch — highlands open and days never end.", budget: "From $1,500", tag: "Nature" },
    { rank: 2, emoji: "🇨🇦", name: "Vancouver", region: "Americas", why: "Mild and dry while everywhere else bakes — mountains and sea at their best.", budget: "From $1,200", tag: "Nature" },
    { rank: 3, emoji: "🇦🇷", name: "Patagonia", region: "Americas", why: "Counter-season — snowy peaks and dramatic winter solitude down south.", budget: "From $1,800", tag: "Adventure" },
    { rank: 4, emoji: "🇩🇰", name: "Copenhagen", region: "Europe", why: "Long Scandinavian summer days, harbor swims, and design-city cool.", budget: "From $1,400", tag: "Culture" },
    { rank: 5, emoji: "🇮🇩", name: "Bali", region: "Asia", why: "Dry season peak — sunny days, lower humidity, perfect for surf and temples.", budget: "From $900", tag: "Beach" },
  ]},
  7: { title: "Best Places to Travel in August", subtitle: "Late summer — chase cooler corners and shoulder deals as peak season winds down.", picks: [
    { rank: 1, emoji: "🇩🇰", name: "Copenhagen", region: "Europe", why: "Warm canals, archipelago day trips, and the long northern summer.", budget: "From $1,400", tag: "Culture" },
    { rank: 2, emoji: "🇰🇪", name: "Nairobi", region: "Africa", why: "Great Migration season — the Maasai Mara at its most dramatic.", budget: "From $1,500", tag: "Nature" },
    { rank: 3, emoji: "🇺🇸", name: "San Francisco", region: "Americas", why: "Cool coastal summer — escape the heat with bay air and clear days.", budget: "From $1,300", tag: "Culture" },
    { rank: 4, emoji: "🇮🇸", name: "Reykjavik", region: "Europe", why: "Last of the long days — whales, waterfalls, and warm-ish hikes.", budget: "From $1,500", tag: "Nature" },
    { rank: 5, emoji: "🇨🇴", name: "Cartagena", region: "Americas", why: "Warm Caribbean color year-round, with August shoulder-season value.", budget: "From $800", tag: "Beach" },
  ]},
  8: { title: "Best Places to Travel in September", subtitle: "The traveler's secret month — summer warmth, autumn calm, and crowds gone home.", picks: [
    { rank: 1, emoji: "🇮🇹", name: "Rome", region: "Europe", why: "Summer heat fades, the crowds thin, and the city feels livable again.", budget: "From $1,100", tag: "Culture" },
    { rank: 2, emoji: "🇬🇷", name: "Athens", region: "Europe", why: "Warm seas, empty ruins, and post-peak prices — Greece relaxes.", budget: "From $900", tag: "Culture" },
    { rank: 3, emoji: "🇯🇵", name: "Tokyo", region: "Asia", why: "Summer heat eases into early autumn — a lovely quiet travel window.", budget: "From $1,400", tag: "Culture" },
    { rank: 4, emoji: "🇺🇸", name: "Boston", region: "Americas", why: "Early fall crispness, fewer crowds, and the first hints of foliage.", budget: "From $1,000", tag: "Culture" },
    { rank: 5, emoji: "🇵🇹", name: "Lisbon", region: "Europe", why: "Warm but mellow — beach days linger and the light turns golden.", budget: "From $800", tag: "Culture" },
  ]},
  9: { title: "Best Places to Travel in October", subtitle: "Autumn peaks — foliage, harvest festivals, and gorgeous shoulder-season light.", picks: [
    { rank: 1, emoji: "🇺🇸", name: "Napa Valley", region: "Americas", why: "Harvest season — the vineyards turn gold and the cellars are buzzing.", budget: "From $1,400", tag: "Food" },
    { rank: 2, emoji: "🇯🇵", name: "Kyoto", region: "Asia", why: "Autumn leaves rival the cherry blossoms — temples ablaze in red.", budget: "From $1,200", tag: "Culture" },
    { rank: 3, emoji: "🇲🇦", name: "Marrakech", region: "Africa", why: "Heat breaks — ideal for medinas, gardens, and desert excursions.", budget: "From $700", tag: "Culture" },
    { rank: 4, emoji: "🇨🇿", name: "Prague", region: "Europe", why: "Crisp golden days, fewer tourists, and the old town at its coziest.", budget: "From $800", tag: "Culture" },
    { rank: 5, emoji: "🇪🇬", name: "Cairo", region: "Africa", why: "Heat eases — perfect weather for the pyramids and the Nile.", budget: "From $800", tag: "Culture" },
  ]},
  10: { title: "Best Places to Travel in November", subtitle: "Off-season value — escape the cold, or lean into cozy pre-holiday calm.", picks: [
    { rank: 1, emoji: "🇹🇭", name: "Chiang Mai", region: "Asia", why: "Cool dry season begins — the best weather of the year, plus lantern festival.", budget: "From $600", tag: "Culture" },
    { rank: 2, emoji: "🇦🇪", name: "Abu Dhabi", region: "Middle East", why: "Perfect weather returns — desert, culture, and coast without the heat.", budget: "From $1,500", tag: "Luxury" },
    { rank: 3, emoji: "🇪🇬", name: "Cairo", region: "Africa", why: "Cool, clear, and ideal for the pyramids and a Nile cruise.", budget: "From $800", tag: "Culture" },
    { rank: 4, emoji: "🇲🇽", name: "Tulum", region: "Americas", why: "Dry season returns to the Caribbean coast — warm, calm, and golden.", budget: "From $1,000", tag: "Beach" },
    { rank: 5, emoji: "🇮🇩", name: "Bali", region: "Asia", why: "Shoulder-season calm before the December rush — green, lush, and cheaper.", budget: "From $900", tag: "Nature" },
  ]},
  11: { title: "Best Places to Travel in December", subtitle: "Festive escapes — chase winter sun or lean into snow-globe magic.", picks: [
    { rank: 1, emoji: "🇨🇿", name: "Prague", region: "Europe", why: "Christmas markets, dustings of snow, and the most magical city of the season.", budget: "From $900", tag: "Culture" },
    { rank: 2, emoji: "🇹🇭", name: "Bangkok", region: "Asia", why: "Peak cool-dry season — escape winter with warm days and clear skies.", budget: "From $700", tag: "Culture" },
    { rank: 3, emoji: "🇦🇪", name: "Dubai", region: "Middle East", why: "Perfect winter weather, festive luxury, and desert adventures.", budget: "From $1,600", tag: "Luxury" },
    { rank: 4, emoji: "🇦🇺", name: "Sydney", region: "Oceania", why: "Southern-hemisphere summer — beaches and a famous NYE fireworks show.", budget: "From $1,700", tag: "Beach" },
    { rank: 5, emoji: "🇲🇻", name: "Maldives", region: "Asia", why: "Dry season sunshine — overwater calm to close the year in paradise.", budget: "From $2,400", tag: "Luxury" },
  ]},
};

// Returns the month index (0-11) the blog should currently feature.
// Normally the current month — but once we're within 7 days of month-end,
// rolls forward to next month (people plan next month's trips that week).
function getActiveBlogMonth(now = new Date()) {
  const month = now.getMonth();
  const year = now.getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysLeft = daysInMonth - now.getDate();
  // Within the final 7 days → show next month
  return daysLeft <= 7 ? (month + 1) % 12 : month;
}


// ── Geography drill-down ────────────────────────────────────────
const REGIONS = ["Anywhere", "Americas", "Europe", "Asia", "Africa", "Middle East", "Oceania"];

// Americas keeps sub-regions; all other regions go straight to countries
const SUB_REGIONS = {
  Americas: ["North America","South America","Caribbean"],
};

// Direct country lists for regions that skip sub-regions
const COUNTRIES_BY_REGION = {
  Europe: ["All Countries","Austria","Belgium","Croatia","Czech Republic","Denmark","Finland","France","Germany","Greece","Hungary","Iceland","Ireland","Italy","Malta","Netherlands","Norway","Poland","Portugal","Romania","Spain","Sweden","Switzerland","United Kingdom"],
  Asia: ["All Countries","Cambodia","China","India","Indonesia","Japan","Malaysia","Maldives","Nepal","Philippines","Singapore","South Korea","Sri Lanka","Taiwan","Thailand","Vietnam"],
  Africa: ["All Countries","Botswana","Egypt","Ethiopia","Ghana","Kenya","Morocco","Namibia","Nigeria","Rwanda","Senegal","South Africa","Tanzania","Tunisia","Zimbabwe"],
  "Middle East": ["All Countries","Bahrain","Israel","Jordan","Kuwait","Lebanon","Oman","Qatar","Saudi Arabia","UAE"],
  Oceania: ["All Countries","Australia","Fiji","New Zealand","Papua New Guinea","Samoa","Vanuatu"],
};

// Countries per Americas sub-region
const COUNTRIES_BY_SUB = {
  "North America": ["All Countries","USA","Canada","Mexico"],
  "South America": ["All Countries","Argentina","Bolivia","Brazil","Chile","Colombia","Ecuador","Guyana","Paraguay","Peru","Suriname","Uruguay","Venezuela"],
  "Caribbean": ["All Countries","Antigua and Barbuda","Bahamas","Barbados","Cuba","Dominica","Dominican Republic","Grenada","Haiti","Jamaica","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Trinidad and Tobago"],
};

// States/provinces for countries that have them in destinations
const STATES_BY_COUNTRY = {
  USA: ["All States","Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming","Puerto Rico"],
  Japan: ["All Regions","Tokyo Metropolis","Kyoto Prefecture","Osaka Prefecture"],
  Italy: ["All Regions","Lazio","Campania","Tuscany","Lombardy"],
  Greece: ["All Regions","Attica","South Aegean","Crete"],
  Spain: ["All Regions","Catalonia","Andalusia","Madrid","Valencia"],
  Indonesia: ["All Regions","Bali","West Nusa Tenggara","Jakarta","Yogyakarta","Komodo","Raja Ampat","Sulawesi"],
  Thailand: ["All Regions","Bangkok","Chiang Mai Province","Phuket Province"],
  Australia: ["All States","New South Wales","Victoria","Queensland","Western Australia","South Australia","Tasmania","Northern Territory","Australian Capital Territory"],
  "New Zealand": ["All Regions","Auckland","Wellington","Canterbury","Otago","Bay of Plenty","Hawke's Bay"],
  Mexico: ["All States","Baja California Sur (Los Cabos)","Chiapas (San Cristóbal)","Chihuahua (Copper Canyon)","CDMX (Mexico City)","Guanajuato","Jalisco (Guadalajara & Puerto Vallarta)","Nayarit (Sayulita & Riviera Nayarit)","Oaxaca","Puebla","Quintana Roo (Cancún, Tulum & Playa del Carmen)","Veracruz","Yucatán (Mérida & Chichén Itzá)"],
  Argentina: ["All Regions","Buenos Aires Province","Santa Cruz","Mendoza"],
  Colombia: ["All Regions","Bolívar","Bogotá D.C.","Antioquia"],
  "South Africa": ["All Regions","Western Cape","Gauteng","KwaZulu-Natal"],
  UAE: ["All Emirates","Dubai Emirate","Abu Dhabi Emirate","Sharjah Emirate"],
  Canada: ["All Provinces","Alberta","British Columbia","Manitoba","New Brunswick","Newfoundland and Labrador","Nova Scotia","Ontario","Prince Edward Island","Quebec","Saskatchewan"],
};

// Vibe icons (shared across Explore, Take Me Anywhere, Surprise Me).
// Each takes a color and renders at the size where it's used. Keep these in
// sync — the visual identity of a vibe should be the same wherever a user
// encounters it (consistent icon + accent color builds recognition).
const VIBE_ICONS = {
  beach:     (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M2 19 Q5 17, 8 19 T 14 19 T 20 19 T 22 19" stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none"/><path d="M2 22 Q5 20, 8 22 T 14 22 T 20 22 T 22 22" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.55"/><circle cx="17.5" cy="6.5" r="2.6" fill={color} opacity="0.85"/><path d="M5 18 L 7 11 L 11 12" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill={`${color}25`}/></svg>),
  culture:   (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 20 L21 20" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><path d="M5 20 L5 10 M9 20 L9 10 M15 20 L15 10 M19 20 L19 10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><path d="M3 10 L21 10 L18 5 L6 5 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill={`${color}22`}/><circle cx="12" cy="3" r="0.8" fill={color}/></svg>),
  food:      (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4 L5 11 Q5 13 7 13 L7 21" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M3 4 L3 9 M7 4 L7 9" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><path d="M16 13 L16 21" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><ellipse cx="16" cy="8" rx="4" ry="5" stroke={color} strokeWidth="1.6" fill={`${color}22`}/></svg>),
  adventure: (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 20 L9 9 L13 15 L17 7 L21 20 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill={`${color}22`}/><path d="M9 9 L11 12 M17 7 L15 10" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/><circle cx="17" cy="4" r="1.4" fill={color}/></svg>),
  romance:   (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21 C 6 17, 3 13, 3 9 A 4 4 0 0 1 12 7 A 4 4 0 0 1 21 9 C 21 13, 18 17, 12 21 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill={`${color}28`}/><path d="M8 9 Q 9 8, 10 9" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/></svg>),
  nature:    (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21 L12 13" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><path d="M12 13 C 6 13, 4 8, 8 5 C 10 7, 11 9, 12 13 Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill={`${color}25`}/><path d="M12 13 C 18 13, 20 8, 16 5 C 14 7, 13 9, 12 13 Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill={`${color}25`}/><path d="M12 9 C 14 9, 15 6, 12 3 C 9 6, 10 9, 12 9 Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill={`${color}30`}/></svg>),
  luxury:    (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 9 L7 4 L17 4 L21 9 L12 21 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill={`${color}28`}/><path d="M3 9 L21 9 M7 4 L12 21 L17 4 M3 9 L12 9" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/></svg>),
  wellness:  (color) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="2.2" fill={color}/><ellipse cx="12" cy="6" rx="2.6" ry="3.6" stroke={color} strokeWidth="1.4" fill={`${color}22`}/><ellipse cx="12" cy="18" rx="2.6" ry="3.6" stroke={color} strokeWidth="1.4" fill={`${color}22`} transform="rotate(180 12 18)"/><ellipse cx="6" cy="12" rx="3.6" ry="2.6" stroke={color} strokeWidth="1.4" fill={`${color}22`}/><ellipse cx="18" cy="12" rx="3.6" ry="2.6" stroke={color} strokeWidth="1.4" fill={`${color}22`} transform="rotate(180 18 12)"/></svg>),
};

const VIBE_COLORS = {
  beach: "#7A8A9C", culture: "#A8853B", food: "#C25E47", adventure: "#A85A3C",
  romance: "#4A8CC4", nature: "#7A8467", luxury: "#9C7B65", wellness: "#A89AC4",
  art: "#7A6A85", nightlife: "#5C4A78",
};

// ── Companion-fit scoring ───────────────────────────────────────────
// Multiplier applied to destination scores based on who the user is traveling with.
// Solo, couple, friends, and family each have different practical needs that go
// beyond personal preference — a family of 5 needs predictability, daytime
// activities, and kid-safe destinations regardless of whether the parents'
// personal vibe is "Romantic Wanderer" or "Adventure Seeker."
//
// Returns a multiplier in roughly [0.3, 1.4]:
//   1.0  = neutral (companion-agnostic destination)
//   >1.0 = boost for this companion type (raises in rankings)
//   <1.0 = demote for this companion type (drops in rankings)
//
// The heuristic uses existing destination tags. As the destination database
// grows, individual destinations can override this with a manual companionFit
// field, but the heuristic is the baseline.
function companionFit(destination, companion) {
  if (!destination || !companion) return 1.0;
  const tags = destination.tags || [];
  const has = t => tags.includes(t);
  let fit = 1.0;

  if (companion === "solo") {
    if (has("nightlife"))   fit *= 1.20;
    if (has("adventure"))   fit *= 1.15;
    if (has("culture"))     fit *= 1.10;
    if (has("wellness"))    fit *= 1.10;
    if (has("food"))        fit *= 1.05;
    if (has("family"))      fit *= 0.65;  // family-only spots feel weird solo
    if (has("romance"))     fit *= 0.80;  // romance is awkward solo
    if ((destination.minBudget || 0) > 4500) fit *= 0.85; // luxury solo less common
  }

  if (companion === "couple") {
    if (has("romance"))     fit *= 1.30;
    if (has("luxury"))      fit *= 1.20;
    if (has("wellness"))    fit *= 1.15;
    if (has("food"))        fit *= 1.10;
    if (has("beach"))       fit *= 1.10;
    if (has("nightlife"))   fit *= 1.05;
    if (has("family"))      fit *= 0.80;
    if (has("backpacking")) fit *= 0.85; // most couples want comfort
  }

  if (companion === "friends") {
    if (has("nightlife"))   fit *= 1.30;
    if (has("adventure"))   fit *= 1.20;
    if (has("food"))        fit *= 1.15;
    if (has("beach"))       fit *= 1.10;
    if (has("culture"))     fit *= 1.05;
    if (has("romance"))     fit *= 0.65;  // weird in a group
    if (has("wellness"))    fit *= 0.85;  // possible but not the norm
    if (has("family"))      fit *= 0.70;
  }

  if (companion === "family") {
    if (has("family"))      fit *= 1.40;  // big boost
    if (has("nature"))      fit *= 1.25;
    if (has("beach"))       fit *= 1.20;
    if (has("culture"))     fit *= 1.05;
    if (has("nightlife"))   fit *= 0.40;  // big penalty
    if (has("romance"))     fit *= 0.50;
    if (has("backpacking")) fit *= 0.55;
    if (has("luxury"))      fit *= 0.85;  // family-friendly luxury is rare
    if ((destination.minBudget || 0) > 5000) fit *= 0.85; // family budgets stretch thin
  }

  // Manual override — destinations can set a per-companion fit explicitly,
  // which takes precedence over the heuristic.
  if (destination.companionFit && typeof destination.companionFit[companion] === "number") {
    return destination.companionFit[companion];
  }

  // Clamp to reasonable bounds so a destination never disappears entirely
  // or dominates beyond reason.
  return Math.max(0.30, Math.min(1.45, fit));
}

// ── Board icon picker library ───────────────────────────────────
// 12 on-brand SVG icons used in the board create/edit picker. Boards now
// store an `iconId` referencing this map instead of an emoji string. Old
// boards with `emoji` keep working (legacy fallback) and migrate to iconId
// on next interaction via the EMOJI_TO_ICON_ID map below.
const BOARD_ICONS = [
  { id: "sparkle",  label: "Sparkle",  render: (c, s) => JGA_ICONS.boards_sparkle(c, s) },
  { id: "compass",  label: "Compass",  render: (c, s) => JGA_ICONS.boards_compass(c, s) },
  { id: "plane",    label: "Plane",    render: (c, s) => JGA_ICONS.boards_plane(c, s) },
  { id: "pin",      label: "Pin",      render: (c, s) => JGA_ICONS.boards_pin(c, s) },
  { id: "mountain", label: "Mountain", render: (c, s) => JGA_ICONS.boards_mountain(c, s) },
  { id: "beach",    label: "Beach",    render: (c, s) => JGA_ICONS.boards_beach(c, s) },
  { id: "palm",     label: "Palm",     render: (c, s) => JGA_ICONS.boards_palm(c, s) },
  { id: "food",     label: "Food",     render: (c, s) => JGA_ICONS.boards_food(c, s) },
  { id: "heart",    label: "Heart",    render: (c, s) => JGA_ICONS.boards_heart(c, s) },
  { id: "lotus",    label: "Lotus",    render: (c, s) => JGA_ICONS.boards_lotus(c, s) },
  { id: "gem",      label: "Gem",      render: (c, s) => JGA_ICONS.boards_gem(c, s) },
  { id: "plant",    label: "Plant",    render: (c, s) => JGA_ICONS.boards_plant(c, s) },
];

// JGA_BENEFITS — single source of truth for the "what you'll unlock" features
// list. Used by:
//   - The dashboard prelogin unlock teaser (for not-signed-in users)
//   - The registration modal's FEATURES checkmark list
// Both surfaces render the same items in the same order with the same wording.
// Adding a new feature here automatically updates both places. Each item has
// an `icon` (rendered as emoji on the dashboard) and a `text` label (rendered
// identically with a ✓ chip on registration).
const JGA_BENEFITS = [
  { icon: "✨", text: "Destinations matched to your Travel Identity™" },
  { icon: "🤖", text: "AI-powered personalised itineraries" },
  { icon: "🎲", text: "Unlimited spins on Take Me Anywhere" },
  { icon: "🎁", text: "Unlimited Surprise Me bookings" },
  { icon: "💰", text: "Smart budget tracking & price drop alerts" },
  { icon: "📓", text: "Travel journal with voice-to-text — capture every moment" },
  { icon: "❤️", text: "Saved trips, wishlist & mood boards" },
  { icon: "📊", text: "Passport, trips & countries tracked" },
];

// Maps legacy emoji to the closest matching iconId for migration. Anything
// not in this map falls back to "sparkle" (default).
const EMOJI_TO_ICON_ID = {
  "✨": "sparkle", "🌟": "sparkle", "⭐": "sparkle",
  "🗓️": "sparkle", "📅": "sparkle",
  "🌴": "palm", "🏝️": "palm", "🏖️": "beach", "🌊": "beach",
  "🍷": "food", "🍴": "food", "🍽️": "food", "🍜": "food",
  "💕": "heart", "❤️": "heart", "💖": "heart", "💑": "heart",
  "🏔️": "mountain", "⛰️": "mountain", "🥾": "mountain", "🧗": "mountain",
  "✈️": "plane", "🛫": "plane", "🛬": "plane",
  "🗺️": "compass", "🎯": "compass",
  "📍": "pin", "📌": "pin",
  "🧘": "lotus", "💆": "lotus", "🌸": "lotus",
  "✨": "gem", "💎": "gem",
  "🌿": "plant", "🌳": "plant", "🍃": "plant",
};

// Helper: resolve a board's icon id (handles legacy emoji-only boards)
function resolveBoardIconId(board) {
  if (board?.iconId) return board.iconId;
  if (board?.emoji && EMOJI_TO_ICON_ID[board.emoji]) return EMOJI_TO_ICON_ID[board.emoji];
  return "sparkle";
}

function getBoardIcon(iconId) {
  return BOARD_ICONS.find(i => i.id === iconId) || BOARD_ICONS[0];
}

// ── JGA Wordmark + Globe (Window Seat rebrand) ───────────────────
// Three-color wordmark per brand spec: Just=sky-mid (calm invitation),
// iOS Safari (and ALL iOS browsers — Chrome, Firefox on iOS use WebKit by
// Apple policy) has a known bug where gradient text via the standard
// `-webkit-background-clip: text + -webkit-text-fill-color: transparent`
// pattern renders fully transparent. Defensive fallbacks (`color: fallback`,
// `backgroundClip: text`, `display: inline-block`) don't fix it on iOS.
// We detect iOS and render solid color there; gradient text everywhere else.
const _IS_IOS_SAFARI = (() => {
  try {
    return typeof navigator !== "undefined" && /iP(ad|hone|od)/.test(navigator.userAgent);
  } catch { return false; }
})();

// Returns inline style props for gradient text that gracefully falls back to
// solid `fallbackColor` on iOS Safari (where the gradient-clip pattern fails).
// Use by spreading into a style object alongside other text styles:
//   <h2 style={{ fontSize: 22, fontWeight: 900, ...gradientTextStyle(gradient, teal) }}>
function gradientTextStyle(gradient, fallbackColor) {
  if (_IS_IOS_SAFARI) {
    return { color: fallbackColor };
  }
  return {
    background: gradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    color: fallbackColor,
    display: "inline-block",
  };
}

// Go=sky-deep (decision/anchor), Anywhere=sun-gold (destination/payoff).
// ™ matches Anywhere's gold so it reads as part of that word, not floating.
// Use this component anywhere the brand name renders as a logo. Plain-text
// references inside body copy (e.g. "...using Just Go Anywhere...") stay
// as plain text — the wordmark is for the brand mark only.
// ── WORDMARK VARIANTS ─────────────────────────────────────────
// Four approved color combinations for the "Just Go Anywhere" wordmark.
// Per Cas's spec — every combination keeps golden hour somewhere; the
// warmth never disappears. Variant 3 is the locked default and the
// fallback when prefers-reduced-motion is set.
const JGA_WORDMARK_VARIANTS = [
  // Variant 3 — the locked default (mid-sky / deep-sky / golden hour)
  { just: "#4A8CC4", go: "#1B3A5C", anywhere: "#E89A3C" },
  // Variant 1 — deep-sky / mid-sky / golden hour
  { just: "#1B3A5C", go: "#4A8CC4", anywhere: "#E89A3C" },
  // Variant 2 — mid-sky / golden hour / deep-sky (gold in the middle)
  { just: "#4A8CC4", go: "#E89A3C", anywhere: "#1B3A5C" },
  // Variant 4 — deep-sky / deep-sky / golden hour (quiet then arrival)
  { just: "#1B3A5C", go: "#1B3A5C", anywhere: "#E89A3C" },
];

function JGAWordmark({ size = 18, showTM = true, style = {} }) {
  // Detect prefers-reduced-motion once; fall back to the locked default
  // (Variant 3 / index 0) if the user has reduced motion preferred.
  const reducedMotion = (typeof window !== "undefined" && typeof window.matchMedia === "function")
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
  const [variantIdx, setVariantIdx] = useState(0);
  useEffect(() => {
    if (reducedMotion) return; // honor the OS-level preference
    // Cycle through variants. 4.5s on each, with the 2.5s transition
    // making the change feel ambient rather than abrupt.
    const interval = setInterval(() => {
      setVariantIdx(i => (i + 1) % JGA_WORDMARK_VARIANTS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [reducedMotion]);
  const v = JGA_WORDMARK_VARIANTS[variantIdx];
  const transition = reducedMotion ? "none" : "color 2.5s ease-in-out";
  return (
    <span style={{
      fontFamily: "'Archivo', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      fontWeight: 900,
      fontSize: size,
      letterSpacing: "-0.01em",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "baseline",
      ...style,
    }} aria-label="Just Go Anywhere">
      <span style={{ color: v.just, transition }}>Just</span>
      <span style={{ color: v.go, transition, margin: "0 0.18em" }}>Go</span>
      <span style={{ color: v.anywhere, transition }}>Anywhere</span>
      {showTM && (
        <sup style={{
          color: "#E89A3C",
          fontSize: "0.4em",
          marginLeft: "0.12em",
          verticalAlign: "super",
          fontWeight: 700,
        }} aria-hidden="true">™</sup>
      )}
    </span>
  );
}

// Globe icon with gold halo + sky-deep line work. Gold halo visually rhymes
// with "Anywhere" in the wordmark — header reads as one connected idea.
// Default 40x40 matches the brand spec; pass size to scale.
function JGAGlobe({ size = 40, style = {} }) {
  const innerSize = Math.round(size * 0.65);
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "#F5D78A", // sun-soft halo
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      ...style,
    }} aria-hidden="true">
      <svg width={innerSize} height={innerSize} viewBox="0 0 100 100" fill="none"
           stroke="#1B3A5C" strokeWidth="8" strokeLinecap="round">
        <circle cx="50" cy="50" r="40"/>
        <ellipse cx="50" cy="50" rx="14" ry="40"/>
        <line x1="10" y1="50" x2="90" y2="50"/>
      </svg>
    </div>
  );
}

// VoiceDictateButton — Web Speech API-powered mic button for dictating into
// text fields (primarily journal entries). Self-contained component that:
//   - Detects Web Speech API support (hides if unsupported — mainly Firefox)
//   - Manages SpeechRecognition lifecycle (start/stop/cleanup on unmount)
//   - Appends FINAL transcribed segments to parent via onTranscript callback
//   - Tracks resultIndex to avoid duplicate text on continuous mode
//   - Shows visual state: idle (sky-deep mic), listening (sun-deep mic + pulse)
//
// Usage:
//   <VoiceDictateButton onTranscript={(text) => setDraft(prev => prev + text)} />
//
// Browser support: Chrome (desktop + Android), Safari (desktop + iOS 14.5+),
// Edge. Firefox does NOT support — component returns null in that case so
// the user sees a clean UI (and can still use their OS keyboard mic).
function VoiceDictateButton({ onTranscript, label = "Dictate" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const lastFinalIndexRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";

    rec.onresult = (event) => {
      let newFinal = "";
      for (let i = lastFinalIndexRef.current; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          newFinal += event.results[i][0].transcript;
          lastFinalIndexRef.current = i + 1;
        }
      }
      if (newFinal && typeof onTranscript === "function") {
        onTranscript(newFinal);
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      console.warn("[JGA Voice] Recognition error:", e.error);
      setListening(false);
    };

    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch {} };
  }, [onTranscript]);

  const toggle = () => {
    if (!recognitionRef.current) return;
    if (listening) {
      try { recognitionRef.current.stop(); } catch {}
      setListening(false);
    } else {
      lastFinalIndexRef.current = 0;
      try {
        recognitionRef.current.start();
        setListening(true);
      } catch (err) {
        console.warn("[JGA Voice] Failed to start recognition:", err);
      }
    }
  };

  if (!supported) return null;

  const color = listening ? "#C8741D" : "#1B3A5C";
  const bg = listening ? "rgba(232,154,60,0.16)" : "rgba(74,140,196,0.10)";
  const border = listening ? "rgba(232,154,60,0.45)" : "rgba(74,140,196,0.30)";

  return (
    <button type="button" onClick={toggle}
      aria-label={listening ? "Stop dictation" : label}
      aria-pressed={listening}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 20,
        background: bg, border: `1px solid ${border}`,
        color, fontSize: 11, fontWeight: 700,
        cursor: "pointer", fontFamily: "inherit",
        transition: "all 0.15s",
        animation: listening ? "jga-mic-pulse 1.4s ease-in-out infinite" : "none",
      }}>
      <style>{`
        @keyframes jga-mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(232,154,60,0.4); }
          50%      { box-shadow: 0 0 0 6px rgba(232,154,60,0); }
        }
      `}</style>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="9" y="3" width="6" height="11" rx="3" stroke={color} strokeWidth="1.8" fill={listening ? color : "none"}/>
        <path d="M5 11 V12 C 5 16 8 19 12 19 C 16 19 19 16 19 12 V11" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <line x1="12" y1="19" x2="12" y2="22" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
      {listening ? "● Listening — tap to stop" : "Tap to dictate"}
    </button>
  );
}


// ── JGA shared icon library ─────────────────────────────────────
// Single source of truth for selector icons across Take Me Anywhere,
// Surprise Me, Explore, and any future selector. Each is a function that
// takes (color, size?) and returns inline SVG. Keeping these centralized
// means rebrand passes touch one place, not 40.
//
// Icons share a visual language: thin strokes (1.4–1.7), rounded line
// caps/joins, low-opacity fills (~22-28% of stroke color) for a "drawn"
// feel that matches the JGA wordmark/globe aesthetic.
const JGA_ICONS = {
  // ── Travellers (people-first silhouettes, scales 1→4+) ──
  traveller_solo: (c, s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="7" r="3.2" stroke={c} strokeWidth="1.6" fill={`${c}22`}/>
      <path d="M5 21 C 5 16, 8.5 13, 12 13 C 15.5 13, 19 16, 19 21" stroke={c} strokeWidth="1.6" strokeLinecap="round" fill={`${c}18`}/>
    </svg>
  ),
  traveller_couple: (c, s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="8" cy="7" r="2.6" stroke={c} strokeWidth="1.4" fill={`${c}22`}/>
      <circle cx="16" cy="7" r="2.6" stroke={c} strokeWidth="1.4" fill={`${c}22`}/>
      <path d="M2 21 C 2 17, 4.5 14.5, 8 14.5 C 11.5 14.5, 14 17, 14 21" stroke={c} strokeWidth="1.4" strokeLinecap="round" fill={`${c}15`}/>
      <path d="M10 21 C 10 17, 12.5 14.5, 16 14.5 C 19.5 14.5, 22 17, 22 21" stroke={c} strokeWidth="1.4" strokeLinecap="round" fill={`${c}15`}/>
    </svg>
  ),
  traveller_three: (c, s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="8" r="2.2" stroke={c} strokeWidth="1.3" fill={`${c}22`}/>
      <circle cx="12" cy="6" r="2.4" stroke={c} strokeWidth="1.3" fill={`${c}22`}/>
      <circle cx="18" cy="8" r="2.2" stroke={c} strokeWidth="1.3" fill={`${c}22`}/>
      <path d="M2 21 C 2 17.5, 3.5 15, 6 15 C 8.5 15, 10 17.5, 10 21" stroke={c} strokeWidth="1.3" strokeLinecap="round" fill={`${c}15`}/>
      <path d="M7 21 C 7 16.5, 9 13.5, 12 13.5 C 15 13.5, 17 16.5, 17 21" stroke={c} strokeWidth="1.3" strokeLinecap="round" fill={`${c}15`}/>
      <path d="M14 21 C 14 17.5, 15.5 15, 18 15 C 20.5 15, 22 17.5, 22 21" stroke={c} strokeWidth="1.3" strokeLinecap="round" fill={`${c}15`}/>
    </svg>
  ),
  traveller_four: (c, s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="5" cy="8" r="1.9" stroke={c} strokeWidth="1.2" fill={`${c}22`}/>
      <circle cx="10" cy="6" r="2.1" stroke={c} strokeWidth="1.2" fill={`${c}22`}/>
      <circle cx="15" cy="6" r="2.1" stroke={c} strokeWidth="1.2" fill={`${c}22`}/>
      <circle cx="20" cy="8" r="1.9" stroke={c} strokeWidth="1.2" fill={`${c}22`}/>
      <path d="M2 21 C 2 18, 3 16, 5 16 C 7 16, 8 18, 8 21" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M6 21 C 6 17, 8 14, 10 14 C 12 14, 13 16, 13 21" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11 21 C 11 16, 13 14, 15 14 C 17 14, 18 17, 18 21" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M16 21 C 16 18, 17 16, 20 16 C 22 16, 22 18, 22 21" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  ),

  // ── Age ranges ──
  age_under18: (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="7" r="3" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><path d="M7 21 C 7 16, 9 14, 12 14 C 15 14, 17 16, 17 21" stroke={c} strokeWidth="1.4" strokeLinecap="round" fill={`${c}15`}/><path d="M9 5 Q 12 2, 15 5" stroke={c} strokeWidth="1.4" strokeLinecap="round" fill="none"/></svg>),
  age_18_24:   (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 8 L12 4 L19 8 L19 10 L5 10 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M8 10 L8 14 Q12 17, 16 14 L16 10" stroke={c} strokeWidth="1.4" fill="none"/><path d="M19 8 L19 14" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  age_25_34:   (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.4" fill={`${c}18`}/><path d="M3 12 H21 M12 3 Q 16 7, 16 12 Q 16 17, 12 21 Q 8 17, 8 12 Q 8 7, 12 3" stroke={c} strokeWidth="1.2" fill="none"/></svg>),
  age_35_44:   (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="13" rx="1.5" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><path d="M9 8 L9 5 Q9 4, 10 4 L14 4 Q15 4, 15 5 L15 8" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round"/><path d="M3 14 L21 14" stroke={c} strokeWidth="1.2" opacity="0.6"/></svg>),
  age_45_54:   (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="4" y="7" width="16" height="14" rx="2" stroke={c} strokeWidth="1.4" fill={`${c}20`}/><path d="M9 7 L9 4 L15 4 L15 7" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11 L12 17 M9 14 L15 14" stroke={c} strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/></svg>),
  age_55_64:   (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 12 L11 6 L11 9 L21 9 L21 14 L11 14 L11 18 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/></svg>),
  age_65plus:  (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M2 18 Q 12 6, 22 18" stroke={c} strokeWidth="1.4" fill={`${c}25`} strokeLinejoin="round"/><circle cx="18" cy="9" r="2.6" fill={c} opacity="0.85"/><path d="M2 18 L22 18" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),

  // ── Includes ──
  inc_flights:    (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M2 13 L10 13 L13 7 L15 7 L13 13 L21 13 L20 16 L13 16 L11 22 L9 22 L11 16 L3 16 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/></svg>),
  inc_hotel:      (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 21 L3 8 L21 8 L21 21" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M3 12 L21 12 M3 16 L21 16" stroke={c} strokeWidth="1.2" opacity="0.6"/><rect x="6" y="9.5" width="2" height="1.5" fill={c} opacity="0.8"/><rect x="11" y="9.5" width="2" height="1.5" fill={c} opacity="0.8"/><rect x="16" y="9.5" width="2" height="1.5" fill={c} opacity="0.8"/><path d="M3 8 L3 4 L21 4 L21 8" stroke={c} strokeWidth="1.4" fill={`${c}28`}/></svg>),
  inc_transport:  (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 17 L4 11 L7 7 L17 7 L20 11 L20 17 L18 17 L18 19 L16 19 L16 17 L8 17 L8 19 L6 19 L6 17 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><circle cx="8" cy="14" r="1.2" fill={c}/><circle cx="16" cy="14" r="1.2" fill={c}/></svg>),
  inc_activities: (c, s = 22) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 9 L21 9 L20 21 L4 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M8 9 L8 5 Q 8 3, 10 3 L 14 3 Q 16 3, 16 5 L 16 9" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round"/><path d="M9 13 L11 15 L15 11" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>),

  // ── Flight preferences ──
  flight_direct: (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.4" fill={`${c}15`}/><circle cx="12" cy="12" r="5" stroke={c} strokeWidth="1.3" fill="none"/><circle cx="12" cy="12" r="1.5" fill={c}/></svg>),
  flight_short:  (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.4" fill={`${c}15`}/><path d="M12 7 L12 12 L15 14" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>),
  flight_any:    (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 14 Q 8 5, 13 14 Q 17 20, 21 9" stroke={c} strokeWidth="1.6" strokeLinecap="round" fill="none"/><path d="M19 6 L21 9 L18 11" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>),

  // ── Hotel types ──
  hotel_luxury:     (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 21 L4 9 L8 5 L16 5 L20 9 L20 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M9 21 L9 14 L15 14 L15 21" stroke={c} strokeWidth="1.4" fill={`${c}30`}/><path d="M12 3 L13 5 L11 5 Z" stroke={c} strokeWidth="1.3" fill={c} strokeLinejoin="round"/></svg>),
  hotel_boutique:   (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 21 L5 8 L12 4 L19 8 L19 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M9 21 L9 14 L15 14 L15 21" stroke={c} strokeWidth="1.4" fill={`${c}30`}/><circle cx="12" cy="11" r="1" fill={c}/></svg>),
  hotel_economical: (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="4" y="9" width="16" height="12" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><path d="M4 13 L20 13 M4 17 L20 17" stroke={c} strokeWidth="1.2" opacity="0.5"/><path d="M4 9 L4 6 L20 6 L20 9" stroke={c} strokeWidth="1.4" fill={`${c}28`}/></svg>),
  hotel_budget:     (c, s = 18) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 21 L20 21 L20 13 L12 8 L4 13 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><rect x="10" y="16" width="4" height="5" stroke={c} strokeWidth="1.3" fill={`${c}30`}/></svg>),

  // ── Transport ──
  trans_car:      (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 14 L4 9 Q 4.5 7, 6 7 L 18 7 Q 19.5 7, 20 9 L 21 14 L 21 18 L 19 18 L 19 16 L 5 16 L 5 18 L 3 18 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><circle cx="7" cy="15" r="1.4" fill={c}/><circle cx="17" cy="15" r="1.4" fill={c}/></svg>),
  trans_rideshare:(c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="6" y="3" width="12" height="18" rx="2" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><circle cx="12" cy="17" r="1" fill={c}/><path d="M9 6 L15 6" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/><rect x="8" y="8" width="8" height="6" stroke={c} strokeWidth="1.2" fill="none"/></svg>),
  trans_train:    (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="14" rx="2" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><rect x="7" y="6" width="10" height="5" stroke={c} strokeWidth="1.2" fill="none"/><circle cx="9" cy="14" r="1" fill={c}/><circle cx="15" cy="14" r="1" fill={c}/><path d="M7 19 L5 22 M17 19 L19 22" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  trans_bus:      (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="13" rx="2" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><rect x="5" y="7" width="14" height="5" stroke={c} strokeWidth="1.2" fill="none"/><circle cx="7" cy="16" r="1.2" fill={c}/><circle cx="17" cy="16" r="1.2" fill={c}/><path d="M5 19 L5 21 M19 19 L19 21" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  trans_metro:    (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.5" fill={`${c}18`}/><path d="M8 8 L8 14 L16 14 L16 8" stroke={c} strokeWidth="1.5" fill="none" strokeLinejoin="round"/><path d="M12 8 L12 14" stroke={c} strokeWidth="1.5"/></svg>),
  trans_taxi:     (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 14 L4 9 Q 4.5 7, 6 7 L 18 7 Q 19.5 7, 20 9 L 21 14 L 21 18 L 19 18 L 19 16 L 5 16 L 5 18 L 3 18 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><circle cx="7" cy="15" r="1.4" fill={c}/><circle cx="17" cy="15" r="1.4" fill={c}/><path d="M9 7 L9 5 L15 5 L15 7" stroke={c} strokeWidth="1.4" fill={`${c}40`}/></svg>),
  trans_bike:     (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="6" cy="17" r="3.5" stroke={c} strokeWidth="1.4" fill="none"/><circle cx="18" cy="17" r="3.5" stroke={c} strokeWidth="1.4" fill="none"/><path d="M6 17 L10 11 L14 11 L18 17 M9 11 L12 6 L15 6" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/><circle cx="6" cy="17" r="0.8" fill={c}/><circle cx="18" cy="17" r="0.8" fill={c}/></svg>),
  trans_walking:  (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="13" cy="4" r="2" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><path d="M11 8 L14 8 L17 14 M14 8 L13 13 L9 18 M13 13 L17 17 L17 21 M9 18 L8 21" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>),

  // ── Activities ──
  act_museums:      (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 20 L21 20 M5 20 L5 10 M9 20 L9 10 M15 20 L15 10 M19 20 L19 10 M3 10 L21 10 L18 5 L6 5 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/></svg>),
  act_water:        (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M2 17 Q 6 14, 10 17 T 18 17 T 22 17" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M2 21 Q 6 18, 10 21 T 18 21 T 22 21" stroke={c} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.6"/><path d="M12 4 Q 8 9, 12 13 Q 16 9, 12 4 Z" stroke={c} strokeWidth="1.4" fill={`${c}28`} strokeLinejoin="round"/></svg>),
  act_walking:      (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 21 L20 21 M4 21 L4 11 L8 7 L16 7 L20 11 L20 21" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M10 14 L10 18 L14 18 L14 14" stroke={c} strokeWidth="1.3" fill={`${c}30`}/><path d="M2 21 L4 11 M22 21 L20 11" stroke={c} strokeWidth="1.2" strokeDasharray="2 2" opacity="0.7"/></svg>),
  act_food:         (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 4 L5 11 Q 5 13, 7 13 L 7 21" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M3 4 L3 9 M7 4 L7 9" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><path d="M16 13 L16 21" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><ellipse cx="16" cy="8" rx="4" ry="5" stroke={c} strokeWidth="1.5" fill={`${c}22`}/></svg>),
  act_nightlife:    (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M21 13 A 9 9 0 1 1 11 3 A 7 7 0 0 0 21 13 Z" stroke={c} strokeWidth="1.5" fill={`${c}25`} strokeLinejoin="round"/><circle cx="17" cy="6" r="0.7" fill={c} opacity="0.8"/><circle cx="14" cy="9" r="0.5" fill={c} opacity="0.6"/></svg>),
  act_concerts:     (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="9" y="3" width="6" height="11" rx="3" stroke={c} strokeWidth="1.5" fill={`${c}22`}/><path d="M5 11 Q 5 17, 12 17 Q 19 17, 19 11" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round"/><path d="M12 17 L12 21 M9 21 L15 21" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>),
  act_hiking:       (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 21 L9 11 L13 16 L18 7 L21 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M9 11 L11 14 M18 7 L16 11" stroke={c} strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/><circle cx="18" cy="4" r="1.4" fill={c}/></svg>),
  act_shopping:     (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 8 L19 8 L18 21 L6 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M9 11 L9 8 Q 9 4, 12 4 Q 15 4, 15 8 L 15 11" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>),
  act_art:          (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3 C 7 3, 3 7, 3 12 C 3 15, 5 17, 8 17 C 9 17, 9 16, 9 15 C 9 14, 8 14, 8 13 C 8 12, 9 11, 11 11 L 13 11 C 17 11, 21 9, 21 12 C 21 18, 17 21, 12 21 C 7 21, 3 17, 3 12" stroke={c} strokeWidth="1.4" fill={`${c}22`} strokeLinejoin="round"/><circle cx="7" cy="9" r="1.2" fill={c} opacity="0.85"/><circle cx="11" cy="6" r="1.2" fill={c} opacity="0.85"/><circle cx="16" cy="7" r="1.2" fill={c} opacity="0.85"/></svg>),
  act_sports:       (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.5" fill={`${c}18`}/><path d="M12 3 L13.5 8 L18 7 L15 11 L18 15 L13.5 14 L12 19 L10.5 14 L6 15 L9 11 L6 7 L10.5 8 Z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" fill={`${c}30`}/></svg>),
  act_spa:          (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="2.5" fill={c}/><ellipse cx="12" cy="6" rx="3" ry="4" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><ellipse cx="12" cy="18" rx="3" ry="4" stroke={c} strokeWidth="1.4" fill={`${c}22`} transform="rotate(180 12 18)"/><ellipse cx="6" cy="12" rx="4" ry="3" stroke={c} strokeWidth="1.4" fill={`${c}22`}/><ellipse cx="18" cy="12" rx="4" ry="3" stroke={c} strokeWidth="1.4" fill={`${c}22`} transform="rotate(180 18 12)"/></svg>),
  act_cooking:      (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 21 L5 12 L19 12 L19 21 Z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" fill={`${c}22`}/><path d="M5 12 Q 5 7, 9 6 Q 9 3, 12 3 Q 15 3, 15 6 Q 19 7, 19 12" stroke={c} strokeWidth="1.4" fill={`${c}28`} strokeLinejoin="round"/><path d="M5 16 L19 16" stroke={c} strokeWidth="1.2" opacity="0.5"/></svg>),
  act_wildlife:     (c, s = 16) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="14" r="6" stroke={c} strokeWidth="1.5" fill={`${c}22`}/><path d="M7 9 L5 5 L9 7 M17 9 L19 5 L15 7" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill={`${c}30`}/><circle cx="9.5" cy="13" r="0.8" fill={c}/><circle cx="14.5" cy="13" r="0.8" fill={c}/><path d="M11 16 Q 12 17, 13 16" stroke={c} strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>),

  // ── Board icons ──
  // Designed for 28-32px size on board cards. Slightly heftier strokes than
  // the small selector icons so they read at a glance as "the identity of
  // this board" rather than as decorative chrome.
  boards_sparkle:  (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2 L13.5 9 L20.5 10.5 L13.5 12 L12 19 L10.5 12 L3.5 10.5 L10.5 9 Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" fill={`${c}28`}/><path d="M19 15 L19.7 17.3 L22 18 L19.7 18.7 L19 21 L18.3 18.7 L16 18 L18.3 17.3 Z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" fill={`${c}30`}/></svg>),
  boards_mountain: (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 20 L9 9 L13 15 L17 7 L21 20 Z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" fill={`${c}28`}/><path d="M9 9 L11 12 M17 7 L15 10" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.65"/><circle cx="17" cy="4" r="1.6" fill={c}/></svg>),
  boards_beach:    (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M2 18 Q5 16, 8 18 T 14 18 T 20 18 T 22 18" stroke={c} strokeWidth="1.7" strokeLinecap="round" fill="none"/><path d="M2 21 Q5 19, 8 21 T 14 21 T 20 21 T 22 21" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.55"/><circle cx="17" cy="6" r="3" fill={c} opacity="0.85"/><path d="M5 17 L 7 10 L 11 11" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill={`${c}25`}/></svg>),
  boards_food:     (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 3 L5 11 Q5 13 7 13 L7 21" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M3 3 L3 9 M7 3 L7 9" stroke={c} strokeWidth="1.7" strokeLinecap="round"/><path d="M16 13 L16 21" stroke={c} strokeWidth="1.7" strokeLinecap="round"/><ellipse cx="16" cy="8" rx="4.5" ry="5.5" stroke={c} strokeWidth="1.7" fill={`${c}28`}/></svg>),
  boards_heart:    (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 21 C 6 17, 3 13, 3 9 A 4 4 0 0 1 12 7 A 4 4 0 0 1 21 9 C 21 13, 18 17, 12 21 Z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" fill={`${c}30`}/><path d="M8 9 Q 9 7.5, 10.5 9" stroke={c} strokeWidth="1.3" strokeLinecap="round" opacity="0.7"/></svg>),
  boards_lotus:    (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="2.6" fill={c}/><ellipse cx="12" cy="6" rx="3" ry="4" stroke={c} strokeWidth="1.5" fill={`${c}25`}/><ellipse cx="12" cy="18" rx="3" ry="4" stroke={c} strokeWidth="1.5" fill={`${c}25`} transform="rotate(180 12 18)"/><ellipse cx="6" cy="12" rx="4" ry="3" stroke={c} strokeWidth="1.5" fill={`${c}25`}/><ellipse cx="18" cy="12" rx="4" ry="3" stroke={c} strokeWidth="1.5" fill={`${c}25`} transform="rotate(180 18 12)"/></svg>),
  boards_gem:      (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 9 L7 4 L17 4 L21 9 L12 21 Z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" fill={`${c}30`}/><path d="M3 9 L21 9 M7 4 L12 21 L17 4 M3 9 L12 9" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/></svg>),
  boards_plant:    (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 21 L12 13" stroke={c} strokeWidth="1.7" strokeLinecap="round"/><path d="M12 13 C 6 13, 4 8, 8 5 C 10 7, 11 9, 12 13 Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" fill={`${c}28`}/><path d="M12 13 C 18 13, 20 8, 16 5 C 14 7, 13 9, 12 13 Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" fill={`${c}28`}/><path d="M12 9 C 14 9, 15 6, 12 3 C 9 6, 10 9, 12 9 Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" fill={`${c}35`}/></svg>),
  boards_compass:  (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.7" fill={`${c}18`}/><path d="M12 4 L14 12 L12 20 L10 12 Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" fill={`${c}40`}/><circle cx="12" cy="12" r="1.4" fill={c}/></svg>),
  boards_plane:    (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 11 L21 4 L17 21 L13 14 L7 12 Z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" fill={`${c}28`}/><path d="M13 14 L21 4" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/></svg>),
  boards_pin:      (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2 C 8 2, 6 5, 6 8 C 6 11, 12 17, 12 17 C 12 17, 18 11, 18 8 C 18 5, 16 2, 12 2 Z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" fill={`${c}28`}/><circle cx="12" cy="8" r="2.5" fill={c}/><path d="M12 17 L12 22" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeDasharray="2 2"/></svg>),
  boards_palm:     (c, s = 28) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M11 22 L13 22 L13 12 L11 12 Z" stroke={c} strokeWidth="1.5" fill={`${c}30`}/><path d="M12 12 C 8 10, 4 11, 3 8 C 6 7, 10 8, 12 12 Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" fill={`${c}28`}/><path d="M12 12 C 16 10, 20 11, 21 8 C 18 7, 14 8, 12 12 Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" fill={`${c}28`}/><path d="M12 12 C 11 8, 13 4, 16 3 C 16 7, 14 10, 12 12 Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" fill={`${c}30`}/><path d="M12 12 C 13 8, 11 4, 8 3 C 8 7, 10 10, 12 12 Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round" fill={`${c}30`}/><circle cx="12" cy="12" r="1" fill={c}/></svg>),
};

const VIBES = [
  { id: "adventure", label: "Adventure",         icon: "🧗", svg: VIBE_ICONS.adventure, color: VIBE_COLORS.adventure },
  { id: "beach",     label: "Beach & Sun",       icon: "🏖️", svg: VIBE_ICONS.beach,     color: VIBE_COLORS.beach },
  { id: "culture",   label: "Culture & History", icon: "🏛️", svg: VIBE_ICONS.culture,   color: VIBE_COLORS.culture },
  { id: "food",      label: "Food & Drink",      icon: "🍜", svg: VIBE_ICONS.food,      color: VIBE_COLORS.food },
  { id: "luxury",    label: "Luxury",            icon: "✨", svg: VIBE_ICONS.luxury,    color: VIBE_COLORS.luxury },
  { id: "nature",    label: "Nature & Wildlife", icon: "🌿", svg: VIBE_ICONS.nature,    color: VIBE_COLORS.nature },
  { id: "romance",   label: "Romance",           icon: "💑", svg: VIBE_ICONS.romance,   color: VIBE_COLORS.romance },
  { id: "wellness",  label: "Wellness",          icon: "🧘", svg: VIBE_ICONS.wellness,  color: VIBE_COLORS.wellness },
];

// Activities auto-suggested when a vibe is selected
const VIBE_TO_ACTIVITIES = {
  beach:     ["water", "spa", "walking_tours"],
  adventure: ["hiking", "water", "wildlife", "sports"],
  culture:   ["museums", "walking_tours", "art", "cooking"],
  food:      ["food_tours", "cooking", "nightlife", "walking_tours"],
  romance:   ["spa", "food_tours", "walking_tours", "art"],
  wellness:  ["spa", "hiking", "walking_tours"],
  luxury:    ["spa", "food_tours", "shopping", "art"],
  nature:    ["wildlife", "hiking", "walking_tours"],
};

const AGE_RANGES = [
  { id: "under18",  label: "Under 18",   icon: "🧒", desc: "Young traveller",          svg: JGA_ICONS.age_under18, color: "#7A8467" },
  { id: "18-24",    label: "18 – 24",    icon: "🎓", desc: "Student & young adult",    svg: JGA_ICONS.age_18_24,   color: "#1B3A5C" },
  { id: "25-34",    label: "25 – 34",    icon: "🌍", desc: "Explorer",                 svg: JGA_ICONS.age_25_34,   color: "#4A8CC4" },
  { id: "35-44",    label: "35 – 44",    icon: "💼", desc: "Established adventurer",   svg: JGA_ICONS.age_35_44,   color: "#4A8CC4" },
  { id: "45-54",    label: "45 – 54",    icon: "🧳", desc: "Seasoned traveller",       svg: JGA_ICONS.age_45_54,   color: "#A85A3C" },
  { id: "55-64",    label: "55 – 64",    icon: "✈️", desc: "Freedom traveller",        svg: JGA_ICONS.age_55_64,   color: "#A8853B" },
  { id: "65plus",   label: "65+",        icon: "🌅", desc: "Golden explorer",          svg: JGA_ICONS.age_65plus,  color: "#4A8CC4" },
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FREE_SPINS = 3;

// ── Theme ───────────────────────────────────────────────────────
// Brand color constants. `teal` was retired in the editorial direction sweep — what was
// teal (#4A8CC4) is now the editorial burgundy ink (#1B3A5C). The name `teal` is kept as
// a const because 300+ refs across this file reference it; renaming all of them would be
// a huge mechanical change with no behavior benefit. Future code should read inkBurgundy
// from useTheme() instead of using this legacy `teal` name.
const gold = "#4A8CC4", amber = "#1B3A5C", teal = "#1B3A5C", coral = "#ff6b6b";

// ── Temperature conversion ─────────────────────────────────────
// Detect if user is in a Fahrenheit country (US, Bahamas, Cayman, Palau, Belize, Micronesia)
// ── Temperature unit detection ────────────────────────────────
// Fahrenheit countries: US, Bahamas, Belize, Cayman, Palau, Micronesia, Marshall Islands
const FAHRENHEIT_TZS = [
  "America/New_York","America/Chicago","America/Denver","America/Los_Angeles",
  "America/Phoenix","America/Anchorage","America/Adak","Pacific/Honolulu",
  "America/Indiana/Indianapolis","America/Indiana/Knox","America/Indiana/Marengo",
  "America/Indiana/Petersburg","America/Indiana/Tell_City","America/Indiana/Vevay",
  "America/Indiana/Vincennes","America/Indiana/Winamac",
  "America/Kentucky/Louisville","America/Kentucky/Monticello",
  "America/Detroit","America/Boise","America/Menominee","America/North_Dakota/Beulah",
  "America/North_Dakota/Center","America/North_Dakota/New_Salem",
  "America/Nassau","America/Belize","America/Cayman",
  "Pacific/Palau","Pacific/Pohnpei","Pacific/Chuuk","Pacific/Majuro","Pacific/Kwajalein",
];

function detectFahrenheit() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // Exact match first
    if (FAHRENHEIT_TZS.includes(tz)) return true;
    // Prefix match for Indiana/Kentucky sub-zones
    if (FAHRENHEIT_TZS.some(z => tz === z || tz.startsWith(z + "/"))) return true;
    // Language tag check — en-US explicitly
    const langs = navigator.languages || [navigator.language || ""];
    if (langs.some(l => l === "en-US")) return true;
    // Intl measurement system API (Chrome 116+, Firefox 126+)
    if (typeof Intl.Locale !== "undefined") {
      try {
        const locale = new Intl.Locale(langs[0] || "en");
        if (locale.getTemperatures) {
          const unit = locale.getTemperatures()?.preferred;
          if (unit === "fahrenheit") return true;
          if (unit === "celsius") return false;
        }
        // Fallback: check measurementSystem
        if (locale.getMeasurementSystem) {
          if (locale.getMeasurementSystem() === "ussystem") return true;
        }
      } catch {}
    }
    // Last resort — if language is en-US variant
    if ((navigator.language || "").startsWith("en-US")) return true;
  } catch {}
  return false;
}

function fmtTemp(tempStr, fahrenheit) {
  if (!fahrenheit || !tempStr) return tempStr;
  const match = tempStr.match(/(-?\d+)/);
  if (!match) return tempStr;
  const c = parseInt(match[1], 10);
  const f = Math.round(c * 9 / 5 + 32);
  return tempStr.replace(/(-?\d+)°C/, `${f}°F`);
}

// ── Weather icon mapping (Open-Meteo WMO codes) ─────────────────
// https://open-meteo.com/en/docs#weathervariables
function weatherCodeToIcon(code) {
  if (code === 0) return "☀️";
  if (code === 1) return "🌤️";
  if (code === 2 || code === 3) return "⛅";
  if (code === 45 || code === 48) return "🌫️";
  if (code >= 51 && code <= 57) return "🌦️";
  if (code >= 61 && code <= 67) return "🌧️";
  if (code >= 71 && code <= 77) return "❄️";
  if (code >= 80 && code <= 82) return "🌧️";
  if (code === 85 || code === 86) return "🌨️";
  if (code >= 95) return "⛈️";
  return "🌡️";
}

// ── Amadeus API hook ─────────────────────────────────────────
// Set REACT_APP_AMADEUS_KEY + REACT_APP_AMADEUS_SECRET in Railway env vars
// Docs: https://developers.amadeus.com/self-service/category/flights/api-doc/flight-offers-search
async function fetchAmadeusToken() {
  try {
    const key = (typeof process !== "undefined" && process.env?.REACT_APP_AMADEUS_KEY) || window.__AMADEUS_KEY__;
    const secret = (typeof process !== "undefined" && process.env?.REACT_APP_AMADEUS_SECRET) || window.__AMADEUS_SECRET__;
    if (!key || !secret) return null;
    const r = await fetch("https://test.api.amadeus.com/v1/security/oauth2/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `grant_type=client_credentials&client_id=${key}&client_secret=${secret}`
    });
    const d = await r.json();
    return d.access_token;
  } catch(e) { return null; }
}

// Airport IATA → Amadeus city code mapping for common destinations
const DEST_IATA = {
  "Paris": "CDG", "Rome": "FCO", "Barcelona": "BCN", "Lisbon": "LIS",
  "Tokyo": "NRT", "Bangkok": "BKK", "Bali": "DPS", "Sydney": "SYD",
  "Dubai": "DXB", "New York City": "JFK", "Miami": "MIA", "London": "LHR",
  "Amsterdam": "AMS", "Athens": "ATH", "Prague": "PRG", "Budapest": "BUD",
  "Santorini": "JTR", "Kyoto": "KIX", "Seoul": "ICN", "Marrakech": "RAK",
  "Cape Town": "CPT", "Mexico City": "MEX", "Tulum": "CUN", "Cartagena": "CTG",
  "Buenos Aires": "EZE", "Reykjavik": "KEF", "Copenhagen": "CPH",
  "Chiang Mai": "CNX", "Hoi An": "DAD", "Nairobi": "NBO", "Cairo": "CAI",
};


// ── Curated activities + experiences per destination ──────────────────
// Each activity has vibes that align with TRAVEL_IDENTITIES vibes for filtering.
// Used by the "Top match" hero card on the dashboard to show what travelers actually do there.
// To add a new destination: provide 8-12 activities so identity filtering can pick the best 6.
const DESTINATION_ACTIVITIES = {
  "Paris": [
    { emoji: "🗼", title: "Eiffel Tower at golden hour", note: "Climb to the top or picnic below", vibes: ["culture","romance"] },
    { emoji: "🎨", title: "Louvre + Musée d'Orsay", note: "Two days of art mastery", vibes: ["culture"] },
    { emoji: "🥐", title: "Croissant tour in Le Marais", note: "Five bakeries, one morning", vibes: ["food"] },
    { emoji: "⛵", title: "Seine sunset cruise", note: "Wine in hand, bridges lit up", vibes: ["romance","luxury"] },
    { emoji: "🍷", title: "Champagne day trip to Reims", note: "TGV out, taxi between maisons", vibes: ["food","luxury"] },
    { emoji: "🌳", title: "Picnic at Jardin du Luxembourg", note: "Cheese, baguette, people-watching", vibes: ["romance","nature"] },
    { emoji: "🛍️", title: "Vintage hunt in Saint-Ouen", note: "Paris's iconic flea market", vibes: ["shopping","culture"] },
    { emoji: "🎭", title: "Moulin Rouge or Crazy Horse", note: "Late-night cabaret", vibes: ["nightlife","culture"] },
    { emoji: "🍽️", title: "Bistro dinner in the 11th", note: "Where Parisians actually eat", vibes: ["food","romance"] },
    { emoji: "🚲", title: "Bike along the Canal Saint-Martin", note: "Trendy, slow, unmistakably Paris", vibes: ["adventure","culture"] },
  ],
  "Rome": [
    { emoji: "🏛️", title: "Colosseum + Roman Forum at dawn", note: "Beat the crowds, feel the empire", vibes: ["culture"] },
    { emoji: "🍝", title: "Cacio e pepe in Trastevere", note: "Tiny trattoria, sidewalk table", vibes: ["food","romance"] },
    { emoji: "🍦", title: "Gelato crawl across centro", note: "Three shops, one perfect afternoon", vibes: ["food"] },
    { emoji: "⛲", title: "Trevi Fountain at midnight", note: "Empty, glowing, magical", vibes: ["romance","culture"] },
    { emoji: "⛪", title: "Vatican Museums & Sistine Chapel", note: "Skip-the-line is non-negotiable", vibes: ["culture"] },
    { emoji: "🍷", title: "Aperitivo in Campo de' Fiori", note: "Spritz, olives, golden hour", vibes: ["food","nightlife"] },
    { emoji: "🌅", title: "Sunset over the Tiber from Pincio", note: "Locals' favorite vantage point", vibes: ["romance","nature"] },
    { emoji: "🛵", title: "Vespa tour through the back alleys", note: "Roman Holiday energy", vibes: ["adventure","culture"] },
    { emoji: "🍕", title: "Pizza al taglio walking tour", note: "Roman-style square slices", vibes: ["food","adventure"] },
    { emoji: "🛍️", title: "Boutique shopping on Via del Corso", note: "Italian leather, no tourist markup", vibes: ["shopping","luxury"] },
  ],
  "Barcelona": [
    { emoji: "⛪", title: "Sagrada Família (book sunrise slot)", note: "Light through stained glass", vibes: ["culture"] },
    { emoji: "🥘", title: "Tapas crawl in El Born", note: "Five bars, one table, all night", vibes: ["food","nightlife"] },
    { emoji: "🏖️", title: "Barceloneta Beach + chiringuito lunch", note: "Sand, sangria, Mediterranean breeze", vibes: ["beach","food"] },
    { emoji: "🎨", title: "Park Güell at golden hour", note: "Gaudí's mosaic playground", vibes: ["culture","romance"] },
    { emoji: "🍷", title: "Day trip to Penedès wineries", note: "Cava country, 45 minutes out", vibes: ["food","luxury"] },
    { emoji: "🌃", title: "Rooftop bar in Eixample", note: "City lights, gin tonics", vibes: ["nightlife","luxury"] },
    { emoji: "🥖", title: "Mercat de la Boqueria breakfast", note: "Jamón, fresh juice, fresh chaos", vibes: ["food","culture"] },
    { emoji: "🚴", title: "Bike to Bunkers del Carmel", note: "Best free skyline view in town", vibes: ["adventure","nature"] },
    { emoji: "💃", title: "Flamenco at Tablao Cordobés", note: "Real, intimate, electric", vibes: ["culture","nightlife"] },
  ],
  "Lisbon": [
    { emoji: "🚋", title: "Tram 28 through Alfama", note: "Hilltops, tile facades, no rush", vibes: ["culture","romance"] },
    { emoji: "🍴", title: "Pastéis de Belém — the original", note: "Warm, custardy, 200 years old", vibes: ["food"] },
    { emoji: "🌅", title: "Miradouro da Senhora do Monte", note: "City's best sunset view", vibes: ["romance","nature"] },
    { emoji: "🎶", title: "Fado dinner in a hidden tasca", note: "Tears optional but likely", vibes: ["culture","romance"] },
    { emoji: "🏖️", title: "Day trip to Cascais", note: "Beach, seafood lunch, 40-min train", vibes: ["beach","romance"] },
    { emoji: "🍷", title: "Vinho verde tasting at LX Factory", note: "Industrial-chic, lots of wine", vibes: ["food","nightlife"] },
    { emoji: "🏰", title: "Sintra day trip — Pena Palace", note: "Looks straight out of a fairytale", vibes: ["culture","adventure"] },
    { emoji: "🛍️", title: "Príncipe Real boutique shopping", note: "Portuguese designers, not chains", vibes: ["shopping","culture"] },
    { emoji: "🌊", title: "Surf lesson at Costa da Caparica", note: "Atlantic waves, 30 min from town", vibes: ["adventure","beach"] },
  ],
  "Tokyo": [
    { emoji: "🍣", title: "Tsukiji Outer Market breakfast", note: "Tuna sashimi at 7am, no regrets", vibes: ["food","culture"] },
    { emoji: "⛩️", title: "Meiji Shrine at sunrise", note: "Quiet forest in the middle of Tokyo", vibes: ["culture","wellness"] },
    { emoji: "🌃", title: "Shibuya Crossing + rooftop view", note: "World's busiest, best from above", vibes: ["culture","nightlife"] },
    { emoji: "🍜", title: "Ramen in Shinjuku's Memory Lane", note: "Smoky, neon, six-stool counters", vibes: ["food","nightlife"] },
    { emoji: "🌸", title: "TeamLab Planets immersive art", note: "Knee-deep water, infinite mirrors", vibes: ["culture","adventure"] },
    { emoji: "🎎", title: "Asakusa & Senso-ji temple", note: "Old Tokyo, lanterns, street snacks", vibes: ["culture","food"] },
    { emoji: "🛍️", title: "Harajuku + Omotesandō", note: "Two streets, two opposite vibes", vibes: ["shopping","culture"] },
    { emoji: "🍶", title: "Golden Gai bar crawl", note: "Six tiny bars, six new friends", vibes: ["nightlife","food"] },
    { emoji: "🗼", title: "Tokyo Tower at blue hour", note: "City lights up, sky still glows", vibes: ["romance","culture"] },
    { emoji: "🚄", title: "Day trip to Kamakura", note: "Big Buddha + beach in one day", vibes: ["adventure","culture"] },
  ],
  "Bali": [
    { emoji: "🏄", title: "Sunrise surf at Canggu", note: "Lessons or longboard lounging", vibes: ["adventure","beach"] },
    { emoji: "🌿", title: "Ubud yoga + jungle retreat", note: "Open-air shalas, monkey neighbors", vibes: ["wellness","nature"] },
    { emoji: "🛕", title: "Tirta Empul water purification", note: "Sacred ritual, ancient temple", vibes: ["culture","wellness"] },
    { emoji: "🌅", title: "Tegallalang rice terraces at dawn", note: "Mist, green, postcard-perfect", vibes: ["nature","romance"] },
    { emoji: "🍴", title: "Cooking class in Ubud", note: "Market visit + 8 dishes", vibes: ["food","culture"] },
    { emoji: "🏖️", title: "Nusa Penida island day trip", note: "Cliff views, Kelingking Beach", vibes: ["adventure","beach"] },
    { emoji: "🌋", title: "Mount Batur sunrise hike", note: "2am start, breakfast at the summit", vibes: ["adventure","nature"] },
    { emoji: "💆", title: "Balinese spa day in Seminyak", note: "Two hours, $40, life-changing", vibes: ["wellness","luxury"] },
    { emoji: "🎶", title: "Beach club sunset at Potato Head", note: "DJ, infinity pool, mango passion drinks", vibes: ["nightlife","luxury","beach"] },
  ],
  "Sydney": [
    { emoji: "🏖️", title: "Bondi to Coogee coastal walk", note: "6km cliffside path, hits 5 beaches", vibes: ["beach","nature","adventure"] },
    { emoji: "🎭", title: "Opera House + Harbour Bridge", note: "Tour by day, drinks at the bar by night", vibes: ["culture","romance"] },
    { emoji: "🥂", title: "Ferry to Manly + lunch with a view", note: "30-min ride, harbor's best", vibes: ["romance","food"] },
    { emoji: "🍴", title: "Sydney Fish Market breakfast", note: "Sashimi at 8am, locals know", vibes: ["food","culture"] },
    { emoji: "🐨", title: "Taronga Zoo + skyline view", note: "Animals up front, city behind", vibes: ["nature","adventure"] },
    { emoji: "🌃", title: "Rooftop drinks in Surry Hills", note: "Best neighborhood for nightlife", vibes: ["nightlife","food"] },
    { emoji: "🌊", title: "Bondi Icebergs ocean pool swim", note: "Iconic. Cold. Worth it.", vibes: ["adventure","beach"] },
    { emoji: "🥾", title: "Blue Mountains day trip", note: "Three Sisters, eucalyptus haze", vibes: ["nature","adventure"] },
    { emoji: "🛍️", title: "Paddington Markets Saturday", note: "Aussie designers, 150 stalls", vibes: ["shopping","culture"] },
  ],
  "Dubai": [
    { emoji: "🏙️", title: "Burj Khalifa at sunset", note: "Level 124, gold light over desert", vibes: ["luxury","culture"] },
    { emoji: "🐪", title: "Desert safari + dune dinner", note: "Dune bashing, falconry, stars", vibes: ["adventure","luxury"] },
    { emoji: "🏖️", title: "Beach day at Jumeirah", note: "White sand, Burj Al Arab in view", vibes: ["beach","luxury"] },
    { emoji: "🛍️", title: "Dubai Mall + indoor aquarium", note: "1,200 stores, also penguins", vibes: ["shopping","luxury"] },
    { emoji: "🍴", title: "Old Dubai food walk in Deira", note: "Spice souk, gold souk, real local", vibes: ["food","culture"] },
    { emoji: "🌃", title: "Rooftop pool club in Marina", note: "Day-to-night party scene", vibes: ["nightlife","luxury"] },
    { emoji: "✈️", title: "Helicopter tour of Palm Jumeirah", note: "12 minutes, lifetime memory", vibes: ["luxury","adventure"] },
    { emoji: "🕌", title: "Sheikh Zayed Mosque day trip", note: "Abu Dhabi, breathtaking", vibes: ["culture","luxury"] },
  ],
  "London": [
    { emoji: "🍻", title: "Pub crawl in Borough Market area", note: "Real ales, Victorian interiors", vibes: ["food","nightlife"] },
    { emoji: "🎭", title: "West End show — anything good", note: "Cheaper than Broadway, often better", vibes: ["culture","nightlife"] },
    { emoji: "🍴", title: "Sunday roast at a proper gastropub", note: "Yorkshire pudding mandatory", vibes: ["food","culture"] },
    { emoji: "🏛️", title: "British Museum (free, all day)", note: "Rosetta Stone, Parthenon marbles", vibes: ["culture"] },
    { emoji: "🌳", title: "Hampstead Heath walk + Pond swim", note: "Kenwood House at the end", vibes: ["nature","wellness"] },
    { emoji: "🛍️", title: "Notting Hill + Portobello Market", note: "Saturdays only, antique heaven", vibes: ["shopping","culture"] },
    { emoji: "🎨", title: "Tate Modern + South Bank stroll", note: "Free art, river views", vibes: ["culture","nature"] },
    { emoji: "☕", title: "Afternoon tea at Fortnum & Mason", note: "Three tiers, 200 years old", vibes: ["luxury","food"] },
    { emoji: "🌃", title: "Sky Garden at sunset (free!)", note: "Book ahead, top of the city", vibes: ["romance","luxury"] },
  ],
  "New York City": [
    { emoji: "🌉", title: "Brooklyn Bridge walk at golden hour", note: "Manhattan side to DUMBO", vibes: ["culture","romance"] },
    { emoji: "🍕", title: "Pizza tour: Joe's, Lucali, Di Fara", note: "Three boroughs, three styles", vibes: ["food","adventure"] },
    { emoji: "🎭", title: "Broadway show + post-theater bar", note: "Matinee + evening = bonus day", vibes: ["culture","nightlife"] },
    { emoji: "🌳", title: "Central Park + The Met", note: "Walk uphill, end with art", vibes: ["culture","nature"] },
    { emoji: "🍴", title: "Smorgasburg Brooklyn (Saturdays)", note: "100 vendors, the best ones", vibes: ["food","culture"] },
    { emoji: "🌃", title: "Top of the Rock at blue hour", note: "Better than Empire State, fight me", vibes: ["romance","luxury"] },
    { emoji: "🛍️", title: "Vintage shopping in SoHo + LES", note: "Beacon's Closet to Procell", vibes: ["shopping","culture"] },
    { emoji: "🍻", title: "Speakeasy crawl: PDT, Attaboy, Death", note: "Hidden bars, killer cocktails", vibes: ["nightlife","luxury"] },
    { emoji: "⛵", title: "Sunset sail around the harbor", note: "Statue of Liberty + skyline", vibes: ["romance","luxury"] },
    { emoji: "🌅", title: "High Line + Hudson Yards walk", note: "Old rails, new architecture", vibes: ["culture","nature"] },
  ],
  "Miami": [
    { emoji: "🏖️", title: "South Beach sunrise stroll", note: "Quiet, pink light, art deco", vibes: ["beach","romance"] },
    { emoji: "🎨", title: "Wynwood Walls + gallery walk", note: "Street art, then gallery hop", vibes: ["culture"] },
    { emoji: "🍴", title: "Cuban food tour in Little Havana", note: "Versailles, La Carreta, mojitos", vibes: ["food","culture"] },
    { emoji: "🌃", title: "Rooftop pool day at Soho Beach House", note: "Or sneak into 1 Hotel", vibes: ["luxury","beach"] },
    { emoji: "⛵", title: "Sunset yacht charter on the bay", note: "Splurge, but iconic", vibes: ["luxury","romance"] },
    { emoji: "🍹", title: "Nightclub night on Ocean Drive", note: "LIV, E11even, dance til 4am", vibes: ["nightlife","luxury"] },
    { emoji: "🐊", title: "Everglades airboat day trip", note: "Alligators, sawgrass, real Florida", vibes: ["adventure","nature"] },
    { emoji: "🏝️", title: "Day trip to Key Biscayne", note: "Beach, lighthouse, no crowds", vibes: ["beach","nature"] },
  ],
  "Honolulu": [
    { emoji: "🏖️", title: "Sunrise at Lanikai Beach", note: "Kayak to the Mokulua islands", vibes: ["beach","adventure","nature"] },
    { emoji: "🥾", title: "Hike Diamond Head at dawn", note: "Crater rim, ocean view", vibes: ["adventure","nature"] },
    { emoji: "🐠", title: "Snorkel at Hanauma Bay", note: "Tuesdays closed — go any other day", vibes: ["adventure","beach","nature"] },
    { emoji: "🍴", title: "Poke + plate lunch crawl", note: "Ono Seafood, Helena's, Leonard's malasadas", vibes: ["food","culture"] },
    { emoji: "🌅", title: "North Shore drive + sunset surf", note: "Pipeline, Sunset Beach, shrimp truck", vibes: ["adventure","beach"] },
    { emoji: "🌺", title: "Lei-making + luau at Paradise Cove", note: "Touristy yes, magical also yes", vibes: ["culture","romance"] },
    { emoji: "🌋", title: "Day trip to Pearl Harbor", note: "USS Arizona, half-day, free", vibes: ["culture"] },
    { emoji: "🚁", title: "Helicopter tour over Oahu", note: "Doors-off, sees the whole island", vibes: ["adventure","luxury"] },
    { emoji: "💆", title: "Lomi lomi massage at a Waikiki spa", note: "Hawaiian healing, 90 minutes", vibes: ["wellness","luxury"] },
  ],
  "San Francisco": [
    { emoji: "🌉", title: "Bike across the Golden Gate", note: "Sausalito for lunch, ferry back", vibes: ["adventure","nature"] },
    { emoji: "🍴", title: "Ferry Building food tour", note: "Saturday market is the move", vibes: ["food","culture"] },
    { emoji: "🌳", title: "Muir Woods day trip", note: "Old-growth redwoods, 30 min north", vibes: ["nature","wellness"] },
    { emoji: "🚋", title: "Cable car + Lombard Street walk", note: "Touristy classics, still fun", vibes: ["culture"] },
    { emoji: "🍷", title: "Napa or Sonoma wine day", note: "1.5 hr drive, three tastings", vibes: ["food","luxury"] },
    { emoji: "🎨", title: "SFMOMA + Mission murals", note: "Big art + neighborhood walk", vibes: ["culture"] },
    { emoji: "🌅", title: "Sunset at Sutro Baths ruins", note: "Pacific drama, abandoned beauty", vibes: ["romance","nature"] },
    { emoji: "🍴", title: "Dim sum in Chinatown", note: "Yank Sing or Hang Ah for old-school", vibes: ["food","culture"] },
  ],
  "New Orleans": [
    { emoji: "🎷", title: "Live jazz on Frenchmen Street", note: "Locals' favorite, not Bourbon", vibes: ["culture","nightlife"] },
    { emoji: "🍴", title: "Beignets at Café du Monde", note: "24-hour, powdered sugar everywhere", vibes: ["food","culture"] },
    { emoji: "🍤", title: "Po'boy + gumbo crawl", note: "Parkway, Cochon, Willie Mae's", vibes: ["food"] },
    { emoji: "🚶", title: "French Quarter walking tour", note: "Architecture, ghosts, history", vibes: ["culture"] },
    { emoji: "🚂", title: "Streetcar to the Garden District", note: "Magazine St shopping after", vibes: ["culture","shopping"] },
    { emoji: "🐊", title: "Swamp tour bayou cruise", note: "Alligators, cypress trees, real Louisiana", vibes: ["adventure","nature"] },
    { emoji: "🍸", title: "Carousel Bar at Hotel Monteleone", note: "Bar that actually rotates", vibes: ["nightlife","luxury"] },
    { emoji: "⚱️", title: "St. Louis Cemetery #1 tour", note: "Above-ground tombs, Marie Laveau", vibes: ["culture"] },
  ],
  "Nashville": [
    { emoji: "🎸", title: "Honky-tonk crawl on Broadway", note: "Live music in every bar, every hour", vibes: ["nightlife","culture"] },
    { emoji: "🍗", title: "Hot chicken pilgrimage", note: "Hattie B's, Prince's, Bolton's", vibes: ["food","culture"] },
    { emoji: "🎤", title: "Grand Ole Opry show", note: "The country music institution", vibes: ["culture","nightlife"] },
    { emoji: "🥃", title: "Distillery day in Lynchburg", note: "Jack Daniel's tour + tasting", vibes: ["food","luxury"] },
    { emoji: "🎶", title: "RCA Studio B tour", note: "Where Elvis recorded 200+ songs", vibes: ["culture"] },
    { emoji: "🍴", title: "Brunch + bottomless mimosas in 12 South", note: "Trendy, sunny, photogenic", vibes: ["food","nightlife"] },
    { emoji: "🌳", title: "Percy Warner Park hike", note: "City escape, 9 miles of trails", vibes: ["nature","adventure"] },
    { emoji: "🛍️", title: "Vintage shopping in East Nashville", note: "Cowboy boots, vinyl, old denim", vibes: ["shopping","culture"] },
  ],
  "Las Vegas": [
    { emoji: "🎰", title: "Strip walk + Bellagio fountains", note: "Hit at dusk, magic happens", vibes: ["nightlife","culture"] },
    { emoji: "🍴", title: "Celebrity chef dinner", note: "Robuchon, Carbone, Gordon Ramsay", vibes: ["food","luxury"] },
    { emoji: "🎭", title: "Cirque du Soleil show", note: "O, KÀ, or Mystère — all great", vibes: ["culture","luxury"] },
    { emoji: "🌃", title: "Pool club day-party", note: "Encore Beach Club or Wet Republic", vibes: ["nightlife","luxury"] },
    { emoji: "🚁", title: "Grand Canyon helicopter tour", note: "Land at the bottom for champagne", vibes: ["adventure","luxury"] },
    { emoji: "🎰", title: "Old Vegas: Fremont Street", note: "Cheaper, weirder, more fun", vibes: ["nightlife","culture"] },
    { emoji: "🥾", title: "Red Rock Canyon morning hike", note: "20 min from the strip, otherworldly", vibes: ["nature","adventure"] },
    { emoji: "🛍️", title: "Forum Shops at Caesars", note: "Indoor sky, luxury brands", vibes: ["shopping","luxury"] },
  ],
  "Chicago": [
    { emoji: "🍕", title: "Deep dish at Lou Malnati's", note: "Buttery crust, two-inch cheese", vibes: ["food","culture"] },
    { emoji: "🏛️", title: "Architecture river cruise", note: "90 minutes, the best tour in America", vibes: ["culture"] },
    { emoji: "🌳", title: "Millennium Park + The Bean", note: "Walking distance to art institute", vibes: ["culture"] },
    { emoji: "🍴", title: "Italian beef + hot dog crawl", note: "Al's #1, Portillo's, no ketchup", vibes: ["food","culture"] },
    { emoji: "🍻", title: "Wrigley Field game day", note: "Cubs win or not, vibes are perfect", vibes: ["culture","nightlife"] },
    { emoji: "🎷", title: "Jazz at Green Mill", note: "Capone's old hangout, still legit", vibes: ["nightlife","culture"] },
    { emoji: "🏖️", title: "North Avenue Beach in summer", note: "Lake Michigan, skyline backdrop", vibes: ["beach","nature"] },
    { emoji: "🛍️", title: "Magnificent Mile shopping", note: "Michigan Ave, all the brands", vibes: ["shopping","luxury"] },
  ],
};


// ── Shared pricing constants (used by Most Affordable + Trending Trips) ──
// Calibrated May 2026 to realistic 2025-26 medians. Lived inside the MA
// IIFE originally — hoisted to module scope so Trending's "Within your
// budget" check uses the same numbers (prior bug: TT showed Paris as
// "within budget" using dest.minBudget while MA used these constants —
// resulted in $1,000 budget claiming Paris fit but real estimate was $2,090).
const HOTEL_NIGHTLY_BY_SUBREGION = {
  "Southeast Asia": 70, "East Asia": 130, "South Asia": 80,
  "Western Europe": 150, "Southern Europe": 130, "Eastern Europe": 100, "Northern Europe": 170,
  "North America": 180, "South America": 100, "Caribbean": 180,
  "North Africa": 90, "East Africa": 130, "Southern Africa": 140,
  "Gulf States": 180, "Levant": 110,
  "Oceania": 180,
};
const FLIGHT_FROM_US_BY_SUBREGION = {
  "Southeast Asia": 1100, "East Asia": 1200, "South Asia": 1200,
  "Western Europe": 750, "Southern Europe": 750, "Eastern Europe": 850, "Northern Europe": 900,
  "North America": 320, "South America": 750, "Caribbean": 500,
  "North Africa": 900, "East Africa": 1300, "Southern Africa": 1400,
  "Gulf States": 1100, "Levant": 1000,
  "Oceania": 1500,
};

// Per-person daily cost estimates for transport sub-options. Global averages —
// imprecise but honest. A train in Switzerland is $80, a train in Vietnam is
// $8; we average at ~$50. Worth revisiting if user feedback says we're
// systematically off for a particular region.
const TRANSPORT_DAILY_COST = {
  walking:    0,
  bike:       20,
  metro:      10,
  bus:        12,
  train:      50,
  rideshare:  55,
  taxi:       70,
  rental_car: 65,
};
// Daily cost estimates for activity types. These are per-person and assume
// a single activity per day in that category (not "all 13 activities every
// day"). Activities SUM because they don't substitute — a museum day and a
// food tour day are separate spends.
const ACTIVITY_DAILY_COST = {
  art:           25,
  concerts:      75,
  cooking:       70,
  food_tours:    55,
  hiking:        15,
  museums:       25,
  nightlife:     60,
  shopping:      45,
  spa:           90,
  sports:        80,
  walking_tours: 25,
  water:         70,
  wildlife:      85,
};

// Hotel-type cost multipliers applied to the per-night baseline.
// 1.0 = standard mix (default when nothing picked or 'economical' alone).
// Multi-select: take the LOWEST multiplier of picked types — when a user is
// "open to" budget OR boutique, they can fit the trip at the cheaper end,
// so destinations should match at that price.
const HOTEL_TYPE_COST_MULT = {
  budget:     0.50, // hostels, no-frills
  economical: 0.85, // comfortable 3-star, value brands
  boutique:   1.50, // stylish, character properties
  luxury:     2.40, // 5-star, resorts
};

function getHotelTypeMultiplier(hotelTypePicks) {
  if (!Array.isArray(hotelTypePicks) || hotelTypePicks.length === 0) return 1.0;
  const mults = hotelTypePicks.map(t => HOTEL_TYPE_COST_MULT[t]).filter(m => typeof m === "number");
  if (mults.length === 0) return 1.0;
  return Math.min(...mults);
}

// Layover-preference flight-cost multipliers. Distance-aware: the same
// "Direct only" pick means very different things on a 2h domestic hop vs a
// 14h long-haul. Premium for direct scales with route length; discount for
// "any layover" mostly only applies on long-haul where layovers are common.
// Multi-select takes the LOWEST multiplier (optimistic — user is flexible).
function getLayoverMultiplier(layoverPicks, dest = null, airport = null) {
  if (!Array.isArray(layoverPicks) || layoverPicks.length === 0) return 1.0;
  // Get the route's flight duration (one-way). Falls back to "medium" tier
  // when unknown. <=3h: short / 3-6h: medium / 6-10h: long / 10h+: very long.
  const hrs = (dest ? estimateFlightDurationHours(dest, airport) : null) || 6;
  function multForPick(pick) {
    if (pick === "no") {
      // Direct only — premium scales with route length
      if (hrs <= 3) return 1.05;
      if (hrs <= 6) return 1.10;
      if (hrs <= 10) return 1.25;
      return 1.40;
    }
    if (pick === "short") return 1.00; // neutral baseline regardless of distance
    if (pick === "yes") {
      // Any layover OK — discount only meaningful on long routes
      if (hrs <= 6) return 0.98;
      return 0.92;
    }
    return 1.0;
  }
  const mults = layoverPicks.map(multForPick);
  return Math.min(...mults);
}

// Compute daily on-ground cost (per person) from picked transport + activity
// sub-options. Transport uses MAX + 30% of remaining sum (primary mode plus
// occasional spillover); activities SUM straight. When `transportPicks` is
// an empty array but transport was included as a parent toggle, defaults
// to a "low estimate" floor of $20/day. Same for activities ($30/day floor).
// Returns null when nothing was specified (caller should fall back to
// dest.minBudget legacy behavior).
function getOnGroundDailyCost(transportPicks, activityPicks, includeTransport, includeActivities) {
  if (!includeTransport && !includeActivities) return null;
  let total = 0;
  if (includeTransport) {
    if (transportPicks && transportPicks.length > 0) {
      const costs = transportPicks.map(t => TRANSPORT_DAILY_COST[t] || 0).filter(c => c > 0).sort((a, b) => b - a);
      if (costs.length === 0) total += 0;
      else if (costs.length === 1) total += costs[0];
      else total += costs[0] + Math.round(costs.slice(1).reduce((s, c) => s + c, 0) * 0.30);
    } else {
      total += 20; // low-estimate floor when transport included but unspecified
    }
  }
  if (includeActivities) {
    if (activityPicks && activityPicks.length > 0) {
      // Activities use MAX + 30% spillover (same model as transport).
      // A realistic trip day has ONE main activity with smaller things
      // sprinkled in — not all 5 picked activities happening daily.
      // SUM was overcounting: 5 activities (25+25+25+70+55) = $200/day,
      // which pushed every destination over budget. With MAX+spillover:
      // 70 + 0.30 × (25+25+25+55) = ~$109/day, much more realistic.
      const costs = activityPicks.map(a => ACTIVITY_DAILY_COST[a] || 0).filter(c => c > 0).sort((a, b) => b - a);
      if (costs.length === 0) total += 0;
      else if (costs.length === 1) total += costs[0];
      else total += costs[0] + Math.round(costs.slice(1).reduce((s, c) => s + c, 0) * 0.30);
    } else {
      total += 30; // low-estimate floor when activities included but unspecified
    }
  }
  // Add a flat $25/day baseline for food regardless of category picks —
  // food is implicit in any trip and isn't captured by transport/activity.
  total += 25;
  return total;
}

// Typical one-way flight duration (hours) from US East Coast to subregion,
// including a realistic connection allowance for common routes (no nonstops
// to many destinations from non-major hubs). Used for the "long flight" tag
// on Explore cards when ≥8h. Round numbers — order-of-magnitude honest.
const FLIGHT_DURATION_FROM_US_BY_SUBREGION = {
  "Southeast Asia": 22, "East Asia": 16, "South Asia": 18,
  "Western Europe": 8, "Southern Europe": 9, "Eastern Europe": 10, "Northern Europe": 8,
  "North America": 4, "South America": 9, "Caribbean": 5,
  "North Africa": 11, "East Africa": 15, "Southern Africa": 18,
  "Gulf States": 13, "Levant": 12,
  "Oceania": 22,
};

// ── Amtrak train trips ──
// Curated American rail journeys for the Explore page. Two tiers:
// - "great_route": multi-day scenic / vacation-style routes (Coast Starlight,
//   California Zephyr, etc.)
// - "easy_connection": practical short/medium hops (NEC corridor, etc.)
// Prices are typical baseline coach (with some advance booking) and roomette
// where overnight class matters. Actual fares vary ±30% on Amtrak.com by date
// and demand — surface a caveat in the UI.
const TRAIN_TRIPS = [
  // ── Great Routes (multi-day, experience-led) ──
  {
    id: "coast-starlight",
    tier: "great_route",
    name: "Coast Starlight",
    route: "Los Angeles → Seattle",
    duration: "1 night, ~35 hours",
    coachFrom: 120,
    roometteFrom: 750,
    highlights: [
      "Pacific coast views from Santa Barbara to San Luis Obispo",
      "Cascade Range and Mt. Shasta from your window",
      "Sightseer Lounge with floor-to-ceiling windows",
    ],
    stations: "LAX Union Station · Oakland · Portland · Seattle",
  },
  {
    id: "california-zephyr",
    tier: "great_route",
    name: "California Zephyr",
    route: "Chicago → San Francisco (Emeryville)",
    duration: "2 nights, ~52 hours",
    coachFrom: 180,
    roometteFrom: 1100,
    highlights: [
      "Colorado Rockies in full daylight — Glenwood Canyon, Ruby Canyon",
      "Sierra Nevada crossing at Donner Pass",
      "Often called the most scenic train ride in America",
    ],
    stations: "Chicago Union · Denver · Salt Lake · Reno · Emeryville",
  },
  {
    id: "empire-builder",
    tier: "great_route",
    name: "Empire Builder",
    route: "Chicago → Seattle / Portland",
    duration: "2 nights, ~46 hours",
    coachFrom: 165,
    roometteFrom: 900,
    highlights: [
      "Glacier National Park edge through Montana (East/West Glacier stations)",
      "Cascade Tunnel — second-longest in North America",
      "Splits into two trains at Spokane (Seattle + Portland)",
    ],
    stations: "Chicago · Minneapolis · Glacier Park · Spokane · Seattle/Portland",
  },
  {
    id: "crescent",
    tier: "great_route",
    name: "Crescent",
    route: "New York → New Orleans",
    duration: "1 night, ~30 hours",
    coachFrom: 130,
    roometteFrom: 650,
    highlights: [
      "Stops in Charlottesville, Atlanta, Birmingham along the way",
      "Crosses the Blue Ridge in northern Virginia",
      "Wakes you up in the Deep South — Mississippi marshlands at dawn",
    ],
    stations: "NY Penn · DC · Charlottesville · Atlanta · Birmingham · NOLA",
  },
  {
    id: "southwest-chief",
    tier: "great_route",
    name: "Southwest Chief",
    route: "Chicago → Los Angeles",
    duration: "2 nights, ~43 hours",
    coachFrom: 170,
    roometteFrom: 950,
    highlights: [
      "New Mexico and Arizona high desert in golden light",
      "Crosses Raton Pass — once the toughest stretch of the Santa Fe Trail",
      "Stops at Lamy (the historic gateway to Santa Fe)",
    ],
    stations: "Chicago · Kansas City · Albuquerque · Flagstaff · LA",
  },
  {
    id: "sunset-limited",
    tier: "great_route",
    name: "Sunset Limited",
    route: "New Orleans → Los Angeles",
    duration: "2 nights, ~48 hours",
    coachFrom: 175,
    roometteFrom: 1000,
    highlights: [
      "Only transcontinental route across the southern tier",
      "Crosses Texas Hill Country, West Texas plains, the Sonoran Desert",
      "Houston, San Antonio, Tucson stops along the way",
    ],
    stations: "NOLA · Houston · San Antonio · El Paso · Tucson · LA",
  },
  // ── Easy Connections (practical short/medium hops) ──
  {
    id: "acela-nyc-dc",
    tier: "easy_connection",
    name: "Acela Express",
    route: "New York → Washington DC",
    duration: "~2h 45m",
    coachFrom: 75,
    businessFrom: 165,
    highlights: [
      "Top US speed of 150mph between Baltimore and DC",
      "Drops you in the heart of each city — no airport transfer",
      "Power outlets, wifi, quiet car option",
    ],
    stations: "NY Penn / Moynihan · Philadelphia · Baltimore · DC Union",
  },
  {
    id: "northeast-regional-nyc-bos",
    tier: "easy_connection",
    name: "Northeast Regional",
    route: "New York → Boston",
    duration: "~4h",
    coachFrom: 50,
    businessFrom: 90,
    highlights: [
      "Cheaper than Acela, more frequent departures",
      "Coastal views through Connecticut and Rhode Island",
      "Lands at Boston South Station — short T ride to anywhere",
    ],
    stations: "NY Penn / Moynihan · New Haven · Providence · Boston South",
  },
  {
    id: "hiawatha-chi-mke",
    tier: "easy_connection",
    name: "Hiawatha Service",
    route: "Chicago → Milwaukee",
    duration: "~1h 30m",
    coachFrom: 25,
    businessFrom: 45,
    highlights: [
      "Seven daily round-trips — most frequent service in the Midwest",
      "Great weekend trip — Milwaukee breweries, lakefront, Summerfest",
      "No security line, no checked bags, just board and ride",
    ],
    stations: "Chicago Union · Milwaukee Intermodal",
  },
  {
    id: "pacific-surfliner",
    tier: "easy_connection",
    name: "Pacific Surfliner",
    route: "Los Angeles → San Diego",
    duration: "~3h",
    coachFrom: 38,
    businessFrom: 65,
    highlights: [
      "Stretches of track run within feet of the Pacific surf",
      "Stops at San Juan Capistrano, Oceanside, Solana Beach",
      "Bike-friendly cars — bring yours along for coastal rides",
    ],
    stations: "LA Union · Anaheim · San Juan Capistrano · Oceanside · San Diego",
  },
  {
    id: "cascades-sea-pdx",
    tier: "easy_connection",
    name: "Amtrak Cascades",
    route: "Seattle → Portland",
    duration: "~3h 45m",
    coachFrom: 35,
    businessFrom: 60,
    highlights: [
      "Puget Sound views through Tacoma, Olympia, southern Washington",
      "Climate-controlled Spanish-built Talgo trains, very comfortable",
      "Onboard bistro car with local craft beer and coffee",
    ],
    stations: "Seattle King Street · Tacoma · Olympia · Vancouver WA · Portland",
  },
  {
    id: "vermonter",
    tier: "easy_connection",
    name: "Vermonter",
    route: "New York → Vermont (St. Albans)",
    duration: "~10h to Burlington area",
    coachFrom: 80,
    businessFrom: 130,
    highlights: [
      "Daily service into the heart of New England",
      "Fall foliage country — book October if you want peak color",
      "Stops at Brattleboro, Montpelier, Burlington-Essex Junction",
    ],
    stations: "NY Penn · Springfield · Brattleboro · Montpelier · Essex Junction",
  },
];

// ── Identity → TMA transport defaults ──
// Maps each of the 20 TRAVEL_IDENTITIES to sensible transport types based
// on how that archetype travels. The vibes are already on the identity
// itself; this fills the only missing piece needed to apply an identity
// as a TMA preset.
const IDENTITY_TRANSPORT_DEFAULTS = {
  "Romantic Wanderer":         ["walking", "rideshare"],
  "Controlled Chaos Traveler": ["metro", "walking", "rideshare"],
  "Culinary Explorer":         ["walking", "metro", "rideshare"],
  "Luxury Escapist":           ["rideshare", "taxi"],
  "Adrenaline Collector":      ["rental_car"],
  "Soft-Life Traveler":        ["rideshare", "walking"],
  "Cultural Nomad":            ["metro", "walking"],
  "Spontaneous Minimalist":    ["walking", "metro", "bus"],
  "Off-Grid Dreamer":          ["rental_car"],
  "Sunset Chaser":             ["rideshare", "walking"],
  "Digital Nomad":             ["metro", "walking"],
  "Heritage Seeker":           ["walking", "metro"],
  "Eco Wanderer":              ["walking", "bike", "metro"],
  "Festival Hunter":           ["rideshare", "walking"],
  "Photo Pilgrim":             ["rental_car", "walking"],
  "Wellness Pilgrim":          ["walking", "rideshare"],
  "Bookworm Wanderer":         ["walking", "metro"],
  "Family Architect":          ["rental_car"],
  "Solo Soul-Searcher":        ["walking", "metro"],
  "Night Owl Nomad":           ["rideshare", "walking"],
};

// Returns the user's identity + up to (n-1) other identities most similar
// to it (by shared-vibe count). Used to populate the TMA Quick Start row.
// If userIdentity is null, returns 5 generic popular ones.
function getTopIdentitiesForUser(userIdentity, n = 5) {
  const allIds = Object.keys(TRAVEL_IDENTITIES);
  if (!userIdentity || !TRAVEL_IDENTITIES[userIdentity]) {
    // No identity yet — show 5 broad/popular ones as a sampler
    return ["Cultural Nomad", "Adrenaline Collector", "Romantic Wanderer", "Wellness Pilgrim", "Family Architect"];
  }
  const myVibes = new Set(TRAVEL_IDENTITIES[userIdentity].vibes || []);
  const scored = allIds
    .filter(id => id !== userIdentity)
    .map(id => {
      const theirVibes = TRAVEL_IDENTITIES[id].vibes || [];
      const shared = theirVibes.filter(v => myVibes.has(v)).length;
      return { id, shared };
    })
    .sort((a, b) => b.shared - a.shared)
    .slice(0, n - 1)
    .map(x => x.id);
  return [userIdentity, ...scored];
}


// when coordinates available (distance/500mph cruise + 1.5h taxi/ground),
// otherwise falls back to subregion average. Returns null if neither path
// works (e.g. unknown destination without subregion mapping).
function estimateFlightDurationHours(dest, airport) {
  if (!dest) return null;
  // Try distance-based first
  if (airport && dest.name) {
    const origin = (typeof MAJOR_AIRPORTS !== "undefined") ? MAJOR_AIRPORTS.find(a => a.code === airport) : null;
    const coords = DEST_COORDS[dest.name];
    if (origin && coords) {
      const mi = distanceMiles(origin.lat, origin.lng, coords.lat, coords.lng);
      // 500mph cruise + 1.5h padding for taxi/connection
      return Math.round(mi / 500 + 1.5);
    }
  }
  // Fallback by subregion
  return FLIGHT_DURATION_FROM_US_BY_SUBREGION[dest.subRegion] || null;
}

// Returns realistic round-trip flight cost. Prefers distance-based
// estimate (accounts for actual origin city + season) when airport set,
// falls back to regional average for anonymous users.
function getBaselineFlightCost(dest, airport, dateStr = null) {
  if (airport && dest?.name) {
    const est = estimateFlightPrice(airport, dest.name, dateStr);
    if (est) return est;
  }
  return FLIGHT_FROM_US_BY_SUBREGION[dest?.subRegion] || 750;
}

// Returns full trip cost: flight + hotel + on-ground floor, scaled by group
// size using the same model as the booking estimate (QuickBookModal):
//   - flights multiply per-person (everyone needs a seat)
//   - hotel is mostly shared — one room holds up to ~2 people; larger groups
//     need a second room, so we add 50% of the room cost per pair beyond 2
//   - on-ground (food/activities) is partly per-person
// Single source of truth for "is this destination within the user's budget?"
// `travelers` defaults to 1 so existing solo call sites are unchanged.
//
// `includes` (Design 2, May 2026): an array of cost categories the user's
// budget should cover. Categories: "air", "hotel", "transport", "activities".
// When null or empty, defaults to all four (= original behavior, backward
// compatible with call sites that don't pass it). When set, only the listed
// components contribute to the returned total. This lets users budget for
// "flight only" or "hotel only" and see destinations whose THAT component
// fits — matching the UX expectation that toggling off a category in the
// "Includes" picker actually relaxes the filter.
function getBaselineTripCost(dest, airport, days = 5, travelers = 1, includes = null, transportPicks = null, activityPicks = null, hotelTypePicks = null, layoverPicks = null) {
  if (!dest) return null;
  const pax = Math.max(1, travelers || 1);
  const baseFlight = getBaselineFlightCost(dest, airport);
  // Layover preference: direct premium and any-layover discount both scale
  // with route distance (short hop vs long-haul). Multi-select takes the
  // lowest multiplier (optimistic — user is flexible).
  const flight = baseFlight * getLayoverMultiplier(layoverPicks, dest, airport);
  const nightly = dest.hotelNightlyOverride || HOTEL_NIGHTLY_BY_SUBREGION[dest.subRegion] || 110;
  const onGround = dest.minBudget || 0; // food, transit, activities baseline (legacy)
  // Hotel: 1 room for 1-2 people, then +1 room per additional pair.
  // Apply hotel-type multiplier so 'luxury' / 'boutique' / 'economical' /
  // 'budget' picks actually shift the cost (and therefore the match count).
  const rooms = Math.ceil(pax / 2);
  const hotelMult = getHotelTypeMultiplier(hotelTypePicks);
  const hotelTotal = nightly * days * rooms * hotelMult;
  // On-ground floor (legacy): ~half scales with headcount (food/activities),
  // rest stays flat. Used when sub-option picks aren't passed.
  const onGroundTotal = Math.round(onGround * 0.5) + Math.round(onGround * 0.25 * (pax - 1));
  // Resolve includes — null/empty falls back to "everything" (legacy behavior)
  const inc = (Array.isArray(includes) && includes.length > 0)
    ? includes
    : ["air", "hotel", "transport", "activities"];
  const wantsFlight     = inc.includes("air");
  const wantsHotel      = inc.includes("hotel");
  const wantsTransport  = inc.includes("transport");
  const wantsActivities = inc.includes("activities");
  const wantsOnGround   = wantsTransport || wantsActivities;
  const flightPart = wantsFlight ? (flight * pax) : 0;
  const hotelPart  = wantsHotel  ? hotelTotal     : 0;
  // On-ground: prefer the sub-option-aware daily cost when transport/activity
  // picks were passed; otherwise fall back to dest.minBudget legacy floor.
  let onGroundPart = 0;
  if (wantsOnGround) {
    const dailyOnGround = (transportPicks !== null || activityPicks !== null)
      ? getOnGroundDailyCost(transportPicks, activityPicks, wantsTransport, wantsActivities)
      : null;
    if (dailyOnGround !== null) {
      onGroundPart = dailyOnGround * days * pax;
    } else {
      onGroundPart = onGroundTotal;
    }
  }
  return flightPart + hotelPart + onGroundPart;
}

async function fetchFlightPrice(originCode, destName, dateStr) {
  const hasAmadeusKey = (typeof process !== "undefined" && process.env?.REACT_APP_AMADEUS_KEY) || window.__AMADEUS_KEY__;
  // Try live Amadeus first
  if (hasAmadeusKey && originCode && destName) {
    const destCode = DEST_IATA[destName];
    if (destCode) {
      try {
        const token = await fetchAmadeusToken();
        if (token) {
          const date = dateStr || (() => {
            const d = new Date(); d.setDate(d.getDate() + 60);
            return d.toISOString().split("T")[0];
          })();
          const r = await fetch(
            `https://test.api.amadeus.com/v2/shopping/flight-offers?originLocationCode=${originCode}&destinationLocationCode=${destCode}&departureDate=${date}&adults=1&nonStop=false&max=1&currencyCode=USD`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const d = await r.json();
          const price = d?.data?.[0]?.price?.grandTotal;
          if (price) return Math.round(parseFloat(price));
        }
      } catch(e) { /* fall through to estimate */ }
    }
  }
  // Fallback: distance-based estimate
  return estimateFlightPrice(originCode, destName, dateStr);
}

// ── localStorage cleanup helpers ──────────────────────────────
// Called at auth transitions to prevent stale state from one session bleeding
// into another. Three transition points:
//
//   - Register success → clear ANONYMOUS state (the user just signed up;
//     anonymous quiz answers / identity from the pre-signup flow have served
//     their purpose and shouldn't override the backend's stored values)
//
//   - Login success → clear ANONYMOUS state only. We do NOT clear synced
//     data (boards, trips, journal, itineraries) because hydration logic
//     handles merge correctly. Clearing them would mean losing offline-only
//     work that was never synced.
//
//   - Logout → clear EVERYTHING jga-* except cosmetic prefs. The user
//     explicitly ended their session; subsequent logins on the same browser
//     should start clean (fresh hydration from backend).
//
// We never clear `jga-theme` or other UI prefs — those are device-level,
// not session-level.
const _ANONYMOUS_KEYS = [
  "jga-travelIdentity",  // anonymous quiz result (pre-signup)
  "jga-quizAnswers",     // anonymous quiz responses
  "jga-pendingIdentity", // any flow-in-progress identity
  "jga-pendingQuiz",     // any flow-in-progress quiz
];
const _SESSION_DATA_KEYS = [
  "jga_token",
  "jga_refresh_token",
  "jga-user",
  "jga-boards",
  "jga-boards-initialized", // tracks whether backend ever had boards for this user
  "jga-past-trips",
  "jga-itineraries",
  "jga-journal-entries",
  "jga-journal-photos",
  "jga-journal-ratings",
  "jga-journal-meta",
  "jga-trip-alerts",       // per-destination alert configs (localStorage-only for now)
  "jga-starred-alerts",   // legacy pre-boards key
  "jga-watchlist",        // legacy
  "jga-passport",         // pre-encryption legacy local copy if any
];
const _PRESERVED_KEYS = new Set([
  "jga-theme",            // user's dark/light preference (device-level)
  "jga-weather-cache-v2", // location/weather cache (not session-bound)
  // Tombstones survive logout — they encode the user's deletion intent and
  // need to outlive a logout/login cycle. Otherwise: user deletes trip on
  // device A, logs out, logs back in → tombstone gone → trip resurrects
  // from backend if DELETE call hadn't propagated. The next successful
  // hydrate clears stale tombstones (codes backend has already deleted).
  "jga-deleted-trips",
  "jga-deleted-journals",
]);

function _clearAnonymousState(reason = "unknown") {
  let cleared = 0;
  for (const k of _ANONYMOUS_KEYS) {
    try { if (localStorage.getItem(k) !== null) { localStorage.removeItem(k); cleared++; } } catch {}
  }
  if (cleared > 0) console.log(`[JGA Storage] Cleared ${cleared} anonymous keys (reason: ${reason})`);
}

function _clearAllJgaState(reason = "logout") {
  let cleared = 0;
  // Clear known anonymous + session keys
  for (const k of [..._ANONYMOUS_KEYS, ..._SESSION_DATA_KEYS]) {
    try { if (localStorage.getItem(k) !== null) { localStorage.removeItem(k); cleared++; } } catch {}
  }
  // Sweep any other jga-* keys we may have missed (defensive)
  try {
    const allKeys = Object.keys(localStorage);
    for (const k of allKeys) {
      if (k.startsWith("jga") && !_PRESERVED_KEYS.has(k)) {
        const wasSet = localStorage.getItem(k) !== null;
        try { localStorage.removeItem(k); if (wasSet) cleared++; } catch {}
      }
    }
  } catch {}
  console.log(`[JGA Storage] Cleared ${cleared} keys total (reason: ${reason})`);
}

// ── Itinerary backend sync ─────────────────────────────────────
// Single source of truth for /api/itineraries CRUD. Auth required.
// Returns { ok, id?, status?, error? } from save; { ok, items? } from load;
// { ok } from delete. localStorage is treated as a write-through cache so
// the UI stays snappy even on slow networks.
async function saveItineraryToBackend({ destination, days, itinerary_data, token }) {
  if (!token) {
    console.warn("[JGA Itinerary Save] No auth token — skipping backend sync (will retry on next login)");
    return { ok: false, error: "Not authenticated" };
  }
  if (!destination || !itinerary_data) {
    console.warn("[JGA Itinerary Save] Missing destination or itinerary_data — skipping");
    return { ok: false, error: "Missing required fields" };
  }
  try {
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    const res = await fetch(`${API}/api/itineraries`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ destination, days: days || 7, itinerary_data }),
    });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      console.log("[JGA Itinerary Save] ✓ Saved to backend, id:", body.id, "destination:", destination);
      return { ok: true, id: body.id };
    }
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) { _signalSessionExpired("Itinerary Save"); return { ok: false, status: 401 }; }
    console.error("[JGA Itinerary Save] Backend rejected", res.status, body);
    return { ok: false, status: res.status, error: body?.error || `HTTP ${res.status}` };
  } catch (err) {
    console.error("[JGA Itinerary Save] Network error", err);
    return { ok: false, error: "Network error" };
  }
}

async function loadItinerariesFromBackend(token) {
  if (!token) return { ok: false, items: [] };
  try {
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    const res = await fetch(`${API}/api/itineraries`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) {
      const items = await res.json().catch(() => []);
      console.log("[JGA Itinerary Load] ✓ Loaded", Array.isArray(items) ? items.length : 0, "from backend");
      return { ok: true, items: Array.isArray(items) ? items : [] };
    }
    if (res.status === 401) { _signalSessionExpired("Itinerary Load"); return { ok: false, status: 401 }; }
    console.error("[JGA Itinerary Load] Backend rejected", res.status);
    return { ok: false, items: [] };
  } catch (err) {
    console.error("[JGA Itinerary Load] Network error", err);
    return { ok: false, items: [] };
  }
}

async function deleteItineraryOnBackend(id, token) {
  if (!token || !id) return { ok: false };
  try {
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    const res = await fetch(`${API}/api/itineraries/${id}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) {
      console.log("[JGA Itinerary Delete] ✓ Deleted, id:", id);
      return { ok: true };
    }
    if (res.status === 401) { _signalSessionExpired("Itinerary Delete"); return { ok: false, status: 401 }; }
    console.error("[JGA Itinerary Delete] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Itinerary Delete] Network error", err);
    return { ok: false };
  }
}

// ── Swipe Skips cross-device sync ──────────────────────────────────────
// Skip state ({ destName: timestamp }) was previously localStorage-only,
// causing users to see the same destinations on Desktop/Mobile because
// each device had its own independent skip history. These helpers add
// backend persistence so skips travel with the user account.
//
// GUESSED CONTRACT (backend TODO — verify in backend chat):
//   GET  /api/swipe-skips  → { skips: { destName: timestamp, ... } }
//   POST /api/swipe-skips  → body: { skips: {...} } → 200 ok
//
// Design notes:
// 1. localStorage remains the primary persistence layer for offline / fast
//    boot. Backend is a "merge with peers" layer that runs after login.
// 2. Merge strategy: per-destination, keep the newer timestamp. This is
//    important because the same destination might be skipped on two devices
//    independently — we don't want either skip to be lost.
// 3. 404 (endpoint not yet built on backend) is silently swallowed — the
//    feature gracefully degrades to localStorage-only until backend exists.
//    Safe to ship before backend is ready.
// 4. Errors never disturb the user. Skip behavior keeps working purely from
//    localStorage even if every network call fails.

// Merges two skip maps preserving the newer timestamp for each destination.
function mergeSwipeSkips(a, b) {
  const out = { ...(a || {}) };
  for (const [dest, ts] of Object.entries(b || {})) {
    if (typeof ts !== "number") continue;
    if (!out[dest] || ts > out[dest]) out[dest] = ts;
  }
  return out;
}

async function loadSwipeSkipsFromBackend(token) {
  if (!token) return { ok: false, skips: {} };
  try {
    const res = await fetch(`${_api()}/api/swipe-skips`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      const skips = (body && body.skips && typeof body.skips === "object") ? body.skips : {};
      console.log("[JGA Skips Load] ✓ Loaded", Object.keys(skips).length, "skips from backend");
      return { ok: true, skips };
    }
    if (res.status === 404) {
      // Endpoint doesn't exist yet on backend — silently degrade. Common during
      // the deploy window where frontend ships ahead of backend.
      console.log("[JGA Skips Load] Backend endpoint not available (404) — using localStorage only");
      return { ok: false, skips: {}, status: 404 };
    }
    if (res.status === 401) { _signalSessionExpired("loadSwipeSkipsFromBackend"); return { ok: false, skips: {}, status: 401 }; }
    console.warn("[JGA Skips Load] Backend rejected", res.status);
    return { ok: false, skips: {} };
  } catch (err) {
    // Network error — silently degrade. localStorage remains source of truth.
    console.warn("[JGA Skips Load] Network error (degrading to localStorage)", err?.message);
    return { ok: false, skips: {} };
  }
}

async function saveSwipeSkipsToBackend(skips, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/swipe-skips`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify({ skips: skips || {} }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 404) {
      // Backend hasn't shipped the endpoint yet — fine, localStorage covers us.
      return { ok: false, status: 404 };
    }
    if (res.status === 401) { _signalSessionExpired("saveSwipeSkipsToBackend"); return { ok: false, status: 401 }; }
    console.warn("[JGA Skips Save] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.warn("[JGA Skips Save] Network error (queued for next attempt)", err?.message);
    return { ok: false };
  }
}

// ── Stamps backend sync ───────────────────────────────────────────
// Cross-device persistence for the Stamps collection (Drop 1 was
// localStorage-only). Same pattern as swipe-skips: load on auth,
// debounced save on change, 404 silently degrades to localStorage-only.
//
// GUESSED CONTRACT (backend TODO — verify in backend chat):
//   GET  /api/stamps  → { stamps: { stampId: { state, createdAt, updatedAt, visitedAt?, visitedNote?, plannedFor? }, ... } }
//   POST /api/stamps  → body: { stamps: {...} } → 200 ok
//   All require Authorization: Bearer <JWT>.
//
// Merge strategy: union with timestamp tiebreak. Per stampId, keep the
// entry whose updatedAt is newer. Append-mostly — additions on one device
// never delete entries that exist on another. Deletes require explicit
// user action from the Profile tab (which writes the deletion timestamp,
// so it wins the tiebreak).

function mergeStamps(a, b) {
  const out = { ...(a || {}) };
  for (const [id, entry] of Object.entries(b || {})) {
    if (!entry || typeof entry !== "object") continue;
    const existing = out[id];
    if (!existing) { out[id] = entry; continue; }
    const aTs = existing.updatedAt || existing.createdAt || 0;
    const bTs = entry.updatedAt || entry.createdAt || 0;
    if (bTs > aTs) out[id] = entry;
  }
  return out;
}

async function loadStampsFromBackend(token) {
  if (!token) return { ok: false, stamps: {} };
  try {
    const res = await fetch(`${_api()}/api/stamps`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      const stamps = (body && body.stamps && typeof body.stamps === "object") ? body.stamps : {};
      console.log("[JGA Stamps Load] ✓ Loaded", Object.keys(stamps).length, "stamps from backend");
      return { ok: true, stamps };
    }
    if (res.status === 404) {
      console.log("[JGA Stamps Load] Backend endpoint not available (404) — using localStorage only");
      return { ok: false, stamps: {}, status: 404 };
    }
    if (res.status === 401) { _signalSessionExpired("loadStampsFromBackend"); return { ok: false, stamps: {}, status: 401 }; }
    console.warn("[JGA Stamps Load] Backend rejected", res.status);
    return { ok: false, stamps: {} };
  } catch (err) {
    console.warn("[JGA Stamps Load] Network error (degrading to localStorage)", err?.message);
    return { ok: false, stamps: {} };
  }
}

async function saveStampsToBackend(stamps, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/stamps`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify({ stamps: stamps || {} }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 404) return { ok: false, status: 404 };
    if (res.status === 401) { _signalSessionExpired("saveStampsToBackend"); return { ok: false, status: 401 }; }
    console.warn("[JGA Stamps Save] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.warn("[JGA Stamps Save] Network error (queued for next attempt)", err?.message);
    return { ok: false };
  }
}

// ── Postcards backend sync ────────────────────────────────────────
// Cross-device persistence for the Postcards collection. Same shape and
// pattern as Stamps above — only the endpoint and storage key differ.
//
// GUESSED CONTRACT (backend TODO — verify in backend chat):
//   GET  /api/postcards  → { postcards: { postcardId: { state, createdAt, updatedAt, visitedAt?, visitedNote?, plannedFor? }, ... } }
//   POST /api/postcards  → body: { postcards: {...} } → 200 ok
//   All require Authorization: Bearer <JWT>.

function mergePostcards(a, b) {
  const out = { ...(a || {}) };
  for (const [id, entry] of Object.entries(b || {})) {
    if (!entry || typeof entry !== "object") continue;
    const existing = out[id];
    if (!existing) { out[id] = entry; continue; }
    const aTs = existing.updatedAt || existing.createdAt || 0;
    const bTs = entry.updatedAt || entry.createdAt || 0;
    if (bTs > aTs) out[id] = entry;
  }
  return out;
}

async function loadPostcardsFromBackend(token) {
  if (!token) return { ok: false, postcards: {} };
  try {
    const res = await fetch(`${_api()}/api/postcards`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      const postcards = (body && body.postcards && typeof body.postcards === "object") ? body.postcards : {};
      console.log("[JGA Postcards Load] ✓ Loaded", Object.keys(postcards).length, "postcards from backend");
      return { ok: true, postcards };
    }
    if (res.status === 404) {
      console.log("[JGA Postcards Load] Backend endpoint not available (404) — using localStorage only");
      return { ok: false, postcards: {}, status: 404 };
    }
    if (res.status === 401) { _signalSessionExpired("loadPostcardsFromBackend"); return { ok: false, postcards: {}, status: 401 }; }
    console.warn("[JGA Postcards Load] Backend rejected", res.status);
    return { ok: false, postcards: {} };
  } catch (err) {
    console.warn("[JGA Postcards Load] Network error (degrading to localStorage)", err?.message);
    return { ok: false, postcards: {} };
  }
}

async function savePostcardsToBackend(postcards, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/postcards`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify({ postcards: postcards || {} }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 404) return { ok: false, status: 404 };
    if (res.status === 401) { _signalSessionExpired("savePostcardsToBackend"); return { ok: false, status: 401 }; }
    console.warn("[JGA Postcards Save] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.warn("[JGA Postcards Save] Network error (queued for next attempt)", err?.message);
    return { ok: false };
  }
}

// ── Boards backend sync ────────────────────────────────────────
// GUESSED CONTRACT (verify with backend chat if anything 4xx's):
//   POST   /api/boards            → { name, emoji, color }                → { id, name, emoji, color, items: [] }
//   GET    /api/boards            → [ { id, name, emoji, color, items: [...] } ]
//   PATCH  /api/boards/:id        → partial update
//   DELETE /api/boards/:id        → 200 ok
//   POST   /api/boards/:id/items  → { destName }                          → 200 ok
//   DELETE /api/boards/:id/items/:destName → 200 ok
// All require Authorization: Bearer <JWT>.
// If backend uses different field names (e.g. items_json vs items, or
// destination_name vs destName), the console errors will tell us and we'll patch.
// ── Backend API base URL ──────────────────────────────────────
// Backend hosted at api.justgoanywhere.com (custom Railway domain).
// Same eTLD+1 as the frontend (justgoanywhere.com), which makes the
// refresh cookie a FIRST-PARTY cookie — never blocked by browsers'
// third-party cookie restrictions (incognito, iOS Safari ITP, etc.).
//
// IMPORTANT: Every fetch to this base URL MUST include
// `credentials: "include"` in its options object. Without it:
//   - The httpOnly refresh cookie is not sent on requests
//   - Set-Cookie response headers are silently dropped by the browser
//   - Refresh-on-expiry never works → users get 401-bombed every
//     ~15 minutes (token TTL) and can't recover without re-login
// Public endpoints still need credentials: include if they SET cookies
// (e.g. /api/auth/login).
//
// Fallback URL is the new subdomain. The legacy Railway URL
// (jga-backend-production.up.railway.app) is still served by Railway
// as a redundant fallback during the cutover, but the frontend should
// always use the custom-domain URL so cookies stay first-party.
function _api() {
  return (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
}
function _authHeaders(token) {
  return { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
}

// ── Session-expired signal ────────────────────────────────────────
// API helpers can't directly setState. Instead, when any helper sees a
// 401, it calls _signalSessionExpired() which:
//   1. Clears auth tokens from localStorage so subsequent requests don't fire
//   2. Dispatches a `jga:session-expired` event the React component listens for
// The React component then tears down session UI and shows a recovery prompt.
//
// We dedupe — only fires once per session expiration (via a module-level flag)
// to prevent log spam if 30 in-flight requests all 401 simultaneously.
//
// GRACE PERIOD: during the first ~3 seconds after page load, we SUPPRESS
// session-expired signals. Why: on mobile Safari, the access token in
// localStorage may be expired but the refresh token cookie is still valid.
// On mount, the auth refresh effect fires after a 600ms delay and can renew
// the access token via /api/auth/refresh. But the data hydration loaders
// (boards, trips, journal, etc.) fire IMMEDIATELY on mount with the old
// stale access token, get 401, and previously killed the session before
// the refresh effect ever ran.
//
// Now during the grace window, helpers still return {ok: false, status: 401}
// to their callers (so loaders just don't have data), but the session-expired
// event isn't fired. This gives the refresh effect time to renew the token.
// When users next interact (or hydration retries naturally after token
// renewal), things work. If the refresh genuinely fails, that's the path
// that signals session-expired.
const _PAGE_LOAD_AT = (typeof Date !== "undefined") ? Date.now() : 0;
const _SESSION_EXPIRED_GRACE_MS = 3000;
let _sessionExpiredFired = false;
function _signalSessionExpired(source = "unknown") {
  if (_sessionExpiredFired) return;
  // Grace period: don't tear down the session during initial page-load auth churn.
  // The auth refresh effect needs ~600ms-2.5s to do its job; during that window
  // any data-loader 401 should be ignored (just leaves the loader's state empty
  // until refresh succeeds and a re-fetch happens).
  const elapsed = Date.now() - _PAGE_LOAD_AT;
  if (elapsed < _SESSION_EXPIRED_GRACE_MS) {
    console.log(`[JGA Auth] Suppressing session-expired from ${source} (within ${_SESSION_EXPIRED_GRACE_MS}ms grace period, elapsed=${elapsed}ms)`);
    return;
  }
  _sessionExpiredFired = true;
  console.warn(`[JGA Auth] Session expired — signaled from ${source}. Clearing tokens.`);
  try {
    localStorage.removeItem("jga_token");
    localStorage.removeItem("jga_refresh_token");
  } catch {}
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("jga:session-expired", { detail: { source } }));
    }
  } catch {}
}
// Reset the dedupe flag when a fresh login succeeds. Called by login/register
// success paths so a future expiration can re-signal.
function _resetSessionExpiredFlag() {
  _sessionExpiredFired = false;
}

async function loadBoardsFromBackend(token) {
  if (!token) return { ok: false, items: [] };
  try {
    const res = await fetch(`${_api()}/api/boards`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const items = await res.json().catch(() => []);
      console.log("[JGA Boards Load] ✓ Loaded", Array.isArray(items) ? items.length : 0, "boards from backend");
      return { ok: true, items: Array.isArray(items) ? items : [] };
    }
    if (res.status === 401) { _signalSessionExpired("loadBoardsFromBackend"); return { ok: false, items: [], status: 401 }; }
    console.error("[JGA Boards Load] Backend rejected", res.status);
    return { ok: false, items: [] };
  } catch (err) {
    console.error("[JGA Boards Load] Network error", err);
    return { ok: false, items: [] };
  }
}

async function createBoardOnBackend({ name, emoji, color }, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/boards`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify({ name, emoji, color }),
    });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      console.log("[JGA Boards Create] ✓ Created, id:", body.id, "name:", name);
      return { ok: true, id: body.id, board: body };
    }
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) { _signalSessionExpired("Boards Create"); return { ok: false, status: 401 }; }
    console.error("[JGA Boards Create] Backend rejected", res.status, body);
    return { ok: false, status: res.status, error: body?.error };
  } catch (err) {
    console.error("[JGA Boards Create] Network error", err);
    return { ok: false };
  }
}

async function updateBoardOnBackend(id, patch, token) {
  if (!token || !id) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/boards/${id}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "PATCH",
      headers: _authHeaders(token),
      body: JSON.stringify(patch),
    });
    if (res.ok) {
      console.log("[JGA Boards Update] ✓ Patched id:", id, "fields:", Object.keys(patch).join(","));
      return { ok: true };
    }
    if (res.status === 401) { _signalSessionExpired("Boards Update"); return { ok: false, status: 401 }; }
    console.error("[JGA Boards Update] Backend rejected", res.status);
    return { ok: false, status: res.status };
  } catch (err) {
    console.error("[JGA Boards Update] Network error", err);
    return { ok: false };
  }
}

async function deleteBoardOnBackend(id, token) {
  if (!token || !id) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/boards/${id}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) { console.log("[JGA Boards Delete] ✓ Deleted id:", id); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Boards Delete"); return { ok: false, status: 401 }; }
    console.error("[JGA Boards Delete] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Boards Delete] Network error", err);
    return { ok: false };
  }
}

async function addItemToBoardOnBackend(boardId, destName, token) {
  if (!token || !boardId || !destName) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/boards/${boardId}/items`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify({ destName }),
    });
    if (res.ok) { console.log("[JGA Boards Add Item] ✓ Added", destName, "to board", boardId); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Boards Add Item"); return { ok: false, status: 401 }; }
    console.error("[JGA Boards Add Item] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Boards Add Item] Network error", err);
    return { ok: false };
  }
}

async function removeItemFromBoardOnBackend(boardId, destName, token) {
  if (!token || !boardId || !destName) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/boards/${boardId}/items/${encodeURIComponent(destName)}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) { console.log("[JGA Boards Remove Item] ✓ Removed", destName, "from board", boardId); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Boards Remove Item"); return { ok: false, status: 401 }; }
    console.error("[JGA Boards Remove Item] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Boards Remove Item] Network error", err);
    return { ok: false };
  }
}

// ── Trips backend sync ────────────────────────────────────────
// CONTRACT (confirmed with backend chat):
//   GET    /api/trips             → array, ordered by depart_date,
//                                   includes both source='booking' and source='user'
//   POST   /api/trips             → body: trip fields, returns { id, ...rest }
//   PATCH  /api/trips/:id         → mutable fields only
//                                   FORBIDDEN: source, code, destination, totalCost,
//                                              includesInsurance
//                                   FORBIDDEN if status is completed/cancelled: status
//                                   400 on violation, 409 on duplicate code
//   DELETE /api/trips/:id         → hard for source='user', soft for source='booking'
// All require Authorization: Bearer <JWT>.
//
// Status reconciliation: backend stores 'confirmed' | 'pending' | 'completed' |
// 'cancelled'. Frontend display logic uses 'upcoming' | 'completed' | 'cancelled'.
// Use tripStatusForUI() / tripStatusForBackend() at the boundary.

// Backend → UI status. Booked trips ('confirmed') display as 'upcoming';
// 'pending' is preserved as its own UI state (shown in the "Pending — Being
// Curated" section of the Trips tab while Cas approves the trip / sources
// the booking). Other statuses fall through to 'completed'.
function tripStatusForUI(backendStatus) {
  if (backendStatus === "confirmed") return "upcoming";
  if (backendStatus === "pending") return "pending";
  return backendStatus || "completed";
}
// UI → Backend status. 'upcoming' on a user-logged trip means 'confirmed' for storage.
// (Backend's status field accepts the canonical values; frontend only ever sends a
// status when transitioning, e.g. cancelling a trip.)
function tripStatusForBackend(uiStatus, source = "user") {
  if (uiStatus === "upcoming") return source === "booking" ? "confirmed" : "confirmed";
  return uiStatus;
}

// Field-name reconciliation. PATCH/POST send the canonical names per backend
// docs; the backend accepts either the new (dateFrom) or legacy (departDate)
// names but we'll use the new ones for cleanliness.
// FORBIDDEN PATCH FIELDS — strip these at the API boundary so we never
// 400 on an accidental field that the user-marked-delayed flow set somewhere.
const PATCH_FORBIDDEN_FIELDS = new Set([
  "source", "code", "destination", "totalCost", "total_cost",
  "includesInsurance", "includes_insurance",
  // Defensive: never PATCH these even though they're not officially forbidden
  "id", "createdAt", "updatedAt", "userId", "user_id",
]);

async function loadTripsFromBackend(token) {
  if (!token) return { ok: false, items: [] };
  try {
    const res = await fetch(`${_api()}/api/trips`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const items = await res.json().catch(() => []);
      console.log("[JGA Trips Load] ✓ Loaded", Array.isArray(items) ? items.length : 0, "trips from backend");
      return { ok: true, items: Array.isArray(items) ? items : [] };
    }
    if (res.status === 401) { _signalSessionExpired("loadTripsFromBackend"); return { ok: false, items: [], status: 401 }; }
    console.error("[JGA Trips Load] Backend rejected", res.status);
    return { ok: false, items: [] };
  } catch (err) {
    console.error("[JGA Trips Load] Network error", err);
    return { ok: false, items: [] };
  }
}

async function createTripOnBackend(trip, token) {
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/trips`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: _authHeaders(token),
      body: JSON.stringify(trip),
    });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      console.log("[JGA Trips Create] ✓ Created, id:", body.id, "destination:", trip.destination);
      return { ok: true, id: body.id, trip: body };
    }
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) { _signalSessionExpired("Trips Create"); return { ok: false, status: 401 }; }
    console.error("[JGA Trips Create] Backend rejected", res.status, body);
    return { ok: false, status: res.status };
  } catch (err) {
    console.error("[JGA Trips Create] Network error", err);
    return { ok: false };
  }
}

async function updateTripOnBackend(id, patch, token) {
  if (!token || !id) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/trips/${id}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "PATCH",
      headers: _authHeaders(token),
      body: JSON.stringify(patch),
    });
    if (res.ok) { console.log("[JGA Trips Update] ✓ Patched id:", id, "fields:", Object.keys(patch).join(",")); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Trips Update"); return { ok: false, status: 401 }; }
    console.error("[JGA Trips Update] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Trips Update] Network error", err);
    return { ok: false };
  }
}

async function deleteTripOnBackend(id, token) {
  if (!token || !id) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/trips/${id}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) { console.log("[JGA Trips Delete] ✓ Deleted id:", id); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Trips Delete"); return { ok: false, status: 401 }; }
    console.error("[JGA Trips Delete] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Trips Delete] Network error", err);
    return { ok: false };
  }
}

// ── Journal backend sync ─────────────────────────────────────
// PROPOSED CONTRACT:
//   GET    /api/journal             → [{ tripCode, entryText, ratings:
//                                        {overall,people,vibe,food,value},
//                                        meta: {budget,travelers}, updatedAt }]
//   PUT    /api/journal/:tripCode   → upsert by user+tripCode
//                                      body: { entryText?, ratings?, meta? }
//   DELETE /api/journal/:tripCode
// Upsert semantics: subsequent writes overwrite the row. One row per trip.
async function loadJournalFromBackend(token) {
  if (!token) return { ok: false, items: [] };
  try {
    const res = await fetch(`${_api()}/api/journal`, { headers: { "Authorization": `Bearer ${token}` } });
    if (res.ok) {
      const items = await res.json().catch(() => []);
      console.log("[JGA Journal Load] ✓ Loaded", Array.isArray(items) ? items.length : 0, "journal entries");
      return { ok: true, items: Array.isArray(items) ? items : [] };
    }
    if (res.status === 401) { _signalSessionExpired("loadJournalFromBackend"); return { ok: false, items: [], status: 401 }; }
    console.error("[JGA Journal Load] Backend rejected", res.status);
    return { ok: false, items: [] };
  } catch (err) {
    console.error("[JGA Journal Load] Network error", err);
    return { ok: false, items: [] };
  }
}

async function saveJournalOnBackend(tripCode, payload, token) {
  if (!token || !tripCode) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/journal/${encodeURIComponent(tripCode)}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "PUT",
      headers: _authHeaders(token),
      body: JSON.stringify(payload),
    });
    if (res.ok) { console.log("[JGA Journal Save] ✓ Saved", tripCode, "fields:", Object.keys(payload).join(",")); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Journal Save"); return { ok: false, status: 401 }; }
    console.error("[JGA Journal Save] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Journal Save] Network error", err);
    return { ok: false };
  }
}

async function deleteJournalOnBackend(tripCode, token) {
  if (!token || !tripCode) return { ok: false };
  try {
    const res = await fetch(`${_api()}/api/journal/${encodeURIComponent(tripCode)}`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (res.ok) { console.log("[JGA Journal Delete] ✓ Deleted", tripCode); return { ok: true }; }
    if (res.status === 401) { _signalSessionExpired("Journal Delete"); return { ok: false, status: 401 }; }
    console.error("[JGA Journal Delete] Backend rejected", res.status);
    return { ok: false };
  } catch (err) {
    console.error("[JGA Journal Delete] Network error", err);
    return { ok: false };
  }
}

// ── Waitlist signup helper ─────────────────────────────────────
// Single source of truth for POST /api/waitlist.
// Replaces the older booking-code-request flow for Phase 1 launch —
// users join the waitlist instead of requesting a booking code, so the
// admin can hand-pick who gets early booking access. Phase 2 will
// reactivate the direct booking-code path for a larger audience.
//
// Backend contract (live as of 2026-05-18):
//   POST /api/waitlist — no auth required, rate-limited 10/min/IP
//   Body: { customerName?, email (required), phone?, destination?,
//           departureAirport?, travelDates?, duration?, budget?, travelers?,
//           mode? ("explore" | "surprise" | "general"), vibes?, includes?, notes? }
//   200 → { ok: true, waitlistId: number, alreadyOnWaitlist?: boolean }
//     - alreadyOnWaitlist=true when (email, mode) matched an existing row;
//       backend returns the existing waitlistId rather than creating a duplicate
//   400 → { error } (email missing/malformed)
//   429 → { error } (rate limited)
//   500 → { error: "Failed to join the waitlist." }
async function joinWaitlist(payload) {
  if (!payload || !payload.email || typeof payload.email !== "string" || !payload.email.includes("@")) {
    console.warn("[JGA Waitlist] Skipped — email missing or invalid");
    return { ok: false, error: "Email required" };
  }
  // Strip any undefined/null fields so we send a clean body
  const clean = Object.fromEntries(
    Object.entries(payload).filter(([_, v]) => v != null && v !== "")
  );
  try {
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    const res = await fetch(`${API}/api/waitlist`, {
      // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(clean),
    });
    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      const alreadyOn = body.alreadyOnWaitlist === true;
      console.log(`[JGA Waitlist] ✓ ${alreadyOn ? "Already on waitlist" : "Joined"}, waitlistId:`, body.waitlistId, "mode:", clean.mode);
      return { ok: true, waitlistId: body.waitlistId, alreadyOnWaitlist: alreadyOn };
    }
    const body = await res.json().catch(() => ({}));
    if (res.status === 429) {
      console.warn("[JGA Waitlist] Rate limited — try again in a minute");
      return { ok: false, status: 429, error: "Too many signups from this network. Try again in a minute." };
    }
    console.error("[JGA Waitlist] Backend rejected", res.status, body);
    return { ok: false, status: res.status, error: body?.error || `HTTP ${res.status}` };
  } catch (err) {
    console.error("[JGA Waitlist] Network error", err);
    return { ok: false, error: "Network error" };
  }
}

// ── Booking / referral code input ───────────────────────────────
// Locks the "TMA-" prefix as a static visual label and accepts only the
// 6-char suffix. The full code is always exactly 10 chars: "TMA-XXXXXX".
// onChange receives the full code (or "" when cleared).
// Pasting a full "TMA-PAR3K7" works — the prefix is auto-stripped and the
// suffix populates the input.
// ── Trip status mapping ────────────────────────────────────────
// Borrows flight-board language. Auto-computed from trip dates + user state.
// Cancelled trips are filtered out at the display layer (silently archived).
//
// Status windows (relative to today):
//   start - end < 0 days       → ARRIVED  (trip ended)
//   start <= today <= end      → DEPARTED (trip in progress)
//   0 < start - today <= 7d    → BOARDING (final week — pack, check in)
//   start - today > 7d         → SCHEDULED (booked, distant)
//   userMarkedDelayed === true → DELAYED  (user-flagged or system-detected blocker)
//
// DELAYED trumps date-based status. Auto-detected DELAYED conditions:
//   - Passport expires before trip end (international destinations)
//   - Trip in BOARDING window but no booking code on file
const TRIP_STATUS_META = {
  SCHEDULED: { label: "SCHEDULED", color: "#7a9eb8", bg: "rgba(122,158,184,0.10)", border: "rgba(122,158,184,0.40)", dot: "#7a9eb8", description: "Booked. Departure on the calendar." },
  BOARDING:  { label: "BOARDING",  color: "#4A8CC4", bg: "rgba(74,140,196,0.12)", border: "rgba(74,140,196,0.55)", dot: "#4A8CC4", description: "Within 7 days. Time to prep." },
  DEPARTED:  { label: "DEPARTED",  color: "#1B3A5C", bg: "rgba(27,58,92,0.12)",  border: "rgba(27,58,92,0.45)",  dot: "#1B3A5C", description: "On the trip right now." },
  ARRIVED:   { label: "ARRIVED",   color: "#B5D497", bg: "rgba(122,132,103,0.10)", border: "rgba(181,212,151,0.55)", dot: "#B5D497", description: "Trip complete." },
  DELAYED:   { label: "DELAYED",   color: "#ffa66d", bg: "rgba(255,166,109,0.12)", border: "rgba(255,166,109,0.50)", dot: "#ffa66d", description: "Action needed." },
};

function parseTripDate(s) {
  // Accepts ISO ("2026-05-14"), short ("May 14"), or full ("May 14, 2026")
  if (!s) return null;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d;
  return null;
}

// Returns { status, reason?, action? } for a given trip.
// `user` is optional — if provided, enables delay detection (passport expiry, missing code).
function getTripStatus(trip, user = null) {
  if (!trip) return { status: "SCHEDULED" };
  // User-marked delay always wins
  if (trip.userMarkedDelayed) {
    return { status: "DELAYED", reason: trip.delayReason || "Flagged by you" };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const start = parseTripDate(trip.dateFrom || trip.startDate);
  const end = parseTripDate(trip.dateTo || trip.endDate);

  // Auto-detected delays (only matter for upcoming trips)
  if (start && start >= now && user) {
    // Passport expires before trip end (international heuristic — not super smart yet)
    if (user.passportExpiry && end) {
      const exp = parseTripDate(user.passportExpiry);
      if (exp && exp < end) {
        return { status: "DELAYED", reason: "Passport expires before trip end", action: "Update passport" };
      }
    }
    // In BOARDING window without a booking code
    const daysUntil = Math.floor((start - now) / 86400000);
    if (daysUntil >= 0 && daysUntil <= 7 && !trip.code && !trip.bookingCode) {
      return { status: "DELAYED", reason: "No booking code on file", action: "Add booking code" };
    }
  }

  // Date-based mapping
  if (end && end < now) return { status: "ARRIVED" };
  if (start && end && start <= now && now <= end) return { status: "DEPARTED" };
  if (start) {
    const daysUntil = Math.floor((start - now) / 86400000);
    if (daysUntil <= 7) return { status: "BOARDING" };
    return { status: "SCHEDULED" };
  }
  // No dates → assume completed (legacy past trips)
  return { status: "ARRIVED" };
}

// Helper to filter out cancelled trips for display
function isVisibleTrip(t) {
  const s = (t?.status || "").toLowerCase();
  return s !== "cancelled" && s !== "canceled";
}

// ── TripStatusPill — flight-board language ─────────────────────
// Two variants:
//   "solari"  — flight-board / split-flap monospace, all-caps, fixed width.
//               Best for the Trips tab where multiple stack like a departure board.
//   "modern"  — softer rounded pill, integrates with rest of the app's chip system.
//               Best for inline use on dashboard cards.
function TripStatusPill({ status, variant = "solari", size = "md", animated = true }) {
  const meta = TRIP_STATUS_META[status] || TRIP_STATUS_META.SCHEDULED;
  const pulses = animated && (status === "BOARDING" || status === "DELAYED" || status === "DEPARTED");

  if (variant === "solari") {
    // Fixed-width monospace chip, dark slab, slight inset shadow — split-flap vibe
    const dims = size === "sm"
      ? { padding: "4px 9px", fontSize: 10, letter: 1.8, dotSize: 5, gap: 6 }
      : size === "lg"
      ? { padding: "8px 14px", fontSize: 13, letter: 2.4, dotSize: 7, gap: 8 }
      : { padding: "6px 11px", fontSize: 11, letter: 2.0, dotSize: 6, gap: 7 };
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: dims.gap,
        padding: dims.padding,
        background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.35))",
        border: `1px solid ${meta.border}`,
        borderRadius: 4, // slabs, not pills
        fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
        fontSize: dims.fontSize,
        fontWeight: 800,
        letterSpacing: dims.letter,
        color: meta.color,
        textShadow: `0 0 8px ${meta.color}55`,
        boxShadow: `inset 0 1px 0 rgba(27,58,92,0.072), inset 0 -1px 0 rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.4)`,
        whiteSpace: "nowrap",
      }}>
        <span aria-hidden="true" style={{
          width: dims.dotSize, height: dims.dotSize, borderRadius: "50%",
          background: meta.dot,
          boxShadow: `0 0 8px ${meta.dot}, 0 0 2px ${meta.dot}`,
          animation: pulses ? "jga-pulse 1.6s ease-in-out infinite" : "none",
          flexShrink: 0,
        }}/>
        {meta.label}
      </span>
    );
  }

  // "modern" variant — softer rounded chip
  const dims = size === "sm"
    ? { padding: "3px 9px", fontSize: 10, dotSize: 5, gap: 5 }
    : size === "lg"
    ? { padding: "6px 13px", fontSize: 12, dotSize: 7, gap: 7 }
    : { padding: "4px 11px", fontSize: 11, dotSize: 6, gap: 6 };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: dims.gap,
      padding: dims.padding,
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      borderRadius: 12,
      fontSize: dims.fontSize, fontWeight: 800, letterSpacing: 1,
      color: meta.color,
      whiteSpace: "nowrap",
    }}>
      <span aria-hidden="true" style={{
        width: dims.dotSize, height: dims.dotSize, borderRadius: "50%",
        background: meta.dot,
        animation: pulses ? "jga-pulse 1.6s ease-in-out infinite" : "none",
        flexShrink: 0,
      }}/>
      {meta.label}
    </span>
  );
}

// ── HeroIdleGlobe — landing-page idle ambience ─────────────────
// After idleMs of no mouse/keyboard/touch activity on an unauthenticated
// landing, the decorative globe in the hero starts a slow rotation and
// subtle breathing-scale animation. The effect: "the world is in motion,
// waiting for you." Replaces an earlier ribbon-of-postcards approach
// because the globe IS the brand identity of the hero — animating it
// rather than parading new content reads as more elegant.
//
// Key behaviors:
//   - Pure CSS keyframes (GPU-accelerated, no JS animation loop)
//   - Pauses + fades back to static on user activity, restarts countdown
//   - Respects `prefers-reduced-motion: reduce` (animation disabled entirely)
//   - Renders nothing in the DOM — just toggles a class on a target element
//     via a ref. Caller wraps the globe element with the returned ref.
function useHeroIdleGlobe({ idleMs = 10000 } = {}) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef(null);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    if (reducedMotionRef.current) return;

    const reset = () => {
      setIsIdle(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setIsIdle(true), idleMs);
    };
    const events = ["mousemove", "keydown", "touchstart", "scroll"];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(ev => window.removeEventListener(ev, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [idleMs]);

  return { isIdle, reducedMotion: reducedMotionRef.current };
}

// ── IdentityFlagDrift — ambient flags behind Travel Identity card ──
// Always-on horizontal drift of country flags relevant to the user's
// identity. Filters DESTINATIONS by tag overlap with identity.vibes so
// the flags emerge organically from data, no hardcoded mapping.
//
// Design notes:
//   - Very low opacity (12-15%) so it never competes with card text
//   - Flags drift in upper portion of card; gradient mask fades them out
//     where they'd cross the body text
//   - 90s cycle = barely perceptible motion (intentionally slow)
//   - Respects prefers-reduced-motion
function IdentityFlagDrift({ identity, destinations = [] }) {
  const reducedMotionRef = useRef(false);
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reducedMotionRef.current) setAnimate(false);
    }
  }, []);

  // Filter destinations whose tags overlap with the identity's vibes.
  // Take a curated 8 (deduped by country) for visual variety.
  const flags = useMemo(() => {
    if (!identity || !Array.isArray(identity.vibes)) return [];
    const seenCountries = new Set();
    const matched = [];
    for (const d of destinations) {
      if (!d.emoji || !Array.isArray(d.tags)) continue;
      if (seenCountries.has(d.country)) continue;
      const overlap = d.tags.some(t => identity.vibes.includes(t));
      if (overlap) {
        matched.push({ flag: d.emoji, country: d.country });
        seenCountries.add(d.country);
      }
      if (matched.length >= 8) break;
    }
    // If we got fewer than 4, pad with any remaining destinations to keep
    // the ribbon feeling full (better to show generic travel imagery than
    // an awkwardly sparse strip).
    if (matched.length < 4) {
      for (const d of destinations) {
        if (matched.length >= 8) break;
        if (seenCountries.has(d.country)) continue;
        if (d.emoji) {
          matched.push({ flag: d.emoji, country: d.country });
          seenCountries.add(d.country);
        }
      }
    }
    return matched;
  }, [identity, destinations]);

  if (flags.length === 0) return null;

  return (
    <div aria-hidden="true" style={{
      position: "absolute", inset: 0, overflow: "hidden",
      pointerEvents: "none", borderRadius: "inherit",
    }}>
      <style>{`
        @keyframes jga-flag-drift { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .jga-flag-row { animation: jga-flag-drift 90s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .jga-flag-row { animation: none; } }

        /* Flags drift along the BOTTOM of the welcome card, aligned with
           the "Just go" CTA strip. The band fills the lower ~28% so flags
           drift across the CTA row specifically — not behind the greeting
           or subline. Previously the band was 55% which made flags visible
           behind the whole lower half; Cas asked for them to align with
           the CTA only. Combined vertical + horizontal edge-fade gives
           soft entry/exit. */
        .jga-flag-band {
          position: absolute;
          left: 0; right: 0;
          top: auto; bottom: 0;
          height: 28%;
          display: flex;
          white-space: nowrap;
          opacity: 0.13;
          -webkit-mask-image:
            linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0) 100%),
            linear-gradient(90deg, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%);
          -webkit-mask-composite: source-in;
          mask-image:
            linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0) 100%),
            linear-gradient(90deg, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%);
          mask-composite: intersect;
        }
      `}</style>
      {/* Flag ribbon — drifts along the bottom of the welcome card on both
         desktop and mobile, fading upward so it sits behind the name row */}
      <div className="jga-flag-band" style={{ alignItems: "center" }}>
        <div className={animate ? "jga-flag-row" : ""} style={{
          display: "flex", gap: 28, paddingRight: 28, flexShrink: 0,
          alignItems: "center", paddingLeft: 12,
        }}>
          {[...flags, ...flags].map((f, i) => (
            <span key={`flag-${i}`} style={{
              fontSize: 36, lineHeight: 1,
              flexShrink: 0,
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
            }}>{f.flag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function BookingCodeInput({ value, onChange, placeholder = "PAR3K7", error = false, accentColor = "#1B3A5C", ariaLabel = "Booking reference code", name = "bookingCode" }) {
  // Derive the suffix-only view from the full code value
  const v = value || "";
  const suffix = v.startsWith("TMA-") ? v.slice(4) : v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  const handleChange = (raw) => {
    let s = String(raw || "").toUpperCase();
    s = s.replace(/^TMA-?/, ""); // strip prefix if user pasted full code
    s = s.replace(/[^A-Z0-9]/g, "");
    s = s.slice(0, 6);
    onChange(s ? `TMA-${s}` : "");
  };
  return (
    <div style={{
      display: "flex", alignItems: "stretch",
      borderRadius: 10,
      border: `1px solid ${error ? "rgba(255,107,107,0.5)" : v ? `${accentColor}66` : "rgba(27,58,92,0.144)"}`,
      background: v ? `${accentColor}0d` : "rgba(27,58,92,0.060)",
      overflow: "hidden",
      transition: "all 0.15s",
      boxSizing: "border-box",
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        padding: "10px 6px 10px 14px",
        color: v ? accentColor : "#4A6B8A",
        fontSize: 14, fontWeight: 700,
        letterSpacing: 1.5,
        fontFamily: "monospace",
        userSelect: "none",
        flexShrink: 0,
      }}>TMA-</div>
      <input
        type="text"
        name={name}
        id={name}
        value={suffix}
        onChange={e => handleChange(e.target.value)}
        maxLength={6}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        spellCheck={false}
        autoCapitalize="characters"
        style={{
          flex: 1,
          minWidth: 0,
          padding: "10px 14px 10px 0",
          border: "none", outline: "none",
          background: "transparent",
          color: v ? accentColor : "#1B3A5C",
          fontSize: 14,
          letterSpacing: 1.5,
          fontFamily: "monospace",
          boxSizing: "border-box",
          textTransform: "uppercase",
        }}
      />
    </div>
  );
}

// ── Destination "image" component ─────────────────────────────
// We previously called source.unsplash.com which Unsplash deprecated years
// ago — every request was failing with a CORB block. Rather than swap to
// another image service (each comes with API keys, attribution rules, and
// flaky cache behavior), we render a design-driven typographic card. It
// communicates destination identity through region-derived color palettes,
// flag emoji corner accent, and big destination type lockup. Result: zero
// failed network requests, instant render, intentional visual brand.
function DestinationImage({ destName, country = "", emoji = "🌍", height = 140, rounded = 12, overlay = true, accentColor = "#1B3A5C", priority = false, showTitle = true }) {
  // Region palette — a small, controlled set so cards have variety without chaos.
  // Lookup is by country since region isn't always passed in.
  const palette = useMemo(() => {
    const c = (country || "").toLowerCase();
    // Quick country → region mapping for color coordination — pairs drawn
    // from the canonical Style Guide vibe palette (no legacy cyan/mint).
    if (/(france|italy|spain|portugal|germany|netherlands|greece|iceland|norway|sweden|denmark|finland|austria|croatia|hungary|poland|czech|switzerland|belgium|ireland|uk|england|scotland)/.test(c)) return ["#4A8CC4", "#7A8A9C"]; // Europe — mauve + beach-blue
    if (/(japan|thailand|vietnam|china|korea|india|indonesia|philippines|malaysia|singapore|taiwan|nepal|bhutan|mongolia|cambodia|laos|myanmar|sri lanka)/.test(c)) return ["#C25E47", "#4A8CC4"]; // Asia — food + mauve
    if (/(usa|canada|united states|mexico)/.test(c)) return ["#7A8A9C", "#7A8467"]; // North America — beach + nature
    if (/(brazil|argentina|peru|chile|colombia|ecuador|bolivia|uruguay|paraguay|venezuela)/.test(c)) return ["#7A8467", "#A8853B"]; // South America — nature + culture
    if (/(costa rica|panama|guatemala|nicaragua|honduras|el salvador|belize)/.test(c)) return ["#C25E47", "#7A8467"]; // Central America — food + nature
    if (/(morocco|egypt|kenya|tanzania|south africa|namibia|botswana|ethiopia|ghana|tunisia|jordan|israel|uae|dubai|turkey)/.test(c)) return ["#9C7B65", "#A8853B"]; // Africa / ME — luxury + culture
    if (/(australia|new zealand|fiji|tahiti|polynesia|samoa)/.test(c)) return ["#7A8A9C", "#A89AC4"]; // Oceania — beach + wellness
    if (/(jamaica|cuba|dominican|bahamas|barbados|aruba|curacao|st\.|saint|trinidad|martinique)/.test(c)) return ["#7A8A9C", "#7A8467"]; // Caribbean — beach + nature
    return [accentColor, accentColor]; // Fallback to caller's accent
  }, [country, accentColor]);

  // Pick deterministically between the two palette colors so each destination
  // is visually distinct from neighbors but stable across renders.
  const baseColor = useMemo(() => {
    const seed = (destName.charCodeAt(0) + destName.charCodeAt(destName.length - 1)) % 2;
    return palette[seed] || palette[0];
  }, [destName, palette]);

  const gradient = `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}cc 60%, ${baseColor}88 100%)`;

  // Scale type sizes with card height for legibility at any size
  const titleSize = Math.max(10, Math.min(height * 0.18, 28));
  const flagSize = Math.max(14, Math.min(height * 0.20, 32));

  return (
    <div style={{
      position: "relative", width: "100%", height,
      borderRadius: rounded, overflow: "hidden",
      background: gradient,
      flexShrink: 0,
    }}>
      {/* Decorative dot grid for texture */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0,
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(27,58,92,0.16) 1px, transparent 0)`,
        backgroundSize: `${Math.max(12, height * 0.13)}px ${Math.max(12, height * 0.13)}px`,
        opacity: 0.5,
        pointerEvents: "none",
      }}/>

      {/* Flag — visual anchor of the image area. Centered at 40% opacity so
         it's clearly the country identifier but still leaves room for overlays
         and text. Scales with card height for legibility at any size. */}
      <div aria-hidden="true" style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.max(60, height * 0.75),
        lineHeight: 1,
        opacity: 0.4,
        pointerEvents: "none",
        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))",
      }}>{emoji}</div>

      {/* Destination name lockup — only shown if there's room AND caller didn't suppress */}
      {height >= 90 && showTitle && (
        <div style={{
          position: "absolute",
          left: 12, bottom: overlay ? 16 : 10, right: 60,
          color: "#fff",
          textShadow: "0 2px 10px rgba(0,0,0,0.4)",
          pointerEvents: "none",
        }}>
          <div style={{ fontSize: Math.max(8, titleSize * 0.36), letterSpacing: 1.5, fontWeight: 800, opacity: 0.85, textTransform: "uppercase", marginBottom: 2 }}>
            {country}
          </div>
          <div style={{ fontSize: titleSize, fontWeight: 900, lineHeight: 1.05, letterSpacing: -0.5 }}>
            {destName}
          </div>
        </div>
      )}

      {/* Bottom legibility gradient for any overlaid text */}
      {overlay && (
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.45) 100%)", pointerEvents: "none" }}/>
      )}

      {/* Flag chip corner — readable at every size */}
      <div style={{
        position: "absolute", top: 8, left: 8,
        fontSize: flagSize * 0.55, lineHeight: 1,
        padding: "3px 7px", borderRadius: 6,
        background: "rgba(0,0,0,0.45)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)",
        border: "1px solid rgba(27,58,92,0.16)",
      }}>{emoji}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// estimateTripCost — single source of truth for trip cost estimates.
//
// DESTINATIONS data audit (Nov 2026): minBudget and maxBudget represent the
// on-ground cost (hotel + food + activities + local transport) for a typical
// 5-7 day trip per person, EXCLUDING the flight. The minimum represents a
// budget traveler doing it cheaply, the maximum a comfortable mid-range trip.
//
// The previous formula `flight + minBudget * 0.55` was incorrect — it took 55%
// of the on-ground number as if it were a "hotel only" portion, which both
// understated the realistic cost and conflicted with the data's actual meaning.
//
// Returns:
//   - from:      The lower-bound honest minimum (flight + minBudget). What it
//                costs if you're scrappy. The marketing-fair "starting at" number.
//   - realistic: The mid-range expectation (flight + midpoint of min/max). What
//                most users will actually spend. The honest "plan against this"
//                number for filtering and fit-checks.
//   - low/high:  Component pieces for callers that want to show ranges or do
//                their own math (Most Affordable, etc.).
//
// Returns null when flightPrice is missing or destination data is incomplete,
// matching the pattern of the previous call sites.
// ─────────────────────────────────────────────────────────────────────────
// Shared helper — derive effective traveler count from the app's two related
// state values (travelCompanion is the dashboard Style cell; travelers is the
// numeric picker). Used everywhere prices need traveler-aware scaling.
// Order: travelCompanion wins (most explicit user choice), then numeric
// travelers if > 1, then default 1. Mirrors filterPool's resolution.
function paxFromCompanion(travelCompanion, travelers) {
  const map = { solo: 1, couple: 2, friends: 3, family: 4 };
  return map[travelCompanion] || (travelers > 1 ? travelers : 0) || 1;
}


// COMPANION_VIBES_FOR_COUNT — the vibe-pill set shown below the count
// picker. Returns [] for count=1 (Solo doesn't need a vibe). For count=2,
// users pick Romantic (couple) vs Friends. For count=3 or 4, users pick
// Friends vs Family. The companionFit scoring at L2715 reads these as
// `couple` / `friends` / `family`, so the underlying tag-boost logic is
// already wired — these pills just give the user a way to TELL the system
// which lens to score through. Before this, a friend-pair could only
// select "Couple" and got romance-coded matches; now they can flag the
// trip as a friends trip and get nightlife/adventure-coded matches.
function getVibeOptionsForCount(count) {
  if (count === 2) return [
    { id: "couple",  label: "Romantic" },
    { id: "friends", label: "Friends"  },
  ];
  if (count === 3 || count === 4) return [
    { id: "friends", label: "Friends" },
    { id: "family",  label: "Family"  },
  ];
  return [];
}

// Default vibe for a given count — used when user taps a count pill so the
// system has a sensible companion intent immediately (no broken state
// where matching tries to score with no companion). Matches the prior
// implicit defaults (couple/friends/family) so existing behavior is
// preserved unless the user explicitly toggles.
function getDefaultVibeForCount(count) {
  if (count === 1) return "solo";
  if (count === 2) return "couple";
  if (count === 3) return "friends";
  if (count === 4) return "family";
  return null;
}

function estimateTripCost(dest, flightPrice, travelers = 1) {
  if (!flightPrice || !dest) return null;
  const pax = Math.max(1, travelers || 1);
  const minB = dest.minBudget || 0;
  const maxB = dest.maxBudget || minB;
  // Flight scales linearly with travelers — each person needs a seat.
  const flightTotal = flightPrice * pax;
  // On-ground (food/transit/activities/hotel mixed into minBudget) scales
  // partially since hotels can be shared. Factor: 0.7 + 0.3*pax (1.0x solo,
  // 1.3x couple, 1.9x family of 4). Rougher than getBaselineTripCost's
  // explicit rooms math, but estimateTripCost is the lightweight estimator
  // used by per-card price displays; precision lives in getBaselineTripCost.
  const groundScale = pax === 1 ? 1.0 : (0.7 + 0.3 * pax);
  const minScaled = Math.round(minB * groundScale);
  const maxScaled = Math.round(maxB * groundScale);
  return {
    from: flightTotal + minScaled,
    realistic: flightTotal + Math.round((minScaled + maxScaled) / 2),
    low: minScaled,
    high: maxScaled,
    flight: flightTotal,
  };
}

function BudgetDisplay({ dest, departureAirport, flightPrice, userBudget = null, travelers = 1, style = {} }) {
  if (!departureAirport) {
    return (
      <div style={{ fontSize: 11, color: "#4A6B8A", fontStyle: "italic", ...style }}>
        Add departure airport for pricing
      </div>
    );
  }
  const _pax = Math.max(1, travelers || 1);
  // Compute an effective flight price for visual consistency. If the live/Amadeus
  // price (passed via prop) isn't loaded yet, fall back to the synchronous
  // distance-based estimator so EVERY card renders the same "From / Realistic"
  // shape regardless of which load state it's in. Previously, the missing-price
  // path showed a "$lo-$hi" range that (a) had a different visual format AND
  // (b) could render reversed when userBudget > dest.maxBudget. Both bugs fixed
  // by removing that path entirely.
  const effectiveFlightPrice = flightPrice || estimateFlightPrice(departureAirport, dest.name, null) || null;
  if (effectiveFlightPrice) {
    const est = estimateTripCost(dest, effectiveFlightPrice, _pax);
    if (!est) return null;
    return (
      <div style={style}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>From ${est.from.toLocaleString()}</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(27,58,92,0.75)", marginTop: 1 }}>Realistic ${est.realistic.toLocaleString()}</div>
        <div style={{ fontSize: 10, color: "#4A6B8A", marginTop: 1, fontStyle: "italic" }}>from {departureAirport}{_pax > 1 ? ` · ${_pax} travelers` : ""}</div>
      </div>
    );
  }
  // True fallback — distance estimator returned null (rare: unknown airport
  // code or unmapped route). Still uses "From / Realistic" format for visual
  // consistency, with destination-only numbers and a "+ flight" hint signaling
  // the user that flight cost isn't yet included.
  // Scale minBudget/maxBudget by same 0.7+0.3*pax factor as estimateTripCost.
  const _groundScale = _pax === 1 ? 1.0 : (0.7 + 0.3 * _pax);
  const minB = Math.round((dest.minBudget || 0) * _groundScale);
  const maxB = Math.round((dest.maxBudget || dest.minBudget || 0) * _groundScale);
  const midpoint = Math.round((minB + maxB) / 2);
  return (
    <div style={style}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>From ${minB.toLocaleString()}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(27,58,92,0.75)", marginTop: 1 }}>Realistic ${midpoint.toLocaleString()}</div>
      <div style={{ fontSize: 10, color: "#4A6B8A", marginTop: 1, fontStyle: "italic" }}>+ flight from {departureAirport}{_pax > 1 ? ` · ${_pax} travelers` : ""}</div>
    </div>
  );
}


// ── Small components ────────────────────────────────────────────
function Pill({ active, color = amber, onClick, children, small = false }) {
  const { darkMode } = useTheme();
  return (
    <button onClick={onClick} style={{
      padding: small ? "5px 10px" : "8px 14px",
      borderRadius: 30, fontSize: small ? 12 : 13, cursor: "pointer",
      border: darkMode
        ? `1px solid ${active ? color : "rgba(27,58,92,0.120)"}`
        : `1.5px solid ${active ? "rgba(27,58,92,0.6)" : "rgba(27,58,92,0.2)"}`,
      background: darkMode
        ? (active ? `${color}28` : "rgba(27,58,92,0.045)")
        : (active ? "rgba(27,58,92,0.15)" : "rgba(27,58,92,0.04)"),
      color: darkMode
        ? (active ? (color === amber ? gold : color) : "#4A6B8A")
        : (active ? "#0a4a4a" : "#3a6a6a"),
      fontWeight: active ? 700 : 500,
      transition: "all 0.15s", fontFamily: "inherit", whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

function Btn({ variant = "gold", onClick, children, style = {}, disabled = false, type = "button" }) {
  const { darkMode } = useTheme();
  const variants = {
    gold: darkMode
      ? { background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5" }
      : { background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#ffffff", borderRadius: 100 },
    teal: darkMode
      ? { background: `linear-gradient(135deg,#0F2747,${teal})`, color: "#FAFAF5" }
      : { background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#ffffff", borderRadius: 100 },
    ghost: darkMode
      ? { background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A" }
      : { background: "rgba(27,58,92,0.08)", border: "1px solid rgba(27,58,92,0.25)", color: "#0a4a4a", borderRadius: 100 },
    coral: { background: `linear-gradient(135deg,#FF6B6B,${coral})`, color: "#fff", borderRadius: darkMode ? 12 : 100 },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      width: "100%", padding: "14px", borderRadius: 12, border: "none",
      fontWeight: 800, fontSize: 15, cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "inherit", letterSpacing: 0.3, opacity: disabled ? 0.5 : 1,
      transition: "all 0.2s", ...variants[variant], ...style,
    }}>{children}</button>
  );
}


function Input({ label, value, onChange, placeholder, type = "text", error = null, min, max, autoComplete, name, inputMode, autoCapitalize }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const inputId = `input-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const errorId = `${inputId}-error`;
  // Derive a name attribute from the autoComplete token (or label) when not
  // explicitly given. Safari's AutoFill recognition is strongest when both
  // `name` and `autoComplete` are present and agree.
  const resolvedName = name || autoComplete || label.replace(/\s+/g, "-").toLowerCase();
  // Password-visibility state — only meaningful when type === "password".
  // Default is masked (security), tap eye toggles to plain text. Re-tap re-masks.
  // We toggle the `type` attribute on the live input, which keeps focus and cursor
  // position intact (vs unmounting and remounting). Modern password managers handle
  // type-attribute changes correctly, so autofill still works.
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && showPassword ? "text" : type;
  return (
    <div style={{ marginBottom: 14 }}>
      {/* Make date picker icon visible in dark mode + ensure date text is readable */}
      {type === "date" && darkMode && (
        <style>{`
          input#${inputId} { color: #1B3A5C !important; color-scheme: dark !important; }
          input#${inputId}::-webkit-calendar-picker-indicator { filter: invert(1) brightness(0.85); cursor: pointer; opacity: 0.85; }
          input#${inputId}::-webkit-calendar-picker-indicator:hover { opacity: 1; }
          input#${inputId}::-webkit-datetime-edit { color: #1B3A5C !important; }
          input#${inputId}::-webkit-datetime-edit-fields-wrapper { color: #1B3A5C !important; }
          input#${inputId}::-webkit-datetime-edit-text { color: #1B3A5C !important; opacity: 0.85; }
          input#${inputId}::-webkit-datetime-edit-month-field,
          input#${inputId}::-webkit-datetime-edit-day-field,
          input#${inputId}::-webkit-datetime-edit-year-field { color: #1B3A5C !important; }
          input#${inputId}:not(:focus)::-webkit-datetime-edit-month-field:placeholder-shown,
          input#${inputId}:not(:focus)::-webkit-datetime-edit-day-field:placeholder-shown,
          input#${inputId}:not(:focus)::-webkit-datetime-edit-year-field:placeholder-shown { color: rgba(240,230,211,0.45) !important; }
        `}</style>
      )}
      <label htmlFor={inputId} style={{ fontSize: 11, letterSpacing: 3, color: darkMode ? "#4A8CC4" : "#4A8CC4", textTransform: "uppercase", marginBottom: 10, display: "block" }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input id={inputId} type={effectiveType} value={value} onChange={onChange} placeholder={placeholder}
          min={min} max={max} autoComplete={autoComplete} name={resolvedName}
          inputMode={inputMode}
          autoCapitalize={autoCapitalize || (type === "email" ? "off" : undefined)}
          autoCorrect={type === "email" ? "off" : undefined}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? "true" : undefined}
          style={{ width: "100%", padding: isPassword ? "11px 44px 11px 14px" : "11px 14px", borderRadius: 10, fontSize: 16,
            // 16px is the minimum font-size that prevents iOS Safari from
            // auto-zooming when the input is focused. Previously 14px caused
            // mobile users to get zoomed in on every input tap. 16px stays
            // visually identical-ish on desktop and respects iOS accessibility.
            fontFamily: "inherit", background: "rgba(27,58,92,0.060)",
            border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C",
            outline: "none", boxSizing: "border-box",
            colorScheme: darkMode ? "dark" : "light",
            transition: "border-color 0.2s" }}
          onFocus={e => e.target.style.borderColor = "rgba(74,140,196,0.5)"}
          onBlur={e => e.target.style.borderColor = "rgba(27,58,92,0.144)"}
        />
        {isPassword && (
          /* Eye toggle — absolute-positioned inside the input padding zone.
             onMouseDown preventDefault stops the input from losing focus when
             the user clicks the eye, so cursor position is preserved. */
          <button type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            style={{
              position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)",
              background: "transparent", border: "none", cursor: "pointer", padding: 6,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: showPassword ? (darkMode ? "#4A8CC4" : "#4A8CC4") : (darkMode ? "rgba(240,230,211,0.45)" : "rgba(0,0,0,0.4)"),
              transition: "color 0.15s",
            }}>
            {showPassword ? (
              /* Eye with slash — password is currently visible, click to hide */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 12 C 5.5 7 8.5 5 12 5 C 13.6 5 15.1 5.4 16.4 6.1 M21 12 C 19.5 15 17.4 17 15 18.2 M9.4 9.4 A 3.5 3.5 0 0 0 14.6 14.6 M5 5 L 19 19" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
              </svg>
            ) : (
              /* Eye open — password masked, click to reveal */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 12 C 5.5 7 8.5 5 12 5 C 15.5 5 18.5 7 21 12 C 18.5 17 15.5 19 12 19 C 8.5 19 5.5 17 3 12 Z" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" fill="none"/>
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function SpinCounter({ used }) {
  const remaining = FREE_SPINS - used;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 5 }}>
        {Array.from({ length: FREE_SPINS }).map((_, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: "50%",
            background: i < used ? "rgba(27,58,92,0.14)" : gold,
            border: i < used ? "1px solid rgba(27,58,92,0.120)" : `1px solid ${amber}`,
            boxShadow: i >= used ? `0 0 6px ${amber}80` : "none",
            transition: "all 0.3s" }} />
        ))}
      </div>
      <span style={{ fontSize: 12, color: remaining > 0 ? "#4A8CC4" : coral }}>
        {remaining > 0 ? `${remaining} free spin${remaining !== 1 ? "s" : ""} left` : "No free spins left"}
      </span>
    </div>
  );
}

function ItineraryView({ data }) {
  const { goldText, textPrimary, textMuted } = useTheme();
  return (
    <div>
      {data.budgetBreakdown && (
        <div style={{ background: "linear-gradient(135deg, rgba(74,140,196,0.10) 0%, rgba(74,140,196,0.04) 100%)", borderRadius: 12, padding: "12px 14px", marginBottom: 14, border: "1px solid rgba(74,140,196,0.30)" }}>
          <Label>Budget Breakdown</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 6 }}>
            {Object.entries(data.budgetBreakdown).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#4A6B8A", textTransform: "capitalize" }}>{k}</span>
                <span style={{ color: "#1B3A5C", fontWeight: 800 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {data.days?.map(day => (
          <div key={day.day} style={{ background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", border: "1px solid rgba(74,140,196,0.55)", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#1B3A5C" }}>Day {day.day}</span>
              <span style={{ fontSize: 11, color: "#4A6B8A", fontStyle: "italic" }}>{day.title}</span>
            </div>
            {[["🌅 Morning", day.morning], ["☀️ Afternoon", day.afternoon], ["🌙 Evening", day.evening]].map(([l, v]) => v && (
              <div key={l} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#4A6B8A", minWidth: 90, letterSpacing: 0.3 }}>{l}</span>
                <span style={{ fontSize: 12, color: textPrimary, flex: 1, lineHeight: 1.5 }}>{v}</span>
              </div>
            ))}
            {day.tip && <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(74,140,196,0.12)", borderRadius: 8, fontSize: 11, color: "#7a4a5c", fontStyle: "italic", border: "1px solid rgba(74,140,196,0.30)" }}>💡 {day.tip}</div>}
          </div>
        ))}
      </div>
      {data.packingTip && (
        <div style={{ background: "rgba(74,85,104,0.10)", borderRadius: 10, padding: "10px 12px", border: "1px solid rgba(74,85,104,0.30)", fontSize: 12, color: "#3d4654", fontStyle: "italic" }}>
          🎒 {data.packingTip}
        </div>
      )}
    </div>
  );
}

// ── Rotating subtitle for loading screen ───────────────────────
function RotatingSubtitle({ lines }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % lines.length), 1800);
    return () => clearInterval(t);
  }, [lines.length]);
  return (
    <p style={{ color: "#4A6B8A", fontSize: 13, fontStyle: "italic", minHeight: 20, transition: "opacity 0.4s" }}>
      {lines[idx]}
    </p>
  );
}

// ── Booking Modal ───────────────────────────────────────────────
function BookingModal({ dest, budget, days, departDate, returnDate, expIncludes, serviceFee = 0, feeLabel = "", onClose, user = null, aiItinerary = null, saveItinerary = null, onPostcardEarn = null, onViewItineraries = null }) {
  // Auto-save the AI itinerary to backend (and localStorage) when the booking
  // modal opens. Previously the modal showed "Your AI itinerary is saved in
  // the Itineraries tab" — but no save actually fired. Users hit Book Now,
  // saw the message, and lost their itinerary if they didn't separately click
  // Save in Explore. Now: if there's an AI itinerary AND a save helper, fire
  // the save once on mount. Idempotent so back-and-forth navigation doesn't
  // create duplicates.
  const [autoSaved, setAutoSaved] = useState(!aiItinerary); // if no itinerary, treat as "no save needed"
  useEffect(() => {
    if (!aiItinerary || !saveItinerary || autoSaved) return;
    let cancelled = false;
    (async () => {
      const result = await saveItinerary({
        destName: dest.name,
        destEmoji: dest.emoji,
        destCountry: dest.country || "",
        days,
        data: aiItinerary,
      });
      if (cancelled) return;
      if (result.ok) {
        console.log("[JGA Booking] Itinerary auto-saved", result.alreadySaved ? "(was already saved)" : "(new)");
      } else {
        console.warn("[JGA Booking] Auto-save failed — kept locally with pendingSync");
      }
      setAutoSaved(true);
    })();
    return () => { cancelled = true; };
  }, [aiItinerary, saveItinerary, autoSaved, dest.name, dest.emoji, dest.country, days]);
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const [step, setStep] = useState(0);
  const [card, setCard] = useState({ name: "", number: "", expiry: "", cvv: "" });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const modalRef = useRef(null);
  useEffect(() => { if (modalRef.current) modalRef.current.scrollTop = 0; }, [step]);
  const [addInsurance, setAddInsurance] = useState(false);
  const [ktnInput, setKtnInput] = useState(user?.ktn || "");
  const [ktnRequested, setKtnRequested] = useState(false);
  const [showKtnRequest, setShowKtnRequest] = useState(false);
  const [bookingCodeInput, setBookingCodeInput] = useState("");
  const [bookingCodeRequested, setBookingCodeRequested] = useState(false);
  const [bookingCodeError, setBookingCodeError] = useState("");
  const [insurancePlan, setInsurancePlan] = useState("standard");
  const [showAdminCode, setShowAdminCode] = useState(false);
  // Pre-fill admin code from localStorage if user saved one on the Sign In
  // screen ("I have a booking code" flow). Saved codes survive page reloads
  // and persist across sign-out → sign-in. Cleared after successful booking.
  const [adminCodeInput, setAdminCodeInput] = useState(() => {
    try { return localStorage.getItem("jga_pending_booking_code") || ""; }
    catch(e) { return ""; }
  });
  const [adminCodeStatus, setAdminCodeStatus] = useState(null); // null | checking | valid | invalid
  const [adminCodeRecord, setAdminCodeRecord] = useState(null);

  // ── Phase 1 waitlist state ────────────────────────────────────
  // For users without a booking code (most users in Phase 1), the booking
  // flow shows a waitlist signup instead of a code-entry field. Admin
  // hand-picks approvals; once approved, user gets a code via email.
  const [waitlistNotes, setWaitlistNotes] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null | "sending" | "joined" | "error"

  // Auto-validate any saved code on mount. If the user pasted their code on
  // the Sign In screen, the code lives in localStorage and we validate it
  // silently here — they see "✓ Cleared to book" instead of a code field.
  // If the code is invalid (expired, typo'd), we fall back to waitlist UI.
  useEffect(() => {
    if (!adminCodeInput.trim() || adminCodeStatus) return;
    let cancelled = false;
    (async () => {
      setAdminCodeStatus("checking");
      try {
        const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
        const r = await fetch(`${API}/api/admin/codes/${adminCodeInput.trim()}`);
        if (cancelled) return;
        if (r.ok) { const data = await r.json(); setAdminCodeRecord(data); setAdminCodeStatus("valid"); }
        else {
          setAdminCodeStatus("invalid");
          // Clear the bad code from localStorage so they don't keep hitting this
          try { localStorage.removeItem("jga_pending_booking_code"); } catch(e) {}
        }
      } catch(e) { if (!cancelled) setAdminCodeStatus("invalid"); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Defensive: confirmCode generation crashed the whole modal when dest was
  // accidentally passed as a string (regression Jun 11 2026: saved itineraries
  // passing it.dest, which is the name string, into setSelected → BookingModal).
  // Root cause is fixed at the onBookDest boundary now, but keep this guard so
  // the same shape mismatch never black-screens the app again.
  if (!dest || !dest.name) {
    console.warn("[JGA BookingModal] mounted without valid dest:", dest);
  }
  const safeDestName = (dest && typeof dest.name === "string") ? dest.name : "???";
  const confirmCode = `TMA-${safeDestName.slice(0,3).toUpperCase()}${Math.random().toString(36).slice(2,5).toUpperCase()}`;
  const parsedBudget = parseInt(String(budget).replace(/\D/g,""),10) || 1000;
  const tripDays = departDate && returnDate
    ? Math.round((new Date(returnDate) - new Date(departDate)) / 86400000)
    : days;

  const insurancePlans = {
    standard: { label: "Standard Cover", price: 49, desc: "Trip cancellation, medical up to $50K, lost baggage" },
    premium:  { label: "Premium Cover", price: 89, desc: "All standard + missed connections, adventure sports, $150K medical" },
  };
  const insuranceCost = addInsurance ? insurancePlans[insurancePlan].price : 0;
  const totalCost = parsedBudget + insuranceCost + serviceFee;

  function validateCard() {
    const e = {};
    if (!card.name.trim()) e.name = "Required";
    if (card.number.replace(/\s/g,"").length < 16) e.number = "Enter a valid 16-digit card number";
    if (!card.expiry.match(/^\d{2}\/\d{2}$/)) e.expiry = "Format: MM/YY";
    if (card.cvv.length < 3) e.cvv = "3 or 4 digits required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const [bookingError, setBookingError] = useState(null);

  async function handlePay() {
    if (!validateCard()) return;
    setProcessing(true);
    setBookingError(null);
    // Move to "processing" step immediately so user sees progress
    setStep(1.5);

    // Brief delay to simulate payment authorization
    await new Promise(r => setTimeout(r, 1500));

    // ── Register booking with backend ──────────────────────────
    let backendOk = false;
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const res = await fetch(`${API}/api/admin/codes/frontend`, {
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: confirmCode,
          confirmationCode: confirmCode,
          dest: dest.name,
          destination: dest.name,
          destinationEmoji: dest.emoji,
          mode: "TMA",
          customerName: user ? `${user.firstName} ${user.lastName || ""}`.trim() : "Guest",
          customerEmail: user?.email || "",
          budget: parsedBudget,
          totalCost: totalCost,
          travelDate: departDate || "",
          days: tripDays,
          travelers: travelers || 1,
          departureAirport: user?.departureAirport || "",
          includesInsurance: addInsurance,
          userEmail: user?.email || "",
          userName: user ? `${user.firstName} ${user.lastName || ""}`.trim() : "Guest",
          adminCodeLinked: adminCodeStatus === "valid" ? adminCodeInput : null,
          bookedAt: new Date().toISOString(),
        }),
      });
      backendOk = res.ok;
      if (!backendOk) {
        console.log("[JGA] Backend returned non-OK status:", res.status);
      }
    } catch(e) {
      console.log("[JGA] Backend booking registration failed:", e.message);
      backendOk = false;
    }

    setProcessing(false);
    // ── Auto-earn the destination's postcard ──
    // On booking success (whether backend confirmed cleanly or fell through to
    // the soft-warning path), save the postcard to the user's collection in
    // "dreaming" state. Booking = intent, not visited. Gracefully skips
    // destinations without a postcard illustration in POSTCARDS_CATALOG.
    const earnedPostcardId = getPostcardIdForDest(dest.name);
    if (earnedPostcardId) {
      try {
        setPostcardState(earnedPostcardId, "dreaming");
        // Notify parent so any open collection view re-renders. Optional — if
        // not provided, the postcard still saves; user sees it next mount.
        if (onPostcardEarn) onPostcardEarn(earnedPostcardId);
      } catch (err) {
        console.warn("[JGA Booking] Postcard auto-save failed:", err);
      }
    }
    if (backendOk) {
      setStep(2); // Confirmed
      // Clear any saved booking code now that it's been used. Without this,
      // a user who saved a code on Sign In and then books a second trip
      // would have the spent code auto-fill the second booking.
      try { localStorage.removeItem("jga_pending_booking_code"); } catch(e) {}
    } else {
      // Don't lose the booking — still show confirmation but with a soft warning
      // (user has already been charged in real Stripe flow; we always honor it)
      setBookingError("Your booking is registered but our system is taking a moment to fully sync. You'll receive an email confirmation shortly.");
      setStep(2);
    }
  }

  const inclLabels = { air: "Return Flights", hotel: "Accommodation", transport: "Local Transport", activities: "Activities" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.8)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div role="dialog" aria-modal="true" aria-label="Booking details" style={{ ...lightCard(darkMode), width: "100%", maxWidth: 500, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>

        {/* Step 0 — Review */}
        {step === 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>Review Your Trip</h2>
            </div>

            <div style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 14, padding: "16px", marginBottom: 16, display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ fontSize: 44 }}>{dest.emoji}</div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>{dest.name}</div>
                <div style={{ fontSize: 13, color: "#4A6B8A" }}>{dest.state ? `${dest.state}, ` : ""}{dest.country}</div>
                <div style={{ fontSize: 11, color: "#4A8CC4", fontStyle: "italic", marginTop: 3 }}>{dest.vibe}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              {[
                ["🗓️ Departure", departDate || "Flexible"],
                ["🗓️ Return", returnDate || "Flexible"],
                ["⏱️ Duration", `${tripDays} ${tripDays === 1 ? "day" : "days"}`],
                ["💰 Trip budget", `$${parsedBudget.toLocaleString()}`],
              ].map(([k, v]) => (
                <div key={k} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px", border: "1px solid rgba(27,58,92,0.084)" }}>
                  <div style={{ fontSize: 10, color: "#4A6B8A", marginBottom: 3 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>{v}</div>
                </div>
              ))}
            </div>

            {expIncludes.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <Label color="#4A8CC4">What's included</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {expIncludes.map(id => (
                    <span key={id} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 20, background: "rgba(27,58,92,0.1)", border: "1px solid rgba(27,58,92,0.3)", color: teal }}>
                      ✓ {inclLabels[id] || id}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ── Travel Insurance Upsell ── */}
            <div style={{ marginBottom: 16 }}>
              <Label color="#4A8CC4">Travel Insurance</Label>
              <button onClick={() => setAddInsurance(i => !i)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
                borderRadius: 12, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                border: `1px solid ${addInsurance ? "rgba(109,189,109,0.5)" : "rgba(27,58,92,0.120)"}`,
                background: addInsurance ? "rgba(109,189,109,0.08)" : "rgba(27,58,92,0.045)"
              }}>
                <div style={{ fontSize: 26, flexShrink: 0 }}>🛡️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: addInsurance ? "#6dbd6d" : "#4A6B8A" }}>Add travel insurance</div>
                  <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2 }}>Protect your trip against cancellations, medical emergencies & more</div>
                </div>
                <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${addInsurance ? "#6dbd6d" : "rgba(27,58,92,0.18)"}`, background: addInsurance ? "#6dbd6d" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 11, color: "#FAFAF5", fontWeight: 900 }}>
                  {addInsurance ? "✓" : ""}
                </div>
              </button>

              {addInsurance && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  {Object.entries(insurancePlans).map(([id, plan]) => (
                    <button key={id} onClick={() => setInsurancePlan(id)} style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                      borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                      border: `1px solid ${insurancePlan === id ? "rgba(109,189,109,0.6)" : "rgba(27,58,92,0.096)"}`,
                      background: insurancePlan === id ? "rgba(109,189,109,0.1)" : "rgba(27,58,92,0.030)"
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: insurancePlan === id ? "#6dbd6d" : "#4A6B8A" }}>{plan.label}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: darkMode ? "#6dbd6d" : "#2a7a4a" }}>+${plan.price}</span>
                        </div>
                        <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 3 }}>{plan.desc}</div>
                      </div>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${insurancePlan === id ? "#6dbd6d" : "rgba(27,58,92,0.18)"}`, background: insurancePlan === id ? "#6dbd6d" : "transparent", flexShrink: 0 }} />
                    </button>
                  ))}
                  <div style={{ fontSize: 11, color: "#4a6a4a", fontStyle: "italic", paddingLeft: 4 }}>
                    Provided by SafetyWing · <a href="https://safetywing.com/?referenceID=26515446&utm_source=26515446&utm_medium=Ambassador" target="_blank" rel="noopener noreferrer" style={{color:"#6dbd6d",fontSize:11}}>View coverage details →</a>
                  </div>
                </div>
              )}
            </div>



            {/* ── Order summary ── */}
            <div style={{ background: "rgba(74,140,196,0.07)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 10 }}>Order Summary</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#4A6B8A" }}>Trip to {dest.name}</span>
                  <span style={{ color: "#1B3A5C" }}>${parsedBudget.toLocaleString()}</span>
                </div>
                {addInsurance && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#4A6B8A" }}>{insurancePlans[insurancePlan].label}</span>
                    <span style={{ color: "#6dbd6d" }}>+${insuranceCost}</span>
                  </div>
                )}
                {serviceFee > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: "#4A6B8A" }}>{feeLabel}</span>
                    <span style={{ color: "#4A6B8A" }}>+${serviceFee}</span>
                  </div>
                )}
                <div style={{ borderTop: "1px solid rgba(74,140,196,0.15)", paddingTop: 8, marginTop: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 14, color: "#4A8CC4", fontWeight: 700 }}>Total estimated cost</span>
                  <span style={{ fontSize: 22, fontWeight: 900, color: goldText }}>${totalCost.toLocaleString()}</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 6 }}>You'll receive a confirmation at booking with 24 hours to secure your trip.</div>
            </div>

            {aiItinerary && autoSaved && (
              <div style={{ fontSize: 11, color: "#4A6B8A", textAlign: "center", marginBottom: 16, lineHeight: 1.6 }}>
                Your AI itinerary for {dest.name} is saved.{" "}
                {onViewItineraries && (
                  <button
                    onClick={() => { onViewItineraries(); if (typeof onClose === "function") onClose(); }}
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: "#4A8CC4", fontWeight: 700, fontSize: "inherit",
                      fontFamily: "inherit", textDecoration: "underline",
                      textUnderlineOffset: 2, cursor: "pointer",
                    }}>
                    View in your Trips →
                  </button>
                )}
              </div>
            )}

            {/* ── Phase 1 booking gate — waitlist signup or cleared-to-book ──
               Approved users (with codes saved on Sign In) auto-validate and
               see a small badge. Everyone else sees waitlist signup as the
               primary path. Booking codes are entirely invisible to new
               users — they don't think they need a code, they just join
               the list to book. Phase 2 brings back the code flow. */}
            {adminCodeStatus === "valid" && adminCodeRecord ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "rgba(109,189,109,0.10)", border: "1px solid rgba(109,189,109,0.4)", marginBottom: 16 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.18)"/>
                  <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C" }}>You're cleared to book</div>
                  <div style={{ fontSize: 11, color: "#4A6B8A" }}>Welcome back, {adminCodeRecord.customerName}. Continue to confirm your trip.</div>
                </div>
              </div>
            ) : adminCodeStatus === "checking" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.20)", marginBottom: 16 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${teal}`, borderTopColor: "transparent", animation: "jgaSpin 0.7s linear infinite", flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: "#4A6B8A" }}>Verifying your booking access…</div>
              </div>
            ) : (
              // Phase 1 default: show waitlist signup instead of code entry
              <div style={{ background: `linear-gradient(135deg, rgba(74,140,196,0.10) 0%, rgba(74,140,196,0.04) 100%)`, border: `1px solid rgba(74,140,196,0.35)`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                {waitlistStatus === "joined" ? (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
                      <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.18)"/>
                      <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1B3A5C", marginBottom: 4 }}>You're on the waitlist!</div>
                      <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.5 }}>
                        We'll email you at <strong>{user?.email || "your email"}</strong> when bookings open. Save {dest.name} to a board so it's easy to find later.
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#4A8CC4", fontWeight: 800, marginBottom: 4 }}>JOIN THE WAITLIST TO BOOK</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, lineHeight: 1.35, marginBottom: 6 }}>
                      {dest.name} sounds great. Want to be first in line?
                    </div>
                    <div style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 12, lineHeight: 1.5 }}>
                      We're rolling out booking access in waves — tell us a bit about your trip and we'll email you when you're cleared to book.
                    </div>
                    <textarea value={waitlistNotes}
                      onChange={e => setWaitlistNotes(e.target.value)}
                      placeholder="Anything you want us to know? (Optional — dates, who you're traveling with, special occasion, etc.)"
                      rows={3}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box", resize: "vertical", marginBottom: 10 }}
                    />
                    <button onClick={async () => {
                        if (!user?.email) {
                          alert("Please sign in or register first so we know where to email you when you're cleared to book.");
                          return;
                        }
                        setWaitlistStatus("sending");
                        const result = await joinWaitlist({
                          customerName: user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "",
                          email: user.email,
                          destination: dest?.name,
                          departureAirport: user?.departureAirport,
                          travelDates: departDate && returnDate ? `${departDate} to ${returnDate}` : undefined,
                          duration: `${tripDays} nights`,
                          budget: String(parsedBudget),
                          mode: "explore",
                          notes: waitlistNotes.trim() || undefined,
                        });
                        if (result.ok) setWaitlistStatus("joined");
                        else setWaitlistStatus("error");
                      }}
                      disabled={waitlistStatus === "sending"}
                      style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: waitlistStatus === "sending" ? "rgba(74,140,196,0.5)" : `linear-gradient(135deg, #4A8CC4, #8B5F70)`, color: "#fff", fontWeight: 800, fontSize: 14, cursor: waitlistStatus === "sending" ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                      {waitlistStatus === "sending" ? "Joining…" : waitlistStatus === "error" ? "Try again" : "Join the waitlist →"}
                    </button>
                    {waitlistStatus === "error" && (
                      <div style={{ fontSize: 11, color: coral, marginTop: 8, textAlign: "center" }}>Something went wrong. Please try again or email support@justgoanywhere.com</div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Continue / Close buttons */}
            {adminCodeStatus === "valid" ? (
              <button onClick={() => setStep(1)} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                Continue to Payment →
              </button>
            ) : null}
            <button onClick={onClose} style={{ width: "100%", marginTop: adminCodeStatus === "valid" ? 8 : 0, padding: "13px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              {waitlistStatus === "joined" ? "Done — back to browsing" : "Close"}
            </button>
          </div>
        )}

        {/* Step 1 — Payment */}
        {step === 1 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <button onClick={() => setStep(0)} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>Payment Details</h2>
            </div>

            {/* ── Booking Reference — read-only display ── */}
            <div style={{ background: "rgba(27,58,92,0.07)", border: "1px solid rgba(27,58,92,0.25)", borderRadius: 10, padding: "10px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#1B3A5C", letterSpacing: 1 }}>BOOKING REFERENCE</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: teal, letterSpacing: 2, fontFamily: "monospace" }}>{confirmCode}</span>
            </div>

            {/* KTN (optional) */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#1B3A5C" }}>Known Traveler Number (KTN / TSA PreCheck) — optional</span>
                <button onClick={() => setShowKtnRequest(s => !s)} style={{ background: "none", border: "none", color: teal, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                  {user?.ktn ? "Edit" : "Add →"}
                </button>
              </div>
              {showKtnRequest && (
                <div style={{ background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "12px 14px" }}>
                  {ktnRequested ? (
                    <div style={{ fontSize: 12, color: "#6dbd6d", fontWeight: 700 }}>✓ KTN saved.</div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={ktnInput} onChange={e => setKtnInput(e.target.value)} placeholder="e.g. 12345678" aria-label="Known Traveler Number"
                        style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(27,58,92,0.3)", background: "rgba(27,58,92,0.060)", color: "#1B3A5C", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                      <button onClick={async () => {
                        try { await fetch(`${_api()}/api/ktn-request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: user?.email, name: user?.firstName, ktn: ktnInput, bookingRef: confirmCode }) }); } catch(e) {}
                        setKtnRequested(true);
                      }} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: teal, color: "#FAFAF5", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                        {ktnInput ? "Save" : "Request →"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>


            <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.18)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#1B3A5C" }}>{dest.emoji} {dest.name} · {tripDays}d{addInsurance ? " + insurance" : ""}</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: goldText }}>${totalCost.toLocaleString()}</span>
            </div>

            {/* ── Apple Pay / Google Pay button ── */}
            <div style={{ marginBottom: 16 }}>
              {/* In production: render Stripe PaymentRequestButton here.
                  It auto-detects Apple Pay on Safari/iOS, Google Pay on Chrome/Android.
                  Only shows if the device & browser support it. */}
              <button
                onClick={() => {
                  /* TODO: stripe.paymentRequest({...}).show() */
                  alert("Apple Pay / Google Pay will be connected via Stripe. Add your Stripe publishable key to enable.");
                }}
                style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "#000", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: 0.3 }}>
                <svg width="18" height="18" viewBox="0 0 50 50" fill="white">
                  <path d="M34.5 5.5c-2.1 2.5-5.5 4.4-8.8 4.1-.4-3.4 1.2-7 3.1-9.2C31 -1.7 34.7-3.6 37.6-3.8c.4 3.5-1 6.9-3.1 9.3zM37.5 10.5c-4.9-.3-9 2.8-11.3 2.8-2.4 0-6-2.6-9.9-2.6C11 10.9 5.6 14.4 2.7 19.8c-5.9 10.2-1.5 25.3 4.2 33.6 2.8 4.1 6.1 8.6 10.5 8.4 4.2-.2 5.8-2.7 10.9-2.7 5.1 0 6.5 2.7 11 2.6 4.5-.1 7.4-4.1 10.2-8.2 3.2-4.7 4.5-9.2 4.6-9.4-.1-.1-8.8-3.4-8.9-13.5-.1-8.5 6.9-12.5 7.2-12.7-3.9-5.8-10-6.4-12.9-6.4z"/>
                </svg>
                Pay with Apple Pay
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(27,58,92,0.096)" }} />
                <span style={{ fontSize: 11, color: "#4A6B8A", letterSpacing: 1 }}>OR PAY BY CARD</span>
                <div style={{ flex: 1, height: 1, background: "rgba(27,58,92,0.096)" }} />
              </div>
            </div>

            {/* ── Card form ── */}
            <Label>Cardholder Name</Label>
            <input value={card.name} onChange={e => setCard(c => ({ ...c, name: e.target.value }))} placeholder="Jane Smith" aria-label="Cardholder name"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: `1px solid ${errors.name ? coral : "rgba(27,58,92,0.144)"}`, color: "#1B3A5C", outline: "none", boxSizing: "border-box", marginBottom: errors.name ? 4 : 14 }} />
            {errors.name && <div style={{ color: coral, fontSize: 11, marginBottom: 10 }}>{errors.name}</div>}

            <Label>Card Number</Label>
            <input value={card.number} onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/[^\d\s]/g,"").slice(0,19) }))} placeholder="1234 5678 9012 3456" aria-label="Card number" maxLength={19}
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: `1px solid ${errors.number ? coral : "rgba(27,58,92,0.144)"}`, color: "#1B3A5C", outline: "none", boxSizing: "border-box", marginBottom: errors.number ? 4 : 14 }} />
            {errors.number && <div style={{ color: coral, fontSize: 11, marginBottom: 10 }}>{errors.number}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 6 }}>
              <div>
                <Label>Expiration Date</Label>
                <input value={card.expiry} onChange={e => setCard(c => ({ ...c, expiry: e.target.value }))} placeholder="MM/YY" aria-label="Card expiry date" maxLength={5}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: `1px solid ${errors.expiry ? coral : "rgba(27,58,92,0.144)"}`, color: "#1B3A5C", outline: "none", boxSizing: "border-box" }} />
                {errors.expiry && <div style={{ color: coral, fontSize: 11, marginTop: 4 }}>{errors.expiry}</div>}
              </div>
              <div>
                <Label>CVV</Label>
                <input value={card.cvv} onChange={e => setCard(c => ({ ...c, cvv: e.target.value.replace(/\D/g,"").slice(0,4) }))} placeholder="•••" aria-label="CVV security code" maxLength={4}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: `1px solid ${errors.cvv ? coral : "rgba(27,58,92,0.144)"}`, color: "#1B3A5C", outline: "none", boxSizing: "border-box" }} />
                {errors.cvv && <div style={{ color: coral, fontSize: 11, marginTop: 4 }}>{errors.cvv}</div>}
              </div>
            </div>

            <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "12px 14px", marginTop: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span style={{ fontSize: 11, color: "#4A6B8A", lineHeight: 1.5 }}>Your payment is encrypted and secure. This is a prototype — no real charges will be made.</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {/* Stripe badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, background: "rgba(99,91,255,0.12)", border: "1px solid rgba(99,91,255,0.25)" }}>
                  <svg width="40" height="16" viewBox="0 0 60 25" fill="none"><text x="0" y="18" fontSize="17" fontWeight="900" fill="#635bff" fontFamily="sans-serif">stripe</text></svg>
                </div>
                {/* Apple Pay badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, background: "rgba(27,58,92,0.072)", border: "1px solid rgba(27,58,92,0.14)" }}>
                  <svg width="14" height="14" viewBox="0 0 50 50" fill="white" opacity="0.85"><path d="M34.5 5.5c-2.1 2.5-5.5 4.4-8.8 4.1-.4-3.4 1.2-7 3.1-9.2C31-1.7 34.7-3.6 37.6-3.8c.4 3.5-1 6.9-3.1 9.3zM37.5 10.5c-4.9-.3-9 2.8-11.3 2.8-2.4 0-6-2.6-9.9-2.6C11 10.9 5.6 14.4 2.7 19.8c-5.9 10.2-1.5 25.3 4.2 33.6 2.8 4.1 6.1 8.6 10.5 8.4 4.2-.2 5.8-2.7 10.9-2.7 5.1 0 6.5 2.7 11 2.6 4.5-.1 7.4-4.1 10.2-8.2 3.2-4.7 4.5-9.2 4.6-9.4-.1-.1-8.8-3.4-8.9-13.5-.1-8.5 6.9-12.5 7.2-12.7-3.9-5.8-10-6.4-12.9-6.4z"/></svg>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#FAFAF5" }}>Pay</span>
                </div>
                {/* Visa */}
                <div style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(27,58,92,0.072)", border: "1px solid rgba(27,58,92,0.14)" }}>
                  <svg aria-hidden="true" width="32" height="14" viewBox="0 0 60 20"><text x="0" y="16" fontSize="18" fontWeight="900" fill="#1a1f71" fontFamily="sans-serif">VISA</text></svg>
                </div>
                {/* Mastercard */}
                <div style={{ padding: "4px 8px", borderRadius: 6, background: "rgba(27,58,92,0.072)", border: "1px solid rgba(27,58,92,0.14)", display: "flex", alignItems: "center", gap: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#eb001b", opacity: 0.9 }} />
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#f79e1b", opacity: 0.9, marginLeft: -6 }} />
                </div>
              </div>
            </div>

            {/* Cancellation policy summary at checkout */}
            <div style={{ background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.15)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: teal, textTransform: "uppercase", marginBottom: 8 }}>Cancellation Policy</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {[
                  ["Within 24h of booking", "Full refund", "#6dbd6d"],
                  ["30+ days before departure", "Full refund", "#6dbd6d"],
                  ["15–30 days before", "50% refund", gold],
                  ["7–14 days before", "25% refund", amber],
                  ["Under 7 days", "No refund", coral],
                ].map(([when, what, col]) => (
                  <div key={when} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                    <span style={{ color: "#4A6B8A" }}>{when}</span>
                    <span style={{ color: col, fontWeight: 700 }}>{what}</span>
                  </div>
                ))}
              </div>
              {addInsurance && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(27,58,92,0.1)", fontSize: 11, color: "#6dbd6d" }}>
                  ✓ Your insurance cover may override these limits — check your SafetyWing policy for covered reasons.
                </div>
              )}
            </div>

            <TrustBand variant="compact" darkMode={true} style={{ marginBottom: 10 }} />
            <button onClick={handlePay} disabled={processing}
              style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: processing ? "rgba(27,58,92,0.120)" : `linear-gradient(135deg,${gold},${amber})`, color: processing ? "#4A6B8A" : "#FAFAF5", fontWeight: 900, fontSize: 16, cursor: processing ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {processing ? "Processing…" : `Pay $${totalCost.toLocaleString()} & Confirm Booking`}
            </button>
          </div>
        )}

        {/* Step 1.5 — Processing (waiting for backend) */}
        {step === 1.5 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <style>{`
              @keyframes jgaSpin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
              @keyframes jgaPulse { 0%,100%{opacity:0.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.04)} }
              @keyframes jgaDot { 0%,80%,100%{opacity:0.3} 40%{opacity:1} }
            `}</style>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20, position: "relative", height: 80 }}>
              {/* Outer rotating ring */}
              <svg width="80" height="80" viewBox="0 0 44 44" fill="none" style={{ animation: "jgaSpin 2.5s linear infinite", position: "absolute" }}>
                <circle cx="22" cy="22" r="20" stroke={teal} strokeWidth="1.5" strokeDasharray="4 6" opacity="0.4"/>
              </svg>
              {/* Inner globe */}
              <svg width="80" height="80" viewBox="0 0 44 44" fill="none" style={{ animation: "jgaPulse 1.8s ease-in-out infinite", position: "absolute" }}>
                <circle cx="22" cy="22" r="14" fill={`${teal}25`} stroke={teal} strokeWidth="1.8"/>
                <ellipse cx="22" cy="22" rx="6" ry="14" fill="none" stroke={teal} strokeWidth="1.4"/>
                <path d="M8 22 Q15 19 22 22 Q29 25 36 22" stroke={teal} strokeWidth="1.4" fill="none"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: goldText, marginBottom: 6 }}>
              Your booking is in process
              <span style={{ display: "inline-block", marginLeft: 4 }}>
                <span style={{ display: "inline-block", animation: "jgaDot 1.4s ease-in-out infinite" }}>.</span>
                <span style={{ display: "inline-block", animation: "jgaDot 1.4s ease-in-out 0.2s infinite" }}>.</span>
                <span style={{ display: "inline-block", animation: "jgaDot 1.4s ease-in-out 0.4s infinite" }}>.</span>
              </span>
            </h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 24, lineHeight: 1.6, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
              Please don't close this window. We're confirming your payment and securing your trip with our partners.
            </p>

            {/* Status checklist */}
            <div style={{ background: "rgba(27,58,92,0.05)", border: `1px solid ${teal}25`, borderRadius: 12, padding: "14px 18px", marginBottom: 16, textAlign: "left", maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: "#6dbd6d" }}>✓</span>
                <span style={{ fontSize: 12, color: textPrimary, fontWeight: 700 }}>Payment received</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: teal, animation: "jgaPulse 1.2s ease-in-out infinite" }}>⟳</span>
                <span style={{ fontSize: 12, color: textPrimary, fontWeight: 700 }}>Confirming with our partners…</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.5 }}>
                <span style={{ fontSize: 14, color: textDim }}>○</span>
                <span style={{ fontSize: 12, color: textMuted }}>Sending email confirmation</span>
              </div>
            </div>

            <div style={{ fontSize: 11, color: textDim, fontStyle: "italic" }}>
              This usually takes 5–15 seconds.
            </div>
          </div>
        )}

        {/* Step 2 — Confirmed */}
        {step === 2 && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <svg width="72" height="72" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="22" cy="22" r="19" fill={`${teal}22`} stroke={teal} strokeWidth="2"/>
                <ellipse cx="22" cy="22" rx="9" ry="19" fill={`${teal}10`} stroke={teal} strokeWidth="1.6"/>
                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                <circle cx="22" cy="22" r="5" fill={`${gold}30`} stroke={gold} strokeWidth="1.5"/>
                <path d="M22 16 L24 20 L28 20 L25 23 L26 27 L22 24 L18 27 L19 23 L16 20 L20 20 Z" fill={gold} opacity="0.9"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: goldText, marginBottom: 8 }}>You're going to {dest.name}!</h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
              Your booking is confirmed. Get ready for an incredible trip.
            </p>
            {bookingError && (
              <div style={{ background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.3)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: goldText, lineHeight: 1.5, textAlign: "left" }}>
                <strong>ⓘ Note:</strong> {bookingError}
              </div>
            )}

            <div style={{ background: "rgba(27,58,92,0.07)", border: "1px solid rgba(27,58,92,0.25)", borderRadius: 14, padding: "18px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: teal, textTransform: "uppercase", marginBottom: 8 }}>Booking Confirmation</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1B3A5C", letterSpacing: 2, marginBottom: 12 }}>{confirmCode}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  ["Destination", `${dest.emoji} ${dest.name}`],
                  ["Duration", `${tripDays} days`],
                  ["Departure", departDate || "Flexible"],
                  ["Total Paid", `$${totalCost.toLocaleString()}`],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ fontSize: 10, color: "#4A6B8A", marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1B3A5C" }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {addInsurance && (
              <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#6dbd6d", textAlign: "left" }}>
                🛡️ {insurancePlans[insurancePlan].label} active · Policy documents sent to your email
              </div>
            )}

            {/* ── Postcard reveal ──
                  After a successful booking, surface the destination's postcard
                  as a souvenir moment. Auto-saved to the user's collection in
                  the booking handler above. If no postcard illustration exists
                  for this destination (one of the fallback-fact destinations),
                  we render a fact card instead so the moment isn't empty.
                  Both branches share the same accent treatment so the surface
                  feels consistent across all destinations. */}
            {(() => {
              const postcardId = getPostcardIdForDest(dest.name);
              const fact = getFunFactForDest(dest.name);
              if (!postcardId && !fact) return null;
              return (
                <div style={{
                  marginBottom: 16,
                  borderRadius: 14,
                  overflow: "hidden",
                  border: `1px solid ${gold}40`,
                  background: `linear-gradient(135deg, ${gold}10, ${gold}05)`,
                  textAlign: "left",
                }}>
                  <div style={{
                    padding: "10px 14px",
                    background: `${gold}15`,
                    borderBottom: `1px solid ${gold}30`,
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 900, letterSpacing: 2,
                      textTransform: "uppercase", color: goldText,
                    }}>
                      ✨ {postcardId ? "Postcard earned · Saved to your collection" : "A souvenir for your trip"}
                    </div>
                  </div>
                  {postcardId && (
                    <div style={{ aspectRatio: "7 / 5", background: "#FAFAF5" }}>
                      <PostcardIllustration id={postcardId} />
                    </div>
                  )}
                  {fact && (
                    <div style={{ padding: "12px 14px" }}>
                      <div style={{
                        fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
                        textTransform: "uppercase", color: "#4A8CC4", marginBottom: 6,
                      }}>
                        Did you know?
                      </div>
                      <div style={{ fontSize: 12, color: "#1B3A5C", lineHeight: 1.5 }}>
                        {fact}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <p style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 16, fontStyle: "italic" }}>
              A confirmation has been sent to your email. Have an amazing trip! ✈️
            </p>

            {/* Manage booking row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              <button onClick={onClose} style={{ flex: 2, padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                Back to Just Go Anywhere
              </button>
              <button onClick={() => setStep(3)} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1px solid rgba(255,107,107,0.3)", background: "rgba(255,107,107,0.05)", color: coral, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel Booking
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Cancellation flow */}
        {step === 3 && (
          <CancellationFlow
            dest={dest}
            departDate={departDate}
            totalCost={totalCost}
            confirmCode={confirmCode}
            addInsurance={addInsurance}
            insurancePlan={insurancePlan}
            insurancePlans={insurancePlans}
            onBack={() => setStep(2)}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

// ── Cancellation Flow ───────────────────────────────────────────
function CancellationFlow({ dest, departDate, totalCost, confirmCode, addInsurance, insurancePlan, insurancePlans, onBack, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const [cancelStep, setCancelStep] = useState(0); // 0=policy, 1=reason, 2=confirmed
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const today = new Date();
  const depart = departDate ? new Date(departDate) : null;
  const daysUntil = depart ? Math.ceil((depart - today) / 86400000) : 999;

  const refundPct = daysUntil > 30 ? 100 : daysUntil > 14 ? 50 : daysUntil > 7 ? 25 : 0;
  const refundAmt = Math.round(totalCost * refundPct / 100);
  const refundColor = refundPct === 100 ? "#6dbd6d" : refundPct >= 50 ? gold : refundPct > 0 ? amber : coral;

  const REASONS = [
    { id: "illness", label: "Illness or medical emergency", insurable: true },
    { id: "family", label: "Family emergency", insurable: true },
    { id: "work", label: "Work or schedule conflict", insurable: false },
    { id: "plans", label: "Plans changed", insurable: false },
    { id: "price", label: "Found a better price", insurable: false },
    { id: "weather", label: "Severe weather / force majeure", insurable: true },
    { id: "other", label: "Other reason", insurable: false },
  ];

  const selectedReason = REASONS.find(r => r.id === reason);
  const isInsurable = selectedReason?.insurable && addInsurance;

  function confirmCancel() {
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setCancelStep(2); }, 1800);
  }

  return (
    <div>
      {/* Step 0 — Policy & refund preview */}
      {cancelStep === 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
            <button onClick={onBack} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: coral }}>Cancel Booking</h2>
          </div>

          {/* Booking being cancelled */}
          <div style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 12, padding: "14px 16px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 36 }}>{dest.emoji}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1B3A5C" }}>{dest.name}</div>
              <div style={{ fontSize: 11, color: "#4A6B8A" }}>Ref: {confirmCode} · {departDate || "Flexible dates"}</div>
            </div>
          </div>

          {/* Refund amount */}
          <div style={{ background: `rgba(${refundPct === 100 ? "109,189,109" : refundPct >= 50 ? "245,215,142" : refundPct > 0 ? "201,120,50" : "255,107,107"},0.08)`, border: `1px solid rgba(${refundPct === 100 ? "109,189,109" : refundPct >= 50 ? "245,215,142" : refundPct > 0 ? "201,120,50" : "255,107,107"},0.25)`, borderRadius: 14, padding: "18px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#4A6B8A", marginBottom: 6 }}>
              {depart ? `${daysUntil} days until departure` : "Departure date flexible"}
            </div>
            <div style={{ fontSize: 13, color: "#4A6B8A", marginBottom: 8 }}>Your refund under current policy</div>
            <div style={{ fontSize: 36, fontWeight: 900, color: refundColor }}>${refundAmt.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: "#4A6B8A", marginTop: 4 }}>
              {refundPct}% of ${totalCost.toLocaleString()} paid · Returned to original payment method in 5–10 days
            </div>
          </div>

          {/* Full policy breakdown */}
          <div style={{ marginBottom: 16 }}>
            <Label color="#4A8CC4">Cancellation Policy</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { when: "Within 24h of booking", pct: 100, days: null },
                { when: "30+ days before departure", pct: 100, days: 30 },
                { when: "15–30 days before", pct: 50, days: 15 },
                { when: "7–14 days before", pct: 25, days: 7 },
                { when: "Under 7 days", pct: 0, days: 0 },
              ].map(({ when, pct, days: d }) => {
                const isActive = d === null ? false : daysUntil >= (d === 30 ? 30 : d === 15 ? 15 : d === 7 ? 7 : 0) && (d === 30 ? true : daysUntil < (d === 15 ? 30 : d === 7 ? 15 : 7));
                const col = pct === 100 ? "#6dbd6d" : pct === 50 ? gold : pct === 25 ? amber : coral;
                return (
                  <div key={when} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 8, background: isActive ? "rgba(27,58,92,0.060)" : "transparent", border: isActive ? "1px solid rgba(27,58,92,0.120)" : "1px solid transparent", fontSize: 12 }}>
                    <span style={{ color: isActive ? "#1B3A5C" : "#4A6B8A" }}>
                      {isActive && <span style={{ color: teal, marginRight: 6 }}>→</span>}{when}
                    </span>
                    <span style={{ color: col, fontWeight: 700 }}>{pct}% refund</span>
                  </div>
                );
              })}
            </div>
          </div>

          {addInsurance && (
            <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 12, color: "#6dbd6d", lineHeight: 1.7 }}>
              🛡️ You have <strong>{insurancePlans[insurancePlan].label}</strong>. If you're cancelling due to illness, family emergency, or force majeure, your insurer may cover the full trip cost regardless of this policy.
              <div style={{ marginTop: 6 }}>
                <a href="https://safetywing.com/?referenceID=26515446&utm_source=26515446&utm_medium=Ambassador" target="_blank" rel="noopener noreferrer" style={{ background: "none", border: "none", color: "#6dbd6d", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 }}>Get a SafetyWing quote →</a>
              </div>
            </div>
          )}

          <button onClick={() => setCancelStep(1)} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,#FF6B6B,${coral})`, color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
            Continue with Cancellation →
          </button>
          <button onClick={onBack} style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Keep My Booking
          </button>
        </div>
      )}

      {/* Step 1 — Reason */}
      {cancelStep === 1 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
            <button onClick={() => setCancelStep(0)} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: coral }}>Reason for Cancellation</h2>
          </div>

          <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 18, fontStyle: "italic" }}>This helps us improve and — if insured — may support your claim.</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {REASONS.map(r => (
              <button key={r.id} onClick={() => setReason(r.id)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s",
                border: `1px solid ${reason === r.id ? "rgba(255,107,107,0.5)" : "rgba(27,58,92,0.096)"}`,
                background: reason === r.id ? "rgba(255,107,107,0.08)" : "rgba(27,58,92,0.030)"
              }}>
                <span style={{ fontSize: 13, color: reason === r.id ? "#f0c0c0" : "#4A6B8A" }}>{r.label}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {r.insurable && addInsurance && (
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "rgba(109,189,109,0.15)", color: "#6dbd6d" }}>insurable</span>
                  )}
                  <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${reason === r.id ? coral : "rgba(27,58,92,0.18)"}`, background: reason === r.id ? coral : "transparent" }} />
                </div>
              </button>
            ))}
          </div>

          {isInsurable && (
            <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 12, color: "#6dbd6d", lineHeight: 1.7 }}>
              ✓ This reason is covered by your {insurancePlans[insurancePlan].label}. Consider filing an insurance claim for a potentially higher refund.
              <div style={{ marginTop: 6 }}>
                <a href="https://safetywing.com/?referenceID=26515446&utm_source=26515446&utm_medium=Ambassador" target="_blank" rel="noopener noreferrer" style={{ background: "none", border: "none", color: "#6dbd6d", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 }}>Get a SafetyWing quote →</a>
              </div>
            </div>
          )}

          {/* Final refund summary before confirm */}
          <div style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: "#4A6B8A" }}>Trip cost paid</span>
              <span style={{ fontSize: 13, color: "#1B3A5C" }}>${totalCost.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: "#4A6B8A" }}>Refund ({refundPct}%)</span>
              <span style={{ fontSize: 13, color: refundColor, fontWeight: 700 }}>${refundAmt.toLocaleString()}</span>
            </div>
            <div style={{ borderTop: "1px solid rgba(255,107,107,0.15)", paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#4A6B8A" }}>Amount forfeited</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: coral }}>${(totalCost - refundAmt).toLocaleString()}</span>
            </div>
          </div>

          <button onClick={confirmCancel} disabled={!reason || processing} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: !reason || processing ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg,#FF6B6B,${coral})`, color: !reason || processing ? "#4A6B8A" : "#fff", fontWeight: 900, fontSize: 15, cursor: !reason ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {processing ? "Cancelling booking…" : `Confirm Cancellation & Refund $${refundAmt.toLocaleString()}`}
          </button>
          <button onClick={() => setCancelStep(0)} style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            ← Back
          </button>
        </div>
      )}

      {/* Step 2 — Cancellation confirmed */}
      {cancelStep === 2 && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <svg width="72" height="72" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="22" cy="22" r="19" fill="rgba(109,189,109,0.12)" stroke="#6dbd6d" strokeWidth="2"/>
                <circle cx="22" cy="22" r="13" fill="rgba(109,189,109,0.08)" stroke="#6dbd6d" strokeWidth="1.2" opacity="0.6"/>
                <path d="M13 22 L19 28 L31 16" stroke="#6dbd6d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: "#6dbd6d", marginBottom: 8 }}>Booking Cancelled</h2>
          <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 22, lineHeight: 1.6 }}>
            Your booking for {dest.name} has been cancelled. A confirmation has been sent to your email.
          </p>

          <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 14, padding: "18px", marginBottom: 16, textAlign: "left" }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: "#6dbd6d", textTransform: "uppercase", marginBottom: 10 }}>Refund Details</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: "#4A6B8A" }}>Refund amount</span>
              <span style={{ fontSize: 20, fontWeight: 900, color: darkMode ? "#6dbd6d" : "#2a7a4a" }}>${refundAmt.toLocaleString()}</span>
            </div>
            <div style={{ fontSize: 12, color: "#4A6B8A" }}>Returned to your original payment method · Allow 5–10 business days</div>
            {refundAmt < totalCost && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#7a6050" }}>
                ${(totalCost - refundAmt).toLocaleString()} forfeited per our cancellation policy
              </div>
            )}
          </div>

          {addInsurance && refundAmt < totalCost && (
            <div style={{ background: "rgba(27,58,92,0.07)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 20, fontSize: 12, color: "#1B3A5C", textAlign: "left", lineHeight: 1.7 }}>
              💡 You may be able to recover the remaining ${(totalCost - refundAmt).toLocaleString()} through your SafetyWing cover. Check your policy documents or contact SafetyWing directly.
            </div>
          )}

          <button onClick={onClose} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
            Back to Just Go Anywhere
          </button>
        </div>
      )}
    </div>
  );
}

// ── Trip Survey Modal ────────────────────────────────────────────
function TripSurveyModal({ trip, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const [step, setStep] = useState(0);
  const [ratings, setRatings] = useState({ overall: 0, accommodation: 0, flights: 0, activities: 0, value: 0 });
  const [wouldReturn, setWouldReturn] = useState(null);
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [recommendNote, setRecommendNote] = useState("");
  const [highlight, setHighlight] = useState("");
  const [improve, setImprove] = useState("");
  const [usedSurpriseMe, setUsedSurpriseMe] = useState(false);
  const [surpriseRating, setSurpriseRating] = useState(0);

  function StarRow({ label, field }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: "#c0d0e0", minWidth: 120 }}>{label}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setRatings(r => ({ ...r, [field]: n }))}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: "2px", color: n <= ratings[field] ? "#4A8CC4" : "rgba(27,58,92,0.14)", transition: "color 0.15s" }}>
              ★
            </button>
          ))}
        </div>
      </div>
    );
  }

  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box", resize: "vertical" };
  const backBtn = (toStep) => (
    <button onClick={() => setStep(toStep)} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit", marginBottom: 18 }} aria-label="Go back">← Back</button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.4s ease", border: "1px solid rgba(74,140,196,0.28)" }}>

        {/* Progress bar */}
        {step < 3 && (
          <div style={{ display: "flex", gap: 4, marginBottom: 22 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? `linear-gradient(90deg,${gold},${amber})` : "rgba(27,58,92,0.120)", transition: "background 0.3s" }} />
            ))}
          </div>
        )}

        {/* Step 0 — Intro */}
        {step === 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>{trip.emoji}</div>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 8 }}>Welcome back!</div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#1B3A5C", marginBottom: 8 }}>How was {trip.dest}?</h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, lineHeight: 1.6, marginBottom: 6 }}>
              Your trip ({trip.date}) has wrapped up. We'd love to hear how it went — takes about 2 minutes.
            </p>
            <div style={{ fontSize: 12, color: "#4A8CC4", marginBottom: 24, fontStyle: "italic" }}>Ref: {trip.code}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 2, padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                Share My Experience →
              </button>
              <button onClick={onClose} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                Maybe later
              </button>
            </div>
          </div>
        )}

        {/* Step 1 — Star Ratings */}
        {step === 1 && (
          <div>
            {backBtn(0)}
            <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>Rate your experience</h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 24, fontStyle: "italic" }}>{trip.emoji} {trip.dest} · {trip.date}</p>

            <StarRow label="Overall trip" field="overall" />
            <StarRow label="Accommodation" field="accommodation" />
            <StarRow label="Flights" field="flights" />
            <StarRow label="Activities" field="activities" />
            <StarRow label="Value for money" field="value" />

            <div style={{ borderTop: "1px solid rgba(27,58,92,0.084)", paddingTop: 16, marginTop: 8, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "#c0d0e0", marginBottom: 10 }}>Would you return to {trip.dest}?</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["Definitely", "Maybe", "Probably not"].map(opt => (
                  <button key={opt} onClick={() => setWouldReturn(opt)}
                    style={{ flex: 1, padding: "9px 6px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: wouldReturn === opt ? 700 : 400, border: `1px solid ${wouldReturn === opt ? "rgba(74,140,196,0.5)" : "rgba(27,58,92,0.120)"}`, background: wouldReturn === opt ? "rgba(74,140,196,0.1)" : "rgba(27,58,92,0.045)", color: wouldReturn === opt ? gold : "#4A6B8A" }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "#c0d0e0", marginBottom: 10 }}>Would you recommend Just Go Anywhere to a friend?</div>
              <div style={{ display: "flex", gap: 8, marginBottom: wouldRecommend ? 10 : 0 }}>
                {["Yes, absolutely", "Yes, with caveats", "Not sure", "No"].map(opt => (
                  <button key={opt} onClick={() => { setWouldRecommend(opt); setRecommendNote(""); }}
                    style={{ flex: 1, padding: "8px 4px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: wouldRecommend === opt ? 700 : 400, border: `1px solid ${wouldRecommend === opt ? "rgba(27,58,92,0.5)" : "rgba(27,58,92,0.120)"}`, background: wouldRecommend === opt ? "rgba(27,58,92,0.1)" : "rgba(27,58,92,0.045)", color: wouldRecommend === opt ? teal : "#4A6B8A" }}>
                    {opt}
                  </button>
                ))}
              </div>
              {wouldRecommend && (
                <textarea
                  value={recommendNote}
                  onChange={e => setRecommendNote(e.target.value)}
                  rows={2}
                  placeholder={
                    wouldRecommend === "Yes, absolutely" ? "What would you tell them about it?" :
                    wouldRecommend === "Yes, with caveats" ? "What caveats would you mention?" :
                    wouldRecommend === "Not sure" ? "What's holding you back?" :
                    "What put you off recommending us?"
                  }
                  style={{ ...inputStyle, marginTop: 2, fontSize: 12 }}
                />
              )}
            </div>

            <button onClick={() => setStep(2)} disabled={!ratings.overall}
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: !ratings.overall ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg,${gold},${amber})`, color: !ratings.overall ? "#4A6B8A" : "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: !ratings.overall ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              Next →
            </button>
          </div>
        )}

        {/* Step 2 — Open feedback */}
        {step === 2 && (
          <div>
            {backBtn(1)}
            <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>Tell us more</h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, marginBottom: 22, fontStyle: "italic" }}>Your feedback shapes future recommendations.</p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Best part of the trip</label>
              <textarea value={highlight} onChange={e => setHighlight(e.target.value)} rows={2}
                placeholder="What was the highlight? Best meal, unexpected moment, view that blew you away..."
                style={inputStyle} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase", display: "block", marginBottom: 8 }}>What could we improve?</label>
              <textarea value={improve} onChange={e => setImprove(e.target.value)} rows={2}
                placeholder="Anything that didn't meet expectations or we could have done better..."
                style={inputStyle} />
            </div>

            {/* Surprise Me follow-up */}
            <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.18)", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "#c0d0e0", marginBottom: 10 }}>Was this a Surprise Me booking?</div>
              <div style={{ display: "flex", gap: 8, marginBottom: usedSurpriseMe ? 14 : 0 }}>
                {["Yes", "No"].map(opt => (
                  <button key={opt} onClick={() => setUsedSurpriseMe(opt === "Yes")}
                    style={{ flex: 1, padding: "9px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: usedSurpriseMe === (opt === "Yes") ? 700 : 400, border: `1px solid ${usedSurpriseMe === (opt === "Yes") ? "rgba(27,58,92,0.5)" : "rgba(27,58,92,0.120)"}`, background: usedSurpriseMe === (opt === "Yes") ? "rgba(27,58,92,0.1)" : "rgba(27,58,92,0.045)", color: usedSurpriseMe === (opt === "Yes") ? teal : "#4A6B8A" }}>
                    {opt}
                  </button>
                ))}
              </div>
              {usedSurpriseMe && (
                <div>
                  <div style={{ fontSize: 12, color: "#1B3A5C", marginBottom: 8 }}>How was the surprise reveal experience?</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setSurpriseRating(n)}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: "2px", color: n <= surpriseRating ? teal : "rgba(27,58,92,0.14)", transition: "color 0.15s" }}>
                        ★
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <button onClick={() => setStep(3)} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                Submit Review
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Thank you */}
        {step === 3 && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🙏</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: goldText, marginBottom: 8 }}>Thank you!</h2>
            <p style={{ color: "#4A6B8A", fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>
              Your feedback helps us make every trip better. We've noted your {ratings.overall}-star rating for {trip.dest}.
            </p>
            {ratings.overall >= 4 && (
              <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#6dbd6d" }}>
                🎉 Glad you loved it! Share your trip and earn a referral credit when a friend books.
              </div>
            )}
            {ratings.overall <= 2 && (
              <div style={{ background: "rgba(255,107,107,0.07)", border: "1px solid rgba(255,107,107,0.18)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#e08080" }}>
                We're sorry it didn't meet your expectations. Our team will review your feedback and may reach out.
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button onClick={onClose} style={{ flex: 2, padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                Back to Just Go Anywhere
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Starred Plan Panel ──────────────────────────────────────────
function StarredPlanPanel({ starredAlerts, topList, accentColor, onClose, onExplore }) {
  const starred = starredAlerts.filter(name => topList.some(d => d.name === name));
  const [budgets, setBudgets] = useState({});
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState(null);

  const accentRgb = accentColor === coral ? "255,107,107" : "109,189,109";

  function saveBudget(name) {
    setSaving(name);
    setTimeout(() => {
      setSaved(s => ({ ...s, [name]: true }));
      setSaving(null);
    }, 700);
  }

  if (starred.length === 0) return null;

  return (
    <div style={{ marginTop: 14, background: "rgba(27,58,92,0.030)", border: `1px solid rgba(${accentRgb},0.18)`, borderRadius: 14, overflow: "hidden", animation: "fadeIn 0.3s ease" }}>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid rgba(${accentRgb},0.1)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1B3A5C" }}>My Wish List</div>
          <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2 }}>Set a budget for each destination — we'll alert you when prices drop within range</div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "#4A6B8A", fontSize: 16, cursor: "pointer", lineHeight: 1 }}>✕</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {starred.map((name, i) => {
          const dest = topList.find(d => d.name === name);
          if (!dest) return null;
          const bgt = budgets[name] || "";
          const isSaved = saved[name];
          const isSaving = saving === name;
          return (
            <div key={name} style={{ padding: "14px 16px", borderBottom: i < starred.length - 1 ? `1px solid rgba(27,58,92,0.060)` : "none", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{dest.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1B3A5C", marginBottom: 6 }}>{name}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: bgt ? gold : "#4A6B8A", fontWeight: 700, pointerEvents: "none" }}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={bgt}
                      onChange={e => { setBudgets(b => ({ ...b, [name]: e.target.value.replace(/\D/g, "") })); setSaved(s => ({ ...s, [name]: false })); }}
                      placeholder="Budget per person"
                      style={{ width: "100%", padding: "8px 10px 8px 24px", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: bgt ? "rgba(74,140,196,0.07)" : "rgba(27,58,92,0.060)", border: `1px solid ${bgt ? "rgba(74,140,196,0.4)" : "rgba(27,58,92,0.120)"}`, color: bgt ? gold : "#1B3A5C", outline: "none", boxSizing: "border-box" }}
                    />
                  </div>
                  <button
                    onClick={() => saveBudget(name)}
                    disabled={!bgt || isSaving}
                    style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: isSaved ? "rgba(109,189,109,0.25)" : !bgt ? "rgba(27,58,92,0.072)" : `linear-gradient(135deg,${gold},${amber})`, color: isSaved ? "#6dbd6d" : !bgt ? "#445" : "#FAFAF5", fontSize: 12, fontWeight: 700, cursor: !bgt ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s", minWidth: 72 }}>
                    {isSaving ? "…" : isSaved ? "✓ Saved" : "Save"}
                  </button>
                </div>
                {/* Quick budget picks */}
                <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                  {[800, 1200, 1500, 2000, 2500].map(b => (
                    <button key={b} onClick={() => { setBudgets(prev => ({ ...prev, [name]: String(b) })); setSaved(s => ({ ...s, [name]: false })); }}
                      style={{ padding: "3px 9px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 11, border: `1px solid ${bgt === String(b) ? "rgba(74,140,196,0.5)" : "rgba(27,58,92,0.096)"}`, background: bgt === String(b) ? "rgba(74,140,196,0.1)" : "rgba(27,58,92,0.030)", color: bgt === String(b) ? gold : "#4A6B8A" }}>
                      ${b >= 1000 ? b / 1000 + "k" : b}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ padding: "12px 16px", borderTop: `1px solid rgba(${accentRgb},0.1)`, display: "flex", gap: 8 }}>
        <button onClick={onExplore}
          style={{ flex: 2, padding: "11px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          Start Planning a Trip →
        </button>
        <button onClick={onClose}
          style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── Loyalty Picker Modal — adds an airline or hotel to the user's list ─
// Opens from the "Add more" buttons in the Profile tab. Shows only programs
// the user hasn't already added. Searchable by name or program name when
// list is long. Tap a row → adds it as unenrolled (user then taps Add to
// enter their number). Type-aware: takes either AIRLINE_LIST or HOTEL_LIST.
function LoyaltyPickerModal({ kind, items, alreadyAdded, onAdd, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();
  const [query, setQuery] = useState("");
  const filtered = items.filter(item => {
    if (alreadyAdded.has(item.id)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return item.name.toLowerCase().includes(q) || item.program.toLowerCase().includes(q);
  });

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)", animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ ...lightCard(darkMode), width: "100%", maxWidth: 460, maxHeight: "82vh", overflowY: "auto", border: "1px solid rgba(74,140,196,0.25)", position: "relative" }}>
        {/* Top-right X */}
        <button onClick={onClose} aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 16,
            border: "none", background: darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.05)",
            color: darkMode ? "rgba(240,230,211,0.7)" : "rgba(0,0,0,0.55)",
            cursor: "pointer", padding: 0, fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s", zIndex: 1,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6 L 18 18 M18 6 L 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        </button>

        <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: textPrimary }}>
          Add {kind === "airline" ? "an airline" : "a hotel"}
        </h2>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
          {kind === "airline"
            ? "Select an airline to add to your loyalty list. You can enter your number afterward."
            : "Select a hotel chain to add to your loyalty list. You can enter your number afterward."}
        </p>

        {/* Search input — visible only when list is longer than 5 to avoid overdesign */}
        {items.length > 5 && (
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={`Search ${kind === "airline" ? "airlines" : "hotels"}…`}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontFamily: "inherit",
              background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)",
              color: "#1B3A5C", outline: "none", boxSizing: "border-box", marginBottom: 12 }} />
        )}

        {filtered.length === 0 ? (
          <div style={{ padding: "28px 16px", textAlign: "center", borderRadius: 10,
            border: `1px dashed ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.08)"}`,
            background: "rgba(27,58,92,0.030)" }}>
            <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic" }}>
              {alreadyAdded.size === items.length
                ? "You've added all available programs."
                : "No matches — try a different search term."}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: "55vh", overflowY: "auto" }}>
            {filtered.map(item => (
              <button key={item.id} onClick={() => { onAdd(item.id); onClose(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 12px", borderRadius: 10,
                  border: `1px solid ${item.color}30`,
                  background: `${item.color}08`,
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${item.color}18`; e.currentTarget.style.borderColor = `${item.color}60`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${item.color}08`; e.currentTarget.style.borderColor = `${item.color}30`; }}>
                <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>{item.logo}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>{item.name}</div>
                  <div style={{ fontSize: 10, color: "#4A6B8A" }}>{item.program}</div>
                </div>
                <span style={{ fontSize: 16, color: textDim, flexShrink: 0 }}>+</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create Board Modal — standalone create flow (no destination context) ─
// Used by the "+ Add Board" tile in the BoardsTabContent grid.
function CreateBoardModal({ onClose, onCreate }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();
  const [name, setName] = useState("");
  const [iconId, setIconId] = useState("sparkle");
  const [color, setColor] = useState("#1B3A5C");

  // Editorial palette — 8 board colors, each carrying a feeling.
  // Order: brand primary first, then warmth → calm, then accents → moody.
  // Burgundy: important / primary.   Gold: treasure / refined.
  // Terracotta: energy / adventure.   Antique gold: luxury / heritage.
  // Sepia: history / archival.       Sage: calm / wellness.
  // Dusty teal: fresh / minimalist.  Aubergine: moody / dramatic.
  const COLOR_CHOICES = ["#1B3A5C","#4A8CC4","#C25E47","#A8853B","#6B4423","#7A9479","#5A8A8C","#6B4470"];

  function handleCreate() {
    if (!name.trim()) return;
    onCreate(name, iconId, color);
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Create a new board"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 500,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
        animation: "fadeIn 0.2s ease",
      }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 520,
        background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff",
        borderRadius: "20px 20px 0 0",
        border: `1px solid ${darkMode ? "rgba(74,140,196,0.2)" : "rgba(0,0,0,0.08)"}`,
        borderBottom: "none",
        padding: "20px 22px 24px",
        animation: "fadeIn 0.3s cubic-bezier(0.34,1.2,0.64,1)",
      }}>
        <div style={{ width: 40, height: 4, background: darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.15)", borderRadius: 4, margin: "0 auto 16px" }} />

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>New Board</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: textPrimary, lineHeight: 1.2 }}>Create a board</h2>
          <div style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>Pick a name, an icon, and a color. You can change them later.</div>
        </div>

        {/* Preview tile */}
        <div style={{
          padding: "16px 14px", borderRadius: 14,
          border: `1.5px solid ${color}50`,
          background: `linear-gradient(135deg, ${color}18, ${color}05)`,
          marginBottom: 16,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: `${color}25`, border: `1px solid ${color}50`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }} aria-hidden="true">
            {getBoardIcon(iconId).render(color, 28)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, lineHeight: 1.2 }}>{name.trim() || "Your board name"}</div>
            <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginTop: 4 }}>Empty</div>
          </div>
        </div>

        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value.slice(0, 40))}
          onKeyDown={e => { if (e.key === "Enter" && name.trim()) handleCreate(); }}
          placeholder="Board name (e.g. Spring Break, Anniversary)"
          autoFocus
          style={{
            width: "100%", padding: "11px 14px", borderRadius: 10,
            border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`,
            background: darkMode ? "rgba(27,58,92,0.060)" : "#ffffff",
            color: textPrimary, fontSize: 14, fontFamily: "inherit",
            outline: "none", boxSizing: "border-box", marginBottom: 14,
          }}
        />

        <div style={{ fontSize: 10, color: textMuted, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Icon</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginBottom: 14 }}>
          {BOARD_ICONS.map(opt => {
            const active = iconId === opt.id;
            return (
              <button key={opt.id} onClick={() => setIconId(opt.id)}
                aria-label={`Icon ${opt.label}`}
                aria-pressed={active}
                style={{
                  height: 40, borderRadius: 8,
                  border: `1.5px solid ${active ? color : "transparent"}`,
                  background: active ? `${color}20` : (darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)"),
                  cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}>
                {opt.render(active ? color : (darkMode ? "#4A6B8A" : "#5a7a8a"), 22)}
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 10, color: textMuted, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Color</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {COLOR_CHOICES.map(c => (
            <button key={c} onClick={() => setColor(c)}
              aria-label="Choose color"
              aria-pressed={color === c}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                border: `2px solid ${color === c ? "#fff" : "transparent"}`,
                boxShadow: color === c ? `0 0 0 2px ${c}` : "none",
                background: c, cursor: "pointer",
                transition: "all 0.15s",
              }} />
          ))}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose}
            style={{
              flex: 1, padding: "13px", borderRadius: 12,
              border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
              background: "transparent", color: textMuted,
              fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>Cancel</button>
          <button onClick={handleCreate} disabled={!name.trim()}
            style={{
              flex: 2, padding: "13px", borderRadius: 12, border: "none",
              background: name.trim() ? color : (darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"),
              color: name.trim() ? "#FAFAF5" : textDim,
              fontSize: 14, fontWeight: 900,
              cursor: name.trim() ? "pointer" : "not-allowed", fontFamily: "inherit",
              transition: "all 0.15s",
            }}>Create Board</button>
        </div>
      </div>
    </div>
  );
}

// ── Boards Tab Content (Pinterest-style mood boards) ─────────────
// Shows board grid with thumb count + drill-in view for each board.
// Includes inline rename and delete for user-created boards.
function BoardsTabContent({ boards, setBoards, boardsApi = null, onOpenBoardPicker, onNavigate, onBookDest }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();
  const [activeBoardId, setActiveBoardId] = useState(null); // null = grid view; id = drill-in view
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editName, setEditName] = useState("");
  const [menuBoardId, setMenuBoardId] = useState(null); // which tile's ⋯ menu is open
  const [showCreateModal, setShowCreateModal] = useState(false);

  // ── Reorder state ───────────────────────────────────────────────
  // Two ways in: (1) tap "Reorder" button → enter reorder mode with
  // visible grip handles, or (2) long-press a tile (~400ms) → enter
  // reorder mode AND begin dragging that tile in one motion.
  const [reorderMode, setReorderMode] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 }); // pointer offset from tile origin
  const [hoverIndex, setHoverIndex] = useState(null);     // where the drop will land
  const tileRefsMap = useRef({});                          // id → DOM node, for hit-testing
  const longPressTimerRef = useRef(null);                  // pending long-press timer
  const dragOriginRef = useRef({ x: 0, y: 0, tileX: 0, tileY: 0 }); // pointer start + tile origin

  // Cancel reorder mode entirely. Used by "Done" button + Escape key.
  const exitReorderMode = () => {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    setReorderMode(false);
    setDraggingId(null);
    setHoverIndex(null);
  };

  const activeBoard = activeBoardId ? boards.find(b => b.id === activeBoardId) : null;

  function removeFromBoard(boardId, destName) {
    if (boardsApi) { boardsApi.removeItem(boardId, destName); return; }
    setBoards(prev => prev.map(b => b.id === boardId ? { ...b, items: (b.items || []).filter(n => n !== destName) } : b));
  }
  function deleteBoard(boardId) {
    if (!window.confirm("Delete this board? The destinations stay saved if they're in other boards.")) return;
    if (boardsApi) { boardsApi.deleteBoard(boardId); }
    else { setBoards(prev => prev.filter(b => b.id !== boardId)); }
    setActiveBoardId(null);
    setMenuBoardId(null);
  }
  function renameBoard(boardId, newName) {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (boardsApi) { boardsApi.updateBoard(boardId, { name: trimmed.slice(0, 40) }); }
    else { setBoards(prev => prev.map(b => b.id === boardId ? { ...b, name: trimmed.slice(0, 40) } : b)); }
    setEditingBoardId(null);
  }
  function createBoard(name, iconId, color) {
    if (boardsApi) { boardsApi.createBoard({ name, iconId, color }); return; }
    const id = `board-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
    setBoards(prev => [...prev, { id, name: name.trim().slice(0, 40), iconId, color, items: [], createdAt: Date.now() }]);
  }

  // ── Reorder pointer handlers ────────────────────────────────────
  // Pointer events unify touch + mouse + pen. Long-press timer
  // starts on pointerdown; if user lifts or moves more than ~10px
  // before 400ms, the timer cancels (interpreted as tap or scroll).
  const startDrag = (boardId, clientX, clientY) => {
    const node = tileRefsMap.current[boardId];
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragOriginRef.current = { x: clientX, y: clientY, tileX: rect.left, tileY: rect.top };
    setDraggingId(boardId);
    setDragPos({ x: 0, y: 0 });
    // Initial hover index = the dragged tile's own index
    const idx = boards.findIndex(b => b.id === boardId);
    setHoverIndex(idx);
    // Block scroll while dragging (mobile)
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
  };

  const handleTilePointerDown = (boardId) => (e) => {
    // Ignore right-click + multitouch
    if (e.button && e.button !== 0) return;
    if (e.pointerType === "touch" && e.isPrimary === false) return;
    // Don't start drag if menu is open — let the menu handle interactions
    if (menuBoardId === boardId) return;

    if (reorderMode) {
      // Already in reorder mode — pointerdown immediately starts drag
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      startDrag(boardId, e.clientX, e.clientY);
      return;
    }

    // Not yet in reorder mode — start long-press timer
    const startX = e.clientX, startY = e.clientY;
    const target = e.currentTarget;
    const pointerId = e.pointerId;

    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      target.setPointerCapture?.(pointerId);
      setReorderMode(true);
      startDrag(boardId, startX, startY);
    }, 400);

    // If user moves the pointer significantly before timer fires, cancel
    // (treat as scroll, not as a hold gesture).
    const moveListener = (ev) => {
      const dx = Math.abs(ev.clientX - startX);
      const dy = Math.abs(ev.clientY - startY);
      if (dx > 10 || dy > 10) {
        if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
        target.removeEventListener("pointermove", moveListener);
        target.removeEventListener("pointerup", upListener);
        target.removeEventListener("pointercancel", upListener);
      }
    };
    const upListener = () => {
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      target.removeEventListener("pointermove", moveListener);
      target.removeEventListener("pointerup", upListener);
      target.removeEventListener("pointercancel", upListener);
    };
    target.addEventListener("pointermove", moveListener);
    target.addEventListener("pointerup", upListener);
    target.addEventListener("pointercancel", upListener);
  };

  const handleTilePointerMove = (e) => {
    if (!draggingId) return;
    e.preventDefault();
    const dx = e.clientX - dragOriginRef.current.x;
    const dy = e.clientY - dragOriginRef.current.y;
    setDragPos({ x: dx, y: dy });

    // Hit-test: find which tile's center the pointer is over
    let nextHover = null;
    for (let i = 0; i < boards.length; i++) {
      const node = tileRefsMap.current[boards[i].id];
      if (!node) continue;
      const r = node.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
        nextHover = i;
        break;
      }
    }
    if (nextHover !== null && nextHover !== hoverIndex) {
      setHoverIndex(nextHover);
    }
  };

  const handleTilePointerUp = () => {
    if (!draggingId) return;
    document.body.style.overflow = "";
    document.body.style.touchAction = "";
    // Commit the reorder
    const fromIdx = boards.findIndex(b => b.id === draggingId);
    const toIdx = hoverIndex ?? fromIdx;
    if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
      setBoards(prev => {
        const next = [...prev];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        return next;
      });
    }
    setDraggingId(null);
    setDragPos({ x: 0, y: 0 });
    setHoverIndex(null);
  };

  // Escape key exits reorder mode
  useEffect(() => {
    if (!reorderMode) return;
    const onKey = (e) => { if (e.key === "Escape") exitReorderMode(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reorderMode]);

  // ── Drill-in view: single board's destinations ──
  if (activeBoard) {
    const items = activeBoard.items || [];
    return (
      <div>
        <button onClick={() => setActiveBoardId(null)}
          style={{ background: "none", border: "none", color: "#4A6B8A", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0, marginBottom: 14, display: "inline-flex", alignItems: "center", gap: 4 }}>
          ← All Boards
        </button>

        {/* Board header */}
        <div style={{
          padding: "16px 18px", borderRadius: 14, marginBottom: 16,
          border: `1.5px solid ${activeBoard.color}40`,
          background: `linear-gradient(135deg, ${activeBoard.color}18, ${activeBoard.color}06)`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: `${activeBoard.color}25`, border: `1px solid ${activeBoard.color}50`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }} aria-hidden="true">
              {/* Use the SVG icon system. Boards use iconId now (legacy emoji
                 boards are migrated by resolveBoardIconId via EMOJI_TO_ICON_ID). */}
              {getBoardIcon(resolveBoardIconId(activeBoard)).render(activeBoard.color, 28)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingBoardId === activeBoard.id ? (
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value.slice(0, 40))}
                  onBlur={() => renameBoard(activeBoard.id, editName)}
                  onKeyDown={e => { if (e.key === "Enter") renameBoard(activeBoard.id, editName); if (e.key === "Escape") setEditingBoardId(null); }}
                  autoFocus
                  style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${activeBoard.color}80`, background: darkMode ? "rgba(27,58,92,0.060)" : "#fff", color: textPrimary, fontSize: 18, fontWeight: 800, fontFamily: "inherit", outline: "none" }}
                />
              ) : (
                <h2 onClick={() => { setEditingBoardId(activeBoard.id); setEditName(activeBoard.name); }}
                  style={{ margin: 0, fontSize: 20, fontWeight: 900, color: textPrimary, lineHeight: 1.2, cursor: "text" }}>{activeBoard.name}</h2>
              )}
              <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                {items.length === 0 ? "No destinations yet" : `${items.length} destination${items.length !== 1 ? "s" : ""}`}
              </div>
            </div>
            <button onClick={() => deleteBoard(activeBoard.id)}
              aria-label="Delete board"
              title="Delete this board"
              style={{ background: "transparent", border: `1px solid rgba(255,107,107,0.25)`, color: "#FF6B6B", padding: "6px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Delete
            </button>
          </div>
        </div>

        {/* Destinations */}
        {items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "26px 18px", color: textDim, border: `1px dashed ${activeBoard.color}30`, borderRadius: 12, background: `${activeBoard.color}04` }}>
            <div style={{ fontSize: 13, fontStyle: "italic", marginBottom: 16, lineHeight: 1.55 }}>
              Empty for now.<br/>Tap ☆ on any destination to save it here.
            </div>
            {/* Discovery CTAs — added Jun 19, 2026 per Cas's call. Gives
               users a fast path to populate the board instead of leaving
               them stuck on an empty state. */}
            <div style={{ fontSize: 10, color: textDim, marginBottom: 8, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700 }}>
              Or browse
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => onNavigate?.("trending")}
                style={{
                  padding: "8px 14px", borderRadius: 10,
                  border: `1px solid rgba(196,69,69,0.35)`,
                  background: "rgba(196,69,69,0.06)",
                  color: "#c44545", fontSize: 12, fontWeight: 800,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                Trending Trips →
              </button>
              <button onClick={() => onNavigate?.("affordable")}
                style={{
                  padding: "8px 14px", borderRadius: 10,
                  border: `1px solid rgba(58,138,58,0.35)`,
                  background: "rgba(58,138,58,0.06)",
                  color: "#3a8a3a", fontSize: 12, fontWeight: 800,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                Most Affordable →
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map(name => {
              const dest = DESTINATIONS.find(d => d.name === name);
              return (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", border: `1px solid ${activeBoard.color}25` }}>
                  <button onClick={() => { if (dest && onBookDest) onBookDest(dest, activeBoard.color); }}
                    aria-label={`Book ${name}`}
                    style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, padding: 0, background: "transparent", border: "none", cursor: dest ? "pointer" : "default", fontFamily: "inherit", textAlign: "left" }}>
                    <div style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{dest?.emoji || "🌍"}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>{name}</div>
                      <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                        {dest?.country || ""}{dest?.vibe ? ` · ${dest.vibe}` : ""}
                      </div>
                    </div>
                  </button>
                  {dest && (
                    <button onClick={() => { if (onBookDest) onBookDest(dest, activeBoard.color); }}
                      aria-label={`Book ${name}`}
                      style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${activeBoard.color}50`, background: `${activeBoard.color}15`, color: activeBoard.color, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.5, textTransform: "uppercase", flexShrink: 0 }}>
                      Book
                    </button>
                  )}
                  <button onClick={() => removeFromBoard(activeBoard.id, name)}
                    aria-label={`Remove ${name} from ${activeBoard.name}`}
                    title="Remove from board"
                    style={{ background: "none", border: "none", color: textMuted, cursor: "pointer", fontSize: 16, fontFamily: "inherit", lineHeight: 1, padding: 4, flexShrink: 0 }}>✕</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Grid view: all boards ──
  // Count total saved places across all boards — used to gate the "Find
  // destinations to save" nudge panel below. Shows for users with 0-2 total
  // saves (gentle onboarding nudge); hides once they're 3+ saves in.
  const totalSavedPlaces = boards.reduce((sum, b) => sum + (b.items?.length || 0), 0);
  const showFindDestinations = totalSavedPlaces < 3;
  return (
    <div>
      {/* Gradient bubble + sun glow wrapper removed Jun 19, 2026 to match
         Trips/Explore/Account/Journal presentation. Home keeps its bubble. */}

      {/* Find destinations to save — gentle nudge for new users / empty boards.
         Shown ABOVE the grid (was previously at the bottom) so it's the first
         actionable thing a new user sees. Auto-hides once user has 3+ saves. */}
      {showFindDestinations && (
        <div style={{ padding: "14px 14px", borderRadius: 12, border: "1px dashed rgba(74,140,196,0.25)", background: "rgba(74,140,196,0.04)", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, marginBottom: 10, textAlign: "center" }}>
            Find destinations to save
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button onClick={() => onNavigate?.("affordable")}
              style={{ padding: "11px 10px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.4)", background: "rgba(27,58,92,0.08)", color: "#1B3A5C", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <svg width="16" height="16" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <circle cx="22" cy="22" r="19" fill="rgba(27,58,92,0.18)" stroke="#1B3A5C" strokeWidth="1.8"/>
                <circle cx="22" cy="22" r="10" fill="none" stroke="#1B3A5C" strokeWidth="1.4"/>
                <path d="M22 7 L22 12 M22 32 L22 37 M7 22 L12 22 M32 22 L37 22" stroke="#1B3A5C" strokeWidth="1.6" strokeLinecap="round"/>
                <text x="22" y="27" textAnchor="middle" fontSize="12" fontWeight="800" fill="#1B3A5C" fontFamily="Inter,sans-serif">$</text>
              </svg>
              Most Affordable
            </button>
            <button onClick={() => onNavigate?.("trending")}
              style={{ padding: "11px 10px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.4)", background: "rgba(27,58,92,0.08)", color: "#1B3A5C", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <svg width="16" height="16" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <circle cx="22" cy="22" r="19" fill="rgba(27,58,92,0.18)" stroke="#1B3A5C" strokeWidth="1.8"/>
                <polyline points="10,30 17,22 23,26 34,13" fill="none" stroke="#1B3A5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M30 11 L34 13 L31 16" fill="none" stroke="#1B3A5C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Trending
            </button>
          </div>
        </div>
      )}

      {/* Reorder toggle — sits just above the grid. Only show when there
         are at least 2 boards (single-board reorder is meaningless) and
         we're not currently editing a name (avoid mode conflict). */}
      {boards.length >= 2 && !editingBoardId && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 10, minHeight: 24 }}>
          {reorderMode ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", justifyContent: "space-between" }}>
              <div style={{ fontSize: 11, color: textDim, fontStyle: "italic" }}>
                Drag tiles to rearrange
              </div>
              <button onClick={exitReorderMode}
                style={{ padding: "5px 14px", borderRadius: 14, border: "1px solid #1B3A5C", background: "#1B3A5C", color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.3 }}>
                Done
              </button>
            </div>
          ) : (
            <button onClick={() => setReorderMode(true)}
              style={{ background: "transparent", border: "none", color: "#4A8CC4", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "2px 0", display: "inline-flex", alignItems: "center", gap: 5 }}
              aria-label="Enter reorder mode">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              Reorder
            </button>
          )}
        </div>
      )}

      {/* Always render the grid (boards.length === 0 is unlikely since defaults seed it).
          The grid always ends with a "+ Add Board" tile so users can grow their collection.
          IMPORTANT: columns use `minmax(0, 1fr)` not bare `1fr`. Bare `1fr` resolves to
          `minmax(auto, 1fr)` which lets a column grow past its fair share if any tile's
          min-content (long board name, flex children, etc.) exceeds the available width.
          On narrow mobile that caused the right column to bleed past the page edge while
          the left had margin. `minmax(0, 1fr)` forces equal columns; truncation handles
          overflow inside each tile. */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginBottom: 16, position: "relative" }}
           onPointerMove={draggingId ? handleTilePointerMove : undefined}
           onPointerUp={draggingId ? handleTilePointerUp : undefined}
           onPointerCancel={draggingId ? handleTilePointerUp : undefined}>
        {boards.map((board, boardIdx) => {
          const itemCount = (board.items || []).length;
          const previewEmojis = (board.items || []).slice(0, 4).map(name => DESTINATIONS.find(d => d.name === name)?.emoji).filter(Boolean);
          const isMenuOpen = menuBoardId === board.id;
          const isEditing = editingBoardId === board.id;
          const isDragging = draggingId === board.id;
          const isHoverTarget = reorderMode && draggingId && draggingId !== board.id && hoverIndex === boardIdx;
          return (
            <div key={board.id}
              ref={el => { if (el) tileRefsMap.current[board.id] = el; else delete tileRefsMap.current[board.id]; }}
              onPointerDown={isEditing ? undefined : handleTilePointerDown(board.id)}
              style={{
                position: "relative",
                height: "100%",
                // Dragging tile floats above + follows pointer
                transform: isDragging ? `translate(${dragPos.x}px, ${dragPos.y}px) scale(1.05)` : (reorderMode ? "scale(0.98)" : "none"),
                transition: isDragging ? "none" : "transform 0.18s ease",
                zIndex: isDragging ? 50 : 1,
                opacity: isDragging ? 0.92 : 1,
                touchAction: reorderMode ? "none" : "auto",
                filter: isDragging ? "drop-shadow(0 12px 24px rgba(27,58,92,0.25)) drop-shadow(0 4px 8px rgba(27,58,92,0.15))" : "none",
                // Hover-target gets a leading outline to indicate drop point
                outline: isHoverTarget ? "2px dashed #4A8CC4" : "none",
                outlineOffset: isHoverTarget ? 4 : 0,
                borderRadius: 14,
              }}>
              <button onClick={() => { if (!isEditing && !reorderMode) setActiveBoardId(board.id); }}
                disabled={reorderMode}
                style={{
                  padding: 0, // hero needs to extend to card edges; padding moved inside
                  borderRadius: 14,
                  border: "1px solid rgba(27,58,92,0.1)",
                  background: "#FAFAF5",
                  cursor: isEditing ? "default" : (reorderMode ? "grab" : "pointer"),
                  fontFamily: "inherit", textAlign: "left",
                  display: "flex", flexDirection: "column",
                  transition: "all 0.15s",
                  minHeight: 100,
                  width: "100%",
                  height: "100%",
                  boxSizing: "border-box",
                  overflow: "hidden", // clips hero corners to button's borderRadius
                  // In reorder mode, the inner button must not block pointer
                  // events so the outer wrapper's onPointerDown handler fires
                  pointerEvents: reorderMode ? "none" : "auto",
                }}
                onMouseEnter={e => { if (!isEditing && !reorderMode) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.25)"; } }}
                onMouseLeave={e => { if (!reorderMode) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.1)"; } }}>
                {/* Hero — 2×2 illustration grid above the footer (Phase 1
                   Boards redesign, Jun 19, 2026). */}
                <BoardHeroGrid board={board} />
                {/* Footer — board name, save count, flag preview, ⋯ menu.
                   Unchanged content; just wrapped in a padded container now
                   that the button itself has padding 0. */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "11px 11px", flex: 1 }}>
                {/* Top row: icon + title inline, with ⋯ menu floating right */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, flexShrink: 0 }} aria-hidden="true">
                      {getBoardIcon(resolveBoardIconId(board)).render("#1B3A5C", 20)}
                    </div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value.slice(0, 40))}
                        onClick={e => e.stopPropagation()}
                        onBlur={() => renameBoard(board.id, editName)}
                        onKeyDown={e => { if (e.key === "Enter") renameBoard(board.id, editName); if (e.key === "Escape") setEditingBoardId(null); }}
                        autoFocus
                        style={{ flex: 1, minWidth: 0, padding: "4px 6px", borderRadius: 6, border: "1px solid rgba(27,58,92,0.25)", background: "#fff", color: textPrimary, fontSize: 15, fontWeight: 800, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                      />
                    ) : (
                      <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{board.name}</div>
                    )}
                  </div>
                  {/* ⋯ menu button */}
                  <span role="button" tabIndex={0}
                    aria-label={`Edit ${board.name}`}
                    onClick={e => { e.stopPropagation(); setMenuBoardId(isMenuOpen ? null : board.id); }}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setMenuBoardId(isMenuOpen ? null : board.id); } }}
                    style={{
                      width: 28, height: 28, borderRadius: 7,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isMenuOpen ? "rgba(27,58,92,0.06)" : "transparent",
                      color: textMuted, fontSize: 16, lineHeight: 1, cursor: "pointer",
                      transition: "background 0.15s",
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isMenuOpen ? "rgba(27,58,92,0.06)" : "transparent"; }}>⋯</span>
                </div>
                {/* Saved count */}
                <div style={{ fontSize: 10, color: textMuted, fontWeight: 600, letterSpacing: 1.6, textTransform: "uppercase" }}>
                  {itemCount === 0 ? "Empty" : `${itemCount} saved`}
                </div>
                {/* Preview emoji strip */}
                {previewEmojis.length > 0 && (
                  <div style={{ display: "flex", gap: 5, marginTop: "auto", alignItems: "center" }}>
                    {previewEmojis.map((em, i) => (
                      <span key={i} style={{ fontSize: 16, opacity: 0.9, lineHeight: 1 }}>{em}</span>
                    ))}
                    {itemCount > 4 && <span style={{ fontSize: 11, color: textDim, fontWeight: 600, marginLeft: 3 }}>+{itemCount - 4}</span>}
                  </div>
                )}
                </div>{/* /footer wrapper */}
              </button>
              {/* Floating ⋯ menu */}
              {isMenuOpen && (
                <>
                  {/* Backdrop closes the menu */}
                  <div onClick={() => setMenuBoardId(null)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
                  <div style={{
                    position: "absolute", top: 42, right: 8, zIndex: 60,
                    background: darkMode ? "rgba(20,28,42,0.98)" : "#ffffff",
                    border: "1px solid rgba(27,58,92,0.1)",
                    borderRadius: 10, padding: 4,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    minWidth: 130,
                    animation: "fadeIn 0.15s ease",
                  }}>
                    <button onClick={e => { e.stopPropagation(); setMenuBoardId(null); setEditingBoardId(board.id); setEditName(board.name); }}
                      style={{ width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 6, border: "none", background: "transparent", color: textPrimary, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}
                      onMouseEnter={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                      <span aria-hidden="true">✏️</span> Rename
                    </button>
                    <button onClick={e => { e.stopPropagation(); setMenuBoardId(null); deleteBoard(board.id); }}
                      style={{ width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 6, border: "none", background: "transparent", color: "#FF6B6B", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,107,0.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                      <span aria-hidden="true">🗑️</span> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* + Add Board tile — always last, hidden during reorder mode */}
        {!reorderMode && (
        <button onClick={() => setShowCreateModal(true)}
          aria-label="Create a new board"
          style={{
            padding: "14px 14px",
            borderRadius: 14,
            border: `1.5px dashed ${darkMode ? "rgba(74,140,196,0.4)" : "rgba(74,140,196,0.5)"}`,
            background: darkMode ? "rgba(74,140,196,0.03)" : "rgba(74,140,196,0.04)",
            cursor: "pointer", fontFamily: "inherit", textAlign: "center",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "all 0.15s",
            minHeight: 120,
            color: "#4A8CC4",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = darkMode ? "rgba(74,140,196,0.08)" : "rgba(74,140,196,0.08)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.7)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = darkMode ? "rgba(74,140,196,0.03)" : "rgba(74,140,196,0.04)"; e.currentTarget.style.borderColor = darkMode ? "rgba(74,140,196,0.4)" : "rgba(74,140,196,0.5)"; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: "rgba(74,140,196,0.15)",
            border: "1px solid rgba(74,140,196,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 700, lineHeight: 1, color: "#4A8CC4",
          }}>+</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#4A8CC4", letterSpacing: 0.3 }}>Add Board</div>
          <div style={{ fontSize: 10, color: textDim, lineHeight: 1.4 }}>Create a new theme</div>
        </button>
        )}
      </div>

      {/* Standalone Create Board modal */}
      {showCreateModal && (
        <CreateBoardModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(name, iconId, color) => { createBoard(name, iconId, color); setShowCreateModal(false); }}
        />
      )}

      {/* Find destinations panel removed from this spot — moved above the
         boards grid, now conditionally rendered for users with <3 saves. */}

      <div style={{ marginTop: 12, fontSize: 11, color: textDim, fontStyle: "italic", textAlign: "center" }}>
        We watch destinations across all your boards for price drops within your budget.
      </div>
    </div>
  );
}

// ── Account Modal ───────────────────────────────────────────────
function AccountModal({ user, starredAlerts, setStarredAlerts, boards = [], setBoards = () => {}, boardsApi = null, tripsApi = null, journalApi = null, onOpenBoardPicker = () => {}, onClose, onUpdate, defaultTab = "trips", defaultProfileSubTab = "overview", defaultJournalSubTab = "dashboard", standalone = false, savedItineraries = [], onDeleteItinerary = null, travelerType = null, onRetakeQuiz = null, onOpenIdentityCard = () => {}, onIdentityUpdate = null, onNavigate = null, onBookDest = null, pastTrips = [], setPastTrips = () => {}, journalEntries = {}, setJournalEntries = () => {}, journalEditing = null, setJournalEditing = () => {}, journalDraft = "", setJournalDraft = () => {}, journalPhotos = {}, setJournalPhotos = () => {}, journalRatings = {}, setJournalRatings = () => {}, journalMeta = {}, setJournalMeta = () => {}, journalRatingDraft = {}, setJournalRatingDraft = () => {}, journalMetaDraft = {}, setJournalMetaDraft = () => {}, tripAlerts = {}, setTripAlerts = () => {}, onOpenAlertModal = () => {}, userMood = null, onOpenStampsSeeding = () => {}, onOpenPostcardsSeeding = () => {}, journalRefreshTick = 0 }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText, editorialCream, editorialCreamDeep, editorialBorder, editorialInkBurgundy, editorialInkDark, editorialInkMid } = useTheme();
  const [tab, setTab] = useState(defaultTab); // profile | trips | journal | watchlist | itineraries | gift
  // (Removed Jun 11 2026: expandedAccountSections + toggleAccountSection state
  // hooks. They powered the accordion view of the account modal, which has
  // been replaced by the unified tab grid for all entry points.)
  // Profile sub-tabs: 'overview' shows the existing personal info/preferences/passport
  // content; 'stamps' shows the user's stamp collection. Past Trips stays
  // inline within 'overview' for now per spec.
  const [profileSubTab, setProfileSubTab] = useState(defaultProfileSubTab);
  useEffect(() => { setProfileSubTab(defaultProfileSubTab); }, [defaultProfileSubTab]);
  // Journal sub-tab state (DASHBOARD / STAMPS / POSTCARDS / ENTRIES).
  // Drives the new Journal IA — Stamps and Postcards moved here from
  // Account → Profile sub-tabs as part of the Journal restructure. Default
  // is "dashboard" — the unified landing view that surfaces recent
  // activity across all four sub-sections.
  const [journalSubTab, setJournalSubTab] = useState(defaultJournalSubTab);
  useEffect(() => { setJournalSubTab(defaultJournalSubTab); }, [defaultJournalSubTab]);
  // Stamps tab filter: 'all' | 'someday' | 'visited'
  const [stampsFilter, setStampsFilter] = useState("all");
  // ── Trip drafts ("Save for later") ──
  // Persisted to localStorage["jga-trip-drafts"] by QuickBookModal's Save-for-
  // later action. Rendered in the Trips tab between Past and Pending so users
  // who bookmarked a trip can come back to it. Removed via the X on each card.
  const [tripDrafts, setTripDrafts] = useState(() => {
    try {
      const raw = localStorage.getItem("jga-trip-drafts");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const removeDraft = (id) => {
    setTripDrafts(prev => {
      const next = prev.filter(d => d.id !== id);
      try { localStorage.setItem("jga-trip-drafts", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // Currently-open stamp detail modal (or null). Holds the stamp's full data
  // (id + catalog metadata + current state). The modal lets the user toggle
  // SOMEDAY/VISITED, edit notes, set dates, or remove from collection.
  const [activeStampDetail, setActiveStampDetail] = useState(null);
  // Mirrored stamps state so the modal re-renders when user changes a stamp.
  // Initialized from localStorage; kept in sync by callbacks below.
  const [stampsData, setStampsData] = useState(() => {
    try { return getStamps(); } catch { return {}; }
  });
  // ── Postcards parallel state (mirrors stamps pattern) ──
  // Filter: 'all' | 'dreaming' | 'visited'
  const [postcardsFilter, setPostcardsFilter] = useState("all");
  const [activePostcardDetail, setActivePostcardDetail] = useState(null);
  const [postcardsData, setPostcardsData] = useState(() => {
    try { return getPostcards(); } catch { return {}; }
  });
  // Re-read stamps + postcards from localStorage when an external write
  // happens (e.g. seeding modal at App level completes). Without this,
  // newly seeded stamps wouldn't show in the Journal grid until the user
  // navigated away and back.
  useEffect(() => {
    if (journalRefreshTick === 0) return; // skip initial mount
    try { setStampsData(getStamps()); } catch (e) {}
    try { setPostcardsData(getPostcards()); } catch (e) {}
  }, [journalRefreshTick]);
  // ── Saved-itineraries expand state ──
  // Stores the `dest` of the currently-expanded saved itinerary, or null if
  // none. Inline expand-in-place matches the past-trips UX above (rather than
  // opening a modal inside a modal, which would be jarring).
  const [expandedSavedItin, setExpandedSavedItin] = useState(null);
  // Journal-scoped Add Trip form. Independent from Trips tab's showAddTrip
  // so users can add a past trip from inside Journal without bouncing to
  // Trips. After save, the journal editor auto-opens for the new trip.
  const [journalAddOpen, setJournalAddOpen] = useState(false);
  const [journalAddForm, setJournalAddForm] = useState({ dest: "", dateFrom: "", dateTo: "", travelers: "1" });
  // Which itinerary is currently in "Are you sure?" delete-confirm mode.
  // Stores the dest string of the active card, or null. Single-active-at-a-time
  // so only one inline confirm row shows at any moment.
  const [itinDeleteConfirm, setItinDeleteConfirm] = useState(null);
  // Tracks an in-flight delete request so we can show "Deleting..." state and
  // disable the buttons. Cleared once the promise resolves regardless of outcome.
  const [itinDeleting, setItinDeleting] = useState(false);
  // Sync tab when modal reopens with a different defaultTab
  const acctModalRef = useRef(null);
  useEffect(() => { setTab(defaultTab); }, [defaultTab]);
  useEffect(() => { if (acctModalRef.current) acctModalRef.current.scrollTop = 0; }, [tab]);

  // On-brand line-art icons — match top nav SVG aesthetic
  const tabSvg = {
    profile: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.5" stroke={color} strokeWidth="1.7"/>
        <path d="M5 20c0-3.5 3.1-6.5 7-6.5s7 3 7 6.5" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
      </svg>
    ),
    identity: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {/* Identity card silhouette — head with horizontal accent line below
           reads as "ID badge" / "identity profile". */}
        <circle cx="12" cy="9" r="3" stroke={color} strokeWidth="1.7" fill="none"/>
        <path d="M5 21 C 5 16 8 14 12 14 C 16 14 19 16 19 21" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        <path d="M9 17.5 L15 17.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
        <circle cx="12" cy="9" r="1" fill={color} opacity="0.7"/>
      </svg>
    ),
    trips: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 16 L21 16" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        <path d="M5 13 L9 7 L11 7 L9 13 L13 13 L15 11 L16 12 L14 14.5 L12 13.5 L9 13.5" stroke={color} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
        <path d="M14 7 C 16 7 19 8 21 10" stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    journal: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="3" width="14" height="18" rx="1.5" stroke={color} strokeWidth="1.7"/>
        <path d="M8 8 L14 8" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 12 L14 12" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 16 L12 16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    watchlist: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polygon points="12,3 14.85,8.78 21.24,9.71 16.62,14.21 17.71,20.57 12,17.57 6.29,20.57 7.38,14.21 2.76,9.71 9.15,8.78" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    ),
    alerts: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {/* Bell — soft modern silhouette with little clapper */}
        <path d="M5.5 17 C 6 13 6 11 6.5 9.5 C 7.5 6.5 9.5 5 12 5 C 14.5 5 16.5 6.5 17.5 9.5 C 18 11 18 13 18.5 17 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
        <path d="M5 17 L19 17" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M10.5 19.5 C 11 20.5 13 20.5 13.5 19.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none"/>
        <circle cx="12" cy="3.5" r="0.9" fill={color}/>
      </svg>
    ),
    itineraries: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 4 L3 6 L3 20 L9 18 L15 20 L21 18 L21 4 L15 6 L9 4 Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
        <path d="M9 4 L9 18" stroke={color} strokeWidth="1.4" strokeDasharray="1.5 2"/>
        <path d="M15 6 L15 20" stroke={color} strokeWidth="1.4" strokeDasharray="1.5 2"/>
      </svg>
    ),
    gift: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="10" width="18" height="11" rx="1.5" stroke={color} strokeWidth="1.6"/>
        <path d="M2 10 L22 10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M12 10 L12 21" stroke={color} strokeWidth="1.6"/>
        <path d="M12 10 C 10 10 7.5 9 7.5 6.5 C 7.5 4.5 9.5 4 12 7 C 14.5 4 16.5 4.5 16.5 6.5 C 16.5 9 14 10 12 10 Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    moodboard: (color) => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {/* Mood board — pinboard with three saved items + small heart accent.
            Reads as "personal mood/saved-things collection." */}
        <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.7"/>
        <rect x="6" y="6" width="5" height="5" rx="0.5" stroke={color} strokeWidth="1.4" fill="none"/>
        <rect x="13" y="6" width="5" height="5" rx="0.5" stroke={color} strokeWidth="1.4" fill="none"/>
        <rect x="6" y="13" width="5" height="5" rx="0.5" stroke={color} strokeWidth="1.4" fill="none"/>
        <path d="M15.5 14.8 C 14 13.7 13 14.4 13 15.5 C 13 16.7 14.5 17.7 15.5 18 C 16.5 17.7 18 16.7 18 15.5 C 18 14.4 17 13.7 15.5 14.8 Z" stroke={color} strokeWidth="1.3" fill="none" strokeLinejoin="round"/>
      </svg>
    ),
  };

  // Gift tab flag — hidden pre-launch per Cas. Flip to true to restore.
  const SHOW_GIFT_TAB = false;

  // Identity / Trips / Boards are accessible via dedicated top-nav entry
  // points, so they're removed from the Account modal sub-nav. Account only
  // contains deep-config: Profile + Alerts + Itineraries.
  // Alerts + Itineraries moved into Trips as sub-tabs (Jun 19, 2026 — matches
  // Journal's sub-tab pattern). Account modal now keeps deep-config only:
  // Profile + Mood Board (+ Gift when enabled).
  const TABS = [
    { id: "profile",     icon: "👤", label: "Profile" },
    { id: "moodboard",   icon: "🎨", label: "Mood Board" },
    ...(SHOW_GIFT_TAB ? [{ id: "gift", icon: "🎁", label: "Gift" }] : []),
  ];

  // Subscription tiers were removed — all features are now free for registered users.
  // Keeping isExplorer as `true` means existing tier-gated UI still works without breaking anything.
  const isExplorer = !!user;

  // SAMPLE_TRIPS draws from pastTrips (the shared state) so the Trips tab
  // stays in sync with the dashboard. We add itinerary detail for known dests.
  const TRIP_ITINERARIES = {
    "Paris": {
      days: [
        { day: 1, title: "Arrival & Montmartre", morning: "Fly in and settle at your hotel near the Marais", afternoon: "Wander Montmartre, visit Sacré-Cœur", evening: "Dinner at a classic French bistro", tip: "Buy a Navigo card for unlimited metro rides" },
        { day: 2, title: "Museums & Seine", morning: "Louvre Museum — arrive early to beat crowds", afternoon: "Walk along the Seine to the Eiffel Tower", evening: "Sunset cruise on the Seine", tip: "Book Louvre tickets online to skip the queue" },
        { day: 3, title: "Versailles Day Trip", morning: "Train to Palace of Versailles", afternoon: "Gardens and Grand Trianon", evening: "Back to Paris, dinner in Saint-Germain", tip: "Get there by 9am to see the Hall of Mirrors" },
      ],
      budgetBreakdown: { flights: "$520", accommodation: "$680", food: "$180", activities: "$120" },
      packingTip: "Pack layers — Paris in March can be chilly but beautiful.",
    },
  };

  // Normalize: every trip from pastTrips gets a default status if missing
  // and gets its itinerary attached if we have one for the destination
  const SAMPLE_TRIPS = pastTrips.map(t => {
    // Map legacy "past" → "completed"; default missing status to "completed"
    const normalizedStatus = t.status === "past" ? "completed" : (t.status || "completed");
    return {
      budget: t.budget || 1200,
      days: t.days || 7,
      itinerary: t.itinerary || TRIP_ITINERARIES[t.dest] || null,
      ...t,
      status: normalizedStatus,
    };
  });

  // Sample itineraries — shown to users with no real saved itineraries as
  // discovery placeholders. Currently empty per Cas's direction (Tokyo +
  // Marrakech were removed); add destinations here if/when fresh defaults
  // become desirable.
  const SAMPLE_ITINERARIES = [];

  const [giftEmail, setGiftEmail] = useState("");
  const [giftAmount, setGiftAmount] = useState("150");
  const [giftNote, setGiftNote] = useState("");
  const [giftSent, setGiftSent] = useState(false);
  const [expandedTrip, setExpandedTrip] = useState(null);
  // Delete confirmation modal state — null when no modal open.
  // tripDeleteConfirm shape: { trip, hasJournal: boolean }
  // journalDeleteConfirm: tripCode string when active
  const [tripDeleteConfirm, setTripDeleteConfirm] = useState(null);
  const [journalDeleteConfirm, setJournalDeleteConfirm] = useState(null);
  // Legacy userTrips state removed — it was a phantom duplicate of pastTrips
  // that caused added trips to render twice on the Trips tab. Tombstones never
  // touched it, so deleted trips kept reappearing. All trip data now lives in
  // pastTrips via tripsApi. The jga-user-trips localStorage entry is wiped
  // below to clean up orphaned phantom trips from this device.
  const [showAddTrip, setShowAddTrip] = useState(false);
  // Tracks which trip code is being edited (null = adding new). When set, the
  // Add Trip form pre-fills with that trip's data and the save handler routes
  // through tripsApi.updateTrip instead of createTrip. Lets users come back
  // and edit trip metadata (dest/dates/days/budget/etc.) after saving.
  const [editingTripCode, setEditingTripCode] = useState(null);
  const [addTripForm, setAddTripForm] = useState({ dest: "", emoji: "🌍", dateFrom: "", dateTo: "", daysGone: "", liked: "", disliked: "", favoriteSpot: "", budget: "", travelers: "1", budgetMode: "perPerson" });
  const [cancellingTrip, setCancellingTrip] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDone, setCancelDone] = useState(null);
  // Trips review state removed — Journal is the single source of truth for ratings + reviews.
  // The "Leave a Review" button on a completed trip now opens Journal in edit mode.
  // journalEntries, journalEditing, journalDraft passed as props from parent

  // Profile state
  const [ktn, setKtn] = useState(user.ktn || "");
  const [ktnEditing, setKtnEditing] = useState(false);
  // Editable personal info — previously read-only, surfaced as static labels.
  // dob is normalized to YYYY-MM-DD because <input type="date"> requires that
  // format. Backend may return DOB as a full ISO timestamp ("1980-01-01T00:
  // 00:00.000Z") — we trim that to just the date portion for display + edit.
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [dob, setDob] = useState(() => {
    const raw = user.dob || "";
    if (!raw) return "";
    // ISO timestamp → take date portion only ("1980-01-01T00:00:00.000Z" → "1980-01-01")
    const m = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : raw;
  });
  const [address, setAddress] = useState(user.address || "");
  // Self-heal: if user.departureAirport has the old "CODE — Name, City" format
  // from a prior bug where Profile detect saved a display string instead of
  // the IATA code, extract just the 3-letter code so subsequent saves pass
  // backend validation.
  const [departureAirport, setDepartureAirport] = useState(() => {
    const raw = user.departureAirport || "";
    if (!raw) return "";
    if (/^[A-Z]{3}$/.test(raw)) return raw;
    const match = raw.match(/^([A-Za-z]{3})\b/);
    return match ? match[1].toUpperCase() : raw;
  });
  const [avoidDestinations, setAvoidDestinations] = useState(user.avoidDestinations || "");
  const [avoidAirlines, setAvoidAirlines] = useState(user.avoidAirlines || "");
  const [avoidHotels, setAvoidHotels] = useState(user.avoidHotels || "");
  // ── Max travel time (one-way) ──
  // Caps the longest one-way flight the user is willing to take. Used by SM
  // to filter destinations that exceed the tolerance. Hydrated from backend
  // user.maxTravelTimeHours when available; falls back to localStorage until
  // backend has the column. See JGA-Max-Travel-Time-Backend-Handoff.md.
  const [maxTravelTimeHours, setMaxTravelTimeHours] = useState(() => {
    if (typeof user.maxTravelTimeHours === "number") return user.maxTravelTimeHours;
    try {
      const cached = localStorage.getItem("jga-max-travel-time-hours");
      if (cached) {
        const n = parseInt(cached, 10);
        if (!isNaN(n) && n > 0) return n;
      }
    } catch {}
    return null; // null = no preference, treat as unlimited
  });
  const [ffPrograms, setFfPrograms] = useState(user.ffPrograms || {});
  const [hotelPrograms, setHotelPrograms] = useState(user.hotelPrograms || {});
  // Tracks which program inputs are actively being edited. While an id is in
  // this set, show the raw input regardless of length. On blur, remove from
  // the set so the field flips to the masked "•••• 1234" view. Without this,
  // the masked view triggered on every keystroke once length >= 4, hiding
  // the input mid-type and making it appear the user couldn't add more chars.
  const [editingPrograms, setEditingPrograms] = useState(new Set());

  // Which airlines/hotels are visible in the Profile tab. Initially: top-3
  // defaults plus anything the user has already enrolled in. Users add more
  // via the picker modal; visible IDs persist across sessions via the user
  // object so the list doesn't reset on reload.
  const [visibleAirlineIds, setVisibleAirlineIds] = useState(() => {
    const ids = new Set(AIRLINE_LIST.filter(a => a.topDefault).map(a => a.id));
    Object.keys(user.ffPrograms || {}).forEach(id => ids.add(id));
    (user.visibleAirlines || []).forEach(id => ids.add(id));
    return ids;
  });
  const [visibleHotelIds, setVisibleHotelIds] = useState(() => {
    const ids = new Set(HOTEL_LIST.filter(h => h.topDefault).map(h => h.id));
    Object.keys(user.hotelPrograms || {}).forEach(id => ids.add(id));
    (user.visibleHotels || []).forEach(id => ids.add(id));
    return ids;
  });
  const [showAirlinePicker, setShowAirlinePicker] = useState(false);
  // Identity share image generation state. Generation takes ~500ms (SVG → canvas
  // → PNG blob). We disable the button + show a spinner during so users don't
  // tap it twice. Errors are rare (mainly old browsers without canvas support)
  // but caught and shown as inline message.
  const [identitySharing, setIdentitySharing] = useState(false);
  const [identityShareError, setIdentityShareError] = useState("");
  const [showHotelPicker, setShowHotelPicker] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [identityCopied, setIdentityCopied] = useState(false);
  const [showAddExternalTrip, setShowAddExternalTrip] = useState(false);
  const [extTripDest, setExtTripDest] = useState("");
  const [extTripFrom, setExtTripFrom] = useState("");
  const [extTripTo, setExtTripTo] = useState("");

  // Passport (sensitive — masked when displayed, full value sent to backend on save)
  const [passportNumber, setPassportNumber] = useState(user.passportNumber || "");
  const [passportCountry, setPassportCountry] = useState(user.passportCountry || "");
  const [passportExpiry, setPassportExpiry] = useState(user.passportExpiry || "");
  const [passportEditing, setPassportEditing] = useState(false);

  const [profileSaveError, setProfileSaveError] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  // Tracks journal entries whose backend save failed (CORS, network, 5xx etc).
  // Keyed by trip code. Local data persists; this just drives a small warning
  // banner so the user knows their data didn't sync to the cloud yet.
  // Auto-clears when a subsequent save succeeds for the same trip.
  const [journalSyncFailed, setJournalSyncFailed] = useState({});

  async function saveProfile() {
    if (profileSaving) return;
    setProfileSaving(true);
    setProfileSaveError("");
    const updatedUser = {
      ...user,
      // Editable personal info — now sent to backend on save. dob is YYYY-MM-DD
      // (HTML date input format); backend may store as DATE or convert to
      // timestamp on its end. Empty strings are sent as null where it makes
      // sense so we don't overwrite existing values with blanks accidentally.
      firstName: firstName.trim() || user.firstName,
      lastName: lastName.trim() || user.lastName,
      dob: dob || null,
      address: address.trim() || null,
      ktn, departureAirport, avoidDestinations, avoidAirlines, avoidHotels,
      ffPrograms, hotelPrograms,
      // Max one-way flight tolerance (in hours). Backend column may not exist
      // yet — until it does, we also persist to localStorage. See
      // JGA-Max-Travel-Time-Backend-Handoff.md for backend wiring.
      maxTravelTimeHours,
      // Persist visible-ids so the Profile tab remembers which programs the user added
      visibleAirlines: Array.from(visibleAirlineIds),
      visibleHotels: Array.from(visibleHotelIds),
      passportNumber, passportCountry, passportExpiry,
    };
    // Always cache to localStorage for the max-travel-time fallback path.
    try {
      if (typeof maxTravelTimeHours === "number" && maxTravelTimeHours > 0) {
        localStorage.setItem("jga-max-travel-time-hours", String(maxTravelTimeHours));
      } else {
        localStorage.removeItem("jga-max-travel-time-hours");
      }
    } catch {}
    try {
      const result = onUpdate ? await onUpdate(updatedUser) : { ok: true };
      if (result && result.ok === false) {
        // Friendlier handling for the specific "passport storage not configured"
        // error — this is a backend config gap, not user fault, and it blocks
        // the WHOLE save (including non-passport changes). Surface clear guidance
        // so the user knows they can save their other changes by clearing the
        // passport field for now. Without this, users see a cryptic developer
        // error and don't realize their hotel/airline/profile edits also failed.
        const raw = result.error || "Couldn't save right now — please try again.";
        const lower = raw.toLowerCase();
        let friendly;
        if (lower.includes("passport storage is not configured") || lower.includes("passport_encryption")) {
          friendly = "Passport storage isn't ready yet — we're still setting up secure encryption. For now, leave the passport fields blank and your other changes will save normally. We'll let you know when passport is ready.";
        } else {
          friendly = raw;
        }
        setProfileSaveError(friendly);
        // Don't show "Saved!" if it actually failed — UNLESS it failed only because no token (offline-style fallback)
        if (result.reason === "no_token") {
          setProfileSaved(true);
          setTimeout(() => setProfileSaved(false), 2000);
        }
      } else {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 2000);
      }
    } catch (err) {
      console.error("[JGA Profile] saveProfile threw", err);
      setProfileSaveError("Something went wrong saving — please try again.");
    } finally {
      setProfileSaving(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Identity share card — generates an 1080x1920 Instagram-Story-formatted
  // PNG of the user's travel identity for sharing. Built entirely from
  // pure SVG (no external library like html2canvas) so it's pixel-perfect
  // across browsers and adds zero dependencies. Process:
  //   1. Compose an SVG string with the identity card visuals
  //   2. Convert SVG → data URL → HTMLImageElement
  //   3. Draw that image onto a 1080x1920 canvas
  //   4. Export canvas as PNG blob
  //   5. On mobile: Web Share API hands off to Instagram/Messages/etc.
  //      On desktop: trigger a download
  // ─────────────────────────────────────────────────────────────────────
  async function shareIdentityCard(identity, travelerType) {
    if (identitySharing) return;
    setIdentitySharing(true);
    setIdentityShareError("");
    try {
      // Helper — escape user content for safe SVG embedding
      const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

      // Look up sky for this identity
      const sky = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];

      // Generate constellation data using the same generator the UI uses
      const chart = generateConstellation(identity);

      // Map sky origin (e.g. "30% 20%") to SVG radial-gradient cx/cy as fractions
      const [oxStr, oyStr] = (sky.origin || "50% 50%").split(/\s+/);
      const ox = parseFloat(oxStr) / 100;
      const oy = parseFloat(oyStr) / 100;

      // Identity name split — first line "The {first}", second line italicized rest
      const nameWords = String(travelerType || "").split(" ");
      const firstLine = `The ${nameWords[0] || ""}`;
      const secondLine = nameWords.slice(1).join(" ");

      // Word-wrap the description to fit within ~36 chars per line
      function wrapText(text, maxChars) {
        const words = String(text || "").split(/\s+/);
        const lines = [];
        let cur = "";
        for (const w of words) {
          if ((cur + " " + w).trim().length > maxChars) {
            if (cur) lines.push(cur.trim());
            cur = w;
          } else {
            cur = cur ? cur + " " + w : w;
          }
        }
        if (cur) lines.push(cur.trim());
        return lines;
      }
      const descLines = wrapText(`"${identity.desc || ""}"`, 36);
      // Center descLines block — start y, each line spaced 38px apart
      const descStartY = 1075;

      // Constellation render area — scaled 3.6x from 200x140 viewBox.
      // Group transform centers the 720x504 chart at (540, 540) on canvas.
      const chartScale = 3.6;
      const chartW = 200 * chartScale; // 720
      const chartH = 140 * chartScale; // 504
      const chartX = (1080 - chartW) / 2; // 180
      const chartY = 280; // top edge of chart area

      // Build SVG markup — 1080 × 1350, Instagram 4:5 feed-optimal
      const svgString = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
  <defs>
    <radialGradient id="sky" cx="${ox}" cy="${oy}" r="0.9" fx="${ox}" fy="${oy}">
      <stop offset="0%" stop-color="${sky.bright}"/>
      <stop offset="55%" stop-color="${sky.mid}"/>
      <stop offset="100%" stop-color="${sky.dark}"/>
    </radialGradient>
  </defs>

  <!-- Night sky background -->
  <rect width="1080" height="1350" fill="url(#sky)"/>

  <!-- Header eyebrow -->
  <text x="540" y="100" text-anchor="middle" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="20" letter-spacing="5" fill="rgba(250,250,245,0.5)">CHARTED</text>
  <text x="540" y="140" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="24" fill="rgba(250,250,245,0.6)">a constellation, drawn for you</text>

  <!-- Constellation chart, scaled to 720x504 -->
  <g transform="translate(${chartX}, ${chartY}) scale(${chartScale})">
    ${chart.bgStars.map(s => `<circle cx="${s.x}" cy="${s.y}" r="${s.r}" fill="#FAFAF5" opacity="${s.o}"/>`).join("\n    ")}
    ${chart.lines.map(l => `<line x1="${l.x1}" y1="${l.y1}" x2="${l.x2}" y2="${l.y2}" stroke="${sky.accent}" stroke-width="1.5" opacity="0.65" stroke-linecap="round"/>`).join("\n    ")}
    ${Object.values(chart.stars).map(s => `<circle cx="${s.x}" cy="${s.y}" r="${s.r * 2.2}" fill="#FAFAF5" opacity="0.18"/><circle cx="${s.x}" cy="${s.y}" r="${s.r}" fill="#FAFAF5"/>`).join("\n    ")}
  </g>

  <!-- Identity name — two lines, italic accent on second -->
  <text x="540" y="930" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-size="62" font-weight="500" fill="#FAFAF5" letter-spacing="-1">${esc(firstLine)}</text>
  <text x="540" y="1005" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="62" font-weight="500" fill="${sky.accent}" letter-spacing="-1">${esc(secondLine)}</text>

  <!-- Description quote — wrapped lines -->
  ${descLines.map((l, i) => `<text x="540" y="${descStartY + i * 38}" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="24" fill="rgba(250,250,245,0.78)">${esc(l)}</text>`).join("\n  ")}

  <!-- Footer mono labels -->
  <g font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="14" letter-spacing="3" font-weight="700">
    <text x="60" y="1300" fill="rgba(74,140,196,0.75)">JUST</text>
    <text x="118" y="1300" fill="rgba(250,250,245,0.65)">GO</text>
    <text x="155" y="1300" fill="rgba(232,154,60,0.78)">ANYWHERE</text>
  </g>
  <text x="1020" y="1300" text-anchor="end" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="14" letter-spacing="3" fill="rgba(250,250,245,0.45)">justgoanywhere.com</text>
</svg>`;

      // Serialize SVG → blob URL → image → canvas → PNG blob
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.crossOrigin = "anonymous";
      const loaded = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load identity card image"));
      });
      img.src = svgUrl;
      await loaded;

      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1350;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 1080, 1350);
      URL.revokeObjectURL(svgUrl);

      const pngBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error("Failed to generate PNG")), "image/png");
      });

      const filename = `JGA-${String(travelerType).replace(/\s+/g, "-")}-chart.png`;
      const file = new File([pngBlob], filename, { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My travel chart: The ${travelerType}`,
          text: `I'm The ${travelerType}. What's your travel identity? Find yours at justgoanywhere.com`,
        });
      } else {
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err && err.name === "AbortError") {
        // User cancelled the native share sheet — not an error
      } else {
        console.error("[JGA Share] Identity chart share failed:", err);
        setIdentityShareError("Couldn't generate the image. Try again in a moment.");
      }
    } finally {
      setIdentitySharing(false);
    }
  }

  function copyReferral() {
    navigator.clipboard?.writeText(`Book a surprise trip with Just Go Anywhere using my code ${user.referralCode} and get $20 off your first booking! justgoanywhere.com`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const fieldStyle = { width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: textPrimary, outline: "none", boxSizing: "border-box" };

  // ── renderJournalSection — extracted Jun 11 2026 so the Journal top-nav
  // tab can render the SAME full journal experience (editor, ratings,
  // voice dictation, photos, save flow) inline that the Trips tab has.
  // Cas's concern: the Journal tab was just a read-only feed; now it's
  // the actual journaling surface. Used in both tab === "trips"
  // (inside expanded trip cards) and tab === "journal" (as the primary
  // card content). All journal* state and theme colors are captured
  // via closure from the AccountModal scope.
  const renderJournalSection = (t) => (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: teal, textTransform: "uppercase", display:"flex", alignItems:"center", gap:6 }}><svg width="12" height="12" viewBox="0 0 44 44" fill="none"><rect x="10" y="6" width="24" height="32" rx="3" stroke="#1B3A5C" strokeWidth="2"/><rect x="8" y="8" width="4" height="28" rx="2" fill="rgba(27,58,92,0.3)" stroke="#1B3A5C" strokeWidth="1.5"/><line x1="16" y1="15" x2="30" y2="15" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round"/><line x1="16" y1="21" x2="30" y2="21" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round"/><line x1="16" y1="27" x2="25" y2="27" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/></svg> My Journal</div>
          {journalEntries[t.code] && journalEditing !== t.code && (
            <button onClick={() => {
              setJournalEditing(t.code);
              setJournalDraft(journalEntries[t.code]);
              // Seed rating + meta drafts from saved values so the
              // editor opens with current selections intact (otherwise
              // ratings/meta UI would render blank on Edit). Matches
              // the behavior the removed "Edit entry" button used to
              // provide at the bottom of the saved entry preview.
              setJournalRatingDraft(p => ({ ...p, [t.code]: journalRatings[t.code] || {} }));
              setJournalMetaDraft(p => ({ ...p, [t.code]: journalMeta[t.code] || {} }));
            }}
              style={{ background: "none", border: "none", color: "#1B3A5C", fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
              Edit
            </button>
          )}
        </div>

        {journalEditing === t.code ? (() => {
          const rd = journalRatingDraft[t.code] || {};
          const md = journalMetaDraft[t.code] || {};
          const setRd = (key, val) => setJournalRatingDraft(p => ({ ...p, [t.code]: { ...(p[t.code] || {}), [key]: val } }));
          const setMd = (key, val) => setJournalMetaDraft(p => ({ ...p, [t.code]: { ...(p[t.code] || {}), [key]: val } }));
          const isNew = !journalEntries[t.code];
          // Delete link visibility — see Trips-tab editor comment above.
          // Show whenever any journal data exists, not just entry text.
          const hasAnyJournalData = !!(
            journalEntries[t.code] ||
            (journalRatings[t.code] && Object.values(journalRatings[t.code]).some(Boolean)) ||
            (journalMeta[t.code] && Object.values(journalMeta[t.code]).some(Boolean))
          );
          const RATINGS = [
            { key: "overall", label: "Overall", svg: (color) => (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <polygon points="12,3 14.85,8.78 21.24,9.71 16.62,14.21 17.71,20.57 12,17.57 6.29,20.57 7.38,14.21 2.76,9.71 9.15,8.78" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
              </svg>
            )},
            { key: "people",  label: "People",  svg: (color) => (
              <svg width="16" height="14" viewBox="0 0 28 24" fill="none" aria-hidden="true">
                <circle cx="9" cy="8" r="3" stroke={color} strokeWidth="1.6"/>
                <path d="M3 19c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
                <circle cx="19" cy="9" r="2.5" stroke={color} strokeWidth="1.5"/>
                <path d="M14.5 18c0.5-2.2 2.5-4 4.5-4s4 1.8 4.5 4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )},
            { key: "vibe",    label: "Vibe",    svg: (color) => (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/>
                <circle cx="18.5" cy="5.5" r="1" fill={color}/>
                <circle cx="5" cy="18" r="0.8" fill={color} opacity="0.6"/>
              </svg>
            )},
            { key: "food",    label: "Food",    svg: (color) => (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 3 L6 11 M9 3 L9 11 M6 11 C 6 12.7 7.3 14 9 14 L 9 21 M9 11 C 9 12.7 7.7 14 6 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M16 3 C 14 4 13 6 13 9 C 13 11 14 12 16 12 L 16 21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            )},
            { key: "value",   label: "Worth it", svg: (color) => (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6"/>
                <path d="M14.5 9 C 14 8 13 7.5 12 7.5 C 10.5 7.5 9.5 8.5 9.5 9.5 C 9.5 10.5 10.5 11 12 11.5 C 13.5 12 14.5 12.5 14.5 13.5 C 14.5 14.5 13.5 15.5 12 15.5 C 11 15.5 10 15 9.5 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <path d="M12 5.5 L12 7.5 M12 15.5 L12 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )},
          ];
          return (
            <div>
              {/* Meta — new entries only */}
              {isNew && (() => {
                const tripHasDates = !!(t.dateFrom && t.dateTo);
                return (
                  <>
                    {!tripHasDates && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 5 }}>📅 START DATE</div>
                          <input type="date" value={md.dateFrom || ""} onChange={e => {
                            const newFrom = e.target.value;
                            setMd("dateFrom", newFrom);
                            // Anchor TO to FROM if TO is empty or would now be before FROM
                            if (newFrom && (!md.dateTo || md.dateTo < newFrom)) {
                              setMd("dateTo", newFrom);
                            }
                          }}
                            style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1px solid rgba(27,58,92,0.25)", background: "rgba(27,58,92,0.060)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", colorScheme: darkMode ? "dark" : "light" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 5 }}>📅 END DATE</div>
                          <input type="date" value={md.dateTo || ""}
                            min={md.dateFrom || undefined}
                            onChange={e => {
                              // Snap forward: if user types a TO before FROM, snap to FROM
                              // (HTML date inputs let typed values bypass min UI).
                              const v = e.target.value;
                              if (md.dateFrom && v && v < md.dateFrom) {
                                setMd("dateTo", md.dateFrom);
                              } else {
                                setMd("dateTo", v);
                              }
                            }}
                            style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1px solid rgba(27,58,92,0.25)", background: "rgba(27,58,92,0.060)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", colorScheme: darkMode ? "dark" : "light" }} />
                        </div>
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 5 }}>💸 TRIP BUDGET</div>
                        <input value={md.budget || ""} onChange={e => setMd("budget", e.target.value)} placeholder="e.g. $2,000"
                          style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1px solid rgba(27,58,92,0.25)", background: "rgba(27,58,92,0.060)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 5 }}>👤 TRAVELERS</div>
                        <select value={md.travelers || ""} onChange={e => setMd("travelers", e.target.value)}
                          style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1px solid rgba(27,58,92,0.25)", background: darkMode ? "rgba(13,21,33,0.98)" : "#fff", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}>
                          <option value="">Select…</option>
                          <option>Solo</option><option>Couple</option><option>3 travelers</option><option>4 travelers</option><option>5+ travelers</option><option>Family</option>
                        </select>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Ratings */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>RATE YOUR TRIP</div>
                {RATINGS.map(r => (
                  <div key={r.key} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 11, color: textMuted, width: 100, flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ display: "flex", alignItems: "center" }}>{r.svg("#4A6B8A")}</span>
                        <span>{r.label}</span>
                      </div>
                      <div style={{ display: "flex", gap: 3 }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setRd(r.key, rd[r.key] === n ? null : n)}
                            style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${rd[r.key] >= n ? teal : "rgba(27,58,92,0.120)"}`, background: rd[r.key] >= n ? `${teal}25` : "transparent", color: rd[r.key] >= n ? teal : "#445", fontSize: 14, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center" }}>★</button>
                        ))}
                      </div>
                    </div>
                    {/* Descriptor word — stacked below the row, left-aligned
                       under the label so it doesn't overflow on mobile. Smaller
                       font for visual balance. */}
                    {rd[r.key] && (
                      <div style={{ fontSize: 10, color: teal, fontWeight: 700, marginTop: 3 }}>
                        {["","Disappointing","OK","Good","Great","Outstanding"][rd[r.key]]}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Text */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                <div style={{ fontSize: 10, color: textMuted, letterSpacing: 1, textTransform: "uppercase" }}>✍️ TELL YOUR STORY</div>
                <VoiceDictateButton
                  onTranscript={(text) => {
                    // Append final transcript segment to the draft.
                    // Inserts a space if the draft doesn't end with whitespace
                    // so consecutive segments don't smush together.
                    setJournalDraft(prev => {
                      const needsSpace = prev && !/\s$/.test(prev);
                      return prev + (needsSpace ? " " : "") + text.replace(/^\s+/, "");
                    });
                  }}
                />
              </div>
              <textarea value={journalDraft} onChange={e => setJournalDraft(e.target.value)}
                placeholder={`Write about your trip to ${t.dest}, or tap the mic to dictate…

What was the highlight? Any hidden gems? What would you tell your future self?`}
                rows={6} autoFocus
                style={{ width: "100%", padding: "14px", borderRadius: 12, fontSize: 13, fontFamily: "inherit", background: "rgba(74,140,196,0.04)", border: "1px solid rgba(74,140,196,0.2)", color: "#e8d8c0", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.8 }} />
              {/* Voice-dictation helper. Always visible so older
                  users and first-time users know that (1) the
                  button is a toggle, and (2) they can dictate
                  multiple times — each session adds to the
                  existing story rather than replacing it. */}
              <div style={{ background: "rgba(74,140,196,0.07)", border: "1px solid rgba(74,140,196,0.22)", borderRadius: 10, padding: "8px 12px", marginTop: 8, marginBottom: 6, fontSize: 11, color: textMuted, lineHeight: 1.55, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>💡</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ color: "#1B3A5C" }}>Voice dictation:</strong> Tap the mic to start, tap again when you're done. You can dictate as many times as you want — each session adds to your story. You can also edit anything by typing.
                </span>
              </div>
              <div style={{ fontSize: 10, color: textDim, marginBottom: 14, fontStyle: "italic" }}>
                {journalDraft.length} characters · {journalDraft.split(/\s+/).filter(Boolean).length} words
              </div>

              {/* Photos */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>📸 TRIP PHOTOS (UP TO 5)</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
                  {Array.from({ length: 5 }).map((_, i) => {
                    const photos = journalPhotos[t.code] || [];
                    const photo = photos[i];
                    return photo ? (
                      <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 10, overflow: "hidden" }}>
                        <img src={photo} alt={`Trip photo ${i+1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button onClick={() => setJournalPhotos(p => ({ ...p, [t.code]: (p[t.code]||[]).filter((_,idx) => idx !== i) }))}
                          style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                      </div>
                    ) : (
                      <label key={i} style={{ aspectRatio: "1", borderRadius: 10, border: "1px dashed rgba(27,58,92,0.25)", background: "rgba(27,58,92,0.03)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                          const file = e.target.files[0]; if (!file) return;
                          const reader = new FileReader();
                          reader.onload = ev => setJournalPhotos(p => { const cur = p[t.code]||[]; if (cur.length>=5) return p; const upd=[...cur]; upd[i]=ev.target.result; return {...p,[t.code]:upd}; });
                          reader.readAsDataURL(file);
                        }} />
                        <span style={{ fontSize: 18, opacity: 0.4 }}>+</span>
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, color: textDim, marginTop: 6, fontStyle: "italic" }}>
                  {(journalPhotos[t.code]||[]).length}/5 photos added · JPG, PNG, HEIC supported
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => {
                  if (journalApi) {
                    journalApi.saveJournal(t.code, {
                      entryText: journalDraft,
                      ratings: rd,
                      ...(isNew ? { meta: md } : {}),
                    });
                  } else {
                    // Synchronous localStorage write — same reasoning
                    // as the Trips-tab editor: persistence shouldn't
                    // depend on useEffect commit timing.
                    setJournalEntries(j => {
                      const next = { ...j, [t.code]: journalDraft };
                      try { localStorage.setItem("jga-journal-entries", JSON.stringify(next)); } catch {}
                      return next;
                    });
                    setJournalRatings(p => {
                      const next = { ...p, [t.code]: rd };
                      try { localStorage.setItem("jga-journal-ratings", JSON.stringify(next)); } catch {}
                      return next;
                    });
                    if (isNew) setJournalMeta(p => {
                      const next = { ...p, [t.code]: md };
                      try { localStorage.setItem("jga-journal-meta", JSON.stringify(next)); } catch {}
                      return next;
                    });
                  }
                  setJournalEditing(null); setJournalDraft("");
                }} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Save Entry ✓
                </button>
                <button onClick={() => { setJournalEditing(null); setJournalDraft(""); }}
                  style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: textMuted, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
              {/* Delete journal link — same pattern as Trips-tab editor.
                 Visible whenever any journal data exists (entry, ratings, or meta). */}
              {hasAnyJournalData && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button
                    onClick={() => setJournalDeleteConfirm(t.code)}
                    style={{ padding: "6px 4px", background: "none", border: "none", color: "#FF6B6B", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, opacity: 0.7 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = 0.7; }}>
                    Delete this journal
                  </button>
                </div>
              )}
            </div>
          );
        })() : journalEntries[t.code] ? (
          <div style={{ background: "rgba(74,140,196,0.04)", border: "1px solid rgba(74,140,196,0.12)", borderRadius: 12, padding: "16px", position: "relative" }}>
            {/* Ratings display */}
            {journalRatings[t.code] && Object.values(journalRatings[t.code]).some(Boolean) && (
              <div style={{ marginBottom: 12 }}>
                {journalRatings[t.code].overall && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 2 }}>
                      {[1,2,3,4,5].map(n => <span key={n} style={{ fontSize: 14, color: n <= journalRatings[t.code].overall ? teal : "rgba(27,58,92,0.14)" }}>★</span>)}
                    </div>
                    {/* Descriptor below stars, left-aligned, smaller — prevents
                       mobile overflow when label is long ("Outstanding"/"Disappointing"). */}
                    <span style={{ display: "block", fontSize: 10, fontWeight: 700, color: teal, marginTop: 3 }}>{["","Disappointing","OK","Good","Great","Outstanding"][journalRatings[t.code].overall]}</span>
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {[
                    { key: "people", svg: (color) => (<svg width="11" height="10" viewBox="0 0 28 24" fill="none" aria-hidden="true"><circle cx="9" cy="8" r="3" stroke={color} strokeWidth="1.6"/><path d="M3 19c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/><circle cx="19" cy="9" r="2.5" stroke={color} strokeWidth="1.5"/><path d="M14.5 18c0.5-2.2 2.5-4 4.5-4s4 1.8 4.5 4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>) },
                    { key: "vibe", svg: (color) => (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/></svg>) },
                    { key: "food", svg: (color) => (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 3 L6 11 M9 3 L9 11 M6 11 C 6 12.7 7.3 14 9 14 L 9 21 M9 11 C 9 12.7 7.7 14 6 14" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M16 3 C 14 4 13 6 13 9 C 13 11 14 12 16 12 L 16 21" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>) },
                    { key: "value", svg: (color) => (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6"/><path d="M14.5 9 C 14 8 13 7.5 12 7.5 C 10.5 7.5 9.5 8.5 9.5 9.5 C 9.5 10.5 10.5 11 12 11.5 C 13.5 12 14.5 12.5 14.5 13.5 C 14.5 14.5 13.5 15.5 12 15.5 C 11 15.5 10 15 9.5 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M12 5.5 L12 7.5 M12 15.5 L12 17.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>) },
                  ]
                    .filter(r => journalRatings[t.code][r.key])
                    .map(r => <span key={r.key} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${teal}18`, border: `1px solid ${teal}35`, color: teal, display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ display: "flex", alignItems: "center" }}>{r.svg(teal)}</span><span>{"★".repeat(journalRatings[t.code][r.key])}{"☆".repeat(5-journalRatings[t.code][r.key])}</span></span>)}
                </div>
                {journalMeta[t.code] && (journalMeta[t.code].budget || journalMeta[t.code].travelers) && (
                  <div style={{ display: "flex", gap: 12, marginTop: 6, fontSize: 11, color: textDim }}>
                    {journalMeta[t.code].budget && <span>💸 {journalMeta[t.code].budget}</span>}
                    {journalMeta[t.code].travelers && <span>👤 {journalMeta[t.code].travelers}</span>}
                  </div>
                )}
              </div>
            )}
            {/* Photos */}
            {(journalPhotos[t.code]||[]).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min((journalPhotos[t.code]||[]).length,3)},1fr)`, gap: 6, marginBottom: 14, borderRadius: 10, overflow: "hidden" }}>
                {(journalPhotos[t.code]||[]).map((photo,i) => (
                  <div key={i} style={{ aspectRatio: i===0&&(journalPhotos[t.code]||[]).length>1?"auto":"1", gridColumn: i===0&&(journalPhotos[t.code]||[]).length>=3?"span 2":"auto", borderRadius: 8, overflow: "hidden", minHeight: 80 }}>
                    <img src={photo} alt={`Trip memory ${i+1}`} style={{ width: "100%", height: "100%", objectFit: "cover", minHeight: 80 }} />
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 16, color: "#4A8CC4", marginBottom: 10, opacity: 0.4 }}>"</div>
            <p style={{ fontSize: 13, color: "#c0a878", lineHeight: 1.9, margin: 0, whiteSpace: "pre-wrap" }}>{journalEntries[t.code]}</p>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(27,58,92,0.060)" }}>
              <span style={{ fontSize: 10, color: textDim, fontStyle: "italic" }}>
                {journalEntries[t.code].split(/\s+/).filter(Boolean).length} words · {(journalPhotos[t.code]||[]).length} photo{(journalPhotos[t.code]||[]).length!==1?"s":""}
              </span>
              {/* "Edit entry" button removed Jun 11 2026: redundant with
                  the "Edit" button next to the My Journal section header
                  above. Both opened the same editor. Cas flagged the
                  duplicate. The top "Edit" now handles the full draft
                  hydration (entry + ratings + meta). */}
            </div>
          </div>
        ) : (
          <button onClick={() => { setJournalEditing(t.code); setJournalDraft(""); setJournalRatingDraft(p=>({...p,[t.code]:{}})); setJournalMetaDraft(p=>({...p,[t.code]:{}})); }}
            style={{ width: "100%", padding: "16px", borderRadius: 12, border: "1px dashed rgba(27,58,92,0.2)", background: "rgba(27,58,92,0.02)", cursor: "pointer", fontFamily: "inherit", textAlign: "center", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor="rgba(27,58,92,0.4)"; e.currentTarget.style.background="rgba(27,58,92,0.05)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(27,58,92,0.2)"; e.currentTarget.style.background="rgba(27,58,92,0.02)"; }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom: 6 }}><svg width="28" height="28" viewBox="0 0 44 44" fill="none"><rect x="10" y="6" width="24" height="32" rx="3" fill="rgba(27,58,92,0.15)" stroke="#1B3A5C" strokeWidth="1.8"/><rect x="8" y="8" width="4" height="28" rx="2" fill="rgba(27,58,92,0.3)" stroke="#1B3A5C" strokeWidth="1.4"/><line x1="16" y1="14" x2="30" y2="14" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round"/><line x1="16" y1="19" x2="30" y2="19" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round"/><line x1="16" y1="24" x2="26" y2="24" stroke="#1B3A5C" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/></svg></div>
            <div style={{ fontSize: 13, fontWeight: 700, color: teal, marginBottom: 4 }}>Start your journal entry</div>
            <div style={{ fontSize: 11, color: textDim, lineHeight: 1.6 }}>Rate your trip · Write about your experience · Add photos</div>
          </button>
        )}
      </div>
  );

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      {/* Outer container — in non-standalone mode (Account hub), uses the
         lightCard rounded-rect frame. In standalone mode (Boards/Trips/
         Journal/Profile from bottom nav), drops the frame so content sits
         directly on the page background — matches Take Me Anywhere /
         Trending Trips / Most Affordable page treatment for visual
         consistency across the app's feature surfaces. */}
      <div ref={acctModalRef} aria-label="My account" style={standalone
        ? { padding: 0, background: "transparent", border: "none", boxShadow: "none" }
        : { ...lightCard(darkMode), border: "1px solid rgba(74,140,196,0.22)" }
      }>

        {/* ── My Account hero — mirrors the travel dashboard's treatment.
           Renders ONLY in non-standalone mode (the Account hub page).
           UNIFIED (Jun 11 2026): renders consistently for all account-config
           tabs regardless of entry mode. Previously the hero rendered only
           when !standalone, so the SAME Profile tab would show the greeting
           when reached via Mood Board link (mode=account) but hide it when
           reached via top-nav Account (mode=profile, standalone=true). That
           inconsistency was the "account misstep" Cas flagged.
           Deep-page surfaces (Trips/Identity/Wishlist/Journal as their own
           focused pages) still skip the hero — visiting Trips shouldn't feel
           like a settings screen. Alerts + Itineraries removed from this
           list Jun 19, 2026 — they're now Trips sub-tabs and inherit the
           standalone Trips hero instead of this Account greeting. */}
        {["profile", "moodboard"].includes(tab) && (
          <div style={{ position: "relative", marginBottom: 22, padding: "4px 0 14px" }}>
            {/* Glow blob — same radial-gradient pattern as the travel dashboard */}
            <div aria-hidden="true" style={{
              position: "absolute", top: -20, left: -20,
              width: 180, height: 180, borderRadius: "50%",
              background: `radial-gradient(circle, ${gold}22 0%, transparent 65%)`,
              pointerEvents: "none",
            }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              {/* Top row: eyebrow only — back-to-home button removed per Jun 19,
                 2026. The bottom nav handles wayfinding (tap Home or any tab to
                 leave). Removing the button matches Boards/Trips standalone heroes. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 10, letterSpacing: 3, color: goldText, textTransform: "uppercase", fontWeight: 800 }}>Your Account</div>
              </div>
              {/* Title — gradient effect on first name if available */}
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, color: textPrimary, lineHeight: 1.05, letterSpacing: "-0.6px" }}>
                {user?.firstName ? (
                  <>
                    Hi,{" "}
                    <span style={{ ...gradientTextStyle(`linear-gradient(135deg, ${gold}, ${teal})`, gold) }}>{user.firstName}</span>
                  </>
                ) : (
                  <span style={{ ...gradientTextStyle(`linear-gradient(135deg, ${gold}, ${teal})`, gold) }}>My Account</span>
                )}
              </h1>
              {user?.email && (
                <div style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>{user.email}</div>
              )}
            </div>
          </div>
        )}

        {/* Standalone-mode back button removed — the persistent bottom nav handles
           wayfinding (tap Home or any other tab to leave this page). Removing the
           back button reclaims a few px and keeps the text-only hero clean. */}


        {/* ── Standalone Boards hero — text-only Option E. Tagline + title
           + count line. Sentence case headline per Jun 2026 Boards redesign
           handoff. No ✨ — single count line in text-secondary. */}
        {standalone && tab === "watchlist" && (
          <div style={{ marginBottom: 24, animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>save your favorite places, organize your dreams</div>
            <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: "#1B3A5C", letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase" }}>Boards</h2>
            {boards && boards.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <span style={{ fontSize: 12, color: textMuted, fontWeight: 600, letterSpacing: 0.3 }}>
                  {boards.length} board{boards.length !== 1 ? "s" : ""} · {boards.reduce((sum, b) => sum + (b.items?.length || 0), 0)} saved place{boards.reduce((sum, b) => sum + (b.items?.length || 0), 0) !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Standalone Trips hero — text-only Option E. Tagline + coral title
           + upcoming/completed count. Coral matches the homepage bottom-nav Trips
           color for consistent brand identity across surfaces. */}
        {standalone && (tab === "trips" || tab === "alerts" || tab === "itineraries") && (
          <div style={{ marginBottom: 24, animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>your journeys, past and ahead</div>
            <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: "#1B3A5C", letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase" }}>Trips</h2>
            {/* "X upcoming · Y completed" line moved from here into the
               Trips sub-tab content area (Jun 19, 2026 per Cas's call) so
               it sits with the trips list, not above the passport. */}
            {/* Contextual hint — first-time guidance for new users.
               Skipped if user already has trips (no need to explain). */}
            <div style={{ marginTop: 16 }}>
              <HintCard
                id="trips"
                title="Welcome to Trips"
                body="This is where your bookings live. Sub-tabs hold your itineraries (AI-generated trip plans) and alerts (price-drop notifications). Tap a trip to see its passport stamp and journal entries."
                accentColor="#ff6b6b"
                shouldShow={shouldShowHint("trips", (pastTrips?.length || 0) > 0)}
              />
            </div>
          </div>
        )}

        {/* ── Standalone Profile hero — text-only Option E. Personalized with
           user's first name when available, falls back to "Profile" otherwise. */}
        {/* Passport hero removed from Profile tab — moved to dashboard top-quick-stats.
           If reintroduced, the original block lived here and was wrapped in:
           {standalone && tab === "profile" && (...)} */}

        {/* Tab bar — 4-column vertical tile grid (replaces the older horizontal
           pill scroll). Each tile: SVG icon on top, label below. Two rows of
           4 tiles fit our 8 tabs cleanly. Selected tile gets the gold accent;
           inactive tiles stay neutral so the active one pops.
           UNIFIED (Jun 11 2026): renders for ALL entry points when the active
           tab is one of the account-config tabs (profile/alerts/itineraries/
           moodboard). Previously this depended on `!standalone`, which caused
           the modal to show a tab grid OR an accordion depending on whether
           the user arrived via top-nav (accordion) or a deep link (tabs).
           Two different nav paradigms for the same content was confusing.
           Other modes (trips/identity/wishlist/journal) intentionally skip
           the tab grid since they're full-page deep surfaces, not account
           config screens. Alerts + Itineraries removed from this list
           Jun 19, 2026 — they're now Trips sub-tabs and render through
           the shared Trips header (Passport + sub-nav) instead. */}
        {["profile", "moodboard"].includes(tab) && (
          <div style={{ display: "flex", borderBottom: "1px solid rgba(74,140,196,0.35)", marginBottom: 18, overflowX: "auto" }}>
            {TABS.map(t => {
              const locked = t.explorerOnly && !isExplorer;
              const isActive = tab === t.id;
              return (
                <button key={t.id} role="tab" aria-selected={isActive} aria-disabled={locked} onClick={() => setTab(t.id)}
                  style={{
                    flex: "1 1 auto",
                    padding: "12px 10px",
                    background: "transparent",
                    border: "none",
                    borderBottom: isActive ? "2px solid #1B3A5C" : "2px solid transparent",
                    marginBottom: "-1px",
                    color: isActive ? "#1B3A5C" : (locked ? textDim : textMuted),
                    fontSize: 11,
                    letterSpacing: 1.4,
                    fontWeight: isActive ? 800 : 600,
                    cursor: locked ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                    opacity: locked ? 0.6 : 1,
                    transition: "color 0.15s, border-color 0.15s",
                  }}>
                  {t.label}
                  {locked && <span style={{ marginLeft: 4, fontSize: 9 }}>🔒</span>}
                </button>
              );
            })}
          </div>
        )}


        {/* (Accordion view removed Jun 11 2026 — unified on tab grid above
            so account navigation is consistent across all entry points.
            Previously this rendered when `standalone && tab in [profile,
            alerts, itineraries, moodboard]` — a parallel dropdown-style
            navigation for the same set of tabs.) */}

        {tab === "watchlist" && (
          <>
            {/* Contextual hint — first-time guidance. Skipped if user
               already has boards (the default board doesn't count as
               "data" — only if they've added items to it). */}
            <HintCard
              id="boards"
              title="Welcome to Boards"
              body="Save places you love and organize them into themed boards — anything from a honeymoon shortlist to a dream-trip wall. Tap any destination's ☆ to save it; create new boards anytime."
              accentColor="#c4a045"
              shouldShow={shouldShowHint("boards", (boards || []).some(b => (b.items || []).length > 0))}
            />
            <BoardsTabContent
              boards={boards}
              setBoards={setBoards}
              boardsApi={boardsApi}
              onOpenBoardPicker={onOpenBoardPicker}
              onNavigate={onNavigate}
              onBookDest={onBookDest}
            />
          </>
        )}

        {/* ── Alerts ── Centralized alert management.
           Lists every destination with an active alert. Tap to edit (reuses
           the existing TripAlertModal via the parent's onOpenAlertModal handler).
           Trash icon to delete inline without opening the modal.
           Currently localStorage-only; cross-device sync is TBD when backend
           ships an alerts persistence endpoint. */}
        {/* ── Shared Trips header (Passport + sub-nav) ──
           Jun 19, 2026: alerts + itineraries moved from the main Account
           nav into Trips as sub-tabs (mirrors Journal's sub-tab pattern).
           Your Passport sits above the sub-nav and is shared across the
           three sub-tabs so it always reads as "this is your Trips area." */}
        {(tab === "trips" || tab === "alerts" || tab === "itineraries") && !cancelDone && !cancellingTrip && (() => {
          const upcomingTrips = pastTrips.filter(t => t.status === "upcoming" || t.status === "pending");
          const completedTrips = pastTrips.filter(t => !t.status || t.status === "past" || t.status === "completed");
          // Inverse lookup: flag emoji → country name. Matches the original
          // map used inline in the trips block; see comments there for source.
          const flagToCountry = {
            "🇬🇧":"United Kingdom","🇫🇷":"France","🇮🇹":"Italy","🇪🇸":"Spain","🇵🇹":"Portugal",
            "🇩🇪":"Germany","🇳🇱":"Netherlands","🇬🇷":"Greece","🇮🇸":"Iceland","🇳🇴":"Norway",
            "🇸🇪":"Sweden","🇩🇰":"Denmark","🇫🇮":"Finland","🇦🇹":"Austria","🇨🇭":"Switzerland",
            "🇧🇪":"Belgium","🇮🇪":"Ireland","🇵🇱":"Poland","🇨🇿":"Czech Republic","🇭🇺":"Hungary",
            "🇭🇷":"Croatia","🇷🇴":"Romania","🇧🇬":"Bulgaria","🇸🇮":"Slovenia","🇸🇰":"Slovakia",
            "🇷🇸":"Serbia","🇺🇦":"Ukraine","🇷🇺":"Russia","🇹🇷":"Turkey","🇲🇹":"Malta",
            "🇯🇵":"Japan","🇨🇳":"China","🇰🇷":"South Korea","🇹🇭":"Thailand","🇻🇳":"Vietnam",
            "🇮🇩":"Indonesia","🇵🇭":"Philippines","🇲🇾":"Malaysia","🇸🇬":"Singapore","🇹🇼":"Taiwan",
            "🇮🇳":"India","🇳🇵":"Nepal","🇰🇭":"Cambodia","🇱🇰":"Sri Lanka","🇲🇻":"Maldives",
            "🇦🇪":"UAE","🇸🇦":"Saudi Arabia","🇶🇦":"Qatar","🇮🇱":"Israel","🇯🇴":"Jordan",
            "🇪🇬":"Egypt","🇲🇦":"Morocco","🇿🇦":"South Africa","🇰🇪":"Kenya","🇹🇿":"Tanzania",
            "🇪🇹":"Ethiopia","🇳🇬":"Nigeria","🇬🇭":"Ghana","🇷🇼":"Rwanda","🇸🇳":"Senegal",
            "🇦🇺":"Australia","🇳🇿":"New Zealand","🇫🇯":"Fiji",
            "🇺🇸":"USA","🇨🇦":"Canada","🇲🇽":"Mexico",
            "🇦🇷":"Argentina","🇧🇷":"Brazil","🇨🇱":"Chile","🇨🇴":"Colombia","🇵🇪":"Peru",
            "🇨🇺":"Cuba","🇯🇲":"Jamaica","🇩🇴":"Dominican Republic","🇨🇷":"Costa Rica","🇵🇦":"Panama",
          };
          const passportMap = {};
          const addCountryFlag = (trip, tier) => {
            const dest = DESTINATIONS.find(d => d.name === trip.dest);
            let country, emoji;
            if (dest) {
              country = dest.country;
              emoji = dest.emoji || trip.emoji;
            } else {
              emoji = (trip.emoji && trip.emoji !== "🌍") ? trip.emoji : resolveFlagEmoji(trip.dest);
              country = flagToCountry[emoji] || (trip.dest && trip.dest.split(",").pop().trim()) || null;
            }
            if (!country || !emoji) return;
            if (!passportMap[country] || passportMap[country].tier < tier) {
              passportMap[country] = { country, emoji, tier };
            }
          };
          upcomingTrips.forEach(t => addCountryFlag(t, 2));
          completedTrips.forEach(t => addCountryFlag(t, 3));
          const passportFlags = Object.values(passportMap).sort((a, b) => b.tier - a.tier);
          const continentMap = { "Europe": "Europe", "Asia": "Asia", "Africa": "Africa", "Oceania": "Oceania", "North America": "Americas", "South America": "Americas", "Middle East": "Asia", "Americas": "Americas", "Caribbean": "Americas" };
          const continents = new Set(
            completedTrips
              .map(t => DESTINATIONS.find(d => d.name === t.dest)?.region)
              .filter(Boolean)
              .map(r => continentMap[r] || r)
          );
          const countriesVisited = new Set(
            completedTrips
              .map(t => DESTINATIONS.find(d => d.name === t.dest)?.country)
              .filter(Boolean)
          ).size;
          const passportGold = "#C9A876";

          return (
            <div>
              {/* Your Passport — only when there are flags to show */}
              {passportFlags.length > 0 && (
                <div style={{ padding: "16px 18px", borderRadius: 16, border: `1px solid ${darkMode ? "rgba(74,140,196,0.22)" : "rgba(44,62,80,0.55)"}`, background: darkMode ? "rgba(20,32,46,0.85)" : "linear-gradient(135deg, #2C3E50 0%, #34495E 100%)", marginBottom: 20, boxShadow: "0 2px 12px rgba(20,32,46,0.18), 0 1px 0 rgba(201,168,118,0.15) inset", color: "#FAFAF5" }}>
                  <style>{`
                    @keyframes passportFlagPop  { from{transform:scale(0) rotate(-8deg);opacity:0} 60%{transform:scale(1.12) rotate(2deg)} to{transform:scale(1) rotate(0);opacity:1} }
                    @keyframes passportFlagGlow { from{filter:drop-shadow(0 0 6px rgba(201,168,118,0.6)) drop-shadow(0 0 14px rgba(201,168,118,0.25))} to{filter:drop-shadow(0 0 12px rgba(201,168,118,0.9)) drop-shadow(0 0 24px rgba(201,168,118,0.5))} }
                  `}</style>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="4" y="3" width="14" height="18" rx="1.5" stroke={passportGold} strokeWidth="1.7"/>
                      <circle cx="11" cy="10" r="2.5" stroke={passportGold} strokeWidth="1.5"/>
                      <path d="M7.5 16 Q11 13.5 14.5 16" stroke={passportGold} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
                      <circle cx="20" cy="6" r="1.2" fill={passportGold} opacity="0.7"/>
                      <circle cx="20" cy="9" r="0.8" fill={passportGold} opacity="0.5"/>
                    </svg>
                    <div style={{ fontSize: 11, letterSpacing: 2.5, color: passportGold, textTransform: "uppercase", fontWeight: 800 }}>Your Passport</div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "4px 0 10px" }}>
                    {passportFlags.map((flag, i) => {
                      const isTraveled = flag.tier === 3;
                      return (
                        <div key={flag.country} title={`${flag.country} · ${isTraveled ? "Traveled" : "Booked"}`}
                          style={{
                            fontSize: 26, lineHeight: 1, cursor: "default",
                            animation: `passportFlagPop 0.45s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.05}s both`,
                            transition: "transform 0.2s",
                            ...(isTraveled ? {
                              filter: `drop-shadow(0 0 8px rgba(74,140,196,0.7)) drop-shadow(0 0 16px rgba(74,140,196,0.3))`,
                              animation: `passportFlagPop 0.45s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.05}s both, passportFlagGlow ${2.5 + (i * 0.3) % 1.5}s ease-in-out infinite alternate`,
                            } : {
                              opacity: 0.78,
                              filter: "drop-shadow(0 0 5px rgba(27,58,92,0.55)) drop-shadow(0 0 10px rgba(27,58,92,0.25))",
                            }),
                          }}
                          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.18)"}
                          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                          {flag.emoji}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 18, fontSize: 11, color: "rgba(250,250,245,0.7)", fontWeight: 600, letterSpacing: 0.5, paddingTop: 8, borderTop: `1px solid rgba(201,168,118,0.18)` }}>
                    <div><span style={{ color: passportGold, fontWeight: 800 }}>{countriesVisited}</span> {countriesVisited === 1 ? "country" : "countries"}</div>
                    <div><span style={{ color: passportGold, fontWeight: 800 }}>{continents.size}</span> {continents.size === 1 ? "continent" : "continents"}</div>
                  </div>
                </div>
              )}

              {/* Sub-nav — matches Journal sub-tab pattern.
                 Uppercase labels with active underline. */}
              <div style={{ display: "flex", borderBottom: "1px solid rgba(74,140,196,0.35)", marginBottom: 18, overflowX: "auto" }}>
                {[
                  { id: "trips",        label: "TRIPS" },
                  { id: "alerts",       label: "ALERTS" },
                  { id: "itineraries",  label: "ITINERARIES" },
                ].map(st => {
                  const active = tab === st.id;
                  return (
                    <button key={st.id} onClick={() => setTab(st.id)}
                      style={{ flex: "1 1 auto", padding: "12px 10px", background: "transparent", border: "none", borderBottom: active ? "2px solid #1B3A5C" : "2px solid transparent", marginBottom: "-1px", color: active ? "#1B3A5C" : textMuted, fontSize: 11, letterSpacing: 1.4, fontWeight: active ? 800 : 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "color 0.15s, border-color 0.15s" }}>
                      {st.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {tab === "alerts" && (() => {
          const alertEntries = Object.entries(tripAlerts || {});
          const enrichedAlerts = alertEntries
            .map(([destName, config]) => {
              const dest = DESTINATIONS.find(d => d.name === destName);
              return dest ? { dest, config } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
              // Most recently created first
              const aT = a.config?.createdAt ? new Date(a.config.createdAt).getTime() : 0;
              const bT = b.config?.createdAt ? new Date(b.config.createdAt).getTime() : 0;
              return bT - aT;
            });

          const ALERT_TYPE_META = {
            budget:  { icon: "💰", label: "Budget" },
            percent: { icon: "📉", label: "Price drop" },
            season:  { icon: "🍂", label: "Best season" },
          };

          return (
            <div>
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: textPrimary }}>Trip Alerts</h3>
                  {/* Cross-link to Trending Trips, where bell icons let
                     users add/remove alerts on individual destinations.
                     Closes the modal via onNavigate (mode change hides it). */}
                  <button onClick={() => onNavigate?.("trending")}
                    style={{ background: "none", border: "none", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, letterSpacing: 0.3, display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0 }}
                    aria-label="Go to Trending Trips to add or remove alerts">
                    Edit your alerts <span aria-hidden="true">→</span>
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                  We watch your alerted destinations and email you when conditions match. Tap an alert below to edit or remove.
                </p>
              </div>

              {enrichedAlerts.length === 0 ? (
                <div style={{
                  padding: "36px 24px", borderRadius: 14,
                  border: `1px dashed ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.08)"}`,
                  background: darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)",
                  textAlign: "center",
                }}>
                  <div style={{ marginBottom: 10, opacity: 0.5 }}>
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: "inline-block" }}>
                      <path d="M5.5 17 C 6 13 6 11 6.5 9.5 C 7.5 6.5 9.5 5 12 5 C 14.5 5 16.5 6.5 17.5 9.5 C 18 11 18 13 18.5 17 Z" stroke={textDim} strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
                      <path d="M5 17 L19 17" stroke={textDim} strokeWidth="1.6" strokeLinecap="round"/>
                      <path d="M10.5 19.5 C 11 20.5 13 20.5 13.5 19.5" stroke={textDim} strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 4 }}>No alerts yet</div>
                  <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.55, maxWidth: 320, margin: "0 auto", marginBottom: 16 }}>
                    Browse destinations and tap the bell icon to get notified about price drops, budget matches, or best seasons.
                  </div>
                  {onNavigate && (
                    <button
                      onClick={() => { onClose?.(); onNavigate("affordable"); }}
                      style={{
                        padding: "10px 18px", borderRadius: 22, border: "none",
                        background: "linear-gradient(135deg, #4A8CC4, #4A8CC4)",
                        color: "#FAFAF5", fontSize: 13, fontWeight: 800,
                        cursor: "pointer", fontFamily: "inherit",
                        boxShadow: "0 4px 12px rgba(74,140,196,0.30)",
                      }}>
                      Set your first alert →
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {enrichedAlerts.map(({ dest, config }) => {
                    const types = config?.types || [];
                    return (
                      <div key={dest.name} style={{
                        position: "relative",
                        borderRadius: 14, overflow: "hidden",
                        border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.06)"}`,
                        background: darkMode ? "rgba(27,58,92,0.045)" : "#fff",
                      }}>
                        {/* Tap card body to edit (opens TripAlertModal via parent handler) */}
                        <button onClick={() => onOpenAlertModal(dest)}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            width: "100%", padding: "12px 14px", paddingRight: 50,
                            border: "none", background: "transparent",
                            textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                          }}>
                          <span style={{ fontSize: 28, flexShrink: 0 }}>{dest.emoji}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {dest.name}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {types.map(t => {
                                const meta = ALERT_TYPE_META[t]; if (!meta) return null;
                                let detail = "";
                                if (t === "budget" && config.budget) detail = ` $${config.budget.toLocaleString()}`;
                                else if (t === "percent" && config.percentOff) detail = ` ${config.percentOff}%`;
                                else if (t === "season" && config.months?.length) detail = ` ${config.months.length} mo`;
                                return (
                                  <span key={t} style={{
                                    fontSize: 10, padding: "3px 7px", borderRadius: 10,
                                    background: `${coral}15`, border: `1px solid ${coral}30`,
                                    color: coral, fontWeight: 600,
                                  }}>
                                    {meta.icon} {meta.label}{detail}
                                  </span>
                                );
                              })}
                              {types.length === 0 && (
                                <span style={{ fontSize: 11, color: textDim, fontStyle: "italic" }}>No types selected</span>
                              )}
                            </div>
                          </div>
                        </button>
                        {/* Delete button — overlays top-right */}
                        <button onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Remove the alert for ${dest.name}?`)) {
                            setTripAlerts(prev => {
                              const next = { ...prev };
                              delete next[dest.name];
                              return next;
                            });
                          }
                        }}
                          aria-label={`Delete alert for ${dest.name}`}
                          style={{
                            position: "absolute", top: 10, right: 10,
                            width: 30, height: 30, borderRadius: 15,
                            border: "none", background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)",
                            color: textDim, cursor: "pointer", padding: 0, fontFamily: "inherit",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = `${coral}20`; e.currentTarget.style.color = coral; }}
                          onMouseLeave={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)"; e.currentTarget.style.color = textDim; }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M5 7 L 19 7 M10 11 L 10 16 M14 11 L 14 16 M6 7 L 7 19 a 1 1 0 0 0 1 1 h 8 a 1 1 0 0 0 1 -1 L 18 7 M9 7 L 9 4 a 1 1 0 0 1 1 -1 h 4 a 1 1 0 0 1 1 1 L 15 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Journal (aggregate view) ──
            Top-nav surface routing here from mode=journal. Shows ALL past
            trips with the FULL journal editor inline — same flow as the
            Trips tab (rate, write, voice dictate, add photos, save). Per
            Cas's Jun 11 product decision, Journal moves OUT of the Trips
            tab and gets its own top-nav home because it's a primary
            retention surface. The journal experience is now the SAME
            whether accessed from Trips or Journal — uses shared
            renderJournalSection helper so there's no drift. */}
        {tab === "journal" && (() => {
          // ALL past trips — sorted most recent first. Users can journal any
          // trip from here without leaving the surface.
          const allTrips = (pastTrips || [])
            .filter(t => t.status !== "cancelled" && t.status !== "canceled")
            .sort((a, b) => {
              const aDate = a.dateTo || a.date || "";
              const bDate = b.dateTo || b.date || "";
              return bDate.localeCompare(aDate);
            });

          // Stats: only count trips that HAVE journal data
          const journaledTrips = allTrips.filter(t => {
            const hasEntry = journalEntries[t.code] && journalEntries[t.code].trim().length > 0;
            const hasRating = journalRatings[t.code] && Object.values(journalRatings[t.code]).some(Boolean);
            const hasPhotos = journalPhotos[t.code] && journalPhotos[t.code].length > 0;
            return hasEntry || hasRating || hasPhotos;
          });
          const totalEntries = journaledTrips.filter(t => journalEntries[t.code] && journalEntries[t.code].trim().length > 0).length;
          const totalPhotos = journaledTrips.reduce((sum, t) => sum + ((journalPhotos[t.code] || []).length), 0);
          const totalWords = journaledTrips.reduce((sum, t) => {
            const txt = journalEntries[t.code] || "";
            return sum + txt.split(/\s+/).filter(Boolean).length;
          }, 0);

          return (
            <div>
              {/* Header — passport-style stats. Gradient bubble + sun glow
                 wrapper removed Jun 19, 2026 to match Trips/Boards/Explore/
                 Account presentation. Home keeps its bubble. */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>your memories, captured and kept</div>
                <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: textPrimary, letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase", marginBottom: 14 }}>Journal</h2>
                {journaledTrips.length > 0 && (
                  <div>
                    {/* Hero stat — entries get top billing */}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                      <div style={{ fontSize: 48, fontWeight: 900, color: teal, lineHeight: 1, letterSpacing: "-0.02em" }}>{totalEntries}</div>
                      <div style={{ fontSize: 13, color: textPrimary, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{totalEntries === 1 ? "Entry" : "Entries"}</div>
                    </div>
                    {/* Supporting stats — words + pictures, smaller */}
                    <div style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: goldText, lineHeight: 1 }}>{totalWords.toLocaleString()}</span>
                        <span style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>{totalWords === 1 ? "Word" : "Words"}</span>
                      </div>
                      <div style={{ fontSize: 11, color: textDim, opacity: 0.55 }}>·</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: coral, lineHeight: 1 }}>{totalPhotos}</span>
                        <span style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>{totalPhotos === 1 ? "Picture" : "Pictures"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── JOURNAL SUB-TAB BAR — Dashboard / Stamps / Postcards / Trip Entries.
                 Stamps and Postcards migrated into Journal from Account → Profile
                 as part of the IA restructure. Dashboard is the default landing
                 view — surfaces recent activity across all sub-sections so users
                 see their travel life at a glance, not a tab picker. */}
              <div style={{ display: "flex", borderBottom: "1px solid rgba(74,140,196,0.35)", marginBottom: 18, overflowX: "auto" }}>
                {[
                  { id: "dashboard", label: "DASHBOARD" },
                  { id: "entries",   label: "ENTRIES" },
                  { id: "stamps",    label: "STAMPS" },
                  { id: "postcards", label: "POSTCARDS" },
                ].map(st => {
                  const active = journalSubTab === st.id;
                  return (
                    <button key={st.id} onClick={() => setJournalSubTab(st.id)}
                      style={{ flex: "1 1 auto", padding: "12px 10px", background: "transparent", border: "none", borderBottom: active ? "2px solid #1B3A5C" : "2px solid transparent", marginBottom: "-1px", color: active ? "#1B3A5C" : textMuted, fontSize: 11, letterSpacing: 1.4, fontWeight: active ? 800 : 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "color 0.15s, border-color 0.15s" }}>
                      {st.label}
                    </button>
                  );
                })}
              </div>

              {/* ── DASHBOARD — unified landing view ── */}
              {journalSubTab === "dashboard" && (() => {
                const stampEntries = Object.entries(stampsData);
                const visitedStamps = stampEntries.filter(([, v]) => v.state === "visited");
                const somedayStamps = stampEntries.filter(([, v]) => v.state === "someday");
                const postcardEntries = Object.entries(postcardsData);
                const visitedPostcards = postcardEntries.filter(([, v]) => v.state === "visited");
                const dreamingPostcards = postcardEntries.filter(([, v]) => v.state === "dreaming");
                const latestVisitedStamp = [...visitedStamps].sort((a, b) => (b[1].visitedAt || 0) - (a[1].visitedAt || 0))[0];
                const latestSomedayStamp = [...somedayStamps].sort((a, b) => (b[1].updatedAt || 0) - (a[1].updatedAt || 0))[0];
                const latestDreamingPostcard = [...dreamingPostcards].sort((a, b) => (b[1].updatedAt || 0) - (a[1].updatedAt || 0))[0];
                const lookupStamp = (id) => (typeof SUGGESTED_STAMPS !== "undefined" ? SUGGESTED_STAMPS : []).find(s => s.id === id);
                const lookupPostcard = (id) => (typeof POSTCARDS_CATALOG !== "undefined" ? POSTCARDS_CATALOG : []).find(p => p.id === id);
                const latestJournaledTrip = journaledTrips[0];
                const hasAnyActivity = visitedStamps.length || visitedPostcards.length || somedayStamps.length || dreamingPostcards.length || journaledTrips.length;
                return (
                  <div style={{ display: "grid", gap: 14 }}>
                    {/* At a glance — counters */}
                    <div style={{ padding: "18px 20px", borderRadius: 14, background: `${teal}06`, border: `1px solid ${teal}25` }}>
                      <div style={{ fontSize: 10, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>AT A GLANCE</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                        <div><div style={{ fontSize: 22, fontWeight: 900, color: teal, lineHeight: 1 }}>{visitedStamps.length}</div><div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>Stamps</div></div>
                        <div><div style={{ fontSize: 22, fontWeight: 900, color: goldText, lineHeight: 1 }}>{visitedPostcards.length}</div><div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>Postcards</div></div>
                        <div><div style={{ fontSize: 22, fontWeight: 900, color: coral, lineHeight: 1 }}>{somedayStamps.length + dreamingPostcards.length}</div><div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>Dreaming</div></div>
                        <div><div style={{ fontSize: 22, fontWeight: 900, color: textPrimary, lineHeight: 1 }}>{journaledTrips.length}</div><div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>Entries</div></div>
                      </div>
                    </div>

                    {/* Recently visited — latest Visited stamp */}
                    {latestVisitedStamp && (() => {
                      const [sid, sdata] = latestVisitedStamp;
                      const meta = lookupStamp(sid);
                      if (!meta) return null;
                      return (
                        <div onClick={() => setJournalSubTab("stamps")} role="button" tabIndex={0} style={{ padding: "16px 20px", borderRadius: 14, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", border: `1px solid ${borderColor}`, cursor: "pointer" }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>RECENTLY VISITED</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ fontSize: 28 }}>{meta.emoji || "📍"}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary, lineHeight: 1.2 }}>{meta.title}</div>
                              <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{meta.country}{sdata.visitedAt ? ` · ${new Date(sdata.visitedAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })}` : ""}</div>
                              {sdata.visitedNote && <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic", marginTop: 4, lineHeight: 1.4 }}>"{sdata.visitedNote.length > 80 ? sdata.visitedNote.slice(0, 80) + "…" : sdata.visitedNote}"</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Currently dreaming — most recent Someday stamp + Dreaming postcard */}
                    {(latestSomedayStamp || latestDreamingPostcard) && (
                      <div style={{ padding: "16px 20px", borderRadius: 14, background: `${goldText}06`, border: `1px solid ${goldText}25` }}>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>CURRENTLY DREAMING</div>
                        <div style={{ display: "grid", gridTemplateColumns: latestSomedayStamp && latestDreamingPostcard ? "1fr 1fr" : "1fr", gap: 10 }}>
                          {latestSomedayStamp && (() => {
                            const [sid] = latestSomedayStamp;
                            const meta = lookupStamp(sid);
                            if (!meta) return null;
                            return (
                              <div onClick={() => setJournalSubTab("stamps")} role="button" tabIndex={0} style={{ padding: "10px 12px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.05)" : "rgba(255,255,255,0.6)", cursor: "pointer" }}>
                                <div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Stamp</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ fontSize: 18 }}>{meta.emoji || "📍"}</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}>{meta.title}</div>
                                </div>
                              </div>
                            );
                          })()}
                          {latestDreamingPostcard && (() => {
                            const [pid] = latestDreamingPostcard;
                            const meta = lookupPostcard(pid);
                            if (!meta) return null;
                            return (
                              <div onClick={() => setJournalSubTab("postcards")} role="button" tabIndex={0} style={{ padding: "10px 12px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.05)" : "rgba(255,255,255,0.6)", cursor: "pointer" }}>
                                <div style={{ fontSize: 9, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Postcard</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}>{meta.title || meta.destName}</div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Latest journal entry preview */}
                    {latestJournaledTrip && (
                      <div onClick={() => setJournalSubTab("entries")} role="button" tabIndex={0} style={{ padding: "16px 20px", borderRadius: 14, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", border: `1px solid ${borderColor}`, cursor: "pointer" }}>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>LATEST JOURNAL ENTRY</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary, marginBottom: 4 }}>{latestJournaledTrip.dest}</div>
                        {latestJournaledTrip.date && <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>{latestJournaledTrip.date}</div>}
                        {journalEntries[latestJournaledTrip.code] && <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic", lineHeight: 1.5 }}>"{journalEntries[latestJournaledTrip.code].length > 140 ? journalEntries[latestJournaledTrip.code].slice(0, 140) + "…" : journalEntries[latestJournaledTrip.code]}"</div>}
                      </div>
                    )}

                    {/* Empty state for new users */}
                    {!hasAnyActivity && (
                      <div style={{ textAlign: "center", padding: "32px 20px", background: `${teal}06`, borderRadius: 14, border: `1px dashed ${teal}30` }}>
                        <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.7 }}>📓</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, marginBottom: 6 }}>Your travel life starts here</div>
                        <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5, maxWidth: 320, margin: "0 auto" }}>
                          Mark places you've been, save destinations you're dreaming of, and journal trips as you take them. Your year wraps up in December.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── STAMPS — migrated from Account → Profile → Stamps. Same content,
                 new home. The conditional check now drives off journalSubTab. ── */}
              {/* ── Sub-tab: Stamps ── */}
              {journalSubTab === "stamps" && (() => {
                // Compute counts and filtered list from stampsData state.
                const allEntries = Object.entries(stampsData);
                const somedayCount = allEntries.filter(([, v]) => v.state === "someday").length;
                const visitedCount = allEntries.filter(([, v]) => v.state === "visited").length;
                const totalSaved = allEntries.length;
                // Filter by current pill selection
                const filtered = allEntries
                  .filter(([, v]) => {
                    if (stampsFilter === "someday") return v.state === "someday";
                    if (stampsFilter === "visited") return v.state === "visited";
                    return true;
                  })
                  .sort((a, b) => (b[1].updatedAt || b[1].createdAt || 0) - (a[1].updatedAt || a[1].createdAt || 0));

                // Resolve each entry to its full stamp catalog metadata
                const resolved = filtered.map(([id, meta]) => {
                  const catalog = (typeof SUGGESTED_STAMPS !== "undefined" ? SUGGESTED_STAMPS : []).find(s => s.id === id);
                  return catalog ? { id, ...catalog, ...meta } : null;
                }).filter(Boolean);

                return (
                  <div>
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: textPrimary }}>Stamps</h3>
                      <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                        A passport of places you're dreaming about and places you've been. Tap any stamp to mark it visited or add notes about when.
                      </p>
                    </div>

                    {/* Count display — no progress bar per spec */}
                    <div style={{
                      fontSize: 11,
                      letterSpacing: 2,
                      color: textMuted,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      marginBottom: 14,
                    }}>
                      {totalSaved === 0 ? "Your stamp collection" : (
                        <>
                          <span style={{ color: "#1B3A5C" }}>{somedayCount} dreaming</span>
                          {" · "}
                          <span style={{ color: "#1B3A5C" }}>{visitedCount} visited</span>
                        </>
                      )}
                    </div>

                    {/* Filter pills — only show if there are stamps */}
                    {totalSaved > 0 && (
                      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                        {[
                          { id: "all", label: `All ${totalSaved}` },
                          { id: "someday", label: `Someday ${somedayCount}` },
                          { id: "visited", label: `Visited ${visitedCount}` },
                        ].map(p => {
                          const active = stampsFilter === p.id;
                          return (
                            <button
                              key={p.id}
                              onClick={() => setStampsFilter(p.id)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: 18,
                                border: active ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                                background: active ? "rgba(27,58,92,0.08)" : "transparent",
                                color: active ? "#1B3A5C" : textMuted,
                                fontSize: 12,
                                fontWeight: active ? 800 : 600,
                                cursor: "pointer",
                                fontFamily: "inherit",
                                letterSpacing: 0.3,
                                transition: "all 0.15s",
                              }}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* "+ Add" button — visible on all three filter states.
                       On Someday/Visited: opens seeding in that specific state.
                       On All: defaults to Visited (most common entry — places
                       you've been). Always visible so users can add without
                       switching filters first. */}
                    <div style={{ marginBottom: 14 }}>
                      <button onClick={() => onOpenStampsSeeding(stampsFilter === "someday" ? "someday" : "visited")}
                        style={{
                          padding: "8px 16px", borderRadius: 10, border: "none",
                          background: stampsFilter === "someday" ? "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)" : "#1B3A5C",
                          color: "#FAFAF5",
                          fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                          letterSpacing: 0.3,
                          boxShadow: stampsFilter === "someday" ? "0 2px 8px rgba(232,154,60,0.3)" : "0 2px 8px rgba(27,58,92,0.25)",
                        }}>
                        + Add {stampsFilter === "someday" ? "Someday" : stampsFilter === "visited" ? "Visited" : ""} stamps
                      </button>
                    </div>

                    {/* Stamps grid OR empty state */}
                    {resolved.length === 0 ? (
                      <div style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                        border: "1px solid rgba(74,140,196,0.4)",
                        borderRadius: 14,
                      }}>
                        <div style={{
                          fontSize: 32,
                          marginBottom: 12,
                          opacity: 0.5,
                        }}>📮</div>
                        <div style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          fontStyle: "italic",
                          fontSize: 17,
                          fontWeight: 500,
                          color: textPrimary,
                          marginBottom: 8,
                        }}>
                          {totalSaved === 0 ? "Your passport is empty" : "Nothing here yet"}
                        </div>
                        <div style={{
                          fontSize: 13,
                          color: textMuted,
                          lineHeight: 1.5,
                          marginBottom: 16,
                          maxWidth: 280,
                          margin: "0 auto 16px",
                        }}>
                          {totalSaved === 0
                            ? "Mark the places you've been. Stamps land here as a passport of every country you've visited."
                            : `No ${stampsFilter} stamps yet. Try another filter or keep exploring.`}
                        </div>
                        {/* Direct CTA into the seeding modal — same trigger
                           as the homepage "Add stamps for places you've
                           been?" prompt. Replaces the older "Go to dashboard"
                           CTA which required an extra step. Added Jun 25, 2026. */}
                        {totalSaved === 0 && (
                          <button
                            onClick={() => onOpenStampsSeeding(stampsFilter === "someday" ? "someday" : "visited")}
                            style={{
                              padding: "10px 20px",
                              borderRadius: 22,
                              border: "none",
                              background: "#1B3A5C",
                              color: "#FAFAF5",
                              fontSize: 12,
                              fontWeight: 800,
                              letterSpacing: 0.5,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              boxShadow: "0 2px 8px rgba(27,58,92,0.25)",
                            }}
                          >
                            Add stamps →
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 12,
                      }}>
                        {resolved.map(stamp => (
                          <button
                            key={stamp.id}
                            onClick={() => setActiveStampDetail(stamp)}
                            style={{
                              background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                              border: "1px solid rgba(74,140,196,0.5)",
                              borderRadius: 12,
                              padding: 0,
                              overflow: "hidden",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              textAlign: "left",
                              transition: "transform 0.15s, box-shadow 0.15s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(27,58,92,0.08)"; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                          >
                            {/* Illustration area */}
                            <div style={{ height: 90, overflow: "hidden", borderBottom: "1px dashed rgba(74,140,196,0.4)", position: "relative" }}>
                              <StampIllustration id={stamp.id} />
                              {stamp.canon === "wonder" && (
                                <div style={{ position: "absolute", top: 4, right: 4, pointerEvents: "none" }}>
                                  <WonderSeal size={26} />
                                </div>
                              )}
                            </div>
                            {/* Info area */}
                            <div style={{ padding: "8px 10px 10px" }}>
                              <div style={{ fontSize: 9, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", marginBottom: 2 }}>{stamp.country}</div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, lineHeight: 1.2, marginBottom: 6 }}>{stamp.title}</div>
                              <div style={{
                                fontSize: 9,
                                letterSpacing: 1.5,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: stamp.state === "visited" ? "#1B3A5C" : "#A87C3C",
                              }}>
                                {stamp.state === "visited" ? "✓ Visited" : "○ Someday"}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* ── End sub-tab: Stamps ── */}

              {/* ── POSTCARDS — migrated from Account → Profile → Postcards. Same
                 content, new home. The conditional check now drives off journalSubTab. ── */}
              {/* ── Sub-tab: Postcards ── */}
              {journalSubTab === "postcards" && (() => {
                // Postcards are EXPLICITLY collected via the swipe-right sheet
                // (BoardPickerModal's "Collect postcard" section). No auto-derivation
                // from boards / wishlist / past trips anymore — those are separate
                // concerns now. Boards = trip planning, Postcards = collection.
                const userPostcardEntries = Object.entries(postcardsData).map(([id, meta]) => {
                  return [id, { ...meta, state: meta.state || "dreaming", _derived: false }];
                });

                const dreamingCount = userPostcardEntries.filter(([, v]) => v.state === "dreaming").length;
                const visitedCount = userPostcardEntries.filter(([, v]) => v.state === "visited").length;
                const totalDiscovered = userPostcardEntries.length;

                const filtered = userPostcardEntries
                  .filter(([, v]) => {
                    if (postcardsFilter === "dreaming") return v.state === "dreaming";
                    if (postcardsFilter === "visited") return v.state === "visited";
                    return true;
                  })
                  .sort((a, b) => (b[1].updatedAt || b[1].createdAt || 0) - (a[1].updatedAt || a[1].createdAt || 0));

                // Resolve postcard IDs against the catalog for display info.
                const resolved = filtered.map(([id, meta]) => {
                  const catalog = POSTCARDS_CATALOG.find(p => p.id === id);
                  return catalog ? { id, ...catalog, ...meta } : null;
                }).filter(Boolean);

                const catalogEmpty = POSTCARDS_CATALOG.length === 0;

                return (
                  <div>
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: textPrimary }}>Postcards</h3>
                      <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                        Destinations you've collected — landscape illustrations from places you'd love to visit or have already been. Tap any postcard to mark it visited or jot down a memory.
                      </p>
                    </div>

                    {/* Count display — mirrors Stamps tab pattern */}
                    <div style={{
                      fontSize: 11,
                      letterSpacing: 2,
                      color: textMuted,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      marginBottom: 14,
                    }}>
                      {totalDiscovered === 0 ? "Your postcard collection" : (
                        <>
                          <span style={{ color: "#1B3A5C" }}>{dreamingCount} dreaming</span>
                          {" · "}
                          <span style={{ color: "#1B3A5C" }}>{visitedCount} visited</span>
                        </>
                      )}
                    </div>

                    {/* Filter pills — show only when user has discovered postcards */}
                    {totalDiscovered > 0 && (
                      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                        {[
                          { id: "all", label: `All ${totalDiscovered}` },
                          { id: "dreaming", label: `Dreaming ${dreamingCount}` },
                          { id: "visited", label: `Visited ${visitedCount}` },
                        ].map(p => {
                          const active = postcardsFilter === p.id;
                          return (
                            <button
                              key={p.id}
                              onClick={() => setPostcardsFilter(p.id)}
                              style={{
                                padding: "6px 14px",
                                borderRadius: 18,
                                border: active ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                                background: active ? "rgba(27,58,92,0.08)" : "transparent",
                                color: active ? "#1B3A5C" : textMuted,
                                fontSize: 12,
                                fontWeight: active ? 800 : 600,
                                cursor: "pointer",
                                fontFamily: "inherit",
                                letterSpacing: 0.3,
                                transition: "all 0.15s",
                              }}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* "+ Add" button — visible on all three filter states.
                       On Dreaming/Visited: opens seeding in that specific state.
                       On All: defaults to Dreaming (most common entry — places
                       you want to go). Always visible so users can add without
                       switching filters first. */}
                    <div style={{ marginBottom: 14 }}>
                      <button onClick={() => onOpenPostcardsSeeding(postcardsFilter === "visited" ? "visited" : "dreaming")}
                        style={{
                          padding: "8px 16px", borderRadius: 10, border: "none",
                          background: postcardsFilter === "visited" ? "#1B3A5C" : "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                          color: "#FAFAF5",
                          fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                          letterSpacing: 0.3,
                          boxShadow: postcardsFilter === "visited" ? "0 2px 8px rgba(27,58,92,0.25)" : "0 2px 8px rgba(232,154,60,0.3)",
                        }}>
                        + Add {postcardsFilter === "visited" ? "Visited" : postcardsFilter === "dreaming" ? "Dreaming" : ""} postcards
                      </button>
                    </div>

                    {/* Grid OR empty state */}
                    {resolved.length === 0 ? (
                      <div style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                        border: "1px solid rgba(74,140,196,0.4)",
                        borderRadius: 14,
                      }}>
                        <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}>✉️</div>
                        <div style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          fontStyle: "italic",
                          fontSize: 17,
                          fontWeight: 500,
                          color: textPrimary,
                          marginBottom: 8,
                        }}>
                          {catalogEmpty
                            ? "Postcards coming soon"
                            : totalDiscovered === 0
                            ? "Start your collection"
                            : "Nothing here yet"}
                        </div>
                        <div style={{
                          fontSize: 13,
                          color: textMuted,
                          lineHeight: 1.5,
                          marginBottom: 16,
                          maxWidth: 300,
                          margin: "0 auto 16px",
                        }}>
                          {catalogEmpty
                            ? "We're hand-illustrating 100 destinations. Check back soon."
                            : totalDiscovered === 0
                            ? "Mark places you've been or are dreaming about. Postcards land here as you collect them."
                            : `No ${postcardsFilter === "all" ? "" : postcardsFilter + " "}postcards yet. Try another filter or keep exploring.`}
                        </div>
                        {/* Direct CTA into the seeding modal — same trigger
                           as the homepage "Add postcards for places you've
                           been?" prompt. Filter-aware: opens in the right
                           state (dreaming vs visited); defaults to dreaming
                           in the "All" filter case. Added Jun 25, 2026. */}
                        {!catalogEmpty && totalDiscovered === 0 && (
                          <button
                            onClick={() => onOpenPostcardsSeeding(postcardsFilter === "visited" ? "visited" : "dreaming")}
                            style={{
                              padding: "10px 20px",
                              borderRadius: 22,
                              border: "none",
                              background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                              color: "#FAFAF5",
                              fontSize: 12,
                              fontWeight: 800,
                              letterSpacing: 0.5,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              boxShadow: "0 2px 8px rgba(232,154,60,0.3)",
                            }}
                          >
                            Add postcards →
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 12,
                      }}>
                        {resolved.map(postcard => (
                          <button
                            key={postcard.id}
                            onClick={() => setActivePostcardDetail(postcard)}
                            style={{
                              background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                              border: "1px solid rgba(74,140,196,0.5)",
                              borderRadius: 12,
                              padding: 0,
                              overflow: "hidden",
                              cursor: "pointer",
                              fontFamily: "inherit",
                              textAlign: "left",
                              transition: "transform 0.15s, box-shadow 0.15s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(27,58,92,0.08)"; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                          >
                            {/* Illustration area — landscape 7:5 aspect (postcard-shaped) */}
                            <div style={{ aspectRatio: "7 / 5", overflow: "hidden", borderBottom: "1px dashed rgba(74,140,196,0.4)" }}>
                              <PostcardIllustration id={postcard.id} />
                            </div>
                            {/* Info area */}
                            <div style={{ padding: "8px 10px 10px" }}>
                              <div style={{ fontSize: 9, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", marginBottom: 2 }}>{postcard.country}</div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, lineHeight: 1.2, marginBottom: 6 }}>{postcard.title}</div>
                              <div style={{
                                fontSize: 9,
                                letterSpacing: 1.5,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: postcard.state === "visited" ? "#1B3A5C" : "#A87C3C",
                              }}>
                                {postcard.state === "visited" ? "✓ Visited" : "○ Dreaming"}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* ── End sub-tab: Postcards ── */}

              {/* ── TRIP ENTRIES — the existing Journal content, scoped under its sub-tab ── */}
              {journalSubTab === "entries" && (<>
                {/* Action row — View Saved Itineraries + Add Past Trip.
                    Replaces the old "Want to journal a new trip?" link-out.
                    Add Trip is fully inline in Journal so users don't have
                    to bounce to Trips to journal something new.
                    Only renders when there are past trips (empty state
                    handles the no-trips case below). */}
                {allTrips.length > 0 && (
                  <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {savedItineraries && savedItineraries.length > 0 && (
                      <button onClick={() => setTab("itineraries")}
                        style={{ flex: "1 1 160px", padding: "10px 14px", borderRadius: 10, border: `1px solid ${"#4A8CC4"}40`, background: `${"#4A8CC4"}08`, color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4 L19 4 L19 20 L12 16 L5 20 Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill="none"/></svg>
                        Saved Itineraries ({savedItineraries.length})
                      </button>
                    )}
                    <button onClick={() => setJournalAddOpen(o => !o)}
                      style={{ flex: "1 1 160px", padding: "10px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${teal}, ${goldText})`, color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      {journalAddOpen ? "✕ Cancel" : "+ Add a Past Trip"}
                    </button>
                  </div>
                )}

                {/* Inline mini Add Trip form. Lives in Journal scope so the
                    user can add + journal without leaving the surface.
                    Minimal fields: destination, dates, traveler count. Trip
                    details (budget, notes, etc.) can be added later in Trips. */}
                {journalAddOpen && (
                  <div style={{ marginTop: 12, padding: "16px", borderRadius: 14, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", border: `1px solid ${teal}25`, animation: "fadeIn 0.2s ease" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: teal, marginBottom: 12 }}>📍 Add a past trip to journal</div>

                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>DESTINATION</div>
                      <input value={journalAddForm.dest}
                        onChange={e => setJournalAddForm(f => ({ ...f, dest: e.target.value }))}
                        placeholder="e.g. Bali, Indonesia"
                        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}/>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>FROM</div>
                        <input type="date" value={journalAddForm.dateFrom}
                          onChange={e => setJournalAddForm(f => ({ ...f, dateFrom: e.target.value, dateTo: (f.dateTo && e.target.value && f.dateTo < e.target.value) ? e.target.value : f.dateTo }))}
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}/>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>TO</div>
                        <input type="date" value={journalAddForm.dateTo}
                          min={journalAddForm.dateFrom || undefined}
                          onChange={e => setJournalAddForm(f => ({ ...f, dateTo: e.target.value }))}
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}/>
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 6 }}>TRAVELERS</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[1, 2, 3, 4].map(n => (
                          <button key={n} onClick={() => setJournalAddForm(f => ({ ...f, travelers: String(n) }))}
                            style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: `1px solid ${journalAddForm.travelers === String(n) ? teal : borderColor}`, background: journalAddForm.travelers === String(n) ? `${teal}18` : "transparent", color: journalAddForm.travelers === String(n) ? teal : textMuted, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                            {formatTravelers(n, "short")}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button onClick={() => {
                      // Validation: destination required
                      const destTrim = (journalAddForm.dest || "").trim();
                      if (!destTrim) {
                        alert("Please enter a destination.");
                        return;
                      }
                      // Build trip with minimal fields. dest + dates +
                      // travelers; everything else null/empty for later edit.
                      const code = `USR-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
                      const dateFrom = journalAddForm.dateFrom || null;
                      const dateTo = journalAddForm.dateTo || null;
                      // Build a readable date label (e.g. "Mar 12 – Mar 19, 2026")
                      const dateLabel = (() => {
                        if (!dateFrom && !dateTo) return "";
                        try {
                          const fmt = d => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
                          if (dateFrom && dateTo) {
                            const y = new Date(dateTo + "T00:00:00").getFullYear();
                            return `${fmt(dateFrom)} – ${fmt(dateTo)}, ${y}`;
                          }
                          if (dateFrom) return fmt(dateFrom);
                          return fmt(dateTo);
                        } catch { return ""; }
                      })();
                      const newTrip = {
                        dest: destTrim,
                        emoji: resolveFlagEmoji(destTrim) || "🌍",
                        code,
                        dateFrom,
                        dateTo,
                        status: "completed",
                        source: "user",
                        days: null,
                        date: dateLabel,
                        budget: null,
                        travelers: parseInt(journalAddForm.travelers, 10) || 1,
                        liked: null,
                        disliked: null,
                        favSpot: null,
                        external: true,
                      };
                      if (tripsApi) tripsApi.createTrip(newTrip);
                      else setPastTrips(p => [...p, newTrip]);
                      // Auto-open journal editor for this new trip so the user
                      // lands directly in the writing flow. Reset form + close.
                      setJournalEditing(code);
                      setJournalDraft("");
                      setJournalRatingDraft(p => ({ ...p, [code]: {} }));
                      setJournalMetaDraft(p => ({ ...p, [code]: {} }));
                      setJournalAddForm({ dest: "", dateFrom: "", dateTo: "", travelers: "1" });
                      setJournalAddOpen(false);
                      // Scroll to the new card after render
                      setTimeout(() => {
                        const el = document.getElementById(`jga-journal-trip-${code}`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 100);
                    }}
                      style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${teal}, ${goldText})`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      Save & Start Journaling →
                    </button>
                  </div>
                )}

              {/* Empty state — only when zero past trips at all */}
              {allTrips.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 20px", background: `${teal}06`, borderRadius: 14, border: `1px dashed ${teal}30` }}>
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.7 }}>📓</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, marginBottom: 6 }}>No trips to journal yet</div>
                  <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5, maxWidth: 320, margin: "0 auto 16px" }}>
                    Add a past trip in the Trips tab — then come back here to capture your memories with ratings, photos, and voice-to-text dictation.
                  </div>
                  <button onClick={() => { setTab("trips"); }}
                    style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${teal}, ${goldText})`, color: "#FAFAF5", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                    Add a Trip →
                  </button>
                </div>
              )}

              {/* Trip cards — each embeds the FULL journal editor */}
              {allTrips.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {allTrips.map(t => (
                    <div key={t.code} id={`jga-journal-trip-${t.code}`} style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(250,250,245,0.7)", border: `1px solid ${borderColor}`, borderRadius: 14, padding: "16px 18px" }}>
                      {/* Trip header — emoji + dest + date */}
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${borderColor}` }}>
                        <span style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{(t.emoji && t.emoji !== "🌍") ? t.emoji : (resolveFlagEmoji(t.dest) || "🌍")}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, lineHeight: 1.2 }}>{t.dest}</div>
                          {t.date && <div style={{ fontSize: 11, color: textMuted, marginTop: 3 }}>{t.date}</div>}
                        </div>
                      </div>

                      {/* Full journal editor — same as Trips tab uses */}
                      {renderJournalSection(t)}
                    </div>
                  ))}
                </div>
              )}
              </>)}
              </div>
          );
        })()}




        {/* ── Trips ── */}
        {tab === "trips" && (
          <div>
            {/* Cancellation confirmed screen */}
            {cancelDone && (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                  <svg width="64" height="64" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="19" fill="rgba(109,189,109,0.12)" stroke="#6dbd6d" strokeWidth="2"/>
                    <circle cx="22" cy="22" r="13" fill="rgba(109,189,109,0.08)" stroke="#6dbd6d" strokeWidth="1.2" opacity="0.6"/>
                    <path d="M13 22 L19 28 L31 16" stroke="#6dbd6d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>
                <div style={{ fontSize: 20, fontWeight: 900, color: darkMode ? "#6dbd6d" : "#2a7a4a", marginBottom: 8 }}>Booking Cancelled</div>
                <div style={{ fontSize: 13, color: textMuted, marginBottom: 20, lineHeight: 1.6 }}>Your trip to {cancelDone} has been cancelled. A confirmation and refund details have been sent to your email.</div>
                <button onClick={() => { setCancelDone(null); setCancellingTrip(null); setCancelReason(""); }} style={{ padding: "10px 24px", borderRadius: 10, border: `1px solid rgba(74,140,196,0.3)`, background: "rgba(74,140,196,0.08)", color: goldText, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back to My Trips</button>
              </div>
            )}

            {/* Cancellation flow */}
            {cancellingTrip && !cancelDone && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <button onClick={() => { setCancellingTrip(null); setCancelReason(""); }} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: coral }}>Cancel Trip to {cancellingTrip.dest}</h3>
                </div>
                <p style={{ fontSize: 13, color: textMuted, marginBottom: 16, fontStyle: "italic" }}>This helps us improve. Select a reason to continue.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
                  {["Dates no longer work", "Change of plans", "Found a better deal", "Budget constraints", "Personal circumstances", "Other"].map(r => (
                    <button key={r} onClick={() => setCancelReason(r)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left", border: `1px solid ${cancelReason === r ? "rgba(255,107,107,0.5)" : "rgba(27,58,92,0.096)"}`, background: cancelReason === r ? "rgba(255,107,107,0.08)" : "rgba(27,58,92,0.030)" }}>
                      <span style={{ fontSize: 13, color: cancelReason === r ? "#f0c0c0" : "#4A6B8A" }}>{r}</span>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${cancelReason === r ? coral : "rgba(27,58,92,0.18)"}`, background: cancelReason === r ? coral : "transparent", flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
                <div style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 12, padding: "14px", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: textMuted }}>Trip cost</span>
                    <span style={{ fontSize: 13, color: textPrimary }}>${cancellingTrip.budget?.toLocaleString() || "—"}</span>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,107,107,0.15)", paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: textMuted }}>Refund (100%)</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: darkMode ? "#6dbd6d" : "#2a7a4a" }}>${cancellingTrip.budget?.toLocaleString() || "—"}</span>
                  </div>
                </div>
                <button onClick={() => { setCancelDone(cancellingTrip.dest); }} disabled={!cancelReason}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: !cancelReason ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg,#FF6B6B,${coral})`, color: !cancelReason ? "#4A6B8A" : "#fff", fontWeight: 900, fontSize: 15, cursor: !cancelReason ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  Confirm Cancellation
                </button>
              </div>
            )}

            {/* Trips review modal removed — review now happens in Journal tab where
               richer rating fields (Overall, People, Vibe, Food, Worth-the-cost) +
               photos already live. Trips tab is logistics-only; the "Leave a Review"
               button on a completed trip jumps to Journal in edit mode for that trip. */}

            {/* Main trips list */}
            {!cancellingTrip && !cancelDone && (
              <div>
                {/* "X upcoming · Y completed" stat — moved here from the
                    standalone Trips hero (Jun 19, 2026) so it sits with the
                    trips list, not above the passport. */}
                {pastTrips && pastTrips.length > 0 && (() => {
                  const upcoming = pastTrips.filter(t => t.status === "upcoming" || t.status === "pending").length;
                  const completed = pastTrips.filter(t => t.status === "completed" || t.status === "past").length;
                  const parts = [];
                  if (upcoming > 0) parts.push(`${upcoming} upcoming`);
                  if (completed > 0) parts.push(`${completed} completed`);
                  if (parts.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <span style={{ fontSize: 11, color: textMuted, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase" }}>
                        {parts.join(" · ")}
                      </span>
                    </div>
                  );
                })()}

                {/* Saved for Later — trip drafts the user bookmarked via
                    QuickBookModal's "Save for later" button. Renders only when
                    there's at least one draft (no empty state clutter). Each
                    card has Continue booking (re-opens QBM with the saved dest
                    + dates pre-filled) and a small X to remove. */}
                {tripDrafts.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <Label color={amberText}>💭 Saved for Later</Label>
                    {tripDrafts.map(d => {
                      const dateLine = (d.departDate && d.returnDate)
                        ? `${d.departDate} → ${d.returnDate}`
                        : (d.departDate ? `Departs ${d.departDate}` : "Dates not set");
                      const handleContinue = () => {
                        if (typeof onBookDest === "function") {
                          // Pass the destination NAME (string) — onBookDest at
                          // the App level resolves it to a full DESTINATIONS
                          // object before opening QBM. Same pattern as the
                          // saved-itinerary Book button.
                          onBookDest(d.dest, undefined, {
                            days: (d.departDate && d.returnDate)
                              ? Math.max(1, Math.round((new Date(d.returnDate) - new Date(d.departDate)) / 86400000))
                              : null,
                            departDate: d.departDate || null,
                            returnDate: d.returnDate || null,
                          });
                          // Close the account modal so QBM is the focus on screen
                          if (typeof onClose === "function") onClose();
                        }
                      };
                      return (
                        <div key={d.id} style={{ background: "rgba(232,154,60,0.06)", border: "1px solid rgba(232,154,60,0.3)", borderRadius: 12, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ fontSize: 28, flexShrink: 0 }}>{d.emoji || "💭"}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary, marginBottom: 2 }}>{d.dest}{d.country ? ` · ${d.country}` : ""}</div>
                            <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>
                              {dateLine}
                              {d.travelers > 1 ? ` · ${d.travelers} travelers` : ""}
                              {d.labelTag ? ` · ${d.labelTag}` : ""}
                            </div>
                            <button onClick={handleContinue}
                              style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                              Continue booking →
                            </button>
                          </div>
                          <button onClick={() => removeDraft(d.id)} aria-label={`Remove ${d.dest} from saved drafts`}
                            style={{ background: "transparent", border: "none", color: textDim, fontSize: 18, cursor: "pointer", padding: "4px 8px", fontFamily: "inherit", lineHeight: 1, flexShrink: 0 }}>
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pending trips */}
                {SAMPLE_TRIPS.filter(t => t.status === "pending").length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <Label color={teal}>🔔 Pending — Being Curated</Label>
                    {SAMPLE_TRIPS.filter(t => t.status === "pending").map(t => (
                      <div key={t.code} style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.25)", borderRadius: 12, padding: "14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ fontSize: 28, animation: "pulse 2s ease-in-out infinite" }}>✈️</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: teal }}>Your trip is being curated</div>
                          <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>Our team is sourcing flights, hotels & activities</div>
                          <div style={{ fontSize: 11, color: textDim, marginTop: 2 }}>Ref: {t.code}</div>
                        </div>
                        <div style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, background: "rgba(27,58,92,0.15)", color: teal, fontWeight: 700, letterSpacing: 1 }}>PENDING</div>
                      </div>
                    ))}
                  </div>
                )}

                <Label color="#4A8CC4">Upcoming Trips</Label>
                {SAMPLE_TRIPS.filter(t => t.status === "upcoming" && isVisibleTrip(t)).length === 0 ? (
                  <div style={{ fontSize: 13, color: textDim, fontStyle: "italic", marginBottom: 20 }}>No upcoming trips booked yet.</div>
                ) : SAMPLE_TRIPS.filter(t => t.status === "upcoming" && isVisibleTrip(t)).map(t => {
                  // Countdown
                  const msUntil = t.date ? (new Date(t.date.split("–")[0] + ", 2026") - new Date()) : null;
                  const daysUntil = msUntil !== null ? Math.ceil(msUntil / 86400000) : null;
                  const hoursUntil = msUntil !== null ? Math.floor((msUntil % 86400000) / 3600000) : null;
                  // Auto-computed flight-board status
                  const tripForStatus = { ...t, dateFrom: t.dateFrom || (t.date ? t.date.split("–")[0] + ", 2026" : null), dateTo: t.dateTo || (t.date ? t.date.split("–")[1]?.trim() + ", 2026" : null) };
                  const { status: flightStatus, reason: delayReason, action: delayAction } = getTripStatus(tripForStatus, user);
                  return (
                  <div key={t.code} style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 12, padding: "14px", marginBottom: 10 }}>
                    {daysUntil !== null && daysUntil > 0 && (
                      <div style={{ marginBottom: 10, background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "12px 16px" }}>
                        <div style={{ fontSize: 10, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 8, textAlign: "center" }}>✈️ Departure Countdown</div>
                        <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                          {[
                            { val: daysUntil, label: "DAYS" },
                            { val: hoursUntil, label: "HRS" },
                          ].map(({ val, label }) => (
                            <div key={label} style={{ textAlign: "center", minWidth: 56 }}>
                              <div style={{ fontSize: 32, fontWeight: 900, color: goldText, lineHeight: 1, letterSpacing: -1 }}>{String(val).padStart(2, "0")}</div>
                              <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, marginTop: 3 }}>{label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 28 }}>{(t.emoji && t.emoji !== "🌍") ? t.emoji : (resolveFlagEmoji(t.dest) || "🌍")}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{t.dest}</div>
                        <div style={{ fontSize: 12, color: teal }}>{t.date}</div>
                        <div style={{ fontSize: 11, color: textDim, marginTop: 2 }}>Ref: {t.code}</div>
                      </div>
                      <TripStatusPill status={flightStatus} variant="solari" />
                    </div>
                    {flightStatus === "DELAYED" && delayReason && (
                      <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,166,109,0.08)", border: "1px solid rgba(255,166,109,0.25)", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#ffa66d" }}>
                        <span>⚠</span><span style={{ flex: 1 }}>{delayReason}</span>
                        {delayAction && <span style={{ fontWeight: 700 }}>{delayAction} →</span>}
                      </div>
                    )}
                    {/* Expanded view */}
                    {expandedTrip === t.code && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(27,58,92,0.15)" }}>
                        {/* Flight-board-style status banner */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, background: TRIP_STATUS_META[flightStatus]?.bg || "rgba(27,58,92,0.045)", border: `1px solid ${TRIP_STATUS_META[flightStatus]?.border || "rgba(27,58,92,0.084)"}`, marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 9, letterSpacing: 1.8, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Trip status</div>
                            <div style={{ fontSize: 11, color: TRIP_STATUS_META[flightStatus]?.color || "#4A6B8A" }}>{TRIP_STATUS_META[flightStatus]?.description}</div>
                          </div>
                          <TripStatusPill status={flightStatus} variant="solari" size="md" />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                          {[["Duration", `${t.days} days`], ["Budget", `$${t.budget?.toLocaleString()}`], ["Ref", t.code]].map(([l,v]) => (
                            <div key={l} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 8, padding: "8px 10px" }}>
                              <div style={{ fontSize: 10, color: textDim, marginBottom: 2 }}>{l}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#4A8CC4" }}>{v}</div>
                            </div>
                          ))}
                        </div>
                        {/* Mark as DELAYED — user-flagged status override */}
                        {flightStatus !== "ARRIVED" && flightStatus !== "DEPARTED" && (
                          <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: "rgba(255,166,109,0.04)", border: "1px solid rgba(255,166,109,0.18)" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 800, color: "#ffa66d", marginBottom: 2 }}>{t.userMarkedDelayed ? "Marked as delayed" : "Flag a problem"}</div>
                                <div style={{ fontSize: 11, color: textMuted }}>{t.userMarkedDelayed ? "We'll show this trip with a DELAYED status until you clear it." : "Use this if something's preventing the trip from going as planned."}</div>
                              </div>
                              <button onClick={() => {
                                const target = pastTrips.find(pt => pt.code === t.code);
                                const next = !target?.userMarkedDelayed;
                                if (tripsApi && target?.id != null) {
                                  tripsApi.updateTrip(target.id, {
                                    userMarkedDelayed: next,
                                    delayReason: next ? "Flagged by you" : null,
                                  });
                                } else {
                                  setPastTrips(prev => prev.map(pt => pt.code === t.code ? { ...pt, userMarkedDelayed: next, delayReason: next ? "Flagged by you" : null } : pt));
                                }
                              }} style={{ padding: "7px 14px", borderRadius: 10, border: t.userMarkedDelayed ? "1px solid rgba(255,166,109,0.5)" : "1px solid rgba(255,166,109,0.3)", background: t.userMarkedDelayed ? "rgba(255,166,109,0.18)" : "transparent", color: "#ffa66d", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                {t.userMarkedDelayed ? "Clear delay" : "Mark as delayed"}
                              </button>
                            </div>
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => setCancellingTrip(t)} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1px solid rgba(255,107,107,0.3)", background: "rgba(255,107,107,0.06)", color: coral, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel Trip</button>
                        </div>
                      </div>
                    )}
                    <button onClick={() => setExpandedTrip(expandedTrip === t.code ? null : t.code)}
                      style={{ marginTop: 10, width: "100%", padding: "8px", borderRadius: 8, border: "1px solid rgba(27,58,92,0.2)", background: "rgba(27,58,92,0.04)", color: teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      {expandedTrip === t.code ? "Hide Details ▲" : "View Trip Details ▼"}
                    </button>
                  </div>
                  );
                })}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, marginBottom: 4 }}>
                  <Label color="#4A8CC4">Past Trips</Label>
                  <button onClick={() => {
                    if (showAddTrip) {
                      // Closing form — also clear edit state and reset form
                      setEditingTripCode(null);
                      setAddTripForm({ dest: "", emoji: "🌍", dateFrom: "", dateTo: "", daysGone: "", liked: "", disliked: "", favoriteSpot: "", budget: "", travelers: "1", budgetMode: "perPerson" });
                    }
                    setShowAddTrip(s => !s);
                  }}
                    style={{ padding: "5px 12px", borderRadius: 10, border: "1px solid rgba(74,140,196,0.35)", background: "rgba(74,140,196,0.07)", color: goldText, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    {showAddTrip ? (editingTripCode ? "✕ Cancel edit" : "✕ Cancel") : "+ Add a trip"}
                  </button>
                </div>

                {/* Add trip form */}
                {showAddTrip && (
                  <div style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 14, padding: "16px", marginBottom: 14, animation: "fadeIn 0.2s ease" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: goldText, marginBottom: 12 }}>{editingTripCode ? "✏️ Edit trip details" : "📍 Add a past trip"}</div>
                    {/* Discoverability hint: surface the voice-dictation feature
                        that lives inside the journal entry editor. Many users
                        won't know it's there — this teases it so they look for
                        it after saving their trip. */}
                    <div style={{ background: "rgba(232,154,60,0.07)", border: "1px solid rgba(232,154,60,0.25)", borderRadius: 10, padding: "8px 12px", marginBottom: 14, fontSize: 11, color: "#1B3A5C", lineHeight: 1.5, display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>🎤</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        Once saved, you'll be able to <strong style={{ color: "#1B3A5C" }}>dictate your trip memories</strong> with voice-to-text in the journal — perfect for capturing details mid-trip or after returning.
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>DESTINATION</div>
                        <input value={addTripForm.dest} onChange={e => setAddTripForm(f => ({...f, dest: e.target.value}))} placeholder="e.g. Paris, France"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>DAYS TRAVELED</div>
                        <input value={addTripForm.daysGone} onChange={e => setAddTripForm(f => ({...f, daysGone: e.target.value}))} placeholder="e.g. 7"
                          type="number" min="1" max="90"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>FROM DATE</div>
                        <input value={addTripForm.dateFrom} onChange={e => setAddTripForm(f => {
                            const newFrom = e.target.value;
                            // Seed TO with FROM when (a) TO is empty, or (b) TO is now
                            // before FROM. Both cases would otherwise leave TO blank,
                            // which makes the native picker open to today on next click
                            // — painful for past trips from years ago. Anchoring TO to
                            // FROM's date makes the picker open in the right month.
                            const needsSeed = !f.dateTo || (newFrom && f.dateTo < newFrom);
                            return {
                              ...f,
                              dateFrom: newFrom,
                              dateTo: needsSeed && newFrom ? newFrom : f.dateTo,
                            };
                          })} type="date"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>TO DATE</div>
                        <input value={addTripForm.dateTo}
                          onChange={e => {
                            // Snap forward: if user types a TO date before FROM,
                            // snap to FROM (rather than rejecting silently — HTML
                            // date inputs let typed values bypass picker min UI).
                            const v = e.target.value;
                            if (addTripForm.dateFrom && v && v < addTripForm.dateFrom) {
                              setAddTripForm(f => ({...f, dateTo: addTripForm.dateFrom}));
                            } else {
                              setAddTripForm(f => ({...f, dateTo: v}));
                            }
                          }}
                          type="date"
                          min={addTripForm.dateFrom || undefined}
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>TRAVELERS</div>
                        <input value={addTripForm.travelers}
                          onChange={e => setAddTripForm(f => ({...f, travelers: e.target.value.replace(/[^0-9]/g,"") || ""}))}
                          placeholder="e.g. 2"
                          type="number" min="1" max="50" inputMode="numeric"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>BUDGET MODE</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {[
                            { id: "perPerson", label: "Per person" },
                            { id: "total",     label: "Total trip" },
                          ].map(opt => {
                            const on = addTripForm.budgetMode === opt.id;
                            return (
                              <button key={opt.id} type="button"
                                onClick={() => setAddTripForm(f => ({...f, budgetMode: opt.id}))}
                                style={{
                                  flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                                  fontSize: 12, fontWeight: on ? 700 : 500,
                                  border: `1px solid ${on ? "rgba(27,58,92,0.45)" : borderColor}`,
                                  background: on ? "rgba(27,58,92,0.10)" : (darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"),
                                  color: on ? "#1B3A5C" : textMuted,
                                  transition: "all 0.15s",
                                }}>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>
                        APPROXIMATE BUDGET ({addTripForm.budgetMode === "total" ? "total trip" : "per person"})
                      </div>
                      <input value={addTripForm.budget} onChange={e => setAddTripForm(f => ({...f, budget: e.target.value.replace(/[^0-9]/g,"")}))} placeholder={addTripForm.budgetMode === "total" ? "e.g. 4500" : "e.g. 1500"}
                        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                      {(() => {
                        // Live preview of the OTHER side of the toggle so the
                        // user can sanity-check their entry. E.g. entering $1500
                        // per person for 3 travelers shows "≈ $4,500 trip total"
                        // beneath the input.
                        const raw = parseInt(addTripForm.budget || "0", 10);
                        const tCount = parseInt(addTripForm.travelers || "0", 10);
                        if (!raw || !tCount || tCount < 1) return null;
                        const other = addTripForm.budgetMode === "total"
                          ? Math.round(raw / tCount)
                          : raw * tCount;
                        const otherLabel = addTripForm.budgetMode === "total"
                          ? `≈ $${other.toLocaleString()} per person`
                          : `≈ $${other.toLocaleString()} total trip`;
                        return (
                          <div style={{ fontSize: 10, color: textDim, marginTop: 4, fontStyle: "italic" }}>
                            {otherLabel}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>✅ WHAT DID YOU LOVE?</div>
                      <textarea value={addTripForm.liked} onChange={e => setAddTripForm(f => ({...f, liked: e.target.value}))} rows={2} placeholder="The food markets, the people, sunset from the hilltop…"
                        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>❌ WHAT DIDN'T WORK?</div>
                      <textarea value={addTripForm.disliked} onChange={e => setAddTripForm(f => ({...f, disliked: e.target.value}))} rows={2} placeholder="Too crowded in August, hotel was disappointing…"
                        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>⭐ FAVORITE MOMENT OR RESTAURANT</div>
                      <input value={addTripForm.favoriteSpot} onChange={e => setAddTripForm(f => ({...f, favoriteSpot: e.target.value}))} placeholder="Dinner at Septime, that hidden bar in Montmartre…"
                        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <button onClick={() => {
                      if (!addTripForm.dest) return;
                      // Try to match destination to a known DESTINATION for richer data
                      const destNameTrim = addTripForm.dest.trim();
                      const matchedDest = DESTINATIONS.find(d =>
                        d.name.toLowerCase() === destNameTrim.toLowerCase() ||
                        destNameTrim.toLowerCase().includes(d.name.toLowerCase())
                      );
                      const days = addTripForm.daysGone ? parseInt(addTripForm.daysGone, 10) : (addTripForm.dateFrom && addTripForm.dateTo ? Math.round((new Date(addTripForm.dateTo) - new Date(addTripForm.dateFrom)) / 86400000) : null);
                      const dateLabel = addTripForm.dateFrom && addTripForm.dateTo
                        ? `${new Date(addTripForm.dateFrom).toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${new Date(addTripForm.dateTo).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`
                        : "";
                      // Normalize budget to TOTAL trip cost regardless of which
                      // mode the user entered. Per-person × travelers = total.
                      // Storing total canonically means downstream displays and
                      // aggregations don't have to know which mode was selected.
                      const rawBudget = parseInt(addTripForm.budget || "0", 10) || null;
                      const travelersCount = parseInt(addTripForm.travelers || "1", 10) || 1;
                      const totalBudget = rawBudget
                        ? (addTripForm.budgetMode === "total" ? rawBudget : rawBudget * travelersCount)
                        : null;

                      // EDIT MODE: update existing trip via tripsApi (preferred)
                      // or local fallback. Preserves code, status, source, id —
                      // only updates the user-editable metadata fields.
                      if (editingTripCode) {
                        const target = pastTrips.find(pt => pt.code === editingTripCode);
                        const updates = {
                          dest: matchedDest?.name || destNameTrim,
                          emoji: matchedDest?.emoji || resolveFlagEmoji(destNameTrim) || "🌍",
                          dateFrom: addTripForm.dateFrom || null,
                          dateTo: addTripForm.dateTo || null,
                          days,
                          date: dateLabel,
                          budget: totalBudget,
                          travelers: travelersCount,
                          liked: addTripForm.liked || null,
                          disliked: addTripForm.disliked || null,
                          favSpot: addTripForm.favoriteSpot || null,
                        };
                        if (tripsApi && target?.id != null) {
                          tripsApi.updateTrip(target.id, updates);
                        } else {
                          setPastTrips(prev => prev.map(pt => pt.code === editingTripCode ? { ...pt, ...updates } : pt));
                        }
                        setEditingTripCode(null);
                      } else {
                        // CREATE MODE: new trip with a fresh code
                        const code = `USR-${Math.random().toString(36).slice(2,8).toUpperCase()}`;
                        // Single source of truth: tripsApi.createTrip adds to pastTrips
                        // and syncs to backend. Legacy userTrips state removed.
                        const sharedTrip = {
                          dest: matchedDest?.name || destNameTrim,
                          // Resolve flag: matched destination → its own emoji;
                          // else try the country/city lookup; else fall back to 🌍
                          emoji: matchedDest?.emoji || resolveFlagEmoji(destNameTrim) || "🌍",
                          code,
                          dateFrom: addTripForm.dateFrom || null,
                          dateTo: addTripForm.dateTo || null,
                          status: "completed",
                          source: "user",
                          days,
                          date: dateLabel,
                          budget: totalBudget,
                          travelers: travelersCount,
                          liked: addTripForm.liked || null,
                          disliked: addTripForm.disliked || null,
                          favSpot: addTripForm.favoriteSpot || null,
                          external: true,
                        };
                        if (tripsApi) tripsApi.createTrip(sharedTrip);
                        else setPastTrips(p => [...p, sharedTrip]);
                      }
                      setAddTripForm({ dest: "", emoji: "🌍", dateFrom: "", dateTo: "", daysGone: "", liked: "", disliked: "", favoriteSpot: "", budget: "", travelers: "1", budgetMode: "perPerson" });
                      setShowAddTrip(false);
                    }} style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {editingTripCode ? "Save Changes →" : "Save Trip →"}
                    </button>
                  </div>
                )}

                {/* User-added trips now render via the main SAMPLE_TRIPS map below
                   (derived from pastTrips). Removed the duplicate userTrips render
                   block that was causing every added trip to appear twice. */}

                {SAMPLE_TRIPS.filter(t => (t.status === "completed" || t.status === "past") && isVisibleTrip(t)).map(t => {
                  const isDemo = t.code && t.code.startsWith("TMA-DEMO-");
                  const isBooking = t.source === "booking";
                  const canDelete = !isDemo && !isBooking;
                  return (
                  <div key={t.code} style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(27,58,92,0.084)", borderRadius: 12, padding: "14px", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 28 }}>{(t.emoji && t.emoji !== "🌍") ? t.emoji : (resolveFlagEmoji(t.dest) || "🌍")}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: textMuted }}>{t.dest}</div>
                        <div style={{ fontSize: 12, color: textDim }}>{t.date}</div>
                        <div style={{ fontSize: 11, color: textDim, marginTop: 2 }}>Ref: {t.code}</div>
                      </div>
                      <TripStatusPill status="ARRIVED" variant="solari" />
                      {/* Delete button — inline with the status pill rather than
                         absolute-positioned. Avoids overlap with ARRIVED on both
                         desktop and mobile. Only shown for user-added trips. */}
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTripDeleteConfirm({
                              trip: t,
                              hasJournal: !!(journalEntries[t.code] || (journalRatings[t.code] && Object.values(journalRatings[t.code]).some(Boolean))),
                            });
                          }}
                          aria-label={`Delete trip to ${t.dest}`}
                          title="Delete trip"
                          style={{
                            flexShrink: 0,
                            width: 28, height: 28, borderRadius: 14,
                            border: "1px solid rgba(27,58,92,0.096)",
                            background: "rgba(27,58,92,0.045)",
                            color: textDim, fontSize: 14, lineHeight: 1,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", fontFamily: "inherit",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,107,0.12)"; e.currentTarget.style.borderColor = "rgba(255,107,107,0.4)"; e.currentTarget.style.color = "#FF6B6B"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.045)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.096)"; e.currentTarget.style.color = "#4A6B8A"; }}>
                          ×
                        </button>
                      )}
                    </div>

                    {/* Expanded past trip */}
                    {expandedTrip === t.code && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(27,58,92,0.084)" }}>
                        {/* Edit Trip Details button — only for user-added trips
                            (booking-source trips have data from the booking
                            flow and shouldn't be editable here). Opens the
                            Add Trip form in edit mode, pre-filled with this
                            trip's current values. */}
                        {canDelete && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                            <button onClick={() => {
                              setAddTripForm({
                                dest: t.dest || "",
                                emoji: t.emoji || "🌍",
                                dateFrom: t.dateFrom || "",
                                dateTo: t.dateTo || "",
                                daysGone: t.days != null ? String(t.days) : "",
                                liked: t.liked || "",
                                disliked: t.disliked || "",
                                favoriteSpot: t.favSpot || "",
                                budget: t.budget != null ? String(t.budget) : "",
                                travelers: t.travelers != null ? String(t.travelers) : "1",
                                budgetMode: "total", // stored values are canonical totals
                              });
                              setEditingTripCode(t.code);
                              setShowAddTrip(true);
                              setExpandedTrip(null); // collapse the card so user sees the form
                              // Scroll to top of trips area after state commits
                              setTimeout(() => {
                                const el = document.getElementById("jga-trips-section-top");
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                else window.scrollTo({ top: 0, behavior: "smooth" });
                              }, 80);
                            }}
                              style={{ padding: "7px 14px", borderRadius: 10, border: "1px solid rgba(74,140,196,0.4)", background: "rgba(74,140,196,0.08)", color: goldText, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                              ✏️ Edit trip details
                            </button>
                          </div>
                        )}
                        {/* Itinerary */}
                        {t.itinerary && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 10 }}>Your Itinerary</div>
                            {t.itinerary.days.map(d => (
                              <div key={d.day} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "12px", marginBottom: 8, border: "1px solid rgba(27,58,92,0.072)" }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: goldText, marginBottom: 6 }}>Day {d.day} — {d.title}</div>
                                {[["🌅 Morning", d.morning], ["☀️ Afternoon", d.afternoon], ["🌙 Evening", d.evening]].map(([label, val]) => (
                                  <div key={label} style={{ fontSize: 12, color: textMuted, marginBottom: 4 }}><span style={{ color: "#4A8CC4" }}>{label}:</span> {val}</div>
                                ))}
                                {d.tip && <div style={{ fontSize: 11, color: teal, marginTop: 6, fontStyle: "italic" }}>💡 {d.tip}</div>}
                              </div>
                            ))}
                            <div style={{ background: "rgba(74,140,196,0.05)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 10, padding: "12px", marginBottom: 8 }}>
                              <div style={{ fontSize: 11, color: "#4A8CC4", marginBottom: 8, letterSpacing: 1 }}>BUDGET BREAKDOWN</div>
                              {Object.entries(t.itinerary.budgetBreakdown).map(([k,v]) => (
                                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                                  <span style={{ color: textMuted, textTransform: "capitalize" }}>{k}</span>
                                  <span style={{ color: goldText, fontWeight: 700 }}>{v}</span>
                                </div>
                              ))}
                            </div>
                            {t.itinerary.packingTip && <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic" }}>🎒 {t.itinerary.packingTip}</div>}
                          </div>
                        )}

                        {/* ── Journal ── */}
                        {renderJournalSection(t)}

                        {/* (Removed Jun 11 2026: redundant "Review section"
                            that duplicated the Journal section above. It
                            rendered a second "Leave a Review" button + saved-
                            rating preview that both routed to the same journal
                            editor as the Journal section right above. Cas
                            flagged this as a confusing phantom CTA. Journal
                            section is now the single source of truth for
                            ratings + entries within the expanded trip view.) */}
                      </div>
                    )}

                    <button onClick={() => {
                      const willExpand = expandedTrip !== t.code;
                      setExpandedTrip(willExpand ? t.code : null);
                      // Auto-open journal editor on expand IF this trip has no
                      // existing entry. Saves the user a second tap — they
                      // expand a trip with the explicit intent to "Review,"
                      // and the editor is right there ready. Skip if entry
                      // already exists (don't blow away their saved state)
                      // or if already in edit mode for this trip.
                      if (willExpand && !journalEntries[t.code] && journalEditing !== t.code) {
                        setJournalEditing(t.code);
                        setJournalDraft("");
                        setJournalRatingDraft(p => ({ ...p, [t.code]: {} }));
                        setJournalMetaDraft(p => ({ ...p, [t.code]: {} }));
                      }
                    }}
                      style={{ marginTop: 10, width: "100%", padding: "8px", borderRadius: 8, border: "1px solid rgba(27,58,92,0.096)", background: "rgba(27,58,92,0.030)", color: textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      {expandedTrip === t.code ? "Hide Details ▲" : "View Itinerary & Review ▼"}
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── My Itineraries ── */}
        {tab === "itineraries" && (
          <div>
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: textPrimary }}>My Itineraries</h3>
              <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                AI-crafted day-by-day plans for destinations you've saved. Generate one from any destination in <button onClick={() => { setMode(null); setAccountDefaultTab("trips"); setTimeout(() => { reset(); setMode("explore"); }, 0); }} style={{ background: "none", border: "none", padding: 0, color: goldText, fontWeight: 700, fontSize: "inherit", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer" }}>Take Me Anywhere</button> and it lives here for whenever you're ready.
              </p>
            </div>
            {(() => {
              const allItineraries = [...(savedItineraries || []), ...SAMPLE_ITINERARIES.filter(s => !(savedItineraries || []).some(u => u.dest === s.dest))];
              return allItineraries.length === 0 ? (
                <div style={{ textAlign: "center", padding: "28px 20px", background: "rgba(74,140,196,0.04)", border: "1px dashed rgba(74,140,196,0.25)", borderRadius: 12 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🗺️</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#1B3A5C", marginBottom: 6 }}>
                    No itineraries yet
                  </div>
                  <div style={{ fontSize: 12.5, color: textMuted, lineHeight: 1.55, marginBottom: 16 }}>
                    {travelerType && getIdentityCopy(travelerType).savedEmpty
                      ? getIdentityCopy(travelerType).savedEmpty
                      : "Generate a day-by-day plan for any destination — they'll save here for whenever you're ready."}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => { setMode(null); setTimeout(() => { reset(); setMode("explore"); }, 0); }}
                      style={{
                        padding: "9px 16px", borderRadius: 10,
                        border: "1px solid rgba(196,160,69,0.4)",
                        background: "rgba(196,160,69,0.08)",
                        color: "#c4a045", fontSize: 12, fontWeight: 800,
                        cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      Take Me Anywhere →
                    </button>
                    <button
                      onClick={() => { setMode(null); setTimeout(() => { reset(); setMode("blind"); }, 0); }}
                      style={{
                        padding: "9px 16px", borderRadius: 10,
                        border: "1px solid rgba(255,107,107,0.4)",
                        background: "rgba(255,107,107,0.08)",
                        color: "#ff6b6b", fontSize: 12, fontWeight: 800,
                        cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      Surprise Me →
                    </button>
                  </div>
                </div>
              ) : allItineraries.map(it => {
                const isExpanded = expandedSavedItin === it.dest;
                const itinData = it.data || {};
                const days = Array.isArray(itinData.days) ? itinData.days : [];
                const budget = itinData.budgetBreakdown || itinData.budget || null;
                const packingTip = itinData.packingTip || itinData.packing || null;
                return (
                  <div key={it.dest} style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(74,140,196,0.18)", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                    {/* Header row — clickable to expand. Whole row is the toggle */}
                    <button
                      onClick={() => {
                        // Diagnostic: log the full saved entry on expand so
                        // we can see exactly what's in `data` if rendering fails.
                        console.log("[JGA Saved Itinerary] Expand clicked (Itineraries tab):", {
                          dest: it.dest,
                          hasData: !!it.data,
                          dataType: typeof it.data,
                          daysCount: Array.isArray(it.data?.days) ? it.data.days.length : "(no days array)",
                          rawData: it.data,
                          fullEntry: it,
                        });
                        setExpandedSavedItin(isExpanded ? null : it.dest);
                      }}
                      aria-expanded={isExpanded}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 12,
                        background: "transparent", border: "none", padding: 0, cursor: "pointer",
                        fontFamily: "inherit", color: "inherit", textAlign: "left",
                      }}
                    >
                      <div style={{ fontSize: 28 }}>{it.emoji}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{it.dest}</div>
                        <div style={{ fontSize: 12, color: textMuted }}>
                          {it.days}-day itinerary
                          {/* Surface the date range so users can tell August Cartagena from September Cartagena.
                              Falls back silently for older entries that pre-date the versioning fix. */}
                          {(() => {
                            if (!it.departDate) return null;
                            try {
                              const s = new Date(it.departDate);
                              const e = it.returnDate ? new Date(it.returnDate) : null;
                              const sStr = s.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                              if (!e) return ` · ${sStr}`;
                              const eStr = (s.getMonth() === e.getMonth())
                                ? e.getDate()
                                : e.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                              return ` · ${sStr}–${eStr}`;
                            } catch { return null; }
                          })()}
                          {" · "}Saved {it.saved}
                        </div>
                      </div>
                      <span style={{ background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.3)", color: goldText, fontSize: 11, padding: "5px 10px", borderRadius: 8, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {isExpanded ? "Hide ▲" : "View ▼"}
                      </span>
                    </button>

                    {/* Expanded panel — full day-by-day + Book CTA */}
                    {isExpanded && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(27,58,92,0.084)" }}>
                        {days.length === 0 ? (
                          <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic", padding: "10px 0" }}>
                            This itinerary's day-by-day details aren't available offline. Try opening it after the next sync, or regenerate from the destination page.
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 11, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 10 }}>Your Itinerary</div>
                            {days.map(d => (
                              <div key={d.day} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "12px", marginBottom: 8, border: "1px solid rgba(27,58,92,0.072)" }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: goldText, marginBottom: 6 }}>Day {d.day}{d.title ? ` — ${d.title}` : ""}</div>
                                {[["🌅 Morning", d.morning], ["☀️ Afternoon", d.afternoon], ["🌙 Evening", d.evening]].map(([label, val]) => (
                                  val ? (
                                    <div key={label} style={{ fontSize: 12, color: textMuted, marginBottom: 4 }}><span style={{ color: "#4A8CC4" }}>{label}:</span> {val}</div>
                                  ) : null
                                ))}
                                {d.tip && <div style={{ fontSize: 11, color: teal, marginTop: 6, fontStyle: "italic" }}>💡 {d.tip}</div>}
                              </div>
                            ))}
                            {budget && typeof budget === "object" && Object.keys(budget).length > 0 && (
                              <div style={{ background: "rgba(74,140,196,0.05)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 10, padding: "12px", marginBottom: 8 }}>
                                <div style={{ fontSize: 11, color: "#4A8CC4", marginBottom: 8, letterSpacing: 1 }}>BUDGET BREAKDOWN</div>
                                {Object.entries(budget).map(([k, v]) => (
                                  <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                                    <span style={{ color: textMuted, textTransform: "capitalize" }}>{k}</span>
                                    <span style={{ color: goldText, fontWeight: 700 }}>{typeof v === "object" ? JSON.stringify(v) : v}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {packingTip && <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic", marginBottom: 14 }}>🎒 {packingTip}</div>}
                          </>
                        )}

                        {/* Book CTA — fires existing booking flow with this destination */}
                        {onBookDest && (
                          <button
                            onClick={() => onBookDest(it.dest, undefined, { days: it.days, departDate: it.departDate, returnDate: it.returnDate })}
                            style={{
                              width: "100%", padding: "12px", borderRadius: 12, border: "none",
                              background: "linear-gradient(135deg, #4A8CC4, #4A8CC4)",
                              color: "#FAFAF5", fontSize: 14, fontWeight: 900,
                              cursor: "pointer", fontFamily: "inherit", marginTop: 6,
                              boxShadow: "0 4px 14px rgba(74,140,196,0.35)",
                            }}
                          >
                            Book this trip →
                          </button>
                        )}

                        {/* Delete — only on real saved itineraries (id present).
                             Sample itineraries are demo placeholders, can't be
                             deleted (they'd just re-render). Inline 'Are you
                             sure?' replaces the Delete button when activated. */}
                        {it.id && onDeleteItinerary && (
                          itinDeleteConfirm === it.dest ? (
                            <div style={{
                              marginTop: 10, padding: "10px 12px", borderRadius: 10,
                              background: "rgba(27,58,92,0.06)",
                              border: "1px solid rgba(27,58,92,0.20)",
                              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                            }}>
                              <span style={{ fontSize: 12, color: textPrimary, flex: 1, minWidth: 0 }}>
                                Delete this itinerary?
                              </span>
                              <button
                                onClick={async () => {
                                  if (itinDeleting) return;
                                  setItinDeleting(true);
                                  const result = await onDeleteItinerary({ id: it.id, destName: it.dest });
                                  setItinDeleting(false);
                                  setItinDeleteConfirm(null);
                                  if (result?.ok) {
                                    // Collapse the now-deleted card's expansion
                                    setExpandedSavedItin(null);
                                  } else {
                                    // Backend failed; brief toast via console for now
                                    console.warn("[JGA Itinerary Delete] Failed — entry restored locally");
                                  }
                                }}
                                disabled={itinDeleting}
                                style={{
                                  padding: "6px 12px", borderRadius: 8, border: "none",
                                  background: "#1B3A5C", color: "#fff",
                                  fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                                  cursor: itinDeleting ? "not-allowed" : "pointer",
                                  opacity: itinDeleting ? 0.6 : 1,
                                }}>
                                {itinDeleting ? "Deleting..." : "Delete"}
                              </button>
                              <button
                                onClick={() => setItinDeleteConfirm(null)}
                                disabled={itinDeleting}
                                style={{
                                  padding: "6px 12px", borderRadius: 8,
                                  border: "1px solid rgba(0,0,0,0.15)",
                                  background: "transparent", color: textMuted,
                                  fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                                  cursor: itinDeleting ? "not-allowed" : "pointer",
                                }}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setItinDeleteConfirm(it.dest)}
                              style={{
                                width: "100%", padding: "10px", borderRadius: 10,
                                border: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(0,0,0,0.10)"}`,
                                background: "transparent",
                                color: textDim, fontSize: 12, fontWeight: 600,
                                cursor: "pointer", fontFamily: "inherit", marginTop: 8,
                              }}>
                              Delete itinerary
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ── Travel Mood Board ── */}
        {tab === "moodboard" && (() => {
          const identity = travelerType && TRAVEL_IDENTITIES[travelerType];
          const allItins = [...(savedItineraries || [])];
          const wishDests = starredAlerts.map(name => DESTINATIONS.find(d => d.name === name)).filter(Boolean);

          // Travel Aura is now computed inside the AuraCard block below
          // (see computeAuraForVibes + forming engine). Identity stays the
          // moat; Aura is a soft monthly read here, not a competing identity.

          // Derive mood tags from everything
          const moodTags = [...new Set([
            ...(identity?.vibes || []),
            ...allItins.flatMap(i => DESTINATIONS.find(d => d.name === i.dest)?.tags || []),
            ...wishDests.flatMap(d => d.tags),
          ])].slice(0, 8);

          const moodColors = { beach: "#7A8A9C", romance: "#4A8CC4", wellness: "#A89AC4", luxury: "#9C7B65", adventure: "#A85A3C", culture: "#A8853B", food: "#C25E47", nature: "#7A8467", art: "#7A6A85", nightlife: "#5C4A78" };
          const moodEmojis = { beach: "🏖️", romance: "💑", wellness: "🧘", luxury: "✨", adventure: "🧗", culture: "🏛️", food: "🍜", nature: "🌿", art: "🎨", nightlife: "🎶" };

          return (
            <div>
              <div style={{ marginBottom: 18 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: textPrimary }}>Travel Mood Board</h3>
                <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                  Your travel personality, saved dreams, and AI itineraries — all in one place.
                </p>
              </div>

              {/* Identity hero — constellation chart treatment, matches dashboard.
                  Now clickable (Jun 11 2026) to open the full Travel Identity
                  modal — mirrors homepage behavior so Mood Board doesn't feel
                  like a dead end. */}
              {identity && (() => {
                const skyEntry = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`View your Travel Identity — The ${travelerType}`}
                    onClick={() => onOpenIdentityCard()}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenIdentityCard(); } }}
                    style={{
                    borderRadius: 16,
                    overflow: "hidden",
                    border: `1px solid ${skyEntry.accent}33`,
                    background: getIdentitySky(travelerType),
                    boxShadow: `0 4px 24px rgba(20,18,24,0.15), 0 1px 0 ${skyEntry.accent}22 inset`,
                    padding: "18px 20px",
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    cursor: "pointer",
                  }}>
                    {/* Mini constellation chart */}
                    <div style={{ flexShrink: 0, width: 64, height: 45, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg viewBox="0 0 200 140" width="64" height="45" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                        {(() => {
                          const chart = generateConstellation(identity);
                          return (
                            <>
                              {chart.lines.map((l, i) => (
                                <line key={`l-${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                                  stroke={skyEntry.accent} strokeWidth="2" opacity="0.7" strokeLinecap="round" />
                              ))}
                              {Object.entries(chart.stars).map(([attr, s]) => (
                                <g key={attr}>
                                  <circle cx={s.x} cy={s.y} r={s.r * 2.5} fill="#FAFAF5" opacity="0.18" />
                                  <circle cx={s.x} cy={s.y} r={s.r * 1.5} fill="#FAFAF5" />
                                </g>
                              ))}
                            </>
                          );
                        })()}
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>Travel Identity</div>
                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 22, fontWeight: 500, color: "#FAFAF5", lineHeight: 1.1, marginBottom: 4, letterSpacing: "-0.3px" }}>
                        <span>The {travelerType.split(" ")[0]}</span>{travelerType.split(" ").length > 1 && <span style={{ fontStyle: "italic", color: skyEntry.accent }}> {travelerType.split(" ").slice(1).join(" ")}</span>}
                      </div>
                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 11, color: "rgba(250,250,245,0.75)", lineHeight: 1.45, fontStyle: "italic" }}>"{identity.desc}"</div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Travel Aura — moved from home page (Jun 2026 rebrand handoff).
                 Forming engine builds percentage from the user's actual engagement;
                 archive snapshots lock at month boundaries. Window-seat palette per
                 spec — no warm rust or terracotta on the card chrome. Identity stays
                 the moat above; Aura is a soft monthly read here, not a parallel
                 identity competing on the highest-traffic surface. */}
              {(() => {
                // Aggregate engagement signals for the forming engine.
                const somedayStampCount = Object.values(stampsData || {}).filter(s => s && s.state === "someday").length;
                const dreamingPostcardCount = Object.values(postcardsData || {}).filter(p => p && p.state === "dreaming").length;
                const tripCount = (pastTrips || []).length;
                const entryCount = Object.values(journalEntries || {}).filter(e => e && String(e).trim().length > 0).length;
                const cur = currentYearMonth();
                const signup = getAuraSignupMonth();
                const isPreview = signup === cur;
                const searchCount = getMonthlySearchCount(cur);
                const allVibes = [...new Set([
                  ...(identity?.vibes || []),
                  ...((userMood?.vibes) || []),
                  ...wishDests.flatMap(d => d.tags || []),
                ])];
                const topVibes = allVibes.slice(0, 3);
                const auraReading = computeAuraForVibes(allVibes);
                const percent = computeAuraPercent({
                  hasIdentity: !!identity,
                  somedayStampCount, dreamingPostcardCount, tripCount, entryCount, searchCount,
                });
                // Archive snapshot for any previous months. Idempotent —
                // only writes months not already in the archive.
                const archive = maybeArchivePreviousMonths({
                  hasIdentity: !!identity,
                  somedayStampCount, dreamingPostcardCount, tripCount, entryCount, searchCount,
                  vibes: allVibes,
                });
                // For the preview footer — "Your first full Aura arrives [next month] 1"
                const nextMonth = (() => {
                  const d = new Date();
                  d.setMonth(d.getMonth() + 1, 1);
                  return d.toLocaleDateString("en-US", { month: "long" });
                })();
                // Build archive list — sorted newest first, exclude any phantom
                // entries that lack an auraLabel (e.g. user had no vibes at month end).
                const archiveEntries = Object.entries(archive)
                  .filter(([, v]) => v && v.auraLabel)
                  .sort((a, b) => b[0].localeCompare(a[0]));
                return (
                  <>
                    <AuraCard
                      aura={auraReading}
                      percent={percent}
                      vibes={topVibes}
                      monthLabel={isPreview ? null : yearMonthLabel(cur)}
                      isPreview={isPreview}
                      nextMonthLabel={isPreview ? nextMonth : null}
                      stats={{
                        hasIdentity: !!identity,
                        somedayStampCount, dreamingPostcardCount, tripCount, entryCount, searchCount,
                      }}
                    />
                    {archiveEntries.length > 0 && (
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 10, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontWeight: 700 }}>
                          Previous months
                        </div>
                        <div style={{ display: "grid", gap: 10 }}>
                          {archiveEntries.map(([ym, snap]) => (
                            <AuraCard
                              key={ym}
                              aura={{ label: snap.auraLabel, emoji: snap.auraEmoji || "✦", desc: snap.auraDesc || "" }}
                              percent={snap.percent || 0}
                              vibes={snap.vibes || []}
                              monthLabel={yearMonthLabel(ym)}
                              isArchived
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Mood tags cloud */}
              {moodTags.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 10, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Your Travel Mood</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {moodTags.map(tag => (
                      <span key={tag} style={{ fontSize: 13, padding: "6px 14px", borderRadius: 30, background: `${moodColors[tag] || teal}18`, border: `1.5px solid ${moodColors[tag] || teal}40`, color: moodColors[tag] || teal, fontWeight: 700 }}>
                        {moodEmojis[tag] || "✦"} {tag.charAt(0).toUpperCase() + tag.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Wish list destinations */}
              {wishDests.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 10, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Dream Destinations</div>
                  {/* Pricing context — airport-specific flight estimates pull from
                     the user's saved departureAirport (Profile → Travel
                     Preferences). When set, each tile shows ~flight cost from
                     that airport + best time to visit. Without one, falls
                     back to a generic trip-cost band and surfaces a soft hint
                     to set it. */}
                  {!user?.departureAirport && (
                    <div style={{ fontSize: 10.5, color: textDim, marginBottom: 8, lineHeight: 1.5, fontStyle: "italic" }}>
                      Set a departure airport in Profile to see flight prices from your home base.
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8 }}>
                    {wishDests.slice(0, 6).map(d => {
                      const flightEst = user?.departureAirport
                        ? estimateFlightPrice(user.departureAirport, d.name, null)
                        : null;
                      return (
                        <div key={d.name} style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(250,250,245,0.6)", borderRadius: 12, padding: "12px", border: `1px solid ${borderColor}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}>{d.emoji}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                            <div style={{ fontSize: 10, color: textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.country}</div>
                            {/* Best time to visit — sourced from DESTINATIONS catalog */}
                            {d.bestTime && (
                              <div style={{ fontSize: 10, color: textDim, marginTop: 3, lineHeight: 1.3 }}>
                                <span style={{ fontWeight: 700, color: textMuted }}>Best:</span> {d.bestTime}
                              </div>
                            )}
                            {/* Airport-specific flight estimate (preferred) or fallback */}
                            {flightEst != null ? (
                              <div style={{ fontSize: 10, color: teal, marginTop: 4, fontWeight: 700 }}>
                                ✈ ~${flightEst.toLocaleString()} from {user.departureAirport}
                              </div>
                            ) : (
                              <div style={{ fontSize: 10, color: teal, marginTop: 4, fontWeight: 700 }}>
                                from ${d.minBudget.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Saved Itineraries removed from Mood Board (Jun 11 2026) —
                  redundant with the dedicated Itineraries tab. Mood Board
                  is now: identity + wish list + mood tags. */}

              {/* Empty state — reflects what Mood Board still shows after
                  removing the redundant Saved Itineraries block. */}
              {!identity && moodTags.length === 0 && wishDests.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 20px", color: textMuted }}>
                  <div style={{ fontSize: 48, marginBottom: 14 }}>🎨</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>Your mood board is empty</div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>Take the travel identity quiz, save destinations to your wish list, and generate itineraries — they'll all appear here.</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Send Someone Somewhere (Gift) ── */}
        {tab === "gift" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="10" width="18" height="11" rx="1.5" stroke={gold} strokeWidth="1.6" fill={`${gold}15`}/>
                  <path d="M2 10 L22 10" stroke={gold} strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M12 10 L12 21" stroke={gold} strokeWidth="1.6"/>
                  <path d="M12 10 C 10 10 7.5 9 7.5 6.5 C 7.5 4.5 9.5 4 12 7 C 14.5 4 16.5 4.5 16.5 6.5 C 16.5 9 14 10 12 10 Z" stroke={gold} strokeWidth="1.5" strokeLinejoin="round" fill={`${gold}25`}/>
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: goldText }}>Send Someone Somewhere</h3>
              <p style={{ fontSize: 13, color: textMuted, marginTop: 6, lineHeight: 1.5 }}>
                Gift a Surprise Me credit to anyone. They'll get an email with their mystery trip voucher — destination revealed when they're ready.
              </p>
            </div>

            {giftSent ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <svg width="60" height="60" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="22" cy="22" r="19" fill="rgba(109,189,109,0.12)" stroke="#6dbd6d" strokeWidth="2"/>
                  <circle cx="22" cy="22" r="13" fill="rgba(109,189,109,0.08)" stroke="#6dbd6d" strokeWidth="1.2" opacity="0.6"/>
                  <path d="M13 22 L19 28 L31 16" stroke="#6dbd6d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: darkMode ? "#6dbd6d" : "#2a7a4a", marginBottom: 8 }}>Gift Sent!</div>
                <div style={{ fontSize: 13, color: textMuted }}>Your gift credit has been emailed. They're in for a surprise!</div>
                <button onClick={() => { setGiftSent(false); setGiftEmail(""); setGiftNote(""); }} style={{ marginTop: 16, background: "none", border: "none", color: goldText, fontSize: 13, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>Send another gift</button>
              </div>
            ) : (
              <div>
                <Label>Recipient's Email</Label>
                <input value={giftEmail} onChange={e => setGiftEmail(e.target.value)} placeholder="friend@email.com" type="email"
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: textPrimary, outline: "none", boxSizing: "border-box", marginBottom: 14 }} />

                <Label>Gift Amount</Label>
                <div style={{ position: "relative", marginBottom: 14 }}>
                  <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 18, fontWeight: 700, color: giftAmount ? gold : "#4A6B8A", pointerEvents: "none" }}>$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={giftAmount}
                    onChange={e => setGiftAmount(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter amount"
                    style={{ width: "100%", padding: "12px 14px 12px 32px", borderRadius: 10, fontSize: 16, fontWeight: 700, fontFamily: "inherit", background: giftAmount ? "rgba(74,140,196,0.07)" : "rgba(27,58,92,0.060)", border: `1px solid ${giftAmount ? "rgba(74,140,196,0.45)" : "rgba(27,58,92,0.144)"}`, color: giftAmount ? gold : "#1B3A5C", outline: "none", boxSizing: "border-box" }}
                  />
                </div>

                <Label>Personal Message (optional)</Label>
                <textarea value={giftNote} onChange={e => setGiftNote(e.target.value)} placeholder="Happy birthday! Hope this takes you somewhere amazing..."
                  rows={3} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: textPrimary, outline: "none", boxSizing: "border-box", resize: "vertical", marginBottom: 20 }} />

                <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#4A8CC4" }}>
                  🎁 Recipient gets a ${giftAmount} Surprise Me credit · They choose their preferences · Destination revealed at reveal moment
                </div>

                <button onClick={() => { if (giftEmail.includes("@")) setGiftSent(true); }}
                  disabled={!giftEmail.includes("@")}
                  style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: !giftEmail.includes("@") ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg,${gold},${amber})`, color: !giftEmail.includes("@") ? "#4A6B8A" : "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: !giftEmail.includes("@") ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  Send ${giftAmount} Gift Credit →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Profile ── */}
        {tab === "profile" && (
          <div>

            {/* ── Profile content — Overview only.
                 Stamps and Postcards migrated to the Journal section per the
                 IA restructure. Profile is operational; Journal is emotional. ── */}
            {(() => { /* Overview unwrapped — no sub-tab gate needed */ return null; })()}
            <div>
            {/* ── Logged in as — surfaces the user's email so they know
                  which account they're editing (important for testing across
                  multiple accounts). Read-only, copyable. */}
            <div style={{
              background: "rgba(27,58,92,0.06)",
              border: "1px solid rgba(27,58,92,0.18)",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 10, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, marginBottom: 2 }}>Logged in as</div>
                <div style={{ fontSize: 13, color: textPrimary, fontWeight: 700, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {user.email || "(no email on file)"}
                </div>
              </div>
            </div>

            {/* ── Unsaved-changes banner — only visible when local edits
                  diverge from saved user.* values. Compares the editable
                  fields against the source-of-truth user object so the
                  banner accurately reflects 'dirty' state. */}
            {(() => {
              const dirty = (
                (firstName.trim() || "") !== (user.firstName || "") ||
                (lastName.trim() || "") !== (user.lastName || "") ||
                (dob || "") !== ((user.dob || "").slice(0, 10)) ||
                (address.trim() || "") !== (user.address || "") ||
                (ktn || "") !== (user.ktn || "") ||
                (departureAirport || "") !== (user.departureAirport || "") ||
                (avoidDestinations || "") !== (user.avoidDestinations || "") ||
                (avoidAirlines || "") !== (user.avoidAirlines || "") ||
                (avoidHotels || "") !== (user.avoidHotels || "") ||
                (passportNumber || "") !== (user.passportNumber || "") ||
                (passportCountry || "") !== (user.passportCountry || "") ||
                (passportExpiry || "") !== (user.passportExpiry || "") ||
                ((typeof maxTravelTimeHours === "number" ? maxTravelTimeHours : null)
                  !== (typeof user.maxTravelTimeHours === "number" ? user.maxTravelTimeHours : null))
              );
              if (!dirty) return null;
              return (
                <div style={{
                  background: `linear-gradient(135deg, ${gold}15, ${amber}10)`,
                  border: `1px solid ${gold}50`,
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}>
                  <div style={{ fontSize: 12, color: goldText, fontWeight: 700 }}>
                    You have unsaved changes.
                  </div>
                  <button onClick={saveProfile} disabled={profileSaving}
                    style={{
                      padding: "8px 14px", borderRadius: 8, border: "none",
                      background: `linear-gradient(135deg, ${gold}, ${amber})`,
                      color: "#FAFAF5", fontSize: 12, fontWeight: 900,
                      cursor: profileSaving ? "not-allowed" : "pointer", fontFamily: "inherit",
                      flexShrink: 0,
                    }}>
                    {profileSaving ? "Saving..." : "Save Now"}
                  </button>
                </div>
              );
            })()}

            {/* Personal info — editable. Tap any field to edit; tap Save
               Preferences at the bottom to persist. Age Range stays read-only
               because it's derived during onboarding via the quiz pills, not
               a free-form field. */}
            <Label color="#4A8CC4">Personal Information</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
              <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>First Name</div>
                <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Your first name"
                  style={{ width: "100%", background: "transparent", border: "none", padding: 0, color: "#4A8CC4", fontSize: 13, fontWeight: 700, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>Last Name</div>
                <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Your last name"
                  style={{ width: "100%", background: "transparent", border: "none", padding: 0, color: "#4A8CC4", fontSize: 13, fontWeight: 700, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>Date of Birth</div>
                <input type="date" value={dob} onChange={e => setDob(e.target.value)} max={new Date().toISOString().slice(0,10)}
                  style={{ width: "100%", background: "transparent", border: "none", padding: 0, color: "#4A8CC4", fontSize: 13, fontWeight: 700, fontFamily: "inherit", outline: "none", boxSizing: "border-box", colorScheme: darkMode ? "dark" : "light" }} />
              </div>
              <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>Age Range</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#4A8CC4" }}>{user.ageRange || "Not set"}</div>
              </div>
            </div>
            <div style={{ background: "rgba(27,58,92,0.045)", borderRadius: 10, padding: "10px 12px", marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>Home Address</div>
              <input value={address} onChange={e => setAddress(e.target.value)} placeholder="City, State or full address"
                style={{ width: "100%", background: "transparent", border: "none", padding: 0, color: "#4A8CC4", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Travel preferences */}
            {/* ── Travel Identity — fixed-length identity facts (KTN, passport, airport) ── */}
            <Label color="#4A8CC4">Travel Identity</Label>

            {/* KTN + Airport — 2-up grid (both are short codes that pair well).
               Passport stays full-width below since its meta line needs space. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              {/* KTN */}
              <div>
                <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>Known Traveller Number</div>
                {user.ktn && !ktnEditing ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", minHeight: 56 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: teal, letterSpacing: 1.5, fontFamily: "monospace" }}>
                        {"•".repeat(Math.max(0, user.ktn.length - 4))}{user.ktn.slice(-4)}
                      </div>
                      <div style={{ fontSize: 10, color: textDim, marginTop: 2 }}>TSA PreCheck</div>
                    </div>
                    <button onClick={() => setKtnEditing(true)} style={{ fontSize: 11, color: teal, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>Edit</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <input value={ktn} onChange={e => setKtn(e.target.value)} placeholder="KTN" style={{ ...fieldStyle, marginBottom: 0 }} />
                    {ktnEditing && <button onClick={() => setKtnEditing(false)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(27,58,92,0.3)", background: "transparent", color: teal, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start" }}>Done</button>}
                  </div>
                )}
              </div>

              {/* Departure Airport */}
              <div>
                <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>Departure Airport</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={departureAirport} onChange={e => setDepartureAirport(e.target.value.toUpperCase().slice(0, 3))} placeholder="e.g. JFK" maxLength={3} style={{ ...fieldStyle, flex: 1, marginBottom: 0, textTransform: "uppercase" }} />
                    <button onClick={() => {
                      console.log("[JGA Detect] Account modal - Detect tapped");
                      if (!navigator.geolocation) { alert("Geolocation isn't supported in this browser. Please type your airport code (e.g. JFK)."); return; }
                      navigator.geolocation.getCurrentPosition(pos => {
                        const nearest = getNearestAirport(pos.coords.latitude, pos.coords.longitude);
                        if (nearest) {
                          // CRITICAL: store ONLY the 3-letter IATA code. Backend validates
                          // departureAirport as 3-letter IATA and rejects the previous
                          // "CODE — Name, City" display string. The detected airport's
                          // friendly name now shows as helper text below the input.
                          setDepartureAirport(nearest.code);
                          try { localStorage.setItem("jga-airport", nearest.code); } catch(e) {}
                        }
                        else alert("No nearby airport found.");
                      }, err => {
                        if (err.code === 1) alert("Location access blocked. Type your airport code.");
                        else alert("Couldn't detect location.");
                      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
                    }} title="Detect nearest airport" style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.3)", background: "rgba(27,58,92,0.08)", color: teal, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
                      📍
                    </button>
                  </div>
                  {/* Friendly helper text — looks up the IATA code to show the
                     full airport name for context. Read-only display only;
                     the actual value stored is the 3-letter code. */}
                  {departureAirport && departureAirport.length === 3 && (() => {
                    const match = (typeof MAJOR_AIRPORTS !== "undefined" ? MAJOR_AIRPORTS : []).find(a => a.code === departureAirport.toUpperCase());
                    if (!match) return null;
                    return (
                      <div style={{ fontSize: 11, color: textDim, fontStyle: "italic" }}>
                        {match.name}{match.city ? `, ${match.city}` : ""}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* ── Max travel time tolerance ──
                  Caps the longest one-way flight the user would accept.
                  Used by SM to filter destinations whose realistic flight
                  exceeds this threshold. Independent of identity — user
                  chooses their own. null = no preference / unlimited. */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="#4A8CC4" strokeWidth="1.6" fill="none"/>
                  <path d="M12 7 L12 12 L15 14" stroke="#4A8CC4" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                </svg>
                <div style={{ fontSize: 11, color: textMuted }}>Max one-way flight time</div>
              </div>
              <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", marginBottom: 8, lineHeight: 1.4 }}>
                The longest single flight you'd accept. Used when matching destinations.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  { hours: 6,  label: "≤ 6h",  desc: "Domestic only" },
                  { hours: 12, label: "≤ 12h", desc: "Most international" },
                  { hours: 18, label: "≤ 18h", desc: "Long-haul OK" },
                  { hours: 24, label: "≤ 24h", desc: "Anywhere" },
                  { hours: null, label: "No preference", desc: "" },
                ].map(opt => {
                  const on = maxTravelTimeHours === opt.hours;
                  return (
                    <button key={String(opt.hours)} onClick={() => setMaxTravelTimeHours(opt.hours)}
                      style={{
                        padding: "8px 12px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
                        fontSize: 12, fontWeight: on ? 800 : 600,
                        border: `1px solid ${on ? `${teal}80` : darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.10)"}`,
                        background: on ? `${teal}15` : "transparent",
                        color: on ? teal : "#4A6B8A",
                        display: "inline-flex", alignItems: "baseline", gap: 6,
                      }}>
                      <span>{opt.label}</span>
                      {opt.desc && <span style={{ fontSize: 10, color: on ? teal : textDim, fontStyle: "italic" }}>· {opt.desc}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Passport — full width because the meta row (country + expiry chips) needs horizontal space */}
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="5" y="3" width="14" height="18" rx="2" stroke="#4A8CC4" strokeWidth="1.6" fill="rgba(74,140,196,0.08)"/>
                  <path d="M9 11 L15 11" stroke="#4A8CC4" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M9 14 L13 14" stroke="#4A8CC4" strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="12" cy="7.5" r="1.6" stroke="#4A8CC4" strokeWidth="1.4" fill="none"/>
                </svg>
                <div style={{ fontSize: 11, color: textMuted }}>Passport <span style={{ fontSize: 10, color: textDim, marginLeft: 4 }}>🔒 Encrypted on save</span></div>
              </div>
              {user.passportNumber && !passportEditing ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: goldText, letterSpacing: 2, fontFamily: "monospace" }}>
                      {"•".repeat(Math.max(0, user.passportNumber.length - 4))}{user.passportNumber.slice(-4)}
                    </div>
                    <div style={{ fontSize: 10, color: textDim, marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {passportCountry && <span>🌍 {passportCountry}</span>}
                      {passportExpiry && <span>📅 Expires {passportExpiry}</span>}
                    </div>
                  </div>
                  <button onClick={() => setPassportEditing(true)} style={{ fontSize: 11, color: goldText, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, flexShrink: 0 }}>Edit</button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    value={passportNumber}
                    onChange={e => setPassportNumber(e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 12))}
                    placeholder="Passport number"
                    autoComplete="off"
                    spellCheck="false"
                    style={{ ...fieldStyle, marginBottom: 0, letterSpacing: 2, fontFamily: "monospace", fontWeight: 700 }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={passportCountry}
                      onChange={e => setPassportCountry(e.target.value.toUpperCase().slice(0, 3))}
                      placeholder="Issuing country (e.g. USA)"
                      maxLength={3}
                      style={{ ...fieldStyle, flex: 1, marginBottom: 0, textTransform: "uppercase" }}
                    />
                    <input
                      type="month"
                      value={passportExpiry}
                      onChange={e => setPassportExpiry(e.target.value)}
                      placeholder="Expiry"
                      style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}
                    />
                  </div>
                  {passportEditing && <button onClick={() => setPassportEditing(false)} style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(74,140,196,0.3)", background: "transparent", color: goldText, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start" }}>Done</button>}
                </div>
              )}
              <div style={{ fontSize: 10, color: textDim, marginTop: 6, lineHeight: 1.5, marginBottom: 16 }}>
                Used for booking international flights. Stored securely — only the last 4 digits are visible to you.
              </div>
            </div>

            {/* ── Avoid Preferences — variable-length lists ── */}
            <Label color="#4A8CC4">Avoid Preferences</Label>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>Destinations to Avoid</div>
              <input value={avoidDestinations} onChange={e => setAvoidDestinations(e.target.value)} placeholder="e.g. Russia, Middle East" style={fieldStyle} />
              <div style={{ fontSize: 11, color: textMuted, marginTop: 8, marginBottom: 4 }}>Airlines to Avoid</div>
              <input value={avoidAirlines} onChange={e => setAvoidAirlines(e.target.value)} placeholder="e.g. Spirit, Ryanair" style={fieldStyle} />
              <div style={{ fontSize: 11, color: textMuted, marginTop: 8, marginBottom: 4 }}>Hotel Chains to Avoid</div>
              <input value={avoidHotels} onChange={e => setAvoidHotels(e.target.value)} placeholder="e.g. Motel 6, Budget Inn" style={fieldStyle} />
            </div>
            {/* Airline Programs (was "Frequent Flyer Programs") */}
            <Label color="#4A8CC4">Airline Programs <span style={{ fontSize: 10, color: textMuted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional</span></Label>
            <p style={{ fontSize: 11, color: textMuted, marginBottom: 12, marginTop: -4, lineHeight: 1.5 }}>Add your loyalty numbers and we'll prioritise these airlines when planning your trip.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
              {AIRLINE_LIST.filter(a => visibleAirlineIds.has(a.id)).map(airline => {
                const enrolled = ffPrograms[airline.id] !== undefined;
                const number = ffPrograms[airline.id] || "";
                return (
                  <div key={airline.id} style={{ borderRadius: 12, border: `1px solid ${enrolled ? `${airline.color}55` : "rgba(27,58,92,0.096)"}`, background: enrolled ? `${airline.color}10` : "rgba(27,58,92,0.030)", overflow: "hidden", transition: "all 0.2s" }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{airline.logo}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: enrolled ? "#1B3A5C" : "#9aaabb" }}>{airline.name}</div>
                        <div style={{ fontSize: 10, color: textDim }}>{airline.program}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (enrolled) {
                            const next = { ...ffPrograms };
                            delete next[airline.id];
                            setFfPrograms(next);
                            setEditingPrograms(prev => { const n = new Set(prev); n.delete(airline.id); return n; });
                          } else {
                            setFfPrograms(p => ({ ...p, [airline.id]: "" }));
                            // Start in editing mode so input renders immediately
                            setEditingPrograms(prev => { const n = new Set(prev); n.add(airline.id); return n; });
                          }
                        }}
                        style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${enrolled ? "rgba(255,107,107,0.35)" : `${airline.color}55`}`, background: enrolled ? "rgba(255,107,107,0.1)" : `${airline.color}18`, color: enrolled ? "#ff6b6b" : "#9aaabb", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                        {enrolled ? "Remove" : "Add"}
                      </button>
                    </div>
                    {/* Number input — only when enrolled.
                        Editing state is now explicit: Add puts user in edit
                        mode, Save exits it. No more auto-save after 4 chars. */}
                    {enrolled && (
                      <div style={{ padding: "0 14px 12px" }}>
                        {editingPrograms.has(airline.id) ? (
                          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                            <input
                              value={number}
                              autoFocus
                              onChange={e => setFfPrograms(p => ({ ...p, [airline.id]: e.target.value }))}
                              placeholder={`${airline.program} number`}
                              style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
                            />
                            <button
                              onClick={() => setEditingPrograms(prev => { const n = new Set(prev); n.delete(airline.id); return n; })}
                              disabled={!number || number.length < 4}
                              style={{ padding: "0 14px", borderRadius: 10, border: "none", background: (!number || number.length < 4) ? "rgba(27,58,92,0.12)" : `linear-gradient(135deg,${airline.color},#0F2747)`, color: (!number || number.length < 4) ? textDim : "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: (!number || number.length < 4) ? "not-allowed" : "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                              Save
                            </button>
                          </div>
                        ) : number ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 10, background: `${airline.color}12`, border: `1px solid ${airline.color}30`, marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, letterSpacing: 1.5, fontFamily: "monospace" }}>
                                {"•".repeat(Math.max(0, number.length - 4))}{number.slice(-4)}
                              </div>
                              <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{airline.program} number</div>
                            </div>
                            <button onClick={() => setEditingPrograms(prev => { const next = new Set(prev); next.add(airline.id); return next; })} style={{ fontSize: 11, color: textMuted, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                          </div>
                        ) : (
                          <button onClick={() => setEditingPrograms(prev => { const n = new Set(prev); n.add(airline.id); return n; })}
                            style={{ ...fieldStyle, marginBottom: 6, textAlign: "left", color: textDim, cursor: "pointer", border: `1px dashed ${airline.color}55` }}>
                            + Add your {airline.program} number
                          </button>
                        )}
                        <a href={airline.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 11, color: teal, textDecoration: "none", fontWeight: 600 }}>
                          Don't have a number? Sign up for {airline.program} →
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Add more airlines button — hidden when all available are visible */}
            {visibleAirlineIds.size < AIRLINE_LIST.length && (
              <button onClick={() => setShowAirlinePicker(true)}
                style={{
                  display: "block", width: "100%", padding: "10px 14px",
                  borderRadius: 10, border: `1px dashed rgba(74,140,196,0.4)`,
                  background: "rgba(74,140,196,0.04)", color: goldText,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit", marginBottom: 20,
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,140,196,0.1)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(74,140,196,0.04)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.4)"; }}>
                + Add another airline
              </button>
            )}

            {/* Hotel Programs — parallel structure to airlines */}
            <Label color="#4A8CC4">Hotel Programs <span style={{ fontSize: 10, color: textMuted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional</span></Label>
            <p style={{ fontSize: 11, color: textMuted, marginBottom: 12, marginTop: -4, lineHeight: 1.5 }}>Add your hotel loyalty numbers so we can match the right brand for your trip.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
              {HOTEL_LIST.filter(h => visibleHotelIds.has(h.id)).map(hotel => {
                const enrolled = hotelPrograms[hotel.id] !== undefined;
                const number = hotelPrograms[hotel.id] || "";
                return (
                  <div key={hotel.id} style={{ borderRadius: 12, border: `1px solid ${enrolled ? `${hotel.color}55` : "rgba(27,58,92,0.096)"}`, background: enrolled ? `${hotel.color}10` : "rgba(27,58,92,0.030)", overflow: "hidden", transition: "all 0.2s" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{hotel.logo}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: enrolled ? "#1B3A5C" : "#9aaabb" }}>{hotel.name}</div>
                        <div style={{ fontSize: 10, color: textDim }}>{hotel.program}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (enrolled) {
                            const next = { ...hotelPrograms };
                            delete next[hotel.id];
                            setHotelPrograms(next);
                            setEditingPrograms(prev => { const n = new Set(prev); n.delete(hotel.id); return n; });
                          } else {
                            setHotelPrograms(p => ({ ...p, [hotel.id]: "" }));
                            setEditingPrograms(prev => { const n = new Set(prev); n.add(hotel.id); return n; });
                          }
                        }}
                        style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${enrolled ? "rgba(255,107,107,0.35)" : `${hotel.color}55`}`, background: enrolled ? "rgba(255,107,107,0.1)" : `${hotel.color}18`, color: enrolled ? "#ff6b6b" : "#9aaabb", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                        {enrolled ? "Remove" : "Add"}
                      </button>
                    </div>
                    {enrolled && (
                      <div style={{ padding: "0 14px 12px" }}>
                        {editingPrograms.has(hotel.id) ? (
                          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                            <input
                              value={number}
                              autoFocus
                              onChange={e => setHotelPrograms(p => ({ ...p, [hotel.id]: e.target.value }))}
                              placeholder={`${hotel.program} number`}
                              style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
                            />
                            <button
                              onClick={() => setEditingPrograms(prev => { const n = new Set(prev); n.delete(hotel.id); return n; })}
                              disabled={!number || number.length < 4}
                              style={{ padding: "0 14px", borderRadius: 10, border: "none", background: (!number || number.length < 4) ? "rgba(27,58,92,0.12)" : `linear-gradient(135deg,${hotel.color},#0F2747)`, color: (!number || number.length < 4) ? textDim : "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: (!number || number.length < 4) ? "not-allowed" : "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                              Save
                            </button>
                          </div>
                        ) : number ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 10, background: `${hotel.color}12`, border: `1px solid ${hotel.color}30`, marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, letterSpacing: 1.5, fontFamily: "monospace" }}>
                                {"•".repeat(Math.max(0, number.length - 4))}{number.slice(-4)}
                              </div>
                              <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{hotel.program} number</div>
                            </div>
                            <button onClick={() => setEditingPrograms(prev => { const next = new Set(prev); next.add(hotel.id); return next; })} style={{ fontSize: 11, color: textMuted, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                          </div>
                        ) : (
                          <button onClick={() => setEditingPrograms(prev => { const n = new Set(prev); n.add(hotel.id); return n; })}
                            style={{ ...fieldStyle, marginBottom: 6, textAlign: "left", color: textDim, cursor: "pointer", border: `1px dashed ${hotel.color}55` }}>
                            + Add your {hotel.program} number
                          </button>
                        )}
                        <a href={hotel.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 11, color: teal, textDecoration: "none", fontWeight: 600 }}>
                          Don't have a number? Sign up for {hotel.program} →
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {visibleHotelIds.size < HOTEL_LIST.length && (
              <button onClick={() => setShowHotelPicker(true)}
                style={{
                  display: "block", width: "100%", padding: "10px 14px",
                  borderRadius: 10, border: `1px dashed rgba(74,140,196,0.4)`,
                  background: "rgba(74,140,196,0.04)", color: goldText,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit", marginBottom: 20,
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,140,196,0.1)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(74,140,196,0.04)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.4)"; }}>
                + Add another hotel
              </button>
            )}

            <button onClick={saveProfile} disabled={profileSaving} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: profileSaving ? "rgba(27,58,92,0.096)" : profileSaved ? "rgba(109,189,109,0.2)" : profileSaveError ? "rgba(255,107,107,0.15)" : `linear-gradient(135deg,${gold},${amber})`, color: profileSaving ? "#4A6B8A" : profileSaved ? "#6dbd6d" : profileSaveError ? "#FF6B6B" : "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: profileSaving ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: profileSaveError ? 8 : 20, transition: "all 0.3s" }}>
              {profileSaving ? "Saving…" : profileSaved ? "✓ Saved!" : profileSaveError ? "Try again" : "Save Preferences"}
            </button>
            {profileSaveError && (
              <div role="alert" style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 10, padding: "10px 14px", marginBottom: 20, fontSize: 12, color: "#FF6B6B", lineHeight: 1.5 }}>
                <strong style={{ display: "block", marginBottom: 2, color: "#FF6B6B" }}>Couldn't save your changes.</strong>
                {profileSaveError}
              </div>
            )}

            {/* Referral */}
            <Label color="#4A8CC4">Referral</Label>
            <div style={{ background: "rgba(27,58,92,0.045)", border: "1px solid rgba(27,58,92,0.096)", borderRadius: 12, padding: "14px", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>Your referral code</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1, fontFamily: "monospace", fontSize: 16, fontWeight: 800, color: goldText, letterSpacing: 2 }}>{user.referralCode}</div>
                <button onClick={copyReferral} style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid rgba(74,140,196,0.3)`, background: "rgba(74,140,196,0.08)", color: goldText, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {copied ? "Copied! ✓" : "Copy"}
                </button>
              </div>

              {/* ── Free Trip Pass — referrer reward tiers ── */}
              {(() => {
                const refCount = user.referralCount || 0;
                const tiers = [
                  { needed: 1, label: "1 Friend", reward: "1 Free Booking" },
                  { needed: 5, label: "5 Friends", reward: "Free Booking for 3 Months" },
                  { needed: 10, label: "10 Friends", reward: "VIP Status — Free Booking for 1 Year", vip: true },
                ];
                return (
                  <div style={{ paddingTop: 12, borderTop: "1px solid rgba(74,140,196,0.12)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontSize: 11, letterSpacing: 2, color: goldText, textTransform: "uppercase", fontWeight: 800 }}>Free Trip Pass</div>
                      <div style={{ fontSize: 10, color: textDim }}>{refCount} {refCount === 1 ? "friend" : "friends"} referred</div>
                    </div>
                    <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.5, marginBottom: 12 }}>
                      Friends get <strong style={{ color: goldText }}>$20 off</strong> their first booking. Earn free bookings yourself by referring friends:
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {tiers.map((tier, i) => {
                        const achieved = refCount >= tier.needed;
                        const prevNeeded = i === 0 ? 0 : tiers[i - 1].needed;
                        const isCurrent = !achieved && refCount >= prevNeeded;
                        const progressPct = Math.min(100, Math.max(0, ((refCount - prevNeeded) / (tier.needed - prevNeeded)) * 100));

                        return (
                          <div key={tier.needed} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 11px", borderRadius: 10,
                            background: achieved ? "rgba(74,140,196,0.1)" : isCurrent ? "rgba(74,140,196,0.05)" : "rgba(27,58,92,0.030)",
                            border: `1px solid ${achieved ? "rgba(74,140,196,0.35)" : isCurrent ? "rgba(74,140,196,0.18)" : "rgba(27,58,92,0.060)"}`,
                            opacity: achieved || isCurrent ? 1 : 0.55,
                            transition: "all 0.2s",
                          }}>
                            {/* Status icon */}
                            <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: achieved ? gold : isCurrent ? `${gold}20` : "rgba(27,58,92,0.072)", border: `1.5px solid ${achieved ? gold : isCurrent ? `${gold}80` : "rgba(27,58,92,0.144)"}` }}>
                              {achieved ? (
                                <svg width="11" height="9" viewBox="0 0 12 10" fill="none" aria-hidden="true">
                                  <path d="M1 5l3.5 3.5L11 1" stroke="#FAFAF5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              ) : tier.vip ? (
                                <span style={{ fontSize: 11 }} aria-hidden="true">🔒</span>
                              ) : (
                                <span style={{ fontSize: 10, color: isCurrent ? gold : textDim, fontWeight: 800 }}>{tier.needed}</span>
                              )}
                            </div>
                            {/* Tier text */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: achieved ? gold : isCurrent ? "#1B3A5C" : textMuted, lineHeight: 1.3 }}>
                                {tier.label}
                              </div>
                              <div style={{ fontSize: 10, color: achieved ? `${gold}cc` : textDim, lineHeight: 1.4, marginTop: 1 }}>
                                {tier.reward}
                              </div>
                              {/* Progress bar — only on the in-progress tier */}
                              {isCurrent && (
                                <div style={{ height: 3, background: "rgba(27,58,92,0.096)", borderRadius: 2, overflow: "hidden", marginTop: 5 }}>
                                  <div style={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg, ${gold}, ${amber})`, transition: "width 0.4s ease" }} />
                                </div>
                              )}
                            </div>
                            {/* Trailing count for in-progress tier */}
                            {isCurrent && (
                              <div style={{ fontSize: 11, fontWeight: 800, color: goldText, flexShrink: 0 }}>
                                {refCount}/{tier.needed}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            </div>
            {/* ── End Overview ── */}

            {/* ── Stamps and Postcards moved to the Journal section ── */}


          </div>
        )}

        {/* ── Travel Identity tab — focused identity card + retake CTA.
           Travel style chips and KTN/passport/airport stay in their existing
           homes (dashboard + Profile tab). This tab is purely about your
           traveler type as a discoverable, expressive surface. */}
        {tab === "identity" && (() => {
          const identity = travelerType && TRAVEL_IDENTITIES[travelerType];

          if (!identity) {
            // No quiz taken yet — invite the user to take it
            return (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ marginBottom: 14, display: "flex", justifyContent: "center" }}>
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="9" r="3" stroke="#4A8CC4" strokeWidth="1.7" fill="rgba(74,140,196,0.08)"/>
                    <path d="M5 21 C 5 16 8 14 12 14 C 16 14 19 16 19 21" stroke="#4A8CC4" strokeWidth="1.7" strokeLinecap="round"/>
                    <path d="M9 17.5 L15 17.5" stroke="#4A8CC4" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
                    <circle cx="12" cy="9" r="1" fill="#4A8CC4" opacity="0.7"/>
                  </svg>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary, marginBottom: 8 }}>Discover your travel identity</div>
                <div style={{ fontSize: 13, color: textMuted, lineHeight: 1.6, marginBottom: 18, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                  Take a quick quiz to find out what kind of traveler you are. We'll use it to personalize every recommendation.
                </div>
                {onRetakeQuiz && (
                  <button onClick={onRetakeQuiz}
                    style={{ padding: "11px 22px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${gold}, ${amber})`, color: "#FAFAF5", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                    Take the quiz →
                  </button>
                )}
              </div>
            );
          }

          // User has identity — show full identity card
          return (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              {/* Identity card — gradient header with traveler-type color */}
              <div style={{
                position: "relative",
                padding: "24px 22px",
                borderRadius: 18,
                background: identity.gradient || `linear-gradient(135deg, ${identity.color}, ${identity.color}aa)`,
                overflow: "hidden",
                marginBottom: 16,
              }}>
                {/* Glow blob in upper right for depth */}
                <div aria-hidden="true" style={{
                  position: "absolute", top: -30, right: -30,
                  width: 180, height: 180, borderRadius: "50%",
                  background: `radial-gradient(circle, ${identity.color}40 0%, transparent 65%)`,
                  pointerEvents: "none",
                }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* Eyebrow */}
                  <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(0,0,0,0.55)", textTransform: "uppercase", marginBottom: 12, fontWeight: 800 }}>
                    Your Travel Identity™
                  </div>
                  {/* Hero icon + name */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                    <div style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {identity.svgIcon ? identity.svgIcon(64, "#FAFAF5") : <span style={{ fontSize: 48 }}>{identity.emoji}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "#FAFAF5", lineHeight: 1.1, letterSpacing: "-0.4px" }}>
                        {travelerType}
                      </h2>
                    </div>
                  </div>
                  {/* Description */}
                  {identity.desc && (
                    <p style={{ fontSize: 13, color: "rgba(0,0,0,0.75)", lineHeight: 1.55, margin: "0 0 14px", fontStyle: "italic" }}>
                      "{identity.desc}"
                    </p>
                  )}
                  {/* Vibe chips */}
                  {Array.isArray(identity.vibes) && identity.vibes.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {identity.vibes.map(v => (
                        <span key={v} style={{
                          fontSize: 10, padding: "3px 9px", borderRadius: 12,
                          background: "rgba(0,0,0,0.18)",
                          color: "rgba(0,0,0,0.85)",
                          fontWeight: 700, textTransform: "capitalize", letterSpacing: 0.3,
                        }}>
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Share button — generates an Instagram Story-formatted PNG of
                 the user's identity card. On mobile, opens the native share
                 sheet (Instagram, Messages, etc.). On desktop, downloads the
                 image. Subtle loading state while the SVG renders to canvas. */}
              <button
                onClick={() => shareIdentityCard(identity, travelerType)}
                disabled={identitySharing}
                aria-label={`Share your ${travelerType} identity`}
                style={{
                  width: "100%", padding: "14px", borderRadius: 12, border: "none",
                  background: identitySharing ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg, ${identity.color}, ${identity.color}cc)`,
                  color: identitySharing ? textMuted : "#FAFAF5",
                  fontSize: 14, fontWeight: 900, fontFamily: "inherit",
                  cursor: identitySharing ? "wait" : "pointer",
                  marginBottom: identityShareError ? 8 : 12,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "all 0.2s",
                }}>
                {identitySharing ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" stroke={textMuted} strokeWidth="2.5" strokeDasharray="14 8" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                      </circle>
                    </svg>
                    Generating your card…
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 12 v6 a2 2 0 0 0 2 2 h12 a2 2 0 0 0 2-2 v-6" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      <path d="M12 14 V3" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M7 8 L12 3 L17 8" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    Share your travel identity
                  </>
                )}
              </button>
              {identityShareError && (
                <div role="alert" style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: "#FF6B6B" }}>
                  {identityShareError}
                </div>
              )}

              {/* Retake CTA */}
              <div style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.06)"}`, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, marginBottom: 2 }}>Identity not feeling right?</div>
                  <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.5 }}>Retake the quiz to refresh how we recommend trips for you.</div>
                </div>
                {onRetakeQuiz && (
                  <button onClick={onRetakeQuiz}
                    style={{ flexShrink: 0, padding: "9px 14px", borderRadius: 10, border: `1px solid ${identity.color}50`, background: `${identity.color}15`, color: identity.color, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    ↻ Retake quiz
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Loyalty picker modals — mounted globally so they overlay profile tab ── */}
        {showAirlinePicker && (
          <LoyaltyPickerModal
            kind="airline"
            items={AIRLINE_LIST}
            alreadyAdded={visibleAirlineIds}
            onAdd={(id) => setVisibleAirlineIds(prev => new Set([...prev, id]))}
            onClose={() => setShowAirlinePicker(false)}
          />
        )}
        {showHotelPicker && (
          <LoyaltyPickerModal
            kind="hotel"
            items={HOTEL_LIST}
            alreadyAdded={visibleHotelIds}
            onAdd={(id) => setVisibleHotelIds(prev => new Set([...prev, id]))}
            onClose={() => setShowHotelPicker(false)}
          />
        )}

        {/* ── Trip delete confirmation ──
           Cascade-by-default: deletes both trip + journal. The "Just journal"
           secondary path lets users keep the trip but wipe their writing.
           Triggered by the × button on completed trip cards in the Trips tab. */}
        {tripDeleteConfirm && (() => {
          const { trip, hasJournal } = tripDeleteConfirm;
          return (
            <div role="dialog" aria-modal="true" aria-labelledby="trip-delete-title"
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
              onClick={(e) => { if (e.target === e.currentTarget) setTripDeleteConfirm(null); }}>
              <div style={{ background: darkMode ? "#0f1722" : "#fff", borderRadius: 16, border: `1px solid ${darkMode ? "rgba(255,107,107,0.25)" : "rgba(255,107,107,0.2)"}`, maxWidth: 420, width: "100%", padding: "22px 22px 18px", boxShadow: "0 20px 60px rgba(0,0,0,0.45)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: "rgba(255,107,107,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 9v4M12 17h0M4.93 19h14.14a2 2 0 0 0 1.74-3l-7.07-12a2 2 0 0 0-3.48 0L3.18 16a2 2 0 0 0 1.75 3z" stroke="#FF6B6B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 id="trip-delete-title" style={{ margin: 0, fontSize: 16, fontWeight: 800, color: textPrimary }}>
                    Delete trip to {trip.dest}?
                  </h3>
                </div>
                <p style={{ margin: "0 0 16px", fontSize: 13, color: textMuted, lineHeight: 1.6 }}>
                  {hasJournal
                    ? <>This will permanently delete your trip to <strong>{trip.dest}</strong> and its journal entry. This action cannot be undone.</>
                    : <>This will permanently delete your trip to <strong>{trip.dest}</strong>. This action cannot be undone.</>
                  }
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button
                    onClick={async () => {
                      // Always cascade when journal exists. The "keep journal"
                      // path orphaned the data — journal entries are keyed by
                      // tripCode and only render against trip cards, so removing
                      // the trip made the journal invisible while leaving stale
                      // data on disk. Cleanest fix: cascade by default.
                      if (hasJournal && journalApi) {
                        await journalApi.deleteJournal(trip.code);
                      }
                      if (tripsApi) {
                        await tripsApi.deleteTrip(trip.id ?? trip.code);
                      } else {
                        setPastTrips(prev => prev.filter(t => (t.id ?? t.code) !== (trip.id ?? trip.code)));
                      }
                      setTripDeleteConfirm(null);
                    }}
                    style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #FF6B6B, #ff6b6b)", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                    {hasJournal ? "Delete trip + journal" : "Delete trip"}
                  </button>
                  <button
                    onClick={() => setTripDeleteConfirm(null)}
                    style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: "transparent", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Journal delete confirmation ──
           Offers cascade ("Delete journal + trip") as a secondary option so
           users can wipe everything from either direction. Mirrors the trip
           delete modal's structure for consistency. */}
        {journalDeleteConfirm && (() => {
          const code = journalDeleteConfirm;
          const trip = pastTrips.find(t => t.code === code);
          // Cascade only valid for user-added trips (booked/demo trips need
          // their own cancellation flow, not a delete).
          const tripCanBeCascadeDeleted = trip && !(trip.code && trip.code.startsWith("TMA-DEMO-")) && trip.source !== "booking";
          return (
            <div role="dialog" aria-modal="true" aria-labelledby="journal-delete-title"
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
              onClick={(e) => { if (e.target === e.currentTarget) setJournalDeleteConfirm(null); }}>
              <div style={{ background: darkMode ? "#0f1722" : "#fff", borderRadius: 16, border: `1px solid ${darkMode ? "rgba(255,107,107,0.25)" : "rgba(255,107,107,0.2)"}`, maxWidth: 420, width: "100%", padding: "22px 22px 18px", boxShadow: "0 20px 60px rgba(0,0,0,0.45)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: "rgba(255,107,107,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="#FF6B6B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 id="journal-delete-title" style={{ margin: 0, fontSize: 16, fontWeight: 800, color: textPrimary }}>
                    {trip ? `Delete journal for ${trip.dest}?` : "Delete journal entry?"}
                  </h3>
                </div>
                <p style={{ margin: "0 0 16px", fontSize: 13, color: textMuted, lineHeight: 1.6 }}>
                  This will permanently delete your journal{trip ? <> for <strong>{trip.dest}</strong></> : ""} — including writing, ratings, and any photos. Your trip itself will stay. This action cannot be undone.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button
                    onClick={async () => {
                      if (journalApi) {
                        await journalApi.deleteJournal(code);
                      } else {
                        setJournalEntries(prev => { const { [code]: _, ...rest } = prev; return rest; });
                        setJournalRatings(prev => { const { [code]: _, ...rest } = prev; return rest; });
                        setJournalMeta(prev => { const { [code]: _, ...rest } = prev; return rest; });
                      }
                      setJournalDeleteConfirm(null);
                      setJournalEditing(null);
                      setJournalDraft("");
                    }}
                    style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #FF6B6B, #ff6b6b)", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                    Delete journal
                  </button>
                  {tripCanBeCascadeDeleted && (
                    <button
                      onClick={async () => {
                        // Cascade: journal first then trip. Matches the trip
                        // delete modal's order. Both are wiped together.
                        if (journalApi) {
                          await journalApi.deleteJournal(code);
                        }
                        if (tripsApi) {
                          await tripsApi.deleteTrip(trip.id ?? trip.code);
                        } else {
                          setPastTrips(prev => prev.filter(t => (t.id ?? t.code) !== (trip.id ?? trip.code)));
                        }
                        setJournalDeleteConfirm(null);
                        setJournalEditing(null);
                        setJournalDraft("");
                      }}
                      style={{ width: "100%", padding: "11px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: textPrimary, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      Delete journal + trip
                    </button>
                  )}
                  <button
                    onClick={() => setJournalDeleteConfirm(null)}
                    style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: "transparent", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Full-page Stamp Detail Modal ─────────────────────────────
           Opens when user taps a stamp in the Stamps grid. Allows state
           toggle, notes, dates, and removal. Modal is a fixed overlay so it
           takes over the screen on mobile. */}
        {activeStampDetail && (() => {
          const stamp = activeStampDetail;
          const isVisited = stamp.state === "visited";
          // Helper: persist state change and sync local state
          const handleStateToggle = (newState) => {
            const next = setStampState(stamp.id, newState);
            setStampsData(next);
            setActiveStampDetail({ ...stamp, ...next[stamp.id] });
          };
          const handleMetaPatch = (patch) => {
            const next = updateStampMeta(stamp.id, patch);
            setStampsData(next);
            setActiveStampDetail({ ...stamp, ...next[stamp.id] });
          };
          const handleRemove = () => {
            if (!window.confirm("Remove this stamp from your collection? It'll go back to suggested.")) return;
            const next = clearStamp(stamp.id);
            setStampsData(next);
            setActiveStampDetail(null);
          };
          // Format date for input field (YYYY-MM-DD) from timestamp
          const formatDate = (ts) => ts ? new Date(ts).toISOString().slice(0, 10) : "";
          return (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Stamp detail: ${stamp.title}`}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 500,
                background: "rgba(20,14,8,0.88)",
                WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                animation: "fadeIn 0.25s ease",
              }}
              onClick={() => setActiveStampDetail(null)}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  width: "100%",
                  maxWidth: 440,
                  maxHeight: "92vh",
                  overflowY: "auto",
                  background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                  border: "1px solid rgba(74,140,196,0.55)",
                  borderRadius: 18,
                  boxShadow: "0 24px 64px rgba(20,14,8,0.4)",
                  animation: "fadeIn 0.35s cubic-bezier(0.34,1.2,0.64,1)",
                }}
              >
                {/* Close button — top right */}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setActiveStampDetail(null)}
                    aria-label="Close"
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "rgba(20,14,8,0.12)",
                      border: "none",
                      cursor: "pointer",
                      color: "#4A6B8A",
                      fontSize: 16,
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    ✕
                  </button>

                  {/* Big illustration */}
                  <div style={{
                    height: 180,
                    overflow: "hidden",
                    borderBottom: "1px dashed rgba(74,140,196,0.5)",
                    background: "rgba(255,255,255,0.3)",
                    position: "relative",
                  }}>
                    <StampIllustration id={stamp.id} />
                    {stamp.canon === "wonder" && (
                      <div style={{ position: "absolute", top: 10, right: 10, pointerEvents: "none" }}>
                        <WonderSeal size={42} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: "20px 22px 24px" }}>
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2.5,
                    color: "#1B3A5C",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 4,
                  }}>{stamp.country}</div>
                  <div style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontStyle: "italic",
                    fontSize: 26,
                    fontWeight: 500,
                    color: "#1B3A5C",
                    lineHeight: 1.15,
                    marginBottom: 10,
                  }}>{stamp.title}</div>
                  {stamp.desc && (
                    <div style={{
                      fontSize: 13,
                      color: "#4A6B8A",
                      lineHeight: 1.55,
                      marginBottom: 20,
                    }}>{stamp.desc}</div>
                  )}

                  {/* State toggle — Someday ⇄ Visited */}
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2,
                    color: textMuted,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}>Status</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                    <button
                      onClick={() => handleStateToggle("someday")}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 22,
                        border: !isVisited ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                        background: !isVisited ? "rgba(27,58,92,0.08)" : "transparent",
                        color: !isVisited ? "#1B3A5C" : textMuted,
                        fontSize: 13,
                        fontWeight: !isVisited ? 800 : 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                      }}
                    >○ Someday</button>
                    <button
                      onClick={() => handleStateToggle("visited")}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 22,
                        border: isVisited ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                        background: isVisited ? "rgba(27,58,92,0.08)" : "transparent",
                        color: isVisited ? "#1B3A5C" : textMuted,
                        fontSize: 13,
                        fontWeight: isVisited ? 800 : 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                      }}
                    >✓ Visited</button>
                  </div>

                  {/* Date field — context depends on state */}
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2,
                    color: textMuted,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 6,
                  }}>{isVisited ? "When did you go?" : "Planning for?"}</div>
                  <input
                    type={isVisited ? "date" : "text"}
                    value={isVisited ? formatDate(stamp.visitedAt) : (stamp.plannedFor || "")}
                    onChange={e => {
                      if (isVisited) {
                        const ts = e.target.value ? new Date(e.target.value).getTime() : null;
                        handleMetaPatch({ visitedAt: ts });
                      } else {
                        handleMetaPatch({ plannedFor: e.target.value });
                      }
                    }}
                    placeholder={isVisited ? "" : "e.g. Spring 2027, next fall…"}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(74,140,196,0.45)",
                      background: "rgba(255,255,255,0.5)",
                      color: textPrimary,
                      fontSize: 13,
                      fontFamily: "inherit",
                      marginBottom: 18,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />

                  {/* Notes textarea — only meaningful once visited */}
                  {isVisited && (
                    <>
                      <div style={{
                        fontSize: 10,
                        letterSpacing: 2,
                        color: textMuted,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginBottom: 6,
                      }}>Notes</div>
                      <textarea
                        value={stamp.visitedNote || ""}
                        onChange={e => handleMetaPatch({ visitedNote: e.target.value })}
                        placeholder="What stayed with you?"
                        rows={3}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "1px solid rgba(74,140,196,0.45)",
                          background: "rgba(255,255,255,0.5)",
                          color: textPrimary,
                          fontSize: 13,
                          fontFamily: "inherit",
                          fontStyle: "italic",
                          marginBottom: 18,
                          outline: "none",
                          boxSizing: "border-box",
                          resize: "vertical",
                          minHeight: 70,
                        }}
                      />
                    </>
                  )}

                  {/* Done — auto-save already commits on every keystroke,
                      but a prominent Done button gives users a visible signal
                      that they're finished + their changes are saved. */}
                  <button
                    onClick={() => setActiveStampDetail(null)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: 12,
                      border: "none",
                      background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                      color: "#FAFAF5",
                      fontSize: 14,
                      fontWeight: 800,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      marginBottom: 8,
                      boxShadow: "0 2px 10px rgba(27,58,92,0.22)",
                    }}
                  >
                    Done — Saved
                  </button>

                  {/* Remove from collection */}
                  <button
                    onClick={handleRemove}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: 12,
                      border: "1px solid rgba(74,140,196,0.4)",
                      background: "transparent",
                      color: textMuted,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >Remove from collection</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Full-page Postcard Detail Modal ──────────────────────────
           Sibling to Stamp Detail Modal. Same shape, different metadata:
           postcards include vibe, price tier, and region. Postcards use
           Dreaming/Visited (not Someday/Visited like stamps). Illustration
           area is taller to honor the landscape postcard aspect ratio. */}
        {activePostcardDetail && (() => {
          const postcard = activePostcardDetail;
          const isVisited = postcard.state === "visited";
          // Determine if this postcard is purely derived (no explicit
          // localStorage entry yet). The first state change converts it
          // from derived → explicit so further changes persist independently
          // of the derivation rules.
          const isDerived = !!postcard._derived;

          const handleStateToggle = (newState) => {
            const next = setPostcardState(postcard.id, newState);
            setPostcardsData(next);
            setActivePostcardDetail({ ...postcard, ...next[postcard.id], _derived: false });
          };
          const handleMetaPatch = (patch) => {
            const next = updatePostcardMeta(postcard.id, patch);
            setPostcardsData(next);
            setActivePostcardDetail({ ...postcard, ...next[postcard.id], _derived: false });
          };
          const handleRemove = () => {
            if (!window.confirm(isDerived
              ? "Hide this postcard? It'll come back if you re-engage with the destination."
              : "Remove this postcard from your collection?")) return;
            const next = clearPostcard(postcard.id);
            setPostcardsData(next);
            setActivePostcardDetail(null);
          };
          const formatDate = (ts) => ts ? new Date(ts).toISOString().slice(0, 10) : "";
          // Price tier display copy
          const priceTierCopy = {
            modest:   "Easy on the wallet",
            moderate: "Mid-range",
            luxe:     "Special-occasion",
          }[postcard.priceTier] || null;

          return (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Postcard detail: ${postcard.title}`}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 500,
                background: "rgba(20,14,8,0.88)",
                WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                animation: "fadeIn 0.25s ease",
              }}
              onClick={() => setActivePostcardDetail(null)}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  width: "100%",
                  maxWidth: 460,
                  maxHeight: "92vh",
                  overflowY: "auto",
                  background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                  border: "1px solid rgba(74,140,196,0.55)",
                  borderRadius: 18,
                  boxShadow: "0 24px 64px rgba(20,14,8,0.4)",
                  animation: "fadeIn 0.35s cubic-bezier(0.34,1.2,0.64,1)",
                }}
              >
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setActivePostcardDetail(null)}
                    aria-label="Close"
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "rgba(20,14,8,0.12)",
                      border: "none",
                      cursor: "pointer",
                      color: "#4A6B8A",
                      fontSize: 16,
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    ✕
                  </button>

                  {/* Big landscape illustration — postcard aspect 7:5 */}
                  <div style={{
                    aspectRatio: "7 / 5",
                    overflow: "hidden",
                    borderBottom: "1px dashed rgba(74,140,196,0.5)",
                    background: "rgba(255,255,255,0.3)",
                  }}>
                    <PostcardIllustration id={postcard.id} />
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: "20px 22px 24px" }}>
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2.5,
                    color: "#1B3A5C",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 4,
                  }}>{postcard.country}</div>
                  <div style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontStyle: "italic",
                    fontSize: 28,
                    fontWeight: 500,
                    color: "#1B3A5C",
                    lineHeight: 1.1,
                    marginBottom: 10,
                  }}>{postcard.title}</div>

                  {/* Vibe quote — postcard-specific richer metadata */}
                  {postcard.vibe && (
                    <div style={{
                      fontSize: 14,
                      color: "#4A6B8A",
                      lineHeight: 1.55,
                      fontStyle: "italic",
                      marginBottom: 16,
                      paddingLeft: 12,
                      borderLeft: "2px solid rgba(27,58,92,0.3)",
                    }}>"{postcard.vibe}"</div>
                  )}

                  {/* Region + price tier strip */}
                  {(postcard.region || priceTierCopy) && (
                    <div style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 20,
                    }}>
                      {postcard.region && (
                        <span style={{
                          fontSize: 10,
                          letterSpacing: 1.5,
                          textTransform: "uppercase",
                          fontWeight: 700,
                          color: "#4A6B8A",
                          padding: "4px 10px",
                          borderRadius: 12,
                          background: "rgba(74,140,196,0.15)",
                          border: "1px solid rgba(74,140,196,0.3)",
                        }}>{postcard.region}</span>
                      )}
                      {priceTierCopy && (
                        <span style={{
                          fontSize: 10,
                          letterSpacing: 1.5,
                          textTransform: "uppercase",
                          fontWeight: 700,
                          color: "#4A6B8A",
                          padding: "4px 10px",
                          borderRadius: 12,
                          background: "rgba(74,140,196,0.15)",
                          border: "1px solid rgba(74,140,196,0.3)",
                        }}>{priceTierCopy}</span>
                      )}
                    </div>
                  )}

                  {/* ── Travel guide content — landmarks + must-do triples ──
                     Rich content shown for saved (dreaming/visited) postcards.
                     Turns each saved postcard into a mini destination guide:
                     famous-for line, three concrete experiences, and four
                     reference facts (best time, currency, language, fun fact). */}
                  {(postcard.landmarks || postcard.mustSee || postcard.mustEat || postcard.mustDo) && (
                    <div style={{
                      marginBottom: 20,
                      padding: "16px 18px",
                      borderRadius: 14,
                      background: "rgba(74,140,196,0.07)",
                      border: "1px solid rgba(74,140,196,0.25)",
                    }}>
                      {postcard.landmarks && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.8, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>
                            Famous for
                          </div>
                          <div style={{ fontSize: 13, color: "#1B3A5C", lineHeight: 1.5, fontWeight: 500 }}>
                            {postcard.landmarks}
                          </div>
                        </div>
                      )}
                      {(postcard.mustSee || postcard.mustEat || postcard.mustDo) && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, paddingTop: 12, borderTop: postcard.landmarks ? "1px dashed rgba(74,140,196,0.3)" : "none" }}>
                          {postcard.mustSee && (
                            <div>
                              <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>See</div>
                              <div style={{ fontSize: 12, color: "#1B3A5C", lineHeight: 1.35, fontWeight: 500 }}>{postcard.mustSee}</div>
                            </div>
                          )}
                          {postcard.mustEat && (
                            <div>
                              <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Eat</div>
                              <div style={{ fontSize: 12, color: "#1B3A5C", lineHeight: 1.35, fontWeight: 500 }}>{postcard.mustEat}</div>
                            </div>
                          )}
                          {postcard.mustDo && (
                            <div>
                              <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Do</div>
                              <div style={{ fontSize: 12, color: "#1B3A5C", lineHeight: 1.35, fontWeight: 500 }}>{postcard.mustDo}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Reference facts — best time, currency, language, fun fact ── */}
                  {(postcard.bestTime || postcard.currency || postcard.language || postcard.funFact) && (
                    <div style={{
                      marginBottom: 20,
                      padding: "14px 18px 16px",
                      borderRadius: 14,
                      background: "rgba(74,140,196,0.10)",
                      border: "1px solid rgba(74,140,196,0.3)",
                    }}>
                      <div style={{ fontSize: 10, letterSpacing: 2, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 800, marginBottom: 10 }}>
                        Good to Know
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px", fontSize: 12, color: "#1B3A5C", lineHeight: 1.4 }}>
                        {postcard.bestTime && (
                          <>
                            <div style={{ color: "#4A6B8A", fontWeight: 700, fontSize: 11 }}>Best time</div>
                            <div>{postcard.bestTime}</div>
                          </>
                        )}
                        {postcard.currency && (
                          <>
                            <div style={{ color: "#4A6B8A", fontWeight: 700, fontSize: 11 }}>Currency</div>
                            <div>{postcard.currency}</div>
                          </>
                        )}
                        {postcard.language && (
                          <>
                            <div style={{ color: "#4A6B8A", fontWeight: 700, fontSize: 11 }}>Language</div>
                            <div>{postcard.language}</div>
                          </>
                        )}
                      </div>
                      {postcard.funFact && (
                        <div style={{
                          marginTop: 12,
                          paddingTop: 12,
                          borderTop: "1px dashed rgba(74,140,196,0.35)",
                          fontSize: 12,
                          color: "#4A6B8A",
                          lineHeight: 1.5,
                          fontStyle: "italic",
                        }}>
                          <span style={{ fontWeight: 700, fontStyle: "normal", color: "#1B3A5C" }}>Did you know? </span>
                          {postcard.funFact}
                        </div>
                      )}
                    </div>
                  )}

                  {/* State toggle — Dreaming ⇄ Visited */}
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2,
                    color: textMuted,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}>Status</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                    <button
                      onClick={() => handleStateToggle("dreaming")}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 22,
                        border: !isVisited ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                        background: !isVisited ? "rgba(27,58,92,0.08)" : "transparent",
                        color: !isVisited ? "#1B3A5C" : textMuted,
                        fontSize: 13,
                        fontWeight: !isVisited ? 800 : 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                      }}
                    >○ Dreaming</button>
                    <button
                      onClick={() => handleStateToggle("visited")}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 22,
                        border: isVisited ? "1.5px solid #1B3A5C" : "1px solid rgba(74,140,196,0.4)",
                        background: isVisited ? "rgba(27,58,92,0.08)" : "transparent",
                        color: isVisited ? "#1B3A5C" : textMuted,
                        fontSize: 13,
                        fontWeight: isVisited ? 800 : 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                      }}
                    >✓ Visited</button>
                  </div>

                  {/* Date field — adaptive */}
                  <div style={{
                    fontSize: 10,
                    letterSpacing: 2,
                    color: textMuted,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 6,
                  }}>{isVisited ? "When did you go?" : "Planning for?"}</div>
                  <input
                    type={isVisited ? "date" : "text"}
                    value={isVisited ? formatDate(postcard.visitedAt) : (postcard.plannedFor || "")}
                    onChange={e => {
                      if (isVisited) {
                        const ts = e.target.value ? new Date(e.target.value).getTime() : null;
                        handleMetaPatch({ visitedAt: ts });
                      } else {
                        handleMetaPatch({ plannedFor: e.target.value });
                      }
                    }}
                    placeholder={isVisited ? "" : "e.g. Spring 2027, next fall…"}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(74,140,196,0.45)",
                      background: "rgba(255,255,255,0.5)",
                      color: textPrimary,
                      fontSize: 13,
                      fontFamily: "inherit",
                      marginBottom: 18,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />

                  {/* Notes — visited only */}
                  {isVisited && (
                    <>
                      <div style={{
                        fontSize: 10,
                        letterSpacing: 2,
                        color: textMuted,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginBottom: 6,
                      }}>Notes</div>
                      <textarea
                        value={postcard.visitedNote || ""}
                        onChange={e => handleMetaPatch({ visitedNote: e.target.value })}
                        placeholder="What stayed with you?"
                        rows={3}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "1px solid rgba(74,140,196,0.45)",
                          background: "rgba(255,255,255,0.5)",
                          color: textPrimary,
                          fontSize: 13,
                          fontFamily: "inherit",
                          fontStyle: "italic",
                          marginBottom: 18,
                          outline: "none",
                          boxSizing: "border-box",
                          resize: "vertical",
                          minHeight: 70,
                        }}
                      />
                    </>
                  )}

                  {/* Done — auto-save already commits, this is the visible
                      "I'm finished" affordance Cas asked for. */}
                  <button
                    onClick={() => setActivePostcardDetail(null)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: 12,
                      border: "none",
                      background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                      color: "#FAFAF5",
                      fontSize: 14,
                      fontWeight: 800,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      marginBottom: 8,
                      boxShadow: "0 2px 10px rgba(27,58,92,0.22)",
                    }}
                  >
                    Done — Saved
                  </button>

                  {/* Remove */}
                  <button
                    onClick={handleRemove}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: 12,
                      border: "1px solid rgba(74,140,196,0.4)",
                      background: "transparent",
                      color: textMuted,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >{isDerived ? "Hide from collection" : "Remove from collection"}</button>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}
function CustomerServiceModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const Sec = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: teal, borderBottom: "1px solid rgba(27,58,92,0.15)", paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );
  const Pt = ({ children }) => <p style={{ margin: "0 0 8px", fontSize: 12, color: textMuted, lineHeight: 1.7 }}>{children}</p>;
  const Qa = ({ q, a }) => (
    <div style={{ marginBottom: 16, background: "rgba(27,58,92,0.030)", borderRadius: 10, padding: "12px 14px", border: "1px solid rgba(27,58,92,0.072)" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#c0d0e0", marginBottom: 6 }}>{q}</div>
      <Pt>{a}</Pt>
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: teal }}>Customer Service & Help</h2>
        </div>
        <Sec title="Contact Us">
          <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: textMuted, marginBottom: 4 }}>📧 Email us at</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: teal }}>support@justgoanywhere.com</div>
            <div style={{ fontSize: 11, color: textDim, marginTop: 4 }}>We respond within 24 hours, Monday–Friday</div>
          </div>
          <Pt>For urgent booking issues, include your booking reference code (e.g. TMA-XXXXX) in your subject line for priority handling.</Pt>
        </Sec>
        <Sec title="Frequently Asked Questions">
          <Qa q="How does Just Go Anywhere work?" a="Two modes: Take Me Anywhere lets you set a budget, pick your vibe, and spin for three destination matches. Surprise Me picks the destination for you and reveals it when you're ready." />
          <Qa q="Do I need an account?" a="You get 3 free spins without registering. A free account unlocks unlimited spins, AI itineraries, and Surprise Me bookings — completely free." />
          <Qa q="Does Just Go Anywhere book flights and hotels directly?" a="Not yet. We connect you to trusted partners — Booking.com for hotels, Viator and GetYourGuide for activities. We earn a small commission at no extra cost to you." />
          <Qa q="Can I change my Surprise Me destination?" a="Once confirmed and paid, the destination cannot be changed. You can cancel in line with our Cancellation Policy. Adjust preferences before confirming if you have concerns." />
          <Qa q="Questions about a third-party booking?" a="Contact the provider directly: Booking.com (booking.com/help), Viator (viator.com/help), or GetYourGuide (getyourguide.com/help)." />
          <Qa q="Found a bug?" a="Email support@justgoanywhere.com with your device, browser, a description of the issue, and screenshots if possible." />
          <Qa q="How do I delete my account?" a='Email support@justgoanywhere.com with the subject "Account Deletion Request". We process requests within 30 days per our Privacy Policy.' />
        </Sec>
      </div>
    </div>
  );
}

// ── Cancellation Policy Modal ───────────────────────────────────
function CancellationPolicyModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const Pt = ({ children }) => <p style={{ margin: "0 0 8px", fontSize: 12, color: textMuted, lineHeight: 1.7 }}>{children}</p>;
  const Sec = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: teal, borderBottom: "1px solid rgba(27,58,92,0.15)", paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );
  const tiers = [
    { timing: "Within 24 hours of booking", refund: "100% — Full refund", color: "#6dbd6d" },
    { timing: "30+ days before departure", refund: "100% — Full refund", color: "#6dbd6d" },
    { timing: "7–29 days before departure", refund: "50% — Partial refund", color: "#4A8CC4" },
    { timing: "Less than 7 days before departure", refund: "0% — No refund", color: "#ff6b6b" },
  ];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: teal }}>Cancellation & Refund Policy</h2>
        </div>
        <Sec title="Refund Schedule">
          <Pt>The following applies to bookings made directly through Just Go Anywhere.</Pt>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {tiers.map(t => (
              <div key={t.timing} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(27,58,92,0.030)", border: "1px solid rgba(27,58,92,0.072)", borderRadius: 10, padding: "12px 16px", gap: 12 }}>
                <span style={{ fontSize: 12, color: "#4A6B8A", flex: 1 }}>{t.timing}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: t.color, flexShrink: 0 }}>{t.refund}</span>
              </div>
            ))}
          </div>
          <Pt style={{ marginTop: 10 }}>Refunds are returned to your original payment method within 5–10 business days.</Pt>
        </Sec>
        <Sec title="How to Cancel">
          <Pt>Log in → My Account → My Trips → select your booking → Cancel Trip. Or email support@justgoanywhere.com with your booking reference code.</Pt>
        </Sec>
        <Sec title="Surprise Me Bookings">
          <Pt>The standard refund schedule applies. The full 24-hour cooling-off period applies regardless of destination. If the selected destination requires a visa you don't hold, contact us immediately at support@justgoanywhere.com for an exceptional review.</Pt>
        </Sec>
        <Sec title="Travel Insurance">
          <Pt>Travel insurance is available at booking for $49 (Standard) or $89 (Premium) and covers trip cancellation, medical emergencies up to $50K, and lost baggage. Claims are handled directly by SafetyWing.</Pt>
        </Sec>
        <Sec title="Third-Party Bookings">
          <Pt>Bookings made through Booking.com, Viator, or GetYourGuide are subject to those providers' own cancellation policies.</Pt>
        </Sec>
      </div>
    </div>
  );
}

// ── Terms of Service Modal ──────────────────────────────────────
function TermsOfServiceModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const Pt = ({ children }) => <p style={{ margin: "0 0 8px", fontSize: 12, color: "#4A6B8A", lineHeight: 1.7 }}>{children}</p>;
  const Sec = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: teal, borderBottom: "1px solid rgba(27,58,92,0.15)", paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(74,140,196,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: goldText }}>Terms of Service</h2>
        </div>
        <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
          <Pt>⚖️ These Terms are currently under legal review. A full version will be available soon. The key points are summarised below.</Pt>
        </div>
        <Sec title="1. Acceptance">
          <Pt>By using Just Go Anywhere, you agree to these Terms. You must be 18 or older to use the Service.</Pt>
        </Sec>
        <Sec title="2. What We Do">
          <Pt>We are a travel discovery and planning platform. We match you with destinations, generate AI itineraries, and connect you to third-party booking providers. We are not a licensed travel agent, airline, or hotel operator.</Pt>
        </Sec>
        <Sec title="3. Pricing">
          <Pt>Just Go Anywhere is free to use. We do not charge service fees, booking fees, or subscription fees on the v1 platform. We earn affiliate commission when you book travel through our partner providers (such as Booking.com) — this is paid by the booking provider, not by you, and does not affect the price you see.</Pt>
        </Sec>
        <Sec title="4. Bookings & Payments">
          <Pt>Prices shown are estimates only. Actual prices are confirmed at the time of booking with third-party providers. Refunds for direct bookings follow our Cancellation Policy.</Pt>
        </Sec>
        <Sec title="5. AI-Generated Content">
          <Pt>Itineraries and recommendations are AI-generated for inspiration only. They may not be accurate or current. Verify all details independently before travel.</Pt>
        </Sec>
        <Sec title="6. Surprise Me">
          <Pt>The destination is selected algorithmically based on your preferences and cannot be changed after payment. You are responsible for ensuring you have the necessary travel documents for any destination.</Pt>
        </Sec>
        <Sec title="7. Liability">
          <Pt>Just Go Anywhere is not liable for travel disruptions, third-party provider issues, or decisions made based on AI-generated content. Our liability is limited to amounts paid to us in the preceding 12 months.</Pt>
        </Sec>
        <Sec title="8. Governing Law">
          <Pt>These Terms are governed by the laws of the State of North Carolina, United States.</Pt>
        </Sec>
        <Sec title="Questions?">
          <Pt>Email legal@justgoanywhere.com</Pt>
        </Sec>
      </div>
    </div>
  );
}

// ── Affiliate Disclosure Modal ──────────────────────────────────
function AffiliateDisclosureModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const Pt = ({ children }) => <p style={{ margin: "0 0 8px", fontSize: 12, color: "#4A6B8A", lineHeight: 1.7 }}>{children}</p>;
  const Sec = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: teal, borderBottom: "1px solid rgba(27,58,92,0.15)", paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );
  const Partner = ({ name, desc, id, status = "active" }) => (
    <div style={{ background: "rgba(27,58,92,0.030)", border: "1px solid rgba(27,58,92,0.072)", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#c0d0e0" }}>{name}</div>
        {status === "pending" && (
          <span style={{
            fontSize: 9, letterSpacing: 1.5, color: "#4A8CC4",
            background: "rgba(74,140,196,0.12)",
            border: "1px solid rgba(74,140,196,0.28)",
            padding: "2px 7px", borderRadius: 4,
            textTransform: "uppercase", fontWeight: 700,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}>
            Application in review
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.6 }}>{desc}</div>
      {id && <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4, fontFamily: "monospace" }}>Partner ID: {id}</div>}
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(74,140,196,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: goldText }}>Affiliate Disclosure</h2>
        </div>
        <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
          <Pt>⚖️ This disclosure is currently under legal review. A full version will be available soon. The key facts are summarised below.</Pt>
        </div>
        <Sec title="What This Means">
          <Pt>Just Go Anywhere participates in affiliate marketing programmes. When you click certain links and make a booking, we may earn a commission from the provider. This is disclosed in accordance with FTC guidelines (16 CFR Part 255).</Pt>
          <Pt>The price you pay is never affected by our affiliate relationship. Commissions are paid by the merchant, not by you.</Pt>
        </Sec>
        <Sec title="Our Affiliate Partners">
          <Partner name="Viator" desc="We earn a commission when you purchase a qualifying tour or activity through our Viator links." id="P00298368" status="active" />
          <Partner name="GetYourGuide" desc="We earn a commission when you complete a qualifying booking through our GetYourGuide links." id="QRF4136" status="active" />
          <Partner name="SafetyWing" desc="We earn a commission when you purchase travel insurance through our SafetyWing links." id="SW-PUBLISHER-ID-TBD" status="active" />
          {/* Booking.com — pending Booking.com program approval on CJ.
              CJ publisher ID: 7966903 (account-level; the Booking.com
              advertiser still needs to approve us before links go live).
              When approved: add id="7966903" and set status="active". */}
          <Partner name="Booking.com (via CJ)" desc="When approved, we'll earn a commission when you complete a qualifying hotel reservation through our Booking.com links." status="pending" />
          <Partner name="Babbel (via Impact)" desc="When approved, we'll earn a commission when you start a Babbel language-learning subscription through our links." status="pending" />
        </Sec>
        <Sec title="Editorial Independence">
          <Pt>Our affiliate relationships do not influence destination recommendations, spin results, or AI-generated itineraries. Recommendations are determined solely by your preferences and our algorithm.</Pt>
        </Sec>
        <Sec title="No Additional Cost">
          <Pt>You may opt out of affiliate links by visiting providers directly: viator.com, getyourguide.com, safetywing.com, booking.com, babbel.com.</Pt>
        </Sec>
        <Sec title="Questions?">
          <Pt>Email legal@justgoanywhere.com</Pt>
        </Sec>
      </div>
    </div>
  );
}

// ── Why Surprise Trips Popup (bottom sheet) ────────────────────
function WhySurprisePopup({ onBook, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  return (
    <div role="dialog" aria-modal="true" aria-label="Why surprise trips" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 300, animation: "slideUp 0.4s cubic-bezier(0.34,1.1,0.64,1)" }}>
      <style>{`@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <div style={{ background: darkMode ? "rgba(10,16,26,0.98)" : "#ffffff", borderTop: `2px solid rgba(27,58,92,0.35)`, borderRadius: "20px 20px 0 0", padding: "24px 24px 32px", maxWidth: 520, margin: "0 auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.3)" }}>
        {/* Handle bar */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.12)", margin: "0 auto 20px" }} />

        <div style={{ fontSize: 11, letterSpacing: 2, color: teal, textTransform: "uppercase", marginBottom: 8 }}>Still thinking?</div>
        <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 900, color: textPrimary, lineHeight: 1.3 }}>Why travelers love giving up control</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
          {[
            ["🧠", "You stop overthinking", "No more decision paralysis — someone else handles it all"],
            ["🌍", "You discover places you wouldn't pick", "Our guests consistently say it was their best trip ever"],
            ["✨", "It turns travel into an experience again", "The mystery makes the journey part of the adventure"],
          ].map(([emoji, title, desc]) => (
            <div key={title} style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 12, background: darkMode ? "rgba(27,58,92,0.05)" : "rgba(27,58,92,0.05)", border: `1px solid rgba(27,58,92,0.15)` }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{emoji}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={onBook} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          I'm Ready — Book My Surprise Trip →
        </button>
        <button onClick={onClose} style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${borderColor}`, background: "transparent", color: textDim, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          Keep browsing
        </button>
      </div>
    </div>
  );
}

// ── Exit Question Modal ─────────────────────────────────────────
// ── NotBookingModal — why didn't you book? ────────────────────
function NotBookingModal({ onClose, onAdjustBudget, onSeeTrips, onSeeGuarantee, onGoHome }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reasons = [
    {
      id: "price",
      emoji: "💰",
      label: "The price is too high",
      response: "We can tailor trips to tighter budgets — $800 or even $600 can unlock some great experiences.",
      cta: "Try a Lower Budget",
      action: onAdjustBudget,
      color: "#4A8CC4",
    },
    {
      id: "destination",
      emoji: "🗺️",
      label: "I want to choose my destination",
      response: "No problem — Take Me Anywhere lets you pick a region, vibe, and budget, and we'll show you 3 real matches.",
      cta: "Explore Destinations",
      action: onSeeTrips,
      color: "#4A8CC4",
    },
    {
      id: "trust",
      emoji: "🔒",
      label: "I'm not sure I trust it yet",
      response: "Totally fair. Every trip comes with a full price promise, a 24hr free cancel, and a destination swap guarantee.",
      cta: "See Our Guarantees",
      action: onSeeGuarantee,
      color: "#4A8CC4",
    },
    {
      id: "timing",
      emoji: "⏳",
      label: "Bad timing — maybe later",
      response: "Got it. Drop your email and we'll send you a hand-picked surprise destination when you're ready.",
      cta: null,
      color: "#4A8CC4",
    },
    {
      id: "confused",
      emoji: "❓",
      label: "I'm not sure how it works",
      response: "You set a budget → we curate flights + hotel → your destination stays sealed until after you book. No commitment until you see the full breakdown.",
      cta: null,
      color: "#0F2747",
    },
    {
      id: "other",
      emoji: "💬",
      label: "Something else",
      response: "Tell us what's on your mind — we read every message.",
      cta: null,
      color: "#4A6B8A",
    },
  ];

  const opt = reasons.find(r => r.id === selected);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(12px)", backdropFilter: "blur(12px)" }}>
      <div style={{
        background: darkMode ? "rgba(10,16,28,0.99)" : "#ffffff",
        border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: "24px 24px 0 0",
        padding: "28px 24px 36px",
        width: "100%",
        maxWidth: 520,
        animation: "slideUp 0.35s cubic-bezier(0.34,1.2,0.64,1)",
      }}>
        <style>{`@keyframes slideUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }`}</style>

        {/* Drag handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)", margin: "0 auto 22px" }} />

        {!submitted ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: "0 0 5px", fontSize: 19, fontWeight: 900, color: textPrimary }}>What held you back?</h3>
              <p style={{ margin: 0, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>Your feedback helps us improve — totally optional, but we'd love to know.</p>
            </div>

            {/* Reason grid */}
            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 18 }}>
              {reasons.map(r => (
                <button key={r.id} onClick={() => setSelected(r.id === selected ? null : r.id)}
                  style={{
                    padding: "12px 16px", borderRadius: 14, border: `1.5px solid ${selected === r.id ? r.color : (darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.07)")}`,
                    background: selected === r.id ? `${r.color}14` : (darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)"),
                    cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.18s",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{r.emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: selected === r.id ? 700 : 500, color: selected === r.id ? r.color : textPrimary, lineHeight: 1.3 }}>{r.label}</span>
                  {selected === r.id && <span style={{ marginLeft: "auto", fontSize: 14, color: r.color }}>✓</span>}
                </button>
              ))}
            </div>

            {/* Response panel */}
            {opt && (
              <div style={{
                background: darkMode ? `${opt.color}10` : `${opt.color}12`,
                border: `1px solid ${opt.color}30`,
                borderRadius: 14, padding: "14px 16px", marginBottom: 16,
                animation: "fadeIn 0.25s ease",
              }}>
                <p style={{ margin: "0 0 12px", fontSize: 13, color: textMuted, lineHeight: 1.65 }}>{opt.response}</p>
                {opt.id === "timing" || opt.id === "confused" || opt.id === "other" ? (
                  emailSent ? (
                    <div style={{ fontSize: 13, color: "#6dbd6d", fontWeight: 700 }}>✓ Thanks — we'll be in touch!</div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" type="email"
                        style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "#f5f5f5", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                      <button onClick={() => { if (email) setEmailSent(true); }}
                        style={{ padding: "9px 16px", borderRadius: 9, border: "none", background: opt.color === "#4A8CC4" ? "#4A8CC4" : opt.color, color: "#FAFAF5", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                        Send →
                      </button>
                    </div>
                  )
                ) : (
                  <button onClick={() => { opt.action?.(); onClose(); }}
                    style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", background: `linear-gradient(135deg,${opt.color},${opt.color}cc)`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                    {opt.cta} →
                  </button>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              {selected && !emailSent && (
                <button onClick={() => { setSubmitted(true); setTimeout(() => { onGoHome(); onClose(); }, 1800); }}
                  style={{ flex: 1, padding: "11px", borderRadius: 11, border: "none", background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)", color: textMuted, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Submit & go home
                </button>
              )}
              <button onClick={() => { onGoHome(); onClose(); }}
                style={{ flex: 1, padding: "11px", borderRadius: 11, border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`, background: "transparent", color: textDim, fontWeight: 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                ← Back to home
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🙏</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 900, color: textPrimary }}>Thanks for the feedback!</h3>
            <p style={{ margin: 0, fontSize: 13, color: textMuted }}>We'll use this to make Just Go Anywhere better. Taking you home…</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ExitQuestionModal({ onClose, onAdjustBudget, onSeeTrips, onSeeGuarantee }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const options = [
    { id: "price", emoji: "💰", label: "Too expensive", response: "We can work with lower budgets — want to try $800–$1,000 trips?", cta: "Adjust My Budget", action: onAdjustBudget },
    { id: "uncertain", emoji: "🤔", label: "Not sure where I'll end up", response: "We only select from top-rated destinations — want to see examples first?", cta: "See Sample Trips", action: onSeeTrips },
    { id: "trust", emoji: "😬", label: "Don't trust it yet", response: "Totally fair — here's exactly what's guaranteed in every trip", cta: "See Guarantee", action: onSeeGuarantee },
    { id: "browsing", emoji: "⏳", label: "Just browsing", response: "No pressure — want us to send you a surprise destination later?", cta: null },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.8)", WebkitBackdropFilter: "blur(10px)", backdropFilter: "blur(10px)" }}>
      <div role="dialog" aria-modal="true" aria-label="Sign in" style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff", border: `1px solid ${borderColor}`, borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 420, animation: "fadeIn 0.3s ease" }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 900, color: textPrimary }}>What's holding you back?</h3>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: textMuted }}>Help us help you — pick what applies.</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {options.map(o => (
            <button key={o.id} onClick={() => setSelected(o.id)}
              style={{ padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${selected === o.id ? teal : borderColor}`, background: selected === o.id ? "rgba(27,58,92,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.2s" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: selected === o.id ? teal : textPrimary }}>{o.emoji} {o.label}</span>
            </button>
          ))}
        </div>

        {selected && (() => {
          const opt = options.find(o => o.id === selected);
          return (
            <div style={{ background: darkMode ? "rgba(27,58,92,0.07)" : "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 12, padding: "14px", marginBottom: 14 }}>
              <p style={{ margin: "0 0 12px", fontSize: 13, color: textMuted, lineHeight: 1.6 }}>{opt.response}</p>
              {opt.id === "browsing" ? (
                emailSent ? (
                  <div style={{ fontSize: 13, color: "#6dbd6d", fontWeight: 700 }}>✓ We'll send you something special!</div>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" type="email"
                      style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                    <button onClick={() => { if (email) { setEmailSent(true); } }}
                      style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: teal, color: "#FAFAF5", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Send</button>
                  </div>
                )
              ) : (
                <button onClick={() => { opt.action?.(); onClose(); }}
                  style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  {opt.cta} →
                </button>
              )}
            </div>
          );
        })()}

        <button onClick={onClose} style={{ width: "100%", background: "none", border: "none", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: "6px 0" }}>
          Maybe later →
        </button>
      </div>
    </div>
  );
}

// ── Loyalty Programs — airlines + hotels ────────────────────────
// Top 3 of each are shown by default. Users can tap "Add more" to open a
// picker modal showing the rest, then tap to add to their visible list.
// Logos are simple emoji for now — when real logo SVGs land, swap the
// `logo` field for an SVG component.
const AIRLINE_LIST = [
  // Top 3 (default visible)
  { id: "american", name: "American Airlines", program: "AAdvantage", logo: "🇺🇸", color: "#c8102e", url: "https://www.aa.com/loyalty/enrollment/enroll", topDefault: true },
  { id: "delta",    name: "Delta Air Lines",   program: "SkyMiles",    logo: "🔷", color: "#003366", url: "https://www.delta.com/us/en/skymiles/overview", topDefault: true },
  { id: "united",   name: "United Airlines",   program: "MileagePlus", logo: "🌐", color: "#005daa", url: "https://www.united.com/en/us/fly/mileageplus.html", topDefault: true },
  // Additional (visible via "Add more")
  { id: "southwest", name: "Southwest Airlines", program: "Rapid Rewards", logo: "💛", color: "#f9b612", url: "https://www.southwest.com/account/enroll" },
  { id: "jetblue",   name: "JetBlue",            program: "TrueBlue",      logo: "🟦", color: "#003876", url: "https://www.jetblue.com/trueblue" },
  { id: "alaska",    name: "Alaska Airlines",    program: "Mileage Plan",  logo: "🏔️", color: "#01426a", url: "https://www.alaskaair.com/account/join" },
  { id: "hawaiian",  name: "Hawaiian Airlines",  program: "HawaiianMiles", logo: "🌺", color: "#9b1b30", url: "https://www.hawaiianairlines.com/hawaiianmiles" },
  { id: "frontier",  name: "Frontier Airlines",  program: "Miles",         logo: "🦅", color: "#00b251", url: "https://www.flyfrontier.com/miles/" },
  { id: "spirit",    name: "Spirit Airlines",    program: "Free Spirit",   logo: "💛", color: "#fbe201", url: "https://www.spirit.com/free-spirit" },
  { id: "aircanada", name: "Air Canada",         program: "Aeroplan",      logo: "🍁", color: "#d22630", url: "https://www.aircanada.com/ca/en/aco/home/aeroplan.html" },
  { id: "ba",        name: "British Airways",    program: "Executive Club", logo: "🇬🇧", color: "#075aaa", url: "https://www.britishairways.com/en-us/executive-club" },
  { id: "lufthansa", name: "Lufthansa",          program: "Miles & More",  logo: "🐦", color: "#05164d", url: "https://www.miles-and-more.com/" },
  { id: "afkl",      name: "Air France/KLM",     program: "Flying Blue",   logo: "✈️", color: "#002157", url: "https://www.flyingblue.com/" },
  { id: "emirates",  name: "Emirates",           program: "Skywards",      logo: "🌍", color: "#d71921", url: "https://www.emirates.com/us/english/skywards/" },
];

const HOTEL_LIST = [
  // Top 3 (default visible)
  { id: "marriott",  name: "Marriott",       program: "Bonvoy",        logo: "🏨", color: "#a02f49", url: "https://www.marriott.com/loyalty/createAccount/createAccountPage1.mi", topDefault: true },
  { id: "hilton",    name: "Hilton",         program: "Honors",        logo: "🛎️", color: "#10446e", url: "https://www.hilton.com/en/hilton-honors/join/", topDefault: true },
  { id: "ihg",       name: "IHG",            program: "One Rewards",   logo: "🌟", color: "#00529b", url: "https://www.ihg.com/onerewards/content/us/en/home", topDefault: true },
  // Additional (visible via "Add more")
  { id: "hyatt",     name: "Hyatt",          program: "World of Hyatt", logo: "🌐", color: "#02314a", url: "https://world.hyatt.com/content/gp/en/enrollment.html" },
  { id: "wyndham",   name: "Wyndham",        program: "Rewards",        logo: "🌅", color: "#0f3d80", url: "https://www.wyndhamhotels.com/wyndham-rewards" },
  { id: "choice",    name: "Choice Hotels",  program: "Privileges",     logo: "🏠", color: "#ee3124", url: "https://www.choicehotels.com/choice-privileges" },
  { id: "bestwestern", name: "Best Western", program: "Rewards",        logo: "🏨", color: "#003e7e", url: "https://www.bestwestern.com/en_US/rewards.html" },
  { id: "accor",     name: "Accor",          program: "Live Limitless", logo: "🇫🇷", color: "#1d1d1b", url: "https://all.accor.com/loyalty-program/index.en.shtml" },
  { id: "radisson",  name: "Radisson",       program: "Rewards",        logo: "✨", color: "#003a70", url: "https://www.radissonhotels.com/en-us/rewards" },
];

// ── Onboarding Modal ───────────────────────────────────────────
// ── Constellation chart foundation ──────────────────────────────
// Each of the 20 travel identities renders as a star chart whose star
// positions and sizes are deterministically derived from the identity's
// six attribute values. The system is data-first: tweaking an attribute
// or rebalancing the quiz updates every chart automatically.
//
// Six zones on a 200×140 viewBox, one per attribute. Within each zone,
// a star's position is interpolated from its attribute level (1-5).
// xDir/yDir tell the generator which direction higher levels push:
//   xDir < 0 = higher level → toward xMin (leftward)
//   xDir > 0 = higher level → toward xMax (rightward)
//   xDir = 0 = stay centered horizontally in the zone (vary y only)
//   yDir works the same on the vertical axis.
//
// Direction choices encode a small visual story (e.g. high energy
// rises and lifts off; luxurious comfort sits above rustic). Levels
// are assigned by lookup maps (IDENTITY_ENERGY_LVL, etc) defined just
// after TRAVEL_IDENTITIES, where every existing attribute string in
// our 20-identity catalog is enumerated.
const CHART_ZONES = {
  energy:   { xMin: 30,  xMax: 80,  yMin: 25,  yMax: 55,  xDir: -1, yDir: -1 }, // upper-left: high energy lifts off
  pace:     { xMin: 120, xMax: 170, yMin: 25,  yMax: 55,  xDir:  1, yDir: -1 }, // upper-right: fast pace races up & right
  planning: { xMin: 30,  xMax: 70,  yMin: 65,  yMax: 90,  xDir:  1, yDir:  0 }, // mid-left: curated pulls toward center
  food:     { xMin: 130, xMax: 170, yMin: 65,  yMax: 90,  xDir:  1, yDir:  0 }, // mid-right: adventurous pushes outward
  comfort:  { xMin: 40,  xMax: 80,  yMin: 95,  yMax: 120, xDir:  0, yDir: -1 }, // lower-left: luxury lifts up off the ground
  social:   { xMin: 120, xMax: 160, yMin: 95,  yMax: 120, xDir:  1, yDir:  0 }, // lower-right: crowded pushes outward
};

// Per-identity budget guesstimate for Explore pre-fill. Used when a user
// taps "Browse my matches" from the identity banner — gives them a starting
// budget that aligns with their identity's vibe instead of defaulting to a
// flat $2000. Numbers tuned to typical comfort/pace profiles; users adjust
// from there. Added Jun 19, 2026 per Cas's call.
const IDENTITY_BUDGET_GUESS = {
  "Romantic Wanderer":       3500,  // boutique hotels, slow & savoring
  "Controlled Chaos Traveler": 2500, // boutique + local, flexible
  "Culinary Explorer":       3000,  // food trips, mid-range
  "Luxury Escapist":         6000,  // 5-star
  "Adrenaline Collector":    2500,  // adventure-focused
  "Soft-Life Traveler":      4000,  // wellness/leisure-oriented
  "Cultural Nomad":          2500,  // museums + mid-range stays
  "Spontaneous Minimalist":  1800,  // travels light, low overhead
  "Off-Grid Dreamer":        1800,  // nature/basic accommodations
  "Sunset Chaser":           2800,  // beach + scenic
  "Digital Nomad":           2200,  // long stays, modest spend
  "Heritage Seeker":         2500,  // family/roots travel
  "Eco Wanderer":            2500,  // sustainable mid-range
  "Festival Hunter":         2500,  // event-driven, varied
  "Photo Pilgrim":           2800,  // scenic locations
  "Wellness Pilgrim":        3500,  // retreats often premium
  "Bookworm Wanderer":       2200,  // quiet, mid-range
  "Family Architect":        4000,  // family-friendly stays
  "Solo Soul-Searcher":      2200,  // introspective, mid-range
  "Night Owl Nomad":         2800,  // nightlife/city focus
};
function getIdentityBudgetGuess(name) {
  return IDENTITY_BUDGET_GUESS[name] || 2500;
}
const STAR_ORDER = ["energy", "pace", "food", "social", "comfort", "planning"];

// Decorative background stars — same fixed set for every identity to
// keep the "sky" consistent across charts. Don't randomize per-identity.
const BG_STARS = [
  { x: 15,  y: 25,  r: 0.5, o: 0.4 },
  { x: 180, y: 15,  r: 0.7, o: 0.5 },
  { x: 195, y: 60,  r: 0.4, o: 0.3 },
  { x: 160, y: 125, r: 0.5, o: 0.4 },
  { x: 30,  y: 115, r: 0.5, o: 0.4 },
  { x: 8,   y: 75,  r: 0.5, o: 0.4 },
  { x: 55,  y: 8,   r: 0.3, o: 0.3 },
  { x: 125, y: 130, r: 0.4, o: 0.3 },
  { x: 100, y: 5,   r: 0.4, o: 0.35 },
  { x: 90,  y: 138, r: 0.4, o: 0.35 },
];

// Canonical ordering reference for the 20 travel identities. Previously used
// for "01 / 20" catalog numbering on identity cards (dashboard tile, modal,
// onboarding reveal, share cards) — those displays were removed per Cas's
// direction. Map kept for ordering reference and in case the catalog count
// becomes useful again later.
const IDENTITY_NUMBER = {
  "Romantic Wanderer":      "01", "Controlled Chaos Traveler": "02",
  "Culinary Explorer":      "03", "Luxury Escapist":           "04",
  "Adrenaline Collector":   "05", "Soft-Life Traveler":        "06",
  "Cultural Nomad":         "07", "Spontaneous Minimalist":    "08",
  "Off-Grid Dreamer":       "09", "Sunset Chaser":             "10",
  "Digital Nomad":          "11", "Heritage Seeker":           "12",
  "Eco Wanderer":           "13", "Festival Hunter":           "14",
  "Photo Pilgrim":          "15", "Wellness Pilgrim":          "16",
  "Bookworm Wanderer":      "17", "Family Architect":          "18",
  "Solo Soul-Searcher":     "19", "Night Owl Nomad":           "20",
};

// Per-identity night sky — radial gradient stops + line accent color.
// Origins vary per quarter of the list to keep compositional variety.
// ── Sky tiers (Revision 1) ──────────────────────────────────────
// Three tiers from deep-night to twilight. Never lighter than twilight —
// past it, white stars stop reading on the lighter ground. Tier 3 uses a
// PAIRED adjustment: lighter sky requires brighter stars + brighter lines,
// applied together. starFill/starGlow/lineOpacity are read by the renderer;
// lineLighten=true tells it to use the brighter accent variant for lines.
const SKY_TIERS = {
  deepNight: { starFill: "#FAFAF5", starGlow: 0.18, lineOpacity: 0.7,  lineLighten: false },
  dusk:      { starFill: "#FAFAF5", starGlow: 0.18, lineOpacity: 0.7,  lineLighten: false },
  twilight:  { starFill: "#FFFDF8", starGlow: 0.2,  lineOpacity: 0.75, lineLighten: true  },
};

const IDENTITY_SKY = {
  "Romantic Wanderer":         { bright: "#2a1828", mid: "#0f0a14", dark: "#050308", accent: "#4A8CC4", origin: "30% 20%", tier: "dusk" },
  "Controlled Chaos Traveler": { bright: "#2d1810", mid: "#170c08", dark: "#080403", accent: "#D89070", origin: "30% 20%", tier: "dusk" },
  "Culinary Explorer":         { bright: "#2e1610", mid: "#190a07", dark: "#090403", accent: "#E89070", origin: "30% 20%", tier: "dusk" },
  "Luxury Escapist":           { bright: "#231a14", mid: "#13100a", dark: "#070604", accent: "#C9B098", origin: "30% 20%", tier: "dusk" },
  "Adrenaline Collector":      { bright: "#2a1410", mid: "#150806", dark: "#080302", accent: "#E07A55", origin: "30% 20%", tier: "deepNight" },
  "Soft-Life Traveler":        { bright: "#46383f", mid: "#2e2429", dark: "#1a1014", accent: "#D5AAB8", origin: "70% 30%", tier: "twilight" },
  "Cultural Nomad":            { bright: "#26200c", mid: "#141005", dark: "#080603", accent: "#D9B870", origin: "70% 30%", tier: "dusk" },
  "Spontaneous Minimalist":    { bright: "#3a4636", mid: "#262e23", dark: "#141a12", accent: "#A8C49A", origin: "70% 30%", tier: "twilight" },
  "Off-Grid Dreamer":          { bright: "#0f2218", mid: "#061310", dark: "#020805", accent: "#A8C49A", origin: "70% 30%", tier: "deepNight" },
  "Sunset Chaser":             { bright: "#46383f", mid: "#2e2429", dark: "#1a1014", accent: "#E8A088", origin: "70% 30%", tier: "twilight" },
  "Digital Nomad":             { bright: "#0e1c22", mid: "#070f12", dark: "#030608", accent: "#90B5C0", origin: "50% 80%", tier: "dusk" },
  "Heritage Seeker":           { bright: "#1f1810", mid: "#100c08", dark: "#060403", accent: "#C0A078", origin: "50% 80%", tier: "dusk" },
  "Eco Wanderer":              { bright: "#33423a", mid: "#212e27", dark: "#101a15", accent: "#90C5A8", origin: "40% 75%", tier: "twilight" },
  "Festival Hunter":           { bright: "#28120e", mid: "#140807", dark: "#080302", accent: "#E8705C", origin: "50% 80%", tier: "dusk" },
  "Photo Pilgrim":             { bright: "#161224", mid: "#0b0912", dark: "#050306", accent: "#A89AC4", origin: "50% 80%", tier: "deepNight" },
  "Wellness Pilgrim":          { bright: "#3d2d3a", mid: "#2a1f2e", dark: "#1a1420", accent: "#C9C0E0", origin: "30% 70%", tier: "twilight" },
  "Bookworm Wanderer":         { bright: "#1f1410", mid: "#100a08", dark: "#060403", accent: "#C09070", origin: "30% 70%", tier: "dusk" },
  "Family Architect":          { bright: "#463f30", mid: "#2e2920", dark: "#1a1410", accent: "#E8C088", origin: "30% 70%", tier: "twilight" },
  "Solo Soul-Searcher":        { bright: "#141020", mid: "#0a080f", dark: "#040305", accent: "#A89AC4", origin: "30% 70%", tier: "deepNight" },
  "Night Owl Nomad":           { bright: "#1a0f2e", mid: "#0a0612", dark: "#030208", accent: "#A89AC4", origin: "30% 70%", tier: "deepNight" },
};

// Returns a CSS radial-gradient string for the given identity's sky.
function getIdentitySky(name) {
  const sky = IDENTITY_SKY[name] || IDENTITY_SKY["Romantic Wanderer"];
  return `radial-gradient(ellipse at ${sky.origin}, ${sky.bright} 0%, ${sky.mid} 55%, ${sky.dark} 100%)`;
}

// Lighten a #RRGGBB hex toward white by `amt` (0–1). Used for the twilight
// tier's brighter line accent so lines don't sink into the lighter sky.
function _lightenHex(hex, amt = 0.12) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const mix = (c) => Math.round(c + (255 - c) * amt);
    const to2 = (n) => n.toString(16).padStart(2, "0");
    return `#${to2(mix(r))}${to2(mix(g))}${to2(mix(b))}`;
  } catch { return hex; }
}

// ── Travel Identity Data ────────────────────────────────────────
const TRAVEL_IDENTITIES = {
  "Romantic Wanderer": {
    emoji: "💑", svgIcon: (size=48, color="#A05568") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Romantic Wanderer">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 32 C22 32 12 25 12 18 C12 14.5 15 12 18 12 C20 12 21.5 13 22 14 C22.5 13 24 12 26 12 C29 12 32 14.5 32 18 C32 25 22 32 22 32Z" stroke={color} strokeWidth="1.8" fill={`${color}30`} strokeLinejoin="round"/>
      </svg>
    ), color: "#A05568", gradient: "linear-gradient(90deg, #A05568 0%, #A05568 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Relaxed", planning: "Curated", pace: "Slow & savoring",
    food: "Intimate & local", comfort: "Boutique hotels", social: "Couple-focused",
    tripLength: "7–10 days",
    desc: "You travel to connect — with your partner, with beauty, with moments that linger long after you're home.",
    vibes: ["romance","food","wellness","beach"],
    modes: ["surprise","explore"],
  },
  "Controlled Chaos Traveler": {
    emoji: "🌀", svgIcon: (size=48, color="#A85A3C") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Controlled Chaos Traveler">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 10 C28 10 33 15 33 22 C33 26 30 29 27 29 C24 29 22 27 22 24 C22 21 24 19 26 19" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <path d="M22 34 C16 34 11 29 11 22 C11 18 14 15 17 15 C20 15 22 17 22 20" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <circle cx="22" cy="22" r="2.5" fill={color}/>
      </svg>
    ), color: "#A85A3C", gradient: "linear-gradient(90deg, #A85A3C 0%, #A85A3C 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "High", planning: "Flexible", pace: "Fast-moving",
    food: "Adventurous", comfort: "Boutique + local", social: "Exploratory",
    tripLength: "6–9 days",
    desc: "You have a rough plan and zero attachment to it. Half the best stories start with 'we got lost and then...'",
    vibes: ["adventure","culture","food","nightlife"],
    modes: ["surprise","explore"],
  },
  "Culinary Explorer": {
    emoji: "🍜", svgIcon: (size=48, color="#A8682E") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Culinary Explorer">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M14 20 Q14 14 22 14 Q30 14 30 20" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <line x1="22" y1="14" x2="22" y2="11" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="12" y="20" width="20" height="3" rx="1.5" fill={color} opacity="0.7"/>
        <line x1="22" y1="23" x2="22" y2="33" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="18" y1="33" x2="26" y2="33" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ), color: "#A8682E", gradient: "linear-gradient(90deg, #A8682E 0%, #A8682E 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Moderate", planning: "Researched", pace: "Destination-driven",
    food: "Obsessively curious", comfort: "Characterful stays", social: "Food-forward",
    tripLength: "5–8 days",
    desc: "Your itinerary is built around restaurants, markets, and that one dish you saw on Instagram six months ago.",
    vibes: ["food","culture","wellness"],
    modes: ["explore","trending"],
  },
  "Luxury Escapist": {
    emoji: "✨", svgIcon: (size=48, color="#A8853B") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Luxury Escapist">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 10 L24 18 L32 18 L26 24 L28 32 L22 27 L16 32 L18 24 L12 18 L20 18 Z" stroke={color} strokeWidth="1.6" fill={`${color}35`} strokeLinejoin="round"/>
      </svg>
    ), color: "#A8853B", gradient: "linear-gradient(90deg, #A8853B 0%, #A8853B 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Relaxed", planning: "Handled for me", pace: "Unhurried",
    food: "Exceptional", comfort: "5-star always", social: "Private & curated",
    tripLength: "5–10 days",
    desc: "You work hard. You travel harder — but softer. Every detail should be perfect, because you earned it.",
    vibes: ["luxury","wellness","beach","romance"],
    modes: ["surprise","explore"],
  },
  "Adrenaline Collector": {
    emoji: "🧗", svgIcon: (size=48, color="#1B3A5C") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Adrenaline Collector">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <polyline points="14,32 20,20 26,26 32,12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <path d="M28,10 L32,12 L30,16" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="17" cy="28" r="2" fill={color} opacity="0.6"/>
      </svg>
    ), color: "#1B3A5C", gradient: "linear-gradient(90deg, #1B3A5C 0%, #1B3A5C 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Extreme", planning: "Activity-first", pace: "Non-stop",
    food: "Fuel, mostly", comfort: "Functional", social: "Group or solo",
    tripLength: "7–14 days",
    desc: "You collect experiences the way others collect souvenirs. If your heart rate didn't spike, was it even a vacation?",
    vibes: ["adventure","nature","beach"],
    modes: ["explore","trending"],
  },
  "Soft-Life Traveler": {
    emoji: "🌸", svgIcon: (size=48, color="#B8927A") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Soft-Life Traveler">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <circle cx="22" cy="22" r="4" fill={`${color}60`} stroke={color} strokeWidth="1.4"/>
        {[0,1,2,3,4].map(i => {
          const a = (i * 72 - 90) * Math.PI / 180;
          return <ellipse key={i} cx={22 + 8 * Math.cos(a)} cy={22 + 8 * Math.sin(a)} rx="4" ry="2.5" fill={`${color}40`} stroke={color} strokeWidth="1.2" transform={`rotate(${i*72-90} ${22 + 8*Math.cos(a)} ${22 + 8*Math.sin(a)})`}/>;
        })}
      </svg>
    ), color: "#B8927A", gradient: "linear-gradient(90deg, #B8927A 0%, #B8927A 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Low", planning: "Minimal", pace: "Wherever the day takes me",
    food: "Pretty & delicious", comfort: "Beautiful spaces", social: "Peaceful",
    tripLength: "5–7 days",
    desc: "Linen. Pool. Book. Overpriced smoothie. Absolutely no agenda. This is the life.",
    vibes: ["wellness","beach","romance","luxury"],
    modes: ["surprise","affordable"],
  },
  "Cultural Nomad": {
    emoji: "🏛️", svgIcon: (size=48, color="#80582A") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Cultural Nomad">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <line x1="12" y1="32" x2="32" y2="32" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="12" y1="18" x2="32" y2="18" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="10" y="14" width="24" height="4" rx="1" fill={`${color}30`} stroke={color} strokeWidth="1.4"/>
        <line x1="16" y1="18" x2="16" y2="32" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
        <line x1="22" y1="18" x2="22" y2="32" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
        <line x1="28" y1="18" x2="28" y2="32" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ), color: "#80582A", gradient: "linear-gradient(90deg, #80582A 0%, #80582A 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Moderate", planning: "Deep research", pace: "Deliberate",
    food: "Authentic & local", comfort: "Immersive stays", social: "Curious & open",
    tripLength: "10–21 days",
    desc: "Museums, temples, street art, conversations with strangers. You don't visit places — you study them.",
    vibes: ["culture","history","food","art"],
    modes: ["explore","trending"],
  },
  "Spontaneous Minimalist": {
    emoji: "🎒", svgIcon: (size=48, color="#8B6B5C") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Spontaneous Minimalist">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <rect x="15" y="16" width="14" height="17" rx="3" fill={`${color}25`} stroke={color} strokeWidth="1.6"/>
        <path d="M18 16 C18 13 26 13 26 16" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
        <line x1="15" y1="22" x2="29" y2="22" stroke={color} strokeWidth="1.4" opacity="0.6"/>
        <line x1="22" y1="25" x2="22" y2="29" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
      </svg>
    ), color: "#8B6B5C", gradient: "linear-gradient(90deg, #8B6B5C 0%, #8B6B5C 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Moderate", planning: "None preferred", pace: "Go with the flow",
    food: "Street food queen/king", comfort: "Clean & simple", social: "Meet anyone",
    tripLength: "Indefinite",
    desc: "One bag. No plan. Just a flight and a feeling. The destination chooses you as much as you choose it.",
    vibes: ["adventure","culture","food"],
    modes: ["surprise","affordable"],
  },
  "Off-Grid Dreamer": {
    emoji: "🌿", svgIcon: (size=48, color="#7A8467") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Off-Grid Dreamer">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 32 C22 32 22 22 22 18" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M22 22 C22 22 16 18 14 13 C17 12 21 14 22 18" stroke={color} strokeWidth="1.6" fill={`${color}30`} strokeLinejoin="round"/>
        <path d="M22 18 C22 18 28 14 30 9 C27 8 23 10 22 14" stroke={color} strokeWidth="1.6" fill={`${color}30`} strokeLinejoin="round"/>
      </svg>
    ), color: "#7A8467", gradient: "linear-gradient(90deg, #7A8467 0%, #7A8467 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Quiet", planning: "Researched escape", pace: "Nature's pace",
    food: "Local & simple", comfort: "Eco stays & lodges", social: "Small & meaningful",
    tripLength: "7–14 days",
    desc: "No Wi-Fi. No crowds. Just sky, silence, and the profound relief of being unreachable.",
    vibes: ["nature","wellness","adventure"],
    modes: ["explore","affordable"],
  },
  "Sunset Chaser": {
    emoji: "🌅", svgIcon: (size=48, color="#B97543") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Sunset Chaser">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M12 28 Q22 14 32 28" stroke={color} strokeWidth="1.8" fill={`${color}25`} strokeLinecap="round"/>
        <line x1="10" y1="28" x2="34" y2="28" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="22" y1="12" x2="22" y2="9" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
        <line x1="29" y1="14" x2="31" y2="12" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
        <line x1="15" y1="14" x2="13" y2="12" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
      </svg>
    ), color: "#B97543", gradient: "linear-gradient(90deg, #B97543 0%, #B97543 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Gentle", planning: "Spontaneous", pace: "Golden hour-guided",
    food: "Rooftop & waterfront", comfort: "Views over everything", social: "Romantic or solo",
    tripLength: "5–8 days",
    desc: "Every trip is planned around a perfect sunset. The destination is secondary to the light.",
    vibes: ["beach","romance","wellness","nature"],
    modes: ["surprise","explore"],
  },
  "Digital Nomad": {
    emoji: "💻", svgIcon: (size=48, color="#6B5582") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Digital Nomad">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <rect x="13" y="15" width="18" height="12" rx="1.5" stroke={color} strokeWidth="1.7" fill={`${color}30`}/>
        <line x1="11" y1="29" x2="33" y2="29" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        <circle cx="29" cy="13" r="3" stroke={color} strokeWidth="1.5" fill={`${color}40`}/>
        <path d="M27.5 13 L 28.7 14 L 30.5 12.2" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
      </svg>
    ), color: "#6B5582", gradient: "linear-gradient(90deg, #6B5582 0%, #6B5582 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Steady", planning: "Long-term", pace: "Live, then explore",
    food: "Cafe-friendly", comfort: "Wifi + workspace", social: "Coworking communities",
    tripLength: "2–4 weeks",
    desc: "Your laptop comes everywhere. Mornings are meetings, afternoons are markets, weekends are adventures.",
    vibes: ["culture","food","wellness","city"],
    modes: ["explore","affordable"],
  },
  "Heritage Seeker": {
    emoji: "🧬", svgIcon: (size=48, color="#6B4423") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Heritage Seeker">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 11 L22 33" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <ellipse cx="22" cy="14" rx="5" ry="2" stroke={color} strokeWidth="1.5" fill="none"/>
        <ellipse cx="22" cy="22" rx="7" ry="2.5" stroke={color} strokeWidth="1.5" fill={`${color}25`}/>
        <ellipse cx="22" cy="30" rx="5" ry="2" stroke={color} strokeWidth="1.5" fill="none"/>
        <circle cx="17" cy="14" r="1.2" fill={color}/>
        <circle cx="27" cy="22" r="1.4" fill={color}/>
        <circle cx="17" cy="30" r="1.2" fill={color}/>
      </svg>
    ), color: "#6B4423", gradient: "linear-gradient(90deg, #6B4423 0%, #6B4423 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Reflective", planning: "Deeply researched", pace: "Slow & intentional",
    food: "Ancestral & authentic", comfort: "Family-run stays", social: "Multi-generational",
    tripLength: "8–14 days",
    desc: "You travel to roots — your family's, your faith's, your culture's. Every meal is a memory you didn't know you had.",
    vibes: ["culture","food","family","spiritual"],
    modes: ["explore"],
  },
  "Eco Wanderer": {
    emoji: "🌱", svgIcon: (size=48, color="#6B7349") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Eco Wanderer">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 33 L22 22" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        <path d="M22 22 C 16 22 12 18 12 12 C 18 12 22 16 22 22 Z" stroke={color} strokeWidth="1.6" fill={`${color}35`} strokeLinejoin="round"/>
        <path d="M22 22 C 28 22 32 18 32 12 C 26 12 22 16 22 22 Z" stroke={color} strokeWidth="1.6" fill={`${color}25`} strokeLinejoin="round"/>
      </svg>
    ), color: "#6B7349", gradient: "linear-gradient(90deg, #6B7349 0%, #6B7349 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Mindful", planning: "Ethical", pace: "Considered",
    food: "Local & seasonal", comfort: "Eco-lodges", social: "Conscious community",
    tripLength: "7–14 days",
    desc: "You read the carbon offset before you book the flight. Slow travel, low impact, deep connection.",
    vibes: ["nature","wellness","culture","food"],
    modes: ["explore","affordable"],
  },
  "Festival Hunter": {
    emoji: "🎪", svgIcon: (size=48, color="#C25E47") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Festival Hunter">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M22 11 L 13 28 L 31 28 Z" stroke={color} strokeWidth="1.7" fill={`${color}35`} strokeLinejoin="round"/>
        <path d="M22 11 L 22 28" stroke={color} strokeWidth="1.4"/>
        <path d="M17 19 L 27 19" stroke={color} strokeWidth="1.3" opacity="0.7"/>
        <line x1="11" y1="28" x2="33" y2="28" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        <circle cx="22" cy="11" r="1.5" fill={color}/>
        <path d="M9 16 L 11 14 M35 16 L 33 14" stroke={color} strokeWidth="1.3" strokeLinecap="round" opacity="0.6"/>
      </svg>
    ), color: "#C25E47", gradient: "linear-gradient(90deg, #C25E47 0%, #C25E47 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "High & social", planning: "Calendar-driven", pace: "Event-anchored",
    food: "Festival fare", comfort: "Camp or hostel", social: "Loud & joyful",
    tripLength: "3–7 days",
    desc: "Coachella. Carnival. Glastonbury. Burning Man. Your trips orbit the world's biggest gatherings.",
    vibes: ["nightlife","culture","music","adventure"],
    modes: ["trending","explore"],
  },
  "Photo Pilgrim": {
    emoji: "📷", svgIcon: (size=48, color="#6B4470") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Photo Pilgrim">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <rect x="11" y="15" width="22" height="14" rx="2" stroke={color} strokeWidth="1.7" fill={`${color}30`}/>
        <rect x="17" y="13" width="8" height="3" rx="0.8" stroke={color} strokeWidth="1.5" fill={`${color}50`}/>
        <circle cx="22" cy="22" r="4.5" stroke={color} strokeWidth="1.6" fill={`${color}40`}/>
        <circle cx="22" cy="22" r="2" fill={color}/>
        <circle cx="29" cy="18" r="0.9" fill={color}/>
      </svg>
    ), color: "#6B4470", gradient: "linear-gradient(90deg, #6B4470 0%, #6B4470 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Visual", planning: "Location-first", pace: "Light-driven",
    food: "Photogenic", comfort: "Aesthetic stays", social: "Curated company",
    tripLength: "5–10 days",
    desc: "You scout your shots before you book the flight. Every destination is a frame waiting to be filled.",
    vibes: ["nature","beach","culture","romance"],
    modes: ["trending","surprise"],
  },
  "Wellness Pilgrim": {
    emoji: "🧘", svgIcon: (size=48, color="#8FA084") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Wellness Pilgrim">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <circle cx="22" cy="15" r="2.5" stroke={color} strokeWidth="1.6" fill={`${color}40`}/>
        <path d="M16 28 C 16 23, 19 20, 22 20 C 25 20, 28 23, 28 28" stroke={color} strokeWidth="1.6" fill={`${color}25`} strokeLinejoin="round"/>
        <path d="M14 30 C 17 28, 19 28, 22 30 C 25 28, 27 28, 30 30" stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round"/>
      </svg>
    ), color: "#8FA084", gradient: "linear-gradient(90deg, #8FA084 0%, #8FA084 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Restorative", planning: "Retreat-based", pace: "Slow & mindful",
    food: "Plant-forward & clean", comfort: "Spa & retreat", social: "Quiet kinship",
    tripLength: "5–10 days",
    desc: "You travel to come back lighter. Yoga at sunrise, silent dinners, hot springs at dusk.",
    vibes: ["wellness","nature","spiritual","beach"],
    modes: ["explore","surprise"],
  },
  "Bookworm Wanderer": {
    emoji: "📚", svgIcon: (size=48, color="#5C3A1F") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Bookworm Wanderer">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M11 14 L 22 12 L 33 14 L 33 30 L 22 28 L 11 30 Z" stroke={color} strokeWidth="1.7" fill={`${color}30`} strokeLinejoin="round"/>
        <line x1="22" y1="12" x2="22" y2="28" stroke={color} strokeWidth="1.5"/>
        <line x1="14" y1="18" x2="20" y2="17" stroke={color} strokeWidth="1.2" opacity="0.7"/>
        <line x1="14" y1="22" x2="20" y2="21" stroke={color} strokeWidth="1.2" opacity="0.7"/>
        <line x1="24" y1="17" x2="30" y2="18" stroke={color} strokeWidth="1.2" opacity="0.7"/>
        <line x1="24" y1="21" x2="30" y2="22" stroke={color} strokeWidth="1.2" opacity="0.7"/>
      </svg>
    ), color: "#5C3A1F", gradient: "linear-gradient(90deg, #5C3A1F 0%, #5C3A1F 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Quiet", planning: "Story-driven", pace: "Pages-per-day",
    food: "Cafe with a window seat", comfort: "Quiet inns", social: "Mostly alone, gladly",
    tripLength: "5–10 days",
    desc: "Your itinerary is built around bookshops, libraries, and the cafe where you'll finish the novel.",
    vibes: ["culture","wellness","food"],
    modes: ["explore","surprise"],
  },
  "Family Architect": {
    emoji: "👨‍👩‍👧", svgIcon: (size=48, color="#A8753C") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Family Architect">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <circle cx="14" cy="16" r="2.5" stroke={color} strokeWidth="1.5" fill="none"/>
        <circle cx="30" cy="16" r="2.5" stroke={color} strokeWidth="1.5" fill="none"/>
        <circle cx="22" cy="24" r="2" stroke={color} strokeWidth="1.4" fill="none"/>
        <path d="M9 28 C 9 23, 12 21, 14 21 C 15.5 21, 16.5 21.6, 17 22.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
        <path d="M35 28 C 35 23, 32 21, 30 21 C 28.5 21, 27.5 21.6, 27 22.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
        <path d="M17 33 C 17 29.5, 19 28, 22 28 C 25 28, 27 29.5, 27 33" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
      </svg>
    ), color: "#A8753C", gradient: "linear-gradient(90deg, #A8753C 0%, #A8753C 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Joyful chaos", planning: "Logistics-heavy", pace: "Kid-paced",
    food: "Family-friendly + fun", comfort: "Suites & apartments", social: "Multi-gen",
    tripLength: "5–10 days",
    desc: "You're building memories your kids will tell their kids. The trip is the gift.",
    vibes: ["beach","family","nature","culture"],
    modes: ["explore","affordable"],
  },
  "Solo Soul-Searcher": {
    emoji: "🪞", svgIcon: (size=48, color="#4A3868") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Solo Soul-Searcher">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <ellipse cx="22" cy="22" rx="7" ry="11" stroke={color} strokeWidth="1.6" fill={`${color}25`}/>
        <ellipse cx="22" cy="22" rx="3.5" ry="7" stroke={color} strokeWidth="1.3" fill="none" opacity="0.6"/>
        <circle cx="22" cy="22" r="1.5" fill={color}/>
        <path d="M22 11 L22 8 M22 36 L22 33" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ), color: "#4A3868", gradient: "linear-gradient(90deg, #4A3868 0%, #4A3868 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "Introspective", planning: "Loose framework", pace: "Listen to yourself",
    food: "Mindful & solo", comfort: "Quiet rooms", social: "Strangers turned friends",
    tripLength: "10–21 days",
    desc: "You travel to know yourself. Long walks, journals, sunrises you watched alone and won't forget.",
    vibes: ["wellness","nature","spiritual","culture"],
    modes: ["surprise","explore"],
  },
  "Night Owl Nomad": {
    emoji: "🌙", svgIcon: (size=48, color="#3D3B5C") => (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-label="Night Owl Nomad">
        <circle cx="22" cy="22" r="19" fill={`${color}22`} stroke={color} strokeWidth="1.8"/>
        <path d="M30 22 A 8 8 0 1 1 22 14 A 6 6 0 0 0 30 22 Z" stroke={color} strokeWidth="1.7" fill={`${color}40`} strokeLinejoin="round"/>
        <circle cx="14" cy="13" r="0.8" fill={color}/>
        <circle cx="32" cy="11" r="0.8" fill={color}/>
        <circle cx="11" cy="20" r="0.6" fill={color} opacity="0.6"/>
        <circle cx="34" cy="29" r="0.7" fill={color} opacity="0.7"/>
      </svg>
    ), color: "#3D3B5C", gradient: "linear-gradient(90deg, #3D3B5C 0%, #3D3B5C 40%, #FAFAF5 65%, #F0EDE3 100%)",
    energy: "After-dark", planning: "Bar map ready", pace: "Sleeps when sun is up",
    food: "Late-night eats", comfort: "City center hotels", social: "Strangers at 2am",
    tripLength: "4–7 days",
    desc: "Your trips begin when most travelers go to bed. Rooftops, jazz clubs, neon-lit alleys.",
    vibes: ["nightlife","culture","food","city"],
    modes: ["trending","explore"],
  },
};

// ─── Identity-aware copy ──────────────────────────────────────────
// One copy set per identity, applied across the dashboard greeting,
// section headers, and empty states. Voice: direct, specific to each
// identity's actual energy + vibes, no corporate-speak. Empty states
// frame as invitations, not errors. CTAs are concrete actions.
const IDENTITY_COPY = {
  "Romantic Wanderer": {
    greetingHeadline: "Welcome back, Romantic Wanderer.",
    greetingSubline: "Your next trip is somewhere slow, soft, and quietly perfect.",
    greetingCta: "Find your next escape",
    moodMatchHeader: "Tuned for the Romantic Wanderer",
    affordableHeader: "Romantic escapes that don't break the bank",
    postcardsEmpty: "Your collection is waiting for its first sunset. Book a trip — the postcard arrives with it.",
    stampsEmpty: "Romantic Wanderers tend to start with somewhere they've been daydreaming about. Mark it as someday.",
    savedEmpty: "No saved trips yet. When something feels right — Tuscany at golden hour, a villa with a view — save it here.",
    detourHeader: "Step out of the slow lane — not your usual, but you might love it.",
  },
  "Controlled Chaos Traveler": {
    greetingHeadline: "Welcome back, Controlled Chaos Traveler.",
    greetingSubline: "Loose plan, open mind. Let's see what you stumble into.",
    greetingCta: "Wing it",
    moodMatchHeader: "Tuned for Controlled Chaos",
    affordableHeader: "Chaos-friendly trips that won't blow the budget",
    postcardsEmpty: "First postcard's still out there somewhere. Book the trip you weren't expecting.",
    stampsEmpty: "Stamp something you've been circling. The best trips never start with a plan anyway.",
    savedEmpty: "Nothing saved yet — which is on-brand. When you find a wild card, park it here.",
    detourHeader: "A different direction — even chaos has its blind spots.",
  },
  "Culinary Explorer": {
    greetingHeadline: "Welcome back, Culinary Explorer.",
    greetingSubline: "Somewhere's serving dinner that'll ruin you for everything else. Let's find it.",
    greetingCta: "Find your next bite",
    moodMatchHeader: "Tuned for the Culinary Explorer",
    affordableHeader: "Food-first trips that fit the budget",
    postcardsEmpty: "Your collection starts with the first market you can't stop thinking about. Book and it lands here.",
    stampsEmpty: "Mark the city whose food has lived rent-free in your head. Someday it's a trip.",
    savedEmpty: "No saved trips yet. When the menu pulls you in — save it before you forget where it was.",
    detourHeader: "A different direction — sometimes the best meal isn't where you'd expect.",
  },
  "Luxury Escapist": {
    greetingHeadline: "Welcome back, Luxury Escapist.",
    greetingSubline: "You've earned this. Let's find somewhere that earns you.",
    greetingCta: "Find your escape",
    moodMatchHeader: "Tuned for the Luxury Escapist",
    affordableHeader: "Quiet luxury that doesn't punish the budget",
    postcardsEmpty: "Your collection is waiting for its first poolside afternoon. Book a stay — the postcard's part of it.",
    stampsEmpty: "Mark the resort, the city, the view you've been promising yourself. Someday's closer than you think.",
    savedEmpty: "Nothing saved yet. When the right suite shows up, keep it close — return when the moment's right.",
    detourHeader: "Off the velvet rope — a different direction worth considering.",
  },
  "Adrenaline Collector": {
    greetingHeadline: "Welcome back, Adrenaline Collector.",
    greetingSubline: "Your heart rate's been too steady. Let's fix that.",
    greetingCta: "Find your next rush",
    moodMatchHeader: "Tuned for the Adrenaline Collector",
    affordableHeader: "Heart-pounding trips that don't drain the account",
    postcardsEmpty: "First postcard's earned, not given. Book a trip with a story worth telling.",
    stampsEmpty: "Mark the mountain, the reef, the drop you keep mentioning to friends. Someday is fast approaching.",
    savedEmpty: "Nothing saved yet. When you find the trip that scares you a little, lock it in.",
    detourHeader: "A different direction — even collectors miss things.",
  },
  "Soft-Life Traveler": {
    greetingHeadline: "Welcome back, Soft-Life Traveler.",
    greetingSubline: "No agenda. No pressure. Let's find the pool.",
    greetingCta: "Find a soft escape",
    moodMatchHeader: "Tuned for the Soft-Life Traveler",
    affordableHeader: "Slow trips that don't break the bank",
    postcardsEmpty: "First postcard's coming — somewhere with linen sheets and zero alarms. Book and it lands here.",
    stampsEmpty: "Mark the place you'd go just to lie still for a week. Someday counts.",
    savedEmpty: "No saved trips yet. When you find one that feels like Sunday, save it here.",
    detourHeader: "A different direction — even soft lives need a small jolt sometimes.",
  },
  "Cultural Nomad": {
    greetingHeadline: "Welcome back, Cultural Nomad.",
    greetingSubline: "There's a city you haven't read enough about. Let's go anyway.",
    greetingCta: "Find your next city",
    moodMatchHeader: "Tuned for the Cultural Nomad",
    affordableHeader: "Cities that pay off without paying full price",
    postcardsEmpty: "Your collection begins with the first museum that stops you cold. Book the trip — postcard follows.",
    stampsEmpty: "Mark the city you've been meaning to read more about. Someday it's a study abroad of one.",
    savedEmpty: "Nothing saved yet. When the right itinerary shows up — saved here, ready when you are.",
    detourHeader: "A different direction — culture lives in unexpected places too.",
  },
  "Spontaneous Minimalist": {
    greetingHeadline: "Welcome back, Spontaneous Minimalist.",
    greetingSubline: "One bag, one flight. Where to?",
    greetingCta: "Just go",
    moodMatchHeader: "Tuned for the Spontaneous Minimalist",
    affordableHeader: "Cheap flights, light packing, real trips",
    postcardsEmpty: "Postcards start small — like the trip you decide on Thursday for Saturday. Book and it lands here.",
    stampsEmpty: "Mark the city you'd leave for tomorrow. Someday's a placeholder until you click book.",
    savedEmpty: "Nothing saved yet — which fits. When something feels right, park it before you overthink.",
    detourHeader: "A different direction — minimalism doesn't have to mean predictable.",
  },
  "Off-Grid Dreamer": {
    greetingHeadline: "Welcome back, Off-Grid Dreamer.",
    greetingSubline: "Somewhere quiet is waiting. No signal required.",
    greetingCta: "Find your quiet place",
    moodMatchHeader: "Tuned for the Off-Grid Dreamer",
    affordableHeader: "Remote escapes that don't cost a fortune",
    postcardsEmpty: "First postcard arrives from somewhere you can barely get to. Book the trip — collection grows from there.",
    stampsEmpty: "Mark the wild place you keep returning to in your head. Someday it's real.",
    savedEmpty: "Nothing saved yet. When you find the trip that goes off-grid the right way, hold it here.",
    detourHeader: "A different direction — even hermits get curious.",
  },
  "Sunset Chaser": {
    greetingHeadline: "Welcome back, Sunset Chaser.",
    greetingSubline: "Somewhere the light's about to be perfect. Let's get you there.",
    greetingCta: "Find your next sunset",
    moodMatchHeader: "Tuned for the Sunset Chaser",
    affordableHeader: "Golden hours that fit the budget",
    postcardsEmpty: "First postcard arrives from the first horizon that holds you. Book and it shows up here.",
    stampsEmpty: "Mark the cliff, the beach, the rooftop you keep seeing in your mind at 7pm. Someday.",
    savedEmpty: "No saved trips yet. When you find one with the right light, save it before you forget.",
    detourHeader: "A different direction — sometimes the best light's not where you'd guess.",
  },
  "Digital Nomad": {
    greetingHeadline: "Welcome back, Digital Nomad.",
    greetingSubline: "Wi-Fi handled. Weekends open. Let's pick the next base.",
    greetingCta: "Find your next base",
    moodMatchHeader: "Tuned for the Digital Nomad",
    affordableHeader: "Workable cities at workable prices",
    postcardsEmpty: "First postcard comes from the first city that becomes home for a month. Book the trip — postcard arrives.",
    stampsEmpty: "Mark the city you've been Slacking from in your head. Someday means soon.",
    savedEmpty: "Nothing saved yet. When the right city + cafe + view shows up, keep it here.",
    detourHeader: "A different direction — the best cafés are sometimes in cities you didn't pick.",
  },
  "Heritage Seeker": {
    greetingHeadline: "Welcome back, Heritage Seeker.",
    greetingSubline: "Somewhere you've never been still knows your name. Let's go.",
    greetingCta: "Find your roots",
    moodMatchHeader: "Tuned for the Heritage Seeker",
    affordableHeader: "Meaningful trips at reasonable prices",
    postcardsEmpty: "First postcard arrives from the first place that feels like coming back. Book the trip — collection starts.",
    stampsEmpty: "Mark the town your family talks about. Someday is the start of the story.",
    savedEmpty: "Nothing saved yet. When the right pilgrimage shows up, save it for the right moment.",
    detourHeader: "A different direction — heritage runs deeper than you think.",
  },
  "Eco Wanderer": {
    greetingHeadline: "Welcome back, Eco Wanderer.",
    greetingSubline: "Slow, low-impact, deep travel. Let's pick somewhere worth the trip.",
    greetingCta: "Find a meaningful trip",
    moodMatchHeader: "Tuned for the Eco Wanderer",
    affordableHeader: "Low-impact trips at honest prices",
    postcardsEmpty: "First postcard comes from somewhere you traveled gently. Book and it shows up here.",
    stampsEmpty: "Mark the place you'd visit slowly and stay a while. Someday is its own kind of commitment.",
    savedEmpty: "No saved trips yet. When the right slow trip shows up, keep it close.",
    detourHeader: "A different direction — even slow travelers sometimes need a nudge.",
  },
  "Festival Hunter": {
    greetingHeadline: "Welcome back, Festival Hunter.",
    greetingSubline: "There's a lineup somewhere you should be tracking. Let's plan around it.",
    greetingCta: "Find your next festival",
    moodMatchHeader: "Tuned for the Festival Hunter",
    affordableHeader: "Festival trips that don't drain the wallet",
    postcardsEmpty: "First postcard comes from your first festival trip on this account. Book and it lands here.",
    stampsEmpty: "Mark the festival you've been promising yourself. Someday means next ticket drop.",
    savedEmpty: "Nothing saved yet. When the right festival + city combo lines up, save the whole trip here.",
    detourHeader: "A different direction — quiet trips have their own kind of energy too.",
  },
  "Photo Pilgrim": {
    greetingHeadline: "Welcome back, Photo Pilgrim.",
    greetingSubline: "Somewhere's framing itself perfectly right now. Let's get you in front of it.",
    greetingCta: "Find your next frame",
    moodMatchHeader: "Tuned for the Photo Pilgrim",
    affordableHeader: "Frame-worthy trips at fair prices",
    postcardsEmpty: "First postcard comes from your first shot worth chasing. Book the trip — postcard follows.",
    stampsEmpty: "Mark the location you've been saving to your camera roll. Someday means the real lens.",
    savedEmpty: "Nothing saved yet. When the right composition shows up — destination, season, light — save it here.",
    detourHeader: "A different direction — the best shots aren't always where the tags are.",
  },
  "Wellness Pilgrim": {
    greetingHeadline: "Welcome back, Wellness Pilgrim.",
    greetingSubline: "Somewhere quiet and intentional is waiting. Let's find it.",
    greetingCta: "Find your retreat",
    moodMatchHeader: "Tuned for the Wellness Pilgrim",
    affordableHeader: "Restorative trips that don't break the budget",
    postcardsEmpty: "First postcard arrives from your first reset. Book the trip — postcard part of the return home.",
    stampsEmpty: "Mark the retreat, the spring, the practice you've been circling. Someday counts.",
    savedEmpty: "Nothing saved yet. When the right slow trip shows up, save it for when you need it.",
    detourHeader: "A different direction — even pilgrims sometimes need a detour.",
  },
  "Bookworm Wanderer": {
    greetingHeadline: "Welcome back, Bookworm Wanderer.",
    greetingSubline: "Somewhere's serving good coffee and you've got chapters to finish.",
    greetingCta: "Find your next chapter",
    moodMatchHeader: "Tuned for the Bookworm Wanderer",
    affordableHeader: "Quiet trips that fit a reader's budget",
    postcardsEmpty: "First postcard comes from your first reading week away. Book the trip — postcard arrives like a bookmark.",
    stampsEmpty: "Mark the bookshop city you've been adding to lists for years. Someday is overdue.",
    savedEmpty: "Nothing saved yet. When the right cafe + library + season aligns, save it here.",
    detourHeader: "A different direction — the best stories aren't always in the section you came for.",
  },
  "Family Architect": {
    greetingHeadline: "Welcome back, Family Architect.",
    greetingSubline: "Somewhere will be a memory in twenty years. Let's pick well.",
    greetingCta: "Find your next family trip",
    moodMatchHeader: "Tuned for the Family Architect",
    affordableHeader: "Family trips that fit the real budget",
    postcardsEmpty: "First postcard comes from the first trip the kids talk about for months. Book and it shows up here.",
    stampsEmpty: "Mark the place that'll be in the family story. Someday is the gift.",
    savedEmpty: "Nothing saved yet. When the right family trip shows up — pace, ages, budget — save it here.",
    detourHeader: "A different direction — family trips don't have to be predictable.",
  },
  "Solo Soul-Searcher": {
    greetingHeadline: "Welcome back, Solo Soul-Searcher.",
    greetingSubline: "Somewhere quiet is waiting to be walked through alone. Let's find it.",
    greetingCta: "Find your next solo trip",
    moodMatchHeader: "Tuned for the Solo Soul-Searcher",
    affordableHeader: "Solo trips that fit a single budget",
    postcardsEmpty: "First postcard arrives from your first long walk alone. Book the trip — postcard's the souvenir.",
    stampsEmpty: "Mark the city you'd go to alone. Someday is permission you don't need to ask for.",
    savedEmpty: "Nothing saved yet. When the right solo trip shows up, save it for when the timing's right.",
    detourHeader: "A different direction — sometimes the trip you didn't plan is the one that changes things.",
  },
  "Night Owl Nomad": {
    greetingHeadline: "Welcome back, Night Owl Nomad.",
    greetingSubline: "Somewhere's just getting started after midnight. Let's get you there.",
    greetingCta: "Find your next late night",
    moodMatchHeader: "Tuned for the Night Owl Nomad",
    affordableHeader: "After-dark cities at affordable prices",
    postcardsEmpty: "First postcard arrives from your first 2am in a new city. Book the trip — postcard's part of it.",
    stampsEmpty: "Mark the city whose nightlife you keep hearing about. Someday is the next free weekend.",
    savedEmpty: "Nothing saved yet. When the right city + scene lines up, save it for next month.",
    detourHeader: "A different direction — even night owls notice the morning sometimes.",
  },
};

// Helper: get identity copy with safe fallback. If identity is unknown or
// not yet selected, returns a generic copy set so the UI doesn't break.
function getIdentityCopy(identity) {
  if (identity && IDENTITY_COPY[identity]) return IDENTITY_COPY[identity];
  return {
    greetingHeadline: "Welcome back.",
    greetingSubline: "Where to next?",
    greetingCta: "Find your next trip",
    moodMatchHeader: "Trips matching your mood",
    affordableHeader: "Most affordable picks",
    postcardsEmpty: "Your collection is waiting for its first stamp. Book a trip — the postcard arrives with it.",
    stampsEmpty: "Mark a place you've been daydreaming about. Someday is the start.",
    savedEmpty: "Nothing saved yet. When you find a trip that feels right, save it here.",
    detourHeader: "A different direction worth considering.",
  };
}

// ─── Behavioral vibe profile ──────────────────────────────────────
// Foundation layer for "behavior-aware" identity refinement. These are
// READ-ONLY helpers — they observe user behavior and return a vibe
// weight profile, but do NOT change anything (no silent re-weighting,
// no UI prompts). The actual wiring (silent recommendation re-weighting
// + drift nudge UI + identity card "leaning toward" line) plugs into
// these helpers in a follow-up.
//
// Threshold: behavioral signal requires >=5 saved interactions across
// postcards + boards + alerts + saved itineraries. Below threshold the
// signal is too noisy to trust — helper returns null and callers skip
// the behavioral layer entirely (declared identity stays canonical).
//
// Inputs are the user's current saved-state arrays/maps. Helper looks up
// each saved destination in DESTINATIONS to read its tags, then tallies
// tags across the saved set.
const BEHAVIORAL_SIGNAL_THRESHOLD = 5;

// Compute a vibe-weight Map from a user's saved interactions.
// Returns null if interaction count is below threshold.
// Returns { weights, totalInteractions, dominantVibes } when above.
//
//   weights: { adventure: 0.4, beach: 0.6, ... }  — normalized 0..1
//   totalInteractions: integer count
//   dominantVibes: top 3-5 vibes by weight (>= 0.2 weight)
function computeBehavioralProfile({ postcards = {}, boards = [], alerts = {}, savedItineraries = [], pastTrips = [], destinations = [] }) {
  // Collect all destination names the user has shown interest in.
  // A destination is "interacted with" if it appears in any of: saved
  // postcard, board (any list), active alert, saved itinerary, or past trip.
  const interactedNames = new Set();

  // Postcards: { postcardId: state }, where state in { dreaming, visited, etc. }
  // We treat ANY state as interaction (saving a postcard is intent).
  Object.keys(postcards || {}).forEach(id => {
    // Postcard IDs are slugified destination IDs — map back to name via destinations
    const dest = destinations.find(d => (d.id || d.name?.toLowerCase().replace(/\s+/g, "-")) === id);
    if (dest?.name) interactedNames.add(dest.name);
  });

  // Boards: array of board objects, each with `items` array of dest names
  (boards || []).forEach(board => {
    const items = board.items || board.destinations || [];
    items.forEach(item => {
      const name = typeof item === "string" ? item : item.name || item.destName;
      if (name) interactedNames.add(name);
    });
  });

  // Alerts: { destName: alertObj }
  Object.keys(alerts || {}).forEach(name => interactedNames.add(name));

  // Saved itineraries: array of itinerary objects with `destination` field
  (savedItineraries || []).forEach(it => {
    const name = it.destination || it.destName || it.name;
    if (name) interactedNames.add(name);
  });

  // Past trips: array with `destination` field
  (pastTrips || []).forEach(t => {
    const name = t.destination || t.destName || t.name;
    if (name) interactedNames.add(name);
  });

  const totalInteractions = interactedNames.size;
  if (totalInteractions < BEHAVIORAL_SIGNAL_THRESHOLD) return null;

  // Tally tags across all interacted destinations
  const tagCounts = {};
  let tagsObserved = 0;
  interactedNames.forEach(name => {
    const dest = destinations.find(d => d.name === name);
    if (!dest || !Array.isArray(dest.tags)) return;
    dest.tags.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      tagsObserved += 1;
    });
  });
  if (tagsObserved === 0) return null;

  // Normalize: each tag's weight = its share of total tag observations
  const weights = {};
  Object.keys(tagCounts).forEach(tag => {
    weights[tag] = tagCounts[tag] / tagsObserved;
  });

  // Dominant vibes: tags with weight >= 0.15 (meaningful share), sorted desc
  const dominantVibes = Object.entries(weights)
    .filter(([, w]) => w >= 0.15)
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag)
    .slice(0, 5);

  return { weights, totalInteractions, dominantVibes };
}

// Compare a behavioral profile against the user's declared identity.
// Returns a drift assessment: how much does their behavior diverge from
// what their declared identity would predict? Used by the (future) drift
// nudge UI to decide when to suggest an identity refinement.
//
// Returns null if either input is missing or threshold not met.
// Returns { alignment, behavioralLean, suggestedIdentity } when computed.
//
//   alignment: 0..1 — share of behavioral dominant vibes that match identity vibes
//   behavioralLean: vibes in behavior that AREN'T in declared identity
//   suggestedIdentity: name of identity whose vibes best match behavior, if
//                     different from declared (and meaningfully better-matching)
function compareIdentityToBehavior(declaredIdentity, behavioralProfile, identities) {
  if (!declaredIdentity || !behavioralProfile || !identities) return null;
  const declared = identities[declaredIdentity];
  if (!declared || !Array.isArray(declared.vibes)) return null;

  const declaredVibes = new Set(declared.vibes);
  const behavioralDominant = behavioralProfile.dominantVibes || [];
  if (behavioralDominant.length === 0) return null;

  // Alignment = share of behavioral dominant vibes that are in declared identity
  const overlap = behavioralDominant.filter(v => declaredVibes.has(v)).length;
  const alignment = overlap / behavioralDominant.length;

  // Behavioral lean = vibes the user actually engages with that AREN'T in declared identity
  const behavioralLean = behavioralDominant.filter(v => !declaredVibes.has(v));

  // Suggested identity: scan all identities, find one whose vibes overlap most
  // strongly with the user's behavioral dominant vibes. Only suggest if it
  // beats the declared identity's alignment by a meaningful margin (>=0.2).
  let bestIdentity = null;
  let bestScore = 0;
  Object.keys(identities).forEach(name => {
    if (name === declaredIdentity) return;
    const candidateVibes = new Set(identities[name].vibes || []);
    const candidateOverlap = behavioralDominant.filter(v => candidateVibes.has(v)).length;
    const candidateScore = candidateOverlap / behavioralDominant.length;
    if (candidateScore > bestScore) {
      bestScore = candidateScore;
      bestIdentity = name;
    }
  });

  const suggestedIdentity = (bestScore - alignment >= 0.2) ? bestIdentity : null;

  return { alignment, behavioralLean, suggestedIdentity, behavioralDominant };
}

// ─── Identity attribute level/label maps ──────────────────────────
// Centralized so all renders that visualize identity attributes stay in sync
// Levels are 1–5 scale; labels are display strings shown on the identity card.
// Includes all values from the 20 TRAVEL_IDENTITIES — add new entries here when adding identities.
const IDENTITY_ENERGY_LVL = {
  // Original 10
  "Extreme": 5, "High": 4, "Moderate": 3, "Relaxed": 2, "Low": 2, "Quiet": 1, "Gentle": 1,
  // New 10
  "Visual": 4, "Steady": 3, "Reflective": 2, "Mindful": 2, "High & social": 5,
  "Restorative": 1, "Joyful chaos": 4, "Introspective": 1, "After-dark": 4,
};
const IDENTITY_PACE_LVL = {
  "Non-stop": 5, "Fast-moving": 4, "Deliberate": 3, "Destination-driven": 3,
  "Slow & savoring": 2, "Unhurried": 2, "Golden hour-guided": 2, "Go with the flow": 2,
  "Nature's pace": 1, "Wherever the day takes me": 1,
  // New
  "Light-driven": 3, "Live, then explore": 3, "Slow & intentional": 1, "Considered": 2,
  "Event-anchored": 4, "Slow & mindful": 1, "Pages-per-day": 1, "Kid-paced": 3,
  "Listen to yourself": 2, "Sleeps when sun is up": 5,
};
const IDENTITY_PLAN_LVL = {
  "Handled for me": 5, "Curated": 4, "Researched": 4, "Deep research": 5,
  "Activity-first": 4, "Flexible": 3, "Minimal": 2, "Spontaneous": 1, "None preferred": 1,
  // New
  "Location-first": 4, "Long-term": 4, "Deeply researched": 5, "Ethical": 4,
  "Calendar-driven": 4, "Retreat-based": 4, "Story-driven": 3, "Logistics-heavy": 5,
  "Loose framework": 2, "Bar map ready": 3,
};
const IDENTITY_PLAN_LABEL = {
  "Handled for me": "Hand it all off", "Curated": "Curated & smooth",
  "Deep research": "Every detail planned", "Researched": "Well prepared",
  "Activity-first": "Activity-first", "Flexible": "Plan lightly",
  "Minimal": "Rough idea at most", "Spontaneous": "Decide on arrival", "None preferred": "No plan is the plan",
  // New
  "Location-first": "Location-first", "Long-term": "Long stay, slow plan",
  "Deeply researched": "Every detail mapped", "Ethical": "Thoughtfully chosen",
  "Calendar-driven": "Built around the date", "Retreat-based": "Retreat-style",
  "Story-driven": "Story-led travel", "Logistics-heavy": "Family logistics",
  "Loose framework": "Rough sketch", "Bar map ready": "Nightlife-mapped",
};
const IDENTITY_FOOD_LVL = {
  "Obsessively curious": 5, "Adventurous": 4, "Authentic & local": 4, "Exceptional": 4,
  "Intimate & local": 3, "Local & simple": 3, "Pretty & delicious": 3,
  "Street food queen/king": 5, "Rooftop & waterfront": 3, "Fuel, mostly": 1,
  // New
  "Cafe-friendly": 3, "Ancestral & authentic": 4, "Local & seasonal": 4,
  "Festival fare": 3, "Photogenic": 3, "Plant-forward & clean": 3,
  "Cafe with a window seat": 3, "Family-friendly + fun": 3, "Mindful & solo": 2, "Late-night eats": 4,
};
const IDENTITY_FOOD_LABEL = {
  "Obsessively curious": "Eats anything, anywhere", "Adventurous": "Eats boldly",
  "Authentic & local": "Locals only", "Exceptional": "Michelin or nothing",
  "Intimate & local": "Quiet tables", "Local & simple": "Simple & fresh",
  "Pretty & delicious": "Aesthetic & delicious", "Street food queen/king": "Street food soul",
  "Rooftop & waterfront": "Views make the meal", "Fuel, mostly": "Eats to move",
  // New
  "Cafe-friendly": "Cafes with wifi", "Ancestral & authentic": "Recipes from the past",
  "Local & seasonal": "Farm to table", "Festival fare": "Festival-ready food",
  "Photogenic": "Camera-ready meals", "Plant-forward & clean": "Mindful & clean",
  "Cafe with a window seat": "Quiet cafe meals", "Family-friendly + fun": "Kid-approved",
  "Mindful & solo": "Solo & savored", "Late-night eats": "After-midnight diners",
};
const IDENTITY_COMFORT_LVL = {
  "5-star always": 5, "Beautiful spaces": 4, "Boutique hotels": 4, "Boutique + local": 4,
  "Characterful stays": 3, "Immersive stays": 3, "Views over everything": 4,
  "Functional": 2, "Eco stays & lodges": 2, "Clean & simple": 2, "Budget": 1,
  // New
  "Wifi + workspace": 3, "Family-run stays": 3, "Eco-lodges": 3, "Camp or hostel": 1,
  "Aesthetic stays": 4, "Spa & retreat": 5, "Quiet inns": 3, "Suites & apartments": 4,
  "Quiet rooms": 2, "City center hotels": 4,
};
const IDENTITY_COMFORT_LABEL = {
  "5-star always": "5-star always", "Beautiful spaces": "Design matters",
  "Boutique hotels": "Boutique always", "Boutique + local": "Local character",
  "Characterful stays": "Story over stars", "Immersive stays": "Embedded in culture",
  "Views over everything": "View IS the room", "Functional": "Clean & quiet",
  "Eco stays & lodges": "Off-grid & eco", "Clean & simple": "Simple done right", "Budget": "Anywhere works",
  // New
  "Wifi + workspace": "Workation-ready", "Family-run stays": "Family hospitality",
  "Eco-lodges": "Eco-lodge stays", "Camp or hostel": "Camp or crash",
  "Aesthetic stays": "Aesthetic stays", "Spa & retreat": "Full spa retreat",
  "Quiet inns": "Quiet country inns", "Suites & apartments": "Family-sized suites",
  "Quiet rooms": "Solo & quiet", "City center hotels": "Heart of the city",
};
const IDENTITY_SOCIAL = {
  "Couple-focused": { n: 2, label: "Just the two of you" },
  "Group or solo": { n: 4, label: "Either works" },
  "Exploratory": { n: 3, label: "Open to anyone" },
  "Curious & open": { n: 3, label: "Talks to everyone" },
  "Food-forward": { n: 2, label: "Food friends" },
  "Private & curated": { n: 1, label: "Small & private" },
  "Peaceful": { n: 1, label: "Solo or one" },
  "Meet anyone": { n: 5, label: "Befriends everyone" },
  "Small & meaningful": { n: 2, label: "Depth over breadth" },
  "Romantic or solo": { n: 1, label: "Intimate or alone" },
  // New
  "Coworking communities": { n: 4, label: "Coworking community" },
  "Multi-generational": { n: 5, label: "Family across ages" },
  "Conscious community": { n: 3, label: "Like-minded travelers" },
  "Loud & joyful": { n: 5, label: "Big festival groups" },
  "Curated company": { n: 2, label: "Hand-picked few" },
  "Quiet kinship": { n: 2, label: "Quiet & connected" },
  "Mostly alone, gladly": { n: 1, label: "Just me" },
  "Multi-gen": { n: 5, label: "Whole family" },
  "Strangers turned friends": { n: 3, label: "Open to strangers" },
  "Strangers at 2am": { n: 4, label: "Late-night people" },
};

// ── Constellation generator ─────────────────────────────────────
// Given a TRAVEL_IDENTITIES entry, returns the data needed to render
// its constellation chart: 6 stars (one per attribute) with positions
// and radii derived from each attribute's 1-5 level, plus the 6 lines
// connecting them in STAR_ORDER. Uses the existing level lookups so
// every identity gets a deterministic, distinct shape.
function getLevel(attr, value) {
  if (attr === "energy")   return IDENTITY_ENERGY_LVL[value] ?? 3;
  if (attr === "pace")     return IDENTITY_PACE_LVL[value] ?? 3;
  if (attr === "planning") return IDENTITY_PLAN_LVL[value] ?? 3;
  if (attr === "food")     return IDENTITY_FOOD_LVL[value] ?? 3;
  if (attr === "comfort")  return IDENTITY_COMFORT_LVL[value] ?? 3;
  if (attr === "social")   return IDENTITY_SOCIAL[value]?.n ?? 3;
  return 3;
}

function generateConstellation(identity) {
  const stars = {};
  for (const attr of Object.keys(CHART_ZONES)) {
    const zone = CHART_ZONES[attr];
    const value = identity[attr];
    const level = getLevel(attr, value);
    const t = (level - 1) / 4; // 0 to 1 across the 1-5 range

    // Direction-aware position. Sign of xDir/yDir determines which
    // edge of the zone the high-level stars settle toward; 0 keeps
    // the star centered on that axis (varies only the other one).
    const x = zone.xDir < 0
      ? zone.xMax - t * (zone.xMax - zone.xMin)
      : zone.xDir > 0
        ? zone.xMin + t * (zone.xMax - zone.xMin)
        : (zone.xMin + zone.xMax) / 2;
    const y = zone.yDir < 0
      ? zone.yMax - t * (zone.yMax - zone.yMin)
      : zone.yDir > 0
        ? zone.yMin + t * (zone.yMax - zone.yMin)
        : (zone.yMin + zone.yMax) / 2;

    const r = 1.5 + (level - 1) * 0.5; // radii 1.5, 2.0, 2.5, 3.0, 3.5
    stars[attr] = { x, y, r, level, value, label: `${attr} · ${String(value || "").toLowerCase()}` };
  }
  const lines = [];
  for (let i = 0; i < STAR_ORDER.length; i++) {
    const from = stars[STAR_ORDER[i]];
    const to   = stars[STAR_ORDER[(i + 1) % STAR_ORDER.length]];
    lines.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y, fromAttr: STAR_ORDER[i] });
  }
  return { stars, lines, bgStars: BG_STARS };
}

// ── ConstellationChart — renders a generated chart as SVG ───────
// Three variants share the same generator output but vary in chrome:
//   share = no labels, no sky (sky lives on parent canvas), large
//   inApp = sky background, labels per star, medium
//   pill  = no sky, no labels, no background stars — tiny nav icon
//
// When `animate` is true, the chart plays a staggered reveal: stars
// fade+scale in one by one, then lines draw via stroke-dashoffset.
// Honor prefers-reduced-motion by skipping all animations.
function ConstellationChart({ identity, identityName, variant = "inApp", animate = false }) {
  if (!identity) return null;
  const sky = IDENTITY_SKY[identityName] || IDENTITY_SKY["Romantic Wanderer"];
  const accent = sky.accent;
  // Sky tier treatment (Revision 1) — twilight skies need brighter stars +
  // lines to stay legible on the lighter ground. Falls back to dusk.
  const tier = SKY_TIERS[sky.tier] || SKY_TIERS.dusk;
  // Lighter accent variant for twilight lines: nudge the accent brighter.
  // For the known mauve pair we use the lifted value; otherwise we lighten
  // the existing accent slightly so twilight lines don't sink into the sky.
  const lineAccent = tier.lineLighten
    ? (accent === "#4A8CC4" ? "#D5AAB8" : _lightenHex(accent, 0.12))
    : accent;

  // Memoize the generator output — it's pure and the input rarely changes
  const data = useMemo(() => {
    try { return generateConstellation(identity); }
    catch (err) { console.error("[JGA Constellation] generator failed:", err); return null; }
  }, [identity]);
  if (!data) return null;
  const { stars, lines, bgStars } = data;

  // Variant configs. The pill variant renders on light (cream) surfaces so
  // it uses the darker saturated identity.color rather than the light night-
  // sky accent (which only reads well on the dark sky backgrounds).
  const pillColor = identity.color || accent;
  const cfg = variant === "share"
    ? { width: "100%", height: "100%", showLabels: false, showSky: false, showBgStars: true, starColor: tier.starFill, lineWidth: 1.5, lineOpacity: tier.lineOpacity }
    : variant === "pill"
      ? { width: 20, height: 14, showLabels: false, showSky: false, showBgStars: false, starColor: pillColor, lineWidth: 2, lineOpacity: 0.85, starScale: 1.6 }
      : { width: "100%", height: "100%", showLabels: true, showSky: true, showBgStars: true, starColor: tier.starFill, lineWidth: 1.5, lineOpacity: tier.lineOpacity };

  // For each line, precompute its length so we can animate stroke-dashoffset.
  const lineLengths = lines.map(l => Math.sqrt((l.x2 - l.x1) ** 2 + (l.y2 - l.y1) ** 2) + 0.5);

  const svgEl = (
    <svg viewBox="0 0 200 140" width={cfg.width} height={cfg.height}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", position: cfg.showSky ? "absolute" : "static", inset: cfg.showSky ? 0 : undefined }}
      aria-hidden="true">

      {/* Background stars — fixed seed, opacity baked in */}
      {cfg.showBgStars && bgStars.map((s, i) => (
        <circle key={`bg-${i}`} cx={s.x} cy={s.y} r={s.r} fill="#FAFAF5" opacity={s.o}
          style={animate ? { opacity: 0, animation: `jga-bg-star 1.2s ease-out ${0.6 + i * 0.08}s forwards` } : undefined} />
      ))}

      {/* Connecting lines — drawn first so they sit behind the stars */}
      {lines.map((l, i) => {
        const len = lineLengths[i];
        const lineStroke = variant === "pill" ? pillColor : lineAccent;
        const baseStyle = { stroke: lineStroke, strokeWidth: cfg.lineWidth, opacity: cfg.lineOpacity, strokeLinecap: "round" };
        const animStyle = animate
          ? {
              ...baseStyle,
              opacity: 0,
              strokeDasharray: `${len} ${len}`,
              strokeDashoffset: len,
              animation: `jga-line-draw 0.5s ease-out ${1.6 + i * 0.15}s forwards`,
            }
          : baseStyle;
        return <line key={`l-${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} style={animStyle} />;
      })}

      {/* Stars — glow halo + bright core. STAR_ORDER drives reveal order. */}
      {STAR_ORDER.map((attr, i) => {
        const s = stars[attr];
        const baseStyle = animate ? { opacity: 0, transformOrigin: `${s.x}px ${s.y}px`, animation: `jga-star-in 0.4s cubic-bezier(0.34,1.56,0.64,1) ${0.3 + i * 0.2}s forwards` } : {};
        return (
          <g key={attr} style={baseStyle}>
            <circle cx={s.x} cy={s.y} r={s.r * 2.2 * (cfg.starScale || 1)} fill={cfg.starColor} opacity={tier.starGlow} />
            <circle cx={s.x} cy={s.y} r={s.r * (cfg.starScale || 1)} fill={cfg.starColor} />
          </g>
        );
      })}

      {/* Inline labels — only in the in-app variant.
          Just the attribute name (ENERGY, PACE, etc.) — clean and short.
          The actual values are shown in a readable grid below the chart. */}
      {cfg.showLabels && STAR_ORDER.map((attr, i) => {
        const s = stars[attr];
        const layout = {
          energy:   { x: s.x,             y: s.y - s.r - 3, anchor: "middle" }, // above
          pace:     { x: s.x,             y: s.y - s.r - 3, anchor: "middle" }, // above
          planning: { x: s.x - s.r - 3,   y: s.y + 1,       anchor: "end" },    // left
          food:     { x: s.x + s.r + 3,   y: s.y + 1,       anchor: "start" },  // right
          comfort:  { x: s.x,             y: s.y + s.r + 6, anchor: "middle" }, // below
          social:   { x: s.x,             y: s.y + s.r + 6, anchor: "middle" }, // below
        }[attr];
        const animStyle = animate ? { opacity: 0, animation: `jga-bg-star 0.4s ease-out ${2.8 + i * 0.06}s forwards` } : undefined;
        return (
          <text key={`lbl-${attr}`} x={layout.x} y={layout.y}
            fontSize="3.4" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            fill={lineAccent} opacity={0.95} textAnchor={layout.anchor}
            letterSpacing="0.6"
            style={animStyle}>
            {attr.toUpperCase()}
          </text>
        );
      })}
    </svg>
  );

  // For in-app + share variants we wrap the SVG in a div so the sky gradient
  // can live on a normal HTML element (reliable in Safari) instead of on the
  // SVG itself (where browsers handle CSS background inconsistently). For
  // the pill variant we render the SVG directly — no sky needed.
  if (cfg.showSky) {
    return (
      <div style={{ position: "absolute", inset: 0, background: getIdentitySky(identityName) }}>
        {svgEl}
      </div>
    );
  }
  return svgEl;
}


const QUIZ_QUESTIONS = [
  {
    id: "budget",
    q: "Your relationship with the trip budget is...",
    options: [
      { text: "Set amount — I know what I have", scores: { "Cultural Nomad": 2, "Culinary Explorer": 1, "Spontaneous Minimalist": 1, "Family Architect": 2, "Eco Wanderer": 1 } },
      { text: "Depends who I'm going with or where", scores: { "Romantic Wanderer": 2, "Controlled Chaos Traveler": 2, "Sunset Chaser": 1, "Festival Hunter": 1, "Heritage Seeker": 1 } },
      { text: "As cheap as possible", scores: { "Spontaneous Minimalist": 3, "Off-Grid Dreamer": 2, "Digital Nomad": 2, "Festival Hunter": 1 } },
      { text: "Best deal", scores: { "Culinary Explorer": 2, "Adrenaline Collector": 2, "Cultural Nomad": 1, "Eco Wanderer": 2, "Bookworm Wanderer": 1 } },
      { text: "Budget? What's that", scores: { "Luxury Escapist": 3, "Romantic Wanderer": 1, "Photo Pilgrim": 1, "Wellness Pilgrim": 1 } },
    ],
  },
  {
    id: "pace",
    q: "Your ideal travel day starts with...",
    options: [
      { text: "An alarm at 6am — not wasting time", scores: { "Adrenaline Collector": 2, "Controlled Chaos Traveler": 2, "Cultural Nomad": 1, "Photo Pilgrim": 2, "Wellness Pilgrim": 2 } },
      { text: "Coffee — no plans", scores: { "Spontaneous Minimalist": 2, "Sunset Chaser": 1, "Soft-Life Traveler": 1, "Bookworm Wanderer": 2, "Digital Nomad": 1 } },
      { text: "Slow breakfast — then a plan I actually follow", scores: { "Culinary Explorer": 2, "Cultural Nomad": 2, "Romantic Wanderer": 1, "Heritage Seeker": 2, "Family Architect": 2 } },
      { text: "Nothing scheduled — I'll find my way to the beach or pool", scores: { "Soft-Life Traveler": 2, "Luxury Escapist": 2, "Sunset Chaser": 1, "Wellness Pilgrim": 1 } },
      { text: "Wake up somewhere new — no idea how I got there (in a good way)", scores: { "Controlled Chaos Traveler": 3, "Spontaneous Minimalist": 2, "Festival Hunter": 2, "Night Owl Nomad": 3 } },
    ],
  },
  {
    id: "companion",
    q: "You're most likely traveling with...",
    options: [
      { text: "Just me — solo is when I think clearest", companion: "solo", scores: { "Spontaneous Minimalist": 2, "Off-Grid Dreamer": 2, "Solo Soul-Searcher": 3, "Bookworm Wanderer": 2, "Cultural Nomad": 1 } },
      { text: "My partner — that's the trip", companion: "couple", scores: { "Romantic Wanderer": 3, "Sunset Chaser": 1, "Luxury Escapist": 1, "Photo Pilgrim": 1 } },
      { text: "A group of friends — louder is better", companion: "friends", scores: { "Controlled Chaos Traveler": 2, "Adrenaline Collector": 1, "Culinary Explorer": 1, "Festival Hunter": 3, "Night Owl Nomad": 2 } },
      { text: "Family — kids, parents, or both", companion: "family", scores: { "Family Architect": 3, "Soft-Life Traveler": 1, "Heritage Seeker": 2, "Sunset Chaser": 1 } },
      { text: "Whoever shows up — strangers, group tours, random new friends", companion: "friends", scores: { "Festival Hunter": 2, "Digital Nomad": 3, "Spontaneous Minimalist": 2, "Cultural Nomad": 1 } },
    ],
  },
  {
    id: "food",
    q: "When it comes to food on the road...",
    options: [
      { text: "I researched the restaurants before I booked the hotel", scores: { "Culinary Explorer": 3, "Cultural Nomad": 1, "Heritage Seeker": 2, "Photo Pilgrim": 1 } },
      { text: "Street food is the soul of a city", scores: { "Spontaneous Minimalist": 2, "Controlled Chaos Traveler": 2, "Festival Hunter": 2, "Heritage Seeker": 1, "Eco Wanderer": 2 } },
      { text: "Rooftop dining, preferably with a view", scores: { "Sunset Chaser": 2, "Luxury Escapist": 2, "Romantic Wanderer": 1, "Night Owl Nomad": 2, "Photo Pilgrim": 2 } },
      { text: "Whatever — I eat to keep moving", scores: { "Adrenaline Collector": 2, "Off-Grid Dreamer": 1, "Digital Nomad": 1, "Solo Soul-Searcher": 1, "Wellness Pilgrim": 1, "Bookworm Wanderer": 1 } },
      { text: "A cooking class or market tour with a local", scores: { "Heritage Seeker": 3, "Cultural Nomad": 2, "Eco Wanderer": 2, "Culinary Explorer": 1, "Family Architect": 1 } },
    ],
  },
  {
    id: "nature",
    q: "The landscape that pulls you most...",
    options: [
      { text: "Ocean, always. Any ocean", scores: { "Sunset Chaser": 2, "Soft-Life Traveler": 2, "Romantic Wanderer": 1, "Family Architect": 1, "Wellness Pilgrim": 1 } },
      { text: "Mountains, trails, something untouched", scores: { "Off-Grid Dreamer": 3, "Adrenaline Collector": 2, "Eco Wanderer": 2, "Solo Soul-Searcher": 2, "Wellness Pilgrim": 1 } },
      { text: "Cities — the energy is the landscape", scores: { "Cultural Nomad": 2, "Culinary Explorer": 2, "Controlled Chaos Traveler": 1, "Digital Nomad": 2, "Night Owl Nomad": 3, "Festival Hunter": 1 } },
      { text: "Wherever looks most beautiful on my camera roll", scores: { "Sunset Chaser": 2, "Romantic Wanderer": 1, "Soft-Life Traveler": 1, "Photo Pilgrim": 3 } },
      { text: "Desert, ice, or anywhere quiet and vast", scores: { "Off-Grid Dreamer": 2, "Solo Soul-Searcher": 3, "Wellness Pilgrim": 2, "Bookworm Wanderer": 2, "Eco Wanderer": 1 } },
    ],
  },
  {
    id: "planning",
    q: "Your packing strategy is...",
    options: [
      { text: "One carry-on. Everything I need fits", scores: { "Spontaneous Minimalist": 3, "Off-Grid Dreamer": 1, "Digital Nomad": 2, "Solo Soul-Searcher": 1, "Eco Wanderer": 2 } },
      { text: "Three checked bags and I'll still forget something", scores: { "Luxury Escapist": 2, "Romantic Wanderer": 1, "Family Architect": 3, "Photo Pilgrim": 2 } },
      { text: "Gear-focused. The right kit for the right adventure", scores: { "Adrenaline Collector": 3, "Off-Grid Dreamer": 1, "Festival Hunter": 2, "Photo Pilgrim": 2 } },
      { text: "Whatever fits around my reading list", scores: { "Soft-Life Traveler": 2, "Cultural Nomad": 1, "Sunset Chaser": 1, "Bookworm Wanderer": 3, "Wellness Pilgrim": 2, "Heritage Seeker": 1 } },
      { text: "Last-minute panic — I throw it together morning of", scores: { "Controlled Chaos Traveler": 3, "Spontaneous Minimalist": 1, "Festival Hunter": 2, "Night Owl Nomad": 2, "Digital Nomad": 1 } },
    ],
  },
];

function computeIdentity(answers) {
  const scores = {};
  Object.keys(TRAVEL_IDENTITIES).forEach(k => { scores[k] = 0; });
  answers.forEach(({ qIdx, optIdx }) => {
    const opts = QUIZ_QUESTIONS[qIdx].options[optIdx].scores;
    Object.entries(opts).forEach(([k, v]) => { scores[k] = (scores[k] || 0) + v; });
  });
  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
}

function OnboardingModal({ onSelect, registered = false, onOpenLogin = () => {}, onOpenRegister = () => {}, forceRetake = false, shareIdentityCard = null }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const savedIdentity = (() => { try { return localStorage.getItem("jga-traveler-type"); } catch { return null; } })();
  // Phase routing:
  // - forceRetake (returning user clicking "Retake quiz"): jump to quiz
  // - registered + has saved identity: skip to reveal (returning users)
  // - everyone else (including unregistered users with stale localStorage):
  //   start at intro so they go through the proper onboarding flow
  const [phase, setPhase] = useState(
    forceRetake ? "quiz"
    : (registered && savedIdentity && TRAVEL_IDENTITIES[savedIdentity]) ? "reveal"
    : "intro"
  );
  // Returning user — pre-load their identity into state (only if registered + not forcing retake)
  const [identity, setIdentity] = useState(forceRetake ? null : (registered && savedIdentity && TRAVEL_IDENTITIES[savedIdentity] ? savedIdentity : null));
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [animating, setAnimating] = useState(false);

  // ── Auto-rotating identity teaser card on intro screen ──
  const [cardIdx, setCardIdx] = useState(0);
  useEffect(() => {
    if (phase !== "intro") return;
    const t = setInterval(() => setCardIdx(i => (i + 1) % 6), 4500);
    return () => clearInterval(t);
  }, [phase]);

  // ── Quiz lead email capture (soft signup) ──
  const [quizLeadEmail, setQuizLeadEmail] = useState("");
  const [quizLeadStatus, setQuizLeadStatus] = useState(() => {
    // If they've already submitted a quiz lead, don't ask again
    try { return localStorage.getItem("jga-quiz-lead-captured") ? "captured" : "idle"; } catch { return "idle"; }
  }); // "idle" | "submitting" | "captured" | "error"
  const [quizLeadError, setQuizLeadError] = useState("");
  const [quizLeadDismissed, setQuizLeadDismissed] = useState(false);
  // True when the submitted email already belongs to a registered account.
  // Backend signals this via `{ alreadyUser: true }` in the 2xx response.
  // Drives a different captured-state UI: "you have an account, sign in"
  // instead of "check your inbox, sign up". Persisted to sessionStorage so
  // the message doesn't reset on page navigation within the same session.
  const [quizLeadAlreadyUser, setQuizLeadAlreadyUser] = useState(() => {
    try { return sessionStorage.getItem("jga-quiz-lead-already-user") === "1"; } catch { return false; }
  });

  async function submitQuizLead() {
    if (quizLeadStatus === "submitting") return;
    const email = quizLeadEmail.trim().toLowerCase();
    if (!email || !email.includes("@") || email.length < 5) {
      setQuizLeadError("Please enter a valid email");
      setQuizLeadStatus("error");
      return;
    }
    setQuizLeadStatus("submitting");
    setQuizLeadError("");
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const idObj = identity ? TRAVEL_IDENTITIES[identity] : null;
      // Richer payload — backend can store these for behavior correlation later.
      // The `answers` array lets backend recompute identity if our frontend
      // logic changes, AND lets analytics see WHY a user got their identity.
      const payload = {
        email,
        identityType: identity || null,
        identityEmoji: idObj?.emoji || null,
        source: "quiz_reveal",
        capturedAt: new Date().toISOString(),
        // Quiz answers as {qIdx, optIdx} pairs — matches the schema the backend
        // shipped Jun 11 2026: quiz_leads.quiz_answers is JSONB, sanitized
        // server-side to max 50 entries with integer qIdx/optIdx. The state
        // shape already matches the wire shape, so no transformation needed.
        // Backend now uses these to power the admin Quiz Leads breakdown view
        // (% choosing each option per question per identity).
        quizAnswers: Array.isArray(answers) ? answers : [],
        referrer: (typeof document !== "undefined" ? document.referrer : "") || null,
        userAgent: (typeof navigator !== "undefined" ? navigator.userAgent : "") || null,
      };
      console.log("[JGA Quiz Lead] Submitting:", { email, identity, answersCount: payload.quizAnswers.length });
      const res = await fetch(`${API}/api/quiz-lead`, {
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      console.log("[JGA Quiz Lead] Response:", res.status, data);
      // Accept any 2xx as success — don't require an explicit `success: true` field
      if (res.ok && !data.error) {
        try { localStorage.setItem("jga-quiz-lead-captured", email); } catch {}
        // Response shapes (backend contract as of Jun 25, 2026):
        //   { alreadyUser: true }                 → email belongs to registered user
        //   { leadId: N, captured: true }         → brand-new lead
        //   { leadId: N, updated: true }          → repeat submission (< 7 days)
        // Check alreadyUser FIRST so if a response ever includes both fields,
        // the existing-user path correctly wins (relevant if backend changes
        // to also create a quiz_leads row for existing users).
        if (data.alreadyUser) {
          // Legitimate no-lead case — the email belongs to an existing registered
          // user, so the backend skipped creating a lead row. Not an error.
          // Set a flag so the captured UI can show "sign in" instead of "check
          // your inbox" — otherwise the user sees a misleading "Check your
          // inbox" message when no email was actually sent.
          console.log("[JGA Quiz Lead] Email already belongs to a registered user — no new lead created (alreadyUser: true).");
          try { sessionStorage.setItem("jga-quiz-lead-already-user", "1"); } catch {}
          setQuizLeadAlreadyUser(true);
        } else if (data.leadId || data.id) {
          // If backend returned a lead identifier, store it for downstream
          // behavior correlation. Backend may or may not return this — if
          // missing, it's a strong signal the lead wasn't actually persisted
          // (a common cause of "user revealed identity but doesn't show up
          // in the backend" reports). Logged so Cas can verify in devtools.
          try { localStorage.setItem("jga-quiz-lead-id", String(data.leadId || data.id)); } catch {}
          console.log("[JGA Quiz Lead] Backend confirmed lead id:", data.leadId || data.id);
          // Clear the alreadyUser flag if this is a fresh successful lead capture
          try { sessionStorage.removeItem("jga-quiz-lead-already-user"); } catch {}
          setQuizLeadAlreadyUser(false);
        } else {
          console.warn("[JGA Quiz Lead] Backend returned 2xx but NO leadId — verify the /api/quiz-lead endpoint is actually persisting to a quiz_leads table. See backend handoff doc.");
          setQuizLeadAlreadyUser(false);
        }
        setQuizLeadStatus("captured");
        if (typeof window.jgaTrack === "function") window.jgaTrack("lead_captured", { identity: identity || null, source: "quiz_reveal", leadId: data.leadId || data.id || null });
      } else {
        console.error("[JGA Quiz Lead] Backend rejected:", res.status, data);
        setQuizLeadError(data.error || "Couldn't save right now — try again?");
        setQuizLeadStatus("error");
      }
    } catch (err) {
      console.error("[JGA Quiz Lead] Network error:", err);
      setQuizLeadError("Network issue — try again?");
      setQuizLeadStatus("error");
    }
  }

  function handleAnswer(optIdx) {
    if (animating) return;
    setSelected(optIdx);
    setAnimating(true);
    const newAnswers = [...answers, { qIdx, optIdx }];
    setTimeout(() => {
      setAnimating(false);
      setSelected(null);
      if (qIdx < QUIZ_QUESTIONS.length - 1) {
        setQIdx(q => q + 1);
        setAnswers(newAnswers);
      } else {
        const id = computeIdentity(newAnswers);
        setIdentity(id);
        setAnswers(newAnswers);
        setPhase("reveal");
        // Pre-save so identity persists even if user closes before tapping Accept
        try {
          localStorage.setItem("jga-traveler-type", id);
          localStorage.setItem("jga-onboarded", "true");
          localStorage.setItem("jga-traveler-identity", JSON.stringify({ name: id, ...TRAVEL_IDENTITIES[id] }));
        } catch(e) {}
        // Track quiz completion
        if (typeof window.jgaTrack === "function") window.jgaTrack("quiz_completed", { identity: id });
      }
    }, 400);
  }

  function handleAccept() {
    // DIAGNOSTIC: log every time this fires, with the current registered value.
    // Used to debug the "quiz POST doesn't fire" bug — if this log appears but
    // [JGA Identity Sync] STARTING doesn't, registered is false at click time.
    console.log("[JGA Quiz] handleAccept called. registered:", registered, "identity:", identity, "savedIdentity:", savedIdentity);
    const id = TRAVEL_IDENTITIES[identity];
    // Extract companion preference from the quiz answer (Q3)
    let travelCompanion = null;
    try {
      const compIdx = QUIZ_QUESTIONS.findIndex(q => q.id === "companion");
      const compAnswer = answers.find(a => a.qIdx === compIdx);
      if (compAnswer) {
        travelCompanion = QUIZ_QUESTIONS[compIdx].options[compAnswer.optIdx]?.companion || null;
      }
    } catch(e) {}
    try {
      localStorage.setItem("jga-onboarded", "true");
      localStorage.setItem("jga-traveler-type", identity);
      localStorage.setItem("jga-traveler-identity", JSON.stringify({ name: identity, ...id }));
      try { localStorage.setItem("jga-quiz-answers", JSON.stringify(answers || [])); } catch {}
      if (travelCompanion) localStorage.setItem("jga-travel-companion", travelCompanion);
      // Mark quiz as completed in THIS browser session — used by registration to know
      // the identity is fresh (not leftover from a previous user's session on this device).
      // sessionStorage is per-tab and clears on tab close, which is exactly what we want.
      try { sessionStorage.setItem("jga-quiz-completed-this-session", identity); } catch {}
      if (typeof window.jgaTrack === "function") window.jgaTrack("identity_selected", { identity, companion: travelCompanion });
    } catch {}

    // ── Sync identity to backend ──
    // Fires whenever a registered user completes the quiz (initial or retake)
    // and clicks "Take me to my matches →". Backend stores travel_identity,
    // travel_companion, and quiz_answers; updates travel_identity_set_at.
    //
    // Previous version had a sessionStorage flag guard that compared the just-
    // saved flag against the just-set identity. The intent was preventing
    // stale-state sync, but in practice it blocked legitimate retakes (e.g.
    // when localStorage.setItem threw in Safari Private mode and the flag
    // was never set to the new value, the guard then bailed out). With our
    // newer auth flow (clean token validation, _signalSessionExpired on 401,
    // localStorage cleanup on login/logout), the guard's protection is
    // redundant — token presence is sufficient to confirm session validity.
    if (registered) {
      console.log("[JGA Identity Sync] STARTING — identity:", identity, "companion:", travelCompanion, "answers:", answers.length);
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      let token = null;
      try { token = localStorage.getItem("jga_token"); } catch {}
      if (token) {
        // Wrap in a function so we can retry once on network failure.
        // Identity sync is critical for cross-device — if mobile POST fails
        // silently, desktop will never see the new identity.
        const postIdentity = async (attempt) => {
          try {
            const r = await fetch(`${API}/api/users/identity`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
              // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
              body: JSON.stringify({
                travelIdentity: identity,
                travelCompanion: travelCompanion || null,
                quizAnswers: answers,
              }),
            });
            if (r.status === 401) {
              if (typeof _signalSessionExpired === "function") _signalSessionExpired("identity-sync");
              return { ok: false, status: 401 };
            }
            if (!r.ok) {
              const body = await r.json().catch(() => ({}));
              console.error(`[JGA Identity Sync] ❌ Backend rejected (attempt ${attempt})`, r.status, body);
              return { ok: false, status: r.status };
            }
            console.log(`[JGA Identity Sync] ✓ Saved to backend (attempt ${attempt}):`, identity, "/ companion:", travelCompanion);
            return { ok: true };
          } catch (err) {
            console.error(`[JGA Identity Sync] ❌ Network error (attempt ${attempt})`, err);
            return { ok: false, networkError: true };
          }
        };
        // First attempt — and retry once after 1.5s if it failed for a non-auth reason
        postIdentity(1).then(result => {
          if (!result.ok && result.status !== 401) {
            console.warn("[JGA Identity Sync] Will retry in 1.5s…");
            setTimeout(() => {
              postIdentity(2).then(retryResult => {
                if (!retryResult.ok && retryResult.status !== 401) {
                  console.error("[JGA Identity Sync] ❌❌ Both attempts failed. Identity NOT synced to backend. Cross-device sync will not work for this identity until user re-takes the quiz.");
                }
              });
            }, 1500);
          }
        });
      } else {
        console.warn("[JGA Identity Sync] User registered but no auth token — skipping backend sync");
      }
    }

    onSelect(identity, travelCompanion);
  }

  const progress = ((qIdx) / QUIZ_QUESTIONS.length) * 100;
  const q = QUIZ_QUESTIONS[qIdx];
  const id = identity ? TRAVEL_IDENTITIES[identity] : null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Travel identity quiz" style={{
      position: "fixed", inset: 0, zIndex: 400,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "20px 16px 40px",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      background: darkMode
        ? "radial-gradient(ellipse at 30% 20%, rgba(27,58,92,0.08) 0%, rgba(5,8,16,0.98) 60%)"
        : "radial-gradient(ellipse at 30% 20%, rgba(27,58,92,0.12) 0%, rgba(240,248,250,0.98) 60%)",
      WebkitBackdropFilter: "blur(20px)", backdropFilter: "blur(20px)",
      animation: "fadeIn 0.4s ease",
    }}>
      <div style={{ width: "100%", maxWidth: 480, animation: "fadeIn 0.5s ease 0.1s both", margin: "auto 0", paddingTop: 8 }}>

        {/* Close X — only visible during reveal phase (intro/quiz must be completed) */}
        {phase === "reveal" && registered && (
          <button onClick={() => onSelect(identity)} aria-label="Close"
            style={{
              position: "sticky", top: 8, marginLeft: "auto", marginBottom: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: 18,
              background: darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.06)",
              WebkitBackdropFilter: "blur(10px)", backdropFilter: "blur(10px)",
              border: `1px solid ${darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.12)"}`,
              color: darkMode ? "#1B3A5C" : "#FAFAF5",
              cursor: "pointer", fontFamily: "inherit", fontSize: 16, fontWeight: 700,
              zIndex: 10,
            }}>
            ✕
          </button>
        )}

        {/* ── INTRO ── */}
        {phase === "intro" && (
          <div style={{ textAlign: "center" }}>
            {/* Mono caps eyebrow — matches the editorial style used across
                the app (Surprise Me, dashboard sections, etc.) */}
            <div style={{
              fontSize: 10,
              letterSpacing: 3,
              color: "#1B3A5C",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 18,
            }}>
              Travel Identity™
            </div>

            {/* Editorial title — solid dark color for contrast on mobile,
                with the second word in serif italic for accent. Previously
                this used a mauve gradient with WebkitTextFillColor:transparent
                which was unreadable on light backgrounds. */}
            <h1 style={{
              margin: "0 0 14px",
              fontSize: 30,
              fontWeight: 900,
              lineHeight: 1.1,
              letterSpacing: -0.5,
              color: textPrimary,
            }}>
              What kind of{" "}
              <span style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontStyle: "italic",
                fontWeight: 500,
                color: "#1B3A5C",
              }}>
                traveler
              </span>
              <br/>
              are you?
            </h1>
            <p style={{ fontSize: 14, color: textMuted, lineHeight: 1.65, margin: "0 auto 24px", maxWidth: 380 }}>
              Take this short quiz and we'll reveal your Travel Identity™ — a personality profile that matches you with destinations that actually fit who you are.
            </p>

            {/* Constellation preview card — same night-sky treatment as the
                dashboard identity tile and the reveal card, so users see
                what they're working toward. Tappable: starts the quiz. */}
            {(() => {
              const sampleNames = Object.keys(TRAVEL_IDENTITIES).slice(0, 6);
              const currentName = sampleNames[cardIdx % sampleNames.length] || sampleNames[0];
              const currentId = TRAVEL_IDENTITIES[currentName];
              if (!currentId) return null;
              const skyEntry = IDENTITY_SKY[currentName] || IDENTITY_SKY["Romantic Wanderer"];
              return (
                <button key={cardIdx}
                  onClick={() => { setPhase("quiz"); if (typeof window.jgaTrack === "function") window.jgaTrack("quiz_started", { from: "teaser_card" }); }}
                  aria-label={`Take the quiz — see if you're a ${currentName} or one of the other Travel Identities`}
                  style={{
                    width: "100%",
                    borderRadius: 18,
                    overflow: "hidden",
                    marginBottom: 18,
                    background: getIdentitySky(currentName),
                    padding: "16px 16px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    boxShadow: `0 12px 32px ${skyEntry.deep}55, 0 0 0 1px ${skyEntry.accent}30`,
                    animation: "fadeIn 0.6s ease",
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    border: "none",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 16px 40px ${skyEntry.deep}66, 0 0 0 1.5px ${skyEntry.accent}55`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 12px 32px ${skyEntry.deep}55, 0 0 0 1px ${skyEntry.accent}30`; }}>
                  {/* Mini constellation chart — wrapped in fixed-size div
                      since the default variant uses absolute positioning */}
                  <div style={{ flexShrink: 0, width: 72, height: 50, position: "relative", borderRadius: 10, overflow: "hidden" }}>
                    <ConstellationChart identity={currentId} identityName={currentName} variant="share" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 9,
                      letterSpacing: 2.5,
                      color: skyEntry.accent,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      textTransform: "uppercase",
                      marginBottom: 4,
                      fontWeight: 700,
                    }}>
                      Travel Identity™
                    </div>
                    <div style={{
                      fontSize: 19,
                      fontWeight: 500,
                      color: "#FAFAF5",
                      marginBottom: 4,
                      lineHeight: 1.1,
                      letterSpacing: -0.3,
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontStyle: "italic",
                    }}>
                      {currentName}
                    </div>
                    <p style={{ fontSize: 11.5, color: "rgba(250,250,245,0.78)", lineHeight: 1.5, margin: 0, fontStyle: "italic" }}>
                      "{currentId.desc}"
                    </p>
                  </div>
                </button>
              );
            })()}

            {/* Dots indicator — refined sizing/color to match editorial palette */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 22 }}>
              {[0,1,2,3,4,5].map(i => (
                <span key={i} style={{
                  width: i === cardIdx ? 18 : 5,
                  height: 5,
                  borderRadius: 3,
                  background: i === cardIdx ? "#1B3A5C" : "rgba(27,58,92,0.18)",
                  transition: "all 0.4s ease",
                }} />
              ))}
            </div>

            {/* Maroon CTA matching the rest of the app */}
            <button onClick={() => { setPhase("quiz"); if (typeof window.jgaTrack === "function") window.jgaTrack("quiz_started", {}); }} style={{
              width: "100%", padding: "15px", borderRadius: 14, border: "none",
              background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
              color: "#FAFAF5", fontWeight: 800, fontSize: 15, cursor: "pointer",
              fontFamily: "inherit", marginBottom: 10,
              boxShadow: "0 8px 22px rgba(27,58,92,0.28)",
              letterSpacing: 0.3,
            }}>
              Start Quiz →
            </button>
            {/* Sign-in path for returning users — typical scenario is a user
                who took the quiz on another device or after clearing localStorage,
                who shouldn't have to retake. Underlined link styling so it reads
                as secondary action, not a competing primary CTA. */}
            <button onClick={() => { if (typeof onOpenLogin === "function") onOpenLogin(); }}
              style={{ background: "none", border: "none", color: "#1B3A5C", fontSize: 13, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", display: "block", margin: "0 auto 8px" }}>
              Already have an identity? Sign in →
            </button>
            <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); } catch {} onSelect(null); }}
              style={{ background: "none", border: "none", color: textDim, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              Skip for now
            </button>
          </div>
        )}

        {/* ── QUIZ ── */}
        {phase === "quiz" && (
          <div key={qIdx} style={{ animation: "fadeIn 0.3s ease" }}>
            {/* Passport progress indicator */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: textMuted, fontWeight: 600, letterSpacing: 0.3 }}>
                  Building Your Travel Identity™ <span style={{ color: teal }}>({qIdx + 1} of {QUIZ_QUESTIONS.length})</span>
                </span>
                {/* Passport icon stamps */}
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  {Array.from({ length: QUIZ_QUESTIONS.length }).map((_, i) => {
                    const filled = i < qIdx + 1;
                    const glowColor = "#4A8CC4";
                    return (
                      <div key={i} style={{
                        width: i === qIdx ? 28 : 18,
                        height: i === qIdx ? 28 : 18,
                        borderRadius: "50%",
                        border: `2px solid ${filled ? glowColor : darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.12)"}`,
                        background: filled ? (i === qIdx ? `radial-gradient(circle, ${glowColor}60, ${glowColor}20)` : `${glowColor}25`) : "transparent",
                        boxShadow: i === qIdx ? `0 0 14px ${glowColor}90, 0 0 6px ${glowColor}60` : filled ? `0 0 6px ${glowColor}40` : "none",
                        transition: "all 0.35s cubic-bezier(0.34,1.4,0.64,1)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {filled && i !== qIdx && (
                          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5 L4 7 L8 3" stroke={glowColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {i === qIdx && (
                          <svg width="12" height="12" viewBox="0 0 44 44" fill="none">
                            <circle cx="22" cy="22" r="19" fill="none" stroke={glowColor} strokeWidth="3"/>
                            <ellipse cx="22" cy="22" rx="8" ry="17" fill="none" stroke={glowColor} strokeWidth="2"/>
                            <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={glowColor} strokeWidth="2" fill="none"/>
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Thin progress line */}
              <div style={{ height: 2, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)", borderRadius: 1, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg,#4A8CC4,#0F2747)", borderRadius: 1, transition: "width 0.4s ease", boxShadow: "0 0 6px rgba(27,58,92,0.6)" }} />
              </div>
            </div>

            <h2 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 800, color: textPrimary, lineHeight: 1.3 }}>
              {q.q}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {q.options.map((opt, i) => (
                <button key={i} onClick={() => handleAnswer(i)}
                  aria-label={opt.text}
                  style={{
                    padding: "16px 18px", borderRadius: 14, border: `2px solid ${selected === i ? "#4A8CC4" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.08)"}`,
                    background: selected === i ? "rgba(27,58,92,0.12)" : darkMode ? "rgba(27,58,92,0.045)" : "#ffffff",
                    cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                    fontSize: 14, fontWeight: selected === i ? 700 : 500,
                    color: selected === i ? "#4A8CC4" : textPrimary,
                    transition: "all 0.2s",
                    transform: selected === i ? "scale(1.01)" : "scale(1)",
                    boxShadow: selected === i ? "0 4px 20px rgba(27,58,92,0.2)" : darkMode ? "none" : "0 1px 4px rgba(0,0,0,0.06)",
                  }}>
                  {opt.text}
                </button>
              ))}
            </div>

            {qIdx > 0 && (
              <button onClick={() => { setQIdx(q => q - 1); setAnswers(a => a.slice(0,-1)); }}
                style={{ marginTop: 16, background: "none", border: "none", color: textDim, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                ← Back
              </button>
            )}
          </div>
        )}

        {/* ── REVEAL ── */}
        {phase === "reveal" && id && (
          <div style={{ animation: "revealBurst 0.6s cubic-bezier(0.34,1.56,0.64,1)" }}>
            <style>{`@keyframes revealBurst { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} } @keyframes emojiPop { from{transform:scale(0);opacity:0} to{transform:scale(1);opacity:1} }`}</style>

            {/* ── REVEAL CARD ──
                Sealed only when user is NOT registered AND has not yet
                captured their email. Once email is captured (soft lead),
                identity reveals — the reveal is the reward for committing
                email, not the friction of full registration. */}
            {!registered && quizLeadStatus !== "captured" ? (
              <div style={{
                position: "relative",
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}40`,
                marginBottom: 18,
                boxShadow: `0 4px 24px rgba(20,18,24,0.22)`,
                background: getIdentitySky(identity),
              }}>
                {/* Header strip — chart number stays redacted too */}
                <div style={{ padding: "14px 18px 4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "rgba(250,250,245,0.55)", textTransform: "uppercase" }}>
                    Travel Identity
                  </div>
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 2, color: "rgba(250,250,245,0.4)", textTransform: "uppercase" }}>
                    🔒 Sealed
                  </div>
                </div>

                {/* Sealed chart — placeholder constellation behind lock */}
                <div style={{ position: "relative", width: "100%", aspectRatio: "200 / 140", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 200 140" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.18 }}>
                    {/* Decorative scattered stars — abstract, not the real constellation */}
                    {[[35,32],[68,48],[102,28],[135,52],[168,36],[42,82],[78,98],[112,72],[150,92],[180,78],[58,118],[120,110]].map(([cx,cy], i) => (
                      <circle key={i} cx={cx} cy={cy} r={2} fill={(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent} />
                    ))}
                  </svg>
                  {/* Center wax-seal style lock */}
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: `radial-gradient(circle at 30% 30%, ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}cc, ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).deep}ee 70%)`,
                    border: `1px solid ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}88`,
                    boxShadow: `0 4px 16px rgba(0,0,0,0.35), inset 0 1px 2px rgba(255,255,255,0.25)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    position: "relative", zIndex: 2,
                  }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="5" y="11" width="14" height="10" rx="2" stroke="#FAFAF5" strokeWidth="2" fill="rgba(250,250,245,0.08)"/>
                      <path d="M8 11 V8 a4 4 0 0 1 8 0 V11" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                </div>

                {/* Redacted name — two-line placeholder matching real card layout */}
                <div style={{ padding: "10px 22px 4px", textAlign: "center" }}>
                  <div style={{
                    display: "inline-block",
                    height: 26, width: "55%", marginBottom: 6,
                    borderRadius: 4,
                    background: "rgba(250,250,245,0.12)",
                  }} />
                  <div style={{
                    display: "block", margin: "0 auto",
                    height: 26, width: "70%",
                    borderRadius: 4,
                    background: `${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}22`,
                  }} />
                </div>

                {/* Sealed description */}
                <p style={{
                  margin: "16px 22px 18px",
                  textAlign: "center",
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "rgba(250,250,245,0.78)",
                  lineHeight: 1.55,
                }}>
                  Your Travel Identity™ is ready to be revealed.
                </p>
              </div>
            ) : (
            <div style={{
              position: "relative",
              borderRadius: 14,
              overflow: "hidden",
              border: `1px solid ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}33`,
              marginBottom: 18,
              boxShadow: `0 4px 24px rgba(20,18,24,0.18)`,
              background: getIdentitySky(identity),
            }}>
              {/* Header strip — chart label only (no catalog number) */}
              <div style={{ padding: "14px 18px 4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "rgba(250,250,245,0.55)", textTransform: "uppercase" }}>
                  {savedIdentity === identity && savedIdentity ? "Welcome Back" : "Travel Identity"}
                </div>
              </div>

              {/* Constellation — aspect-ratio container */}
              <div style={{ position: "relative", width: "100%", aspectRatio: "200 / 140" }}>
                <ConstellationChart identity={id} identityName={identity} variant="inApp" animate={true} />
              </div>

              {/* Name — two lines, italic accent on second */}
              <div style={{ padding: "10px 22px 4px", textAlign: "center" }}>
                {(() => {
                  const nameWords = String(identity || "").split(" ");
                  const firstLine = `The ${nameWords[0] || ""}`;
                  const secondLine = nameWords.slice(1).join(" ");
                  const accent = (IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent;
                  return (
                    <>
                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 28, fontWeight: 500, color: "#FAFAF5", lineHeight: 1.1, letterSpacing: "-0.4px" }}>
                        {firstLine}
                      </div>
                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: "italic", fontSize: 28, fontWeight: 500, color: accent, lineHeight: 1.1, letterSpacing: "-0.4px" }}>
                        {secondLine}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Description quote */}
              <p style={{
                margin: "10px 22px 14px",
                textAlign: "center",
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontStyle: "italic",
                fontSize: 14,
                color: "rgba(250,250,245,0.85)",
                lineHeight: 1.55,
              }}>
                "{id.desc}"
              </p>

              {/* Dashed divider */}
              <div style={{ borderTop: "1px dashed rgba(250,250,245,0.2)", margin: "0 22px" }} />

              {/* Attribute grid — maps each star on the chart to its value.
                  The chart labels are just ENERGY/PACE/etc; the actual values
                  live here in readable form. */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, padding: "14px 22px 6px" }}>
                {STAR_ORDER.map(attr => {
                  const accentColor = (IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent;
                  return (
                    <div key={attr} style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: accentColor, textTransform: "uppercase", marginBottom: 4, fontWeight: 600 }}>
                        {attr}
                      </div>
                      <div style={{ fontSize: 11, color: "#FAFAF5", lineHeight: 1.3 }}>
                        {id[attr] || "—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Dashed divider */}
              <div style={{ borderTop: "1px dashed rgba(250,250,245,0.2)", margin: "6px 22px 0" }} />

              {/* 3-column info strip: TRIP | SUITS | VIBES */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "14px 22px 18px" }}>
                {(() => {
                  const modeNames = { surprise: "Surprise Me", explore: "Take Me Anywhere", mood: "Mood Match", trending: "Trending", affordable: "Most Affordable" };
                  const firstMode = Array.isArray(id.modes) && id.modes[0] ? (modeNames[id.modes[0]] || id.modes[0]) : "Mood Match";
                  const vibeStr = Array.isArray(id.vibes) ? id.vibes.slice(0, 2).map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(" · ") : "";
                  return (
                    <>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>Trip</div>
                        <div style={{ fontSize: 11, color: "#FAFAF5", fontWeight: 600 }}>{id.tripLength || "—"}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>Suits</div>
                        <div style={{ fontSize: 11, color: "#FAFAF5", fontWeight: 600 }}>{firstMode}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>Vibes</div>
                        <div style={{ fontSize: 11, color: "#FAFAF5", fontWeight: 600 }}>{vibeStr || "—"}</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            )}

            {/* Matched vibes preview — registered only (identity-derived) */}
            {registered && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: textMuted, marginBottom: 8 }}>Your matched vibes:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {id.vibes.map(v => {
                  const vibe = { beach: "🏖️ Beach", romance: "💑 Romance", wellness: "🧘 Wellness", luxury: "✨ Luxury", adventure: "🧗 Adventure", culture: "🏛️ Culture", food: "🍜 Food", nature: "🌿 Nature", history: "📜 History", art: "🎨 Art", nightlife: "🎶 Nightlife" };
                  return (
                    <span key={v} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 20, background: `${id.color}18`, border: `1px solid ${id.color}40`, color: id.color, fontWeight: 700 }}>
                      {vibe[v] || v}
                    </span>
                  );
                })}
              </div>
            </div>
            )}

            {/* ── "Here's where you should go next" — concrete preview ──
                Registered-only. Destination picks are scored against the
                identity's vibes, so showing them = revealing the identity. */}
            {registered && (() => {
              // Score destinations against identity vibes; pick top 3
              const matchedPicks = DESTINATIONS
                .filter(d => d.tags && id.vibes && id.vibes.some(v => d.tags.includes(v)))
                .map(d => ({
                  ...d,
                  matchScore: id.vibes.filter(v => d.tags.includes(v)).length,
                }))
                .sort((a, b) => b.matchScore - a.matchScore)
                .slice(0, 3);
              if (matchedPicks.length === 0) return null;
              return (
                <div style={{ marginBottom: 16, padding: "16px 18px", borderRadius: 14, border: `1.5px solid ${id.color}40`, background: `linear-gradient(135deg, ${id.color}10, ${id.color}03)`, position: "relative", overflow: "hidden" }}>
                  {/* Soft glow accent */}
                  <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle, ${id.color}25 0%, transparent 70%)`, pointerEvents: "none" }}/>
                  <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" fill={`${id.color}30`} stroke={id.color} strokeWidth="1.4" strokeLinejoin="round"/>
                      </svg>
                      <div style={{ fontSize: 10, letterSpacing: 2.5, color: id.color, textTransform: "uppercase", fontWeight: 800 }}>Where you'd go next</div>
                    </div>
                    <h4 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 900, color: textPrimary, lineHeight: 1.2 }}>
                      Built for a {identity}
                    </h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                      {matchedPicks.map(d => (
                        <div key={d.name} style={{ borderRadius: 10, border: `1px solid ${id.color}30`, background: darkMode ? `${id.color}08` : "#fff", overflow: "hidden", position: "relative" }}>
                          <DestinationImage destName={d.name} country={d.country} emoji={d.emoji} height={70} rounded={0} accentColor={id.color}/>
                          <div style={{ padding: "6px 8px 8px" }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: textPrimary, lineHeight: 1.2 }}>{d.name}</div>
                            <div style={{ fontSize: 11, color: textMuted, marginTop: 1 }}>{d.country}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: textMuted, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
                      + more matches inside your dashboard
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* IMMEDIATE-ACTION ROW — Share + Continue browsing, right after
                identity reveal. Previously these were below the email-capture/
                Sign-Up box, which on mobile pushed them below the fold. Cas's
                ask: users should see "share my identity" and "browse the site"
                immediately at the peak engagement moment (identity revealed),
                not after they've already scrolled past a tall conversion CTA.
                Renders for both registered and unregistered users (registered
                shows just Share; unregistered shows Share + Continue browsing).
                The full share row + Continue browsing button below still
                render as backup discoverability for users who scroll. */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button onClick={() => {
                // Prefer image share (Instagram-friendly PNG with identity chart + URL)
                // over plain text. Falls back to text if shareIdentityCard not wired.
                if (shareIdentityCard) {
                  shareIdentityCard(id, identity);
                } else {
                  const text = `I'm a ${id.emoji} ${identity}! Find your travel identity at justgoanywhere.com ✈️`;
                  if (navigator.share) { navigator.share({ title: `I'm a ${identity}`, text, url: "https://justgoanywhere.com" }); }
                  else { navigator.clipboard?.writeText(text); }
                }
              }} style={{ flex: 1, padding: "11px", borderRadius: 10, border: `1px solid ${id.color}55`, background: `${id.color}12`, color: id.color, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                ↗ Share My Identity
              </button>
              {!registered && (
                <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); localStorage.setItem("jga-traveler-type", identity); sessionStorage.setItem("jga-quiz-completed-this-session", identity); } catch {} onSelect(identity); }}
                  style={{ flex: 1, padding: "11px", borderRadius: 10, border: `1px solid rgba(27,58,92,0.30)`, background: "transparent", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Browse the site →
                </button>
              )}
            </div>

            {/* "Take me to my matches" — registered only */}
            {registered && (
            <button onClick={handleAccept} style={{
              width: "100%", padding: "16px", borderRadius: 12, border: "none",
              background: `linear-gradient(135deg, ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).bright}, ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).mid})`,
              color: "#FAFAF5",
              fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit", marginBottom: 16,
              boxShadow: `0 4px 18px ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).mid}AA, 0 0 0 1px ${(IDENTITY_SKY[identity]||IDENTITY_SKY["Romantic Wanderer"]).accent}33`,
              letterSpacing: 0.3,
            }}>
              {savedIdentity === identity && savedIdentity ? "Show my matches →" : "Take me to my matches →"}
            </button>
            )}

            {/* Register/Login CTA — non-registered users */}
            {!registered && !quizLeadDismissed && (
              <>
                {quizLeadStatus !== "captured" ? (
                  // ── PROMINENT EMAIL CAPTURE ──
                  <div style={{
                    marginBottom: 16, padding: "20px 18px", borderRadius: 16,
                    border: `1px solid rgba(27,58,92,0.30)`,
                    background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                    boxShadow: `0 4px 24px rgba(27,58,92,0.12)`,
                    position: "relative", overflow: "hidden",
                  }}>
                    {/* Decorative glow */}
                    <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: `radial-gradient(circle, rgba(27,58,92,0.14) 0%, transparent 70%)`, pointerEvents: "none" }}/>
                    <div style={{ position: "relative" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="5" y="11" width="14" height="10" rx="2" stroke="#1B3A5C" strokeWidth="1.7" fill="rgba(27,58,92,0.12)"/>
                          <path d="M8 11 V8 a4 4 0 0 1 8 0 V11" stroke="#1B3A5C" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                        </svg>
                        <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>Unlock your reveal</div>
                      </div>
                      <div style={{ fontSize: 14, color: "#1B3A5C", lineHeight: 1.5, marginBottom: 14 }}>
                        Enter your email to reveal your Travel Identity™, your constellation chart, and destinations matched to who you are.
                      </div>

                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                          type="email"
                          value={quizLeadEmail}
                          onChange={e => { setQuizLeadEmail(e.target.value); if (quizLeadStatus === "error") { setQuizLeadStatus("idle"); setQuizLeadError(""); } }}
                          onKeyDown={e => { if (e.key === "Enter") submitQuizLead(); }}
                          placeholder="your@email.com"
                          disabled={quizLeadStatus === "submitting"}
                          autoComplete="email"
                          style={{
                            flex: 1, padding: "12px 14px", borderRadius: 10,
                            border: `1.5px solid ${quizLeadStatus === "error" ? "#4A8CC4" : "rgba(27,58,92,0.35)"}`,
                            background: "#fff",
                            color: "#1B3A5C", fontSize: 14, fontWeight: 500,
                            fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                            transition: "border-color 0.15s",
                          }}
                          onFocus={e => { e.currentTarget.style.borderColor = "#1B3A5C"; }}
                          onBlur={e => { e.currentTarget.style.borderColor = quizLeadStatus === "error" ? "#4A8CC4" : "rgba(27,58,92,0.35)"; }}
                        />
                        <button onClick={submitQuizLead}
                          disabled={quizLeadStatus === "submitting" || !quizLeadEmail.trim()}
                          style={{
                            padding: "12px 18px", borderRadius: 10, border: "none",
                            background: quizLeadEmail.trim() && quizLeadStatus !== "submitting" ? "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)" : "rgba(27,58,92,0.12)",
                            color: quizLeadEmail.trim() && quizLeadStatus !== "submitting" ? "#FAFAF5" : "#94A3B8",
                            fontSize: 13, fontWeight: 800, letterSpacing: 0.5,
                            cursor: quizLeadEmail.trim() && quizLeadStatus !== "submitting" ? "pointer" : "not-allowed",
                            fontFamily: "inherit", whiteSpace: "nowrap",
                            transition: "transform 0.15s",
                          }}>
                          {quizLeadStatus === "submitting" ? "Saving…" : "Save →"}
                        </button>
                      </div>

                      {quizLeadStatus === "error" && quizLeadError && (
                        <div style={{ fontSize: 11, color: "#4A8CC4", marginBottom: 6 }}>{quizLeadError}</div>
                      )}

                      <div style={{ textAlign: "center", marginTop: 6 }}>
                        <button onClick={() => setQuizLeadDismissed(true)}
                          style={{ background: "transparent", border: "none", color: textDim, fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: "4px 8px", textDecoration: "underline", textUnderlineOffset: 2 }}>
                          Skip — just show me matches
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // ── CAPTURED SUCCESS STATE ──
                  <div style={{
                    marginBottom: 16, padding: "16px 18px", borderRadius: 14,
                    border: `1px solid rgba(27,58,92,0.30)`,
                    background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#1B3A5C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M5 12 L 10 17 L 19 7" stroke="#FAFAF5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.3 }}>{quizLeadAlreadyUser ? "Welcome back!" : "Saved! Check your inbox."}</div>
                        <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2 }}>{quizLeadAlreadyUser ? "That email already has a JGA account. Sign in to see your travel identity." : "Your identity is unlocked — sign up to save it and book trips."}</div>
                      </div>
                    </div>
                    <div style={{ paddingTop: 10, borderTop: `1px dashed rgba(27,58,92,0.25)` }}>
                      {quizLeadAlreadyUser ? (
                        // EXISTING USER: a single primary "Sign In" CTA. No
                        // sign-up button (they don't need a new account) and
                        // no "check your inbox" because no email was sent.
                        // Pre-fills the email so they don't have to retype.
                        <>
                          <div style={{ fontSize: 11, color: "#4A6B8A", textAlign: "center", marginBottom: 8, lineHeight: 1.5 }}>
                            We didn't send a new email since you're already registered.
                          </div>
                          <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); } catch {} onOpenLogin(quizLeadEmail || ""); }}
                            style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 10px rgba(27,58,92,0.22)" }}>
                            Sign In to Your Account
                          </button>
                        </>
                      ) : (
                        // NEW LEAD: original two-button flow — Sign Up primary,
                        // Sign In secondary for the rare case the user IS
                        // already registered but the backend didn't flag it.
                        <>
                          <div style={{ fontSize: 11, color: "#4A6B8A", textAlign: "center", marginBottom: 8, lineHeight: 1.5 }}>
                            Want a full account to book trips and save itineraries?
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); localStorage.setItem("jga-traveler-type", identity); sessionStorage.setItem("jga-quiz-completed-this-session", identity); } catch {} onOpenRegister(quizLeadEmail || "", identity); }}
                              style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 10px rgba(27,58,92,0.22)" }}>
                              Sign Up
                            </button>
                            <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); localStorage.setItem("jga-traveler-type", identity); } catch {} onOpenLogin(); }}
                              style={{ flex: 1, padding: "10px", borderRadius: 10, border: `1px solid rgba(27,58,92,0.35)`, background: "transparent", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                              Sign In
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* If user dismissed/skipped capture, show smaller register option */}
            {!registered && quizLeadDismissed && quizLeadStatus !== "captured" && (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 12, border: `1px solid rgba(27,58,92,0.2)`, background: "rgba(27,58,92,0.04)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.4 }}>
                  Want to book trips? <button onClick={() => setQuizLeadDismissed(false)} style={{ background: "transparent", border: "none", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline" }}>Unlock your reveal</button> or
                </div>
                <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); localStorage.setItem("jga-traveler-type", identity); sessionStorage.setItem("jga-quiz-completed-this-session", identity); } catch {} onOpenRegister(quizLeadEmail || "", identity); }}
                  style={{ padding: "7px 14px", borderRadius: 9, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                  Register
                </button>
              </div>
            )}

            {/* Share row — visible to everyone post-reveal. (Was previously
                gated to `registered` only with a comment about leaking the
                sealed reveal, but by the time this row renders the identity
                has already been shown — there's nothing left to leak. Sharing
                is the primary word-of-mouth lever for the brand, especially
                for the just-revealed-but-not-yet-registered moment.) */}
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {/* Big share button */}
              <button onClick={() => {
                // Prefer image share over text — image is the viral asset
                if (shareIdentityCard) {
                  shareIdentityCard(id, identity);
                } else {
                  const text = `I'm a ${id.emoji} ${identity}! Find your travel identity at justgoanywhere.com ✈️`;
                  if (navigator.share) { navigator.share({ title: `I'm a ${identity}`, text, url: "https://justgoanywhere.com" }); }
                  else { navigator.clipboard?.writeText(text); }
                }
              }} style={{ flex: 2, padding: "12px", borderRadius: 12, border: `1px solid ${id.color}50`, background: `${id.color}14`, color: id.color, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", minHeight: 46, display: "flex", alignItems: "center", justifyContent: "center" }}>
                ↗ Share My Identity
              </button>
              {/* Icon-only social links. SVG dimensions normalized to 18x18
                  across all four so they appear visually consistent inside
                  their pills. Previously FB and TT were 16x18 which made
                  them look smaller next to SMS and IG (both 18x18). */}
              {[
                { key: "sms", label: "Text",      url: `sms:?body=${encodeURIComponent(`I'm a ${id.emoji} ${identity}! Find yours at justgoanywhere.com ✈️`)}`, svg: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
                { key: "ig",  label: "Instagram", share: true, svg: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>) },
                { key: "fb",  label: "Facebook",  url: `https://www.facebook.com/sharer/sharer.php?u=https://justgoanywhere.com&quote=${encodeURIComponent(`I'm a ${id.emoji} ${identity}! justgoanywhere.com`)}`, svg: (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>) },
                { key: "tt",  label: "TikTok",    share: true, svg: (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg>) },
              ].map(s => {
                const sharedStyle = { flex: 1, padding: "12px 4px", borderRadius: 12, border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.08)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", color: textMuted, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontFamily: "inherit", minHeight: 46 };
                return s.share ? (
                  <button key={s.key} onClick={() => {
                    const text = `I'm a ${id.emoji} ${identity}! Find your travel identity at justgoanywhere.com ✈️`;
                    if (navigator.share) { navigator.share({ title: `I'm a ${identity}`, text, url: "https://justgoanywhere.com" }); }
                    else { navigator.clipboard?.writeText(text); }
                  }} aria-label={`Share to ${s.label}`} style={sharedStyle}>
                    {s.svg}
                  </button>
                ) : (
                  <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} style={sharedStyle}>
                    {s.svg}
                  </a>
                );
              })}
            </div>

            {/* Continue browsing — only for unregistered users. Calls onSelect
                which closes the onboarding modal AND saves the identity to
                localStorage + state. User lands on the homepage with their
                identity already set; can browse without registering, and when
                they DO register later, the identity carries over (because
                handleRegisterSuccess reads from localStorage). Most features
                that require login (booking, saving itineraries, alerts) will
                still gate behind auth — this is the "let me look around"
                escape hatch from the auth wall. */}
            {!registered && (
              <button onClick={() => { try { localStorage.setItem("jga-onboarded", "true"); localStorage.setItem("jga-traveler-type", identity); sessionStorage.setItem("jga-quiz-completed-this-session", identity); } catch {} onSelect(identity); }}
                style={{ width: "100%", padding: "10px", borderRadius: 10, border: `1px solid ${id.color}40`, background: "transparent", color: id.color, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 4 }}>
                Continue browsing →
              </button>
            )}

            {/* Retake — ghost text at very bottom */}
            <button onClick={() => { setPhase("quiz"); setQIdx(0); setAnswers([]); setIdentity(null); if (typeof window.jgaTrack === "function") window.jgaTrack("quiz_started", { retake: true }); }}
              style={{ width: "100%", background: "none", border: "none", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: "8px 0" }}>
              🔄 Retake quiz
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Agent Waitlist Modal ────────────────────────────────────────
function AgentWaitlistModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, goldText, amberText, creamText } = useTheme();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", agency: "", iata: "", phone: "", markets: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const fieldStyle = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 12 };

  async function handleSubmit() {
    if (!form.firstName || !form.email || !form.agency) return;
    setSubmitting(true);
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      await fetch(`${API}/api/agent-waitlist`, {
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    } catch(e) { console.error(e); }
    setSubmitting(false);
    setSubmitted(true);
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff", border: `1px solid rgba(74,140,196,0.3)`, borderRadius: 24, padding: "32px 28px", width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease" }}>
        {submitted ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: goldText, marginBottom: 12 }}>You're on the List!</h2>
            <p style={{ fontSize: 14, color: textMuted, lineHeight: 1.7, marginBottom: 24 }}>
              Thanks for your interest in the Just Go Anywhere Agent Program. We'll be in touch as soon as we launch — you'll be among the first to know.
            </p>
            <button onClick={onClose} style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Back to Site
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: `1px solid ${borderColor}`, color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: goldText }}>Travel Agent Program</h2>
            </div>
            <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 22 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                {/* Mini briefcase + tag — matches the dashboard CTA icon for visual continuity */}
                <svg width="14" height="14" viewBox="0 0 32 32" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <rect x="4" y="11" width="22" height="15" rx="2" stroke={gold} strokeWidth="1.8" fill="rgba(74,140,196,0.12)"/>
                  <path d="M11 11 V 8 a 2 2 0 0 1 2-2 h 4 a 2 2 0 0 1 2 2 V 11" stroke={gold} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                  <rect x="13.5" y="16" width="3" height="2" rx="0.5" fill={gold}/>
                  <line x1="4" y1="17" x2="26" y2="17" stroke={gold} strokeWidth="1" opacity="0.4"/>
                  <path d="M23 12 L 27 12 L 28 14 L 27 16 L 23 16 Z" stroke={gold} strokeWidth="1.2" fill="rgba(74,140,196,0.18)" strokeLinejoin="round"/>
                </svg>
                <div style={{ fontSize: 13, fontWeight: 700, color: goldText }}>Coming Soon — Join the Waitlist</div>
              </div>
              <p style={{ fontSize: 12, color: textMuted, lineHeight: 1.7, margin: 0 }}>
                We're building a dedicated agent portal with referral codes, commission tracking, client booking management, and co-branded itineraries. Register your interest and we'll reach out first.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
              <div>
                <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>FIRST NAME *</div>
                <input {...f("firstName")} placeholder="Jane" style={fieldStyle} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>LAST NAME *</div>
                <input {...f("lastName")} placeholder="Smith" style={fieldStyle} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>EMAIL ADDRESS *</div>
            <input {...f("email")} type="email" placeholder="jane@travelagency.com" style={fieldStyle} />
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>AGENCY NAME *</div>
            <input {...f("agency")} placeholder="Smith Travel Group" style={fieldStyle} />
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>IATA NUMBER (if applicable)</div>
            <input {...f("iata")} placeholder="e.g. 12345678" style={fieldStyle} />
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>PHONE NUMBER</div>
            <input {...f("phone")} placeholder="+1 (555) 000-0000" style={fieldStyle} />
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>PRIMARY MARKETS / SPECIALTIES</div>
            <input {...f("markets")} placeholder="e.g. Caribbean, Europe, Luxury travel" style={fieldStyle} />

            <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: teal, fontWeight: 700, marginBottom: 6 }}>What agents get:</div>
              {["Unique referral code for your clients", "Commission on every confirmed booking", "Agent dashboard with client & earnings tracking", "Co-branded itineraries to share with clients", "Priority support and dedicated account manager"].map(b => (
                <div key={b} style={{ fontSize: 12, color: textMuted, marginBottom: 4 }}>✓ {b}</div>
              ))}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !form.firstName || !form.email || !form.agency}
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: (!form.firstName || !form.email || !form.agency) ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg,${gold},${amber})`, color: (!form.firstName || !form.email || !form.agency) ? textDim : "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: (!form.firstName || !form.email || !form.agency) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {submitting ? "Submitting…" : "Join the Agent Waitlist →"}
            </button>
            <p style={{ fontSize: 11, color: textDim, textAlign: "center", marginTop: 10 }}>
              * Required fields. We'll never share your information with third parties.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── AuraCard ──────────────────────────────────────────────────
// Renders a single aura reading (current or archived) with the
// forming-engine treatment: name + emoji + descriptor + 3 vibe chips
// + circular progress ring + percentage + tier-contextual copy.
// At 100% the ring and copy hide — a formed aura stands on its own.
// isPreview adds a "preview" framing for mid-month signups: an
// honest acknowledgment that the reading is incomplete and points to
// the next 1st as the first real reading.
//
// Window-seat palette per the rebrand spec: cloud-white background,
// sky-deep headline, sun-gold accent, sky-light chips with sky-deep
// text. No warm rust or terracotta on the card chrome itself —
// aura distinction is conveyed by the name/emoji, not the visual.
function AuraCard({ aura, percent, vibes = [], monthLabel = null, isPreview = false, isArchived = false, nextMonthLabel = null, stats = null }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, goldText } = useTheme();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const formed = percent >= 100;
  const tierCopy = auraFormingCopy(percent);
  // Circular progress geometry — small ring tucked into the card corner.
  const ringRadius = 14;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - percent / 100);
  const eyebrowText = isArchived
    ? (monthLabel ? `${monthLabel.toUpperCase()} AURA` : "ARCHIVED AURA")
    : isPreview
      ? "PREVIEW · YOUR READ SO FAR"
      : (monthLabel ? `${monthLabel.toUpperCase()} AURA` : "YOUR AURA");

  if (!aura) {
    return (
      <div style={{
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 16,
        background: darkMode ? "rgba(27,58,92,0.045)" : "#FAFAF5",
        border: `1px dashed ${borderColor}`,
      }}>
        <div style={{ fontSize: 10, letterSpacing: 1.8, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>{eyebrowText}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 4 }}>Your aura is taking shape</div>
        <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5, fontStyle: "italic" }}>
          {isPreview
            ? "Save destinations, take a trip, or journal an entry to start your reading."
            : "Take the identity quiz and start exploring — your aura forms from what you do."}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "relative",
      borderRadius: 14,
      padding: "16px 18px",
      marginBottom: 16,
      background: darkMode ? "rgba(27,58,92,0.045)" : "#FAFAF5",
      border: `1px solid ${darkMode ? "rgba(74,140,196,0.22)" : "rgba(27,58,92,0.14)"}`,
      boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset, 0 2px 8px rgba(27,58,92,0.04)",
    }}>
      {/* Top row — eyebrow + circular progress (formed states hide ring) */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.8, color: goldText, textTransform: "uppercase", fontWeight: 800 }}>{eyebrowText}</div>
        </div>
        {!formed && !isArchived && (
          <div style={{ position: "relative", width: 36, height: 36, flexShrink: 0 }}>
            <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r={ringRadius} fill="none" stroke="rgba(27,58,92,0.12)" strokeWidth="3" />
              <circle cx="18" cy="18" r={ringRadius} fill="none" stroke="#E89A3C" strokeWidth="3" strokeLinecap="round" strokeDasharray={ringCircumference} strokeDashoffset={ringOffset} transform="rotate(-90 18 18)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#1B3A5C" }}>{percent}</div>
          </div>
        )}
        {isArchived && (
          <div style={{ fontSize: 10, letterSpacing: 1.4, color: textDim, textTransform: "uppercase", fontWeight: 700, flexShrink: 0 }}>
            {percent < 100 ? `formed ${percent}%` : ""}
          </div>
        )}
      </div>
      {/* Body — emoji + name + descriptor */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 34, flexShrink: 0, lineHeight: 1 }}>{aura.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.15, marginBottom: 4 }}>{aura.label}</div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5, fontStyle: "italic" }}>{aura.desc}</div>
        </div>
      </div>
      {/* Vibe chips — top 3 from user's vibes */}
      {vibes && vibes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: tierCopy || (isPreview && nextMonthLabel) ? 12 : 0 }}>
          {vibes.slice(0, 3).map(v => (
            <span key={v} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 999, background: "rgba(74,140,196,0.16)", color: "#1B3A5C", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
              {v}
            </span>
          ))}
        </div>
      )}
      {/* Forming copy (hidden at 100% / archived) */}
      {tierCopy && !isArchived && (
        <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", lineHeight: 1.5 }}>{tierCopy}</div>
      )}
      {/* "What fills my ring?" — expandable breakdown.
         Only shown on the active current-month card (not archived, sub-100%)
         when stats are provided. Teaches the user what closes the ring. */}
      {!isArchived && !formed && stats && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowBreakdown(s => !s)}
            aria-expanded={showBreakdown}
            style={{ background: "none", border: "none", color: "#1B3A5C", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: "2px 0", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {showBreakdown ? "Hide breakdown" : "What fills my ring?"}
            <span style={{ fontSize: 9, transform: showBreakdown ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.15s" }} aria-hidden="true">▾</span>
          </button>
          {showBreakdown && (() => {
            // Row helper: a single line showing icon + label + counter
            const rows = [
              { label: "Identity quiz",     done: !!stats.hasIdentity,                    current: stats.hasIdentity ? 1 : 0, max: 1, points: "+25" },
              { label: "Someday stamps",    done: (stats.somedayStampCount || 0) >= 6,    current: Math.min(stats.somedayStampCount || 0, 6), max: 6, points: "+3 each" },
              { label: "Dreaming postcards", done: (stats.dreamingPostcardCount || 0) >= 6, current: Math.min(stats.dreamingPostcardCount || 0, 6), max: 6, points: "+3 each" },
              { label: "Trips added",       done: (stats.tripCount || 0) >= 4,            current: Math.min(stats.tripCount || 0, 4), max: 4, points: "+5 each" },
              { label: "Journal entries",   done: (stats.entryCount || 0) >= 3,           current: Math.min(stats.entryCount || 0, 3), max: 3, points: "+5 each" },
              { label: "Searches this month", done: (stats.searchCount || 0) >= 4,        current: Math.min(stats.searchCount || 0, 4), max: 4, points: "+1 each" },
            ];
            return (
              <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.05)" : "rgba(74,140,196,0.05)", border: `1px solid ${borderColor}` }}>
                <div style={{ display: "grid", gap: 7 }}>
                  {rows.map(r => {
                    const ratio = r.max > 0 ? r.current / r.max : 0;
                    const indicator = r.done ? "✓" : ratio > 0 ? "◐" : "○";
                    const indicatorColor = r.done ? "#1B3A5C" : ratio > 0 ? "#E89A3C" : textDim;
                    return (
                      <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
                        <span style={{ fontSize: 13, color: indicatorColor, fontWeight: 800, width: 14, textAlign: "center" }}>{indicator}</span>
                        <span style={{ flex: 1, color: textPrimary, fontWeight: r.done ? 700 : 500 }}>{r.label}</span>
                        <span style={{ color: r.done ? "#1B3A5C" : textMuted, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 36, textAlign: "right" }}>
                          {r.max === 1 ? (r.done ? "done" : "todo") : `${r.current}/${r.max}`}
                        </span>
                        <span style={{ color: textDim, fontSize: 10, minWidth: 50, textAlign: "right" }}>{r.points}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${borderColor}`, fontSize: 10.5, color: textDim, lineHeight: 1.5, fontStyle: "italic" }}>
                  Your ring resets each month — searches start fresh on the 1st. Identity, stamps, postcards, trips, and entries carry forward.
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {/* Preview footer — only on mid-month preview reads */}
      {isPreview && nextMonthLabel && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${borderColor}`, fontSize: 11, color: textMuted, fontStyle: "italic" }}>
          Your first full Aura arrives {nextMonthLabel} 1.
        </div>
      )}
    </div>
  );
}

// ── StampsSeedingModal ─────────────────────────────────────────
// Post-quiz onboarding step that lets new users seed their stamp wall
// with places they've already been. Three phases (intro → add → confirm),
// year picker inline, 75-stamp session cap, world map confirmation.
//
// Per spec (Jun 2026 Stamps History Seeding handoff):
//   - Intro screen with Skip + Start
//   - Add screen with chips + search + added list + counter
//   - Year picker is inline (per-stamp), includes "Don't remember" option
//   - 200-char optional note per stamp
//   - 75-stamp session cap with friendly stop message
//   - World map confirm: dots positioned by lat/lng + summary stats
//   - All seeded stamps get source='seeded_during_onboarding'
//   - Seeded stamps appear in Journal indistinguishably from other visited
//   - Identity recalc: not yet built (no engine exists); logs analytics
//     event for backend to consume when the engine ships
//   - Skip always available — intro AND mid-session
function StampsSeedingModal({ catalog, identity, travelerType, onSkip, onComplete, existingStampCount = 0, existingStampIds = [], addState = "visited" }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [phase, setPhase] = useState(existingStampCount > 0 ? "add" : "intro"); // intro | add | confirm
  // addedStamps: array of { id, yearVisited, note, seededAt }
  const [addedStamps, setAddedStamps] = useState([]);
  // activeYearPickerStamp: catalog entry currently in year-picker state
  const [activeYearPickerStamp, setActiveYearPickerStamp] = useState(null);
  const [pendingYear, setPendingYear] = useState(null);
  const [pendingNote, setPendingNote] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const SESSION_CAP = 500; // Safety net — effective cap is the catalog size (12 illustrated experiences as of launch). Counter no longer displays this number.

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4,
  ];

  // Catalog filtered to ONLY experiences (hasIllustration: true).
  // The 93 country placeholders are excluded — Cas's call: stamps are
  // experiences they've done (Cherry Blossoms, Northern Lights, Pyramids
  // of Giza, etc.), not countries.
  const experiencesCatalog = catalog.filter(c => c.hasIllustration);

  // Catalog filtered to chip suggestions, with already-added stamps excluded
  // (both current session and existing user stamps)
  const addedIds = new Set([...addedStamps.map(s => s.id), ...existingStampIds]);
  const chipSuggestions = experiencesCatalog.filter(c => !addedIds.has(c.id));

  // Search: typeahead against experiences-only catalog (max 8 results)
  const searchResults = (() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return experiencesCatalog
      .filter(c => !addedIds.has(c.id))
      .filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.country.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q)
      )
      .slice(0, 8);
  })();

  const openYearPicker = (catalogEntry) => {
    if (addedStamps.length >= SESSION_CAP) return; // cap enforced
    setActiveYearPickerStamp(catalogEntry);
    setPendingYear(null);
    setPendingNote("");
  };

  const confirmAddStamp = () => {
    if (!activeYearPickerStamp) return;
    const next = [...addedStamps, {
      id: activeYearPickerStamp.id,
      title: activeYearPickerStamp.title,
      country: activeYearPickerStamp.country,
      emoji: activeYearPickerStamp.emoji,
      lat: activeYearPickerStamp.lat,
      lng: activeYearPickerStamp.lng,
      yearVisited: pendingYear, // can be null = "I don't remember"
      note: pendingNote.trim() || null,
      seededAt: new Date().toISOString(),
    }];
    setAddedStamps(next);
    setActiveYearPickerStamp(null);
    setPendingYear(null);
    setPendingNote("");
    setSearchQuery("");
  };

  const cancelYearPicker = () => {
    setActiveYearPickerStamp(null);
    setPendingYear(null);
    setPendingNote("");
  };

  const removeStamp = (id) => {
    setAddedStamps(addedStamps.filter(s => s.id !== id));
  };

  const reachedCap = (existingStampCount + addedStamps.length) >= SESSION_CAP;

  // Derived for confirm phase: country count
  const countryCount = new Set(addedStamps.map(s => s.country)).size;

  // ────────────────────────────────────────────────────────────
  //   INTRO PHASE
  // ────────────────────────────────────────────────────────────
  if (phase === "intro") {
    return (
      <ModalShell onClose={onSkip}>
        <div style={{ padding: "40px 28px 28px", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.16em", color: "#E89A3C", marginBottom: 14, textTransform: "lowercase" }}>
            a quick start
          </div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.01em", lineHeight: 1.2, marginBottom: 14 }}>
            {addState === "someday" ? "Where do you dream of going?" : "Where have you already been?"}
          </h2>
          <p style={{ margin: 0, fontSize: 15, color: textMuted, lineHeight: 1.55, marginBottom: 24, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
            {addState === "someday"
              ? "Mark the experiences you'd love to chase — we'll start building your bucket list as collectible stamps."
              : "Mark the places you've already traveled — we'll start your stamp wall with your real history."}
            {identity && travelerType ? ` This also helps us refine what we know about you as a ${travelerType}.` : ""}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: textDim, lineHeight: 1.5, marginBottom: 32 }}>
            No pressure. You can skip and add stamps later from Journal anytime.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
            <button onClick={() => setPhase("add")}
              style={{
                padding: "13px 18px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                color: "#FAFAF5", fontSize: 14, fontWeight: 800, cursor: "pointer",
                fontFamily: "inherit", letterSpacing: 0.3,
                boxShadow: "0 2px 8px rgba(232,154,60,0.3)",
              }}>
              Start adding →
            </button>
            <button onClick={onSkip}
              style={{
                padding: "13px 18px", borderRadius: 10,
                border: "1px solid rgba(27,58,92,0.25)",
                background: "transparent", color: "#1B3A5C",
                fontSize: 14, fontWeight: 700, cursor: "pointer",
                fontFamily: "inherit", letterSpacing: 0.3,
              }}>
              Skip for now
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  // ────────────────────────────────────────────────────────────
  //   ADD PHASE
  // ────────────────────────────────────────────────────────────
  if (phase === "add") {
    return (
      <ModalShell onClose={onSkip}>
        {/* Header — counter + skip */}
        <div style={{ padding: "20px 22px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${borderColor}` }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 1.5, color: textMuted, fontWeight: 600, textTransform: "uppercase" }}>
              {existingStampCount > 0
                ? (addState === "someday" ? "Add to your someday list" : "Add more experiences")
                : (addState === "someday" ? "Pick your dream experiences" : "Mark where you've been")}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1B3A5C", marginTop: 3 }}>
              {existingStampCount + addedStamps.length} <span style={{ color: textMuted, fontWeight: 500, fontSize: 14 }}>stamp{(existingStampCount + addedStamps.length) === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button onClick={onSkip}
            style={{ background: "none", border: "none", color: "#4A8CC4", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "8px 4px" }}>
            Skip
          </button>
        </div>

        <div style={{ padding: "16px 22px 20px", maxHeight: "70vh", overflowY: "auto" }}>
          {/* Cap reached — friendly stop (cap is 500 as a safety net; this
              card effectively never fires with the current 12-experience
              catalog, but kept here for future growth). */}
          {reachedCap && (
            <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(232,154,60,0.08)", border: "1px solid rgba(232,154,60,0.25)", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#C8741D", marginBottom: 4 }}>
                That's a lot of stamps.
              </div>
              <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
                Tap Done to finish, or remove a stamp above if you want to swap one in.
              </div>
            </div>
          )}

          {/* Year picker — appears when a stamp is being added */}
          {activeYearPickerStamp && (
            <div style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid #1B3A5C`, background: "rgba(74,140,196,0.06)", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 22 }}>{activeYearPickerStamp.emoji}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1B3A5C" }}>{activeYearPickerStamp.title}</div>
                  <div style={{ fontSize: 10, letterSpacing: 1, color: textDim, textTransform: "uppercase", fontWeight: 600 }}>
                    {activeYearPickerStamp.country}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11, letterSpacing: 1.2, color: textMuted, fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                When did you go?
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {yearOptions.map(y => (
                  <button key={y} onClick={() => setPendingYear(y)}
                    style={{
                      padding: "7px 14px", borderRadius: 8,
                      border: pendingYear === y ? "1px solid #1B3A5C" : "1px solid rgba(27,58,92,0.2)",
                      background: pendingYear === y ? "#1B3A5C" : "transparent",
                      color: pendingYear === y ? "#FAFAF5" : "#1B3A5C",
                      fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}>
                    {y}
                  </button>
                ))}
                <button onClick={() => setPendingYear("earlier")}
                  style={{
                    padding: "7px 14px", borderRadius: 8,
                    border: pendingYear === "earlier" ? "1px solid #1B3A5C" : "1px solid rgba(27,58,92,0.2)",
                    background: pendingYear === "earlier" ? "#1B3A5C" : "transparent",
                    color: pendingYear === "earlier" ? "#FAFAF5" : "#1B3A5C",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Earlier
                </button>
                <button onClick={() => setPendingYear(null)}
                  style={{
                    padding: "7px 14px", borderRadius: 8,
                    border: pendingYear === null ? "1px solid #1B3A5C" : "1px solid rgba(27,58,92,0.2)",
                    background: pendingYear === null ? "#1B3A5C" : "transparent",
                    color: pendingYear === null ? "#FAFAF5" : "#1B3A5C",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Don't remember
                </button>
              </div>
              <textarea
                placeholder="Optional note — a moment, a meal, anything (200 chars)"
                value={pendingNote}
                onChange={e => setPendingNote(e.target.value.slice(0, 200))}
                maxLength={200}
                rows={2}
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 8,
                  border: "1px solid rgba(27,58,92,0.15)", background: "#FAFAF5",
                  fontSize: 12, color: textPrimary, fontFamily: "inherit",
                  resize: "none", boxSizing: "border-box", marginBottom: 10,
                }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={cancelYearPicker}
                  style={{
                    padding: "8px 14px", borderRadius: 8,
                    border: "1px solid rgba(27,58,92,0.2)",
                    background: "transparent", color: textMuted,
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Cancel
                </button>
                <button onClick={confirmAddStamp}
                  style={{
                    padding: "8px 18px", borderRadius: 8, border: "none",
                    background: "#1B3A5C", color: "#FAFAF5",
                    fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Add stamp
                </button>
              </div>
            </div>
          )}

          {/* Chip suggestions — only show when not actively picking + not at cap */}
          {!activeYearPickerStamp && !reachedCap && chipSuggestions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, letterSpacing: 1.2, color: textMuted, fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                Popular places
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {chipSuggestions.map(c => (
                  <button key={c.id} onClick={() => openYearPicker(c)}
                    style={{
                      padding: "8px 12px", borderRadius: 20,
                      border: "1px solid rgba(27,58,92,0.15)",
                      background: "#FAFAF5", color: "#1B3A5C",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit", display: "inline-flex",
                      alignItems: "center", gap: 6,
                    }}>
                    <span>{c.emoji}</span>
                    {c.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          {!activeYearPickerStamp && !reachedCap && (
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Search for a place… (Paris, Tokyo, Bali)"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: 10,
                  border: "1px solid rgba(27,58,92,0.2)", background: "#FAFAF5",
                  fontSize: 13, color: textPrimary, fontFamily: "inherit",
                  boxSizing: "border-box",
                }} />
              {searchResults.length > 0 && (
                <div style={{ marginTop: 8, borderRadius: 10, border: "1px solid rgba(27,58,92,0.1)", overflow: "hidden", background: "#FAFAF5" }}>
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => openYearPicker(r)}
                      style={{
                        width: "100%", padding: "10px 12px",
                        background: "transparent", border: "none",
                        borderBottom: "1px solid rgba(27,58,92,0.05)",
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                      <span style={{ fontSize: 18 }}>{r.emoji}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>{r.title}</div>
                        <div style={{ fontSize: 10, letterSpacing: 1, color: textDim, textTransform: "uppercase", fontWeight: 600 }}>
                          {r.country}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: "#4A8CC4", fontWeight: 800 }}>+</span>
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.trim() && searchResults.length === 0 && (
                <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 10, background: "rgba(27,58,92,0.04)", fontSize: 12, color: textMuted, fontStyle: "italic" }}>
                  No matches. Try a different spelling, or skip this one.
                </div>
              )}
            </div>
          )}

          {/* Added stamps list */}
          {addedStamps.length > 0 && (
            <div>
              <div style={{ fontSize: 11, letterSpacing: 1.2, color: textMuted, fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                Added ({addedStamps.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {addedStamps.map(s => (
                  <div key={s.id} style={{
                    padding: "10px 12px", borderRadius: 10,
                    border: "1px solid rgba(27,58,92,0.1)",
                    background: "#FAFAF5",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <span style={{ fontSize: 18 }}>{s.emoji}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C" }}>{s.title}</div>
                      <div style={{ fontSize: 10, letterSpacing: 1, color: textDim, textTransform: "uppercase", fontWeight: 600 }}>
                        {s.country} · {s.yearVisited === null ? "year unknown" : s.yearVisited === "earlier" ? "earlier" : s.yearVisited}
                      </div>
                    </div>
                    <button onClick={() => removeStamp(s.id)}
                      aria-label={`Remove ${s.title}`}
                      style={{ background: "none", border: "none", color: textDim, fontSize: 18, cursor: "pointer", padding: "4px 6px", fontFamily: "inherit", lineHeight: 1 }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer — Done */}
        {!activeYearPickerStamp && (
          <div style={{ padding: "14px 22px", borderTop: `1px solid ${borderColor}`, display: "flex", gap: 10, justifyContent: "space-between" }}>
            <button onClick={onSkip}
              style={{
                padding: "11px 18px", borderRadius: 10,
                border: "1px solid rgba(27,58,92,0.2)",
                background: "transparent", color: textMuted,
                fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}>
              Skip
            </button>
            <button onClick={() => setPhase("confirm")}
              disabled={addedStamps.length === 0}
              style={{
                padding: "11px 22px", borderRadius: 10, border: "none",
                background: addedStamps.length === 0 ? "rgba(27,58,92,0.12)" : "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                color: addedStamps.length === 0 ? textDim : "#FAFAF5",
                fontSize: 13, fontWeight: 800,
                cursor: addedStamps.length === 0 ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                boxShadow: addedStamps.length === 0 ? "none" : "0 2px 8px rgba(232,154,60,0.3)",
              }}>
              Done →
            </button>
          </div>
        )}
      </ModalShell>
    );
  }

  // ────────────────────────────────────────────────────────────
  //   CONFIRM PHASE — World map
  // ────────────────────────────────────────────────────────────
  if (phase === "confirm") {
    // Equirectangular projection: x = (lng + 180) * W/360, y = (90 - lat) * H/180
    const mapW = 600, mapH = 300;
    const project = (lat, lng) => ({
      x: ((lng + 180) / 360) * mapW,
      y: ((90 - lat) / 180) * mapH,
    });
    return (
      <ModalShell onClose={() => onComplete(addedStamps)}>
        <div style={{ padding: "28px 22px 22px", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.16em", color: "#E89A3C", marginBottom: 12, textTransform: "lowercase" }}>
            your wall is started
          </div>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.01em", lineHeight: 1.2, marginBottom: 6 }}>
            Welcome to your travel map.
          </h2>
          <div style={{ fontSize: 14, color: textMuted, marginBottom: 22 }}>
            <span style={{ fontWeight: 700, color: "#1B3A5C" }}>{addedStamps.length}</span> stamp{addedStamps.length !== 1 ? "s" : ""}
            {" · "}
            <span style={{ fontWeight: 700, color: "#1B3A5C" }}>{countryCount}</span> countr{countryCount !== 1 ? "ies" : "y"}
          </div>

          {/* World map — simple equirectangular dot plot */}
          <div style={{
            position: "relative",
            width: "100%", maxWidth: mapW, margin: "0 auto 24px",
            aspectRatio: `${mapW} / ${mapH}`,
            borderRadius: 14,
            background: "linear-gradient(180deg, #E8F0F7 0%, #F4F9FD 60%, #F0E8DC 100%)",
            border: "1px solid rgba(27,58,92,0.1)",
            overflow: "hidden",
          }}>
            <svg viewBox={`0 0 ${mapW} ${mapH}`} style={{ width: "100%", height: "100%", display: "block" }}>
              {/* Soft latitude lines for "earth-ness" */}
              <line x1="0" y1={mapH * 0.5} x2={mapW} y2={mapH * 0.5} stroke="rgba(27,58,92,0.08)" strokeDasharray="3 4" />
              <line x1={mapW * 0.5} y1="0" x2={mapW * 0.5} y2={mapH} stroke="rgba(27,58,92,0.08)" strokeDasharray="3 4" />
              {/* Stamp dots */}
              {addedStamps.map((s, idx) => {
                const { x, y } = project(s.lat, s.lng);
                return (
                  <g key={s.id} style={{ animation: `jga-fadeIn 0.4s ease-out ${idx * 0.06}s both` }}>
                    <circle cx={x} cy={y} r="8" fill="rgba(232,154,60,0.25)" />
                    <circle cx={x} cy={y} r="4" fill="#E89A3C" stroke="#FAFAF5" strokeWidth="1.5" />
                  </g>
                );
              })}
            </svg>
            <style>{`
              @keyframes jga-fadeIn { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
            `}</style>
          </div>

          <p style={{ margin: 0, fontSize: 13, color: textDim, lineHeight: 1.55, marginBottom: 26, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
            These are your starting stamps. You can add more anytime from Journal — and every JGA trip earns new stamps as you go.
          </p>

          <button onClick={() => onComplete(addedStamps)}
            style={{
              padding: "13px 30px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
              color: "#FAFAF5", fontSize: 14, fontWeight: 800, cursor: "pointer",
              fontFamily: "inherit", letterSpacing: 0.3,
              boxShadow: "0 2px 8px rgba(232,154,60,0.3)",
            }}>
            Go to my dashboard →
          </button>
        </div>
      </ModalShell>
    );
  }

  return null;
}

// ── PostcardsSeedingModal ───────────────────────────────────────
// Mirrors StampsSeedingModal but for postcards. Postcards are
// destinations users dream about — no year picker needed. Catalog
// is POSTCARDS_CATALOG; visual uses real PostcardIllustration so
// users pick by the art, not by name alone. Cap 50.
function PostcardsSeedingModal({ catalog, onSkip, onComplete, existingPostcardCount = 0, existingPostcardIds = [], addState = "dreaming" }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [addedPostcards, setAddedPostcards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const SESSION_CAP = 500; // Safety net — effective cap is the catalog size (58 illustrated postcards as of launch). Counter no longer displays this number.

  // Filter to catalog entries with bespoke illustrations available
  // (every entry in POSTCARDS_CATALOG has a matching illustration in
  // PostcardIllustration's switch — fallback handles any that don't.)
  const addedIds = new Set([...addedPostcards.map(p => p.id), ...existingPostcardIds]);
  const availablePool = catalog.filter(c => !addedIds.has(c.id));

  // Default view: show first 12 catalog entries as featured. Search
  // narrows by destName, country, or title.
  const searchResults = (() => {
    if (!searchQuery.trim()) return availablePool.slice(0, 12);
    const q = searchQuery.toLowerCase();
    return availablePool
      .filter(c =>
        (c.destName || "").toLowerCase().includes(q) ||
        (c.country || "").toLowerCase().includes(q) ||
        (c.title || "").toLowerCase().includes(q) ||
        (c.region || "").toLowerCase().includes(q)
      )
      .slice(0, 16);
  })();

  const reachedCap = (existingPostcardCount + addedPostcards.length) >= SESSION_CAP;

  const togglePostcard = (entry) => {
    const isAdded = addedPostcards.some(p => p.id === entry.id);
    if (isAdded) {
      setAddedPostcards(addedPostcards.filter(p => p.id !== entry.id));
    } else if (!reachedCap) {
      setAddedPostcards([...addedPostcards, {
        id: entry.id,
        destName: entry.destName,
        country: entry.country,
        title: entry.title,
        seededAt: new Date().toISOString(),
      }]);
    }
  };

  return (
    <ModalShell onClose={onSkip}>
      {/* Header — counter + skip */}
      <div style={{ padding: "20px 22px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${borderColor}` }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: textMuted, fontWeight: 600, textTransform: "uppercase" }}>
            {existingPostcardCount > 0
              ? (addState === "visited" ? "Add more memories" : "Add more dreams")
              : (addState === "visited" ? "Mark where you've been" : "Pick your dream destinations")}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1B3A5C", marginTop: 3 }}>
            {existingPostcardCount + addedPostcards.length} <span style={{ color: textMuted, fontWeight: 500, fontSize: 14 }}>postcard{(existingPostcardCount + addedPostcards.length) === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {addedPostcards.length > 0 && (
            <button onClick={() => onComplete(addedPostcards)}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#1B3A5C", color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              Done ({addedPostcards.length})
            </button>
          )}
          <button onClick={onSkip}
            style={{ background: "none", border: "none", color: "#4A8CC4", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "8px 4px" }}>
            {addedPostcards.length > 0 ? "Cancel" : "Skip"}
          </button>
        </div>
      </div>

      <div style={{ padding: "16px 22px 20px", maxHeight: "70vh", overflowY: "auto" }}>
        {reachedCap && (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(232,154,60,0.08)", border: "1px solid rgba(232,154,60,0.25)", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#C8741D", marginBottom: 4 }}>
              That's a lot of postcards.
            </div>
            <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
              Tap Done to finish, or remove a postcard below if you want to swap one in.
            </div>
          </div>
        )}

        {/* Search */}
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by city, country, or region…"
          style={{
            width: "100%", padding: "10px 14px", borderRadius: 10,
            border: `1px solid ${borderColor}`,
            background: darkMode ? "rgba(27,58,92,0.06)" : "rgba(250,250,245,0.6)",
            color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none",
            marginBottom: 16, boxSizing: "border-box",
          }}
        />

        {/* Featured / search results grid */}
        <div style={{ fontSize: 11, letterSpacing: 1.2, color: textMuted, fontWeight: 600, marginBottom: 10, textTransform: "uppercase" }}>
          {searchQuery.trim() ? `Matches (${searchResults.length})` : "Featured destinations"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {searchResults.map(entry => (
            <button key={entry.id}
              onClick={() => togglePostcard(entry)}
              disabled={reachedCap}
              style={{
                padding: 0, borderRadius: 12,
                border: "1px solid rgba(27,58,92,0.12)",
                background: "#FAFAF5", cursor: reachedCap ? "not-allowed" : "pointer",
                fontFamily: "inherit", textAlign: "left",
                overflow: "hidden", opacity: reachedCap ? 0.5 : 1,
                transition: "all 0.15s",
              }}>
              <div style={{ aspectRatio: "7 / 5", overflow: "hidden", background: "#1B3A5C" }}>
                <PostcardIllustration id={entry.id} />
              </div>
              <div style={{ padding: "8px 10px" }}>
                <div style={{ fontSize: 9, letterSpacing: "0.1em", color: textMuted, textTransform: "uppercase", fontWeight: 600 }}>
                  {entry.country}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1B3A5C", lineHeight: 1.2, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.destName || entry.title}
                </div>
                <div style={{ fontSize: 10, color: "#C8741D", fontWeight: 700, marginTop: 3 }}>
                  + Add to collection
                </div>
              </div>
            </button>
          ))}
        </div>

        {searchResults.length === 0 && searchQuery.trim() && (
          <div style={{ fontSize: 13, color: textMuted, textAlign: "center", padding: "20px 0" }}>
            No matches for "{searchQuery}". Try a different city or region.
          </div>
        )}

        {/* Currently added — shows what's in this session */}
        {addedPostcards.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${borderColor}` }}>
            <div style={{ fontSize: 11, letterSpacing: 1.2, color: textMuted, fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
              Adding ({addedPostcards.length})
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {addedPostcards.map(p => (
                <button key={p.id} onClick={() => togglePostcard({ id: p.id })}
                  style={{
                    padding: "5px 10px", borderRadius: 16, border: "1px solid rgba(27,58,92,0.2)",
                    background: "rgba(27,58,92,0.05)", color: "#1B3A5C",
                    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  {p.destName || p.title} ×
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// Simple shared modal shell — backdrop + centered card. Lets each phase
// own its layout/padding rather than fighting a shared scaffold.
function ModalShell({ children, onClose }) {
  const { darkMode } = useTheme();
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 80,
      background: "rgba(20,32,46,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
      animation: "jga-fadeIn 0.18s ease",
    }}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 540,
        background: darkMode ? "#1a1a1a" : "#FAFAF5",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 12px 40px rgba(20,32,46,0.45)",
        animation: "jga-fadeIn 0.22s ease",
      }}>
        {children}
      </div>
    </div>
  );
}



// ── MilestoneCard ──────────────────────────────────────────────
// Reusable inline card for capturing trip-level occasion context.
// Surfaces:
//   - TMA: between destination detail card and the See Itinerary /
//     Get on the waitlist choice row
//   - SM: as a step in the Surprise Me setup form, before matching runs
//
// Props:
//   value:        { type, otherText } | null
//   onChange:     (newValue) => void
//   onSkip:       () => void  — fired when user taps "Skip"
//   compact:      bool — tighter layout for SM's vertical form (default false)
//   destName:     string — used in the header copy when present
//
// Behavior:
//   - "just-because" pre-selected on mount (spec — first-class answer, not absence)
//   - Single-select (one occasion per trip)
//   - "Something else" reveals a 50-char free-text input
//   - Skip link in the corner — always available, preserves "Just because" silently
function MilestoneCard({ value, onChange, onSkip, compact = false, destName = null }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, goldText } = useTheme();
  const currentType = (value && value.type) || "just-because";
  const currentOtherText = (value && value.otherText) || "";

  function pick(type) {
    if (type === "other") {
      onChange({ type: "other", otherText: currentOtherText });
    } else {
      onChange({ type, otherText: null });
    }
  }

  function updateOtherText(text) {
    onChange({ type: "other", otherText: text.slice(0, 50) });
  }

  return (
    <div style={{
      padding: compact ? "14px 16px" : "16px 18px",
      borderRadius: 14,
      background: darkMode ? "rgba(27,58,92,0.05)" : "rgba(74,140,196,0.06)",
      border: `1px solid ${darkMode ? "rgba(74,140,196,0.2)" : "rgba(74,140,196,0.22)"}`,
      marginBottom: compact ? 14 : 18,
    }}>
      {/* Header row — title + Skip link */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.6, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>OCCASION</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: goldText }}>
            {destName ? `Is this trip to ${destName} for a special occasion?` : "Is this trip for a special occasion?"}
          </div>
        </div>
        {onSkip && (
          <button onClick={onSkip} style={{ background: "transparent", border: "none", color: textMuted, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", flexShrink: 0, padding: "2px 4px" }}>
            Skip
          </button>
        )}
      </div>
      <p style={{ margin: "0 0 12px", fontSize: 11.5, color: textMuted, fontStyle: "italic", lineHeight: 1.5 }}>
        Helps us tailor the recommendations. "Just because" is a real answer too.
      </p>

      {/* Pills — single-select */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {MILESTONE_OPTIONS.map(opt => {
          const active = currentType === opt.value;
          return (
            <button key={opt.value} onClick={() => pick(opt.value)}
              style={{
                padding: "8px 12px", borderRadius: 999,
                border: active ? "1px solid rgba(27,58,92,0.55)" : `1px solid ${borderColor}`,
                background: active ? "rgba(232,154,60,0.18)" : (darkMode ? "rgba(27,58,92,0.06)" : "rgba(250,250,245,0.5)"),
                color: active ? goldText : textPrimary,
                fontSize: 12, fontWeight: active ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", gap: 5,
                transition: "all 0.15s ease",
              }}>
              <span style={{ fontSize: 13 }}>{opt.emoji}</span>
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* "Something else" — free-text reveal (max 50 chars per spec) */}
      {currentType === "other" && (
        <div style={{ marginTop: 10 }}>
          <input
            type="text"
            value={currentOtherText}
            onChange={e => updateOtherText(e.target.value)}
            placeholder="Tell us what's special (max 50)"
            maxLength={50}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.7)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
          />
          <div style={{ fontSize: 10, color: textDim, marginTop: 4, textAlign: "right" }}>
            {currentOtherText.length}/50
          </div>
        </div>
      )}
    </div>
  );
}

// ── TMA Waitlist Modal ──────────────────────────────────────────
// Compact "Get on the waitlist" flow that opens from the TMA choice row
// + date-picker card. Replaces the booking modal during the affiliate-only
// v1 window where Booking.com handoff is still being tested. Carries the
// destination + dates the user picked into the joinWaitlist payload so
// admin gets full context for manual outreach when bookings open.
function TMAWaitlistModal({ context, user, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, goldText } = useTheme();
  const dest = context?.dest;
  const departDate = context?.departDate || null;
  const returnDate = context?.returnDate || null;
  const milestone = context?.milestone || null;
  const milestoneStatus = context?.milestoneStatus || null;
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | "ok" | "already" | "error"
  const [error, setError] = useState("");

  async function handleSubmit() {
    const e = (email || "").trim();
    if (!e || !e.includes("@")) { setError("Please enter a valid email."); return; }
    setSubmitting(true); setError("");
    try {
      const result = await joinWaitlist({
        email: e,
        mode: "explore",
        customerName: user?.firstName || user?.name || undefined,
        destination: dest?.name || undefined,
        departureDate: departDate || undefined,
        returnDate: returnDate || undefined,
        milestoneType: milestone?.type || undefined,
        milestoneOtherText: milestone?.type === "other" ? (milestone.otherText || undefined) : undefined,
        milestoneStatus: milestoneStatus || undefined,
      });
      setSubmitting(false);
      if (result.ok) {
        // Backend returns:
        //   - { ok, alreadyOnWaitlist: false } → fresh join
        //   - { ok, alreadyOnWaitlist: true, updated: true } → existing row,
        //     destination changed (explore-mode last-write-wins behavior)
        //   - { ok, alreadyOnWaitlist: true } (no updated flag) → existing row,
        //     same destination as before
        const nextStatus = result.updated ? "updated"
          : result.alreadyOnWaitlist ? "already"
          : "ok";
        setStatus(nextStatus);
        if (typeof window.jgaTrack === "function") {
          window.jgaTrack("tma_waitlist_join", {
            dest: dest?.name,
            already: !!result.alreadyOnWaitlist,
            updated: !!result.updated,
          });
        }
      } else {
        setStatus("error");
        setError(result.error || "Couldn't join the waitlist. Try again?");
      }
    } catch (err) {
      setSubmitting(false);
      setStatus("error");
      setError("Couldn't reach the server. Try again in a moment.");
    }
  }

  if (!dest) return null;

  const fmtDate = (s) => {
    if (!s) return "";
    try { return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return s; }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff", border: `1px solid rgba(74,140,196,0.3)`, borderRadius: 24, padding: "32px 28px", width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease" }}>
        {status === "ok" || status === "already" || status === "updated" ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>✈️</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: goldText, marginBottom: 10 }}>
              {status === "updated" ? "Got it — updated"
                : status === "already" ? "You're already on the list"
                : "You're on the list"}
            </h2>
            <p style={{ fontSize: 13, color: textMuted, lineHeight: 1.7, marginBottom: 22 }}>
              {status === "updated"
                ? `We've updated your waitlist interest to ${dest.name}. We'll reach out as soon as bookings open.`
                : status === "already"
                ? `We've got your interest in ${dest.name} on file. We'll reach out as soon as bookings open.`
                : `Thanks for letting us know you want to go to ${dest.name}. We'll email you as soon as we can book it for you.`}
            </p>
            <button onClick={onClose} style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${teal}, #0F2747)`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Keep exploring →
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: `1px solid ${borderColor}`, color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 900, color: goldText }}>Get on the waitlist</h2>
            </div>

            <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 18, marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>{dest.emoji || "📍"}</span>
                <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>{dest.name}{dest.country ? `, ${dest.country}` : ""}</div>
              </div>
              {(departDate && returnDate) && (
                <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>
                  {fmtDate(departDate)} — {fmtDate(returnDate)}
                </div>
              )}
            </div>

            <p style={{ fontSize: 13, color: textMuted, lineHeight: 1.6, margin: "0 0 18px" }}>
              We're finalizing booking partners for v1. Drop your email and we'll reach out as soon as you can book {dest.name} through JGA.
            </p>

            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, letterSpacing: 1 }}>EMAIL ADDRESS</div>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (error) setError(""); }}
              placeholder="you@example.com"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 8 }}
            />
            {error && (
              <div style={{ fontSize: 12, color: "#c25e47", marginBottom: 10 }}>{error}</div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting || !email || !email.includes("@")}
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: (!email || !email.includes("@")) ? "rgba(27,58,92,0.096)" : `linear-gradient(135deg, ${teal}, #0F2747)`, color: (!email || !email.includes("@")) ? textDim : "#FAFAF5", fontWeight: 900, fontSize: 14, cursor: (!email || !email.includes("@")) ? "not-allowed" : "pointer", fontFamily: "inherit", marginTop: 8, marginBottom: 10 }}>
              {submitting ? "Adding you to the list…" : "Get on the waitlist →"}
            </button>
            <p style={{ fontSize: 11, color: textDim, textAlign: "center", margin: 0 }}>
              We'll only email you about {dest.name}. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Privacy Policy Modal ────────────────────────────────────────
function PrivacyPolicyModal({ onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: teal, borderBottom: "1px solid rgba(27,58,92,0.15)", paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );

  const Sub = ({ title, children }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#c0d0e0", marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );

  const Pt = ({ children }) => (
    <p style={{ margin: "0 0 8px", fontSize: 12, color: "#4A6B8A", lineHeight: 1.7 }}>{children}</p>
  );

  const Bl = ({ items }) => (
    <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.7, marginBottom: 3 }}>{item}</li>
      ))}
    </ul>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 600, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#1B3A5C" }}>Privacy Policy</h2>
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4 }}>Effective: {today} · Draft — pending legal review</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "#4A6B8A", fontSize: 20, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.18)", borderRadius: 10, padding: "10px 14px", marginBottom: 20, fontSize: 11, color: "#4A8CC4", lineHeight: 1.6 }}>
          ⚠️ This policy is a draft template pending legal review. It should not be considered final legal advice. We are working to have it reviewed by a qualified attorney.
        </div>

        <Pt>Just Go Anywhere ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, who we share it with, and your rights. By using justgoanywhere.com you agree to the practices described here.</Pt>

        <Section title="1. Information We Collect">
          <Sub title="1.1 Information You Provide">
            <Bl items={["Account registration: name, email, password, date of birth, home address, age range, passport status", "Booking information: travel dates, destinations, traveler count, budget, accommodation and flight preferences", "Payment information: card details processed securely by Stripe — we do not store full card numbers", "Travel preferences: airlines, hotel chains, or destinations to avoid; Known Traveler Number (KTN)", "Communications: customer service messages, survey responses, and feedback", "Referral information: codes you use or share"]} />
          </Sub>
          <Sub title="1.2 Information Collected Automatically">
            <Bl items={["Device and browser: IP address, browser type, operating system, device identifiers", "Usage data: pages visited, features used, search queries, destinations viewed", "Cookies and tracking technologies (see Section 7)"]} />
          </Sub>
          <Sub title="1.3 Information from Third Parties">
            <Bl items={["Travel providers: booking confirmations from airlines, hotels, and activity companies", "Payment processors: transaction status and fraud signals from Stripe", "Insurance providers: policy information from SafetyWing when insurance is purchased"]} />
          </Sub>
        </Section>

        <Section title="2. How We Use Your Information">
          <Bl items={["Process bookings, payments, and cancellations", "Send confirmations, itineraries, and travel updates", "Personalise recommendations, Surprise Me bookings, and price alerts", "Process referral credits and gift vouchers", "Respond to customer service enquiries", "Send marketing communications if you have opted in", "Improve the platform through analytics and product research", "Detect fraud and comply with legal obligations"]} />
          <Pt>We do not sell your personal data to third parties.</Pt>
        </Section>

        <Section title="3. How We Share Your Information">
          <Sub title="Travel Suppliers"><Pt>We share necessary details with airlines, hotels, and activity providers to fulfil your booking.</Pt></Sub>
          <Sub title="Stripe (Payments)"><Pt>All payment processing is handled by Stripe. Your card details go directly to Stripe and are subject to stripe.com/privacy. We store only a Stripe customer token.</Pt></Sub>
          <Sub title="SafetyWing"><Pt>If you purchase insurance, you will be directed to SafetyWing to complete your policy. Visit <a href="https://safetywing.com/?referenceID=26515446&utm_source=26515446&utm_medium=Ambassador" target="_blank" rel="noopener noreferrer">SafetyWing</a> for full policy details.</Pt></Sub>
          <Sub title="Legal Disclosures"><Pt>We may disclose information if required by law, court order, or to protect the rights and safety of our users.</Pt></Sub>
        </Section>

        <Section title="4. Data Retention">
          <Bl items={["Account data: retained while active and 30 days after deletion", "Booking records: 7 years for financial and tax compliance", "Survey responses: 3 years", "Server logs: 90 days"]} />
        </Section>

        <Section title="5. Your Rights">
          <Sub title="GDPR (EEA, UK, Switzerland)">
            <Bl items={["Access, rectify, or delete your personal data", "Data portability — receive your data in machine-readable format", "Restrict processing or object to processing based on legitimate interests", "Withdraw consent at any time"]} />
          </Sub>
          <Sub title="CCPA (California Residents)">
            <Bl items={["Know what personal information we collect and how it is used", "Delete your personal information", "We do not sell personal information — no opt-out required", "Non-discrimination for exercising privacy rights"]} />
          </Sub>
          <Pt>To exercise your rights: privacy@justgoanywhere.com · We respond within 30 days.</Pt>
        </Section>

        <Section title="6. Data Security">
          <Bl items={["All data transmitted over HTTPS with TLS encryption", "Passwords hashed using bcrypt", "Payment data handled by Stripe (PCI DSS Level 1 compliant)", "Access restricted to staff who need it to provide our service", "Data breach notification within 72 hours as required by law"]} />
        </Section>

        <Section title="7. How We Protect Your Passport Information">
          <Pt>When you choose to add passport information to your profile, we treat it with the highest level of care.</Pt>

          <Sub title="7.1 Encryption at Rest">
            <Pt>Passport numbers are encrypted using AES-256-GCM (Advanced Encryption Standard with 256-bit keys in Galois/Counter Mode) before being stored in our database. This is the same encryption standard used by financial institutions and government agencies. Even if our database were compromised, the encrypted passport numbers would remain unreadable without our separate encryption key.</Pt>
          </Sub>

          <Sub title="7.2 What You See">
            <Pt>Once saved, only the last four digits of your passport number are visible in your account — even to you. The full number is never displayed back to you on the platform. This protects you in case someone gains access to your screen or session.</Pt>
          </Sub>

          <Sub title="7.3 Who Can Access Your Passport Information">
            <Pt>Access to decrypt full passport numbers is strictly limited to authorized administrators acting on your behalf — for example, when processing a flight booking that requires passport details. Every access is logged with a timestamp, the administrator's identity, and the reason for access. We maintain a permanent audit trail for accountability.</Pt>
          </Sub>

          <Sub title="7.4 What We Do Not Do">
            <Pt>We do not sell or share your passport information with third parties for marketing purposes. We do not use it for any purpose beyond facilitating your travel bookings. We do not store it longer than necessary — you can remove your passport information at any time by clearing the field in your profile.</Pt>
          </Sub>

          <Sub title="7.5 Your Control">
            <Bl items={[
              "View only the masked last 4 digits of your passport at any time",
              "Update your passport information when it changes",
              "Remove your passport information completely from your account",
              "Request a record of who has accessed your passport information by emailing support@justgoanywhere.com",
            ]} />
          </Sub>
        </Section>

        <Section title="8. Cookies">
          <Bl items={["Essential cookies: required for authentication and booking state — cannot be disabled", "Preference cookies: remember your settings across visits", "Analytics cookies: anonymised usage data to improve the platform", "Marketing cookies: relevant promotions if you have opted in"]} />
          <Pt>Manage preferences through our cookie banner or your browser settings.</Pt>
        </Section>

        <Section title="9. Affiliate Relationships">
          <Pt>Just Go Anywhere earns commissions from travel providers when you book through our platform, at no additional cost to you. Our recommendations are editorially independent and not influenced by affiliate relationships.</Pt>
        </Section>

        <Section title="10. Children's Privacy">
          <Pt>Our service is not directed to children under 16 (or 13 in the United States). We do not knowingly collect data from children. Contact privacy@justgoanywhere.com to request deletion of any such data.</Pt>
        </Section>

        <Section title="11. Contact">
          <Bl items={["Email: privacy@justgoanywhere.com", "Subject: Privacy Request — [your name]", "Response time: within 30 days"]} />
          <Pt>EEA residents may lodge a complaint with their local data protection authority if unsatisfied with our response.</Pt>
        </Section>

        <div style={{ borderTop: "1px solid rgba(27,58,92,0.084)", paddingTop: 16, marginTop: 8 }}>
          <button onClick={onClose} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User Profile Modal ──────────────────────────────────────────
function UserProfileModal({ user, onClose, onUpdate }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const [ktn, setKtn] = useState(user.ktn || "");
  const [ktnEditing, setKtnEditing] = useState(false);
  const [departureAirport, setDepartureAirport] = useState(user.departureAirport || "");
  const [avoidDestinations, setAvoidDestinations] = useState(user.avoidDestinations || "");
  const [avoidAirlines, setAvoidAirlines] = useState(user.avoidAirlines || "");
  const [avoidHotels, setAvoidHotels] = useState(user.avoidHotels || "");
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  function save() {
    onUpdate({ ...user, ktn, departureAirport, avoidDestinations, avoidAirlines, avoidHotels });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function copyReferral() {
    navigator.clipboard?.writeText(`Book a surprise trip with Just Go Anywhere using my code ${user.referralCode} and get $20 off your first booking! justgoanywhere.com`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const fieldStyle = { width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.8)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 500, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(74,140,196,0.25)" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <button onClick={onClose} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1B3A5C" }}>My Profile</h2>
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2 }}>{user.email}</div>
          </div>
        </div>

        {/* Account info */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 22 }}>
          {[
            ["👤 Name", `${user.firstName} ${user.lastName || ""}`],
            ["📧 Email", user.email],
            ["🎂 Date of Birth", user.dob || "Not set"],
            ["🌍 Age Range", user.ageRange || "Not set"],
          ].map(([k, v]) => (
            <div key={k} style={{ background: "rgba(27,58,92,0.045)", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(27,58,92,0.072)" }}>
              <div style={{ fontSize: 10, color: "#4A6B8A", marginBottom: 3 }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1B3A5C" }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Referral section */}
        <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 14, padding: "16px", marginBottom: 22 }}>
          <Label color="#4A8CC4">Referral Programme</Label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, color: goldText, letterSpacing: 2 }}>{user.referralCode}</div>
              <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 3 }}>Share this code · earn 1 free month per signup</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#4A8CC4", textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#4A6B8A", marginBottom: 2 }}>Referred</div>
              {user.referralCount || 0}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={copyReferral} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid rgba(74,140,196,0.35)`, background: copied ? "rgba(109,189,109,0.15)" : "rgba(74,140,196,0.08)", color: copied ? "#6dbd6d" : gold, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {copied ? "✓ Copied!" : "📋 Copy Referral Message"}
            </button>
            <button onClick={() => { if (navigator.share) navigator.share({ title: "Just Go Anywhere", text: `Use my code ${user.referralCode} and we both get a free month!`, url: "https://justgoanywhere.com" }); }} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(74,140,196,0.2)", background: "rgba(74,140,196,0.05)", color: goldText, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              ↗ Share
            </button>
          </div>
        </div>

        {/* Departure Airport */}
        <div style={{ marginBottom: 18 }}>
          <Label>Preferred Departure Airport</Label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none" }}>✈️</span>
              <input
                value={departureAirport}
                onChange={e => setDepartureAirport(e.target.value.toUpperCase().slice(0, 3))}
                placeholder="e.g. JFK"
                maxLength={3}
                style={{ ...fieldStyle, paddingLeft: 38, letterSpacing: 3, fontWeight: 700, fontSize: 16, textTransform: "uppercase", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <button onClick={() => {
              console.log("[JGA Detect] Profile - Detect tapped");
              if (!navigator.geolocation) { alert("Geolocation isn't supported in this browser. Please type your airport code (e.g. JFK)."); return; }
              navigator.geolocation.getCurrentPosition(pos => {
                console.log("[JGA Detect] Got position:", pos.coords.latitude, pos.coords.longitude);
                const nearest = getNearestAirport(pos.coords.latitude, pos.coords.longitude);
                if (nearest) { console.log("[JGA Detect] Nearest airport:", nearest.code); setDepartureAirport(nearest.code); }
                else alert("No nearby airport found. Please type your airport code (e.g. JFK).");
              }, err => {
                console.error("[JGA Detect] Geolocation error:", err.code, err.message);
                if (err.code === 1) alert("Location access was blocked. Please enable location in your browser settings, or type your airport code (e.g. JFK).");
                else alert("Couldn't detect your location. Please type your airport code (e.g. JFK).");
              }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
            }} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.3)", background: "rgba(27,58,92,0.08)", color: teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
              📍 Detect
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 5, lineHeight: 1.5 }}>
            Enter your home airport's 3-letter IATA code (e.g. JFK, LAX, LHR, SYD). Tap Detect to auto-fill from your location.
          </div>
        </div>

        {/* KTN */}
        <div style={{ marginBottom: 18 }}>
          <Label>Known Traveller Number (KTN / TSA PreCheck)</Label>
          <input value={ktn} onChange={e => setKtn(e.target.value)} placeholder="e.g. 12345678" style={fieldStyle} />
          <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 5, lineHeight: 1.5 }}>
            Your KTN is used for TSA PreCheck, Global Entry, and NEXUS. We'll add it automatically to flight bookings so you get expedited screening.
          </div>
        </div>

        {/* Travel Preferences — Avoid */}
        <div style={{ background: "rgba(255,107,107,0.04)", border: "1px solid rgba(255,107,107,0.15)", borderRadius: 14, padding: "16px", marginBottom: 22 }}>
          <Label color="#c07070">Travel Preferences — Places & Services to Avoid</Label>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 6 }}>🚫 Destinations I don't want to visit</div>
            <textarea value={avoidDestinations} onChange={e => setAvoidDestinations(e.target.value)}
              placeholder="e.g. Russia, North Korea, anywhere in conflict zones..."
              rows={2}
              style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4 }}>We'll exclude these from all recommendations and Surprise Me bookings.</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 6 }}>✈️ Airlines I want to avoid</div>
            <textarea value={avoidAirlines} onChange={e => setAvoidAirlines(e.target.value)}
              placeholder="e.g. Spirit, Frontier, RyanAir..."
              rows={2}
              style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4 }}>We'll filter out these carriers when searching for flights.</div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 6 }}>🏨 Hotel chains I want to avoid</div>
            <textarea value={avoidHotels} onChange={e => setAvoidHotels(e.target.value)}
              placeholder="e.g. Motel 6, budget chains, anything below 3 stars..."
              rows={2}
              style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4 }}>We'll exclude these when booking accommodation.</div>
          </div>
        </div>

        <button onClick={save} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: saved ? "rgba(109,189,109,0.3)" : `linear-gradient(135deg,${gold},${amber})`, color: saved ? "#6dbd6d" : "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit", transition: "all 0.3s" }}>
          {saved ? "✓ Preferences Saved!" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
}

// ── Blind Gate Modal ────────────────────────────────────────────
function BlindGateModal({ onClose, onSuccess, onLogin }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText } = useTheme();
  const [view, setView] = useState("gate"); // "gate" | "register" | "login"
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", dob: "", address: "" });
  const [hasPassport, setHasPassport] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [agreed, setAgreed] = useState(false);

  // Feature list now sourced from module-scope JGA_BENEFITS so dashboard
  // unlock teaser and registration modal stay in sync.
  const FEATURES = JGA_BENEFITS;

  function validateRegister() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.includes("@")) e.email = "Valid email required";
    if (!form.dob) e.dob = "Required";
    if (!form.address.trim()) e.address = "Required";
    if (!agreed) e.agreed = "Please accept to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function submitRegister() {
    if (validateRegister()) onSuccess({ ...form, hasPassport });
  }

  function submitLogin() {
    if (!loginForm.email.includes("@")) { setErrors({ login: "Valid email required" }); return; }
    if (!loginForm.password) { setErrors({ login: "Password required" }); return; }
    onLogin({ firstName: loginForm.email.split("@")[0], email: loginForm.email });
  }

  const f = field => ({ value: form[field], onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.35)" }}>

        {/* ── Gate view ── */}
        {view === "gate" && (
          <div>
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "#4A6B8A", fontSize: 18, cursor: "pointer", float: "right", lineHeight: 1 }}>✕</button>
            <div style={{ textAlign: "center", marginBottom: 24, paddingTop: 8 }}>
              {/* Mini globe */}
              <svg width="64" height="64" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: "0 auto 12px", display: "block" }}>
                <circle cx="22" cy="22" r="19" fill="rgba(27,58,92,0.15)" stroke="#1B3A5C" strokeWidth="2"/>
                <ellipse cx="22" cy="22" rx="9" ry="19" fill="rgba(27,58,92,0.08)" stroke="#1B3A5C" strokeWidth="1.6"/>
                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke="#1B3A5C" strokeWidth="1.6" fill="none"/>
                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke="#1B3A5C" strokeWidth="1.1" fill="none" opacity="0.7"/>
                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke="#1B3A5C" strokeWidth="1.1" fill="none" opacity="0.7"/>
              </svg>
              <div style={{ fontSize: 11, letterSpacing: 3, color: teal, textTransform: "uppercase", marginBottom: 8 }}>One step away</div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#1B3A5C", lineHeight: 1.2 }}>Unlock Surprise Me</h2>
              <p style={{ color: "#4A6B8A", fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                Create a free account to book your surprise trip.
              </p>
            </div>

            {/* Price card */}
            <div style={{ background: "rgba(27,58,92,0.07)", border: "2px solid rgba(27,58,92,0.4)", borderRadius: 16, padding: "20px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 38, fontWeight: 900, color: teal }}>Free</span>
                <span style={{ fontSize: 14, color: "#4A6B8A" }}>account</span>
              </div>
              <div style={{ display: "inline-block", fontSize: 11, padding: "3px 12px", borderRadius: 20, background: "rgba(109,189,109,0.2)", color: "#6dbd6d", marginBottom: 14, fontWeight: 700, letterSpacing: 1 }}>
                FREE FOREVER · BOOK WHEN YOU'RE READY
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, textAlign: "left" }}>
                {FEATURES.map(f => (
                  <div key={f.text} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(27,58,92,0.2)", border: "1px solid rgba(27,58,92,0.4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: teal, fontWeight: 900 }}>✓</div>
                    <span style={{ color: "#c0d0e0" }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => setView("register")} style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,#0F2747,${teal})`, color: "#FAFAF5", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
              Create Account & Book My Trip →
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: "#4A6B8A" }}>
              Already a member?{" "}
              <button onClick={() => setView("login")} style={{ background: "none", border: "none", color: teal, cursor: "pointer", fontSize: 12, fontFamily: "inherit", textDecoration: "underline" }}>Sign in</button>
            </div>
          </div>
        )}

        {/* ── Register view ── */}
        {view === "register" && (
          <div>
            {/* Brand lockup at top — registration is a conversion moment; show
                the brand so users know who they're signing up with. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <JGAGlobe size={28} />
              <JGAWordmark size={15} showTM />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <button onClick={() => setView("gate")} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#1B3A5C" }}>Create Your Account</h2>
                <div style={{ fontSize: 11, color: teal, marginTop: 2 }}>Free account · book anytime</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
              <div style={{ paddingRight: 6 }}>
                <Input label="First Name" placeholder="Jane" {...f("firstName")} />
                {errors.firstName && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.firstName}</div>}
              </div>
              <div style={{ paddingLeft: 6 }}>
                <Input label="Last Name" placeholder="Smith" {...f("lastName")} />
                {errors.lastName && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.lastName}</div>}
              </div>
            </div>
            <Input label="Email Address" placeholder="jane@email.com" type="email" {...f("email")} />
            {errors.email && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.email}</div>}
            <Input label="Date of Birth" type="date" {...f("dob")} />
            {errors.dob && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.dob}</div>}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="input-address" style={{ fontSize: 11, letterSpacing: 3, color: darkMode ? "#4A8CC4" : "#4A8CC4", textTransform: "uppercase", marginBottom: 10, display: "block" }}>Home Address</label>
              <input
                id="input-address"
                ref={el => {
                  if (el && !el._autocomplete && window.google?.maps?.places) {
                    const ac = new window.google.maps.places.Autocomplete(el, { types: ["address"], componentRestrictions: { country: "us" } });
                    ac.addListener("place_changed", () => {
                      const place = ac.getPlace();
                      if (place?.formatted_address) setForm(p => ({ ...p, address: place.formatted_address }));
                    });
                    el._autocomplete = ac;
                  }
                }}
                value={form.address}
                onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="123 Main St, City, State"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            {errors.address && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.address}</div>}

            {/* Passport checkbox */}
            <button onClick={() => setHasPassport(p => !p)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, marginBottom: 14, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s", border: `1px solid ${hasPassport ? "rgba(74,140,196,0.45)" : "rgba(27,58,92,0.120)"}`, background: hasPassport ? "rgba(74,140,196,0.07)" : "rgba(27,58,92,0.045)" }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${hasPassport ? gold : "rgba(27,58,92,0.23)"}`, background: hasPassport ? gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {hasPassport && <svg width="11" height="9" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hasPassport ? gold : "#4A6B8A" }}>I have a valid passport</div>
                <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 1 }}>Just a yes/no — add details later in your profile when you're ready. 🔒 Encrypted on save.</div>
              </div>
              <div style={{ fontSize: 18 }}>🛂</div>
            </button>

            {/* Terms agreement */}
            <button onClick={() => setAgreed(a => !a)} style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", borderRadius: 10, marginBottom: 4, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s", border: `1px solid ${agreed ? "rgba(27,58,92,0.45)" : errors.agreed ? "rgba(255,107,107,0.4)" : "rgba(27,58,92,0.120)"}`, background: agreed ? "rgba(27,58,92,0.06)" : "rgba(27,58,92,0.045)" }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${agreed ? teal : "rgba(27,58,92,0.23)"}`, background: agreed ? teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                {agreed && <svg width="11" height="9" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.5 }}>
                I agree to the <span style={{ color: teal }}>Terms of Service</span> and <span style={{ color: teal }}>Privacy Policy</span>.
              </div>
            </button>
            {errors.agreed && <div style={{ color: coral, fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{errors.agreed}</div>}

            <div style={{ background: "rgba(74,140,196,0.04)", borderRadius: 8, padding: "8px 12px", marginBottom: 16, marginTop: 6, fontSize: 11, color: "#4A6B8A", lineHeight: 1.5 }}>
              🔒 Free account — save your Travel Identity, build mood boards, and journal every trip.
            </div>

            <button onClick={submitRegister} style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,#0F2747,${teal})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
              Create Account & Book My Surprise Trip →
            </button>
          </div>
        )}

        {/* ── Login view ── */}
        {view === "login" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <button onClick={() => setView("gate")} style={{ background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.120)", color: "#4A6B8A", padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#1B3A5C" }}>Sign In to Test Account</h2>
            </div>
            <Input label="Email Address" placeholder="jane@email.com" type="email"
              value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} />
            <Input label="Password" placeholder="••••••••" type="password"
              value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} />
            {errors.login && <div style={{ color: coral, fontSize: 12, marginBottom: 12, marginTop: -6 }}>{errors.login}</div>}
            <div style={{ textAlign: "right", marginBottom: 16 }}>
              <button style={{ background: "none", border: "none", color: teal, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Forgot password?</button>
            </div>
            <button onClick={submitLogin} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,#0F2747,${teal})`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
              Sign In & Book My Trip →
            </button>
            <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "#4A6B8A" }}>
              Don't have an account?{" "}
              <button onClick={() => setView("register")} style={{ background: "none", border: "none", color: teal, cursor: "pointer", fontSize: 12, fontFamily: "inherit", textDecoration: "underline" }}>Create account</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Region drill-down component ─────────────────────────────────
// Top 4 cities per US state for search suggestions
const US_CITIES_BY_STATE = {
  "Alabama": ["Birmingham","Montgomery","Huntsville","Mobile"],
  "Alaska": ["Anchorage","Juneau","Fairbanks","Sitka"],
  "Arizona": ["Phoenix","Tucson","Scottsdale","Sedona"],
  "Arkansas": ["Little Rock","Fayetteville","Fort Smith","Bentonville"],
  "California": ["Los Angeles","San Francisco","San Diego","Palm Springs"],
  "Colorado": ["Denver","Colorado Springs","Aspen","Boulder"],
  "Connecticut": ["Hartford","New Haven","Stamford","Greenwich"],
  "Delaware": ["Wilmington","Dover","Rehoboth Beach","Newark"],
  "District of Columbia": ["Washington D.C."],
  "Florida": ["Miami","Orlando","Tampa","Key West"],
  "Georgia": ["Atlanta","Savannah","Athens","Augusta"],
  "Hawaii": ["Honolulu","Maui","Kauai","Kona"],
  "Idaho": ["Boise","Sun Valley","Coeur d'Alene","Twin Falls"],
  "Illinois": ["Chicago","Springfield","Naperville","Rockford"],
  "Indiana": ["Indianapolis","Fort Wayne","Bloomington","South Bend"],
  "Iowa": ["Des Moines","Iowa City","Cedar Rapids","Dubuque"],
  "Kansas": ["Wichita","Kansas City","Topeka","Lawrence"],
  "Kentucky": ["Louisville","Lexington","Bowling Green","Frankfort"],
  "Louisiana": ["New Orleans","Baton Rouge","Lafayette","Shreveport"],
  "Maine": ["Portland","Bar Harbor","Augusta","Bangor"],
  "Maryland": ["Baltimore","Annapolis","Ocean City","Bethesda"],
  "Massachusetts": ["Boston","Cape Cod","Salem","Nantucket"],
  "Michigan": ["Detroit","Grand Rapids","Traverse City","Ann Arbor"],
  "Minnesota": ["Minneapolis","Saint Paul","Duluth","Rochester"],
  "Mississippi": ["Jackson","Biloxi","Gulfport","Oxford"],
  "Missouri": ["Kansas City","St. Louis","Branson","Columbia"],
  "Montana": ["Billings","Missoula","Bozeman","Glacier"],
  "Nebraska": ["Omaha","Lincoln","Grand Island","Kearney"],
  "Nevada": ["Las Vegas","Reno","Lake Tahoe","Henderson"],
  "New Hampshire": ["Manchester","Portsmouth","Concord","North Conway"],
  "New Jersey": ["Atlantic City","Jersey City","Princeton","Cape May"],
  "New Mexico": ["Albuquerque","Santa Fe","Taos","Las Cruces"],
  "New York": ["New York City","Buffalo","Niagara Falls","The Hamptons"],
  "North Carolina": ["Charlotte","Asheville","Raleigh","Outer Banks"],
  "North Dakota": ["Fargo","Bismarck","Grand Forks","Minot"],
  "Ohio": ["Columbus","Cleveland","Cincinnati","Dayton"],
  "Oklahoma": ["Oklahoma City","Tulsa","Norman","Broken Arrow"],
  "Oregon": ["Portland","Eugene","Bend","Crater Lake"],
  "Pennsylvania": ["Philadelphia","Pittsburgh","Lancaster","Gettysburg"],
  "Rhode Island": ["Providence","Newport","Bristol","Narragansett"],
  "South Carolina": ["Charleston","Myrtle Beach","Greenville","Hilton Head"],
  "South Dakota": ["Sioux Falls","Rapid City","Black Hills","Deadwood"],
  "Tennessee": ["Nashville","Memphis","Chattanooga","Knoxville"],
  "Texas": ["Austin","Houston","Dallas","San Antonio"],
  "Utah": ["Salt Lake City","Moab","Park City","Zion"],
  "Vermont": ["Burlington","Stowe","Montpelier","Woodstock"],
  "Virginia": ["Virginia Beach","Richmond","Alexandria","Charlottesville"],
  "Washington": ["Seattle","Spokane","Tacoma","Bellingham"],
  "West Virginia": ["Charleston","Morgantown","Harpers Ferry","Huntington"],
  "Wisconsin": ["Milwaukee","Madison","Green Bay","Door County"],
  "Wyoming": ["Jackson Hole","Yellowstone","Cheyenne","Casper"],
  "Puerto Rico": ["San Juan","Ponce","Mayagüez","Rincon"],
};


function getNearestAirport(lat, lng) {
  let nearest = null, minDist = Infinity;
  for (const a of MAJOR_AIRPORTS) {
    const d = Math.sqrt(Math.pow(a.lat - lat, 2) + Math.pow(a.lng - lng, 2));
    if (d < minDist) { minDist = d; nearest = a; }
  }
  return nearest;
}

// Friendly label for an airport code, e.g. "JFK" → "New York (JFK)".
// Falls back to just the uppercased code when it's not in MAJOR_AIRPORTS.
function airportLabel(code) {
  if (!code) return "";
  const c = String(code).toUpperCase().trim();
  const match = MAJOR_AIRPORTS.find(a => a.code === c);
  return match ? `${match.city} (${match.code})` : c;
}

// ── Shared geolocation helper for all Detect buttons ─────────────
// Calls onSuccess with airport code (e.g. "JFK") or onError with message
function detectAirport(onSuccess, onError = null) {
  console.log("[JGA Detect] Detect requested");
  if (!navigator.geolocation) {
    const msg = "Geolocation isn't supported in this browser. Please type your airport code (e.g. JFK).";
    console.error("[JGA Detect] No geolocation API");
    if (onError) onError(msg); else alert(msg);
    return;
  }
  // Check secure context (HTTPS or localhost)
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    const msg = "Location detection requires a secure connection (HTTPS).";
    console.error("[JGA Detect] Not in secure context");
    if (onError) onError(msg); else alert(msg);
    return;
  }
  navigator.geolocation.getCurrentPosition(
    pos => {
      console.log("[JGA Detect] Got position:", pos.coords.latitude, pos.coords.longitude);
      const nearest = getNearestAirport(pos.coords.latitude, pos.coords.longitude);
      if (nearest) {
        console.log("[JGA Detect] Nearest airport:", nearest.code);
        try { localStorage.setItem("jga-airport", nearest.code); } catch(e) {}
        onSuccess(nearest.code, nearest);
      } else {
        const msg = "No nearby airport found. Please type your airport code (e.g. JFK).";
        console.error("[JGA Detect] No airport found near coords");
        if (onError) onError(msg); else alert(msg);
      }
    },
    err => {
      console.error("[JGA Detect] Geolocation error:", err.code, err.message);
      let msg;
      if (err.code === 1) msg = "Location access was blocked. Please enable location in your browser settings, or type your airport code (e.g. JFK).";
      else if (err.code === 2) msg = "Couldn't determine your location. Please type your airport code (e.g. JFK).";
      else if (err.code === 3) msg = "Location request timed out. Please type your airport code (e.g. JFK).";
      else msg = "Couldn't detect your location. Please type your airport code (e.g. JFK).";
      if (onError) onError(msg); else alert(msg);
    },
    { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
  );
}

// ── Airport Trust Banner ─────────────────────────────────────────
// Surfaces the departure-airport assumption in plain language so the
// user understands what prices are based on, with detect + manual
// change options. Builds trust by being explicit about the assumption
// rather than silently pricing from a guessed airport.
//
// Props:
//   airport       — current airport code (string) or "" / null
//   onChange      — (code) => void; called when detected or manually set
//   accent        — accent color (defaults to maroon)
// ── Budget headroom upsell ───────────────────────────────────────
// When a booked trip comes in under budget, the leftover is the strongest
// upsell signal there is — the user already said they'd spend it. We surface
// identity-matched activity CATEGORIES (food tours, day trips, live events…)
// framed as "you have room for more," never "spend the rest." Pre-launch this
// shows categories; each carries a `partner` so it becomes a real affiliate
// link (Viator / GetYourGuide / SeatGeek) once those programs are live.
const ACTIVITY_CATEGORIES = [
  { id: "food",      match: ["food","culinary"],            emoji: "🍽️", label: "Food & wine experiences", blurb: "Tastings, market tours, cooking classes", low: 45,  high: 180, partner: "viator" },
  { id: "culture",   match: ["culture","history","art"],     emoji: "🏛️", label: "Culture & history tours",   blurb: "Guided sights, museums, walking tours",   low: 25,  high: 120, partner: "getyourguide" },
  { id: "adventure", match: ["adventure","nature"],          emoji: "🥾", label: "Adventures & day trips",    blurb: "Hikes, excursions, outdoor experiences",  low: 60,  high: 250, partner: "viator" },
  { id: "beach",     match: ["beach"],                       emoji: "⛵", label: "Water & beach activities",   blurb: "Boat trips, snorkeling, coastal tours",   low: 50,  high: 200, partner: "viator" },
  { id: "wellness",  match: ["wellness","spiritual"],        emoji: "🧘", label: "Wellness & spa",            blurb: "Spa days, yoga, thermal retreats",        low: 40,  high: 220, partner: "getyourguide" },
  { id: "nightlife", match: ["nightlife","music"],           emoji: "🎶", label: "Live events & nightlife",   blurb: "Concerts, shows, games while you're there", low: 35, high: 300, partner: "seatgeek" },
  { id: "luxury",    match: ["luxury","romance"],            emoji: "✨", label: "Special occasions",         blurb: "Private tours, romantic add-ons, upgrades", low: 90, high: 400, partner: "viator" },
];

// Resolve which categories to suggest: identity vibes first, destination tags
// as fallback. Returns a de-duplicated, headroom-affordable, ranked list.
function resolveUpsellCategories({ identityVibes = [], destTags = [], perPersonHeadroom }) {
  const signals = [
    ...(identityVibes || []),
    ...(destTags || []),
  ].map(s => String(s).toLowerCase());
  const seen = new Set();
  const out = [];
  // Identity/tag-matched categories first, in catalog order
  for (const cat of ACTIVITY_CATEGORIES) {
    if (seen.has(cat.id)) continue;
    const hits = cat.match.some(m => signals.includes(m));
    // Honesty guardrail: only suggest if its entry price fits the headroom
    if (hits && cat.low <= perPersonHeadroom) { out.push(cat); seen.add(cat.id); }
  }
  // If nothing matched (sparse vibes/tags), fall back to broadly-appealing,
  // affordable categories so the card is never empty.
  if (out.length === 0) {
    for (const cat of ACTIVITY_CATEGORIES) {
      if (["food","culture","adventure"].includes(cat.id) && cat.low <= perPersonHeadroom) out.push(cat);
    }
  }
  return out.slice(0, 3);
}

function BudgetHeadroomUpsell({ budget, tripCost, travelers = 1, identityVibes = [], destTags = [], destName = "", activitiesIncluded = true, accent = "#1B3A5C" }) {
  const { textPrimary, textMuted, textDim } = useTheme();
  const b = parseInt(String(budget).replace(/[^0-9]/g, ""), 10) || 0;
  const cost = Math.round(tripCost || 0);
  const headroom = b - cost;
  // Only surface meaningful headroom: at least 20% of budget OR $200, whichever
  // is larger. Below that, the user basically nailed their budget — don't nag.
  const threshold = Math.max(Math.round(b * 0.2), 200);
  if (b <= 0 || cost <= 0 || headroom < threshold) return null;
  const pax = Math.max(1, travelers || 1);
  const perPersonHeadroom = Math.floor(headroom / pax);
  let cats = resolveUpsellCategories({ identityVibes, destTags, perPersonHeadroom });
  if (cats.length === 0) return null;
  // If they explicitly left activities OUT, lead with an experience category —
  // that's the real gap, and the most legitimate thing to offer.
  if (!activitiesIncluded) {
    const expIds = ["food","adventure","culture"];
    cats = [...cats].sort((a, bcat) => (expIds.includes(bcat.id) ? 0 : 1) - (expIds.includes(a.id) ? 0 : 1));
  }

  return (
    <div style={{
      marginTop: 16, padding: "16px 18px", borderRadius: 16,
      border: `1px solid ${accent}33`,
      background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
    }}>
      <div style={{ fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", marginBottom: 6 }}>
        {activitiesIncluded ? "You have room for more" : "Add experiences?"}
      </div>
      <div style={{ fontSize: 14, color: textPrimary, lineHeight: 1.5, marginBottom: 14 }}>
        {activitiesIncluded
          ? <>This trip comes in about <strong style={{ color: accent }}>${headroom.toLocaleString()}</strong> under your budget{destName ? <> — room to add a little more to {destName}.</> : "."}</>
          : <>You haven't added experiences yet, and this trip is about <strong style={{ color: accent }}>${headroom.toLocaleString()}</strong> under your budget. Interested in any of these{destName ? <> in {destName}</> : ""}?</>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {cats.map(cat => (
          <div key={cat.id} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 12px", borderRadius: 12,
            background: "rgba(255,255,255,0.5)",
            border: `1px solid ${accent}1f`,
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>{cat.emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary }}>{cat.label}</div>
              <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.4 }}>{cat.blurb}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: accent, flexShrink: 0, textAlign: "right", whiteSpace: "nowrap" }}>
              ${cat.low}–{cat.high}<span style={{ color: textDim, fontWeight: 500 }}>/person</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: textDim, fontStyle: "italic", marginTop: 10, lineHeight: 1.5 }}>
        Comparison is against what we're booking for you. Add experiences during checkout — prices are typical ranges.
      </div>
    </div>
  );
}

function AirportTrustBanner({ airport, onChange, accent = "#1B3A5C" }) {
  const { textMuted, textDim } = useTheme();
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [manualInput, setManualInput] = useState("");

  const handleDetect = () => {
    setError("");
    setDetecting(true);
    detectAirport(
      (code) => { setDetecting(false); onChange(code); },
      (msg) => { setDetecting(false); setError(msg); }
    );
  };

  const commitManual = () => {
    const code = manualInput.trim().toUpperCase();
    if (code.length < 3) { setError("Enter a 3-letter airport code (e.g. JFK)."); return; }
    setError("");
    setEditing(false);
    setManualInput("");
    try { localStorage.setItem("jga-airport", code); } catch {}
    onChange(code);
  };

  return (
    <div style={{
      padding: "12px 14px",
      borderRadius: 12,
      border: `1px solid ${airport ? "rgba(27,58,92,0.22)" : "rgba(27,58,92,0.30)"}`,
      background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
      marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        {/* Plane glyph */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" fill={accent} opacity="0.85"/>
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          {detecting ? (
            <div style={{ fontSize: 13, color: textMuted, fontWeight: 600 }}>Finding your closest airport…</div>
          ) : airport ? (
            <>
              <div style={{ fontSize: 13, color: "#1B3A5C", lineHeight: 1.5 }}>
                It looks like your closest airport is <strong style={{ color: accent }}>{airportLabel(airport)}</strong>. Prices are based on departures from here.
              </div>
              {!editing && (
                <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
                  <button onClick={() => { setEditing(true); setManualInput(""); setError(""); }}
                    style={{ background: "none", border: "none", color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline", textUnderlineOffset: 2 }}>
                    Change airport
                  </button>
                  <button onClick={handleDetect}
                    style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                    Re-detect
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#1B3A5C", lineHeight: 1.5 }}>
                We can base prices on flights from your closest airport. Detect it, or enter it yourself.
              </div>
              {!editing && (
                <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
                  <button onClick={handleDetect}
                    style={{ background: "none", border: "none", color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline", textUnderlineOffset: 2 }}>
                    Detect my airport
                  </button>
                  <button onClick={() => { setEditing(true); setManualInput(""); setError(""); }}
                    style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                    Enter manually
                  </button>
                </div>
              )}
            </>
          )}

          {/* Manual entry */}
          {editing && (
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input
                value={manualInput}
                autoFocus
                onChange={e => setManualInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") commitManual(); if (e.key === "Escape") { setEditing(false); setError(""); } }}
                placeholder="Airport code (e.g. JFK)"
                maxLength={4}
                style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `1.5px solid ${accent}55`, background: "#fff", color: "#1B3A5C", fontSize: 14, fontWeight: 600, fontFamily: "inherit", outline: "none", boxSizing: "border-box", textTransform: "uppercase" }}
              />
              <button onClick={commitManual}
                style={{ padding: "0 16px", borderRadius: 9, border: "none", background: `linear-gradient(135deg, ${accent} 0%, #0F2747 100%)`, color: "#FAFAF5", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                Save
              </button>
              <button onClick={() => { setEditing(false); setError(""); }}
                style={{ padding: "0 12px", borderRadius: 9, border: `1px solid ${accent}30`, background: "transparent", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                Cancel
              </button>
            </div>
          )}

          {error && <div style={{ fontSize: 11, color: "#4A8CC4", marginTop: 6 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

function LocationPicker({ region, setRegion, subRegion, setSubRegion, country, setCountry, stateVal, setStateVal, color = teal }) {
  const { darkMode, textMuted, textDim, borderColor } = useTheme();

  // Support multi-select — values can be arrays or single strings
  const regions = Array.isArray(region) ? region : (region && region !== "Anywhere" ? [region] : []);
  const countries = Array.isArray(country) ? country : (country ? [country] : []);
  const states = Array.isArray(stateVal) ? stateVal : (stateVal ? [stateVal] : []);

  function toggleRegion(r) {
    if (r === "Anywhere") { setRegion("Anywhere"); setSubRegion(null); setCountry(null); setStateVal(null); return; }
    const cur = Array.isArray(region) ? region : (region && region !== "Anywhere" ? [region] : []);
    const next = cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r];
    setRegion(next.length === 0 ? "Anywhere" : next);
    setSubRegion(null); setCountry(null); setStateVal(null);
  }

  function toggleCountry(c) {
    if (c === "All Countries") { setCountry(null); setStateVal(null); return; }
    const cur = Array.isArray(country) ? country : (country ? [country] : []);
    const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
    setCountry(next.length === 0 ? null : next);
    setStateVal(null);
  }

  function toggleState(s) {
    if (s.startsWith("All")) { setStateVal(null); return; }
    const cur = Array.isArray(stateVal) ? stateVal : (stateVal ? [stateVal] : []);
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    setStateVal(next.length === 0 ? null : next);
  }

  const isAmericas = regions.includes("Americas") || region === "Americas";
  const isMultiRegion = Array.isArray(region) && region.length > 0;
  const subs = isAmericas ? (SUB_REGIONS["Americas"] || []) : [];

  // Countries for selected regions
  let availableCountries = [];
  const regArr = Array.isArray(region) ? region : (region && region !== "Anywhere" ? [region] : []);
  regArr.forEach(r => {
    if (r === "Americas") {
      if (subRegion) {
        (COUNTRIES_BY_SUB[subRegion] || []).forEach(c => { if (!availableCountries.includes(c)) availableCountries.push(c); });
      }
    } else {
      (COUNTRIES_BY_REGION[r] || []).forEach(c => { if (!availableCountries.includes(c)) availableCountries.push(c); });
    }
  });

  // States for selected countries
  let availableStates = [];
  const countryArr = Array.isArray(country) ? country : (country ? [country] : []);
  countryArr.forEach(c => {
    (STATES_BY_COUNTRY[c] || []).forEach(s => { if (!availableStates.includes(s)) availableStates.push(s); });
  });

  // US cities for selected states
  const stateArr = Array.isArray(stateVal) ? stateVal : (stateVal ? [stateVal] : []);
  const availableCities = [];
  stateArr.filter(s => !s.startsWith("All")).forEach(s => {
    (US_CITIES_BY_STATE[s] || []).forEach(c => { if (!availableCities.includes(c)) availableCities.push(c); });
  });

  return (
    <div>
      <Label color="#4A8CC4">Region <span style={{ fontSize: 10, color: textDim, fontWeight: 400 }}>— select one or more</span></Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
        <Pill active={region === "Anywhere" || regions.length === 0} color={color} onClick={() => toggleRegion("Anywhere")}>Anywhere</Pill>
        {REGIONS.filter(r => r !== "Anywhere").map(r => (
          <Pill key={r} active={regions.includes(r)} color={color} onClick={() => toggleRegion(r)}>{r}</Pill>
        ))}
      </div>

      {/* Sub-regions — Americas only */}
      {isAmericas && subs.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 12, height: 1, background: "rgba(27,58,92,0.4)" }} />
            <Label color="#4a8a9a">Sub-region</Label>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
            {subs.map(sr => (
              <Pill key={sr} active={subRegion === sr} color={color} small onClick={() => {
                setSubRegion(sr === subRegion ? null : sr); setCountry(null); setStateVal(null);
              }}>{sr}</Pill>
            ))}
          </div>
        </>
      )}

      {/* Countries */}
      {availableCountries.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 24, height: 1, background: "rgba(27,58,92,0.3)" }} />
            <Label color="#3a7a8a">Country <span style={{ fontSize: 10, color: textDim, fontWeight: 400 }}>— select multiple</span></Label>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
            <Pill active={countries.length === 0} color={color} small onClick={() => { setCountry(null); setStateVal(null); }}>All Countries</Pill>
            {availableCountries.filter(c => c !== "All Countries").map(c => (
              <Pill key={c} active={countries.includes(c)} color={color} small onClick={() => toggleCountry(c)}>{c}</Pill>
            ))}
          </div>
        </>
      )}

      {/* States / Provinces */}
      {availableStates.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 36, height: 1, background: "rgba(27,58,92,0.2)" }} />
            <Label color="#2a6a7a">State / Province <span style={{ fontSize: 10, color: textDim, fontWeight: 400 }}>— select multiple</span></Label>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: availableCities.length > 0 ? 14 : 24 }}>
            <Pill active={states.length === 0} color={color} small onClick={() => setStateVal(null)}>All States</Pill>
            {availableStates.filter(s => !s.startsWith("All")).map(s => (
              <Pill key={s} active={states.includes(s)} color={color} small onClick={() => toggleState(s)}>{s}</Pill>
            ))}
          </div>
        </>
      )}

      {/* Popular cities for selected US states */}
      {availableCities.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <div style={{ width: 48, height: 1, background: "rgba(27,58,92,0.15)" }} />
            <Label color="#1a5a6a">Popular Cities</Label>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 24 }}>
            {availableCities.map(city => (
              <span key={city} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.1)", color: teal, border: `1px solid rgba(27,58,92,0.2)` }}>
                📍 {city}
              </span>
            ))}
          </div>
        </>
      )}

      {availableCountries.length === 0 && subs.length === 0 && <div style={{ marginBottom: 24 }} />}
    </div>
  );
}

// ── Login modal ─────────────────────────────────────────────────
function LoginModal({ onClose, onSuccess, initialEmail = "", initialBookingCode = "", onOpenRegister = null }) {
  const { darkMode, goldText, amberText, creamText } = useTheme();
  // Pre-fill email from initialEmail prop (e.g. quiz lead handed us the email
  // they just entered, no reason to make them retype it). Normalized to lower
  // case to match how the backend stores email lookups.
  const [form, setForm] = useState({ email: (initialEmail || "").trim().toLowerCase(), password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // ── Booking code save panel state ─────────────────────────────
  // Lets users from the waitlist approval flow paste their TMA-XXXXXX code
  // before signing in. The code stays in localStorage and the booking modal
  // reads it on mount, so the user never has to type it twice.
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeSaved, setCodeSaved] = useState(() => {
    try { return Boolean(localStorage.getItem("jga_pending_booking_code")); }
    catch(e) { return false; }
  });

  async function submit() {
    if (!form.email.includes("@")) { setError("Please enter a valid email."); return; }
    if (!form.password) { setError("Please enter your password."); return; }
    setSubmitting(true);
    setError("");
    try {
      // Try real backend, fall back to local simulation if not running
      let res;
      try {
        const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
        const r = await fetch(`${API}/api/auth/login`, {
          // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email, password: form.password }),
        });
        if (!r.ok) {
          // Status-first error handling. Some rate limiters return 429 with an
          // empty body which would crash `r.json()` and surface as a generic
          // error. Capturing the status before parsing keeps the user message
          // accurate even when backend response has no JSON body.
          if (r.status === 429) {
            throw new Error("Too many attempts. Please wait a moment and try again.");
          }
          const b = await r.json().catch(() => ({}));
          throw new Error(b.error || (r.status >= 500 ? "Server error — please try again in a moment." : "Login failed"));
        }
        res = await r.json();
        try {
          localStorage.setItem("jga_token", res.accessToken);
          // Store refresh token in localStorage when the backend provides one.
          // This enables a cookie-independent refresh path that survives Safari
          // ITP (which blocks the cross-site refresh cookie). No-op if absent.
          if (res.refreshToken) localStorage.setItem("jga_refresh_token", res.refreshToken);
        } catch(e) {}
        onSuccess({ firstName: res.user.firstName, email: form.email, ...res.user, pendingSurvey: res.pendingSurvey });
      } catch (apiErr) {
        throw apiErr;
      }
    } catch (err) {
      // Map known cryptic backend errors to user-friendly text.
      // Backend sometimes returns schema-validation messages like
      // "The string did not match the expected pattern" for what is
      // really just a wrong password or malformed input. Users shouldn't
      // see developer error messages.
      const rawMsg = (err && err.message) || "";
      const lowerMsg = rawMsg.toLowerCase();
      let friendlyMsg;
      if (rawMsg === "Invalid credentials." || lowerMsg.includes("invalid credentials")) {
        friendlyMsg = "Incorrect email or password.";
      } else if (lowerMsg.includes("did not match the expected pattern") || lowerMsg.includes("validation") || lowerMsg.includes("schema")) {
        // Generic validation message — almost certainly a wrong-password or
        // malformed-input scenario. Don't expose the developer wording.
        friendlyMsg = "Incorrect email or password. Make sure there are no extra spaces.";
      } else if (lowerMsg.includes("rate") || lowerMsg.includes("too many")) {
        friendlyMsg = "Too many attempts. Please wait a minute and try again.";
      } else if (lowerMsg.includes("network") || lowerMsg.includes("failed to fetch")) {
        friendlyMsg = "Couldn't reach the server. Check your connection and try again.";
      } else {
        friendlyMsg = rawMsg || "Something went wrong.";
      }
      setError(friendlyMsg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 420, animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)", position: "relative" }}>
        {/* Top-right X — universal "get me out of here" affordance.
           Mirrors the bottom Cancel button. Top-right is where users
           instinctively look first; the X gives them a fast escape
           without scrolling. aria-label for screen readers. */}
        <button onClick={onClose} aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 16,
            border: "none", background: darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.05)",
            color: darkMode ? "rgba(240,230,211,0.7)" : "rgba(0,0,0,0.55)",
            cursor: "pointer", fontFamily: "inherit", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s", zIndex: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.05)"; }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6 L 18 18 M18 6 L 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        </button>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}><span style={{ display: "inline-block", transformOrigin: "70% 80%", animation: "jga-wave 4s ease-in-out infinite" }}>👋</span></div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, ...gradientTextStyle(`linear-gradient(135deg,${teal},#0F2747)`, teal) }}>
            Welcome Back
          </h2>
          <p style={{ color: "#4A6B8A", fontSize: 13, marginTop: 8 }}>Sign in to your Just Go Anywhere account</p>
        </div>

        {/* Booking-code confirmation banner — shown when the user arrived via
            an approval email link (?code=TMA-XXX&email=...). Reassures them
            that signing in WILL bring them to their pending trip. */}
        {initialBookingCode && (
          <div style={{ background: "linear-gradient(135deg, rgba(74,140,196,0.1), rgba(232,154,60,0.08))", border: "1px solid rgba(74,140,196,0.35)", borderRadius: 12, padding: "12px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1B3A5C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12 L 10 17 L 19 7" stroke="#FAFAF5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "#1B3A5C", fontWeight: 800, lineHeight: 1.3 }}>You're cleared to book!</div>
              <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2, lineHeight: 1.4 }}>
                Sign in to find your trip waiting · <span style={{ fontWeight: 700, color: "#1B3A5C", fontFamily: "ui-monospace, monospace" }}>{initialBookingCode}</span>
              </div>
            </div>
          </div>
        )}

        <Input label="Email Address" placeholder="jane@email.com" type="email"
          value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
        <Input label="Password" placeholder="••••••••" type="password"
          value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />

        {error && <div style={{ color: coral, fontSize: 12, marginBottom: 12, marginTop: -6 }}>{error}</div>}

        {/* "Sign up instead" fallback — shown when arriving via approval email
            (initialBookingCode is set). For users who haven't created an
            account yet, this routes them to RegisterModal with email + code
            pre-filled. The code is also persisted in localStorage so the
            post-register flow can route to Trips tab. */}
        {initialBookingCode && typeof onOpenRegister === "function" && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, marginTop: -4 }}>
            <button
              type="button"
              onClick={() => onOpenRegister(form.email || initialEmail || "", initialBookingCode)}
              style={{ background: "none", border: "none", color: amberText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, fontWeight: 600 }}>
              Don't have an account yet? Sign up →
            </button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button onClick={() => setShowCodePanel(s => !s)}
            style={{ background: "none", border: "none", color: goldText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, fontWeight: 600 }}
            aria-expanded={showCodePanel}>
            {showCodePanel ? "Hide code" : "I have a booking code →"}
          </button>
          <button onClick={() => { onClose(); document.dispatchEvent(new CustomEvent("jga:openForgotPassword")); }}
            style={{ background: "none", border: "none", color: teal, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3 }}>
            Forgot password?
          </button>
        </div>

        {/* Booking code save panel — for users who received an approval
           email from the waitlist and have a TMA-XXXXXX code in hand.
           Storing here pre-fills the booking modal after sign-in, so the
           code only has to be typed once. localStorage survives reload
           and persists across the sign-in transition. */}
        {showCodePanel && (
          <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 10, border: `1px solid ${gold}33`, background: `${gold}08` }}>
            {codeSaved ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.15)"/>
                  <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.45 }}>
                  <strong style={{ color: "#6dbd6d" }}>Code saved.</strong> Sign in below, then pick a destination — your code will be ready at checkout.
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: "#4A6B8A", marginBottom: 8, lineHeight: 1.45 }}>
                  Paste your booking code below — we'll have it ready when you check out.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={codeInput}
                    onChange={e => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="TMA-XXXXXX"
                    style={{ flex: 1, padding: "9px 12px", borderRadius: 8, fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: 1.5, background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box", textTransform: "uppercase" }}
                    aria-label="Booking code"
                  />
                  <button type="button" disabled={!codeInput.trim()}
                    onClick={() => {
                      try { localStorage.setItem("jga_pending_booking_code", codeInput.trim()); } catch(e) {}
                      setCodeSaved(true);
                    }}
                    style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: !codeInput.trim() ? "rgba(27,58,92,0.3)" : `linear-gradient(135deg, ${teal}, ${teal}dd)`, color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: !codeInput.trim() ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <Btn variant="teal" onClick={submit} disabled={submitting}>{submitting ? "Signing in…" : "Sign In →"}</Btn>
        <Btn variant="ghost" onClick={onClose} style={{ marginTop: 8 }}>Cancel</Btn>
        <div style={{ textAlign: "center", marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(27,58,92,0.084)", fontSize: 13, color: "#4A6B8A" }}>
          Don't have an account?{" "}
          <button onClick={() => { onClose(); document.dispatchEvent(new CustomEvent("jga:openRegister")); }}
            style={{ background: "none", border: "none", color: goldText, cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 700, textDecoration: "underline", padding: 0 }}>
            Register free →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Forgot Password modal ──────────────────────────────────────
// Triggered via dispatchEvent("jga:openForgotPassword") from LoginModal
function ForgotPasswordModal({ onClose, onBackToLogin }) {
  const { darkMode, goldText, amberText, creamText } = useTheme();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | "sent" | "error"
  const [error, setError] = useState("");
  // Resend cooldown — prevents spam-clicking "Resend" while still letting users
  // request a second send if the first didn't arrive. 60s matches the typical
  // backend rate-limit window. Tick down once per second using setInterval.
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState(null); // null | "sending" | "sent" | "error"
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  async function submit() {
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const r = await fetch(`${API}/api/auth/forgot-password`, {
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      // Backend always returns success to prevent email enumeration
      if (r.ok) {
        setStatus("sent");
        setResendCooldown(60); // start 60s cooldown before user can resend
      } else {
        setStatus("error");
      }
    } catch (e) {
      console.error("[Forgot] Network error:", e);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  }

  // Resend handler — fires the same /api/auth/forgot-password endpoint again.
  // Backend rate limit is 10 attempts per 15 min per IP, so 60s cooldown is
  // generous AND respects backend constraints (worst case, a determined user
  // can resend 15 times in 15 min = well under the limit).
  async function resend() {
    if (resendCooldown > 0 || resendStatus === "sending") return;
    setResendStatus("sending");
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const r = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (r.ok) {
        setResendStatus("sent");
        setResendCooldown(60);
        // Clear the "sent" indicator after 4s so the button label resets cleanly
        setTimeout(() => setResendStatus(null), 4000);
      } else if (r.status === 429) {
        setResendStatus("error");
        setResendCooldown(60); // backend rate-limited us; respect it
      } else {
        setResendStatus("error");
      }
    } catch (e) {
      console.error("[Forgot Resend] Network error:", e);
      setResendStatus("error");
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 420, animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>
        {status === "sent" ? (
          // Success state
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, background: "rgba(109,189,109,0.15)", border: "2px solid rgba(109,189,109,0.5)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="6" width="18" height="13" rx="2" stroke="#6dbd6d" strokeWidth="1.7" fill="rgba(109,189,109,0.15)"/>
                <path d="M3 7 L 12 13 L 21 7" stroke="#6dbd6d" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M16 16 L 19 19 L 22 14" stroke="#6dbd6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 900, color: "#1B3A5C" }}>Check your inbox</h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "#4A6B8A", lineHeight: 1.6 }}>
              If an account exists for <strong style={{ color: "#1B3A5C" }}>{email}</strong>, we've sent a password reset link. It will expire in 1 hour.
            </p>

            {/* "Didn't get it?" panel — concrete steps the user can take to recover */}
            <div style={{
              textAlign: "left", padding: "12px 14px", borderRadius: 10,
              background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.18)",
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, color: teal, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>Didn't see it?</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#4A6B8A", lineHeight: 1.7 }}>
                <li>Check your <strong style={{ color: "#1B3A5C" }}>spam</strong> or <strong style={{ color: "#1B3A5C" }}>promotions</strong> folder</li>
                <li>Search for <span style={{ color: "#4A8CC4", fontFamily: "monospace", fontSize: 11 }}>from:hello@justgoanywhere.com</span></li>
                <li>Wait up to a few minutes — sometimes mail providers delay delivery</li>
                <li>Still nothing? Email <a href="mailto:support@justgoanywhere.com" style={{ color: goldText, textDecoration: "none", fontWeight: 600 }}>support@justgoanywhere.com</a> for help</li>
              </ul>
            </div>

            {/* Resend button — disabled during cooldown, shows live countdown */}
            <button onClick={resend}
              disabled={resendCooldown > 0 || resendStatus === "sending"}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 10,
                border: `1px solid ${resendCooldown > 0 ? "rgba(27,58,92,0.096)" : "rgba(27,58,92,0.4)"}`,
                background: resendCooldown > 0 ? "transparent" : "rgba(27,58,92,0.08)",
                color: resendCooldown > 0 ? "#6a7a90" : teal,
                fontSize: 13, fontWeight: 700, cursor: resendCooldown > 0 ? "default" : "pointer",
                fontFamily: "inherit", marginBottom: 10,
                transition: "all 0.15s",
              }}>
              {resendStatus === "sending" ? "Sending…"
                : resendStatus === "sent" ? "✓ Sent again — check your inbox"
                : resendStatus === "error" ? "Couldn't resend — try again later"
                : resendCooldown > 0 ? `Resend in ${resendCooldown}s`
                : "Resend reset link"}
            </button>

            <Btn variant="ghost" onClick={onClose}>Close</Btn>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔑</div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, ...gradientTextStyle(`linear-gradient(135deg,${teal},#0F2747)`, teal) }}>
                Forgot your password?
              </h2>
              <p style={{ color: "#4A6B8A", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                Enter your email and we'll send you a link to reset it.
              </p>
            </div>

            <Input label="Email Address" placeholder="jane@email.com" type="email" autoComplete="email"
              value={email} onChange={e => setEmail(e.target.value)} />

            {error && <div style={{ color: coral, fontSize: 12, marginBottom: 12, marginTop: -6 }}>{error}</div>}
            {status === "error" && <div style={{ color: coral, fontSize: 12, marginBottom: 12 }}>Couldn't send reset email. Please try again.</div>}

            <Btn variant="teal" onClick={submit} disabled={submitting}>
              {submitting ? "Sending…" : "Send reset link →"}
            </Btn>
            <Btn variant="ghost" onClick={() => { onClose(); onBackToLogin && onBackToLogin(); }} style={{ marginTop: 8 }}>
              Back to sign in
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reset Password modal ──────────────────────────────────────
// Triggered when URL has ?reset=TOKEN
function ResetPasswordModal({ token, onClose, onSuccess }) {
  const { darkMode } = useTheme();
  const [form, setForm] = useState({ password: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | "success" | "expired" | "invalid" | "error"
  const [error, setError] = useState("");

  async function submit() {
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    setError("");
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        body: JSON.stringify({ token, password: form.password }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setStatus("success");
        // Save token + auto-login user
        if (data.accessToken) {
          try { localStorage.setItem("jga_token", data.accessToken); } catch {}
        }
        if (data.refreshToken) { try { localStorage.setItem("jga_refresh_token", data.refreshToken); } catch {} }
        if (data.user && onSuccess) onSuccess(data.user);
      } else if (r.status === 410) {
        setStatus("expired");
      } else if (r.status === 400 || r.status === 404) {
        setStatus("invalid");
        setError(data.error || "This reset link is no longer valid.");
      } else {
        setStatus("error");
      }
    } catch (e) {
      console.error("[Reset] Network error:", e);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 420, animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>
        {status === "success" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, background: "rgba(109,189,109,0.15)", border: "2px solid rgba(109,189,109,0.5)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.15)"/>
                <path d="M8 12 L 11 15 L 16.5 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900, color: "#1B3A5C" }}>Password reset!</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#4A6B8A", lineHeight: 1.5 }}>
              You're now signed in with your new password.
            </p>
            <Btn variant="teal" onClick={onClose}>Continue to dashboard</Btn>
          </div>
        ) : status === "expired" || status === "invalid" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 28, background: "rgba(245,180,80,0.15)", border: "2px solid rgba(245,180,80,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#f5b450" strokeWidth="1.8" fill="rgba(245,180,80,0.15)"/>
                <path d="M12 7 L 12 13" stroke="#f5b450" strokeWidth="2.2" strokeLinecap="round"/>
                <circle cx="12" cy="16.5" r="1.2" fill="#f5b450"/>
              </svg>
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 900, color: "#1B3A5C" }}>
              {status === "expired" ? "This link expired" : "Invalid link"}
            </h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#4A6B8A", lineHeight: 1.6 }}>
              {status === "expired"
                ? "Reset links expire after 1 hour for security. Request a new one to continue."
                : "This link isn't valid. It may have been used already, or copied incorrectly."}
            </p>
            <Btn variant="teal" onClick={() => { onClose(); document.dispatchEvent(new CustomEvent("jga:openForgotPassword")); }}>
              Request a new link
            </Btn>
            <Btn variant="ghost" onClick={onClose} style={{ marginTop: 8 }}>Close</Btn>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, ...gradientTextStyle(`linear-gradient(135deg,${teal},#0F2747)`, teal) }}>
                Choose a new password
              </h2>
              <p style={{ color: "#4A6B8A", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                Pick something secure — at least 8 characters.
              </p>
            </div>

            <Input label="New Password" placeholder="••••••••" type="password" autoComplete="new-password"
              value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
            <Input label="Confirm Password" placeholder="••••••••" type="password" autoComplete="new-password"
              value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} />

            {error && <div style={{ color: coral, fontSize: 12, marginBottom: 12, marginTop: -6 }}>{error}</div>}
            {status === "error" && <div style={{ color: coral, fontSize: 12, marginBottom: 12 }}>Couldn't reset password. Please try again.</div>}

            <Btn variant="teal" onClick={submit} disabled={submitting}>
              {submitting ? "Resetting…" : "Reset password →"}
            </Btn>
            <Btn variant="ghost" onClick={onClose} style={{ marginTop: 8 }}>Cancel</Btn>
          </>
        )}
      </div>
    </div>
  );
}

// ── Registration modal ──────────────────────────────────────────
function RegisterModal({ onClose, onSuccess, initialEmail = "", initialBookingCode = "", onOpenLogin = null }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: initialEmail || "", password: "", confirmPassword: "", dob: "", address: "", referralCode: "", bookingCode: initialBookingCode || "" });
  const [hasPassport, setHasPassport] = useState(false);
  const [ageRange, setAgeRange] = useState(null);
  // DOB parts as independent state — preserves UI selections even when partial,
  // while form.dob remains the single source of truth for submission (only set when complete)
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  // Set true when register response is 409 (email already exists). Drives the
  // "Sign in instead" fallback CTA below the error message: clicking it stashes
  // the booking code (so it's available after login via the existing
  // jga_pending_booking_code flow) and opens LoginModal with email pre-filled.
  // Reset whenever the user edits the form so a fresh attempt isn't pre-locked
  // into the duplicate state.
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const [showCodeRequest, setShowCodeRequest] = useState(false);
  const [codeRequestStatus, setCodeRequestStatus] = useState(null); // null | "sending" | "sent" | "error"
  const [codeRequestForm, setCodeRequestForm] = useState({ context: "" });
  // Collapsible "Have a code?" section — keeps booking + referral code inputs
  // out of the default view (they're optional and most users have neither).
  // Opens automatically when a code arrives via URL (referral/booking links).
  // Also opens when initialBookingCode is passed in via prop from the App-level
  // ?code= deep-link handler (approval email path).
  const [showCodes, setShowCodes] = useState(!!initialBookingCode);

  // Auto-fill referral and booking codes from URL on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref") || params.get("referral");
      const booking = params.get("code") || params.get("booking");
      if (ref) { setForm(p => ({ ...p, referralCode: ref.toUpperCase() })); setShowCodes(true); }
      if (booking) { setForm(p => ({ ...p, bookingCode: booking.toUpperCase() })); setShowCodes(true); }
    } catch (e) {}
  }, []);

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.includes("@")) e.email = "Valid email required";
    if (form.password.length < 8) e.password = "Minimum 8 characters";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    // DOB + address intentionally NOT required at registration — collected at
    // booking instead (progressive disclosure). Asking for full PII up front
    // is jarring and hurts conversion.
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    console.log("[JGA Register] Submit clicked. Form state:", { ...form, password: form.password ? "***" : "EMPTY", confirmPassword: form.confirmPassword ? "***" : "EMPTY", ageRange, hasPassport });
    if (!validate()) {
      console.warn("[JGA Register] Validation failed. Missing/invalid fields will show in red below each input.");
      // Surface a top-level message so user knows something's wrong
      setApiError("Please fill in all required fields above. Missing fields are highlighted in red.");
      // Scroll to top of modal so they can see the errors
      try { document.querySelector('[role="dialog"][aria-label="Create account"]')?.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
      // Also try to focus the first invalid field — guarantees the user sees
      // exactly what's missing rather than just "the form is still here."
      try {
        // Wait one tick for setErrors to apply, then scrollIntoView the first invalid field
        setTimeout(() => {
          const firstInvalidLabel = document.querySelector('[role="dialog"][aria-label="Create account"] input[aria-invalid="true"]');
          if (firstInvalidLabel) {
            firstInvalidLabel.scrollIntoView({ behavior: "smooth", block: "center" });
            firstInvalidLabel.focus({ preventScroll: true });
          }
        }, 100);
      } catch {}
      return;
    }
    setSubmitting(true);
    setApiError("");
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    console.log("[JGA Register] Submitting to:", `${API}/api/auth/register`);
    try {
      try {
        // Only include travel identity if the quiz was completed in THIS browser session.
        // sessionStorage clears when the tab closes — so leftover localStorage data from
        // a previous user/session on the same browser won't bleed into this registration.
        let savedIdentity = null;
        try {
          const sessionFlag = sessionStorage.getItem("jga-quiz-completed-this-session");
          const localIdentity = localStorage.getItem("jga-traveler-type");
          // Only trust localStorage if the session flag matches it
          if (sessionFlag && localIdentity && sessionFlag === localIdentity) {
            savedIdentity = localIdentity;
            console.log("[JGA Register] Including identity from this-session quiz:", savedIdentity);
          } else if (localIdentity && !sessionFlag) {
            console.warn("[JGA Register] Found stale localStorage identity but no session flag — ignoring");
          }
        } catch {}

        const r = await fetch(`${API}/api/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
          body: JSON.stringify({ ...form, hasPassport, ageRange, travelIdentity: savedIdentity || null }),
        });
        console.log("[JGA Register] Backend response status:", r.status);
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          console.error("[JGA Register] Backend rejected (full response):", { status: r.status, body: b });
          // Surface as much detail as possible to help diagnose
          let errorMsg = b.error || b.message || b.detail;
          // 409 = email already registered. ALWAYS override any backend-supplied
          // message with the enumeration-resistant phrasing. The backend may say
          // "Email already taken" or similar in `b.error`, which would otherwise
          // win the `b.error ||` chain above and confirm to a casual attacker that
          // the email is registered. The conditional "if you already have an account"
          // copy preserves the help-the-forgetful-user UX without the leak.
          if (r.status === 409) {
            errorMsg = "If you already have an account with this email, sign in instead — or use \"Forgot password\" if you don't remember your credentials.";
            setDuplicateEmail(true);
          }
          if (!errorMsg) {
            // No explicit error — give a status-specific generic
            if (r.status === 400) errorMsg = "Some information was invalid. Please double-check all fields.";
            else if (r.status === 409) errorMsg = "If you already have an account with this email, sign in instead — or use \"Forgot password\" if you don't remember your credentials.";
            else if (r.status === 422) errorMsg = "Some information didn't pass validation. Please review and try again.";
            else if (r.status >= 500) errorMsg = `Our server had a problem (status ${r.status}). Please try again, or email support@justgoanywhere.com if it keeps failing.`;
            else errorMsg = `Registration failed (status ${r.status}). Please contact support.`;
          }
          throw new Error(errorMsg);
        }
        const res = await r.json();
        console.log("[JGA Register] Success — user created in backend");
        // ── Cross-account leak prevention ──
        // A successful registration is by definition a NEW account. Any
        // pre-existing jga-* localStorage (boards, postcards, stamps, mood,
        // budget, prior tokens/user) belongs to a different user from this
        // browser session — without this clear, that data leaks into the
        // new account and appears pre-populated. Clearing AFTER the register
        // call (not before) keeps the pre-signup quiz identity intact in the
        // request body that's already been sent to the backend.
        _clearAllJgaState("register-success-new-account");
        try {
          localStorage.setItem("jga_token", res.accessToken);
          if (res.refreshToken) localStorage.setItem("jga_refresh_token", res.refreshToken);
        } catch(e) {}
        // Pass through the local form values (the user just typed these — we know them).
        // Backend response is layered on top so server-side fields like emailVerified, referralCode override.
        onSuccess({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          dob: form.dob,
          address: form.address,
          hasPassport,
          ageRange,
          ...res.user,  // server-side fields override (emailVerified, referralCode, id, etc.)
        });
        // ── Clean up identity localStorage AFTER successful registration ──
        // Prevents the next user on this browser from inheriting this user's quiz result.
        // The user's identity is now persisted on the backend, so localStorage is redundant
        // (the dashboard will hydrate from the backend on next login). If the user retakes
        // the quiz later, fresh data goes back into localStorage at that point.
        try {
          localStorage.removeItem("jga-traveler-type");
          localStorage.removeItem("jga-traveler-identity");
          localStorage.removeItem("jga-travel-companion");
          sessionStorage.removeItem("jga-quiz-completed-this-session");
          console.log("[JGA Register] Cleared identity localStorage after successful registration");
        } catch {}
      } catch (apiErr) {
        const msg = apiErr.message || "";
        const isNetworkErr = msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("ERR_CONNECTION_REFUSED") || apiErr.name === "TypeError" || apiErr.name === "AbortError";
        if (isNetworkErr) {
          // Network/connection failure — backend unreachable
          console.error("[JGA Register] Backend unreachable:", apiErr);
          throw new Error("Can't connect to our server. Please check your connection or try again in a moment. (If the issue continues, contact support.)");
        } else {
          throw apiErr;
        }
      }
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const f = (field) => ({
    value: form[field],
    onChange: e => { setForm(p => ({ ...p, [field]: e.target.value })); if (duplicateEmail) setDuplicateEmail(false); },
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.75)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)" }}>
      <div role="dialog" aria-modal="true" aria-label="Create account" style={{ ...lightCard(darkMode), width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(74,140,196,0.3)", position: "relative" }}>
        {/* Top-right X — same affordance as LoginModal */}
        <button onClick={onClose} aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 32, height: 32, borderRadius: 16,
            border: "none", background: darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.05)",
            color: darkMode ? "rgba(240,230,211,0.7)" : "rgba(0,0,0,0.55)",
            cursor: "pointer", fontFamily: "inherit", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s", zIndex: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.05)"; }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6 L 18 18 M18 6 L 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        </button>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <svg width="56" height="56" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "0 auto 10px" }}>
            <circle cx="22" cy="22" r="19" fill={`${gold}22`} stroke={gold} strokeWidth="2"/>
            <ellipse cx="22" cy="22" rx="9" ry="19" fill={`${gold}10`} stroke={gold} strokeWidth="1.6"/>
            <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={gold} strokeWidth="1.6" fill="none"/>
            <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={gold} strokeWidth="1.3" fill="none" opacity="0.7"/>
            <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={gold} strokeWidth="1.3" fill="none" opacity="0.7"/>
          </svg>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, ...gradientTextStyle(`linear-gradient(135deg,${gold},${amber})`, gold) }}>
            Create Your Free Account
          </h2>
          <p style={{ color: "#4A6B8A", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Register free to keep spinning and book your perfect trip!
          </p>
          <div style={{ marginTop: 12, fontSize: 13, color: "#4A6B8A" }}>
            Already have an account?{" "}
            <button onClick={() => { onClose(); document.dispatchEvent(new CustomEvent("jga:openLogin")); }}
              style={{ background: "none", border: "none", color: teal, cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 700, textDecoration: "underline", padding: 0 }}>
              Sign in →
            </button>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); if (!submitting) submit(); }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          <div style={{ paddingRight: 6 }}>
            <Input label="First Name" placeholder="Jane" autoComplete="given-name" {...f("firstName")} />
            {errors.firstName && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.firstName}</div>}
          </div>
          <div style={{ paddingLeft: 6 }}>
            <Input label="Last Name" placeholder="Smith" autoComplete="family-name" {...f("lastName")} />
            {errors.lastName && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.lastName}</div>}
          </div>
        </div>

        <Input label="Email Address" placeholder="jane@email.com" type="email" autoComplete="email" inputMode="email" {...f("email")} />
        {errors.email && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.email}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          <div style={{ paddingRight: 6 }}>
            <Input label="Password" placeholder="Min. 8 characters" type="password" autoComplete="new-password" {...f("password")} />
            {errors.password && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.password}</div>}
          </div>
          <div style={{ paddingLeft: 6 }}>
            <Input label="Confirm Password" placeholder="••••••••" type="password" autoComplete="new-password" {...f("confirmPassword")} />
            {errors.confirmPassword && <div style={{ color: coral, fontSize: 11, marginTop: -10, marginBottom: 8 }}>{errors.confirmPassword}</div>}
          </div>
        </div>

        {/* DOB, Home Address, Age Range, KTN, and passport number are NOT
            collected here anymore — they're requested at booking, where the
            ask is contextually obvious and the user already has intent. This
            keeps registration to name + email + password only. The passport
            yes/no checkbox below stays as a lightweight personalization signal
            (it doesn't ask for the actual passport number). */}

        {/* Age range — auto-filled from DOB, user can adjust */}
        {false && (
        <div style={{ marginBottom: 16 }}>
          <Label>Your Age Range <span style={{ fontSize: 11, color: "#4A6B8A", fontWeight: 400 }}>— auto-filled from your date of birth</span></Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {AGE_RANGES.map(a => (
              <Pill key={a.id} active={ageRange === a.id} color={a.color} onClick={() => setAgeRange(ageRange === a.id ? null : a.id)}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{a.svg(ageRange === a.id ? a.color : "#4A6B8A", 16)}</span>
                  {a.label}
                </span>
              </Pill>
            ))}
          </div>
        </div>
        )}

        {/* Collapsible codes section — booking + referral codes are optional
            and most users have neither, so they're hidden behind a toggle to
            keep registration short. Auto-expands when a code arrives via URL. */}
        {!showCodes ? (
          <button type="button" onClick={() => setShowCodes(true)}
            style={{ width: "100%", marginBottom: 16, padding: "11px 14px", borderRadius: 10, border: `1px dashed rgba(27,58,92,0.25)`, background: "transparent", color: teal, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Have a booking or referral code?</span>
            <span style={{ fontSize: 16, color: "#4A8CC4" }}>+</span>
          </button>
        ) : (
        <div style={{ marginBottom: 16, padding: "14px 14px 4px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.15)", background: "rgba(27,58,92,0.025)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#4A6B8A", letterSpacing: 0.5 }}>Codes</span>
            <button type="button" onClick={() => setShowCodes(false)}
              style={{ background: "none", border: "none", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              Hide
            </button>
          </div>

        {/* Booking code */}
        <div style={{ marginBottom: 16 }}>
          <Label>Booking Code (optional) <span style={{ fontSize: 11, color: "#4A6B8A", fontWeight: 400 }}>— if you've already booked a trip with us</span></Label>
          <BookingCodeInput
            value={form.bookingCode}
            onChange={(v) => setForm(p => ({ ...p, bookingCode: v }))}
            placeholder="PAR3K7"
            accentColor={teal}
            name="bookingCode"
          />
          {form.bookingCode && <div style={{ fontSize: 11, color: teal, marginTop: 4 }}>✓ We'll link this booking to your new account</div>}

          {/* Join-the-waitlist link — for users who don't yet have a
             booking code. Phase 1 launch: everyone without a code goes
             through the waitlist. Phase 2: we'll bring back direct code
             requests for a broader audience. */}
          {!form.bookingCode && !showCodeRequest && (
            <button type="button" onClick={() => setShowCodeRequest(true)}
              style={{ marginTop: 8, padding: "6px 0", background: "transparent", border: "none", color: teal, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, display: "inline-flex", alignItems: "center", gap: 5 }}>
              Don't have a code? Join the waitlist →
            </button>
          )}

          {/* Inline waitlist signup — POSTs to /api/waitlist */}
          {showCodeRequest && (
            <div style={{ marginTop: 10, padding: "14px 14px 12px", borderRadius: 10, border: `1px solid ${teal}40`, background: `${teal}06` }}>
              {codeRequestStatus === "sent" ? (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.7" fill="rgba(109,189,109,0.15)"/>
                    <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                  <div style={{ fontSize: 12, color: textPrimary, lineHeight: 1.5 }}>
                    <strong style={{ color: "#6dbd6d" }}>You're on the waitlist.</strong> We'll email you when bookings open for your area. Keep building your account in the meantime — your boards and preferences carry over.
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: textPrimary, fontWeight: 700, marginBottom: 4 }}>Join the waitlist</div>
                  <div style={{ fontSize: 11, color: textMuted, marginBottom: 10, lineHeight: 1.5 }}>
                    Tell us a bit about the trip you're dreaming of. We're rolling out booking access in waves — you'll hear from us by email.
                  </div>
                  <textarea value={codeRequestForm.context}
                    onChange={e => setCodeRequestForm({ context: e.target.value })}
                    placeholder="e.g. Honeymoon to Lisbon next spring, or somewhere warm for the holidays"
                    rows={3}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box", resize: "vertical", marginBottom: 10 }}/>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" disabled={codeRequestStatus === "sending" || !codeRequestForm.context.trim() || !form.email}
                      onClick={async () => {
                        if (!form.email) { alert("Please enter your email above first — we'll email you when you're cleared to book."); return; }
                        setCodeRequestStatus("sending");
                        const result = await joinWaitlist({
                          customerName: `${form.firstName || ""} ${form.lastName || ""}`.trim() || "Pre-registration request",
                          email: form.email,
                          notes: codeRequestForm.context,
                          mode: "general",
                        });
                        if (result.ok) setCodeRequestStatus("sent");
                        else setCodeRequestStatus("error");
                      }}
                      style={{ flex: 1, padding: "9px", borderRadius: 8, border: "none", background: codeRequestStatus === "sending" || !codeRequestForm.context.trim() || !form.email ? "rgba(27,58,92,0.4)" : `linear-gradient(135deg, ${teal}, ${teal}dd)`, color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: codeRequestStatus === "sending" || !codeRequestForm.context.trim() || !form.email ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                      {codeRequestStatus === "sending" ? "Sending…" : codeRequestStatus === "error" ? "Try again" : "Join the waitlist"}
                    </button>
                    <button type="button" onClick={() => { setShowCodeRequest(false); setCodeRequestStatus(null); setCodeRequestForm({ context: "" }); }}
                      style={{ padding: "9px 14px", borderRadius: 8, border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                  {codeRequestStatus === "error" && (
                    <div style={{ fontSize: 11, color: coral, marginTop: 8 }}>Couldn't join the waitlist right now. Please try again or email support@justgoanywhere.com</div>
                  )}
                  {!form.email && (
                    <div style={{ fontSize: 10, color: "#f5b450", marginTop: 8, fontStyle: "italic" }}>↑ Enter your email above first so we know where to reach you</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Referral code */}
        <div style={{ marginBottom: 16 }}>
          <Label>Referral Code (optional)</Label>
          <BookingCodeInput
            value={form.referralCode}
            onChange={(v) => setForm(p => ({ ...p, referralCode: v }))}
            placeholder="JAN4X2"
            accentColor="#6dbd6d"
            ariaLabel="Referral code"
            name="referralCode"
          />
          {form.referralCode && <div style={{ fontSize: 11, color: "#6dbd6d", marginTop: 4 }}>✓ Referral applied — your friend gets a free month too!</div>}
        </div>
        </div>
        )}

        {/* Passport checkbox */}
        <button onClick={() => setHasPassport(p => !p)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, marginBottom: 16, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s", border: `1px solid ${hasPassport ? "rgba(74,140,196,0.5)" : "rgba(27,58,92,0.120)"}`, background: hasPassport ? "rgba(74,140,196,0.07)" : "rgba(27,58,92,0.045)" }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${hasPassport ? gold : "rgba(27,58,92,0.23)"}`, background: hasPassport ? gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
            {hasPassport && <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#FAFAF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: hasPassport ? gold : "#4A6B8A" }}>I have a valid passport</div>
            <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2 }}>Just a yes/no — add details later in your profile when you're ready. 🔒 Encrypted on save.</div>
          </div>
          <div style={{ fontSize: 20 }}>🛂</div>
        </button>

        {!hasPassport && (
          <div style={{ background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#c07070", lineHeight: 1.6 }}>
            No passport yet? No worries — you can still explore destinations. We'll show you how to apply when you're ready to book.
          </div>
        )}

        <div style={{ background: "rgba(74,140,196,0.05)", borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 11, color: "#4A6B8A", lineHeight: 1.6 }}>
          🔒 Your information is secure and will only be used to personalise your travel experience.
        </div>

        {apiError && (
          <div style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.25)", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: coral }}>
            {apiError}
          </div>
        )}

        {/* Duplicate-email fallback CTA — when register fails with 409 (email
            already exists), offer to switch to Login with the email pre-filled
            and the booking code stashed in jga_pending_booking_code so the
            existing post-login flow links the trip after sign-in. Only renders
            when both duplicateEmail flag is set AND onOpenLogin is provided. */}
        {duplicateEmail && typeof onOpenLogin === "function" && (
          <div style={{ background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.3)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#1B3A5C", marginBottom: 8, lineHeight: 1.5 }}>
              {form.bookingCode || initialBookingCode
                ? "We'll save your booking code and link it after you sign in."
                : "Switch to sign in with this email."}
            </div>
            <button
              type="button"
              onClick={() => {
                // Stash the booking code (from form or prop) so the existing
                // post-login flow picks it up via the jga_pending_booking_code
                // localStorage key — same plumbing the in-app code-save panel uses.
                const codeToStash = (form.bookingCode || initialBookingCode || "").trim().toUpperCase();
                if (codeToStash) {
                  try { localStorage.setItem("jga_pending_booking_code", codeToStash); } catch {}
                }
                const emailToPass = (form.email || initialEmail || "").trim().toLowerCase();
                onOpenLogin(emailToPass);
                onClose();
              }}
              style={{
                width: "100%", padding: "10px", borderRadius: 8, border: "none",
                background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                color: "#FAFAF5", fontSize: 13, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 8px rgba(27,58,92,0.18)",
              }}>
              Sign In Instead →
            </button>
          </div>
        )}

        {Object.keys(errors).length > 0 && (
          <div style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.25)", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: coral }}>
            ⚠️ Please fix: {Object.entries(errors).map(([field, msg], i, arr) => (
              <span key={field}>{field === "firstName" ? "First Name" : field === "lastName" ? "Last Name" : field === "confirmPassword" ? "Confirm Password" : field === "dob" ? "Date of Birth" : field.charAt(0).toUpperCase() + field.slice(1)} ({msg}){i < arr.length - 1 ? ", " : ""}</span>
            ))}
          </div>
        )}

        <Btn type="submit" onClick={submit} disabled={submitting}>{submitting ? "Creating account…" : "Create Account & Keep Exploring →"}</Btn>
        <Btn variant="ghost" type="button" onClick={onClose} style={{ marginTop: 8 }}>Maybe later</Btn>
        </form>
      </div>
    </div>
  );
}

// ── Mood Quiz Modal ─────────────────────────────────────────────
// 3-question mood check that picks fresh vibes for "Trips matching your mood"
function MoodQuizModal({ questions, onClose, onComplete }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  function pickAnswer(opt) {
    const newAnswers = [...answers, opt];
    if (step === questions.length - 1) {
      onComplete(newAnswers);
    } else {
      setAnswers(newAnswers);
      setStep(step + 1);
    }
  }

  function goBack() {
    if (step === 0) return;
    setAnswers(answers.slice(0, -1));
    setStep(step - 1);
  }

  const q = questions[step];

  return (
    <div role="dialog" aria-modal="true" aria-label="Mood check"
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 420, border: `1px solid #4A8CC430` }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {step > 0 && (
              <button onClick={goBack} aria-label="Go back to previous question"
                style={{ background: "none", border: "none", color: "#4A8CC4", fontSize: 18, cursor: "pointer", fontFamily: "inherit", padding: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>←</button>
            )}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M2 12 Q5 6, 8 12 T 14 12 T 20 12 T 22 12" stroke="#4A8CC4" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                  <circle cx="12" cy="12" r="2" fill="#4A8CC4" opacity="0.9"/>
                </svg>
                <div style={{ fontSize: 11, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 700 }}>Mood Check · {step + 1} of {questions.length}</div>
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", color: textMuted, fontSize: 22, cursor: "pointer", fontFamily: "inherit", padding: 4 }}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {questions.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? "#4A8CC4" : (darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.06)") }} />
          ))}
        </div>

        <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800, color: textPrimary, lineHeight: 1.3 }}>{q.q}</h2>

        {/* Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {q.options.map(opt => (
            <button key={opt.label} onClick={() => pickAnswer(opt)}
              style={{ padding: "14px 16px", borderRadius: 12, border: `1.5px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", cursor: "pointer", fontFamily: "inherit", textAlign: "left", fontSize: 15, fontWeight: 600, color: textPrimary, transition: "all 0.15s", display: "flex", alignItems: "center", gap: 12 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#4A8CC4"; e.currentTarget.style.background = "#4A8CC410"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)"; }}>
              {opt.icon && <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{opt.icon}</span>}
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── CollapsibleSection — dashboard section wrapper ─────────────────
// Wraps dashboard rails/cards in a collapse/expand container with persistent
// state. Header shows title + optional count + chevron. Per-section default
// (open vs closed) configured at call site. State persisted to localStorage
// keyed by `id` so a user's preferences stick across sessions.
//
// Skipped entirely if always-visible — only the sections we WANT collapsible
// get wrapped. Not a wholesale render-all-sections-collapsible system.
function CollapsibleSection({ id, title, count = null, defaultOpen = true, accent = "#1B3A5C", children, style = {}, order = undefined }) {
  const storageKey = `jga-collapse-${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "open") return true;
      if (stored === "closed") return false;
    } catch {}
    return defaultOpen;
  });
  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? "open" : "closed"); } catch {}
      return next;
    });
  };
  return (
    <div style={{ order, ...style }}>
      <button
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`collapse-${id}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          marginBottom: open ? 12 : 0,
          borderRadius: 16,
          border: `1px solid ${accent}66`,
          background: open ? "transparent" : "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
          boxShadow: open ? "none" : "0 1px 0 rgba(255,255,255,0.4) inset, 0 2px 8px rgba(27,58,92,0.04)",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          transition: "background 0.15s ease, margin-bottom 0.2s ease",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase", fontWeight: 800, color: accent }}>{title}</span>
          {count !== null && (
            <span style={{ fontSize: 10, fontWeight: 700, color: accent, opacity: 0.55, letterSpacing: 1 }}>· {count}</span>
          )}
        </div>
        <span aria-hidden="true" style={{
          fontSize: 12,
          color: accent,
          opacity: 0.85,
          transition: "transform 0.2s ease",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          display: "inline-block",
        }}>▼</span>
      </button>
      {open && (
        <div id={`collapse-${id}`} style={{ animation: "fadeIn 0.2s ease" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Quick Book Modal — opened from dashboard discovery cards ─────
// Minimal booking flow: pick dates + travelers, see estimate, proceed to BookingModal
// Optional inline itinerary preview via onPreview callback
// Clever rotating loading messages for itinerary generation. Travel-curious
// brand voice; rotates every ~2.4s while loading so impatient users see new
// content instead of staring at one frozen line. The original list of 8 was
// extended Jun 19, 2026 to ~14 since AI gen takes 1min+ and users will see
// most of them during a single wait.
const ITINERARY_LOADING_MESSAGES = [
  "Consulting the locals…",
  "Tracing your route…",
  "Pinning the spots worth your time…",
  "Asking what's worth the detour…",
  "Polishing your future passport stamps…",
  "Reading between the timetables…",
  "Charting the constellations…",
  "Eavesdropping at the train station…",
  "Folding the map just right…",
  "Checking which café has better espresso…",
  "Picking the seat with the best window…",
  "Wandering off the beaten path…",
  "Asking around for the best view…",
  "Trying to remember what month it is there…",
];

// Rotating loader for the itinerary generation flow. Picks a random message
// on mount and rotates every 2400ms with a quick cross-fade. Self-contained
// — no parent state needed. Cleans up its interval on unmount.
function RotatingItineraryLoader({ accentColor = "#4A8CC4" }) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * ITINERARY_LOADING_MESSAGES.length));
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    let switchTimer = null;
    const tick = setInterval(() => {
      // Fade out, swap text, fade back in. The 200ms blink keeps things
      // readable without making the rotation feel laggy.
      setVisible(false);
      switchTimer = setTimeout(() => {
        setIdx(prev => {
          // Avoid showing the same message twice in a row when possible
          if (ITINERARY_LOADING_MESSAGES.length < 2) return prev;
          let next = Math.floor(Math.random() * ITINERARY_LOADING_MESSAGES.length);
          if (next === prev) next = (next + 1) % ITINERARY_LOADING_MESSAGES.length;
          return next;
        });
        setVisible(true);
      }, 200);
    }, 4000);
    return () => {
      clearInterval(tick);
      if (switchTimer) clearTimeout(switchTimer);
    };
  }, []);
  return (
    <div style={{ textAlign: "center", padding: "28px 0" }}>
      <div style={{
        color: accentColor, fontSize: 14, fontWeight: 700,
        animation: "pulse 1.5s ease-in-out infinite",
        opacity: visible ? 1 : 0, transition: "opacity 200ms ease",
        minHeight: 20, // reserves vertical space so layout doesn't jump on fade
      }}>
        {ITINERARY_LOADING_MESSAGES[idx]}
      </div>
    </div>
  );
}

function QuickBookModal({ dest, departureAirport, flightPrice, userBudget, defaultTravelers = 1, defaultDateRange = null, onClose, onConfirm, onPreview, accentColor = "#1B3A5C", labelTag = "", saveItinerary = null, autoPreview = false }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor, goldText, amberText, creamText } = useTheme();
  const teal = "#1B3A5C";
  const gold = "#4A8CC4";

  // ── Compute Fri-Mon defaults if requested ──
  // For Quick Getaways: pick next available Friday → following Monday
  const computeFriMonDefaults = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    let daysUntilFri = (5 - dayOfWeek + 7) % 7;
    // If today is already Thu/Fri/Sat/Sun, skip to NEXT week's Friday for booking lead time
    if (daysUntilFri < 2) daysUntilFri += 7;
    const friday = new Date(today);
    friday.setDate(today.getDate() + daysUntilFri);
    const monday = new Date(friday);
    monday.setDate(friday.getDate() + 3); // Fri + 3 = Monday
    const fmt = d => d.toISOString().split("T")[0];
    return { depart: fmt(friday), ret: fmt(monday) };
  };

  // initialDates handles three shapes of defaultDateRange:
  //   - "fri-mon"  → compute next Fri-Mon long weekend
  //   - { departDate, returnDate, days } → from a saved itinerary; use the
  //     explicit dates if present, otherwise fall back to a depart+days
  //     calculation when the user picks a depart date
  //   - null       → empty, falls back to depart+tripDays auto-fill
  const initialDates = (() => {
    if (defaultDateRange === "fri-mon") return computeFriMonDefaults();
    if (defaultDateRange && typeof defaultDateRange === "object") {
      return {
        depart: defaultDateRange.departDate || "",
        ret: defaultDateRange.returnDate || "",
      };
    }
    return { depart: "", ret: "" };
  })();
  // tripDays — the duration to use when auto-filling the return after the
  // user picks a depart date. Defaults to 7 (week-long trip) but a saved
  // itinerary may carry its own day count (e.g. a 3-day SF trip).
  const tripDays = (defaultDateRange && typeof defaultDateRange === "object" && defaultDateRange.days)
    ? defaultDateRange.days
    : 7;

  const [departDate, setDepartDate] = useState(initialDates.depart);
  const [returnDate, setReturnDate] = useState(initialDates.ret);
  const [travelers, setTravelers] = useState(defaultTravelers);

  // Save-for-later state — drives the top-right header button feedback.
  // idle → saving → saved → (auto-close 1.5s later). Persists trip metadata
  // (dest, dates, travelers, budget intent) to localStorage so the user can
  // come back to it from Account → Saved Drafts (downstream wiring to surface
  // these in the Itineraries tab is separate; this just persists the data
  // and gives the user clear feedback that the save happened).
  const [saveForLaterState, setSaveForLaterState] = useState("idle");
  const handleSaveForLater = () => {
    if (saveForLaterState !== "idle") return;
    setSaveForLaterState("saving");
    try {
      const draft = {
        id: `draft-${Date.now()}`,
        dest: dest.name,
        emoji: dest.emoji,
        country: dest.country,
        state: dest.state || null,
        departDate: departDate || null,
        returnDate: returnDate || null,
        travelers,
        savedAt: new Date().toISOString(),
        labelTag: labelTag || null,
      };
      const existing = JSON.parse(localStorage.getItem("jga-trip-drafts") || "[]");
      // Dedupe by dest+departDate to avoid spammy duplicates from repeat taps.
      const filtered = existing.filter(d => !(d.dest === draft.dest && d.departDate === draft.departDate));
      filtered.unshift(draft);
      localStorage.setItem("jga-trip-drafts", JSON.stringify(filtered.slice(0, 50))); // cap at 50
      setSaveForLaterState("saved");
      setTimeout(() => { if (typeof onClose === "function") onClose(); }, 1500);
    } catch (err) {
      console.error("[JGA] Save for later failed:", err);
      setSaveForLaterState("idle");
    }
  };
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Loading message rotation moved to RotatingItineraryLoader (self-managed).
  const [previewItinerary, setPreviewItinerary] = useState(null);
  // Tracks the itinerary save action — null = not attempted, "saving" = in flight,
  // "saved" = persisted, "error" = save failed. Drives the corner save button state.
  const [itinerarySaveState, setItinerarySaveState] = useState(null);
  // Tracks whether to show the post-save fact card. Set true when a save
  // succeeds (if a funFact exists for the destination); cleared when modal
  // closes. Separate from itinerarySaveState so the fact persists longer
  // than the 3-second 'saved' button state.
  const [showSavedFact, setShowSavedFact] = useState(false);

  // ── Bundle options — what to include in the trip ──
  const [includeHotel, setIncludeHotel] = useState(true);
  const [includeActivities, setIncludeActivities] = useState(true);
  const [includeTransport, setIncludeTransport] = useState(false);

  // Pre-launch: the booking gate locks the earliest selectable date to
  // BOOKING_OPEN_DATE so users can't request travel before we're ready.
  // After launch this auto-rolls forward to today.
  const today = getMinTravelDate();
  // ── days fallback uses tripDays from saved itinerary, not a hard-coded 7 ──
  // When opened from a 3-day SF saved itinerary, the initial estimate (before
  // user picks dates) should show 3-day costs, not 7-day. Without this the
  // total visibly drops when dates are picked, which looks like a bug.
  const days = (departDate && returnDate)
    ? Math.max(1, Math.round((new Date(returnDate) - new Date(departDate)) / 86400000))
    : (tripDays || 7);

  // ── Date-aware flight cost ──
  // The flightPrice prop is computed at modal open time WITHOUT a date, so
  // it's always the optimistic baseline (no booking-window multiplier).
  // Once the user picks a depart date, recompute through estimateFlightPrice
  // so the last-minute / short-notice premium reflects in the total. Falls
  // back to the prop if dates aren't picked or estimation isn't available.
  const effectiveFlightCost = (() => {
    if (!departDate || !departureAirport || !dest?.name) return flightPrice || 0;
    const dateAware = (typeof estimateFlightPrice === "function")
      ? estimateFlightPrice(departureAirport, dest.name, departDate)
      : null;
    return (dateAware !== null && dateAware !== undefined) ? dateAware : (flightPrice || 0);
  })();

  // Cost components
  const flightCost = effectiveFlightCost;
  const hotelEstimate = Math.round((dest?.minBudget || 1000) * 0.55);
  const activitiesEstimate = Math.round(50 * days * travelers); // ~$50/day/person
  const transportEstimate = Math.round(40 * days); // ~$40/day for rideshare/local transport

  const estTotal = flightCost
    + (includeHotel ? hotelEstimate : 0)
    + (includeActivities ? activitiesEstimate : 0)
    + (includeTransport ? transportEstimate : 0);
  const totalForTravelers = (flightCost * travelers)
    + (includeHotel ? hotelEstimate : 0)
    + (includeActivities ? activitiesEstimate : 0)
    + (includeTransport ? transportEstimate : 0);
  // canBook: requires both dates, return after depart, AND depart >= today.
  // The depart-vs-today check is needed because iOS Safari's native date picker
  // doesn't strictly honor the min attribute on input[type=date] — users can
  // select past dates even though we set min={today}. This guard prevents the
  // form submitting with a past departure even if the input picker fails us.
  const canBook = !!departDate && !!returnDate
    && new Date(returnDate) > new Date(departDate)
    && departDate >= today;
  // Clamping setters — use the shared helpers (applyDepartChange /
  // applyReturnChange) so behavior is consistent with TMA + Explore booking
  // date pickers. Notable behaviors:
  //   - Past-date clamping to getMinTravelDate() (pre-launch floor or today)
  //   - Auto-fill return to depart+tripDays when return is empty (tripDays
  //     comes from the saved itinerary if any, else defaults to 7)
  //   - PRESERVE trip duration when depart changes with an existing return
  //     (e.g. 3-day trip stays 3 days when user shifts the depart date)
  const clampedSetDepart = (v) => applyDepartChange(v, departDate, returnDate, setDepartDate, setReturnDate, tripDays);
  const clampedSetReturn = (v) => applyReturnChange(v, departDate, setReturnDate);
  const fitsBudget = userBudget && totalForTravelers <= userBudget;
  const overBy = userBudget ? totalForTravelers - userBudget : 0;

  async function handlePreview() {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    setShowPreview(true);
    if (previewItinerary || !onPreview) return; // already loaded
    setPreviewLoading(true);
    try {
      const result = await onPreview(dest, days);
      setPreviewItinerary(result);
    } catch (e) {
      console.error("[JGA Preview] Failed:", e);
    }
    setPreviewLoading(false);
  }

  // Auto-trigger preview when modal opens with autoPreview=true (e.g., user
  // tapped the "Trip" icon on a Mood Match card instead of the card body).
  // Skips the manual "Preview a sample itinerary first" tap so user lands
  // directly in the generating-state.
  //
  // We use a ref-based "already fired" guard rather than empty deps + disable
  // comment because Vercel's CI strict lint mode treats hook-dep warnings as
  // errors. The ref pattern is recognized by react-hooks/exhaustive-deps as
  // a stable reference, so listing all dep dynamic values is fine.
  const autoPreviewFiredRef = useRef(false);
  useEffect(() => {
    if (autoPreviewFiredRef.current) return;
    if (autoPreview && !showPreview && !previewItinerary && onPreview) {
      autoPreviewFiredRef.current = true;
      handlePreview();
    }
  }, [autoPreview, showPreview, previewItinerary, onPreview]); // eslint-disable-line

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#fff", borderRadius: 18, padding: 22, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto", border: `1.5px solid ${accentColor}40`, boxShadow: `0 20px 60px ${accentColor}25`, animation: "fadeIn 0.25s ease" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {labelTag && <div style={{ fontSize: 10, letterSpacing: 2, color: accentColor, textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>{labelTag}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 32 }}>{dest?.emoji || "🌍"}</span>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: textPrimary, lineHeight: 1.1 }}>{dest?.name}</h2>
                <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{dest?.country}{dest?.state ? ` · ${dest.state}` : ""}</div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {/* Save for later — for users who want to come back to this trip
                without booking now. Persists to localStorage via handleSaveForLater. */}
            <button
              onClick={handleSaveForLater}
              disabled={saveForLaterState !== "idle"}
              aria-label={saveForLaterState === "saved" ? "Trip saved for later" : "Save trip for later"}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                background: saveForLaterState === "saved" ? "rgba(109,189,109,0.10)" : "rgba(27,58,92,0.072)",
                border: `1px solid ${saveForLaterState === "saved" ? "rgba(109,189,109,0.35)" : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`,
                color: saveForLaterState === "saved" ? "#3a8a3a" : accentColor,
                padding: "5px 10px", borderRadius: 10,
                cursor: saveForLaterState === "idle" ? "pointer" : "default",
                fontSize: 11, fontWeight: 700,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease",
              }}
            >
              {saveForLaterState === "saved" ? (
                <>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Saved
                </>
              ) : (
                <>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
                  {saveForLaterState === "saving" ? "Saving…" : "Save for later"}
                </>
              )}
            </button>
            <button onClick={onClose} aria-label="Close"
              style={{ background: "rgba(27,58,92,0.072)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, color: textMuted, fontSize: 18, padding: "4px 11px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit", lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Date pickers */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>📅 When do you want to go?</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>DEPARTURE</div>
              <input type="date" value={departDate} onChange={e => clampedSetDepart(e.target.value)} min={today}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${departDate ? `${gold}66` : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, background: departDate ? "rgba(74,140,196,0.07)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", color: departDate ? gold : textPrimary, fontSize: 13, fontWeight: departDate ? 700 : 400, fontFamily: "inherit", outline: "none", boxSizing: "border-box", cursor: "pointer" }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>RETURN</div>
              <input type="date" value={returnDate} onChange={e => clampedSetReturn(e.target.value)} min={departDate || today}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${returnDate ? `${gold}66` : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, background: returnDate ? "rgba(74,140,196,0.07)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", color: returnDate ? gold : textPrimary, fontSize: 13, fontWeight: returnDate ? 700 : 400, fontFamily: "inherit", outline: "none", boxSizing: "border-box", cursor: "pointer" }} />
            </div>
          </div>
          {departDate && returnDate && (
            <div style={{ fontSize: 11, color: textMuted, marginTop: 6, fontStyle: "italic" }}>
              {days} {days === 1 ? "day" : "days"} · {new Date(departDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {new Date(returnDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              {defaultDateRange === "fri-mon" && <span style={{ color: "#7A8467", marginLeft: 6 }}>· long weekend</span>}
            </div>
          )}
          {isPreLaunch() && (
            <div style={{
              marginTop: 10,
              padding: "8px 12px",
              borderRadius: 10,
              background: "rgba(74,140,196,0.08)",
              border: "1px solid rgba(74,140,196,0.30)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="#4A8CC4" strokeWidth="1.6"/>
                <path d="M12 7v5l3 2" stroke="#4A8CC4" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: 11, color: "#1B3A5C", fontWeight: 600 }}>
                Coming soon
              </span>
            </div>
          )}
        </div>

        {/* ── Bundle options — what to include ── */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>✦ Add to your trip</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { key: "hotel", label: "Hotel", note: `~$${hotelEstimate.toLocaleString()} for ${days} ${days === 1 ? "night" : "nights"}`, value: includeHotel, setter: setIncludeHotel, icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 21 L 3 8 L 12 3 L 21 8 L 21 21 Z M 9 21 L 9 14 L 15 14 L 15 21" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/></svg>) },
              { key: "activities", label: "Activities", note: `~$${activitiesEstimate.toLocaleString()} (~$50/day/person)`, value: includeActivities, setter: setIncludeActivities, icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2 L 14 8 L 20 9 L 15.5 13 L 17 19 L 12 16 L 7 19 L 8.5 13 L 4 9 L 10 8 Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/></svg>) },
              { key: "transport", label: "Local transport", note: `~$${transportEstimate.toLocaleString()} for rideshare/car`, value: includeTransport, setter: setIncludeTransport, icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 17 H 4 V 11 L 6 6 H 18 L 20 11 V 17 H 19 M 5 17 V 19 H 7 V 17 M 17 17 V 19 H 19 V 17" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/><circle cx="7.5" cy="14" r="1.2" fill="currentColor"/><circle cx="16.5" cy="14" r="1.2" fill="currentColor"/></svg>) },
            ].map(opt => (
              <button key={opt.key}
                onClick={() => opt.setter(!opt.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 12px", borderRadius: 10,
                  border: `1px solid ${opt.value ? `${accentColor}50` : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`,
                  background: opt.value ? `${accentColor}10` : "transparent",
                  cursor: "pointer", fontFamily: "inherit",
                  textAlign: "left", width: "100%",
                  transition: "all 0.15s",
                }}>
                {/* Checkbox indicator */}
                <div style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                  border: `1.5px solid ${opt.value ? accentColor : darkMode ? "rgba(27,58,92,0.23)" : "rgba(0,0,0,0.2)"}`,
                  background: opt.value ? accentColor : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {opt.value && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12 L 10 17 L 19 7" stroke="#FAFAF5" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  )}
                </div>
                {/* Icon */}
                <span style={{ color: opt.value ? accentColor : textMuted, display: "flex", alignItems: "center", flexShrink: 0 }}>{opt.icon}</span>
                {/* Label + note */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: opt.value ? textPrimary : textMuted, lineHeight: 1.2 }}>{opt.label}</div>
                  <div style={{ fontSize: 10, color: textDim, marginTop: 1, lineHeight: 1.3 }}>{opt.note}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Travelers */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>👥 Travelers</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[1,2,3,4].map(n => (
              <button key={n} onClick={() => setTravelers(n)}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1px solid ${travelers === n ? `${accentColor}80` : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: travelers === n ? `${accentColor}18` : "transparent", color: travelers === n ? accentColor : textMuted, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                {formatTravelers(n, "short")}
              </button>
            ))}
          </div>
        </div>

        {/* Estimate vs Budget */}
        {departureAirport && estTotal > 0 && (
          <div style={{ background: `${accentColor}08`, border: `1px solid ${accentColor}25`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>Estimated total</div>
                <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>From {departureAirport} · {travelers > 1 ? `${travelers} travelers` : "per person"}</div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: accentColor }}>${totalForTravelers.toLocaleString()}</div>
            </div>
            <div style={{ fontSize: 10, color: textDim, marginTop: 6, fontStyle: "italic" }}>
              Includes: flight ~${flightPrice?.toLocaleString() || "?"}{travelers > 1 ? ` × ${travelers}` : ""}
              {includeHotel && ` + hotel ~$${hotelEstimate.toLocaleString()}`}
              {includeActivities && ` + activities ~$${activitiesEstimate.toLocaleString()}`}
              {includeTransport && ` + transport ~$${transportEstimate.toLocaleString()}`}
            </div>
            {userBudget && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${accentColor}20`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 11, color: textDim }}>Your budget: <strong style={{ color: goldText }}>${userBudget.toLocaleString()}</strong></div>
                {fitsBudget ? (
                  <span style={{ fontSize: 10, fontWeight: 800, color: "#6dbd6d", background: "rgba(109,189,109,0.15)", border: "1px solid rgba(109,189,109,0.4)", padding: "3px 9px", borderRadius: 12, letterSpacing: 0.3 }}>✓ Within budget · ${(userBudget - totalForTravelers).toLocaleString()} to spare</span>
                ) : (
                  <span style={{ fontSize: 10, fontWeight: 800, color: "#F5B450", background: "rgba(245,180,80,0.15)", border: "1px solid rgba(245,180,80,0.4)", padding: "3px 9px", borderRadius: 12, letterSpacing: 0.3 }}>+${overBy.toLocaleString()} over budget</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Budget headroom upsell — when the trip lands under budget, surface
            activity categories that fit the leftover. Tag-driven here (this
            modal doesn't receive identity vibes); component self-hides if the
            headroom is too small to matter. */}
        {userBudget && fitsBudget && (
          <BudgetHeadroomUpsell
            budget={userBudget}
            tripCost={totalForTravelers}
            travelers={travelers}
            destTags={dest?.tags || []}
            destName={dest?.name || ""}
            activitiesIncluded={includeActivities}
            accent={accentColor}
          />
        )}

        {!departureAirport && (
          <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.25)", borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 11, color: goldText, lineHeight: 1.5 }}>
            <strong>ⓘ</strong> Set your departure airport on the homepage for accurate pricing.
          </div>
        )}

        {/* Destination-specific safety badge — only renders when the
            destination has a safetyNote field. Shows the brief context
            + a link to travel.state.gov for current advisory. */}
        {dest?.safetyNote && (
          <SafetyBadge safetyNote={dest.safetyNote} variant="inline" country={dest.country} />
        )}

        {/* CTAs */}
        <button onClick={() => canBook && onConfirm({ departDate, returnDate, travelers, days, estTotal: totalForTravelers, userBudget, includeHotel, includeActivities, includeTransport })}
          disabled={!canBook}
          style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: canBook ? `linear-gradient(135deg,${accentColor},${accentColor}dd)` : "rgba(27,58,92,0.096)", color: canBook ? "#FAFAF5" : textDim, fontWeight: 900, fontSize: 15, cursor: canBook ? "pointer" : "not-allowed", fontFamily: "inherit", marginBottom: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {canBook ? (<>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 8 L5 18 C 5 19 6 20 7 20 L 17 20 C 18 20 19 19 19 18 L 19 8" stroke="#FAFAF5" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
              <path d="M3 8 L21 8" stroke="#FAFAF5" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M9 12 L15 12" stroke="#FAFAF5" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M9 4 L 9 8 M15 4 L 15 8" stroke="#FAFAF5" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Book Now →
          </>) : "Select dates to continue"}
        </button>

        {/* Advisory disclaimer — appears below Book Now. Brief reminder
            to check current government travel advisories. Links to
            travel.state.gov which has current Level 1-4 advisories per
            country. Liability cover + good user practice. */}
        <div style={{ fontSize: 10.5, color: textDim, lineHeight: 1.5, marginBottom: 10, padding: "0 2px", fontStyle: "italic" }}>
          Travel involves inherent risks. Check current advisories at{" "}
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer"
             style={{ color: "#4A8CC4", textDecoration: "underline", textDecorationStyle: "dotted" }}>
            travel.state.gov
          </a>{" "}before booking. JGA destinations are curated, but conditions can change.
        </div>

        {/* Preview itinerary toggle */}
        {onPreview && (
          <>
            <button onClick={handlePreview}
              style={{ width: "100%", padding: "8px", borderRadius: 10, border: "none", background: "transparent", color: accentColor, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 4 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <ellipse cx="12" cy="12" rx="9" ry="6" stroke={accentColor} strokeWidth="1.6"/>
                <circle cx="12" cy="12" r="2.5" fill={accentColor}/>
              </svg>
              {showPreview ? "Hide preview" : "Preview a sample itinerary first"}
            </button>

            {showPreview && (
              <div style={{ background: `${accentColor}06`, border: `1px solid ${accentColor}25`, borderRadius: 12, padding: "14px 16px", marginBottom: 10, animation: "fadeIn 0.3s ease" }}>
                {previewLoading && (
                  <RotatingItineraryLoader accentColor={accentColor} />
                )}
                {!previewLoading && !previewItinerary && (
                  <div style={{ fontSize: 12, color: textMuted, textAlign: "center", padding: "12px 0" }}>
                    Couldn't generate a preview right now. You can still book — we'll create your full itinerary post-confirmation.
                  </div>
                )}
                {!previewLoading && previewItinerary && (
                  <div>
                    {/* Section eyebrow on left, Save button on right.
                       Save button only renders when saveItinerary handler is
                       wired in (defensive — modal is reusable in contexts where
                       save may not be available, e.g., unregistered users). */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 10, letterSpacing: 1.5, color: accentColor, textTransform: "uppercase", fontWeight: 800 }}>Sample · {previewItinerary.days?.length || 0}-day preview</div>
                      {saveItinerary && (
                        <button
                          onClick={async () => {
                            if (itinerarySaveState === "saving" || itinerarySaveState === "saved") return;
                            setItinerarySaveState("saving");
                            try {
                              const result = await saveItinerary({
                                destName: dest.name,
                                destEmoji: dest.emoji,
                                destCountry: dest.country,
                                days: previewItinerary.days?.length || days,
                                data: previewItinerary,
                              });
                              if (result?.ok || result?.alreadySaved) {
                                setItinerarySaveState("saved");
                                // Surface the destination's funFact as a small
                                // moment of delight. Falls back to MISSING_POSTCARD_FACTS
                                // for destinations not yet in POSTCARDS_CATALOG.
                                if (getFunFactForDest(dest.name)) setShowSavedFact(true);
                                // Revert to idle after 3s so user can still tap if needed
                                setTimeout(() => setItinerarySaveState(null), 3000);
                              } else {
                                setItinerarySaveState("error");
                                setTimeout(() => setItinerarySaveState(null), 3000);
                              }
                            } catch (err) {
                              console.error("[JGA Preview Save] Failed:", err);
                              setItinerarySaveState("error");
                              setTimeout(() => setItinerarySaveState(null), 3000);
                            }
                          }}
                          disabled={itinerarySaveState === "saving" || itinerarySaveState === "saved"}
                          aria-label="Save this itinerary"
                          style={{
                            padding: "5px 11px", borderRadius: 8,
                            border: `1px solid ${itinerarySaveState === "saved" ? "rgba(109,189,109,0.5)" : itinerarySaveState === "error" ? "rgba(255,107,107,0.5)" : `${accentColor}55`}`,
                            background: itinerarySaveState === "saved" ? "rgba(109,189,109,0.15)" : itinerarySaveState === "error" ? "rgba(255,107,107,0.12)" : `${accentColor}15`,
                            color: itinerarySaveState === "saved" ? "#6dbd6d" : itinerarySaveState === "error" ? "#FF6B6B" : accentColor,
                            fontSize: 11, fontWeight: 800, cursor: (itinerarySaveState === "saving" || itinerarySaveState === "saved") ? "default" : "pointer",
                            fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5,
                            transition: "all 0.2s", whiteSpace: "nowrap", flexShrink: 0,
                          }}>
                          {itinerarySaveState === "saving" ? (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="14 8" strokeLinecap="round">
                                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite"/>
                                </circle>
                              </svg>
                              Saving…
                            </>
                          ) : itinerarySaveState === "saved" ? (
                            <>✓ Saved</>
                          ) : itinerarySaveState === "error" ? (
                            <>Try again</>
                          ) : (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M6 2 L6 22 L12 18 L18 22 L18 2 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
                              </svg>
                              Save itinerary
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {previewItinerary.days?.slice(0, 3).map(d => (
                      <div key={d.day} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)"}` }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: textPrimary, marginBottom: 4 }}>Day {d.day} · {d.title}</div>
                        <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.5 }}>
                          <strong style={{ color: textDim }}>Morning:</strong> {d.morning}<br/>
                          <strong style={{ color: textDim }}>Afternoon:</strong> {d.afternoon}<br/>
                          <strong style={{ color: textDim }}>Evening:</strong> {d.evening}
                        </div>
                        {d.tip && <div style={{ fontSize: 11, color: accentColor, marginTop: 4, fontStyle: "italic" }}>💡 {d.tip}</div>}
                      </div>
                    ))}
                    {previewItinerary.days?.length > 3 && (
                      <div style={{ fontSize: 11, color: textDim, textAlign: "center", marginTop: 6, fontStyle: "italic" }}>
                        + {previewItinerary.days.length - 3} more days · full itinerary delivered after booking
                      </div>
                    )}
                    {previewItinerary.packingTip && (
                      <div style={{ fontSize: 11, color: textMuted, marginTop: 10, padding: "8px 10px", borderRadius: 8, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)" }}>
                        <strong style={{ color: textDim }}>🧳 Packing:</strong> {previewItinerary.packingTip}
                      </div>
                    )}
                    {/* Post-save fact reveal — a small moment of delight tied
                         to the deliberate save action. Reads funFact from the
                         destination's POSTCARDS_CATALOG entry (or fallback
                         MISSING_POSTCARD_FACTS for destinations not yet in
                         the catalog). Only renders when (a) showSavedFact is
                         true, (b) a fact exists. */}
                    {showSavedFact && (() => {
                      const fact = getFunFactForDest(dest.name);
                      if (!fact) return null;
                      return (
                        <div style={{
                          marginTop: 12,
                          padding: "12px 14px",
                          borderRadius: 12,
                          background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)`,
                          border: `1px solid ${accentColor}30`,
                          animation: "fadeIn 0.4s ease",
                          position: "relative",
                        }}>
                          <button
                            onClick={() => setShowSavedFact(false)}
                            aria-label="Dismiss"
                            style={{
                              position: "absolute", top: 6, right: 8,
                              background: "transparent", border: "none",
                              fontSize: 16, color: textDim, cursor: "pointer",
                              padding: 4, lineHeight: 1, fontFamily: "inherit",
                            }}>×</button>
                          <div style={{
                            fontSize: 10, fontWeight: 800, letterSpacing: 2,
                            textTransform: "uppercase", color: accentColor,
                            marginBottom: 6, paddingRight: 18,
                          }}>
                            ✨ Saved · Did you know?
                          </div>
                          <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.5, paddingRight: 18 }}>
                            {fact}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <button onClick={onClose}
          style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Trip Alert Modal ────────────────────────────────────────────
// Multi-type alert setup: budget threshold + % drop + best-season notification
function TripAlertModal({ dest, existingAlert, onClose, onSave, onDelete }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [budget, setBudget] = useState(existingAlert?.budget || dest?.minBudget || 1000);
  const [types, setTypes] = useState(existingAlert?.types || ["budget"]);
  const [percentOff, setPercentOff] = useState(existingAlert?.percentOff || 15);
  const [months, setMonths] = useState(existingAlert?.months || []);

  if (!dest) return null;

  // Calculate best months for this destination based on region
  const region = dest.subRegion || dest.region || "";
  const seasonalLow = (() => {
    if (region.includes("Europe")) return ["Jan","Feb","Nov"];
    if (region.includes("Asia")) return ["May","Jun","Sep"];
    if (region.includes("Caribbean") || region.includes("South America")) return ["Apr","May","Sep","Oct","Nov"];
    if (region.includes("Africa")) return ["Apr","May","Oct","Nov"];
    if (region.includes("North America")) return ["Jan","Feb","Mar","Nov"];
    if (region.includes("Oceania")) return ["May","Jun","Aug","Sep"];
    if (region.includes("Gulf") || region.includes("Levant")) return ["Apr","May","Oct","Nov"];
    return ["Apr","May","Sep","Oct"];
  })();

  function toggleType(t) {
    setTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }
  function toggleMonth(m) {
    setMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  }

  function save() {
    onSave({
      budget: parseInt(String(budget).replace(/[^0-9]/g,""), 10) || dest.minBudget,
      types,
      percentOff,
      months: months.length ? months : seasonalLow,
      createdAt: existingAlert?.createdAt || new Date().toISOString(),
    });
  }

  const ALL_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div role="dialog" aria-modal="true" aria-label={`Set alert for ${dest.name}`}
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
      <div style={{ ...lightCard(darkMode), width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", border: `1px solid ${coral}30` }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <span style={{ fontSize: 36 }}>{dest.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: coral, textTransform: "uppercase", fontWeight: 700 }}>🔔 Trip Alert</div>
            <h2 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 900, color: textPrimary }}>{dest.name}</h2>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", color: textMuted, fontSize: 22, cursor: "pointer", fontFamily: "inherit", padding: 4 }}>✕</button>
        </div>

        <p style={{ fontSize: 13, color: textMuted, lineHeight: 1.6, marginBottom: 20 }}>
          Pick how you want to be notified when this trip becomes a reality. We'll email you when conditions match.
        </p>

        {/* Alert types */}
        <div style={{ fontSize: 11, letterSpacing: 2, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>Alert me when…</div>

        {/* Budget threshold */}
        <button onClick={() => toggleType("budget")}
          style={{ width: "100%", padding: "14px", borderRadius: 12, border: `1.5px solid ${types.includes("budget") ? `${coral}80` : borderColor}`, background: types.includes("budget") ? `${coral}10` : "transparent", marginBottom: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${types.includes("budget") ? coral : borderColor}`, background: types.includes("budget") ? coral : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
            {types.includes("budget") && <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 2 }}>💰 Drops to my budget</div>
            <div style={{ fontSize: 12, color: textMuted }}>Notify when this trip is at or below my target</div>
          </div>
        </button>

        {types.includes("budget") && (
          <div style={{ marginBottom: 10, paddingLeft: 34 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: textMuted }}>My budget: $</span>
              <input type="number" value={budget} onChange={e => setBudget(e.target.value)}
                style={{ width: 100, padding: "6px 10px", borderRadius: 8, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.060)" : "#fff", color: textPrimary, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
            </div>
            <div style={{ fontSize: 11, color: textDim, marginTop: 6 }}>Trip currently estimates ${dest.minBudget?.toLocaleString()}–${dest.maxBudget?.toLocaleString()}</div>
          </div>
        )}

        {/* Percent drop */}
        <button onClick={() => toggleType("percent")}
          style={{ width: "100%", padding: "14px", borderRadius: 12, border: `1.5px solid ${types.includes("percent") ? `${coral}80` : borderColor}`, background: types.includes("percent") ? `${coral}10` : "transparent", marginBottom: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${types.includes("percent") ? coral : borderColor}`, background: types.includes("percent") ? coral : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
            {types.includes("percent") && <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 2 }}>📉 Price drops by %</div>
            <div style={{ fontSize: 12, color: textMuted }}>Notify when there's a meaningful price drop</div>
          </div>
        </button>

        {types.includes("percent") && (
          <div style={{ marginBottom: 10, paddingLeft: 34 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {[10, 15, 20, 25, 30].map(p => (
                <button key={p} onClick={() => setPercentOff(p)}
                  style={{ padding: "6px 12px", borderRadius: 16, border: `1px solid ${percentOff === p ? `${coral}80` : borderColor}`, background: percentOff === p ? `${coral}20` : "transparent", color: percentOff === p ? coral : textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {p}% off
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Best season */}
        <button onClick={() => toggleType("season")}
          style={{ width: "100%", padding: "14px", borderRadius: 12, border: `1.5px solid ${types.includes("season") ? `${coral}80` : borderColor}`, background: types.includes("season") ? `${coral}10` : "transparent", marginBottom: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${types.includes("season") ? coral : borderColor}`, background: types.includes("season") ? coral : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
            {types.includes("season") && <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary, marginBottom: 2 }}>📅 Best season approaches</div>
            <div style={{ fontSize: 12, color: textMuted }}>Notify before specific months — typically when prices are lowest</div>
          </div>
        </button>

        {types.includes("season") && (
          <div style={{ marginBottom: 10, paddingLeft: 34 }}>
            <div style={{ fontSize: 11, color: textDim, marginBottom: 6 }}>Recommended months for {dest.name}: {seasonalLow.join(", ")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {ALL_MONTHS.map(m => {
                const active = months.includes(m);
                const recommended = seasonalLow.includes(m);
                return (
                  <button key={m} onClick={() => toggleMonth(m)}
                    style={{ padding: "6px 10px", borderRadius: 12, border: `1px solid ${active ? `${coral}80` : recommended ? `${coral}30` : borderColor}`, background: active ? `${coral}20` : "transparent", color: active ? coral : recommended ? coral : textMuted, fontSize: 11, fontWeight: active ? 700 : 600, cursor: "pointer", fontFamily: "inherit" }}>
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          {existingAlert && (
            <button onClick={onDelete}
              style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${borderColor}`, background: "transparent", color: textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              🗑️ Remove
            </button>
          )}
          <button onClick={save} disabled={types.length === 0}
            style={{ flex: 1, padding: "12px 16px", borderRadius: 12, border: "none", background: types.length === 0 ? `${coral}40` : `linear-gradient(135deg,${coral},#FF6B6B)`, color: "#fff", fontSize: 14, fontWeight: 800, cursor: types.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {existingAlert ? "Update Alert" : "Set Alert"}
          </button>
        </div>

        <p style={{ fontSize: 11, color: textDim, marginTop: 14, textAlign: "center", lineHeight: 1.6 }}>
          We'll email you when conditions match. You can change or remove this alert anytime.
        </p>
      </div>
    </div>
  );
}

// Starter boards offered to users with zero boards. Module-scope factory so
// both the App's DEFAULT_BOARDS state initializer and the BoardPickerModal's
// empty-state "Start with our boards" action can use the same source of truth.
// Returns fresh objects (new createdAt timestamps) on each call.
function makeStarterBoards() {
  const now = Date.now();
  return [
    { id: "board-2026",   name: "2026 Plans",     iconId: "sparkle",  color: "#A8853B", items: [], createdAt: now },
    { id: "board-bucket", name: "Bucket List",    iconId: "mountain", color: "#A85A3C", items: [], createdAt: now + 1 },
    { id: "board-dream",  name: "Dream Vacation", iconId: "palm",     color: "#4A8CC4", items: [], createdAt: now + 2 },
  ];
}

// ── Board Picker Modal — opens when user taps ☆ on any destination ──
// Multi-select checkboxes for existing boards + inline "create new board" flow.
// Pinterest-style: a destination can live in multiple boards.
function BoardPickerModal({ destName, boards, setBoards, boardsApi = null, setPostcardsData = null, hidePostcardSection = false, onClose }) {
  const { darkMode, textPrimary, textMuted, textDim, borderColor } = useTheme();
  const [creatingNew, setCreatingNew] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [newBoardIconId, setNewBoardIconId] = useState("sparkle");
  const [newBoardColor, setNewBoardColor] = useState("#1B3A5C");

  // Editorial palette — same 8 as CreateBoardModal.
  const COLOR_CHOICES = ["#1B3A5C","#4A8CC4","#C25E47","#A8853B","#6B4423","#7A9479","#5A8A8C","#6B4470"];

  // ── Postcard collect state ──
  // Derive postcard ID from destination name; only show the collect section
  // if there's a matching entry in POSTCARDS_CATALOG (we've illustrated it).
  // Once collected, lock the state — can't collect twice (per product spec).
  const postcardId = destNameToPostcardId(destName);
  const catalogEntry = postcardId ? POSTCARDS_CATALOG.find(p => p.id === postcardId) : null;
  const [postcardState, setPostcardStateLocal] = useState(() => {
    if (!postcardId) return null;
    const existing = getPostcards()[postcardId];
    return existing?.state || null; // "dreaming" | "visited" | null (not collected)
  });

  function collectPostcard(state) {
    if (!postcardId || postcardState) return; // no-op if already collected
    // setPostcardState writes to localStorage and returns the merged map.
    const merged = setPostcardState(postcardId, state);
    setPostcardStateLocal(state);
    // Also update global React state so the backend-sync effect fires.
    // Without this, collected postcards stay localStorage-only until the
    // user refreshes (which would trigger the load-on-auth effect anyway,
    // but the round-trip would lose this collect if the user closes the
    // tab before refreshing).
    if (typeof setPostcardsData === "function") {
      setPostcardsData(merged);
    }
  }

  // Track which boards currently contain this destination (initial state, mutable)
  const [selected, setSelected] = useState(() => {
    const set = new Set();
    boards.forEach(b => { if ((b.items || []).includes(destName)) set.add(b.id); });
    return set;
  });

  function toggleBoard(boardId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(boardId)) next.delete(boardId); else next.add(boardId);
      return next;
    });
  }

  function handleSave() {
    if (boardsApi) {
      boardsApi.applySelection(destName, Array.from(selected));
    } else {
      setBoards(prev => prev.map(b => {
        const shouldHave = selected.has(b.id);
        const has = (b.items || []).includes(destName);
        if (shouldHave && !has) return { ...b, items: [...(b.items || []), destName] };
        if (!shouldHave && has) return { ...b, items: (b.items || []).filter(n => n !== destName) };
        return b;
      }));
    }
    onClose();
  }

  async function handleCreateBoard() {
    const name = newBoardName.trim();
    if (!name) return;
    if (boardsApi) {
      // Use boardsApi — it returns the temp/server id we can pre-select
      const result = await boardsApi.createBoard({ name, iconId: newBoardIconId, color: newBoardColor });
      const newId = result?.id;
      if (newId != null) {
        // Add this destination to the new board
        boardsApi.addItem(newId, destName);
        setSelected(prev => { const next = new Set(prev); next.add(newId); return next; });
      }
    } else {
      const id = `board-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      const newBoard = { id, name, iconId: newBoardIconId, color: newBoardColor, items: [destName], createdAt: Date.now() };
      setBoards(prev => [...prev, newBoard]);
      setSelected(prev => { const next = new Set(prev); next.add(id); return next; });
    }
    setCreatingNew(false);
    setNewBoardName("");
    setNewBoardIconId("sparkle");
    setNewBoardColor("#7A8467");
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Save to board"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 500,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
        animation: "fadeIn 0.2s ease",
        padding: 0,
      }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 520, maxHeight: "85vh",
        background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff",
        borderRadius: "20px 20px 0 0",
        border: `1px solid ${darkMode ? "rgba(74,140,196,0.2)" : "rgba(0,0,0,0.08)"}`,
        borderBottom: "none",
        padding: "20px 22px 24px",
        overflowY: "auto",
        animation: "fadeIn 0.3s cubic-bezier(0.34,1.2,0.64,1)",
      }}>
        {/* Drag handle */}
        <div style={{ width: 40, height: 4, background: darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.15)", borderRadius: 4, margin: "0 auto 16px" }} />

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Save to Board</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: textPrimary, lineHeight: 1.2 }}>{destName}</h2>
          <div style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>Pick one or more boards — you can always add to others later.</div>
        </div>

        {/* Postcard collect — appears above boards when destination has an illustration.
           Locked once collected; user can't change state from "dreaming" → "visited"
           here (that happens elsewhere when they log a trip). Suppressed entirely
           when hidePostcardSection is set (e.g. star-button intent from MA/TT
           where the user is saving an alert, not collecting a postcard). */}
        {catalogEntry && !hidePostcardSection && (
          <div style={{
            background: darkMode ? "rgba(74,140,196,0.05)" : "linear-gradient(135deg, #f8ecd9 0%, #ecd6c2 100%)",
            border: `1px solid ${darkMode ? "rgba(74,140,196,0.18)" : "rgba(27,58,92,0.28)"}`,
            borderRadius: 12, padding: 14, marginBottom: 16,
          }}>
            <div style={{ fontSize: 10, letterSpacing: 2.4, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 800, marginBottom: 10 }}>
              {postcardState ? "Postcard collected" : "Collect postcard"}
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: postcardState ? 0 : 12, alignItems: "center" }}>
              {/* Mini postcard preview — 62×44, leans on the catalog vibe color */}
              <div aria-hidden="true" style={{
                width: 62, height: 44, borderRadius: 6, flexShrink: 0,
                background: "linear-gradient(180deg, #d4b4a4 0%, #c89aa4 45%, #8d9aae 100%)",
                position: "relative", overflow: "hidden",
                boxShadow: postcardState ? "0 0 0 2px rgba(27,58,92,0.35)" : "none",
                transition: "box-shadow 0.2s",
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {postcardState ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: darkMode ? "#f0d8c8" : "#3a2818", marginBottom: 2 }}>
                      Added to your collection
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? "#4A8CC4" : "#7a5a58", lineHeight: 1.4 }}>
                      Marked as {postcardState} — view in Profile
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: darkMode ? "#f0d8c8" : "#3a2818", marginBottom: 2 }}>
                      Add {destName} to your collection
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? "#4A8CC4" : "#7a5a58", lineHeight: 1.4 }}>
                      A hand-drawn postcard for your Profile
                    </div>
                  </>
                )}
              </div>
            </div>
            {!postcardState && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <button onClick={() => collectPostcard("dreaming")}
                  style={{
                    padding: 11, borderRadius: 10, border: "none",
                    background: "#1B3A5C", color: "#FAFAF5",
                    fontSize: 13, fontWeight: 600, fontFamily: "inherit", letterSpacing: 0.3,
                    cursor: "pointer", transition: "transform 0.12s, background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#7a2f25"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#1B3A5C"; }}
                  onMouseDown={e => { e.currentTarget.style.transform = "scale(0.97)"; }}
                  onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                  Dreaming
                </button>
                <button onClick={() => collectPostcard("visited")}
                  style={{
                    padding: 11, borderRadius: 10, border: "none",
                    background: "#4A8CC4", color: "#5c1f1a",
                    fontSize: 13, fontWeight: 600, fontFamily: "inherit", letterSpacing: 0.3,
                    cursor: "pointer", transition: "transform 0.12s, background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#b890a0"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#4A8CC4"; }}
                  onMouseDown={e => { e.currentTarget.style.transform = "scale(0.97)"; }}
                  onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                  Visited
                </button>
              </div>
            )}
          </div>
        )}

        {/* Heading + one-line explainer. Shown for every save, not just
           postcard-collected destinations — answers 'what does this do?' */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Save to a board</div>
          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.45, fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif" }}>
            Boards group destinations you're considering — a place to come back to when you're ready to plan.
          </div>
        </div>

        {boards.length === 0 ? (
          /* Empty state — no boards yet. Offer two paths: seed defaults or
             create the first board manually. Better than dumping the user
             into a grid with only a 'New board' tile. */
          <div style={{
            padding: "18px 16px 16px", marginBottom: 12, borderRadius: 12,
            border: `1px dashed ${darkMode ? "rgba(74,140,196,0.32)" : "rgba(74,140,196,0.45)"}`,
            background: darkMode ? "rgba(74,140,196,0.04)" : "rgba(74,140,196,0.05)",
          }}>
            <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.5, marginBottom: 12, fontFamily: "'Source Serif 4', Georgia, serif" }}>
              Looks like you don't have any boards yet. Want us to start you off with three, or build your own?
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setBoards(makeStarterBoards())}
                style={{
                  flex: 1, minWidth: 0, padding: "11px 14px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #4A8CC4, #4A8CC4)", color: "#FAFAF5",
                  fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                }}>
                Start with our 3 boards
              </button>
              <button onClick={() => setCreatingNew(true)}
                style={{
                  flex: 1, minWidth: 0, padding: "11px 14px", borderRadius: 10,
                  border: `1px solid ${darkMode ? "rgba(74,140,196,0.4)" : "rgba(74,140,196,0.45)"}`,
                  background: "transparent", color: "#4A8CC4",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                Create my own
              </button>
            </div>
          </div>
        ) : (
        /* Existing boards — 2-column grid so user doesn't have to scroll
           through a long list to find "create new" at the bottom. */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          {boards.map(board => {
            const isSelected = selected.has(board.id);
            const itemCount = (board.items || []).length;
            return (
              <button key={board.id} onClick={() => toggleBoard(board.id)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6,
                  padding: "10px 12px", borderRadius: 12,
                  border: `1.5px solid ${isSelected ? `${board.color}80` : (darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)")}`,
                  background: isSelected ? `${board.color}15` : (darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)"),
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  transition: "all 0.18s",
                  minWidth: 0,
                  position: "relative",
                }}>
                {/* Top row: icon + checkbox */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 9,
                    background: `${board.color}20`, border: `1px solid ${board.color}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }} aria-hidden="true">
                    {getBoardIcon(resolveBoardIconId(board)).render(board.color, 20)}
                  </div>
                  <div style={{
                    width: 20, height: 20, borderRadius: 6,
                    border: `2px solid ${isSelected ? board.color : (darkMode ? "rgba(27,58,92,0.23)" : "rgba(0,0,0,0.25)")}`,
                    background: isSelected ? board.color : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {isSelected && <svg width="11" height="9" viewBox="0 0 12 10" fill="none"><path d="M1 5l3.5 3.5L11 1" stroke="#FAFAF5" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                </div>
                {/* Name + count */}
                <div style={{ width: "100%", minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? board.color : textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{board.name}</div>
                  <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>
                    {itemCount === 0 ? "Empty" : `${itemCount} dest${itemCount !== 1 ? "s" : ""}`}
                  </div>
                </div>
              </button>
            );
          })}
          {/* Inline "Create new" tile that sits in the grid as a peer */}
          {!creatingNew && (
            <button onClick={() => setCreatingNew(true)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "10px 12px", borderRadius: 12, minHeight: 86,
                border: `1.5px dashed ${darkMode ? "rgba(74,140,196,0.4)" : "rgba(74,140,196,0.45)"}`,
                background: "transparent",
                color: "#4A8CC4", fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,140,196,0.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
              <span style={{ fontSize: 11, letterSpacing: 0.5 }}>New board</span>
            </button>
          )}
        </div>
        )}

        {/* Create new board section — the inline "New board" tile in the
           grid above flips creatingNew; the expanded form renders below. */}
        {creatingNew && (
          <div style={{
            padding: "14px 14px 16px",
            borderRadius: 12,
            border: `1.5px solid ${newBoardColor}55`,
            background: `${newBoardColor}08`,
            marginBottom: 18,
          }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 8 }}>New Board</div>
            <input
              type="text"
              value={newBoardName}
              onChange={e => setNewBoardName(e.target.value.slice(0, 40))}
              onKeyDown={e => { if (e.key === "Enter" && newBoardName.trim()) handleCreateBoard(); }}
              placeholder="Board name (e.g. Spring Break)"
              autoFocus
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`,
                background: darkMode ? "rgba(27,58,92,0.060)" : "#ffffff",
                color: textPrimary, fontSize: 14, fontFamily: "inherit",
                outline: "none", boxSizing: "border-box", marginBottom: 10,
              }}
            />
            {/* Icon choices */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 5, marginBottom: 8 }}>
              {BOARD_ICONS.map(opt => {
                const active = newBoardIconId === opt.id;
                return (
                  <button key={opt.id} onClick={() => setNewBoardIconId(opt.id)}
                    aria-label={`Icon ${opt.label}`}
                    aria-pressed={active}
                    style={{
                      height: 36, borderRadius: 8,
                      border: `1.5px solid ${active ? newBoardColor : "transparent"}`,
                      background: active ? `${newBoardColor}20` : (darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)"),
                      cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                    {opt.render(active ? newBoardColor : (darkMode ? "#4A6B8A" : "#5a7a8a"), 20)}
                  </button>
                );
              })}
            </div>
            {/* Color choices */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {COLOR_CHOICES.map(c => (
                <button key={c} onClick={() => setNewBoardColor(c)}
                  aria-label="Choose color"
                  aria-pressed={newBoardColor === c}
                  style={{
                    width: 28, height: 28, borderRadius: "50%",
                    border: `2px solid ${newBoardColor === c ? "#fff" : "transparent"}`,
                    boxShadow: newBoardColor === c ? `0 0 0 2px ${c}` : "none",
                    background: c, cursor: "pointer",
                  }} />
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setCreatingNew(false); setNewBoardName(""); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: 8,
                  border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`,
                  background: "transparent", color: textMuted,
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>Cancel</button>
              <button onClick={handleCreateBoard} disabled={!newBoardName.trim()}
                style={{
                  flex: 1, padding: "10px", borderRadius: 8, border: "none",
                  background: newBoardName.trim() ? newBoardColor : (darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"),
                  color: newBoardName.trim() ? "#FAFAF5" : textDim,
                  fontSize: 13, fontWeight: 800, cursor: newBoardName.trim() ? "pointer" : "not-allowed", fontFamily: "inherit",
                }}>Create</button>
            </div>
          </div>
        )}

        {/* Footer actions. Left = cancel (no-op exit). Right = save, disabled
           visually + functionally when no board is selected (was 'Don't save'
           which read as a second cancel option). User can still create a new
           board inline above to populate the picker. */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose}
            style={{
              flex: 1, padding: "13px", borderRadius: 12,
              border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
              background: "transparent", color: textMuted,
              fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>Cancel</button>
          <button onClick={selected.size === 0 ? undefined : handleSave}
            disabled={selected.size === 0}
            aria-disabled={selected.size === 0}
            style={{
              flex: 2, padding: "13px", borderRadius: 12, border: "none",
              background: selected.size === 0
                ? (darkMode ? "rgba(74,140,196,0.18)" : "rgba(74,140,196,0.22)")
                : "linear-gradient(135deg, #4A8CC4, #4A8CC4)",
              color: selected.size === 0 ? textDim : "#FAFAF5",
              fontSize: 14, fontWeight: 900,
              cursor: selected.size === 0 ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              opacity: selected.size === 0 ? 0.7 : 1,
              transition: "background 0.18s, color 0.18s, opacity 0.18s",
            }}>
            {selected.size === 0 ? "Save" : `Save to ${selected.size} board${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Swipe Info Modal — shown when user up-swipes or taps a card ──
// Bottom-sheet style. Full destination details for "tell me more" intent.
function SwipeInfoModal({ destination, userBudget, userAirport, flightPrice, fahrenheit, travelers = 1, onClose, onSave, onSkip }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();
  if (!destination) return null;
  const d = destination;
  // Use realistic (mid-range) as the fitting number — honest budget check.
  const est = estimateTripCost(d, flightPrice, travelers);
  const totalEst = est?.realistic || null;
  const fits = est && userBudget ? est.realistic <= userBudget : null;

  return (
    <div role="dialog" aria-modal="true" aria-label={`About ${d.name}`}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 520, maxHeight: "85vh",
        background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff",
        borderRadius: "20px 20px 0 0",
        border: `1px solid ${darkMode ? "rgba(74,140,196,0.2)" : "rgba(0,0,0,0.08)"}`,
        borderBottom: "none",
        padding: "20px 22px 24px",
        overflowY: "auto",
        animation: "fadeIn 0.3s cubic-bezier(0.34,1.2,0.64,1)",
      }}>
        <div style={{ width: 40, height: 4, background: darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.15)", borderRadius: 4, margin: "0 auto 16px" }} />
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>About this destination</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 28 }}>{d.emoji}</span>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: textPrimary, lineHeight: 1.15 }}>{d.name}</h2>
              <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{d.country}{d.state ? ` · ${d.state}` : ""}</div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 14, color: textPrimary, fontStyle: "italic", marginBottom: 14, lineHeight: 1.5 }}>{d.vibe}</div>
        {d.tags && d.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {d.tags.map(t => (
              <span key={t} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 12, background: "rgba(27,58,92,0.1)", border: "1px solid rgba(27,58,92,0.3)", color: "#0F2747", fontWeight: 700, letterSpacing: 0.3 }}>{t}</span>
            ))}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.096)" }}>
            <div style={{ fontSize: 9, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Budget</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>${d.minBudget?.toLocaleString()} – ${d.maxBudget?.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>per person, hotel + activities</div>
          </div>
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.096)" }}>
            <div style={{ fontSize: 9, letterSpacing: 1.5, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Climate</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>{fmtTemp(d.temp, fahrenheit) || "—"}</div>
            <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>typical for the region</div>
          </div>
        </div>
        {est && (
          <div style={{ padding: "12px 14px", borderRadius: 12, background: fits ? "rgba(109,189,109,0.08)" : "rgba(255,166,109,0.08)", border: `1px solid ${fits ? "rgba(109,189,109,0.3)" : "rgba(255,166,109,0.3)"}`, marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, color: fits ? "#6dbd6d" : "#ffa66d", textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>{fits ? "✓ Fits your budget" : "Above your saved budget"}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 1 }}>From</div>
                <div style={{ fontSize: 15, fontWeight: 900, color: textPrimary }}>${est.from.toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: textDim, marginBottom: 1 }}>Realistic</div>
                <div style={{ fontSize: 15, fontWeight: 900, color: textPrimary }}>${est.realistic.toLocaleString()}</div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: textDim, marginTop: 6 }}>from {userAirport} · flight + hotel + meals + activities</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Primary commitment buttons — skip vs save */}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { onSkip(); onClose(); }}
              style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1px solid rgba(255,107,107,0.3)", background: "rgba(255,107,107,0.06)", color: "#FF6B6B", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              ✕ Not for me
            </button>
            <button onClick={() => { onSave(); onClose(); }}
              style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #4A8CC4, #4A8CC4)", color: "#FAFAF5", fontSize: 14, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>
              ❤ Save to a board
            </button>
          </div>
          {/* Soft "set aside" option — doesn't commit to skip (which hides the
             destination for 30 days) or to a save. Just closes the modal so
             the user can revisit later. Important escape hatch — without it,
             tapping info felt like a trap of "now you must decide." */}
          <button onClick={onClose}
            style={{ width: "100%", padding: "10px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.144)", background: "transparent", color: textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Let me think about it
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MoodMatchShortlist — 3-card rolling shortlist with like / skip ──
// Three visible cards at all times. Each has a small ♥ (like → save to board) and
// ✕ (skip → 30-day cooldown) button. When a card leaves, it animates out and the
// next mood-matched destination from the pool slides into its slot.
//
// Save-detection trick: BoardPickerModal doesn't expose a "did save succeed"
// callback (only onClose). To distinguish a real save from a cancel, we snapshot
// `boards` when the user taps ♥ and watch for the destination to appear in any
// board's items list. If it does → real save, animate the card out. If picker
// closes without the dest appearing → user cancelled, leave the card visible.
//
// State per slot: { dest, exiting: null | "left" | "right" }
//   - exiting=null → card sits normally
//   - exiting="left" → user skipped, card slides off-left + fades over 320ms
//   - exiting="right" → user liked + saved, card slides off-right + fades
// After animation completes, slot's dest is replaced with the next pool item.
function MoodMatchShortlist({ pool, boards, flightPrices, skips, onSkip, onLike, onTap, textPrimary, textMuted, textDim, userMood, onUpdateMood, travelCompanion, travelers = 1 }) {
  const accent = "#4A8CC4";
  const EXIT_MS = 320;

  // Compute usable pool: full mood pool minus already-skipped (30-day cooldown)
  // and minus destinations already in any board (re-saving makes no sense).
  // This recomputes whenever skips/boards change, which is correct.
  const eligiblePool = useMemo(() => {
    const now = Date.now();
    const THIRTY_DAYS = 30 * 86400000;
    const skipped = new Set(
      Object.entries(skips || {}).filter(([_, ts]) => now - ts < THIRTY_DAYS).map(([name]) => name)
    );
    const inBoard = new Set();
    (boards || []).forEach(b => (b.items || []).forEach(name => inBoard.add(name)));
    return (pool || []).filter(d => !skipped.has(d.name) && !inBoard.has(d.name));
  }, [pool, skips, boards]);

  // Refs so handlers can read latest values without dep-array gymnastics.
  const eligiblePoolRef = useRef(eligiblePool);
  useEffect(() => { eligiblePoolRef.current = eligiblePool; }, [eligiblePool]);

  // Active row size — was 3 (grid), now 20 (horizontal scroll). The user
  // sees ~3 cards fully + a 4th peeking at the right edge, and can scroll
  // horizontally to browse the rest of the row. Skip/save still rolls in
  // a fresh card from the pool, but at the row's tail rather than into a
  // fixed slot. 20 keeps DOM light while feeling "deep" — users almost
  // never reach the end before exhausting the underlying pool.
  const ROW_SIZE = 20;

  // Slots state. Each slot: { dest, exiting }
  const [slots, setSlots] = useState(() => (pool || []).slice(0, ROW_SIZE).map(d => ({ dest: d, exiting: null })));
  const [pendingLike, setPendingLike] = useState(null);
  const slotsRef = useRef(slots);
  useEffect(() => { slotsRef.current = slots; }, [slots]);

  // ── Name-based "next destination" picker ──
  // Picks the first destination from eligiblePool that isn't currently shown
  // in any other slot. This makes the algorithm robust to pool size changes
  // (skipped cards drop out of eligiblePool naturally) and avoids index drift
  // when the pool shrinks mid-interaction.
  function pickNextDest(excludeSlotIdx) {
    const shownNames = new Set();
    slotsRef.current.forEach((s, i) => {
      if (i !== excludeSlotIdx && s.dest) shownNames.add(s.dest.name);
    });
    return eligiblePoolRef.current.find(d => !shownNames.has(d.name)) || null;
  }

  // ── Re-seed when mood actually changes ──
  // Tracks userMood.answeredAt as a stable signal for "the user took a fresh
  // mood quiz". This avoids false-positive reseeds when parent re-renders
  // with a new pool array reference but the same logical pool content.
  const lastAnsweredAtRef = useRef(userMood?.answeredAt);
  useEffect(() => {
    if (userMood?.answeredAt !== lastAnsweredAtRef.current) {
      lastAnsweredAtRef.current = userMood?.answeredAt;
      // New mood → fresh slots from the top of the new pool
      setSlots((pool || []).slice(0, ROW_SIZE).map(d => ({ dest: d, exiting: null })));
    }
  }, [userMood, pool]);

  // Watch boards for the pendingLike destination to appear (= save succeeded).
  // We compare the current boards-count for this dest against the snapshot
  // captured at click time. Only a NEW save (count went up) triggers the
  // refill — if the dest was already in a board when the user clicked like,
  // we don't false-positive into the refill flow.
  useEffect(() => {
    if (!pendingLike) return;
    const currentCount = (boards || []).filter(b => (b.items || []).some(n => n === pendingLike.destName)).length;
    if (currentCount <= (pendingLike.initialBoardsCount || 0)) return;
    const { slotIdx } = pendingLike;
    setPendingLike(null);
    setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, exiting: "right" } : s));
    setTimeout(() => {
      const next = pickNextDest(slotIdx);
      setSlots(prev => {
        const copy = [...prev];
        copy[slotIdx] = next ? { dest: next, exiting: null } : { dest: null, exiting: null };
        return copy;
      });
    }, EXIT_MS);
  }, [boards, pendingLike]);

  function handleSkip(slotIdx) {
    const slot = slots[slotIdx];
    if (!slot?.dest || slot.exiting) return;
    onSkip(slot.dest.name);
    setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, exiting: "left" } : s));
    setTimeout(() => {
      const next = pickNextDest(slotIdx);
      setSlots(prev => {
        const copy = [...prev];
        copy[slotIdx] = next ? { dest: next, exiting: null } : { dest: null, exiting: null };
        return copy;
      });
    }, EXIT_MS);
  }

  function handleLike(slotIdx) {
    const slot = slots[slotIdx];
    if (!slot?.dest || slot.exiting) return;
    const destName = slot.dest.name;
    // Snapshot how many boards already contain this dest. The watch-effect
    // only treats the action as a successful save if this count INCREASES,
    // preventing the false-positive refill when the user clicks like on a
    // dest they've already saved previously.
    const initialBoardsCount = (boards || []).filter(b => (b.items || []).some(n => n === destName)).length;
    setPendingLike({ slotIdx, destName, initialBoardsCount });
    onLike(destName);
  }

  const visibleCount = slots.filter(s => s.dest).length;

  // Empty state — pool exhausted
  if (visibleCount === 0) {
    return (
      <div style={{
        padding: "20px 18px", borderRadius: 12, border: `1px dashed ${accent}35`,
        background: `${accent}06`, textAlign: "center",
      }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>✦</div>
        <div style={{ fontSize: 13, color: textPrimary, fontWeight: 700, marginBottom: 4 }}>You've seen all your mood matches</div>
        <div style={{ fontSize: 11, color: textMuted, marginBottom: 12, fontStyle: "italic" }}>Update your mood for fresh picks based on how you feel now.</div>
        <button onClick={onUpdateMood}
          style={{
            padding: "8px 16px", borderRadius: 16,
            border: `1px solid ${accent}60`, background: `${accent}15`,
            color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>
          Update mood →
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes jga-mood-card-exit-left  { to { transform: translateX(-30%) rotate(-8deg); opacity: 0; } }
        @keyframes jga-mood-card-exit-right { to { transform: translateX(30%) rotate(8deg);   opacity: 0; } }
        @keyframes jga-mood-card-enter      { from { transform: translateY(20%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        /* Hide native scrollbar on the mood scroll row — the peeking 4th card
           is enough visual cue, and a scrollbar would clash with the rest of
           the dashboard. Still scrollable via touch swipe and mouse wheel. */
        .jga-mood-scroll::-webkit-scrollbar { display: none; }
        .jga-mood-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      {/* Horizontal scroll row — replaces the previous 3-column grid. 20 cards
         are kept "active" at once (was 3); the rest of the mood pool slides in
         from the right as the user skips/saves at the head. Fixed card widths
         so 3 fit fully + a 4th peeks from the right edge, signaling more. */}
      <div className="jga-mood-scroll" style={{
        display: "flex", gap: 8, overflowX: "auto", scrollSnapType: "x mandatory",
        paddingBottom: 4, // breathing room so card box-shadow isn't clipped
        WebkitOverflowScrolling: "touch", // native momentum scroll on iOS
      }}>
        {slots.map((slot, slotIdx) => {
          if (!slot.dest) {
            // Empty slot — pool ran out at this position. Render nothing so
            // the flex row naturally closes up rather than showing a confusing
            // "All caught up" placeholder next to active cards. If ALL slots
            // empty, the visibleCount === 0 branch above renders the proper
            // empty state with the Update mood CTA.
            return null;
          }
          const d = slot.dest;
          // Small card context — show only "From" (scrappy minimum) for space.
          // Helper returns null if flightPrice missing, matching old behavior.
          const est = estimateTripCost(d, flightPrices?.[d.name], paxFromCompanion(travelCompanion, 1));
          const total = est?.from || null;

          // Match-reason flavor copy (per-vibe variants, hash-stable per dest).
          // Keys here MUST cover every vibe tag used in DESTINATIONS that users
          // can match on (via mood quiz), or destinations matching only on a
          // missing key end up with no description rendered. Previously missing
          // nightlife/art/shopping caused some destinations (e.g. Miami, which
          // matches mostly on nightlife+food+beach) to render blank when the
          // hash landed on the missing key.
          const MOOD_VIBE_REASONS = {
            romance:   ["tender and soft-lit", "made for slow walks", "for the long stares"],
            food:      ["for the eaters", "every meal earns its keep", "the menu is the map"],
            wellness:  ["permission to slow down", "softer than your weekday", "where stillness is the plan"],
            beach:     ["where time loses meaning", "saltwater therapy", "barefoot most days"],
            culture:   ["a little brain food", "history under your feet", "thick with stories"],
            adventure: ["matches your itch to move", "you'll come back tired and full", "the kind of trip you'll retell"],
            luxury:    ["the deep exhale you need", "soft sheets, slower mornings", "for when you've earned it"],
            nature:    ["quiet, green, alive", "trees over towers", "for the deep breaths"],
            nightlife: ["where the night writes its own plans", "the city stays awake with you", "made for late tables and louder mornings"],
            art:       ["galleries, murals, music \u2014 the whole spectrum", "a feast for the eyes", "creativity lives in the streets"],
            shopping:  ["bring an extra bag", "every block has something worth holding", "the storefronts are the souvenirs"],
          };
          const moodOverlap = (d.tags || []).filter(t => (userMood?.vibes || []).includes(t));
          const hash = (d.name + String(slotIdx)).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
          let moodReason = null;
          if (moodOverlap.length > 0) {
            const chosenVibe = moodOverlap[hash % moodOverlap.length];
            const variants = MOOD_VIBE_REASONS[chosenVibe];
            if (variants?.length) moodReason = variants[hash % variants.length];
          }

          const isExiting = !!slot.exiting;
          const exitAnim = slot.exiting === "left" ? "jga-mood-card-exit-left"
            : slot.exiting === "right" ? "jga-mood-card-exit-right" : null;
          const animation = isExiting
            ? `${exitAnim} ${EXIT_MS}ms ease-in forwards`
            : `jga-mood-card-enter 280ms cubic-bezier(0.34,1.1,0.64,1) both`;

          return (
            <div key={`${d.name}-slot${slotIdx}`} style={{
              flex: "0 0 30%", minWidth: 0, scrollSnapAlign: "start",
              position: "relative", borderRadius: 12,
              border: `1px solid ${accent}30`, overflow: "hidden",
              background: `${accent}08`,
              animation, willChange: "transform, opacity",
              pointerEvents: isExiting ? "none" : "auto",
            }}>
              {/* Tap card body → QuickBook (existing behavior) */}
              <button onClick={() => onTap(d)} disabled={isExiting}
                style={{ display: "block", width: "100%", padding: 0, border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                <DestinationImage destName={d.name} country={d.country} emoji={d.emoji} height={80} rounded={0} accentColor={accent}/>
                <div style={{ padding: "8px 10px 10px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}>{d.name}</div>
                  <div style={{ fontSize: 10, color: textMuted, marginTop: 2 }}>{d.country}</div>
                  {total && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: accent, marginTop: 4 }}>From ${total.toLocaleString()}</div>
                  )}
                  {moodReason && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${accent}25`, fontSize: 10, color: accent, fontStyle: "italic", lineHeight: 1.35 }}>
                      {moodReason}
                    </div>
                  )}
                </div>
              </button>

              {/* Action buttons — overlay top corners. preventDefault stops them
                 from bubbling to the card-body button below. */}
              <button onClick={(e) => { e.stopPropagation(); handleSkip(slotIdx); }}
                disabled={isExiting} aria-label={`Skip ${d.name}`}
                style={{
                  position: "absolute", top: 6, left: 6,
                  width: 26, height: 26, borderRadius: 13,
                  background: "rgba(0,0,0,0.55)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)",
                  border: `1px solid rgba(27,58,92,0.18)`,
                  color: "#fff", fontSize: 12, fontWeight: 700,
                  cursor: isExiting ? "default" : "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!isExiting) { e.currentTarget.style.background = "rgba(255,107,107,0.85)"; e.currentTarget.style.borderColor = "rgba(255,107,107,1)"; } }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.55)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.18)"; }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6 L 18 18 M18 6 L 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleLike(slotIdx); }}
                disabled={isExiting} aria-label={`Save ${d.name} to a board`}
                style={{
                  position: "absolute", top: 6, right: 6,
                  width: 26, height: 26, borderRadius: 13,
                  background: "rgba(0,0,0,0.55)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)",
                  border: `1px solid rgba(27,58,92,0.18)`,
                  color: "#fff", fontSize: 13, lineHeight: 1,
                  cursor: isExiting ? "default" : "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!isExiting) { e.currentTarget.style.background = `${accent}d0`; e.currentTarget.style.borderColor = accent; } }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.55)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.18)"; }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 21 C 6 17, 3 13, 3 9 A 4 4 0 0 1 12 7 A 4 4 0 0 1 21 9 C 21 13, 18 17, 12 21 Z"/>
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Fact Rotator — homepage "Did you know?" module ────────────────
// Surfaces curated funFact entries from POSTCARDS_CATALOG, tailored to
// the user's mood vibes when available. Auto-rotates every 8 seconds;
// manual prev/next nav pauses auto-rotation for 30 seconds so users can
// linger. Designed to feel like an editorial flourish, not a feature
// banner — minimal chrome, mauve accent, gentle fade transitions.
function FactRotator({ userMoodVibes = [], identityVibes = [], accentColor = "#4A8CC4" }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();

  // Build the candidate fact pool. Strategy:
  //   1. If userMood is set, scope to destinations whose tags intersect mood vibes
  //   2. Else if identity has vibes, scope to those
  //   3. Else fall back to all destinations with funFacts
  // The pool is built once per signal change (not per render).
  const facts = useMemo(() => {
    // Cross-reference catalog → DESTINATIONS for tags
    const enriched = POSTCARDS_CATALOG
      .filter(p => p.funFact) // must have a fact to be a candidate
      .map(p => {
        const dest = DESTINATIONS.find(d => d.name === p.destName);
        return {
          destName: p.destName,
          country: p.country,
          funFact: p.funFact,
          tags: dest?.tags || [],
          emoji: dest?.emoji || "🌍",
        };
      });

    // Filter by mood if available
    const vibes = (userMoodVibes && userMoodVibes.length > 0) ? userMoodVibes
                : (identityVibes && identityVibes.length > 0) ? identityVibes
                : null;
    if (!vibes) return enriched;

    const matched = enriched.filter(f => f.tags.some(t => vibes.includes(t)));
    // If filter produced enough matches, use them; else fall back to all
    return matched.length >= 3 ? matched : enriched;
  }, [userMoodVibes, identityVibes]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fadeKey, setFadeKey] = useState(0); // remount inner block to retrigger fade

  // Auto-rotate every 8 seconds; reset interval if user manually navigates
  useEffect(() => {
    if (paused || facts.length < 2) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % facts.length);
      setFadeKey(k => k + 1);
    }, 8000);
    return () => clearInterval(id);
  }, [paused, facts.length]);

  // Reset index if facts pool changes (e.g. user retook mood quiz)
  useEffect(() => { setIndex(0); }, [facts]);

  const pauseFor = (ms = 30000) => {
    setPaused(true);
    setTimeout(() => setPaused(false), ms);
  };

  const goPrev = () => { setIndex(i => (i - 1 + facts.length) % facts.length); setFadeKey(k => k + 1); pauseFor(); };
  const goNext = () => { setIndex(i => (i + 1) % facts.length); setFadeKey(k => k + 1); pauseFor(); };

  if (facts.length === 0) return null;
  const current = facts[index];

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {/* Light bulb — the universal "did you know" icon */}
            <path d="M9 21 L 15 21" stroke="#4A8CC4" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M10 18 L 14 18" stroke="#4A8CC4" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M12 3 C 8 3, 6 6, 6 9 C 6 11, 7 13, 9 14.5 L 9 16 L 15 16 L 15 14.5 C 17 13, 18 11, 18 9 C 18 6, 16 3, 12 3 Z"
                  stroke="#4A8CC4" strokeWidth="1.7" fill="rgba(74,140,196,0.15)" strokeLinejoin="round"/>
            <path d="M10 9 L 12 7 L 14 9" stroke="#4A8CC4" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7"/>
          </svg>
          <div style={{ fontSize: 11, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800 }}>
            Did you know?
          </div>
        </div>
        {facts.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={goPrev} aria-label="Previous fact"
              style={{ width: 26, height: 26, borderRadius: "50%", border: "1px solid rgba(74,140,196,0.4)", background: "rgba(74,140,196,0.08)", color: "#4A8CC4", fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>‹</button>
            <button onClick={goNext} aria-label="Next fact"
              style={{ width: 26, height: 26, borderRadius: "50%", border: "1px solid rgba(74,140,196,0.4)", background: "rgba(74,140,196,0.08)", color: "#4A8CC4", fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>›</button>
          </div>
        )}
      </div>

      {/* Fact body — keyed by fadeKey so it remounts and replays the fade */}
      <div key={fadeKey} style={{ animation: "fadeIn 0.45s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 18 }}>{current.emoji}</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: textPrimary }}>
            {current.destName}
          </span>
          <span style={{ fontSize: 10, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>
            · {current.country}
          </span>
        </div>
        <div style={{ fontSize: 14, color: "#1B3A5C", lineHeight: 1.55, fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: "italic" }}>
          {current.funFact}
        </div>
      </div>

      {/* Dot indicators — only shown if facts > 1 and pool is small enough to display reasonably */}
      {facts.length > 1 && facts.length <= 12 && (
        <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 14 }}>
          {facts.map((_, i) => (
            <button key={i} onClick={() => { setIndex(i); setFadeKey(k => k + 1); pauseFor(); }}
              aria-label={`Fact ${i + 1} of ${facts.length}`}
              style={{ width: i === index ? 18 : 6, height: 6, borderRadius: 4, border: "none", background: i === index ? "#4A8CC4" : "rgba(74,140,196,0.3)", cursor: "pointer", padding: 0, transition: "all 0.2s" }}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Swipe Deck — Tinder-style destination discovery ──────────────
// Hand-rolled drag physics (no library deps). Swipe right = open board picker,
// left = skip 30 days, up = info modal. Tap card = info modal too.
// Buttons below the deck mirror the gestures for users who don't grok swipe.
function SwipeDeck({ allDestinations, identity, identityVibes, userMoodVibes, userBoards, skips, onSkip, onSave, onInfo, accentColor, userBudget, userAirport, flightPrices, onResetSkips, travelCompanion, fahrenheit, onUpdateMood = () => {}, hasMood = false }) {
  const { darkMode, textPrimary, textMuted, textDim } = useTheme();

  // ── Compose the deck once on mount (or when filters change) ──
  // Mix of identity (60%), mood (30%), and wildcard (10%) destinations.
  // Excludes: anything already in a board, anything skipped < 30 days ago.
  const deck = useMemo(() => {
    const now = Date.now();
    const THIRTY_DAYS = 30 * 86400000;
    const inBoard = new Set();
    (userBoards || []).forEach(b => (b.items || []).forEach(name => inBoard.add(name)));
    const skipped = new Set(
      Object.entries(skips || {}).filter(([_, ts]) => now - ts < THIRTY_DAYS).map(([name]) => name)
    );

    const candidates = allDestinations.filter(d => d.tags && !inBoard.has(d.name) && !skipped.has(d.name));

    // Score by identity vibe overlap, mood vibe overlap, and companion fit.
    // Companion fit reweights what destinations make sense for who you're with —
    // family-friendly destinations rise for family, nightlife rises for friends, etc.
    const scored = candidates.map(d => {
      const idScore = (identityVibes || []).filter(v => d.tags.includes(v)).length;
      const moodScore = (userMoodVibes || []).filter(v => d.tags.includes(v)).length;
      const fit = companionFit(d, travelCompanion);
      return { dest: d, idScore: idScore * fit, moodScore: moodScore * fit, fit };
    });

    // Mood-first ordering (matches the new "Mood Match" section name).
    // When userMood is set, lead with mood-matched picks; backfill with identity
    // picks if mood pool is thin. When no mood is set, fall back to identity-first
    // as before (the section's inline "pick a mood" prompt asks the user to refine).
    const hasMood = (userMoodVibes || []).length > 0;
    let firstTier, secondTier;
    if (hasMood) {
      firstTier = scored.filter(s => s.moodScore > 0).sort((a, b) => b.moodScore - a.moodScore).slice(0, 30);
      const firstNames = new Set(firstTier.map(s => s.dest.name));
      secondTier = scored.filter(s => s.idScore > 0 && !firstNames.has(s.dest.name)).sort((a, b) => b.idScore - a.idScore).slice(0, 15);
    } else {
      firstTier = scored.filter(s => s.idScore > 0).sort((a, b) => b.idScore - a.idScore).slice(0, 30);
      const firstNames = new Set(firstTier.map(s => s.dest.name));
      secondTier = scored.filter(s => s.moodScore > 0 && !firstNames.has(s.dest.name)).sort((a, b) => b.moodScore - a.moodScore).slice(0, 15);
    }
    const firstNames = new Set(firstTier.map(s => s.dest.name));
    const secondNames = new Set(secondTier.map(s => s.dest.name));
    // Wildcard tier — also weighted by companion fit so we don't show e.g. Vegas to a family
    const wildcardTier = scored
      .filter(s => !firstNames.has(s.dest.name) && !secondNames.has(s.dest.name) && s.fit >= 0.7)
      .sort(() => Math.random() - 0.5).slice(0, 5);

    // Combine and shuffle for variety
    const combined = [...firstTier, ...secondTier, ...wildcardTier].map(s => s.dest);
    // Light shuffle: keep some first-tier weighting but mix it up
    for (let i = combined.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }
    return combined.slice(0, 50);
  }, [allDestinations, identityVibes, userMoodVibes, userBoards, skips, travelCompanion]);

  const [currentIndex, setCurrentIndex] = useState(0);
  // ── Active deck snapshot — stays stable across swipes ────────────────
  // The deck useMemo above filters by skips/boards which change on every
  // swipe. If we read directly from it, under-cards lie — they preview
  // destinations that won't actually appear on top after the swipe
  // because the underlying list reshuffled mid-interaction.
  // Solution: snapshot the deck at mount. Active swipe session uses the
  // snapshot. We refresh the snapshot only when we exhaust it (currentIndex
  // exceeds length) or the user resets, so peek-cards always match what
  // slides up after the swipe.
  const [activeDeck, setActiveDeck] = useState(() => deck);
  // Refresh snapshot when we run out, falling back to the latest live deck.
  useEffect(() => {
    if (currentIndex >= activeDeck.length && deck.length > 0) {
      setActiveDeck(deck);
      setCurrentIndex(0);
    }
  }, [currentIndex, activeDeck.length, deck]);
  // Drag state
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [exitDir, setExitDir] = useState(null); // null | "left" | "right" | "up"
  const startRef = useRef({ x: 0, y: 0 });

  const SWIPE_THRESHOLD = 100;

  const currentDest = activeDeck[currentIndex];
  const nextDests = activeDeck.slice(currentIndex + 1, currentIndex + 3);

  function handlePointerDown(e) {
    if (exitDir) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
  }
  function handlePointerMove(e) {
    if (!dragging) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    setDragX(dx);
    setDragY(dy);
  }
  function handlePointerUp() {
    if (!dragging) return;
    setDragging(false);
    const dx = dragX, dy = dragY;
    // Up swipe — info modal
    if (dy < -SWIPE_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
      setDragX(0); setDragY(0);
      onInfo(currentDest);
      return;
    }
    // Right swipe — save
    if (dx > SWIPE_THRESHOLD) {
      triggerExit("right");
      return;
    }
    // Left swipe — skip
    if (dx < -SWIPE_THRESHOLD) {
      triggerExit("left");
      return;
    }
    // Snap back
    setDragX(0); setDragY(0);
  }

  function triggerExit(direction) {
    setExitDir(direction);
    setDragging(false);
    // After animation completes, fire callback and advance
    setTimeout(() => {
      if (direction === "right") onSave(currentDest);
      else if (direction === "left") onSkip(currentDest);
      setCurrentIndex(i => i + 1);
      setDragX(0); setDragY(0);
      setExitDir(null);
    }, 300);
  }

  // Tap (no significant movement) → info
  function handleClick() {
    if (Math.abs(dragX) < 8 && Math.abs(dragY) < 8 && !exitDir) {
      onInfo(currentDest);
    }
  }

  // Keyboard support
  useEffect(() => {
    function onKey(e) {
      if (!currentDest || exitDir) return;
      if (e.key === "ArrowRight") triggerExit("right");
      else if (e.key === "ArrowLeft") triggerExit("left");
      else if (e.key === "ArrowUp") onInfo(currentDest);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentDest, exitDir]); // eslint-disable-line

  // ── Empty state — celebration ──
  if (!currentDest) {
    const totalSaves = (userBoards || []).reduce((acc, b) => acc + (b.items || []).length, 0);
    const topBoards = [...(userBoards || [])].sort((a, b) => (b.items?.length || 0) - (a.items?.length || 0)).slice(0, 3);
    return (
      <div style={{ padding: "20px 18px", borderRadius: 16, border: `1.5px solid ${accentColor}40`, background: `linear-gradient(135deg, ${accentColor}12, ${accentColor}04)`, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle, ${accentColor}30 0%, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, position: "relative" }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <div style={{ fontSize: 11, letterSpacing: 2.5, color: accentColor, textTransform: "uppercase", fontWeight: 800 }}>Discovery</div>
        </div>
        <h3 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 900, color: textPrimary, position: "relative" }}>You've explored them all!</h3>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: textMuted, lineHeight: 1.5, position: "relative" }}>
          {totalSaves > 0 ? <>You've saved <strong style={{ color: accentColor }}>{totalSaves} destination{totalSaves !== 1 ? "s" : ""}</strong> across {(userBoards || []).length} board{(userBoards || []).length !== 1 ? "s" : ""}.</> : "Take a look at your boards or come back later for new picks."}
        </p>
        {topBoards.length > 0 && totalSaves > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {topBoards.map(b => (
              <div key={b.id} style={{ padding: "10px 12px", borderRadius: 10, background: `${b.color}10`, border: `1px solid ${b.color}30`, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${b.color}25`, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
                  {getBoardIcon(resolveBoardIconId(b)).render(b.color, 20)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: b.color, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{(b.items || []).length} saved</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => { onResetSkips(); setCurrentIndex(0); setActiveDeck(deck); }}
          style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, color: "#FAFAF5", fontSize: 13, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>
          ↺ Reshuffle the deck
        </button>
      </div>
    );
  }

  // ── Card renderer ──
  const cardRotation = dragging || exitDir ? dragX * 0.05 : 0;
  const opacity = exitDir ? 0 : (1 - Math.min(Math.abs(dragX) / 300, 0.3));
  const exitTransform = exitDir === "right" ? "translate(150vw, -40px) rotate(30deg)" :
                        exitDir === "left" ? "translate(-150vw, -40px) rotate(-30deg)" : null;

  // Decision indicator overlays (shown during drag)
  const showRightIndicator = dragX > 30;
  const showLeftIndicator = dragX < -30;
  const showUpIndicator = dragY < -30 && Math.abs(dragY) > Math.abs(dragX);

  const idMatch = identity && (currentDest.tags || []).filter(t => (identityVibes || []).includes(t)).length >= 2;
  const flightPrice = flightPrices?.[currentDest.name];
  // Use helper. SwipeDeck card chip already says "FROM" so we use est.from
  // (true minimum: flight + minBudget). Fit check uses realistic (honest mid-range).
  const est = estimateTripCost(currentDest, flightPrice, paxFromCompanion(travelCompanion, 1));
  const totalEst = est?.from || null;
  const fits = est && userBudget ? est.realistic <= userBudget : null;

  return (
    <div style={{ padding: "16px 0", borderRadius: 16, border: `1px solid ${accentColor}25`, background: `${accentColor}05`, position: "relative" }}>
      {/* Section header */}
      <div style={{ padding: "0 18px", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M2 12 Q5 6, 8 12 T 14 12 T 20 12 T 22 12" stroke={accentColor} strokeWidth="1.7" strokeLinecap="round" fill="none"/>
            <circle cx="12" cy="12" r="2" fill={accentColor} opacity="0.9"/>
          </svg>
          <div style={{ fontSize: 11, letterSpacing: 2.5, color: accentColor, textTransform: "uppercase", fontWeight: 800 }}>Mood Match</div>
        </div>
        <h3 style={{
          margin: "0 0 4px",
          fontFamily: "'Source Serif 4', Georgia, serif",
          fontStyle: "italic",
          fontSize: 19,
          fontWeight: 500,
          color: textPrimary,
          lineHeight: 1.15,
          letterSpacing: "-0.2px",
        }}>
          {hasMood ? "Matching your mood" : "Where to next?"}
        </h3>
        <p style={{ margin: 0, fontSize: 12, color: textMuted, fontStyle: "italic", lineHeight: 1.5 }}>
          → save · ← skip · ↑ details · or use the buttons below
        </p>
        {/* "Pick a mood" inline prompt — shown only when no mood set. These picks
           are currently identity-based; setting a mood reweights toward how you feel
           right now. Button opens the mood quiz. */}
        {!hasMood && (
          <div style={{
            marginTop: 10, padding: "9px 12px", borderRadius: 10,
            border: `1px dashed ${accentColor}55`,
            background: `${accentColor}08`,
            display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.4 }}>
              These are based on your travel identity. <strong style={{ color: accentColor }}>Pick a mood</strong> for matches tuned to how you feel today.
            </div>
            <button onClick={onUpdateMood}
              style={{
                flexShrink: 0, padding: "6px 12px", borderRadius: 8,
                border: `1px solid ${accentColor}66`,
                background: `${accentColor}18`,
                color: accentColor, fontSize: 11, fontWeight: 800, cursor: "pointer",
                fontFamily: "inherit", whiteSpace: "nowrap",
              }}>
              Pick mood
            </button>
          </div>
        )}
      </div>

      {/* Card stack */}
      <div style={{ position: "relative", height: 320, padding: "0 18px", marginBottom: 16 }}>
        {/* Render up to 3 cards: top is interactive, others are decorative */}
        {[currentDest, ...nextDests].map((d, i) => {
          if (!d) return null;
          const isTop = i === 0;
          const stackOffset = i * 8;
          const stackScale = 1 - i * 0.04;
          const stackOpacity = i === 0 ? 1 : 1 - i * 0.18;
          const transform = isTop
            ? (exitTransform || `translate(${dragX}px, ${dragY}px) rotate(${cardRotation}deg)`)
            : `translateY(${stackOffset}px) scale(${stackScale})`;
          const transition = isTop && !dragging ? "transform 0.3s cubic-bezier(0.34,1.2,0.64,1), opacity 0.3s ease" : "none";
          return (
            <div key={`${d.name}-${currentIndex}-${i}`}
              onPointerDown={isTop ? handlePointerDown : undefined}
              onPointerMove={isTop ? handlePointerMove : undefined}
              onPointerUp={isTop ? handlePointerUp : undefined}
              onPointerCancel={isTop ? handlePointerUp : undefined}
              onClick={isTop ? handleClick : undefined}
              style={{
                position: "absolute", inset: "0 18px",
                borderRadius: 18,
                overflow: "hidden",
                cursor: isTop ? (dragging ? "grabbing" : "grab") : "default",
                userSelect: "none", touchAction: "none",
                transform,
                transition,
                opacity: isTop ? opacity : stackOpacity,
                boxShadow: isTop ? "0 12px 40px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3)" : "0 8px 24px rgba(0,0,0,0.3)",
                zIndex: 10 - i,
                pointerEvents: isTop ? "auto" : "none",
              }}>
              {/* ── Card body — designed, typography-forward, no broken image dep ── */}
              {(() => {
                // Pick BOTH palette hues for a real two-color gradient (not the
                // previous monochrome fade). One hue dominates per destination
                // via the seed, the other anchors the corner. Gives the card
                // genuine depth — closer to "stylized poster" feel.
                const REGION_PALETTE = {
                  Europe: ["#4A8CC4", "#7a9e9f"],
                  Asia: ["#C25E47", "#4A8CC4"],
                  "North America": ["#4A8CC4", "#7A8467"],
                  "South America": ["#7A8467", "#A8853B"],
                  "Central America": ["#C25E47", "#7A8467"],
                  Africa: ["#A8853B", "#7A8467"],
                  Oceania: ["#4A8CC4", "#7A8A9C"],
                  Caribbean: ["#4A8CC4", "#7A8467"],
                };
                const palette = REGION_PALETTE[d.region] || [accentColor, "#4A8CC4"];
                const seed = (d.name.charCodeAt(0) + d.name.charCodeAt(d.name.length - 1)) % 2;
                const primary = palette[seed] || palette[0];
                const secondary = palette[(seed + 1) % 2] || palette[1];
                // Two distinct hues create real depth — primary up top, secondary
                // anchoring the bottom-right, with a dark vignette layered over for
                // dramatic contrast on the white text below.
                const gradient = `linear-gradient(135deg, ${primary} 0%, ${primary} 40%, ${secondary}d8 100%)`;

                return (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: gradient,
                    display: "flex", flexDirection: "column", justifyContent: "space-between",
                    padding: "22px 22px 18px",
                    color: "#fff",
                  }}>
                    {/* Decorative dot pattern */}
                    <div aria-hidden="true" style={{
                      position: "absolute", inset: 0,
                      backgroundImage: `radial-gradient(circle at 1px 1px, rgba(27,58,92,0.16) 1px, transparent 0)`,
                      backgroundSize: "20px 20px",
                      opacity: 0.5,
                      pointerEvents: "none",
                    }}/>
                    {/* Dark vignette at bottom — adds drama and makes white text
                       pop. Mimics the contrast of a real photo card without the
                       photo. The gradient fades from transparent at 50% to
                       deep shadow at the bottom edge. */}
                    <div aria-hidden="true" style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.45) 100%)",
                      pointerEvents: "none",
                    }}/>
                    {/* Giant flag emoji as background art */}
                    <div aria-hidden="true" style={{
                      position: "absolute",
                      top: "-18px", right: "-30px",
                      fontSize: 240, lineHeight: 1,
                      opacity: 0.16,
                      transform: "rotate(-8deg)",
                      pointerEvents: "none",
                      filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.3))",
                    }}>{d.emoji}</div>

                    {/* Top: country eyebrow + temperature chip on same row */}
                    <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 11, letterSpacing: 3, fontWeight: 800, opacity: 0.95, textTransform: "uppercase", textShadow: "0 1px 4px rgba(0,0,0,0.35)" }}>
                        {d.country}{d.state ? ` · ${d.state}` : ""}
                      </div>
                      {d.temp && (
                        <div style={{ fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 10, background: "rgba(27,58,92,0.16)", border: "1px solid #F0EDE3", WebkitBackdropFilter: "blur(4px)", backdropFilter: "blur(4px)", letterSpacing: 0.5, whiteSpace: "nowrap", flexShrink: 0 }}>
                          {fmtTemp(d.temp, fahrenheit)}
                        </div>
                      )}
                    </div>

                    {/* Center: destination name + vibe + tags. Bigger name,
                       elegant separator before vibe quote, slightly bigger tags. */}
                    <div style={{ position: "relative", zIndex: 2 }}>
                      <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 0.98, letterSpacing: -1.5, marginBottom: 12, textShadow: "0 2px 18px rgba(0,0,0,0.4)" }}>
                        {d.name}
                      </div>
                      {d.vibe && (
                        <div style={{ position: "relative", paddingTop: 10, marginBottom: 14 }}>
                          {/* Hairline accent above the quote — gives the type a frame */}
                          <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, width: 36, height: 2, background: "#F0EDE3", borderRadius: 1 }}/>
                          <div style={{ fontSize: 14, fontStyle: "italic", opacity: 0.95, lineHeight: 1.4, textShadow: "0 1px 6px rgba(0,0,0,0.35)" }}>
                            "{d.vibe}"
                          </div>
                        </div>
                      )}
                      {d.tags && d.tags.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {d.tags.slice(0, 3).map(t => (
                            <span key={t} style={{ fontSize: 11, padding: "4px 11px", borderRadius: 12, background: "rgba(27,58,92,0.20)", border: "1px solid #F0EDE3", fontWeight: 700, letterSpacing: 0.5, WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)", textTransform: "capitalize" }}>
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Bottom: budget chip aligned right */}
                    <div style={{ position: "relative", zIndex: 2, display: "flex", justifyContent: "flex-end" }}>
                      {totalEst && (
                        <div style={{ padding: "6px 12px", borderRadius: 11, background: "rgba(0,0,0,0.4)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)", border: "1px solid rgba(27,58,92,0.16)" }}>
                          <div style={{ fontSize: 9, letterSpacing: 1, fontWeight: 700, opacity: 0.85 }}>FROM</div>
                          <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1 }}>${totalEst.toLocaleString()}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Top-right badges — only on top card */}
              <div style={{ position: "absolute", top: 12, right: 12, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end", zIndex: 3 }}>
                {idMatch && isTop && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", background: "rgba(0,0,0,0.55)", border: "1px solid #F0EDE3", padding: "4px 10px", borderRadius: 12, letterSpacing: 0.5, WebkitBackdropFilter: "blur(4px)", backdropFilter: "blur(4px)", textTransform: "uppercase" }}>★ Your match</span>
                )}
                {fits && isTop && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", background: "rgba(109,189,109,0.85)", border: "1px solid rgba(109,189,109,1)", padding: "4px 10px", borderRadius: 12, letterSpacing: 0.5, WebkitBackdropFilter: "blur(4px)", backdropFilter: "blur(4px)" }}>✓ FITS BUDGET</span>
                )}
              </div>

              {/* Decision indicators (only on top card during drag) */}
              {isTop && showRightIndicator && (
                <div style={{ position: "absolute", top: 28, left: 22, transform: `rotate(-15deg) scale(${Math.min(0.85 + dragX / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: "4px solid #6dbd6d", background: "rgba(109,189,109,0.95)", color: "#fff", fontSize: 24, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(109,189,109,0.55), 0 2px 8px rgba(0,0,0,0.4)", textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>SAVE</div>
              )}
              {isTop && showLeftIndicator && (
                <div style={{ position: "absolute", top: 28, right: 22, transform: `rotate(15deg) scale(${Math.min(0.85 + Math.abs(dragX) / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: "4px solid #ff6b6b", background: "rgba(255,107,107,0.95)", color: "#fff", fontSize: 24, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(255,107,107,0.55), 0 2px 8px rgba(0,0,0,0.4)", textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>SKIP</div>
              )}
              {isTop && showUpIndicator && (
                <div style={{ position: "absolute", top: "45%", left: "50%", transform: `translate(-50%, -50%) scale(${Math.min(0.85 + Math.abs(dragY) / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: "4px solid #4A8CC4", background: "rgba(27,58,92,0.95)", color: "#fff", fontSize: 24, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(27,58,92,0.55), 0 2px 8px rgba(0,0,0,0.4)", textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>INFO</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18, padding: "0 18px" }}>
        <button onClick={() => triggerExit("left")} aria-label="Skip"
          style={{ width: 56, height: 56, borderRadius: "50%", border: "1.5px solid rgba(255,107,107,0.4)", background: "rgba(255,107,107,0.08)", color: "#ff6b6b", fontSize: 22, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,107,0.18)"; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,107,107,0.08)"; e.currentTarget.style.transform = "scale(1)"; }}>✕</button>
        <button onClick={() => onInfo(currentDest)} aria-label="See details"
          style={{ width: 46, height: 46, borderRadius: "50%", border: "1.5px solid rgba(27,58,92,0.4)", background: "rgba(27,58,92,0.08)", color: "#1B3A5C", fontSize: 18, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", fontWeight: 800 }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.18)"; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.08)"; e.currentTarget.style.transform = "scale(1)"; }}>ⓘ</button>
        <button onClick={() => triggerExit("right")} aria-label="Save"
          style={{ width: 56, height: 56, borderRadius: "50%", border: "1.5px solid rgba(109,189,109,0.5)", background: "rgba(109,189,109,0.12)", color: "#6dbd6d", fontSize: 22, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(109,189,109,0.22)"; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(109,189,109,0.12)"; e.currentTarget.style.transform = "scale(1)"; }}>♥</button>
      </div>

      {/* Progress dots */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 4 }}>
        <span style={{ fontSize: 10, color: textDim, fontStyle: "italic" }}>{Math.min(currentIndex + 1, deck.length)} of {deck.length}</span>
      </div>
    </div>
  );
}

// ── PostcardSwipeDeck ───────────────────────────────────────────
// Postcard-specific swipe deck modeled on SwipeDeck (above). Mirrors the
// same pointer/drag mechanics, exit animations, and action button row,
// but renders postcard tiles instead of destination tiles. Used on the
// dashboard in place of the previous thumbnail wall.
//
// Behavior per product spec:
//   - Right swipe / ♥ button → save as "dreaming", advance
//   - Left swipe / ✕ button  → no-op skip, advance (no permanent skip)
//   - Up swipe / ⓘ button     → open BoardPickerModal (which has Visited button)
//
// Props:
//   postcards   — filtered queue (uncollected catalog entries)
//   onSave(p)   — fires after right-swipe exit animation
//   onSkip(p)   — fires after left-swipe exit animation (currently no-op)
//   onInfo(p)   — fires on up-swipe or ⓘ tap (opens BoardPickerModal)
//   accentColor — themable accent (default mauve)
function PostcardSwipeDeck({ postcards, onSave, onSkip, onInfo, accentColor = "#4A8CC4" }) {
  const { textPrimary, textMuted, textDim } = useTheme();

  // Snapshot the queue at mount; refresh when exhausted. Same pattern as
  // SwipeDeck — keeps under-cards stable when the parent re-renders due
  // to a collection state change.
  const [activeDeck, setActiveDeck] = useState(() => postcards);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (currentIndex >= activeDeck.length && postcards.length > 0) {
      setActiveDeck(postcards);
      setCurrentIndex(0);
    }
  }, [currentIndex, activeDeck.length, postcards]);

  // Drag state — copied from SwipeDeck
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [exitDir, setExitDir] = useState(null);
  const startRef = useRef({ x: 0, y: 0 });
  const SWIPE_THRESHOLD = 100;
  const EXIT_MS = 300;

  const currentPostcard = activeDeck[currentIndex];
  const nextPostcards = activeDeck.slice(currentIndex + 1, currentIndex + 3);

  function handlePointerDown(e) {
    if (exitDir) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
  }
  function handlePointerMove(e) {
    if (!dragging) return;
    setDragX(e.clientX - startRef.current.x);
    setDragY(e.clientY - startRef.current.y);
  }
  function handlePointerUp() {
    if (!dragging) return;
    setDragging(false);
    const dx = dragX, dy = dragY;
    if (dy < -SWIPE_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
      triggerExit("up");
      return;
    }
    if (dx > SWIPE_THRESHOLD) { triggerExit("right"); return; }
    if (dx < -SWIPE_THRESHOLD) { triggerExit("left"); return; }
    setDragX(0); setDragY(0);
  }
  function triggerExit(direction) {
    setExitDir(direction);
    setDragging(false);
    setTimeout(() => {
      if (direction === "right") onSave(currentPostcard);
      else if (direction === "left") onSkip(currentPostcard);
      else if (direction === "up") onInfo(currentPostcard);
      setCurrentIndex(i => i + 1);
      setDragX(0); setDragY(0);
      setExitDir(null);
    }, EXIT_MS);
  }
  function handleClick() {
    if (Math.abs(dragX) < 8 && Math.abs(dragY) < 8 && !exitDir) {
      triggerExit("up");
    }
  }

  // Keyboard support
  useEffect(() => {
    function onKey(e) {
      if (!currentPostcard || exitDir) return;
      if (e.key === "ArrowRight") triggerExit("right");
      else if (e.key === "ArrowLeft") triggerExit("left");
      else if (e.key === "ArrowUp") triggerExit("up");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentPostcard, exitDir]); // eslint-disable-line

  // Empty state — user has gone through all uncollected postcards
  if (!currentPostcard) {
    return (
      <div style={{ padding: "32px 18px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>✉️</div>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#FAFAF5", marginBottom: 4 }}>
          You've seen them all
        </div>
        <div style={{ fontSize: 12, color: "rgba(250,250,245,0.7)", lineHeight: 1.5, maxWidth: 280, margin: "0 auto" }}>
          Check Profile → Postcards to see your full collection. New destinations come with each catalog drop.
        </div>
      </div>
    );
  }

  const cardRotation = dragX * 0.06; // degrees
  const opacity = exitDir ? 0 : Math.max(0.4, 1 - Math.abs(dragX) / 400);
  const exitTransform = exitDir === "right" ? "translateX(120%) rotate(20deg)"
    : exitDir === "left" ? "translateX(-120%) rotate(-20deg)"
    : exitDir === "up" ? "translateY(-100%) scale(0.94)"
    : null;

  return (
    <>
      <style>{`
        @keyframes jga-postcard-exit-left  { to { transform: translateX(-30%) rotate(-8deg); opacity: 0; } }
        @keyframes jga-postcard-exit-right { to { transform: translateX(30%) rotate(8deg);   opacity: 0; } }
        @keyframes jga-postcard-enter      { from { transform: translateY(20%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* Card stack — height accommodates 200px illustration + metadata
         content. Width capped at 480 so the 280:110 illustration doesn't
         get aggressively cropped by slice mode on wide desktop. */}
      {/* Card stack — height accommodates 7:5 illustration + metadata footer.
         Max-width 480 caps card geometry; at that width the 7:5 illustration
         is ~317px tall (444 wide / 1.4), leaving ~240px for the metadata
         section (country, title, vibe quote, landmarks, tag pills). */}
      <div style={{ position: "relative", height: 580, padding: "0 18px", marginBottom: 16, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
        {[currentPostcard, ...nextPostcards].map((p, i) => {
          if (!p) return null;
          const isTop = i === 0;
          const stackOffset = i * 8;
          const stackScale = 1 - i * 0.04;
          const stackOpacity = i === 0 ? 1 : 1 - i * 0.2;
          const transform = isTop
            ? (exitTransform || `translate(${dragX}px, ${dragY}px) rotate(${cardRotation}deg)`)
            : `translateY(${stackOffset}px) scale(${stackScale})`;
          const transition = isTop && !dragging
            ? "transform 0.3s cubic-bezier(0.34,1.2,0.64,1), opacity 0.3s ease"
            : "none";

          // Cross-reference DESTINATIONS so we can show tags + a real
          // blurb. The catalog has poetic vibe; DESTINATIONS has the
          // tags + the destination's own vibe quote.
          const destLookup = (typeof DESTINATIONS !== "undefined") ? DESTINATIONS.find(d => d.name === p.destName) : null;
          const destTags = destLookup?.tags || [];
          const destVibe = destLookup?.vibe || null;

          return (
            <div key={`${p.id}-${currentIndex}-${i}`}
              onPointerDown={isTop ? handlePointerDown : undefined}
              onPointerMove={isTop ? handlePointerMove : undefined}
              onPointerUp={isTop ? handlePointerUp : undefined}
              onPointerCancel={isTop ? handlePointerUp : undefined}
              onClick={isTop ? handleClick : undefined}
              style={{
                position: "absolute", inset: "0 18px",
                borderRadius: 16,
                overflow: "hidden",
                cursor: isTop ? (dragging ? "grabbing" : "grab") : "default",
                userSelect: "none", touchAction: "none",
                transform, transition,
                opacity: isTop ? opacity : stackOpacity,
                boxShadow: isTop ? "0 12px 40px rgba(27,58,92,0.25), 0 4px 12px rgba(27,58,92,0.15)" : "0 8px 24px rgba(27,58,92,0.15)",
                zIndex: 10 - i,
                pointerEvents: isTop ? "auto" : "none",
                background: "#fdfaf3",
                border: `1px solid ${accentColor}30`,
                display: "flex", flexDirection: "column",
              }}>
              {/* Top portion — postcard illustration. Uses 7:5 aspect to
                 match the SVG viewBox (280x200) so the entire illustration
                 renders without slice cropping. Same aspect used by the
                 collection grid and dashboard wall renders. */}
              <div style={{ aspectRatio: "7 / 5", flexShrink: 0, overflow: "hidden", borderBottom: `1px solid ${accentColor}25` }}>
                <PostcardIllustration id={p.id} />
              </div>
              {/* Bottom portion — metadata */}
              <div style={{ padding: "14px 16px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: accentColor, textTransform: "uppercase", fontWeight: 700 }}>
                  {p.country}
                </div>
                <div style={{ fontSize: 24, fontWeight: 900, color: textPrimary, lineHeight: 1.1, letterSpacing: -0.5 }}>
                  {p.title}
                </div>
                {/* Vibe quote — prefer catalog vibe (poetic), fallback to dest vibe */}
                {(p.vibe || destVibe) && (
                  <div style={{ fontSize: 12, color: textMuted, fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: 1.45, marginTop: 2 }}>
                    "{p.vibe || destVibe}"
                  </div>
                )}
                {/* Famous-for line — 3 iconic landmarks per destination,
                   sourced from the postcard catalog. Turns the card into
                   a discovery moment ("oh right, Lisbon has Tram 28") and
                   uses the bottom space productively on wider cards. */}
                {p.landmarks && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 9, letterSpacing: 1.8, color: accentColor, textTransform: "uppercase", fontWeight: 700, marginBottom: 3, opacity: 0.85 }}>
                      Famous for
                    </div>
                    <div style={{ fontSize: 12, color: textPrimary, lineHeight: 1.45, fontWeight: 500 }}>
                      {p.landmarks}
                    </div>
                  </div>
                )}
                {/* Must See / Must Eat / Must Do — three-column row of
                   curated, concrete experiences. Distinct from "Famous for"
                   which lists abstract landmarks; this row gives travelers
                   three specific things they'd actually plan around. */}
                {(p.mustSee || p.mustEat || p.mustDo) && (
                  <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px dashed ${accentColor}30`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {p.mustSee && (
                      <div>
                        <div style={{ fontSize: 8.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 3 }}>See</div>
                        <div style={{ fontSize: 11, color: textPrimary, lineHeight: 1.3, fontWeight: 500 }}>{p.mustSee}</div>
                      </div>
                    )}
                    {p.mustEat && (
                      <div>
                        <div style={{ fontSize: 8.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 3 }}>Eat</div>
                        <div style={{ fontSize: 11, color: textPrimary, lineHeight: 1.3, fontWeight: 500 }}>{p.mustEat}</div>
                      </div>
                    )}
                    {p.mustDo && (
                      <div>
                        <div style={{ fontSize: 8.5, letterSpacing: 1.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 800, marginBottom: 3 }}>Do</div>
                        <div style={{ fontSize: 11, color: textPrimary, lineHeight: 1.3, fontWeight: 500 }}>{p.mustDo}</div>
                      </div>
                    )}
                  </div>
                )}
                {/* Tag pills — pulled from DESTINATIONS since catalog
                   doesn't carry tags. Shows up to 3 vibes the destination matches. */}
                {destTags.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: "auto" }}>
                    {destTags.slice(0, 3).map(t => (
                      <span key={t} style={{ fontSize: 9.5, padding: "3px 9px", borderRadius: 10, background: `${accentColor}18`, border: `1px solid ${accentColor}40`, fontWeight: 700, color: accentColor, letterSpacing: 0.4, textTransform: "capitalize" }}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Drag-direction labels (only on top card while dragging) */}
              {isTop && dragX > 40 && !exitDir && (
                <div style={{ position: "absolute", top: "45%", left: "50%", transform: `translate(-50%, -50%) scale(${Math.min(0.85 + dragX / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: "4px solid #6dbd6d", background: "rgba(109,189,109,0.92)", color: "#fff", fontSize: 22, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(27,58,92,0.4)" }}>
                  DREAM
                </div>
              )}
              {isTop && dragX < -40 && !exitDir && (
                <div style={{ position: "absolute", top: "45%", left: "50%", transform: `translate(-50%, -50%) scale(${Math.min(0.85 + Math.abs(dragX) / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: "4px solid #ff6b6b", background: "rgba(255,107,107,0.92)", color: "#fff", fontSize: 22, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(27,58,92,0.4)" }}>
                  SKIP
                </div>
              )}
              {isTop && dragY < -40 && Math.abs(dragY) > Math.abs(dragX) && !exitDir && (
                <div style={{ position: "absolute", top: "45%", left: "50%", transform: `translate(-50%, -50%) scale(${Math.min(0.85 + Math.abs(dragY) / 200, 1.3)})`, padding: "10px 22px", borderRadius: 10, border: `4px solid ${accentColor}`, background: `${accentColor}f0`, color: "#fff", fontSize: 22, fontWeight: 900, letterSpacing: 3, pointerEvents: "none", zIndex: 4, boxShadow: "0 6px 24px rgba(27,58,92,0.4)" }}>
                  OPTIONS
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action button row */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18, padding: "0 18px" }}>
        <button onClick={() => triggerExit("left")} aria-label="Skip"
          style={{ width: 56, height: 56, borderRadius: "50%", border: "1.5px solid rgba(255,107,107,0.4)", background: "rgba(255,107,107,0.08)", color: "#ff6b6b", fontSize: 22, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,107,0.18)"; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,107,107,0.08)"; e.currentTarget.style.transform = "scale(1)"; }}>✕</button>
        <button onClick={() => triggerExit("up")} aria-label="See details"
          style={{ width: 46, height: 46, borderRadius: "50%", border: `1.5px solid ${accentColor}66`, background: `${accentColor}15`, color: accentColor, fontSize: 18, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", fontWeight: 800 }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accentColor}28`; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${accentColor}15`; e.currentTarget.style.transform = "scale(1)"; }}>ⓘ</button>
        <button onClick={() => triggerExit("right")} aria-label="Save as Dreaming"
          style={{ width: 56, height: 56, borderRadius: "50%", border: "1.5px solid rgba(109,189,109,0.5)", background: "rgba(109,189,109,0.12)", color: "#6dbd6d", fontSize: 22, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(109,189,109,0.22)"; e.currentTarget.style.transform = "scale(1.05)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(109,189,109,0.12)"; e.currentTarget.style.transform = "scale(1)"; }}>♥</button>
      </div>

      {/* Progress counter */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <span style={{ fontSize: 10, color: "rgba(250,250,245,0.55)", fontStyle: "italic" }}>{Math.min(currentIndex + 1, activeDeck.length)} of {activeDeck.length}</span>
      </div>
    </>
  );
}

// ── Main App ────────────────────────────────────────────────────
export default function TakeMeAnywhere() {
  const [mode, setMode] = useState(null);
  const [prefilledDest, setPrefilledDest] = useState(null);
  // When set, the user has picked a specific destination (e.g. from home
  // State 7 "Show me Vancouver") and wants to set budget/dates BEFORE the
  // itinerary auto-generates. The TMA step 0 detects this and replaces the
  // matching flow with a "Generate itinerary for {dest}" button. Cleared
  // once the itinerary fires.
  const [lockedTripDest, setLockedTripDest] = useState(null);
  // Home page recommendation refresh tick — incremented on Zone 1 dismiss
  // (e.g. "Not today" on State 7 destination rec) to reliably trigger a
  // re-render. The previous setBottomTab(null) hack was a no-op when
  // bottomTab is already null, leaving the same dismissed dest visible.
  const [homeRefreshTick, setHomeRefreshTick] = useState(0);

  // Track which discovery modes the user has entered. Used to gate the
  // mode-comparison popup — once a user has tried Surprise Me or Take Me
  // Anywhere, they understand what each one is and don't need the
  // comparison anymore. Stored as a comma-separated string in localStorage.
  useEffect(() => {
    if (mode !== "blind" && mode !== "explore") return;
    try {
      const raw = localStorage.getItem("jga-modes-used") || "";
      const used = raw ? raw.split(",") : [];
      if (!used.includes(mode)) {
        used.push(mode);
        localStorage.setItem("jga-modes-used", used.join(","));
      }
    } catch {}
  }, [mode]);

  // Idle globe rotation on the unauthenticated landing hero
  const { isIdle: heroIdle } = useHeroIdleGlobe({ idleMs: 10000 });

  // Bottom-nav hide-on-scroll-down behavior. Pattern matches Twitter/Instagram:
  //   - Always visible above the fold (scrollY < 100)
  //   - Hide when scrolling DOWN past the threshold
  //   - Show on any scroll UP, regardless of position
  // Uses a ref to track lastScrollY without re-running the listener on every render.
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const last = lastScrollYRef.current;
        // Above-the-fold: always show
        if (y < 100) setNavHidden(false);
        // Scrolling down by more than 5px: hide
        else if (y > last + 5) setNavHidden(true);
        // Scrolling up by more than 5px: show
        else if (y < last - 5) setNavHidden(false);
        lastScrollYRef.current = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Read ?dest= query param on load (from blog links)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const destName = params.get("dest");
      if (destName) {
        const dest = DESTINATIONS.find(d => d.name.toLowerCase() === destName.toLowerCase());
        if (dest) {
          setPrefilledDest(dest);
          setMode("explore");
          // Clean the URL
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
    } catch (e) {}
  }, []); // eslint-disable-line
  // ── Theme: locked to editorial cream parchment (single curated direction) ──
  // The dark/light toggle is retired. Every surface now reads from the
  // canonical editorial tokens (bgCream, inkBurgundy, etc.) defined further
  // down. `darkMode` is kept as a const for backward compat with the ~513
  // ternary references throughout this file — they all resolve to the light
  // branch (the closer-to-cream branch), pending Phase 4 sweep that collapses
  // each ternary to its correct cream-direction value. setDarkMode is a no-op
  // stub so any legacy setter calls don't crash.
  const darkMode = false;
  const setDarkMode = () => {};
  const [step, setStep] = useState(0);
  // Scroll to appropriate position when step/mode changes
  useEffect(() => {
    if (step === 1 && mode === "explore") {
      // Give DOM time to render, then scroll to airport banner or step content
      setTimeout(() => {
        const banner = document.getElementById("exp-airport-banner");
        if (banner) {
          banner.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // Airport already set — scroll to step pills
          const pills = document.getElementById("exp-step-pills");
          if (pills) pills.scrollIntoView({ behavior: "smooth", block: "start" });
          else window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 80);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [step, mode]);

  // Auth
  const [spinCount, setSpinCount] = useState(0);
  // ── DEV: auto-login with test account ──────────────────────────
  const [fahrenheit, setFahrenheit] = useState(false);
  useEffect(() => { setFahrenheit(detectFahrenheit()); }, []);

  // ── Weather for current location (Open-Meteo, no API key) ─────────
  // Always fetches in celsius; we convert to F for display only. This avoids a
  // race condition where two effect runs (one before fahrenheit is detected,
  // one after) could clobber each other with different units.
  // Only auto-fetches if geolocation permission is already granted, so we
  // don't trigger surprise permission prompts. Cached for 30min.
  const [weather, setWeather] = useState(null); // { tempC, icon, code }
  useEffect(() => {
    const CACHE_KEY = "jga-weather-cache-v2";
    const CACHE_DURATION = 30 * 60 * 1000;
    let cancelled = false;
    // Hydrate from cache if fresh
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setWeather(cached.data);
        return;
      }
    } catch {}
    if (!navigator.geolocation) return;

    const fetchWeather = () => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          if (cancelled) return;
          const { latitude, longitude } = pos.coords;
          fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (cancelled) return;
              if (!data || !data.current) return;
              const tempC = data.current.temperature_2m;
              const code = data.current.weather_code;
              const icon = weatherCodeToIcon(code);
              const result = { tempC, icon, code };
              setWeather(result);
              try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data: result, timestamp: Date.now() })); } catch {}
            })
            .catch(() => {});
        },
        () => {}, // silently ignore denial / errors
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    };

    // Only auto-fetch if permission already granted (no surprise prompts)
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then(result => { if (!cancelled && result.state === "granted") fetchWeather(); })
        .catch(() => {});
    } else {
      fetchWeather();
    }

    return () => { cancelled = true; };
  }, []);
  // ── Auth state ──────────────────────────────────────────────────
  // Session restore: on mount, hydrate user + registered status from localStorage.
  // Real session validity is verified by the auth refresh effect; this just
  // avoids the "logged-in user reloads page → sees logged-out state" flicker.
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("jga-user");
      const token = localStorage.getItem("jga_token");
      if (saved && token) return JSON.parse(saved);
    } catch {}
    return null;
  });
  const [registered, setRegistered] = useState(() => {
    try {
      const saved = localStorage.getItem("jga-user");
      const token = localStorage.getItem("jga_token");
      return !!(saved && token);
    } catch { return false; }
  });
  // Re-hydration trigger — bumped when the auth refresh effect successfully
  // renews the access token. Hydration effects (boards, trips, journal) include
  // this in their deps array, so they re-fetch with the fresh token.
  // Critical for mobile: data loaders bail during the 3s grace period if the
  // stored access token is stale; without this trigger they'd never re-fetch
  // after refresh, leaving the user with empty UI on a valid session.
  const [hydrationTrigger, setHydrationTrigger] = useState(0);
  useEffect(() => {
    function onTokenRefreshed() {
      console.log("[JGA Hydrate] Token refreshed — bumping re-hydration trigger");
      setHydrationTrigger(c => c + 1);
    }
    window.addEventListener("jga:token-refreshed", onTokenRefreshed);
    return () => window.removeEventListener("jga:token-refreshed", onTokenRefreshed);
  }, []);
  // Re-hydrate whenever the tab becomes visible OR window regains focus.
  // No throttling — correctness over politeness. Better to fire a few extra
  // /api/trips calls than to show stale data after cross-device delete.
  // iOS Safari is unreliable about which event fires (visibilitychange vs focus),
  // so we listen to both. setHydrationTrigger dedupes via React batching anyway.
  useEffect(() => {
    function bump(label) {
      console.log(`[JGA Hydrate] ${label} — bumping re-hydration trigger`);
      setHydrationTrigger(c => c + 1);
    }
    function onVisibilityChange() {
      if (document.visibilityState === "visible") bump("Tab visible");
    }
    function onFocus() { bump("Window focused"); }
    function onPageShow(e) {
      // pageshow fires when navigating back via bfcache (Safari especially).
      // Without this, Safari can serve a fully-cached page with stale state.
      if (e.persisted) bump("Page restored from bfcache");
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);
  // Re-hydrate on EVERY entry to trips/journal/profile mode — these surfaces
  // show server-backed data so we want strict freshness. No throttle: each
  // navigation gets its own fetch. Same reasoning as above — correctness wins.
  useEffect(() => {
    if (mode !== "trips" && mode !== "journal" && mode !== "account" && mode !== "profile") return;
    console.log(`[JGA Hydrate] Entered ${mode} — bumping re-hydration trigger`);
    setHydrationTrigger(c => c + 1);
  }, [mode]);
  // Transient toast for surfacing backend sync errors. Critical on mobile
  // where DevTools isn't accessible — silent DELETE failures would otherwise
  // be invisible. type: "error" | "warn" | "info" controls color.
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((message, type = "error") => {
    setToast({ message, type, id: Date.now() });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 5000);
  }, []);
  const [showRegister, setShowRegister] = useState(false);
  const [showWorthKnowing, setShowWorthKnowing] = useState(false);
  // Mode comparison modal — shown on identity CTA tap for users who haven't
  // yet used Surprise Me or Take Me Anywhere. Helps new users understand the
  // difference between the two main discovery modes before committing to one.
  const [showModeComparison, setShowModeComparison] = useState(false);
  // Email captured at the quiz-save step, pre-filled into RegisterModal so the
  // user doesn't retype what they just entered.
  const [prefillRegisterEmail, setPrefillRegisterEmail] = useState("");
  const [prefillBookingCode, setPrefillBookingCode] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  // Optional pre-fill for the LoginModal email field — set by handlers that
  // know the user's email (e.g. quiz lead capture detected alreadyUser:true,
  // so the user shouldn't have to retype the email they JUST entered).
  const [loginInitialEmail, setLoginInitialEmail] = useState("");
  // Optional booking code to display in LoginModal as a confirmation banner
  // ("Welcome! Your booking code TMA-XXX is ready"). Set when the approval
  // email's ?code= URL routes to login. After successful login, the code
  // (also stashed in jga_pending_booking_code localStorage) drives the
  // post-auth routing to the Trips tab.
  const [loginInitialBookingCode, setLoginInitialBookingCode] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [accountDefaultTab, setAccountDefaultTab] = useState("trips");
  // Sub-tab requested when navigating to Profile (so dashboard chip + toast
  // can route directly to STAMPS sub-tab instead of OVERVIEW)
  const [accountProfileSubTab, setAccountProfileSubTab] = useState("overview");
  // Journal sub-tab deep-link state. Set before navigating to Journal so the
  // user lands on the right sub-tab (e.g. stamp toast → "stamps"). Resets
  // to "dashboard" when Journal is opened without a specific intent.
  const [accountJournalSubTab, setAccountJournalSubTab] = useState("dashboard");
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showCustomerService, setShowCustomerService] = useState(false);
  const [showAgentWaitlist, setShowAgentWaitlist] = useState(false);
  // TMA "Get on the waitlist" — holds the trip context when the modal is open
  // ({ dest, departDate, returnDate }) or null when closed. Wired to the
  // TMA choice-row + date-picker card CTAs in place of the booking modal,
  // since v1 hands booking off to affiliate partners (Booking.com link
  // still being tested) — until that's live, users join a waitlist instead
  // and we notify when bookable.
  const [tmaWaitlistContext, setTmaWaitlistContext] = useState(null);
  // TMA milestones — per-destination, persisted to localStorage so the user
  // can navigate away from a destination and back without losing context.
  // Map: { [destId]: { type, otherText, updatedAt } }. Default for any
  // destination not in the map is "just-because" (a real answer, not empty).
  const [tmaMilestones, setTmaMilestonesState] = useState(() => getTmaMilestones());
  // Session-only flag tracking which destinations the user has explicitly
  // engaged with (vs default). Used so backend can distinguish answered vs
  // skipped vs never-saw-it. Map: { [destId]: "answered" | "skipped" }.
  const [tmaMilestoneStatus, setTmaMilestoneStatus] = useState({});
  // SM milestone — single value, session-only. Resets between spins.
  const [blindMilestone, setBlindMilestone] = useState({ type: "just-because", otherText: null });
  const [blindMilestoneStatus, setBlindMilestoneStatus] = useState("answered"); // answered | skipped
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [showAbandonFeedback, setShowAbandonFeedback] = useState(false);
  const [abandonContext, setAbandonContext] = useState({ from: null, hadProgress: false });
  const lastModeRef = useRef(null);
  const hadProgressRef = useRef(false);
  const feedbackShownRef = useRef(false);
  // Tracks the destination name the user most recently engaged with in
  // TMA/Surprise Me. Used by abandon feedback to dismiss the destination
  // from home-page recommendations once feedback is submitted — prevents
  // the same destination from being re-recommended after engagement.
  // Added Jun 25, 2026 per Cas (the "Chiang Mai still shows after feedback" bug).
  const lastSelectedDestRef = useRef(null);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showAffiliateDisclosure, setShowAffiliateDisclosure] = useState(false);

  // ════════════════════════════════════════════════════════════════
  // ANALYTICS — initialize tracking on mount (UTM, anonId, sessionId, window.jgaTrack)
  // ════════════════════════════════════════════════════════════════
  useEffect(() => {
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";

      // ── 1. Capture UTM params from current URL (sticky for session) ──
      const url = new URL(window.location.href);
      const newUtm = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(key => {
        const val = url.searchParams.get(key);
        if (val) newUtm[key.replace("utm_", "")] = val.slice(0, 120);
      });
      // Persist UTM if we have any new values; otherwise keep existing
      let savedUtm = {};
      try { savedUtm = JSON.parse(localStorage.getItem("jga-utm") || "{}"); } catch {}
      if (Object.keys(newUtm).length > 0) {
        // First-touch attribution: only overwrite if no UTM stored yet
        if (Object.keys(savedUtm).length === 0) {
          try { localStorage.setItem("jga-utm", JSON.stringify(newUtm)); } catch {}
          savedUtm = newUtm;
        }
      }

      // ── 2. Generate or reuse anonId (persists across sessions) ──
      let anonId;
      try {
        anonId = localStorage.getItem("jga-anon-id");
        if (!anonId) {
          anonId = "anon_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
          localStorage.setItem("jga-anon-id", anonId);
        }
      } catch { anonId = "anon_fallback_" + Date.now(); }

      // ── 3. Generate sessionId (one per browser session) ──
      let sessionId;
      try {
        sessionId = sessionStorage.getItem("jga-session-id");
        if (!sessionId) {
          sessionId = "sess_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
          sessionStorage.setItem("jga-session-id", sessionId);
        }
      } catch { sessionId = "sess_fallback_" + Date.now(); }

      // ── 4. Define window.jgaTrack — used everywhere in the app ──
      window.jgaTrack = function(event, properties = {}) {
        try {
          let token = null;
          try { token = localStorage.getItem("jga_token"); } catch {}
          const headers = { "Content-Type": "application/json" };
          if (token) headers["Authorization"] = `Bearer ${token}`;

          fetch(`${API}/api/track`, {
            method: "POST",
            headers,
            // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
            keepalive: true, // ensures the request completes even if user navigates away
            body: JSON.stringify({
              event: String(event).slice(0, 80),
              anonId,
              sessionId,
              properties: properties || {},
              utm: savedUtm,
              referrer: document.referrer || null,
              pageUrl: window.location.href,
            }),
          }).catch(() => {}); // Silent fail — analytics never disrupts UX
        } catch {}
      };

      // ── 5. Track initial page view ──
      window.jgaTrack("page_view", {
        path: window.location.pathname,
        firstVisit: !localStorage.getItem("jga-returning"),
      });
      try { localStorage.setItem("jga-returning", "true"); } catch {}
    } catch (e) {
      console.error("[JGA Analytics] Init failed:", e);
    }
  }, []); // eslint-disable-line

  // Track session start
  useEffect(() => {
    if (typeof window.jgaTrack === "function") window.jgaTrack("session_start", { darkMode, referrer: document.referrer || "direct" });
  }, []); // eslint-disable-line

  const [showIdentityCard, setShowIdentityCard] = useState(false);
  // True only when the modal opens immediately after the quiz finishes.
  // Plays the constellation reveal animation once, then resets to false.
  const [identityCardFreshReveal, setIdentityCardFreshReveal] = useState(false);
  // Identity share render state — disables the button while the PNG generates
  // (~500ms), surfaces errors inline. Used by the constellation share render.
  const [identitySharing, setIdentitySharing] = useState(false);
  const [identityShareError, setIdentityShareError] = useState("");

  // shareIdentityCard — copied from AccountModal so the new TakeMeAnywhere
  // identity modal can produce the 1080×1350 share PNG. Both scopes need
  // their own copy because the state setters are component-local.
  async function shareIdentityCard(identity, travelerType) {
    if (identitySharing) return;
    setIdentitySharing(true);
    setIdentityShareError("");
    try {
      const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const sky = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
      const chart = generateConstellation(identity);
      const [oxStr, oyStr] = (sky.origin || "50% 50%").split(/\s+/);
      const ox = parseFloat(oxStr) / 100;
      const oy = parseFloat(oyStr) / 100;
      const nameWords = String(travelerType || "").split(" ");
      const firstLine = `The ${nameWords[0] || ""}`;
      const secondLine = nameWords.slice(1).join(" ");
      function wrapText(text, maxChars) {
        const words = String(text || "").split(/\s+/);
        const lines = [];
        let cur = "";
        for (const w of words) {
          if ((cur + " " + w).trim().length > maxChars) {
            if (cur) lines.push(cur.trim());
            cur = w;
          } else { cur = cur ? cur + " " + w : w; }
        }
        if (cur) lines.push(cur.trim());
        return lines;
      }
      const descLines = wrapText(`"${identity.desc || ""}"`, 36);
      const descStartY = 1075;
      const chartScale = 3.6;
      const chartW = 200 * chartScale;
      const chartH = 140 * chartScale;
      const chartX = (1080 - chartW) / 2;
      const chartY = 280;

      const svgString = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
  <defs>
    <radialGradient id="sky" cx="${ox}" cy="${oy}" r="0.9" fx="${ox}" fy="${oy}">
      <stop offset="0%" stop-color="${sky.bright}"/>
      <stop offset="55%" stop-color="${sky.mid}"/>
      <stop offset="100%" stop-color="${sky.dark}"/>
    </radialGradient>
  </defs>
  <rect width="1080" height="1350" fill="url(#sky)"/>
  <text x="540" y="100" text-anchor="middle" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="20" letter-spacing="5" fill="rgba(250,250,245,0.5)">CHARTED</text>
  <text x="540" y="140" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="24" fill="rgba(250,250,245,0.6)">a constellation, drawn for you</text>
  <g transform="translate(${chartX}, ${chartY}) scale(${chartScale})">
    ${chart.bgStars.map(s => `<circle cx="${s.x}" cy="${s.y}" r="${s.r}" fill="#FAFAF5" opacity="${s.o}"/>`).join("\n    ")}
    ${chart.lines.map(l => `<line x1="${l.x1}" y1="${l.y1}" x2="${l.x2}" y2="${l.y2}" stroke="${sky.accent}" stroke-width="1.5" opacity="0.65" stroke-linecap="round"/>`).join("\n    ")}
    ${Object.values(chart.stars).map(s => `<circle cx="${s.x}" cy="${s.y}" r="${s.r * 2.2}" fill="#FAFAF5" opacity="0.18"/><circle cx="${s.x}" cy="${s.y}" r="${s.r}" fill="#FAFAF5"/>`).join("\n    ")}
  </g>
  <text x="540" y="930" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-size="62" font-weight="500" fill="#FAFAF5" letter-spacing="-1">${esc(firstLine)}</text>
  <text x="540" y="1005" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="62" font-weight="500" fill="${sky.accent}" letter-spacing="-1">${esc(secondLine)}</text>
  ${descLines.map((l, i) => `<text x="540" y="${descStartY + i * 38}" text-anchor="middle" font-family="'Source Serif 4', Georgia, serif" font-style="italic" font-size="24" fill="rgba(250,250,245,0.78)">${esc(l)}</text>`).join("\n  ")}
  <g font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="14" letter-spacing="3" font-weight="700">
    <text x="60" y="1300" fill="rgba(74,140,196,0.75)">JUST</text>
    <text x="118" y="1300" fill="rgba(250,250,245,0.65)">GO</text>
    <text x="155" y="1300" fill="rgba(232,154,60,0.78)">ANYWHERE</text>
  </g>
  <text x="1020" y="1300" text-anchor="end" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="14" letter-spacing="3" fill="rgba(250,250,245,0.45)">justgoanywhere.com</text>
</svg>`;

      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.crossOrigin = "anonymous";
      const loaded = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load identity card image"));
      });
      img.src = svgUrl;
      await loaded;
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1350;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 1080, 1350);
      URL.revokeObjectURL(svgUrl);
      const pngBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error("Failed to generate PNG")), "image/png");
      });
      const filename = `JGA-${String(travelerType).replace(/\s+/g, "-")}-chart.png`;
      const file = new File([pngBlob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My travel chart: The ${travelerType}`,
          text: `I'm The ${travelerType}. What's your travel identity? Find yours at justgoanywhere.com`,
        });
      } else {
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err && err.name === "AbortError") {
        // user cancelled native share
      } else {
        console.error("[JGA Share] Identity chart share failed:", err);
        setIdentityShareError("Couldn't generate the image. Try again in a moment.");
      }
    } finally {
      setIdentitySharing(false);
    }
  }
  // Detour immersive mode — when the user scrolls the horizontal card row past
  // a threshold, the chrome (eyebrow, headline, copy, footer flourish) fades
  // and the cards grow to fill the space. Scroll back to start or tap × to exit.
  // Tracks scroll position via ref + scroll event handler; updates only when
  // crossing the threshold (with hysteresis) to avoid render thrash.
  const [detourImmersive, setDetourImmersive] = useState(false);
  // Callback ref for Detour scroll element. Attaches/detaches the scroll
  // listener as the element mounts/unmounts — handles the conditional render
  // (Detour only renders when identity + challengerPool are ready) cleanly.
  // Throttled via rAF; hysteresis at 100/50px to prevent flicker.
  const detourScrollListenerRef = useRef(null);
  const detourScrollRef = useCallback((el) => {
    // Detach from previous element if any
    if (detourScrollListenerRef.current?.el && detourScrollListenerRef.current.handler) {
      detourScrollListenerRef.current.el.removeEventListener("scroll", detourScrollListenerRef.current.handler);
    }
    if (!el) {
      detourScrollListenerRef.current = null;
      return;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const x = el.scrollLeft;
        setDetourImmersive(prev => {
          if (!prev && x > 100) return true;
          if (prev && x < 50) return false;
          return prev;
        });
        ticking = false;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    detourScrollListenerRef.current = { el, handler: onScroll };
  }, []);
  // Identity pick feedback — thumbs up/down per destination on the Identity Card modal.
  // Shape: { [destName]: { value: "up" | "down", at: ISOdate } }. localStorage-persisted
  // for now (analytics signal collected client-side). When backend endpoint exists,
  // this can be sent to /api/identity-feedback for aggregated insights.
  const [identityPickFeedback, setIdentityPickFeedback] = useState(() => {
    try {
      const saved = localStorage.getItem("jga-identity-pick-feedback");
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      // Don't show if already onboarded, identity saved, OR returning user (has saved account)
      const onboarded = localStorage.getItem("jga-onboarded");
      // If we're arriving via an email-verification or password-reset link,
      // suppress the quiz immediately. localStorage is empty in this browser
      // but the user almost certainly has an identity on their backend account.
      // The verify/reset flow will complete and hydrate identity from the
      // server. Without this guard, the user gets re-quizzed in the verifying
      // browser (the classic "clicked verify in Safari but did quiz in Chrome"
      // case).
      if (typeof window !== "undefined") {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get("verify") || params.get("reset")) return false;
        } catch {}
      }
      const hasIdentity = localStorage.getItem("jga-traveler-type");
      const savedUser = localStorage.getItem("jga-user");
      return !onboarded && !hasIdentity && !savedUser;
    } catch { return true; }
  });
  const [onboardingRetake, setOnboardingRetake] = useState(false);
  // Stamps history seeding flow — shown post-quiz, pre-dashboard for first-time
  // quiz completions only. Skip is always available (intro + mid-session).
  const [stampsSeedingState, setStampsSeedingState] = useState(null); // null | "someday" | "visited"
  const [postcardsSeedingState, setPostcardsSeedingState] = useState(null); // null | "dreaming" | "visited"
  // Bumps every time stamps or postcards persist from outside AccountModal
  // (e.g., the seeding modals). AccountModal watches this and re-reads its
  // local stampsData / postcardsData state so the Journal grid stays in sync
  // without requiring the user to navigate away and back.
  const [journalRefreshTick, setJournalRefreshTick] = useState(0);
  const [sampleTripDay, setSampleTripDay] = useState(null); // null | 1 | 2 | 3 | "budget" | "journal"
  const [travelerType, setTravelerType] = useState(() => {
    try { return localStorage.getItem("jga-traveler-type") || null; } catch { return null; }
  });
  const [travelCompanion, setTravelCompanion] = useState(() => {
    try { return localStorage.getItem("jga-travel-companion") || null; } catch { return null; }
  });
  // Persist travelCompanion
  useEffect(() => {
    if (travelCompanion) {
      try { localStorage.setItem("jga-travel-companion", travelCompanion); } catch {}
    }
  }, [travelCompanion]);

  // ── Phase 2: identity-update nudge for sustained companion changes ──
  // When a user changes their Style chip and that change persists for 2+ days,
  // we soft-prompt them to update their travel identity. The nudge is
  // dismissable for 30 days. Skipped entirely for non-dramatic transitions
  // (Solo↔Couple has too-similar discovery profiles to bother nudging about).
  const [showCompanionIdentityNudge, setShowCompanionIdentityNudge] = useState(false);
  const [pendingCompanionChange, setPendingCompanionChange] = useState(null); // { from, to } | null

  // Sync companion change to backend (POST /api/users/companion). Backend
  // tracks companion_changed_at + companion_changed_from server-side and
  // detects "revert" — if user changed back to the previous value, all
  // tracking clears. This keeps the 2-day nudge timer cross-device consistent.
  //
  // Returns { ok, reverted, unchanged } so the caller can react to revert
  // by clearing local tracking state immediately (without waiting for next
  // hydration). Falls back gracefully on network failure — the localStorage
  // tracking still works; backend will catch up next time the user is online.
  async function syncCompanionToBackend(newCompanion) {
    let token = null;
    try { token = localStorage.getItem("jga_token"); } catch {}
    if (!token) return { ok: false, offline: true };
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const r = await fetch(`${API}/api/users/companion`, {
        method: "POST",
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ travelCompanion: newCompanion }),
      });
      if (r.status === 401) {
        if (typeof _signalSessionExpired === "function") _signalSessionExpired("companion-sync");
        return { ok: false, expired: true };
      }
      if (!r.ok) {
        console.warn("[JGA Companion Sync] Backend rejected", r.status);
        return { ok: false };
      }
      const data = await r.json().catch(() => ({}));
      console.log("[JGA Companion Sync] ✓", data);
      // Handle revert — backend says user reverted to previous value, so we
      // clear local tracking immediately. The 2-day nudge effect won't fire.
      if (data.reverted) {
        try {
          localStorage.removeItem("jga-companion-changed-at");
          localStorage.removeItem("jga-companion-nudge-dismissed-at");
        } catch {}
        setPendingCompanionChange(null);
        setShowCompanionIdentityNudge(false);
      }
      return { ok: true, reverted: !!data.reverted, unchanged: !!data.unchanged };
    } catch (err) {
      console.warn("[JGA Companion Sync] Network error", err);
      return { ok: false };
    }
  }

  // Sync nudge dismissal to backend so 30-day silence is cross-device.
  async function syncNudgeDismissedToBackend() {
    let token = null;
    try { token = localStorage.getItem("jga_token"); } catch {}
    if (!token) return;
    try {
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      const r = await fetch(`${API}/api/users/companion-nudge-dismissed`, {
        method: "POST",
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (r.status === 401) {
        if (typeof _signalSessionExpired === "function") _signalSessionExpired("nudge-dismiss-sync");
        return;
      }
      if (!r.ok) console.warn("[JGA Nudge Dismiss Sync] Backend rejected", r.status);
    } catch (err) {
      console.warn("[JGA Nudge Dismiss Sync] Network error", err);
    }
  }

  // Check on mount: was there a recent companion change worth nudging about?
  useEffect(() => {
    if (!travelerType) return; // user has no identity yet (didn't take quiz)
    let raw, dismissed;
    try {
      raw = localStorage.getItem("jga-companion-changed-at");
      dismissed = localStorage.getItem("jga-companion-nudge-dismissed-at");
    } catch { return; }
    if (!raw) return;

    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (!parsed?.at || !parsed?.to) return;

    // Threshold: 2 days = 172,800,000 ms
    const TWO_DAYS = 2 * 86400000;
    const elapsed = Date.now() - parsed.at;
    if (elapsed < TWO_DAYS) return; // not yet — change is too fresh

    // Check dismissed-recently: 30 days silence after a "Keep current" click
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < 30 * 86400000) return;
    }

    // Skip non-dramatic transitions. Solo↔Couple has overlapping discovery
    // profiles (both adults, similar destinations) so identity rewrite isn't
    // worth nudging about. Same companion = no-op (shouldn't happen but safe).
    const NON_DRAMATIC = new Set(["solo|couple", "couple|solo"]);
    const pairKey = `${parsed.from}|${parsed.to}`;
    if (NON_DRAMATIC.has(pairKey)) return;
    if (parsed.from === parsed.to) return;
    if (!parsed.from) return; // first-time set, no "change" to nudge about

    // Confirm current travelCompanion still matches the recorded "to" — if the
    // user changed their mind and switched back, skip the nudge.
    if (travelCompanion !== parsed.to) return;

    setPendingCompanionChange({ from: parsed.from, to: parsed.to });
    setShowCompanionIdentityNudge(true);
    // Note: we don't clear jga-companion-changed-at here. It clears when the
    // user either accepts the nudge (setShowOnboarding triggers retake) or
    // dismisses it (the dismissed-at timestamp is set, silencing for 30 days).
  }, [travelerType, travelCompanion]);
  const [showStarredPlan, setShowStarredPlan] = useState(false);
  const [showStatBreakdown, setShowStatBreakdown] = useState(null); // null | "trips" | "countries" | "cities" | "days"
  const [quickBookDest, setQuickBookDest] = useState(null); // { dest, accent, label } | null — for dashboard card → quick book flow
  const [dashboardBudget, setDashboardBudget] = useState(() => {
    try { return localStorage.getItem("jga-dashboard-budget") || ""; } catch(e) { return ""; }
  });
  const [showBudgetEdit, setShowBudgetEdit] = useState(false);
  const [showCompanionEdit, setShowCompanionEdit] = useState(false);
  const [showRegionEdit, setShowRegionEdit] = useState(false);
  // Postcards / Stamps collapse state. Default-collapsed per Cas's request to
  // reduce dashboard noise and lean harder into identity-driven discovery.
  // Same localStorage key pattern as CollapsibleSection (`jga-collapse-${id}`)
  // so collapse state is unified across the dashboard.
  const [postcardsOpen, setPostcardsOpen] = useState(() => {
    try { return localStorage.getItem("jga-collapse-postcards") === "open"; } catch { return false; }
  });
  const [stampsOpen, setStampsOpen] = useState(() => {
    try { return localStorage.getItem("jga-collapse-stamps") === "open"; } catch { return false; }
  });
  const [moodMatchOpen, setMoodMatchOpen] = useState(() => {
    try { return localStorage.getItem("jga-collapse-moodmatch") === "open"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-collapse-postcards", postcardsOpen ? "open" : "closed"); } catch {}
  }, [postcardsOpen]);
  useEffect(() => {
    try { localStorage.setItem("jga-collapse-stamps", stampsOpen ? "open" : "closed"); } catch {}
  }, [stampsOpen]);
  useEffect(() => {
    try { localStorage.setItem("jga-collapse-moodmatch", moodMatchOpen ? "open" : "closed"); } catch {}
  }, [moodMatchOpen]);
  // Preferred Region — user's persistent default discovery region. Used to
  // seed expRegion and blindRegion at startup so flows default to the
  // user's preferred area instead of "Anywhere". User can still override
  // per-session via the per-mode region picker. Persisted to localStorage.
  const [preferredRegion, setPreferredRegion] = useState(() => {
    try { return localStorage.getItem("jga-preferred-region") || "Anywhere"; } catch { return "Anywhere"; }
  });
  // Sync preferredRegion → localStorage and into the active session regions.
  // We only push to expRegion/blindRegion when they're at their default
  // "Anywhere" state, so we don't overwrite a user mid-flow.
  useEffect(() => {
    try { localStorage.setItem("jga-preferred-region", preferredRegion); } catch {}
  }, [preferredRegion]);

  // Setup-completion flags — track whether the user has explicitly engaged
  // with Budget and Region. These have defaults ($2000, "Anywhere") so they're
  // never technically "empty," but the new dashboard empty-state UX treats
  // "user has opened the edit panel" as confirmation that they've seen and
  // accepted the value. Style + Airport are tracked implicitly (truly empty
  // until user picks a value).
  const [budgetConfirmed, setBudgetConfirmed] = useState(() => {
    try { return localStorage.getItem("jga-budget-confirmed") === "true"; } catch { return false; }
  });
  const [regionConfirmed, setRegionConfirmed] = useState(() => {
    try { return localStorage.getItem("jga-region-confirmed") === "true"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-budget-confirmed", budgetConfirmed ? "true" : "false"); } catch {}
  }, [budgetConfirmed]);
  useEffect(() => {
    try { localStorage.setItem("jga-region-confirmed", regionConfirmed ? "true" : "false"); } catch {}
  }, [regionConfirmed]);
  const [airportDetectStatus, setAirportDetectStatus] = useState(null); // null | "detecting" | "success" | "error"
  const [airportDetectError, setAirportDetectError] = useState("");
  const [showManualAirport, setShowManualAirport] = useState(false);
  const [manualAirportInput, setManualAirportInput] = useState("");
  // Persist dashboard budget changes
  useEffect(() => {
    if (dashboardBudget) {
      try { localStorage.setItem("jga-dashboard-budget", dashboardBudget); } catch {}
    }
  }, [dashboardBudget]);
  // ── Mood Boards (Pinterest-style) ─────────────────────────────────
  // boards = primary state. Each board has its own color/iconId/items.
  // starredAlerts is derived (union of items across all boards) so the
  // existing watchlist/dashboard logic keeps working unchanged.
  // Boards now use iconId (referencing BOARD_ICONS) instead of emoji.
  // Legacy boards with emoji are migrated automatically on first load.
  const DEFAULT_BOARDS = makeStarterBoards();
  const [boards, setBoards] = useState(() => {
    // Migration to iconId model: any board with `emoji` and no `iconId` gets
    // its emoji translated via EMOJI_TO_ICON_ID. Strips the emoji field after
    // migration. User-customized emojis that aren't in the map fall back to
    // "sparkle". This is a one-way migration — once a board has iconId, it
    // never goes back to emoji.
    const migrateToIconId = (list) => list.map(b => {
      if (b.iconId) return b; // already migrated
      const iconId = b.emoji ? (EMOJI_TO_ICON_ID[b.emoji] || "sparkle") : "sparkle";
      const { emoji, ...rest } = b; // drop emoji field
      return { ...rest, iconId };
    });
    try {
      const stored = JSON.parse(localStorage.getItem("jga-boards") || "null");
      if (Array.isArray(stored) && stored.length > 0) return migrateToIconId(stored);
    } catch {}
    // Migrate from legacy flat starredAlerts if present
    try {
      const legacy = JSON.parse(localStorage.getItem("jga-starred-alerts") || "null");
      if (Array.isArray(legacy) && legacy.length > 0) {
        const migrated = [...DEFAULT_BOARDS];
        migrated[0] = { ...migrated[0], items: legacy };
        return migrated;
      }
    } catch {}
    return DEFAULT_BOARDS;
  });
  // Persist boards
  useEffect(() => {
    try { localStorage.setItem("jga-boards", JSON.stringify(boards)); } catch {}
  }, [boards]);

  // ── Boards backend wiring (rewritten) ──────────────────────────
  // Explicit action functions (createBoard, deleteBoard, etc) replace the
  // fragile diff-based sync. Every action does optimistic local update +
  // backend call. Hydration on session start fetches authoritative list and
  // pushes any local-only boards (defaults) up to the backend.
  const tokenRef = useRef(null);
  useEffect(() => {
    try { tokenRef.current = localStorage.getItem("jga_token"); } catch { tokenRef.current = null; }
  });

  // Hydration — runs once per registered session, re-runs when token is refreshed
  // (so data loaders that bailed during grace-period 401s can re-fetch with the fresh token)
  const hydrationRanRef = useRef(false);
  useEffect(() => {
    if (!registered) { hydrationRanRef.current = false; return; }
    // Re-hydrate when token refreshes — hydrationTrigger > 0 means we just got a fresh token
    if (hydrationRanRef.current && hydrationTrigger === 0) return;
    hydrationRanRef.current = true;

    let cancelled = false;
    (async () => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) {
        console.warn("[JGA Boards Hydrate] No auth token — skipping");
        return;
      }
      console.log("[JGA Boards Hydrate] Starting…");

      const { ok, items } = await loadBoardsFromBackend(token);
      if (cancelled) return;
      if (!ok) {
        console.warn("[JGA Boards Hydrate] Load failed — keeping local copy");
        return;
      }
      console.log("[JGA Boards Hydrate] Backend returned", items.length, "boards");

      // Normalize backend shape → local shape. Backend stores iconId in
      // the `emoji` field (legacy schema). If the value matches a known
      // icon id, use it directly; otherwise treat as emoji and migrate.
      // Mark _backendConfirmed: true so we know this board has been on
      // backend at some point — used by the cross-device delete fix below.
      const normalized = items.map(srv => {
        const stored = srv.iconId || srv.emoji || "";
        const iconId = BOARD_ICONS.some(i => i.id === stored)
          ? stored
          : (EMOJI_TO_ICON_ID[stored] || "sparkle");
        return {
          id: srv.id,
          name: srv.name || "Untitled",
          iconId,
          color: srv.color || "#4A8CC4",
          items: Array.isArray(srv.items) ? srv.items : Array.isArray(srv.destinations) ? srv.destinations : [],
          createdAt: srv.created_at ? new Date(srv.created_at).getTime() : (srv.createdAt || Date.now()),
          _backendConfirmed: true,
        };
      });

      // Get current local state synchronously by reading from localStorage
      // (avoids closure-stale-data problems with reading boards from React state)
      const currentLocal = (() => {
        try { return JSON.parse(localStorage.getItem("jga-boards") || "[]") || []; } catch { return []; }
      })();

      const backendIds = new Set(normalized.map(b => b.id));
      // Identify "seed defaults" — boards from the DEFAULT_BOARDS constant
      // that haven't been touched on this device yet. These have known string
      // IDs ("board-2026", "board-bucket", "board-dream") and were generated
      // locally only because the user hadn't connected to the backend yet.
      const SEED_DEFAULT_IDS = new Set(["board-2026", "board-bucket", "board-dream"]);
      const isSeedDefault = (b) => SEED_DEFAULT_IDS.has(b.id);
      const backendHasBoards = normalized.length > 0;

      // ── Initialization flag — strongest defense against re-seed-after-delete ──
      // Once a user has been initialized (account has ever had boards on backend),
      // we should NEVER push local boards up again. The user may have legitimately
      // deleted everything, or boards may have been deleted on another device.
      // Re-pushing creates ghost boards that the user already explicitly removed.
      //
      // Set when: backend returns ≥1 board on hydration (user is initialized)
      // Persisted: localStorage["jga-boards-initialized"] = "true"
      // Read: every hydration checks this flag before considering push-up
      let boardsInitialized = false;
      try { boardsInitialized = localStorage.getItem("jga-boards-initialized") === "true"; } catch {}
      if (backendHasBoards && !boardsInitialized) {
        try { localStorage.setItem("jga-boards-initialized", "true"); } catch {}
        boardsInitialized = true;
        console.log("[JGA Boards Hydrate] Marking account as initialized — future hydrations will not re-seed");
      }

      // Cross-device delete handling: if a local board was previously confirmed
      // on backend (_backendConfirmed: true) but now ISN'T in the hydration
      // response, the user deleted it on another device. We must NOT push it
      // back up — instead drop it from local state entirely.
      const droppedAsDeleted = currentLocal.filter(b =>
        b._backendConfirmed && !backendIds.has(b.id)
      );
      if (droppedAsDeleted.length > 0) {
        console.log(
          "[JGA Boards Hydrate] Dropping",
          droppedAsDeleted.length,
          "boards deleted on another device:",
          droppedAsDeleted.map(b => `${b.name} (id:${b.id})`).join(", ")
        );
      }

      // Push-up logic:
      //   1. NEW STRONGEST RULE: if account is already initialized
      //      (boardsInitialized = true), NEVER push anything up. Backend is
      //      authoritative. User deletions are respected.
      //   2. If backend has boards but account wasn't yet flagged initialized
      //      (legacy users mid-migration): push only true local-only boards
      //      that are not seed defaults and haven't been backend-confirmed.
      //   3. If backend is empty AND account is uninitialized (first-ever
      //      sign-in for a legacy user with no backend boards yet): push
      //      everything up so they have starting state.
      const localOnly = boardsInitialized
        ? [] // Initialized accounts: never re-push, even if user deleted everything
        : currentLocal.filter(b => {
            if (backendIds.has(b.id)) return false; // already on backend
            if (backendHasBoards && isSeedDefault(b)) return false; // skip seed defaults — backend has its own
            if (b._backendConfirmed) return false; // deleted on another device — don't recreate
            return true;
          });

      console.log(
        "[JGA Boards Hydrate]", normalized.length, "from backend,",
        localOnly.length, "local-only to push up",
        backendHasBoards ? "(skipping seed defaults — backend already initialized)" : "(legacy user — pushing all locals up)"
      );

      // For each local-only board, create it on backend then push items
      const remappedLocals = [];
      for (const local of localOnly) {
        const result = await createBoardOnBackend(
          { name: local.name, emoji: local.iconId || "sparkle", color: local.color },
          token
        );
        if (cancelled) return;
        if (result.ok && result.id != null) {
          // Push items
          for (const destName of local.items || []) {
            await addItemToBoardOnBackend(result.id, destName, token);
            if (cancelled) return;
          }
          remappedLocals.push({ ...local, id: result.id });
        } else {
          // Backend rejected the create — keep the local copy as-is
          // (will retry next session)
          remappedLocals.push(local);
        }
      }

      if (cancelled) return;
      // Final state = backend-authoritative boards + any locals we pushed up
      // (with their new server-assigned IDs). Local seed-defaults that were
      // skipped don't appear here — that's correct, the backend versions
      // shown via `normalized` replace them.
      const finalBoards = [...normalized, ...remappedLocals];
      setBoards(finalBoards);
      try { localStorage.setItem("jga-boards", JSON.stringify(finalBoards)); } catch {}
      console.log("[JGA Boards Hydrate] ✓ Complete. Final count:", finalBoards.length);
    })();
    return () => { cancelled = true; };
  }, [registered, hydrationTrigger]); // eslint-disable-line

  // ── Action wrappers — explicit backend calls + local update ──
  // These get passed down to BoardsTabContent and BoardPickerModal,
  // replacing direct setBoards calls at the action sites.

  const boardsApi = useMemo(() => ({
    // Create a new board
    createBoard: async ({ name, iconId, color }) => {
      const trimmedName = (name || "").trim().slice(0, 40);
      if (!trimmedName) return { ok: false };
      const finalIconId = iconId || "sparkle";
      // Optimistic — temp id, marked pending
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      const optimistic = { id: tempId, name: trimmedName, iconId: finalIconId, color, items: [], createdAt: Date.now(), pendingSync: true };
      setBoards(prev => [...prev, optimistic]);
      // Fire to backend. Backend accepts an `emoji` string field — we send
      // the iconId there until backend is updated to accept `iconId`.
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) {
        console.warn("[JGA Boards Create] No auth — saved locally only");
        return { ok: false, id: tempId };
      }
      const result = await createBoardOnBackend({ name: trimmedName, emoji: finalIconId, color }, token);
      if (result.ok && result.id != null) {
        // Mark _backendConfirmed: true so a future hydration knows this board
        // has been seen on backend. If it later disappears from the backend
        // response (deleted on another device), the hydration filter drops it
        // instead of re-pushing it up (the "Girls Trip kept reappearing" bug).
        setBoards(prev => prev.map(b => b.id === tempId ? { ...b, id: result.id, pendingSync: false, _backendConfirmed: true } : b));
        return { ok: true, id: result.id };
      }
      // Keep optimistic entry but flagged pending
      return { ok: false, id: tempId };
    },

    // Delete a board
    deleteBoard: async (id) => {
      // Comprehensive logging: backend chat reported zero DELETE attempts
      // ever reaching the API for Cas's account, despite the React state
      // update working. This tracing exposes exactly where execution stops.
      console.log("[JGA Boards Delete] HANDLER ENTRY — id:", id, "type:", typeof id);

      // Snapshot the board BEFORE removing — used to revert if auth check fails
      // below. Previously the handler silently removed the board locally even
      // when no backend write would happen (e.g., expired session) — leaving
      // the user thinking the delete worked. Now we revert and surface an
      // error so behavior matches reality.
      let snapshot = null;
      setBoards(prev => {
        const found = prev.find(b => b.id === id);
        snapshot = found || null;
        console.log("[JGA Boards Delete] Local state had board?", !!found, found ? `(name: ${found.name})` : "");
        return prev.filter(b => b.id !== id);
      });

      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      console.log("[JGA Boards Delete] Token present?", !!token, "registered?", registered);
      if (!token || !registered) {
        console.warn("[JGA Boards Delete] EARLY RETURN — missing token or not registered. Reverting local removal.");
        // Revert the optimistic removal — the board is still on backend, so
        // showing it locally is the correct state. User will see session-
        // expired UI (already triggered by the 401 that cleared tokens).
        if (snapshot) {
          setBoards(prev => {
            // Defensive: only re-add if not already present
            if (prev.some(b => b.id === snapshot.id)) return prev;
            return [...prev, snapshot];
          });
        }
        return { ok: false, reason: "no_auth" };
      }
      // Don't fire delete for temp ids that never reached backend
      if (typeof id === "string" && id.startsWith("tmp-")) {
        console.log("[JGA Boards Delete] EARLY RETURN — tmp id, skipping backend delete:", id);
        return { ok: true };
      }
      console.log("[JGA Boards Delete] Calling deleteBoardOnBackend for id:", id);
      const result = await deleteBoardOnBackend(id, token);
      console.log("[JGA Boards Delete] deleteBoardOnBackend result:", result);
      // If backend rejected for any reason (other than already-deleted),
      // revert the optimistic removal. Backend is source of truth.
      if (!result.ok && snapshot) {
        console.warn("[JGA Boards Delete] Backend rejected — reverting local removal");
        setBoards(prev => {
          if (prev.some(b => b.id === snapshot.id)) return prev;
          return [...prev, snapshot];
        });
      }
      return result;
    },

    // Update name/iconId/color
    updateBoard: async (id, patch) => {
      setBoards(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) return { ok: false };
      if (typeof id === "string" && id.startsWith("tmp-")) return { ok: true };
      // Translate iconId → emoji field for backend (which uses legacy schema)
      const backendPatch = { ...patch };
      if (backendPatch.iconId !== undefined) {
        backendPatch.emoji = backendPatch.iconId;
        delete backendPatch.iconId;
      }
      return updateBoardOnBackend(id, backendPatch, token);
    },

    // Add a destination to a board
    addItem: async (id, destName) => {
      setBoards(prev => prev.map(b => b.id === id ? { ...b, items: (b.items || []).includes(destName) ? b.items : [...(b.items || []), destName] } : b));
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) return { ok: false };
      if (typeof id === "string" && id.startsWith("tmp-")) return { ok: true };
      return addItemToBoardOnBackend(id, destName, token);
    },

    // Remove a destination from a board
    removeItem: async (id, destName) => {
      setBoards(prev => prev.map(b => b.id === id ? { ...b, items: (b.items || []).filter(n => n !== destName) } : b));
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) return { ok: false };
      if (typeof id === "string" && id.startsWith("tmp-")) return { ok: true };
      return removeItemFromBoardOnBackend(id, destName, token);
    },

    // Bulk apply selection from BoardPickerModal — handles add+remove for one destName
    applySelection: async (destName, selectedBoardIds) => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      const selected = new Set(selectedBoardIds);
      // CRITICAL: Compute adds/removes from CURRENT boards prop (closure value) BEFORE
      // calling setBoards. Reading state from inside a setBoards updater and using
      // it for side effects on the next line is a race — React updaters are async
      // and may run multiple times. The original implementation had this bug, which
      // caused live saves to never reach the backend (adds/removes arrays were empty
      // by the time the for-loop ran). This version is deterministic.
      const adds = [];
      const removes = [];
      for (const b of boards) {
        const shouldHave = selected.has(b.id);
        const has = (b.items || []).includes(destName);
        if (shouldHave && !has) adds.push(b.id);
        else if (!shouldHave && has) removes.push(b.id);
      }
      // Optimistic local update — single, deterministic pass
      setBoards(prev => prev.map(b => {
        const shouldHave = selected.has(b.id);
        const has = (b.items || []).includes(destName);
        if (shouldHave && !has) return { ...b, items: [...(b.items || []), destName] };
        if (!shouldHave && has) return { ...b, items: (b.items || []).filter(n => n !== destName) };
        return b;
      }));
      // Sidecar: track recent saves with timestamp for Zone 1 State 3 detection.
      // Items array stays as bare strings (~30 read sites depend on that shape).
      // This log is the source of truth for "saves in last 7 days" — items added
      // before this code shipped have no entry here and correctly skip detection.
      try {
        const log = JSON.parse(localStorage.getItem("jga-recent-saves-v1") || "[]");
        for (const id of adds) {
          const board = boards.find(b => b.id === id);
          if (!board) continue;
          log.unshift({
            destName,
            boardId: id,
            boardName: board.name,
            addedAt: new Date().toISOString(),
          });
        }
        localStorage.setItem("jga-recent-saves-v1", JSON.stringify(log.slice(0, 50)));
      } catch (e) {}
      console.log("[JGA Boards Apply] dest:", destName, "adds:", adds.length, "removes:", removes.length);
      if (!token || !registered) return;
      // Fire backend calls — now adds/removes are guaranteed populated
      for (const id of adds) {
        if (typeof id === "string" && id.startsWith("tmp-")) continue;
        addItemToBoardOnBackend(id, destName, token); // fire and forget
      }
      for (const id of removes) {
        if (typeof id === "string" && id.startsWith("tmp-")) continue;
        removeItemFromBoardOnBackend(id, destName, token);
      }
    },
  }), [registered, boards]); // eslint-disable-line
  // Derived: all unique destination names across all boards (drop-in for old starredAlerts)
  const starredAlerts = useMemo(() => {
    const set = new Set();
    boards.forEach(b => (b.items || []).forEach(name => set.add(name)));
    return Array.from(set);
  }, [boards]);
  // Backward-compat shim: setStarredAlerts mutations now route through boards
  const setStarredAlerts = useCallback((updater) => {
    setBoards(prev => {
      const currentDerived = (() => {
        const set = new Set();
        prev.forEach(b => (b.items || []).forEach(name => set.add(name)));
        return Array.from(set);
      })();
      const next = typeof updater === "function" ? updater(currentDerived) : updater;
      const nextSet = new Set(next);
      // Remove items that are no longer in the new derived list (across all boards)
      // Keep items that ARE in the new list in their existing boards
      // Add new items to the first (default) board
      const newlyAdded = next.filter(n => !currentDerived.includes(n));
      const updatedBoards = prev.map((b, idx) => {
        const filtered = (b.items || []).filter(name => nextSet.has(name));
        if (idx === 0 && newlyAdded.length > 0) {
          return { ...b, items: [...filtered, ...newlyAdded.filter(n => !filtered.includes(n))] };
        }
        return { ...b, items: filtered };
      });
      return updatedBoards;
    });
  }, []);

  // Board picker modal state — opened when ☆ is tapped on any destination
  const [boardPickerDest, setBoardPickerDest] = useState(null); // string | null (destination name)
  // When true, hide the Dreaming/Visited postcard collect section in the
  // modal. Used by star buttons in Most Affordable / Trending Trips where
  // the user intent is "save an alert / wishlist" not "collect a postcard".
  const [boardPickerHidePostcard, setBoardPickerHidePostcard] = useState(false);

  // ── Swipe Deck state ──────────────────────────────────────────
  // skips: { [destName]: timestamp } — destinations the user has left-swiped on
  // Excluded from the deck for 30 days. Persisted to localStorage.
  const [swipeSkips, setSwipeSkips] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-swipe-skips") || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-swipe-skips", JSON.stringify(swipeSkips)); } catch {}
  }, [swipeSkips]);

  // stampSkips: { [stampId]: timestamp } — stamps the user has dismissed (X button)
  // Filtered out of the stamps row so they don't reappear. Persisted to localStorage.
  // Mirrors swipeSkips pattern for consistency.
  const [stampSkips, setStampSkips] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-stamp-skips") || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-stamp-skips", JSON.stringify(stampSkips)); } catch {}
  }, [stampSkips]);

  // ── Backend sync for swipe skips (cross-device) ─────────────────────
  // Hydrate from backend on registered-state transition (login). Merge with
  // local — don't overwrite — so skips from another device are added without
  // losing local skips made while offline.
  //
  // When backend endpoint isn't shipped yet (404), this gracefully degrades:
  // hydration silently fails and localStorage remains the source of truth.
  useEffect(() => {
    if (!registered) return;
    let cancelled = false;
    (async () => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      const { ok, skips: serverSkips } = await loadSwipeSkipsFromBackend(token);
      if (cancelled || !ok) return;
      // Merge: per-destination, keep newer timestamp. Important — never blindly
      // overwrite, or device A's skips made while device B was offline get lost.
      setSwipeSkips(prev => mergeSwipeSkips(prev, serverSkips));
    })();
    return () => { cancelled = true; };
  }, [registered]); // eslint-disable-line

  // Debounced write-through to backend on every swipeSkips change. 800ms gives
  // a rapid skip sequence (3-4 in a row) time to batch into one network call.
  // Empty skip state on first mount won't write (avoids overwriting backend
  // with empty before hydration completes).
  const swipeSkipsBackendSyncedRef = useRef(false);
  useEffect(() => {
    if (!registered) return;
    // Don't write until after first hydration attempt — prevents the initial
    // empty-state write from clobbering backend data before we've merged it in.
    if (!swipeSkipsBackendSyncedRef.current) {
      swipeSkipsBackendSyncedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      saveSwipeSkipsToBackend(swipeSkips, token);
    }, 800);
    return () => clearTimeout(handle);
  }, [swipeSkips, registered]);

  // ── User's Stamps collection (state declarations) ──
  // Map of stampId → { state: "someday"|"visited", createdAt, updatedAt, ... }.
  // Declared HERE (above the backend-sync effects below) so the effects can
  // reference it without hitting the temporal dead zone. See § STAMPS
  // comment block near top of file for the spec.
  const [userStamps, setUserStamps] = useState(() => getStamps());
  // Per-session shuffled order of suggested stamps. Initialized once on mount;
  // does not reshuffle when user saves a stamp (that would feel chaotic). Stable
  // for the session, fresh on page reload.
  const [shuffledStampOrder] = useState(() => shuffleArray(SUGGESTED_STAMPS));
  // First-save toast — appears once per session as a discoverability nudge.
  // Resets on page reload (no persistence — intentional).
  const [showStampToast, setShowStampToast] = useState(false);
  const stampToastShownRef = useRef(false);

  // ── User's Postcards collection (state declaration) ──
  // Map of postcardId → { state: "dreaming"|"visited", createdAt, updatedAt, ... }.
  // Lives at the main App level so:
  //   - the dashboard Postcard Wall can read collected entries
  //   - BoardPickerModal can call setPostcardsData on collect (via prop)
  //   - the backend-sync effects below can watch this state for changes
  // AccountModal has its own local mirror (initialized from getPostcards())
  // that re-syncs from localStorage when the modal opens — see L5816.
  const [postcardsData, setPostcardsData] = useState(() => {
    try { return getPostcards(); } catch { return {}; }
  });

  // ── Stamps + Postcards backend sync ──────────────────────────────
  // Mirror the swipe-skips pattern: load on auth, debounce save on change,
  // merge with union strategy so device collections accumulate rather than
  // overwrite each other. Endpoints don't exist yet on backend — calls
  // degrade silently to 404 and the feature continues to work as
  // localStorage-only. The moment backend ships /api/stamps and /api/
  // postcards, cross-device sync activates automatically with no frontend
  // change needed.

  // Load stamps from backend on auth, merge with local. Also writes the
  // merged result to localStorage so any code path that reads localStorage
  // directly (e.g. PostcardIllustration helpers) sees the same data.
  useEffect(() => {
    if (!registered) return;
    let cancelled = false;
    (async () => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      const { ok, stamps: serverStamps } = await loadStampsFromBackend(token);
      if (cancelled || !ok) return;
      setUserStamps(prev => {
        const merged = mergeStamps(prev, serverStamps);
        try { localStorage.setItem(STAMP_STORAGE_KEY, JSON.stringify(merged)); } catch {}
        return merged;
      });
    })();
    return () => { cancelled = true; };
  }, [registered]); // eslint-disable-line

  const stampsBackendSyncedRef = useRef(false);
  useEffect(() => {
    if (!registered) return;
    if (!stampsBackendSyncedRef.current) {
      stampsBackendSyncedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      saveStampsToBackend(userStamps, token);
    }, 800);
    return () => clearTimeout(handle);
  }, [userStamps, registered]);

  // Postcards — same shape as Stamps.
  useEffect(() => {
    if (!registered) return;
    let cancelled = false;
    (async () => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      const { ok, postcards: serverPostcards } = await loadPostcardsFromBackend(token);
      if (cancelled || !ok) return;
      setPostcardsData(prev => {
        const merged = mergePostcards(prev, serverPostcards);
        try { localStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(merged)); } catch {}
        return merged;
      });
    })();
    return () => { cancelled = true; };
  }, [registered]); // eslint-disable-line

  const postcardsBackendSyncedRef = useRef(false);
  useEffect(() => {
    if (!registered) return;
    if (!postcardsBackendSyncedRef.current) {
      postcardsBackendSyncedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      savePostcardsToBackend(postcardsData, token);
    }, 800);
    return () => clearTimeout(handle);
  }, [postcardsData, registered]);

  // ── Hydrate stamps + postcards on tab focus / BFCache restore ─────
  // Critical for cross-device sync. The load-on-auth effects above only
  // fire when `registered` flips false→true (login). If user is already
  // logged in on both desktop and mobile, collecting Paris on desktop
  // won't appear on mobile until mobile re-logs-in — UNLESS we also
  // re-fetch when mobile's tab regains focus.
  //
  // Mirrors the existing /me visibility-change listener (~L17520).
  // visibilitychange = tab/app switch. pageshow with persisted=true =
  // Safari BFCache restore (back/forward navigation).
  //
  // Same merge strategy as load-on-auth: union with timestamp tiebreak.
  // Local optimistic writes (collected just now, not yet POSTed) are
  // preserved because their updatedAt is newer than anything the server
  // could send back.
  useEffect(() => {
    if (!registered) return;
    async function rehydrate(reason) {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) return;
      console.log("[JGA Collections Rehydrate]", reason);
      // Fire both in parallel — they're independent endpoints.
      const [stampsRes, postcardsRes] = await Promise.all([
        loadStampsFromBackend(token),
        loadPostcardsFromBackend(token),
      ]);
      if (stampsRes.ok) {
        setUserStamps(prev => {
          const merged = mergeStamps(prev, stampsRes.stamps);
          try { localStorage.setItem(STAMP_STORAGE_KEY, JSON.stringify(merged)); } catch {}
          return merged;
        });
      }
      if (postcardsRes.ok) {
        setPostcardsData(prev => {
          const merged = mergePostcards(prev, postcardsRes.postcards);
          try { localStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(merged)); } catch {}
          return merged;
        });
      }
    }
    function onVisibilityChange() {
      if (document.visibilityState === "visible") rehydrate("visibilitychange");
    }
    function onPageShow(e) {
      if (e.persisted) rehydrate("pageshow-bfcache");
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [registered]); // eslint-disable-line
  const [swipeInfoDest, setSwipeInfoDest] = useState(null); // destination object | null

  // Auto-rotating identity teaser card index for the unauthenticated landing dashboard.
  // Cycles every 2.8s through a sample of the 20 travel identities.
  const [identityTeaserIdx, setIdentityTeaserIdx] = useState(0);
  useEffect(() => {
    if (registered) return; // Only rotates while user is in the unauthenticated state
    const t = setInterval(() => setIdentityTeaserIdx(i => (i + 1) % 6), 2800);
    return () => clearInterval(t);
  }, [registered]);
  const [userMood, setUserMood] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-user-mood") || "null"); } catch { return null; }
  });
  useEffect(() => { try { localStorage.setItem("jga-user-mood", JSON.stringify(userMood)); } catch {} }, [userMood]);
  const [showMoodQuiz, setShowMoodQuiz] = useState(false);
  // Tracks when the user dismissed the "still feel this way?" nudge so it stays hidden until next time
  const [moodNudgeDismissedAt, setMoodNudgeDismissedAt] = useState(() => {
    try { return localStorage.getItem("jga-mood-nudge-dismissed") || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      if (moodNudgeDismissedAt) localStorage.setItem("jga-mood-nudge-dismissed", moodNudgeDismissedAt);
      else localStorage.removeItem("jga-mood-nudge-dismissed");
    } catch {}
  }, [moodNudgeDismissedAt]);
  const [tripAlerts, setTripAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-trip-alerts") || "{}"); } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem("jga-trip-alerts", JSON.stringify(tripAlerts)); } catch {} }, [tripAlerts]);
  const [showAlertModal, setShowAlertModal] = useState(null); // holds destination obj when modal open
  const [showIdleBanner, setShowIdleBanner] = useState(false);
  // "Almost there" banner — appears on the homepage for unregistered users who
  // completed the quiz/onboarding flow (jga-onboarded === true) and clicked
  // "Continue browsing" instead of signing up. Reminds them registration is
  // available so they don't get lost in the homepage feature wall.
  // Dismissal is session-scoped (not persisted permanently) — if they close
  // and reopen the site without registering, the banner returns. This is
  // intentional: persistent dismissal would mean a permanent escape hatch
  // away from the conversion lever, but we shouldn't nag mid-session if
  // they actively dismiss it.
  const [showAlmostThereBanner, setShowAlmostThereBanner] = useState(() => {
    try {
      if (sessionStorage.getItem("jga-almost-there-dismissed") === "true") return false;
      return localStorage.getItem("jga-onboarded") === "true";
    } catch { return false; }
  });
  const [bannerCollapsed, setBannerCollapsed] = useState(() => {
    try { return localStorage.getItem("jga-banner-collapsed") === "true"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("jga-banner-collapsed", String(bannerCollapsed)); } catch {} }, [bannerCollapsed]);
  const [showDestPreview, setShowDestPreview] = useState(false);
  const [destPreviewDismissed, setDestPreviewDismissed] = useState(false);
  const [destPreviewIdx, setDestPreviewIdx] = useState(0);
  const destPreviewTimer = useRef(null);
  const destCycleTimer = useRef(null);
  const idleTimer = useRef(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyTrip, setSurveyTrip] = useState(null);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  // Past trips — pre-seeded with sample data for logged-in users; grows as trips complete
  // Journal data persists across modal closes & page refreshes via localStorage
  const [journalEntries, setJournalEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-journal-entries") || "{}"); } catch { return {}; }
  });
  const [journalEditing, setJournalEditing] = useState(null);
  const [journalDraft, setJournalDraft] = useState("");
  const [journalPhotos, setJournalPhotos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-journal-photos") || "{}"); } catch { return {}; }
  });
  const [journalRatings, setJournalRatings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-journal-ratings") || "{}"); } catch { return {}; }
  }); // { [code]: { overall, people, vibe, food, value } }
  const [journalMeta, setJournalMeta] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jga-journal-meta") || "{}"); } catch { return {}; }
  }); // { [code]: { budget, travelers } }
  const [journalRatingDraft, setJournalRatingDraft] = useState({});
  const [journalMetaDraft, setJournalMetaDraft] = useState({});

  // Persist journal data so entries survive page refreshes & browser sessions
  useEffect(() => { try { localStorage.setItem("jga-journal-entries", JSON.stringify(journalEntries)); } catch {} }, [journalEntries]);
  useEffect(() => { try { localStorage.setItem("jga-journal-photos", JSON.stringify(journalPhotos)); } catch {} }, [journalPhotos]);
  useEffect(() => { try { localStorage.setItem("jga-journal-ratings", JSON.stringify(journalRatings)); } catch {} }, [journalRatings]);
  useEffect(() => { try { localStorage.setItem("jga-journal-meta", JSON.stringify(journalMeta)); } catch {} }, [journalMeta]);
  const [pastTrips, setPastTrips] = useState(() => {
    try {
      const saved = localStorage.getItem("jga-past-trips");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed; // honor saved data even if empty
      }
    } catch (e) {}
    // Default: empty for new users. Dev seed data is added by useEffect below.
    return [];
  });
  // Persist pastTrips to localStorage whenever it changes
  useEffect(() => {
    try { localStorage.setItem("jga-past-trips", JSON.stringify(pastTrips)); } catch {}
  }, [pastTrips]);
  // One-time cleanup: wipe legacy jga-user-trips localStorage entry. This was
  // a phantom state that mirrored pastTrips and caused added trips to render
  // twice on the Trips tab. Tombstones never touched it, so user-deleted
  // trips kept reappearing. Wiping it here clears any orphaned phantom trips
  // on this device. Safe because no code reads from this key anymore.
  useEffect(() => {
    try {
      if (localStorage.getItem("jga-user-trips") !== null) {
        localStorage.removeItem("jga-user-trips");
        console.log("[JGA Migration] Cleaned up legacy jga-user-trips entry");
      }
    } catch {}
  }, []);

  // ── Trips & Journal backend hydration ─────────────────────────
  // Runs ONCE per registered session. Loads authoritative server lists
  // and merges with local data. Local-only trips (created offline or before
  // backend was wired) get pushed up. Local-only journal entries likewise.
  // Same defensive pattern as boards: hydrationRanRef guards re-running.
  const tripsHydrationRanRef = useRef(false);
  useEffect(() => {
    if (!registered) { tripsHydrationRanRef.current = false; return; }
    if (tripsHydrationRanRef.current && hydrationTrigger === 0) return;
    tripsHydrationRanRef.current = true;

    let cancelled = false;
    (async () => {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token) {
        console.warn("[JGA Trips Hydrate] No auth token — skipping");
        return;
      }
      console.log("[JGA Trips Hydrate] Starting…");

      // Fetch trips and journal in parallel
      const [tripsRes, journalRes] = await Promise.all([
        loadTripsFromBackend(token),
        loadJournalFromBackend(token),
      ]);
      if (cancelled) return;

      // ── Trips merge ──
      if (tripsRes.ok) {
        // Normalize backend → local shape. Backend confirms camelCase response;
        // we keep snake_case fallbacks for defensive coding (e.g., if a future
        // endpoint version returns mixed shapes during a deploy).
        const normalize = (srv) => ({
          id: srv.id,
          code: srv.code || srv.confirmationCode || srv.confirmation_code || "",
          dest: srv.destination || srv.dest || "",
          emoji: srv.emoji || srv.destinationEmoji || srv.destination_emoji || "",
          dateFrom: srv.dateFrom || srv.departDate || srv.depart_date || null,
          dateTo: srv.dateTo || srv.returnDate || srv.return_date || null,
          // Status translation — booked 'confirmed'/'pending' display as 'upcoming'
          status: tripStatusForUI(srv.status),
          source: srv.source || "user",                 // discriminator for UI badges + delete behavior
          userMarkedDelayed: srv.userMarkedDelayed ?? srv.user_marked_delayed ?? false,
          delayReason: srv.delayReason || srv.delay_reason || null,
          budget: srv.budget ?? srv.totalCost ?? srv.total_cost ?? null,
          travelers: srv.travelers ?? null,
          liked: srv.liked || null,
          disliked: srv.disliked || null,
          favSpot: srv.favSpot || srv.fav_spot || null,
          createdAt: srv.createdAt || srv.created_at || Date.now(),
        });
        const normalizedTrips = (tripsRes.items || []).map(normalize);

        // Get current local trips from localStorage (avoids stale React state)
        const currentLocal = (() => {
          try { return JSON.parse(localStorage.getItem("jga-past-trips") || "[]") || []; } catch { return []; }
        })();

        // Merge by code (backend wins; locals not on backend get pushed up).
        // CRITICAL: only push up local trips that have NO backend id. A trip
        // with an id was previously synced to backend; if it's missing from
        // backend's response now, that means backend DELETED it (perhaps from
        // another device) and we must NOT re-create it. Without this filter,
        // mobile would resurrect any trip the user deleted on desktop.
        const backendCodes = new Set(normalizedTrips.map(t => t.code).filter(Boolean));
        const localOnly = currentLocal.filter(t =>
          t.code &&
          !backendCodes.has(t.code) &&
          // Never-synced trips have no backend id (null) OR have a temp id.
          // Trips WITH a real backend id that are missing from backend response
          // = backend deleted them = skip push-up.
          (t.id == null || (typeof t.id === "string" && t.id.startsWith("tmp-")))
        );
        // Trips with an id but missing from backend response — treat as
        // remotely deleted and add to a virtual tombstone list so we drop
        // them from final state too. This handles cross-device deletes
        // without requiring backend tombstone sync.
        const remotelyDeleted = currentLocal
          .filter(t => t.code && t.id != null && typeof t.id !== "string" && !backendCodes.has(t.code))
          .map(t => t.code);
        if (remotelyDeleted.length > 0) {
          console.log("[JGA Trips Hydrate] Detected", remotelyDeleted.length, "remote deletions:", remotelyDeleted);
          // Add to tombstone list to keep this device's state consistent
          try {
            const existing = JSON.parse(localStorage.getItem("jga-deleted-trips") || "[]");
            const merged = [...new Set([...existing, ...remotelyDeleted])];
            localStorage.setItem("jga-deleted-trips", JSON.stringify(merged));
          } catch {}
        }
        const backendHasTrips = normalizedTrips.length > 0;

        console.log(
          "[JGA Trips Hydrate]", normalizedTrips.length, "from backend,",
          localOnly.length, "local-only to push up"
        );

        const remappedLocals = [];
        for (const local of localOnly) {
          // Skip seeded sample data — never push these to backend
          if (local.code && local.code.startsWith("TMA-DEMO-")) continue;
          // Skip trips with TMA-* codes that are user-source — backend will
          // 400 on these (TMA- prefix is reserved for source='booking')
          if (local.code && local.code.startsWith("TMA-") && (local.source || "user") === "user") {
            console.warn("[JGA Trips Hydrate] Skipping push-up of TMA-coded user trip:", local.code);
            remappedLocals.push(local);
            continue;
          }
          const result = await createTripOnBackend({
            code: local.code,
            destination: local.dest,
            emoji: local.emoji,
            dateFrom: local.dateFrom,
            dateTo: local.dateTo,
            // Local trips are always source='user' — booked trips reach backend via Stripe webhook
            source: "user",
            status: tripStatusForBackend(local.status || "completed", "user"),
            userMarkedDelayed: local.userMarkedDelayed || false,
            delayReason: local.delayReason,
            budget: local.budget,
            travelers: local.travelers,
            liked: local.liked,
            disliked: local.disliked,
            favSpot: local.favSpot,
          }, token);
          if (cancelled) return;
          if (result.ok && result.id != null) {
            remappedLocals.push({ ...local, id: result.id });
          } else {
            // Backend rejected — keep local copy, retry next session
            remappedLocals.push(local);
          }
        }
        if (cancelled) return;
        // Apply trip tombstone — filter out backend trips the user deleted
        // locally. Same guarantee as for journals: deletion intent survives
        // even if the backend DELETE call didn't complete successfully.
        const tripTombstones = (() => {
          try { return new Set(JSON.parse(localStorage.getItem("jga-deleted-trips") || "[]")); }
          catch { return new Set(); }
        })();
        const filteredBackend = normalizedTrips.filter(t => !tripTombstones.has(t.code));
        const finalTrips = [...filteredBackend, ...remappedLocals];
        setPastTrips(finalTrips);
        try { localStorage.setItem("jga-past-trips", JSON.stringify(finalTrips)); } catch {}
        // Clear tombstones for trips backend has already deleted (i.e., codes
        // in our tombstone list that DIDN'T come back from backend). Keeps the
        // tombstone list bounded and matches "server-as-truth" pattern: once
        // backend confirms deletion, the local tombstone is redundant.
        if (tripTombstones.size > 0) {
          const backendCodes = new Set(normalizedTrips.map(t => t.code));
          const stillNeeded = [...tripTombstones].filter(code => backendCodes.has(code));
          if (stillNeeded.length !== tripTombstones.size) {
            try { localStorage.setItem("jga-deleted-trips", JSON.stringify(stillNeeded)); } catch {}
            console.log("[JGA Trips Hydrate] Cleared", tripTombstones.size - stillNeeded.length, "stale tombstone(s)");
          }
        }
        console.log("[JGA Trips Hydrate] ✓ Complete. Final count:", finalTrips.length, "(tombstones filtered:", normalizedTrips.length - filteredBackend.length, ")");
      } else {
        console.warn("[JGA Trips Hydrate] Load failed — keeping local copy");
      }

      // ── Journal merge ──
      // Journal data is normalized by tripCode. Backend wins on conflict.
      // Local-only entries get pushed up via PUT.
      if (journalRes.ok) {
        const fromBackend = journalRes.items || [];
        // Build maps from backend payload. Defensive filtering: only accept
        // non-empty values from the backend so a stale or empty record can't
        // overwrite a user's local entry that hasn't synced up yet.
        // Tombstone list: codes the user deleted locally. Backend may still
        // have these entries (DELETE could've failed, hit rate limit, or
        // backend hasn't propagated yet). The tombstone ensures user's
        // deletion intent survives refresh regardless.
        const deletedTombstones = (() => {
          try { return new Set(JSON.parse(localStorage.getItem("jga-deleted-journals") || "[]")); }
          catch { return new Set(); }
        })();

        const beEntries = {}, beRatings = {}, beMeta = {};
        for (const row of fromBackend) {
          const code = row.tripCode || row.trip_code;
          if (!code) continue;
          // Skip backend rows that the user deleted locally
          if (deletedTombstones.has(code)) continue;
          const entryText = row.entryText ?? row.entry_text;
          if (typeof entryText === "string" && entryText.trim().length > 0) {
            beEntries[code] = entryText;
          }
          if (row.ratings && typeof row.ratings === "object" && Object.keys(row.ratings).length > 0) {
            beRatings[code] = row.ratings;
          } else if (row.rating_overall != null || row.rating_people != null) {
            beRatings[code] = {
              overall: row.rating_overall, people: row.rating_people,
              vibe: row.rating_vibe, food: row.rating_food, value: row.rating_value,
            };
          }
          if (row.meta && typeof row.meta === "object" && Object.keys(row.meta).length > 0) {
            beMeta[code] = row.meta;
          } else if (row.meta_budget != null || row.meta_travelers != null) {
            beMeta[code] = { budget: row.meta_budget, travelers: row.meta_travelers };
          }
        }

        // Read current local
        const currentEntries = (() => { try { return JSON.parse(localStorage.getItem("jga-journal-entries") || "{}"); } catch { return {}; } })();
        const currentRatings = (() => { try { return JSON.parse(localStorage.getItem("jga-journal-ratings") || "{}"); } catch { return {}; } })();
        const currentMeta = (() => { try { return JSON.parse(localStorage.getItem("jga-journal-meta") || "{}"); } catch { return {}; } })();

        // Backend wins where present; local fills gaps
        const mergedEntries = { ...currentEntries, ...beEntries };
        const mergedRatings = { ...currentRatings, ...beRatings };
        const mergedMeta = { ...currentMeta, ...beMeta };

        // Push up local-only entries (codes that exist locally but not on backend)
        const localOnlyCodes = new Set();
        Object.keys(currentEntries).forEach(code => { if (!(code in beEntries)) localOnlyCodes.add(code); });
        Object.keys(currentRatings).forEach(code => { if (!(code in beRatings)) localOnlyCodes.add(code); });
        Object.keys(currentMeta).forEach(code => { if (!(code in beMeta)) localOnlyCodes.add(code); });

        console.log(
          "[JGA Journal Hydrate]", fromBackend.length, "from backend,",
          localOnlyCodes.size, "local-only to push up"
        );

        // Pattern for legitimate manual-trip codes generated by the Add Trip
        // flow (see ~L9498): `USR-` + exactly 6 alphanumeric chars (uppercase).
        // Anything else with a USR- prefix is legacy bug data (user IDs that
        // accidentally got stored as trip codes from a past auth flow). The
        // legacy data is longer/contains lowercase/dashes/special chars and
        // generates CORS-failing PUTs; legitimate codes are fine to sync.
        const MANUAL_TRIP_CODE = /^USR-[A-Z0-9]{6}$/;
        const isLegacyBugKey = (k) => typeof k === "string" && k.startsWith("USR-") && !MANUAL_TRIP_CODE.test(k);

        for (const code of localOnlyCodes) {
          if (cancelled) return;
          // Skip ONLY legacy bug keys (longer/malformed USR- strings from a
          // past auth bug that generate CORS-failing PUTs). Legitimate
          // manual-trip codes are valid trip codes and get synced normally.
          if (isLegacyBugKey(code)) {
            console.warn("[JGA Journal Hydrate] Skipping legacy malformed key", code);
            continue;
          }
          const payload = {};
          if (currentEntries[code] !== undefined) payload.entryText = currentEntries[code];
          if (currentRatings[code]) payload.ratings = currentRatings[code];
          if (currentMeta[code]) payload.meta = currentMeta[code];
          if (Object.keys(payload).length > 0) {
            await saveJournalOnBackend(code, payload, token);
          }
        }

        if (cancelled) return;
        // Strip ONLY legacy bug keys from merged data. Legitimate manual-trip
        // codes (USR-XXXXXX) are real user data and must survive the hydrate.
        // Previous version stripped ALL USR-* keys including legitimate ones,
        // which silently erased every journal entry attached to a manual trip
        // on each refresh / deploy. Cas hit this with her own test data.
        const isCleanKey = (k) => !isLegacyBugKey(k);
        const cleanEntries = Object.fromEntries(Object.entries(mergedEntries).filter(([k]) => isCleanKey(k)));
        const cleanRatings = Object.fromEntries(Object.entries(mergedRatings).filter(([k]) => isCleanKey(k)));
        const cleanMeta = Object.fromEntries(Object.entries(mergedMeta).filter(([k]) => isCleanKey(k)));
        setJournalEntries(cleanEntries);
        setJournalRatings(cleanRatings);
        setJournalMeta(cleanMeta);
        try { localStorage.setItem("jga-journal-entries", JSON.stringify(cleanEntries)); } catch {}
        try { localStorage.setItem("jga-journal-ratings", JSON.stringify(cleanRatings)); } catch {}
        try { localStorage.setItem("jga-journal-meta", JSON.stringify(cleanMeta)); } catch {}
        // Clear journal tombstones for codes backend has confirmed deleted.
        // Same pattern as trips above. Codes in tombstone but absent from
        // backend response = backend already deleted it = tombstone redundant.
        if (deletedTombstones.size > 0) {
          const backendCodes = new Set(fromBackend.map(r => r.tripCode || r.trip_code).filter(Boolean));
          const stillNeeded = [...deletedTombstones].filter(code => backendCodes.has(code));
          if (stillNeeded.length !== deletedTombstones.size) {
            try { localStorage.setItem("jga-deleted-journals", JSON.stringify(stillNeeded)); } catch {}
            console.log("[JGA Journal Hydrate] Cleared", deletedTombstones.size - stillNeeded.length, "stale tombstone(s)");
          }
        }
        console.log("[JGA Journal Hydrate] ✓ Complete.");
      } else {
        console.warn("[JGA Journal Hydrate] Load failed — keeping local copy");
      }
    })();
    return () => { cancelled = true; };
  }, [registered, hydrationTrigger]); // eslint-disable-line

  // ── Trips action API ──
  // Wraps setPastTrips with backend persistence. Use these instead of direct
  // setPastTrips calls at action sites for create/update/delete.
  const tripsApi = useMemo(() => ({
    createTrip: async (trip) => {
      // Validate before optimistic update — TMA-* prefix is reserved for source='booking'.
      // Frontend always creates source='user' trips here (booked trips arrive via Stripe webhook).
      if (trip.code && trip.code.toUpperCase().startsWith("TMA-")) {
        console.error("[JGA Trips Create] Refusing to create user-source trip with TMA-* code:", trip.code);
        return { ok: false, error: "Trip codes starting with TMA- are reserved for booked trips." };
      }
      // Optimistic add with temp id
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      const optimistic = { ...trip, id: tempId, source: trip.source || "user", pendingSync: true };
      setPastTrips(prev => [...prev, optimistic]);
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) {
        console.warn("[JGA Trips Create] No auth — saved locally only");
        return { ok: false, id: tempId };
      }
      const result = await createTripOnBackend({
        code: trip.code, destination: trip.dest, emoji: trip.emoji,
        dateFrom: trip.dateFrom, dateTo: trip.dateTo,
        source: trip.source || "user",
        status: tripStatusForBackend(trip.status || "completed", trip.source || "user"),
        userMarkedDelayed: trip.userMarkedDelayed || false, delayReason: trip.delayReason,
        budget: trip.budget, travelers: trip.travelers,
        liked: trip.liked, disliked: trip.disliked, favSpot: trip.favSpot,
      }, token);
      if (result.ok && result.id != null) {
        setPastTrips(prev => prev.map(t => t.id === tempId ? { ...t, id: result.id, pendingSync: false } : t));
        return { ok: true, id: result.id };
      }
      // 409 = duplicate code; surface to caller so UI can show helpful message
      if (result.status === 409) {
        // Roll back optimistic add
        setPastTrips(prev => prev.filter(t => t.id !== tempId));
        return { ok: false, error: "A trip with this code already exists.", status: 409 };
      }
      return { ok: false, id: tempId };
    },
    updateTrip: async (id, patch) => {
      // Apply optimistic update first
      setPastTrips(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) return { ok: false };
      if (typeof id === "string" && id.startsWith("tmp-")) return { ok: true };
      // Build backend patch — translate field names + strip forbidden fields
      const backendPatch = {};
      for (const [key, value] of Object.entries(patch)) {
        // Skip forbidden fields (backend will 400 if we send them)
        if (PATCH_FORBIDDEN_FIELDS.has(key)) {
          console.warn("[JGA Trips Update] Stripping forbidden patch field:", key);
          continue;
        }
        // Translate dest → destination (which is itself forbidden, but the
        // local model uses 'dest' so this is just defensive normalization)
        if (key === "dest") continue;
        // Translate UI status → backend status when patching the status field
        if (key === "status") {
          // Find current trip to determine source for translation
          const currentTrip = pastTrips.find(t => t.id === id);
          backendPatch.status = tripStatusForBackend(value, currentTrip?.source || "user");
          continue;
        }
        backendPatch[key] = value;
      }
      if (Object.keys(backendPatch).length === 0) return { ok: true };
      return updateTripOnBackend(id, backendPatch, token);
    },
    deleteTrip: async (idOrCode) => {
      // Accepts either a numeric id or a trip code string (callers may pass
      // either via `trip.id ?? trip.code`). Look up by both fields.
      const trip = pastTrips.find(t => t.id === idOrCode || t.code === idOrCode);
      const isBooking = trip?.source === "booking";
      const code = trip?.code || (typeof idOrCode === "string" ? idOrCode : null);
      // Tombstone: track this trip code so backend hydrate can't resurrect
      // it. Even if the DELETE call fails or never fires, the deletion
      // survives the next refresh. Only tombstone user-added trips —
      // bookings have their own cancellation flow and shouldn't be hidden.
      if (code && !isBooking) {
        try {
          const tombstones = JSON.parse(localStorage.getItem("jga-deleted-trips") || "[]");
          if (!tombstones.includes(code)) {
            tombstones.push(code);
            localStorage.setItem("jga-deleted-trips", JSON.stringify(tombstones));
          }
        } catch {}
      }
      if (isBooking) {
        // Soft-delete locally too — set status to cancelled instead of removing
        setPastTrips(prev => {
          const next = prev.map(t => (t.id === idOrCode || t.code === idOrCode) ? { ...t, status: "cancelled" } : t);
          try { localStorage.setItem("jga-past-trips", JSON.stringify(next)); } catch {}
          return next;
        });
      } else {
        setPastTrips(prev => {
          const next = prev.filter(t => t.id !== idOrCode && t.code !== idOrCode);
          try { localStorage.setItem("jga-past-trips", JSON.stringify(next)); } catch {}
          return next;
        });
      }
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) {
        console.warn("[JGA Trips Delete] No auth — local delete only. Cross-device sync will not happen.");
        return { ok: false };
      }
      if (typeof idOrCode === "string" && idOrCode.startsWith("tmp-")) return { ok: true };
      // Backend delete uses the trip's numeric id if available, falls back to
      // whatever was passed in (could be a code for user-added trips that
      // haven't synced to backend yet).
      const beId = trip?.id ?? idOrCode;
      const result = await deleteTripOnBackend(beId, token);
      if (!result.ok) {
        console.warn(
          "[JGA Trips Delete] Backend DELETE failed for id/code:", beId, "—",
          "Local delete persisted via tombstone, but other devices will still see this trip until backend processes it.",
          "Status:", result.status || "unknown"
        );
        // Surface to user — silent failures would be invisible on mobile.
        showToast(`Delete didn't sync (status ${result.status || "?"}) — trip may reappear on other devices`, "error");
      }
      return result;
    },
  }), [registered, pastTrips, showToast]);

  // ── Journal action API ──
  // Single upsert call per change. Coalesces entry/ratings/meta into the
  // PUT payload so we don't hit backend three times for one save.
  const journalApi = useMemo(() => ({
    saveJournal: async (tripCode, { entryText, ratings, meta }) => {
      if (!tripCode) return { ok: false };
      // Optimistic local update — both React state AND localStorage synchronously.
      // The useEffect that normally syncs state→localStorage is async; if anything
      // interrupts the commit phase (component unmount, navigation, tab close),
      // the localStorage write may not happen. Writing synchronously here makes
      // persistence guaranteed regardless of what React does next.
      if (entryText !== undefined) {
        setJournalEntries(prev => {
          const next = { ...prev, [tripCode]: entryText };
          try { localStorage.setItem("jga-journal-entries", JSON.stringify(next)); } catch {}
          return next;
        });
      }
      if (ratings !== undefined) {
        setJournalRatings(prev => {
          const next = { ...prev, [tripCode]: ratings };
          try { localStorage.setItem("jga-journal-ratings", JSON.stringify(next)); } catch {}
          return next;
        });
      }
      if (meta !== undefined) {
        setJournalMeta(prev => {
          const next = { ...prev, [tripCode]: meta };
          try { localStorage.setItem("jga-journal-meta", JSON.stringify(next)); } catch {}
          return next;
        });
      }
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) {
        console.warn("[JGA Journal Save] No auth — saved locally only");
        return { ok: false };
      }
      const payload = {};
      if (entryText !== undefined) payload.entryText = entryText;
      if (ratings !== undefined) payload.ratings = ratings;
      if (meta !== undefined) payload.meta = meta;
      if (Object.keys(payload).length === 0) return { ok: true };
      return saveJournalOnBackend(tripCode, payload, token);
    },
    deleteJournal: async (tripCode) => {
      if (!tripCode) return { ok: false };
      // Tombstone: track this code so backend hydrate can't resurrect it.
      // Even if the DELETE call fails or never fires, the user's deletion
      // intent is respected on subsequent refreshes.
      try {
        const tombstones = JSON.parse(localStorage.getItem("jga-deleted-journals") || "[]");
        if (!tombstones.includes(tripCode)) {
          tombstones.push(tripCode);
          localStorage.setItem("jga-deleted-journals", JSON.stringify(tombstones));
        }
      } catch {}
      // Synchronous localStorage updates for the same reason as save.
      setJournalEntries(prev => {
        const { [tripCode]: _, ...rest } = prev;
        try { localStorage.setItem("jga-journal-entries", JSON.stringify(rest)); } catch {}
        return rest;
      });
      setJournalRatings(prev => {
        const { [tripCode]: _, ...rest } = prev;
        try { localStorage.setItem("jga-journal-ratings", JSON.stringify(rest)); } catch {}
        return rest;
      });
      setJournalMeta(prev => {
        const { [tripCode]: _, ...rest } = prev;
        try { localStorage.setItem("jga-journal-meta", JSON.stringify(rest)); } catch {}
        return rest;
      });
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      if (!token || !registered) return { ok: false };
      return deleteJournalOnBackend(tripCode, token);
    },
  }), [registered]);
  // Persist user state changes to localStorage so they survive reloads
  // (Initial register/login already saves; this catches subsequent updates like airport detection)
  useEffect(() => {
    if (!user) return;
    try { localStorage.setItem("jga-user", JSON.stringify(user)); } catch {}
  }, [user]);

  // One-time cleanup of legacy dev seed data from any user's localStorage
  // (Bali+Paris combo from the previous dev mode). Safe no-op for clean accounts.
  useEffect(() => {
    if (!user) return;
    const codes = pastTrips.map(t => t.code).sort().join(",");
    if (codes === "TMA-BAL9X2,TMA-PAR3K7") {
      console.log("[JGA] Removing legacy dev seed data");
      setPastTrips([]);
    }
  }, [user]); // eslint-disable-line

  // Explore
  const [budget, setBudget] = useState("2000");
  const [expIncludes, setExpIncludes] = useState(["hotel","air"]);
  const [expAgeRange, setExpAgeRange] = useState(null);
  const [expVibes, setExpVibes] = useState([]);
  // expLayoverOk drives the Flight Preferences UI in TMA (and feeds into
  // filterPool's layover-multiplier math). Empty array = "no preference"
  // and yields a 1.0× multiplier. Mirrors blindLayoverOk in SM.
  const [expLayoverOk, setExpLayoverOk] = useState([]);
  const [expHotelType, setExpHotelType] = useState([]);
  const [expTransportType, setExpTransportType] = useState([]);
  const [expActivityTypes, setExpActivityTypes] = useState([]);
  // ── Trip-shape preset tracking ──
  // When the user taps a preset chip, this stores its id. Any manual change
  // to vibe/transport/activities/includes after that clears it, so the chip
  // un-highlights and the user knows they're now in "custom" territory.
  const [tmaActivePreset, setTmaActivePreset] = useState(null);
  // Whether to expand the Quick Start row to show all 20 identities
  // (vs just the user's identity + 4 most similar).
  const [tmaShowAllIdentities, setTmaShowAllIdentities] = useState(false);
  // ── TMA match-count delta tracking ──
  // Shows a transient "(−12)" or "(+3)" next to the sticky pill so the user
  // sees what their last change did. Aggregates rapid toggles into one
  // "settled" change by debouncing (~500ms after last input change).
  const [lastSettledMatchCount, setLastSettledMatchCount] = useState(null);
  const [matchDelta, setMatchDelta] = useState(null);
  const matchDeltaSettleTimer = useRef(null);
  const matchDeltaHideTimer = useRef(null);
  const [expDateMode, setExpDateMode] = useState("duration"); // dates | month | duration
  const [expMonth, setExpMonth] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [bottomTab, setBottomTab] = useState(null); // explore | journal | trips | profile
  // Explore-page-specific budget filter pill ('<1K' | '1-2K' | '2-3K' | '3K+' | null=Any).
  // Independent of dashboardBudget — user can narrow Explore without changing
  // their permanent budget setting. On first mount, inherits from dashboardBudget
  // via the inference effect below.
  const [exploreBudgetPill, setExploreBudgetPill] = useState(null);
  // Per-card day customization on the Explore search list. Map of
  // {destName: dayCount}. Min 3 days, max 14. When a card isn't in the map,
  // the default day count (5) is used for cost calculations.
  const [explorePerCardDays, setExplorePerCardDays] = useState({});
  // Over-budget interstitial: when user clicks an over-budget Explore card,
  // we show an interstitial offering to bump budget or pick differently
  // instead of dropping them into TMA with "0 matches".
  const [exploreOverBudget, setExploreOverBudget] = useState(null); // {dest, neededBudget}
  // On the first time the Explore surface opens, infer the pill from
  // dashboardBudget. Independent state from there — Explore changes don't
  // touch dashboardBudget.
  // Re-infers the pill from dashboardBudget on both first explore visit AND
  // every dashboardBudget change. Session overrides (user manually tapping a
  // pill while on Explore) still work — they call setExploreBudgetPill directly
  // and this effect only fires when its deps change. The bottomTab gate keeps
  // us from inferring when the user isn't on Explore (no point setting state
  // they can't see).
  useEffect(() => {
    if (bottomTab === "explore") {
      const b = parseInt(String(dashboardBudget || "").replace(/[^0-9]/g, ""), 10);
      if (!b) return;
      if (b < 1000) setExploreBudgetPill("<1K");
      else if (b < 2000) setExploreBudgetPill("1-2K");
      else if (b < 3000) setExploreBudgetPill("2-3K");
      else setExploreBudgetPill("3K+");
    }
  }, [bottomTab, dashboardBudget]); // eslint-disable-line
  const [showWelcome, setShowWelcome] = useState(true);
  useEffect(() => {
    if (registered && showWelcome) {
      const t = setTimeout(() => setShowWelcome(false), 5000);
      return () => clearTimeout(t);
    }
  }, [registered]);

  // ── Session-expired listener ────────────────────────────────────
  // API helpers fire `jga:session-expired` (via _signalSessionExpired) when
  // they get a 401. Tokens are already cleared at signal time. This listener
  // is responsible for tearing down React state and showing a recovery prompt.
  // We dedupe by ignoring the event if `registered` is already false.
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null);
  useEffect(() => {
    function handle(ev) {
      console.log("[JGA Auth] Tearing down session — cause:", ev?.detail?.source);
      // Clear user-facing UI state
      setRegistered(false);
      try { localStorage.removeItem("jga-user"); } catch {}
      // Surface a recovery prompt
      setSessionExpiredMsg("Your session expired. Please sign in again.");
      // Auto-dismiss after 8 seconds (user can also click to dismiss)
      setTimeout(() => setSessionExpiredMsg(null), 8000);
    }
    window.addEventListener("jga:session-expired", handle);
    return () => window.removeEventListener("jga:session-expired", handle);
  }, []);

  // Auto-refresh access token every 12 minutes (token expires at 15 min)
  // Uses refresh token cookie sent by backend on login/register.
  //
  // Key behavior changes (2026-05-08):
  //   1. ON-MOUNT DELAY: 600ms before first refresh fires. iOS Safari sometimes
  //      doesn't send cookies on the first request after a page navigation if
  //      JS dispatches it immediately. Delaying lets the cookie store settle.
  //   2. 401 RETRY: a single 401 isn't treated as session-fatal. Could be a
  //      transient cookie issue or backend hiccup. We clear the access token,
  //      wait 1.5s, retry once. Only if BOTH attempts 401 do we signal
  //      session-expired. This dramatically reduces false-positive logouts on
  //      mobile Safari.
  //   3. Network errors stay silent (interval will retry at next 12-min cycle).
  useEffect(() => {
    if (!registered) return;
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    let stopped = false;
    let interval = null;

    async function attemptRefresh() {
      if (stopped) return { ok: false, status: 0 };
      try {
        // Send the refresh token in the body when we have one in localStorage.
        // This is the Safari-safe path: Safari ITP blocks the cross-site
        // refresh cookie, so cookie-only refresh fails and users get logged
        // out every ~15 min. A body token (like the access token) isn't a
        // cookie, so ITP doesn't touch it. credentials:include stays as a
        // fallback for browsers where the cookie still works.
        let storedRefresh = null;
        try { storedRefresh = localStorage.getItem("jga_refresh_token"); } catch {}
        const r = await fetch(`${API}/api/auth/refresh`, {
          method: "POST",
          credentials: "include", // fallback: send refresh-token cookie where allowed
          headers: storedRefresh ? { "Content-Type": "application/json" } : undefined,
          body: storedRefresh ? JSON.stringify({ refreshToken: storedRefresh }) : undefined,
        });
        if (r.ok) {
          const data = await r.json();
          if (data.accessToken) {
            try { localStorage.setItem("jga_token", data.accessToken); } catch {}
            // Capture a rotated refresh token if the backend issues one.
            if (data.refreshToken) { try { localStorage.setItem("jga_refresh_token", data.refreshToken); } catch {} }
            console.log("[JGA Auth] Token refreshed");
            // Notify hydration listeners so they can re-fetch with the fresh
            // token. Data loaders that bailed during the grace period will
            // pick this up and retry — fixing the "mobile login on every
            // refresh" issue where stale-token 401s killed the session before
            // refresh could fix it.
            try { window.dispatchEvent(new CustomEvent("jga:token-refreshed")); } catch {}
          }
          // Cross-device user profile re-hydration: pull /me to pick up any
          // changes made on other devices (identity quiz retake, profile
          // edits, etc). Backend wins. Gracefully degrades if endpoint
          // doesn't exist (404) or returns network error.
          //
          // IMPORTANT: this runs on EVERY successful refresh, even when the
          // backend's /api/auth/refresh response doesn't include a new
          // accessToken. The /me fetch is independent of token rotation —
          // we just need ANY valid access token to authorize it. Previously
          // /me only fired when data.accessToken was truthy, which meant
          // some refresh cycles silently skipped cross-device sync.
          {
            const tokenForMe = data.accessToken || (() => {
              try { return localStorage.getItem("jga_token"); } catch { return null; }
            })();
            if (tokenForMe) {
              try {
                console.log("[JGA Auth] Fetching /me for cross-device sync…");
                const meRes = await fetch(`${API}/api/users/me`, {
                  method: "GET",
                  // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
                  // cache: "no-store" prevents Safari (especially mobile Safari)
                  // from serving a stale cached response. Without this, mobile
                  // would silently use an old /me response even after the user
                  // updated their identity on another device, breaking the
                  // cross-device sync.
                  cache: "no-store",
                  headers: { "Authorization": `Bearer ${tokenForMe}` },
                });
                if (meRes.ok) {
                  const me = await meRes.json().catch(() => null);
                  if (me && typeof me === "object") {
                    console.log("[JGA Auth] /me hydration:", { identity: me.travelIdentity, companion: me.travelCompanion });
                    try { window.dispatchEvent(new CustomEvent("jga:user-hydrated", { detail: me })); } catch {}
                  } else {
                    console.warn("[JGA Auth] /me returned ok but body was empty/invalid");
                  }
                } else if (meRes.status === 404) {
                  // Endpoint not implemented yet — silent. Backend chat is aware.
                } else {
                  console.warn("[JGA Auth] /me returned", meRes.status);
                }
              } catch (e) {
                console.warn("[JGA Auth] /me fetch error:", e.message || e);
              }
            }
          }
          return { ok: true, status: r.status };
        }
        return { ok: false, status: r.status };
      } catch (e) {
        return { ok: false, status: 0, networkError: true };
      }
    }

    async function refresh() {
      if (stopped) return;
      const first = await attemptRefresh();
      if (first.ok) return;
      if (first.networkError) return; // silent retry next cycle, don't tear down

      // Non-401 backend error (e.g., 500) — also silent, retry next cycle
      if (first.status !== 401) {
        console.warn("[JGA Auth] Refresh got unexpected status:", first.status, "(will retry next cycle)");
        return;
      }

      // First attempt 401 — could be transient (cookie not sent on first
      // post-navigation request, brief backend hiccup, etc). Wait + retry once
      // before declaring session dead. This is the key fix for "mobile Safari
      // makes me log in on every refresh."
      console.log("[JGA Auth] Refresh 401 — retrying once before declaring session expired");
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (stopped) return;
      const second = await attemptRefresh();
      if (second.ok) {
        console.log("[JGA Auth] Retry succeeded — session was fine, transient issue");
        return;
      }
      if (second.networkError) {
        console.log("[JGA Auth] Retry hit network error — session probably fine, retrying next cycle");
        return;
      }
      if (second.status === 401) {
        console.warn("[JGA Auth] Refresh failed twice with 401 — stopping refresh loop and ending session");
        stopped = true;
        if (interval) clearInterval(interval);
        _signalSessionExpired("auth-refresh");
      }
    }

    // Delayed on-mount refresh (600ms gives iOS Safari time to settle cookies
    // after navigation), then 12-min interval afterwards. Both go through the
    // retry-aware refresh() function above.
    const onMountTimer = setTimeout(() => {
      if (!stopped) refresh();
    }, 600);
    interval = setInterval(refresh, 12 * 60 * 1000);
    return () => {
      stopped = true;
      clearTimeout(onMountTimer);
      if (interval) clearInterval(interval);
    };
  }, [registered]);

  // ─── Email verification ───────────────────────────────────────────
  // Banner dismissal is session-only (survives page navigation but not full reload)
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  // Tracks the result of a verification attempt — drives the success modal
  const [verifyStatus, setVerifyStatus] = useState(null); // null | "verifying" | "success" | "expired" | "invalid" | "error"
  const [resendStatus, setResendStatus] = useState(null); // null | "sending" | "sent" | "rate-limited" | "error"
  const [showVerifyBlock, setShowVerifyBlock] = useState(false); // hard-block modal at booking step
  const [showMinorBlock, setShowMinorBlock] = useState(false); // hard-block modal for under-18 users at booking step

  // Helper: send/resend verification email
  // Store the last resend error detail so we can show it in the UI rather
  // than hiding it behind a generic "Try again" — pre-launch we'd rather
  // surface backend errors than swallow them.
  const [resendErrorDetail, setResendErrorDetail] = useState(null);

  async function resendVerificationEmail() {
    if (resendStatus === "sending") return;
    setResendStatus("sending");
    setResendErrorDetail(null);
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    try {
      const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
      const r = await fetch(`${API}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
      });
      if (r.status === 429) {
        // Try to surface the retry-after if the backend gave one
        const data = await r.json().catch(() => ({}));
        if (data?.error || data?.message) setResendErrorDetail(data.error || data.message);
        setResendStatus("rate-limited");
        setTimeout(() => { setResendStatus(null); setResendErrorDetail(null); }, 30000);
        return;
      }
      if (!r.ok) {
        // Surface the actual backend error so debugging doesn't require Railway logs
        const data = await r.json().catch(() => ({}));
        const msg = data?.error || data?.message || `HTTP ${r.status}`;
        // "User not found" (or 404) from an authenticated endpoint means our
        // token references a user_id that no longer exists in the database —
        // e.g. account was deleted on the backend while this browser still
        // holds a valid-looking JWT. Treat as a dead session: clear tokens
        // and trigger the existing session-expired UI recovery flow. The
        // user sees a friendly "session expired, please sign in" message
        // rather than a confusing red error box about themselves.
        const looksLikeMissingUser = r.status === 404 || /user not found|account not found|user does not exist/i.test(String(msg));
        if (looksLikeMissingUser) {
          console.warn("[JGA Verify] Resend returned user-not-found — treating as dead session");
          try { _signalSessionExpired("resendVerification"); } catch {}
          // Do NOT show the raw error box — the session-expired handler will
          // surface its own recovery message.
          setResendStatus(null);
          return;
        }
        setResendErrorDetail(msg);
        console.error("[JGA Verify] Resend rejected:", r.status, msg);
        setResendStatus("error");
        setTimeout(() => { setResendStatus(null); setResendErrorDetail(null); }, 8000);
        return;
      }
      setResendStatus("sent");
      setTimeout(() => setResendStatus(null), 12000);
    } catch (e) {
      console.error("[JGA Verify] Resend failed:", e);
      setResendErrorDetail(e?.message || "Network error");
      setResendStatus("error");
      setTimeout(() => { setResendStatus(null); setResendErrorDetail(null); }, 8000);
    }
  }

  // On page load: check for ?verify=TOKEN in URL and submit it
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);

    // Reset password flow: ?reset=TOKEN — opens the ResetPasswordModal
    const resetToken = params.get("reset");
    if (resetToken) {
      setResetPasswordToken(resetToken);
      // Strip from URL so refreshes don't re-trigger
      const cleanUrl = window.location.pathname + window.location.hash;
      try { window.history.replaceState({}, "", cleanUrl); } catch {}
    }

    // Booking-code deep-link: ?code=TMA-XXXXXX — sent in approval emails
    // when Cas marks a waitlist user "cleared to book." 
    // FLOW CHANGE (Jun 11 2026): now opens LoginModal (not RegisterModal) so
    // users with existing accounts go straight to sign-in. New users without
    // accounts can switch to register via a "Sign up instead" CTA inside
    // LoginModal. Post-login routes the user to the Trips tab where their
    // pending trip is visible with the booking code, ready to book.
    // Backend POST /api/auth/register accepts bookingCode and links the trip
    // server-side; the equivalent login-side linking happens on next
    // GET /api/trips fetch (backend should auto-resolve pending trips
    // matching the user's email).
    // ADDITIONAL ?email= param: the approval email also carries the user's
    // email so we can pre-fill the email field — saves them from retyping
    // the email they just received the link at. Sets both Register and Login
    // pre-fill state so whichever modal they end up in has it.
    const bookingCode = params.get("code") || params.get("booking");
    const emailFromUrl = params.get("email");
    if (bookingCode) {
      const upperCode = bookingCode.toUpperCase();
      setPrefillBookingCode(upperCode);
      // Stash in localStorage so the existing jga_pending_booking_code
      // plumbing (BookingModal auto-fill, etc.) picks it up post-auth.
      try { localStorage.setItem("jga_pending_booking_code", upperCode); } catch {}
      // Also stash for the LoginModal banner to display.
      setLoginInitialBookingCode(upperCode);
      if (emailFromUrl) {
        const trimmed = emailFromUrl.trim().toLowerCase();
        setPrefillRegisterEmail(trimmed);
        setLoginInitialEmail(trimmed);
      }
      // OPEN LOGIN (not Register). LoginModal has "Sign up instead" fallback
      // for users without an account.
      setShowLogin(true);
      // Strip params from URL so a refresh doesn't reopen the modal.
      try {
        const params2 = new URLSearchParams(window.location.search);
        params2.delete("code");
        params2.delete("booking");
        params2.delete("email");
        const rest = params2.toString();
        const cleanUrl = window.location.pathname + (rest ? "?" + rest : "") + window.location.hash;
        window.history.replaceState({}, "", cleanUrl);
      } catch {}
    }

    const verifyToken = params.get("verify");
    if (!verifyToken) return;

    // Strip the token from the URL immediately so refreshes don't re-submit
    const cleanUrl = window.location.pathname + window.location.hash;
    try { window.history.replaceState({}, "", cleanUrl); } catch {}

    setVerifyStatus("verifying");
    const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
          body: JSON.stringify({ token: verifyToken }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok) {
          setVerifyStatus("success");
          // Update user object — backend should return the verified user
          if (data.user) {
            // ── Cross-account verification guard ──
            // If the verify link was for a DIFFERENT email than the currently
            // logged-in user, do NOT merge the verified user's data into the
            // current session. Otherwise the wrong user's name/email/identity
            // gets overlaid onto the existing session, creating account
            // confusion. The backend has already verified the new email —
            // the user just needs to log out + log in as that account to use it.
            setUser(prev => {
              if (prev && prev.email && data.user.email && prev.email !== data.user.email) {
                console.warn("[JGA Verify] Cross-account verify ignored — current session:", prev.email, "/ verified email:", data.user.email, ". The verified email is valid; sign out + sign in as that account to use it.");
                return prev;
              }
              return { ...prev, ...data.user, emailVerified: true };
            });
            // Cross-browser hydration: when the user clicks verify in a
            // different browser than they registered in, this browser has no
            // localStorage identity. The backend response is the only place
            // their identity lives in this session. Hydrate state + persist
            // to localStorage so the dashboard renders with their identity
            // (instead of forcing the quiz again).
            // NOTE: Also guarded — only hydrate if NOT a cross-account verify.
            if (data.user.travelIdentity && TRAVEL_IDENTITIES[data.user.travelIdentity]) {
              setUser(prev => {
                if (prev && prev.email && data.user.email && prev.email !== data.user.email) {
                  // Cross-account — skip identity hydration too
                  return prev;
                }
                setTravelerType(data.user.travelIdentity);
                try { localStorage.setItem("jga-traveler-type", data.user.travelIdentity); } catch {}
                try { localStorage.setItem("jga-onboarded", "true"); } catch {}
                setExpVibes(TRAVEL_IDENTITIES[data.user.travelIdentity].vibes || []);
                setBlindVibes(TRAVEL_IDENTITIES[data.user.travelIdentity].vibes || []);
                return prev; // identity hydration is a side-effect; user state untouched here
              });
            }
          } else {
            setUser(prev => prev ? { ...prev, emailVerified: true } : prev);
          }
          // ── /me hydration fallback ──
          // If the verify response didn't carry travelIdentity (either because
          // data.user is missing or data.user.travelIdentity is null/undefined),
          // fetch /api/users/me directly. This catches backends whose verify
          // endpoint returns a thin response without the full user record.
          // The /me endpoint dispatches jga:user-hydrated which is already
          // wired to set travelerType + persist to localStorage.
          // Added Jun 25, 2026 (Cas's cross-browser identity bug).
          try {
            const verifyIdentityMissing = !data?.user?.travelIdentity;
            if (verifyIdentityMissing) {
              const token = (() => { try { return localStorage.getItem("jga_token"); } catch { return null; } })();
              if (token) {
                console.log("[JGA Verify] travelIdentity missing from verify response — fetching /me as fallback");
                fetch(`${API}/api/users/me`, {
                  method: "GET",
                  cache: "no-store",
                  headers: { "Authorization": `Bearer ${token}` },
                }).then(meR => {
                  if (meR.ok) return meR.json();
                  console.warn("[JGA Verify] /me fallback returned", meR.status);
                  return null;
                }).then(me => {
                  if (me && typeof me === "object") {
                    console.log("[JGA Verify] /me fallback returned identity:", me.travelIdentity);
                    try { window.dispatchEvent(new CustomEvent("jga:user-hydrated", { detail: me })); } catch {}
                  }
                }).catch(e => {
                  console.warn("[JGA Verify] /me fallback fetch error:", e.message || e);
                });
              } else {
                console.warn("[JGA Verify] travelIdentity missing AND no auth token — can't fall back to /me");
              }
            }
          } catch (e) { jgaErrorReport(e, "verify.meFallback"); }
          // ── Recover the quiz identity ──
          // If the user took the quiz BEFORE verifying (e.g. backend rejected
          // the identity write pre-verification, or the initial sync failed),
          // their identity lives only in localStorage. Now that they're
          // verified, push it up so it isn't lost and they don't have to redo
          // the quiz. Safe no-op if there's nothing saved or it already synced.
          try {
            const savedId = localStorage.getItem("jga-traveler-type");
            const savedCompanion = localStorage.getItem("jga-travel-companion");
            let savedAnswers = [];
            try { savedAnswers = JSON.parse(localStorage.getItem("jga-quiz-answers") || "[]"); } catch {}
            let tok = null;
            try { tok = localStorage.getItem("jga_token"); } catch {}
            if (savedId && tok && TRAVEL_IDENTITIES[savedId]) {
              fetch(`${API}/api/users/identity`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
                body: JSON.stringify({
                  travelIdentity: savedId,
                  travelCompanion: savedCompanion || null,
                  quizAnswers: savedAnswers,
                }),
              }).then(res => {
                if (res.ok) console.log("[JGA Verify] Re-synced saved identity after verification:", savedId);
                else console.warn("[JGA Verify] Identity re-sync after verification returned", res.status);
              }).catch(err => console.warn("[JGA Verify] Identity re-sync failed:", err));
            }
          } catch (e) { console.warn("[JGA Verify] Identity recovery skipped:", e); }
        } else if (r.status === 410) {
          setVerifyStatus("expired");
        } else if (r.status === 400 || r.status === 404) {
          setVerifyStatus("invalid");
        } else {
          setVerifyStatus("error");
        }
      } catch (e) {
        console.error("[JGA Verify] Verification request failed:", e);
        setVerifyStatus("error");
      }
    })();
  }, []);

  // Convenience flag — true when registered user has unverified email
  // FAIL-CLOSED: if emailVerified is undefined (e.g., backend hasn't returned it on login),
  // treat as unverified rather than skipping the gate. Only bypass when explicitly true.
  const isUnverified = registered && user && user.emailVerified !== true;

  // Convenience flag — true when registered user is under 18
  // Calculated from DOB if available, falls back to ageRange === "under18"
  // Used to gate booking flows (booking, surprise, affordable) — minors can browse and save trips but not book
  const isMinor = (() => {
    if (!registered || !user) return false;
    if (user.dob) {
      const dobDate = new Date(user.dob);
      if (!isNaN(dobDate.getTime())) {
        const ageMs = Date.now() - dobDate.getTime();
        const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
        return ageYears < 18;
      }
    }
    return user.ageRange === "under18";
  })();

  // Detect booking abandonment — when user leaves blind/explore with progress but no booking
  useEffect(() => {
    const isBookingMode = mode === "blind" || mode === "explore";
    const wasBookingMode = lastModeRef.current === "blind" || lastModeRef.current === "explore";

    // Track whether they made progress (selected anything in the flow)
    if (isBookingMode && step > 0) hadProgressRef.current = true;

    // Detect abandonment: leaving booking mode with progress, going to home
    if (wasBookingMode && !isBookingMode && hadProgressRef.current && !feedbackShownRef.current) {
      setAbandonContext({
        from: lastModeRef.current,
        hadProgress: true,
        // Snapshot the destination they were engaging with so we can
        // dismiss it from recommendations after feedback submits. Pulled
        // from the ref (not current `selected`) because reset() has
        // already nulled `selected` by the time this effect runs.
        destName: lastSelectedDestRef.current,
      });
      setShowAbandonFeedback(true);
      feedbackShownRef.current = true; // only show once per session
    }

    // Reset progress when entering a fresh booking flow
    if (isBookingMode && step === 0) hadProgressRef.current = false;

    lastModeRef.current = mode;
  }, [mode, step]);

  const [searchedDests, setSearchedDests] = useState([]);
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [days, setDays] = useState(7);
  const [results, setResults] = useState(null);
  // Previous spin batch — lets the user go back to picks they liked after
  // spinning to a new set. Holds the prior `results` array or null.
  const [prevResults, setPrevResults] = useState(null);
  // Names already shown across spins this session, so "see more" surfaces
  // genuinely fresh destinations instead of repeating what they've seen.
  const [seenNames, setSeenNames] = useState([]);

  // Auto-populate from "You could be here" banner click / home State 7
  // recommendation. Previously this skipped straight to step 2 + auto-generated
  // the itinerary with default budget (7 days, $2k). Per Cas's Day 28 call:
  // now lands on step 0 so the user can set budget and dates first, then taps
  // "Generate itinerary for {dest}" to lock it in.
  // Also pre-populates the form from the user's identity so the vibe pills
  // and companion are pre-selected — same data that drove the recommendation.
  useEffect(() => {
    if (mode === "explore" && prefilledDest) {
      const dest = prefilledDest;
      setPrefilledDest(null);
      // Land on step 0 with the destination locked in
      setTimeout(() => {
        setLockedTripDest(dest);
        setStep(0);
        // Pre-populate vibes from identity catalog (the same data
        // that drove this recommendation).
        const tt = (() => {
          try { return localStorage.getItem("jga-traveler-type"); } catch { return null; }
        })();
        if (tt && typeof TRAVEL_IDENTITIES !== "undefined" && TRAVEL_IDENTITIES[tt]) {
          const ident = TRAVEL_IDENTITIES[tt];
          if (Array.isArray(ident.vibes) && ident.vibes.length > 0) {
            setExpVibes(ident.vibes.slice(0, 4));
          }
        }
        // Pre-populate travel companion from quiz storage if known.
        try {
          const comp = localStorage.getItem("jga-travel-companion");
          if (comp) setTravelCompanion(comp);
        } catch {}
        // Force region back to "Anywhere" — any persisted region preference
        // would conflict with the locked destination and read as a
        // contradictory filter ("planning Vancouver" + "region: Americas").
        setExpRegion("Anywhere");
        setExpSub(null);
        setExpCountry(null);
        setExpState(null);
      }, 50);
    }
  }, [mode, prefilledDest]); // eslint-disable-line
  const [spinning, setSpinning] = useState(false);
  const [spinText, setSpinText] = useState("");
  const [selected, setSelected] = useState(null);
  // Keep lastSelectedDestRef in sync with the active destination. Updates
  // only when `selected` becomes a real destination object (not on clear).
  // Must be placed AFTER the `selected` state is declared — earlier
  // placement caused a TDZ ReferenceError that crashed the app on mount
  // (the dep array is evaluated during render before `selected` exists).
  useEffect(() => {
    if (selected && selected.name) {
      lastSelectedDestRef.current = selected.name;
    }
  }, [selected]);
  const [aiItinerary, setAiItinerary] = useState(null);
  const [savedItineraries, setSavedItineraries] = useState(() => {
    try {
      const s = localStorage.getItem("jga-itineraries");
      const parsed = s ? JSON.parse(s) : [];
      // ── One-time migration: strip Tokyo + Marrakech from existing saves ──
      // Per Cas's direction, these are no longer default itineraries. Removes
      // any user-saved versions on THIS device. Versioned so it runs exactly
      // once. Note: if the user is logged in and the backend has Tokyo/Marrakech
      // for them, the next sync will re-hydrate those — backend cleanup is a
      // separate task (see handoff doc).
      const MIGRATION_KEY = "jga-itin-migration-v1-remove-tokyo-marrakech";
      try {
        if (!localStorage.getItem(MIGRATION_KEY) && Array.isArray(parsed)) {
          const filtered = parsed.filter(i => i.dest !== "Tokyo" && i.dest !== "Marrakech");
          if (filtered.length !== parsed.length) {
            localStorage.setItem("jga-itineraries", JSON.stringify(filtered));
            console.log("[JGA] Migration: removed", parsed.length - filtered.length, "Tokyo/Marrakech itineraries from local storage");
          }
          localStorage.setItem(MIGRATION_KEY, new Date().toISOString());
          return filtered;
        }
      } catch (e) { console.warn("[JGA] Itinerary migration failed:", e); }
      return parsed;
    } catch(e) { return []; }
  });

  // ── Centralized itinerary save ────────────────────────────────
  // Single helper called from every place that wants to persist an AI
  // itinerary: explicit "Save to My Itineraries" button, booking flow
  // (auto-save before showing "your itinerary is saved" message), and
  // any future surface (Mood Match save, post-trip review, etc).
  //
  // Idempotent: if an itinerary with the same destination is already
  // saved, returns ok without re-creating. Avoids duplicate rows on
  // backend if the user navigates back-and-forth in the booking flow.
  //
  // Returns: { ok: boolean, alreadySaved?: boolean, id?: number }
  const saveItineraryFlow = useCallback(async ({ destName, destEmoji, destCountry, days, data, departDate, returnDate }) => {
    // Reject saves with missing or empty content. `data` must be a non-null
    // object with at least one day. This prevents the Dallas-shape bug where
    // an itinerary saved without real content becomes broken stock — backend
    // stores it as null, hydration faithfully returns null, and the local
    // override (intentional, backend-wins) means we can never re-save over it.
    if (!destName || !data || typeof data !== "object" || !Array.isArray(data.days) || data.days.length === 0) {
      console.warn("[JGA Itinerary Flow] Missing destName or empty itinerary content — skipping", { destName, hasData: !!data, daysCount: Array.isArray(data?.days) ? data.days.length : "(no days array)" });
      return { ok: false, reason: "empty-content" };
    }
    // Dedupe key: dest + dates. Previously this deduped on dest alone, which
    // silently rejected a user's September save if they had an August save for
    // the same destination. Different dates produce a different AI prompt
    // (different weather/festivals/seasonal content) so they're genuinely
    // different itineraries worth keeping separately. Matching dates still
    // dedupes (no point in two identical entries).
    const dateKey = `${departDate || ""}|${returnDate || ""}`;
    const existing = savedItineraries.find(i => i.dest === destName && (`${i.departDate || ""}|${i.returnDate || ""}`) === dateKey);
    if (existing) {
      console.log("[JGA Itinerary Flow] Already saved (dest + dates match):", destName, dateKey, "id:", existing.id);
      return { ok: true, alreadySaved: true, id: existing.id };
    }
    // Optimistic local insert — UI feels instant
    const newItem = {
      dest: destName,
      emoji: destEmoji || "🌍",
      days: days || 7,
      departDate: departDate || null,
      returnDate: returnDate || null,
      saved: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      data,
      pendingSync: true, // cleared once backend confirms
    };
    setSavedItineraries(prev => {
      const next = [...prev, newItem];
      try { localStorage.setItem("jga-itineraries", JSON.stringify(next)); } catch {}
      return next;
    });
    // Fire backend persistence
    let token = null;
    try { token = localStorage.getItem("jga_token"); } catch {}
    if (!token || !registered) {
      console.warn("[JGA Itinerary Flow] No auth — kept local with pendingSync; will retry on next login");
      return { ok: false, id: null };
    }
    const result = await saveItineraryToBackend({
      destination: destCountry ? `${destName}, ${destCountry}` : destName,
      days: days || 7,
      itinerary_data: data,
      token,
    });
    if (result.ok && result.id != null) {
      // Replace pending entry with confirmed one (carries server id now)
      setSavedItineraries(prev => {
        const next = prev.map(i => (i.dest === destName && i.pendingSync && (`${i.departDate || ""}|${i.returnDate || ""}`) === `${departDate || ""}|${returnDate || ""}`) ? { ...i, id: result.id, pendingSync: false } : i);
        try { localStorage.setItem("jga-itineraries", JSON.stringify(next)); } catch {}
        return next;
      });
      return { ok: true, id: result.id };
    }
    // Backend rejected — keep local pending; flush on next login retries automatically
    return { ok: false };
  }, [savedItineraries, registered]);

  // ── Delete a saved itinerary ──
  // Optimistic local removal + backend DELETE. If backend rejects (network,
  // 4xx) we restore the entry. Only deletes USER-saved entries (those with
  // a real id); SAMPLE_ITINERARIES are demo placeholders and aren't in
  // savedItineraries, so they can't be deleted from here.
  const deleteItineraryFlow = useCallback(async ({ id, destName }) => {
    if (!id && !destName) return { ok: false };
    // Snapshot for rollback
    let snapshot = null;
    setSavedItineraries(prev => {
      snapshot = prev;
      const next = prev.filter(i => i.id !== id && i.dest !== destName);
      try { localStorage.setItem("jga-itineraries", JSON.stringify(next)); } catch {}
      return next;
    });
    // Backend call (only if we have a real id — pending-sync items never made
    // it to the server, so there's nothing to delete remotely)
    if (id && registered) {
      let token = null;
      try { token = localStorage.getItem("jga_token"); } catch {}
      if (token) {
        const result = await deleteItineraryOnBackend(id, token);
        if (!result.ok) {
          // Rollback local state
          if (snapshot) {
            setSavedItineraries(snapshot);
            try { localStorage.setItem("jga-itineraries", JSON.stringify(snapshot)); } catch {}
          }
          return { ok: false };
        }
      }
    }
    return { ok: true };
  }, [registered]);

  // ── Backend hydration + pending-sync flush ──
  // On registered session start, fetch authoritative server list and merge with
  // local cache. Any items still flagged pendingSync (failed earlier save) get
  // retried here. Backend wins on conflict by destination name.
  useEffect(() => {
    if (!registered) return;
    let cancelled = false;
    (async () => {
      let token = null;
      try { token = localStorage.getItem("jga_token"); } catch {}
      if (!token) return;

      // 1) Flush any pending local saves to backend first
      const localPending = (() => { try { return JSON.parse(localStorage.getItem("jga-itineraries") || "[]").filter(i => i.pendingSync); } catch { return []; } })();
      for (const item of localPending) {
        const result = await saveItineraryToBackend({
          destination: item.dest,
          days: item.days,
          itinerary_data: item.data,
          token,
        });
        if (result.ok && !cancelled) {
          setSavedItineraries(s => {
            const next = s.map(i => i.dest === item.dest && i.pendingSync ? { ...i, id: result.id, pendingSync: false } : i);
            try { localStorage.setItem("jga-itineraries", JSON.stringify(next)); } catch(e) {}
            return next;
          });
        }
      }

      // 2) Hydrate from backend (source of truth)
      const { ok, items } = await loadItinerariesFromBackend(token);
      if (!ok || cancelled) return;
      // Backend response shape — accept either { id, destination, days, itinerary_data, created_at }
      // or the legacy local shape. Coerce into the local shape used throughout the UI.
      // Filter out entries with no usable content — these are "broken stock"
      // saves from earlier code paths that let nulls through. Showing them
      // would give the user a row that can never expand to anything useful.
      const normalized = items
        .filter(srv => {
          const content = srv.itinerary_data || srv.data;
          const hasUsableContent = content && typeof content === "object" && Array.isArray(content.days) && content.days.length > 0;
          if (!hasUsableContent) {
            console.warn("[JGA Itinerary Hydrate] Skipping broken-stock entry (no day content):", { id: srv.id, dest: srv.destination });
          }
          return hasUsableContent;
        })
        .map(srv => ({
          id: srv.id,
          dest: srv.destination?.split(",")[0]?.trim() || srv.dest || "Unknown",
          emoji: srv.emoji || "📍",
          days: srv.days || 7,
          saved: srv.created_at ? new Date(srv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : (srv.saved || ""),
          data: srv.itinerary_data || srv.data || null,
        }));
      // Merge: backend wins on destination match; keep any local-only items not on backend (will sync later).
      // CRITICAL: filter BOTH sides for usable content. If the backend filter
      // drops a broken entry, but it still exists in local cache from a prior
      // session, the merge fallback would resurrect it. Apply the same content
      // check to local items so broken-stock entries can't leak through either path.
      const hasUsableContent = (entry) => {
        return entry && entry.data && typeof entry.data === "object" && Array.isArray(entry.data.days) && entry.data.days.length > 0;
      };
      setSavedItineraries(prev => {
        const byDest = new Map();
        prev.forEach(i => {
          if (!i.dest) return;
          // Allow pending-sync items even without content yet (they're mid-flight)
          // Otherwise require usable content to keep them visible.
          if (i.pendingSync || hasUsableContent(i)) {
            byDest.set(i.dest, i);
          } else {
            console.warn("[JGA Itinerary Merge] Dropping stale local entry (no day content):", { id: i.id, dest: i.dest });
          }
        });
        normalized.forEach(i => byDest.set(i.dest, i)); // backend overrides
        const merged = Array.from(byDest.values());
        try { localStorage.setItem("jga-itineraries", JSON.stringify(merged)); } catch(e) {}
        return merged;
      });
    })();
    return () => { cancelled = true; };
  }, [registered]); // eslint-disable-line
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(false);
  const [spinError, setSpinError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [affordableBookingDest, setAffordableBookingDest] = useState(null);
  const [affordableBookingBudget, setAffordableBookingBudget] = useState(null);

  // Explore location filter
  const [expRegion, setExpRegion] = useState(() => {
    try { return localStorage.getItem("jga-preferred-region") || "Anywhere"; } catch { return "Anywhere"; }
  });
  const [expSub, setExpSub] = useState(null);
  const [expCountry, setExpCountry] = useState(null);
  const [expState, setExpState] = useState(null);
  const [expDepartureAirport, setExpDepartureAirport] = useState(() => {
    try { return localStorage.getItem("jga-airport") || user?.departureAirport || ""; } catch(e) { return user?.departureAirport || ""; }
  });

  // Blind
  const [blindBudget, setBlindBudget] = useState("2000");
  const [blindDepartureAirport, setBlindDepartureAirport] = useState(() => {
    try { return localStorage.getItem("jga-airport") || user?.departureAirport || ""; } catch(e) { return user?.departureAirport || ""; }
  });

  // ── dashboardBudget → budget + blindBudget sync ──
  // Dashboard is the source of truth for the user's permanent budget. When
  // dashboardBudget changes (via the Budget cell on the dashboard), propagate
  // to TMA's `budget` and SM's `blindBudget` so all flows reflect the same
  // intent. Session overrides within TMA/SM still work — those setters mutate
  // local state only, and this effect only re-fires when dashboardBudget
  // itself changes (not on every render). resolveUserBudget() already
  // prioritizes dashboardBudget for MA + TT, so this completes the picture
  // for SM + TMA. Explore's pill infers from dashboardBudget via its own
  // effect (see exploreBudgetPill).
  useEffect(() => {
    if (dashboardBudget) {
      const v = String(dashboardBudget);
      setBudget(v);
      setBlindBudget(v);
    }
  }, [dashboardBudget]);

  const [flightPrices, setFlightPrices] = useState({}); // { "Paris": 420, "Rome": 380, ... }

  // Fetch flight prices when departure airport is set and results appear
  useEffect(() => {
    const airport = expDepartureAirport || blindDepartureAirport;
    if (!airport || !results?.length) return;
    results.forEach(async dest => {
      if (flightPrices[dest.name]) return; // already fetched
      const price = await fetchFlightPrice(airport, dest.name, departDate || null);
      if (price) setFlightPrices(p => ({ ...p, [dest.name]: price }));
    });
  }, [results, expDepartureAirport, blindDepartureAirport]); // eslint-disable-line

  // Pre-compute estimated flight prices for ALL destinations whenever airport changes
  // Uses the synchronous distance-based estimator so dashboard cards, trending,
  // and identity matches all show realistic prices immediately
  useEffect(() => {
    const airport = expDepartureAirport || blindDepartureAirport;
    if (!airport) return;
    const newPrices = {};
    DESTINATIONS.forEach(d => {
      if (!flightPrices[d.name]) {
        const est = estimateFlightPrice(airport, d.name, departDate || null);
        if (est) newPrices[d.name] = est;
      }
    });
    if (Object.keys(newPrices).length > 0) {
      setFlightPrices(p => ({ ...p, ...newPrices }));
    }
  }, [expDepartureAirport, blindDepartureAirport, departDate]); // eslint-disable-line
  const [blindVibes, setBlindVibes] = useState([]);

  // Restore identity vibes + prefs on mount — after both vibes states declared
  useEffect(() => {
    try {
      const savedType = localStorage.getItem("jga-traveler-type");
      if (savedType && TRAVEL_IDENTITIES[savedType]) {
        const id = TRAVEL_IDENTITIES[savedType];
        if (expVibes.length === 0) setExpVibes(id.vibes || []);
        if (blindVibes.length === 0) setBlindVibes(id.vibes || []);
      }
    } catch(e) {}
  }, []); // eslint-disable-line

  // ── Cross-device user-data sync ──────────────────────────────────
  // The auth refresh effect fetches /api/users/me after every successful
  // token refresh (every 12 minutes, plus on mount) and dispatches a
  // `jga:user-hydrated` event with the fresh user object. This listener
  // applies that data — backend wins, localStorage gets updated to match,
  // and the UI re-renders with the latest values.
  //
  // This is what makes a quiz retake on desktop propagate to mobile on
  // refresh, profile edits propagate cross-device, etc. Without this
  // listener the /me hydration was being fetched but ignored.
  useEffect(() => {
    function onUserHydrated(e) {
      const me = e?.detail;
      if (!me || typeof me !== "object") return;
      console.log("[JGA User Hydrate] Applying fresh /me data");

      // Travel identity — backend wins
      try {
        const backendIdentity = me.travelIdentity;
        if (backendIdentity && TRAVEL_IDENTITIES[backendIdentity]) {
          if (backendIdentity !== travelerType) {
            console.log("[JGA User Hydrate] Identity changed:", travelerType, "→", backendIdentity);
            setTravelerType(backendIdentity);
            setExpVibes(TRAVEL_IDENTITIES[backendIdentity].vibes || []);
            setBlindVibes(TRAVEL_IDENTITIES[backendIdentity].vibes || []);
            try { localStorage.setItem("jga-traveler-type", backendIdentity); } catch {}
          }
        }
      } catch {}

      // Travel companion — backend wins
      try {
        if (me.travelCompanion && me.travelCompanion !== travelCompanion) {
          console.log("[JGA User Hydrate] Companion changed:", travelCompanion, "→", me.travelCompanion);
          setTravelCompanion(me.travelCompanion);
          try { localStorage.setItem("jga-travel-companion", me.travelCompanion); } catch {}
        }
      } catch {}

      // User object — merge backend fields into local user state
      // (preserves anything the backend hasn't returned, like photoURL data URIs)
      try {
        setUser(prev => {
          if (!prev) return prev;
          const merged = { ...prev };
          // Only overwrite fields backend explicitly returned
          const overwriteFields = [
            "firstName", "lastName", "email", "emailVerified", "dob", "address",
            "ageRange", "ktn", "departureAirport",
            "avoidDestinations", "avoidAirlines", "avoidHotels",
            "ffPrograms", "hotelPrograms", "visibleAirlines", "visibleHotels",
            "passportCountry", "passportExpiry", // not passportNumber — that's encrypted
            "referralCode", "freeTripPasses",
            "companionChangedAt", "companionChangedFrom", "companionNudgeDismissedAt",
          ];
          overwriteFields.forEach(f => {
            if (me[f] !== undefined && me[f] !== null) merged[f] = me[f];
          });
          try { localStorage.setItem("jga-user", JSON.stringify(merged)); } catch {}
          return merged;
        });
      } catch {}
    }
    window.addEventListener("jga:user-hydrated", onUserHydrated);
    return () => window.removeEventListener("jga:user-hydrated", onUserHydrated);
  }, [travelerType, travelCompanion]);

  // ── Cross-device sync on tab focus (Safari workaround) ────────────
  // Auth refresh fires every 12 minutes, which means between refresh
  // cycles your other-device changes aren't picked up. Worse, Safari
  // (mobile AND desktop) sometimes restores tabs from BFCache without
  // running useEffect at all — so a normal reload doesn't help.
  //
  // Solution: when the user tabs BACK to the page (visibility goes from
  // hidden → visible), fire a fresh /me fetch and dispatch user-hydrated.
  // The existing listener applies the data. Throttled to once per 10s
  // so we don't hammer the backend if the user is tab-switching rapidly.
  //
  // This fixes the "took quiz on Chrome desktop, switched to Safari, saw
  // old identity" pattern that's bitten us repeatedly today.
  useEffect(() => {
    if (!registered) return;
    let lastFetchAt = 0;
    const MIN_GAP_MS = 10000;

    async function fetchMeAndDispatch(trigger) {
      const now = Date.now();
      if (now - lastFetchAt < MIN_GAP_MS) {
        console.log(`[JGA Visibility Sync] Skipping /me fetch from ${trigger} (last fetch ${Math.round((now - lastFetchAt) / 1000)}s ago, min gap ${MIN_GAP_MS / 1000}s)`);
        return;
      }
      lastFetchAt = now;
      let token = null;
      try { token = localStorage.getItem("jga_token"); } catch {}
      if (!token) return;
      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
      try {
        console.log(`[JGA Visibility Sync] Fetching /me (trigger: ${trigger})…`);
        const r = await fetch(`${API}/api/users/me`, {
          method: "GET",
          // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
          cache: "no-store", // critical for Safari
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (r.ok) {
          const me = await r.json().catch(() => null);
          if (me && typeof me === "object") {
            console.log(`[JGA Visibility Sync] /me result: identity=${me.travelIdentity}, companion=${me.travelCompanion}`);
            try { window.dispatchEvent(new CustomEvent("jga:user-hydrated", { detail: me })); } catch {}
          }
        } else if (r.status === 401) {
          // Don't tear down session — let the refresh effect handle it
          console.warn("[JGA Visibility Sync] /me returned 401, skipping");
        } else if (r.status === 404) {
          // User not found — account was deleted on the backend. The token
          // looks valid (it decodes fine, refresh path mints new access tokens)
          // but the user_id it references no longer exists. Treat as a dead
          // session and trigger the recovery flow. Without this, the user
          // sits in zombie state showing cached dashboard data until they
          // click something that hits a stricter route.
          console.warn("[JGA Visibility Sync] /me returned 404 — user not found, signaling session-expired");
          try { _signalSessionExpired("visibilitySync /me 404"); } catch {}
        } else if (r.status === 429) {
          // Rate limited — back off by pushing lastFetchAt 60 seconds into the
          // future so subsequent visibility events skip the fetch. Prevents the
          // sync from hammering the rate limiter and worsening the cascade.
          console.warn("[JGA Visibility Sync] /me returned 429 — backing off for 60s");
          lastFetchAt = Date.now() + 60000 - MIN_GAP_MS;
        } else {
          console.warn("[JGA Visibility Sync] /me returned", r.status);
        }
      } catch (e) {
        console.warn("[JGA Visibility Sync] /me fetch error:", e.message || e);
      }
    }

    // Fires when user switches tabs/apps and comes back.
    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        fetchMeAndDispatch("visibilitychange");
      }
    }

    // Fires when page is restored from BFCache (Safari's back-forward cache).
    // The `persisted` flag distinguishes BFCache restore from a fresh load.
    function onPageShow(e) {
      if (e.persisted) {
        console.log("[JGA Visibility Sync] Page restored from BFCache");
        fetchMeAndDispatch("pageshow-bfcache");
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [registered]);
  const [showBlindPayment, setShowBlindPayment] = useState(false);
  const [showCuratingModal, setShowCuratingModal] = useState(false);
  const [showWhyPopup, setShowWhyPopup] = useState(false);
  const [whyPopupShown, setWhyPopupShown] = useState(false);
  const [showExitQuestion, setShowExitQuestion] = useState(false);
  const [showNotBookingModal, setShowNotBookingModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showFooterFaq, setShowFooterFaq] = useState(false);
  const [carouselSlide, setCarouselSlide] = useState(0);
  const [carouselTouchStart, setCarouselTouchStart] = useState(null);

  // Show "why surprise trips" popup after 45s of being in blind flow without booking
  useEffect(() => {
    if (mode !== "blind" || showBlindPayment || showWhyPopup || whyPopupShown) return;
    const timer = setTimeout(() => {
      setShowWhyPopup(true);
      setWhyPopupShown(true);
    }, 45000);
    return () => clearTimeout(timer);
  }, [mode, showBlindPayment, showWhyPopup, whyPopupShown]); // eslint-disable-line
  const [blindSuggestion, setBlindSuggestion] = useState("");
  const [blindSuggestionSent, setBlindSuggestionSent] = useState(false);
  const [blindRegion, setBlindRegion] = useState(() => {
    try { return localStorage.getItem("jga-preferred-region") || "Anywhere"; } catch { return "Anywhere"; }
  });

  const [blindSub, setBlindSub] = useState(null);
  const [blindCountry, setBlindCountry] = useState(null);
  const [blindState, setBlindState] = useState(null);
  // ── Layover preference (SM only) ──
  // Direct flights typically cost ~25% more than connections; long layovers
  // can be 5-10% cheaper. Affects the SM flight cost estimate which in turn
  // affects which destinations match the budget.
  const [blindLayoverOk, setBlindLayoverOk] = useState([]);

  // ── Dynamic fee calculation ─────────────────────────────────
  // ── Domestic/international helpers ──
  // A booking is "domestic" if the actual destination's country is the US.
  // Previously this was determined by the user's TMA filter selections
  // (expCountry === "United States") — but that fails on most booking paths
  // (vibe pills, specific-destination taps, Mood Match, Most Affordable)
  // because the user never sets the country filter explicitly. The
  // destination's country IS reliably populated, so we use that.
  //
  // Also handles the string-mismatch bug: destinations use "USA", the
  // filter dropdown also offers "USA", but the old check compared to
  // "United States" — so even users who filtered to USA got an
  // international fee. Now defensive against all common variants.
  const _US_COUNTRY_FORMS = new Set(["USA", "US", "United States", "United States of America"]);
  function isDestDomestic(dest) {
    return !!(dest && dest.country && _US_COUNTRY_FORMS.has(dest.country));
  }
  function feeForDest(dest) {
    // V1 monetization is affiliate-only — no user-paid booking fees.
    // Kept as a function (not a constant) so a future Pro/concierge tier
    // can return real fees per-dest without re-architecting. domestic/
    // isDestDomestic kept above for the same reason.
    void isDestDomestic;
    return {
      serviceFee: 0,
      feeLabel: "",
    };
  }

  // V1: no curation fee on Surprise Me. The legacy SM payment flow is
  // already unreachable (replaced by waitlist preview), but zeroing the
  // constant here means even if it were reached, the row would hide.
  const isBlindDomestic = (() => {
    const regions = Array.isArray(blindRegion) ? blindRegion : [blindRegion];
    if (regions.length === 0 || regions.includes("Anywhere")) return false;
    return regions.every(r => r === "Americas") && _US_COUNTRY_FORMS.has(blindCountry);
  })();
  const blindCurationFee = 0;
  const blindFeeLabel = "";

  // V1: no service fee on Take Me Anywhere either (affiliate handoff).
  // Same pattern — kept as a constant for legacy call sites; future Pro
  // tier can set real values without ripping out the variable.
  const isExpDomestic = (() => {
    const regions = Array.isArray(expRegion) ? expRegion : [expRegion];
    if (regions.length === 0 || regions.includes("Anywhere")) return false;
    return regions.every(r => r === "Americas") && _US_COUNTRY_FORMS.has(expCountry);
  })();
  const expServiceFee = 0;
  const expFeeLabel = "";
  const [blindMonth, setBlindMonth] = useState(null);
  const [blindDuration, setBlindDuration] = useState(7);
  const [blindDateFrom, setBlindDateFrom] = useState("");
  const [blindBookingCode, setBlindBookingCode] = useState(() => {
    try { return localStorage.getItem("jga_pending_booking_code") || ""; }
    catch(e) { return ""; }
  });
  const [blindBookingCodeError, setBlindBookingCodeError] = useState("");
  const [blindBookingCodeRequested, setBlindBookingCodeRequested] = useState(false);
  // Inline validation state for the booking code (parity with explore gate)
  const [blindCodeStatus, setBlindCodeStatus] = useState(null); // null | "checking" | "valid" | "invalid"
  const [blindCodeRecord, setBlindCodeRecord] = useState(null);
  // ── Phase 1 waitlist state (SM) ───────────────────────────────
  // Mirrors the BookingModal Phase 1 flow. Users without a saved booking
  // code see a waitlist signup form at SM payment time instead of code entry.
  const [blindWaitlistNotes, setBlindWaitlistNotes] = useState("");
  const [blindWaitlistStatus, setBlindWaitlistStatus] = useState(null); // null | "sending" | "joined" | "error"
  const [blindDateTo, setBlindDateTo] = useState("");
  const [blindDateMode, setBlindDateMode] = useState("dates"); // dates | month | duration
  const [travelers, setTravelers] = useState(1);

  // MA trip length — user-controlled. Default 7 nights. Persisted so user's
  // preference survives reloads. Wired into DATE_WINDOWS so changing this
  // re-prices every MA card with the new hotel-nights math.
  const [maTripNights, setMaTripNights] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem("jga-ma-trip-nights") || "7", 10);
      return Number.isFinite(n) && n >= 2 && n <= 14 ? n : 7;
    } catch { return 7; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-ma-trip-nights", String(maTripNights)); } catch {}
  }, [maTripNights]);

  // MA trip month — user-controlled. null = "any time" (default DATE_WINDOWS
  // rotation across this month / next month / two weeks out). When set, it's
  // an offset from current month (0=this month, 1=next, ..., 5=five months
  // ahead). Drives the seasonal flight multiplier in estimateFlightPrice
  // (peak summer / Dec = +25%, shoulder = neutral, off-peak winter = -15%).
  // Persisted so the choice survives reloads.
  const [maTripMonthOffset, setMaTripMonthOffset] = useState(() => {
    try {
      const v = localStorage.getItem("jga-ma-trip-month-offset");
      if (v === null || v === "null") return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n >= 0 && n <= 5 ? n : null;
    } catch { return null; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-ma-trip-month-offset", maTripMonthOffset === null ? "null" : String(maTripMonthOffset)); } catch {}
  }, [maTripMonthOffset]);

  // MA budget override — session-level budget that takes precedence over
  // dashboardBudget WITHIN MA. null = "use dashboard budget" (default).
  // Per Cas: changes made in MA stay in MA and don't propagate back to the
  // dashboard. This lets users explore "what if I had $6k?" without committing
  // to it as their permanent dashboard intent. Persisted to localStorage.
  const [maBudgetOverride, setMaBudgetOverride] = useState(() => {
    try {
      const v = localStorage.getItem("jga-ma-budget-override");
      if (v === null || v === "null") return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  });
  useEffect(() => {
    try { localStorage.setItem("jga-ma-budget-override", maBudgetOverride === null ? "null" : String(maBudgetOverride)); } catch {}
  }, [maBudgetOverride]);
  const [travelingWithPet, setTravelingWithPet] = useState(false);
  const [travelingWithChildren, setTravelingWithChildren] = useState(false);
  const [blindIncludes, setBlindIncludes] = useState(["hotel","air"]);
  const [blindAgeRange, setBlindAgeRange] = useState(null);
  const [travelerAges, setTravelerAges] = useState([]);
  // NOTE: legacy `layoverOk` state was here. Removed when SM Flight
  // preferences was relocated to the top of the surface and rewired to
  // `blindLayoverOk` which actually drives the cost multiplier.
  const [hotelType, setHotelType] = useState([]); // multi-select
  const [transportType, setTransportType] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [blindResult, setBlindResult] = useState(null);
  const [blindRerollsUsed, setBlindRerollsUsed] = useState(0);
  const [showSwapConfirm, setShowSwapConfirm] = useState(false);
  const [blindRevealed, setBlindRevealed] = useState(false);
  const [blindCancelStep, setBlindCancelStep] = useState(0);
  const [blindCancelReason, setBlindCancelReason] = useState("");
  const [exitEmail, setExitEmail] = useState("");
  const [blindCard, setBlindCard] = useState({ name: "", number: "", expiry: "", cvv: "" });
  const [blindCardErrors, setBlindCardErrors] = useState({});
  const [blindProcessing, setBlindProcessing] = useState(false);
  const [blindAddInsurance, setBlindAddInsurance] = useState(false);
  const [blindLoading, setBlindLoading] = useState(false);
  const [envelopeOpen, setEnvelopeOpen] = useState(false);
  const [showBlindGate, setShowBlindGate] = useState(false);

  // ── Surprise Me v1: Preview + Waitlist state ──────────────────────────
  // Surprise Me ships at v1 launch as a teaser experience (not a buyable
  // product). User completes preferences → sees a spinning-globe animation →
  // 3 sample destinations matching their inputs are revealed → CTA to join
  // the waitlist for when real bookings open (post host-agency partnership).
  // This preserves the magic of the experience without the legal/operational
  // complexity of acting as a seller of travel.
  //
  // blindPreviewStep: null = inactive, "spinning" = wheel animation playing,
  // "revealed" = samples + waitlist CTA visible
  //
  // Note: "previewWaitlist*" prefix (not "blindWaitlist*") to avoid colliding
  // with the legacy payment-flow waitlist state at L23496 which is now
  // unreachable but kept in case the legacy flow gets re-enabled.
  const [blindPreviewStep, setBlindPreviewStep] = useState(null);
  const [blindPreviewSamples, setBlindPreviewSamples] = useState([]);
  const [previewWaitlistEmail, setPreviewWaitlistEmail] = useState("");
  const [previewWaitlistSubmitting, setPreviewWaitlistSubmitting] = useState(false);
  const [previewWaitlistStatus, setPreviewWaitlistStatus] = useState(null); // null | "ok" | "already" | "error"
  const [previewWaitlistError, setPreviewWaitlistError] = useState("");

  // Auto-validate any saved booking code when SM payment opens. Mirror of
  // the same logic in BookingModal — approved users with saved codes get
  // the silent "you're cleared to book" experience; others see waitlist.
  useEffect(() => {
    if (!showBlindPayment || !blindBookingCode.trim() || blindCodeStatus) return;
    let cancelled = false;
    (async () => {
      setBlindCodeStatus("checking");
      try {
        const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
        const r = await fetch(`${API}/api/admin/codes/${blindBookingCode.trim()}`);
        if (cancelled) return;
        if (r.ok) { const data = await r.json(); setBlindCodeRecord(data); setBlindCodeStatus("valid"); }
        else {
          setBlindCodeStatus("invalid");
          try { localStorage.removeItem("jga_pending_booking_code"); } catch(e) {}
        }
      } catch(e) { if (!cancelled) setBlindCodeStatus("invalid"); }
    })();
    return () => { cancelled = true; };
  }, [showBlindPayment]);

  // ── helpers ─────────────────────────────────────────────────

  // Single source of truth for "what budget should we assume?"
  //
  // The four modes (SM / TMA / TT / MA) are separate products and each keeps
  // its OWN explicit budget when the user sets one. This resolver answers the
  // fallback question — "what do we assume when a surface has no explicit
  // budget yet" (defaults, prefills, dashboard previews, fit-check badges).
  //
  // Usage:
  //   resolveUserBudget()                    → smart cascade (no explicit value)
  //   resolveUserBudget({ explicit: budget })→ respect the mode's own value,
  //                                            cascade only if it's blank
  //
  // Returns { value:Number, source:String } so callers can debug/telemetry
  // which input won. Replaces six divergent inline chains that used three
  // different defaults ($1000/$1200/$1500) and produced inconsistent
  // "fits your budget" results across surfaces.
  const AGE_GROUP_BUDGETS = {
    "under18": 600,    // Young traveler — typically family-funded short trips
    "18-24":   850,    // Students, hostels, budget travel
    "25-34":   1500,   // Explorers — most active travel age group
    "35-44":   2000,   // Established adventurers — more disposable income
    "45-54":   2400,   // Seasoned, often higher comfort
    "55-64":   2800,   // Freedom travelers, longer trips
    "65plus":  2400,   // Golden explorers — quality over quantity
  };
  const _parseBudget = (v) => {
    const n = parseInt(String(v ?? "").replace(/[^0-9]/g, ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  function resolveUserBudget(opts = {}) {
    const { explicit } = opts;
    // 1. A mode's own explicit budget always wins (four-products model)
    const explicitVal = _parseBudget(explicit);
    if (explicitVal) return { value: explicitVal, source: "explicit" };

    // 2. Fallback cascade — dashboard > TMA > SM > saved pref > age-typical > default
    const dash = _parseBudget(dashboardBudget);
    if (dash) return { value: dash, source: "dashboard" };

    const exp = _parseBudget(budget);
    if (exp) return { value: exp, source: "explore" };

    const blind = _parseBudget(blindBudget);
    if (blind) return { value: blind, source: "blind" };

    const pref = _parseBudget(user?.budgetPref);
    if (pref) return { value: pref, source: "preference" };

    const ageBudget = user?.ageRange ? AGE_GROUP_BUDGETS[user.ageRange] : null;
    if (ageBudget) return { value: ageBudget, source: "age" };

    return registered
      ? { value: 1500, source: "default-registered" }
      : { value: 1200, source: "default-unregistered" };
  }

  function filterPool(b, region, sub, ctry, st, travelersArg, includesArg, daysArg, transportPicksArg, activityPicksArg, hotelTypePicksArg, layoverPicksArg) {
    // Pull airport from any active state — distance-based flight estimate
    // sharpens the lower-bound check when set (LA→Tokyo vs Boston→Tokyo
    // can swing $400 with the same regional fallback otherwise).
    const airport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
    // Resolve group size: explicit arg wins, else derive from travelCompanion
    // (TMA's signal) or the travelers state (SM's signal), else solo.
    const companionMap = { solo: 1, couple: 2, friends: 3, family: 4 };
    const pax = travelersArg
      || companionMap[travelCompanion]
      || (travelers > 1 ? travelers : 0)
      || 1;
    // Days: use explicit arg, else state default of 5 to preserve legacy
    // call-site behavior. (State default is 7 but legacy filters all used 5.)
    const daysToUse = daysArg || 5;
    return DESTINATIONS.filter(d => {
      // Lower bound: user budget must cover the real trip components the user
      // says they're paying for (per includesArg) FOR THE GROUP. Group size
      // scales flights and part of on-ground cost; hotel is shared. Without
      // includes-awareness, a "flight only" budget would compare against the
      // full-trip floor and exclude everything affordable.
      const baselineCost = getBaselineTripCost(d, airport, daysToUse, pax, includesArg, transportPicksArg, activityPicksArg, hotelTypePicksArg, layoverPicksArg);
      const fitsBudget = b >= baselineCost;
      // NO upper-bound exclusion.
      //
      // Previous logic capped at maxBudget + flight + $400. Since the most
      // expensive destination tops out at maxBudget $5,000, ANY budget above
      // ~$6,000 excluded the entire dataset → zero results for high-budget
      // users (e.g. $8K–$20K from Chicago returned nothing). Hard bug.
      //
      // Rule now: if the user can afford the realistic floor, the destination
      // qualifies. Affording a trip easily is never a reason to hide it — a
      // high budget should see everything it can cover, not nothing. Relevance
      // ranking (showing pricier/closer-fit destinations first) belongs in the
      // SORT, not as a hard filter that can zero out results.
      const budgetOk = fitsBudget;
      // Region — supports array or single string
      const regionArr = Array.isArray(region) ? region : (region && region !== "Anywhere" ? [region] : []);
      const regionOk = regionArr.length === 0 || regionArr.includes(d.region);
      const subOk = !sub || d.subRegion === sub;
      // Country — supports array or single string
      const ctryArr = Array.isArray(ctry) ? ctry : (ctry ? [ctry] : []);
      const countryOk = ctryArr.length === 0 || ctryArr.includes(d.country);
      // State — supports array or single string
      const stArr = Array.isArray(st) ? st : (st ? [st] : []);
      const stateOk = stArr.length === 0 || stArr.includes(d.state);
      // ── Max travel time filter ──
      // If user has set a maxTravelTimeHours preference, drop destinations
      // whose realistic one-way flight exceeds it. Uses estimateFlightDurationHours
      // which factors in airport distance when known, falls back to subregion
      // average otherwise. null/undefined preference = no filtering.
      let travelTimeOk = true;
      const maxHrs = user?.maxTravelTimeHours;
      if (typeof maxHrs === "number" && maxHrs > 0) {
        const flightHrs = estimateFlightDurationHours(d, airport);
        if (typeof flightHrs === "number" && flightHrs > maxHrs) {
          travelTimeOk = false;
        }
      }
      // ── Companion fit filter ──
      // Hard-filter destinations that are clearly poor matches for the user's
      // chosen travel companion (family on a nightlife trip, romance for a
      // group of friends, etc). Uses companionFit() — anything below 0.7
      // means a multiplier penalty has compounded enough to signal a real
      // mismatch (family+nightlife=0.40, family+romance=0.50, friends+romance=0.65).
      // Above 0.7 stays in the pool. This is the missing wire-up that made
      // TMA's solo→couple toggle have no effect on the match count.
      let companionOk = true;
      if (travelCompanion) {
        const fit = companionFit(d, travelCompanion);
        if (typeof fit === "number" && fit < 0.7) companionOk = false;
      }
      return budgetOk && regionOk && subOk && countryOk && stateOk && travelTimeOk && companionOk;
    });
  }

  // ── Match-count delta watcher ──
  // Watches every TMA filter input. On settle (500ms after last change),
  // computes the delta vs the last settled count and shows "(−12)" or
  // "(+3)" next to the sticky pill for 3 seconds. Only active on TMA
  // setup screen (mode === "explore" && step === 0).
  useEffect(() => {
    if (mode !== "explore" || step !== 0) return;
    if (matchDeltaSettleTimer.current) clearTimeout(matchDeltaSettleTimer.current);
    matchDeltaSettleTimer.current = setTimeout(() => {
      const parsedB = parseInt(String(budget).replace(/[^0-9]/g, ""), 10) || 1000;
      const currentCount = filterPool(parsedB, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
      setLastSettledMatchCount(prev => {
        if (prev === null) return currentCount; // baseline, no delta
        if (currentCount !== prev) {
          setMatchDelta(currentCount - prev);
          if (matchDeltaHideTimer.current) clearTimeout(matchDeltaHideTimer.current);
          matchDeltaHideTimer.current = setTimeout(() => setMatchDelta(null), 3000);
        }
        return currentCount;
      });
    }, 500);
    return () => {
      if (matchDeltaSettleTimer.current) clearTimeout(matchDeltaSettleTimer.current);
    };
  }, [budget, expRegion, expSub, expCountry, expState, expIncludes, days, expTransportType, expActivityTypes, mode, step]);
  // Reset baseline when user leaves TMA setup
  useEffect(() => {
    if (mode !== "explore" || step !== 0) {
      setLastSettledMatchCount(null);
      setMatchDelta(null);
      if (matchDeltaSettleTimer.current) clearTimeout(matchDeltaSettleTimer.current);
      if (matchDeltaHideTimer.current) clearTimeout(matchDeltaHideTimer.current);
    }
  }, [mode, step]);

  // Finds the cheapest destination that matches the given non-budget
  // filters. Used by SM/TMA empty states to tell the user how much
  // budget would unlock a result (instead of a generic "no matches").
  // Returns { dest, cost } or null if no destinations match the filters
  // at all (e.g. country picked + no dests in DESTINATIONS for it).
  function minBudgetForFilters(region, sub, ctry, st, includesArg, daysArg) {
    const airport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
    const companionMap = { solo: 1, couple: 2, friends: 3, family: 4 };
    const pax = companionMap[travelCompanion] || (travelers > 1 ? travelers : 0) || 1;
    const daysToUse = daysArg || 5;
    const regionArr = Array.isArray(region) ? region : (region && region !== "Anywhere" ? [region] : []);
    const matching = DESTINATIONS.filter(d => {
      const regionOk = regionArr.length === 0 || regionArr.includes(d.region);
      const subOk = !sub || d.subRegion === sub;
      const ctryArr = Array.isArray(ctry) ? ctry : (ctry ? [ctry] : []);
      const countryOk = ctryArr.length === 0 || ctryArr.includes(d.country);
      const stArr = Array.isArray(st) ? st : (st ? [st] : []);
      const stateOk = stArr.length === 0 || stArr.includes(d.state);
      return regionOk && subOk && countryOk && stateOk;
    });
    if (!matching.length) return null;
    let best = null;
    for (const d of matching) {
      const cost = getBaselineTripCost(d, airport, daysToUse, pax, includesArg);
      if (!best || cost < best.cost) best = { dest: d, cost };
    }
    return best;
  }

  function trySpinExplore() {
    if (typeof window.jgaTrack === "function") window.jgaTrack("spin", { mode: "take_me_somewhere", spin_count: spinCount + 1 });
    if (!registered && spinCount >= FREE_SPINS) { setShowRegister(true); return; }
    const parsedBudget = parseInt(String(budget).replace(/\D/g, ""), 10) || 1000;
    const pool = filterPool(parsedBudget, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk);
    if (!pool.length) {
      // Find the cheapest option matching the user's region/country/state
      // filters so we can tell them the actual budget threshold needed.
      // Falls back to generic copy if no destinations exist for the filters
      // at all (e.g. obscure country with no DESTINATIONS coverage).
      const minOption = minBudgetForFilters(expRegion, expSub, expCountry, expState, expIncludes, days);
      if (minOption) {
        const rounded = Math.ceil(minOption.cost / 100) * 100;
        setSpinError(`Nothing fits $${parsedBudget.toLocaleString()} with those filters. The cheapest match would be ${minOption.dest.name} at about $${rounded.toLocaleString()} — raise your budget or pick a wider region.`);
      } else {
        setSpinError("No destinations in that region or country yet. Try widening to Anywhere or pick a different region.");
      }
      return;
    }
    // Remember the current batch so the user can return to it
    setResults(cur => { if (cur && cur.length) setPrevResults(cur); return cur; });
    setSpinCount(c => c + 1);
    recordSearch();
    setSpinning(true); setResults(null); setSelected(null); setAiItinerary(null);
    const iv = setInterval(() => {
      const r = pool[Math.floor(Math.random() * pool.length)];
      setSpinText(`${r.emoji} ${r.name}`);
    }, 80);
    setTimeout(() => {
      clearInterval(iv);
      const picks = [...pool].sort(() => Math.random() - 0.5).slice(0, 3);
      setResults(picks);
      setSeenNames(prev => Array.from(new Set([...prev, ...picks.map(d => d.name)])));
      setSpinning(false); setSpinText("");
    }, 2200);
  }

  // "See 10 more" — surfaces a larger batch of fresh destinations (excluding
  // ones already seen this session where possible). Same gate + animation as
  // the 3-pick spin. Saves the current batch so the user can go back.
  function spinMoreExplore() {
    if (typeof window.jgaTrack === "function") window.jgaTrack("spin", { mode: "take_me_somewhere", spin_count: spinCount + 1, batch: 10 });
    if (!registered && spinCount >= FREE_SPINS) { setShowRegister(true); return; }
    const parsedBudget = parseInt(String(budget).replace(/\D/g, ""), 10) || 1000;
    const pool = filterPool(parsedBudget, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk);
    if (!pool.length) { trySpinExplore(); return; } // reuse the no-match error path
    setResults(cur => { if (cur && cur.length) setPrevResults(cur); return cur; });
    setSpinCount(c => c + 1);
    setSpinning(true); setResults(null); setSelected(null); setAiItinerary(null);
    const iv = setInterval(() => {
      const r = pool[Math.floor(Math.random() * pool.length)];
      setSpinText(`${r.emoji} ${r.name}`);
    }, 80);
    setTimeout(() => {
      clearInterval(iv);
      // Prefer unseen destinations; if too few remain, top up from the full pool.
      const unseen = pool.filter(d => !seenNames.includes(d.name));
      const base = unseen.length >= 10 ? unseen : pool;
      const picks = [...base].sort(() => Math.random() - 0.5).slice(0, 10);
      setResults(picks);
      setSeenNames(prev => Array.from(new Set([...prev, ...picks.map(d => d.name)])));
      setSpinning(false); setSpinText("");
    }, 2200);
  }

  async function callAI(prompt) {
    try {
      const r = await fetch(`${_api()}/api/itinerary/generate`, {
        method: "POST",
        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      if (!r.ok) throw new Error(`Backend returned ${r.status}`);
      const data = await r.json();
      console.log("Raw backend response:", data.result?.slice(0, 200));
      const text = data.result || "";
      const start = text.indexOf("{");
      const end = text.lastIndexOf("}");
      if (start === -1 || end === -1) throw new Error(`No JSON found in: ${text.slice(0, 100)}`);
      const jsonStr = text.slice(start, end + 1);
      console.log("Parsed JSON string:", jsonStr.slice(0, 200));
      return JSON.parse(jsonStr);
    } catch (err) {
      console.error("callAI failed:", err.message);
      throw err;
    }
  }

  async function generateItinerary(dest, opts = {}) {
    setLoadingAI(true); setAiItinerary(null); setAiError(false);
    const parsedBudget = parseInt(String(budget).replace(/\D/g, ""), 10) || 1000;
    // Extract milestone context. Treat "just-because" as no-context so the
    // prompt doesn't change AI behavior for non-occasion trips.
    const milestone = opts.milestone || null;
    const milestoneType = milestone && milestone.type && milestone.type !== "just-because" ? milestone.type : null;
    const milestoneOther = milestone && milestone.type === "other" ? (milestone.otherText || "") : "";
    const milestoneNote = milestoneType
      ? (milestoneType === "other" && milestoneOther
          ? `This trip is for: ${milestoneOther}. Tailor restaurant, activity, and pacing recommendations to fit this occasion — but DO NOT mention the occasion explicitly in the day-by-day text. Just make the recommendations themselves fit.`
          : `This trip is a ${milestoneLabel(milestoneType) || milestoneType}. Tailor restaurant, activity, and pacing recommendations to fit this occasion — but DO NOT mention the occasion explicitly in the day-by-day text. Just make the recommendations themselves fit.`)
      : "";

    const fallback = {
      days: Array.from({ length: Math.min(days, 5) }, (_, i) => ({
        day: i + 1,
        title: ["Arrive & Explore", "Local Highlights", "Hidden Gems", "Culture & Food", "Farewell Day"][i] || `Day ${i + 1}`,
        morning: "Start the day with a local breakfast and explore the neighbourhood",
        afternoon: "Visit the main attractions and soak in the local culture",
        evening: "Dinner at a recommended local restaurant",
        tip: "Ask locals for their favourite hidden spots — they always know best",
      })),
      budgetBreakdown: {
        flights: `$${Math.round(parsedBudget * 0.4)}`,
        accommodation: `$${Math.round(parsedBudget * 0.35)}`,
        food: `$${Math.round(parsedBudget * 0.15)}`,
        activities: `$${Math.round(parsedBudget * 0.10)}`,
      },
      packingTip: `Pack layers and comfortable walking shoes — perfect for exploring ${dest.name}.`,
    };

    try {
      const loc = dest.state ? `${dest.name}, ${dest.state}, ${dest.country}` : `${dest.name}, ${dest.country}`;
      const dateContext = expDateMode === "dates" && departDate && returnDate
        ? `Travel dates: ${departDate} to ${returnDate}.`
        : expDateMode === "month" && expMonth !== null
        ? `Travel month: ${MONTHS[expMonth]}.`
        : "";
      const currencyNote = fahrenheit
        ? "Use USD ($) for all prices throughout."
        : `Use the local currency for ${dest.country} for all prices (e.g. £ for UK, € for Europe, ¥ for Japan, A$ for Australia, CA$ for Canada — whatever is standard for ${dest.country}).`;
      const tempNote = fahrenheit
        ? "Express all temperatures in Fahrenheit (°F)."
        : "Express all temperatures in Celsius (°C).";
      const j = await callAI(`Create a ${days}-day itinerary for ${loc}, budget $${parsedBudget}, included in budget: ${expIncludes.join(", ") || "open"}, traveller age range: ${expAgeRange || "open"}, vibes: ${expVibes.join(", ") || "open"}, hotel type: ${expHotelType.length ? expHotelType.join(", ") : "open"}, transport: ${expTransportType.length ? expTransportType.join(", ") : "open"}, activities: ${expActivityTypes.length ? expActivityTypes.join(", ") : "open"}. ${dateContext} ${milestoneNote} ${currencyNote} ${tempNote} Return ONLY JSON:
{"days":[{"day":1,"title":"string","morning":"string","afternoon":"string","evening":"string","tip":"string"}],"budgetBreakdown":{"flights":"$X","accommodation":"$X","food":"$X","activities":"$X"},"packingTip":"string"}`);
      setAiItinerary(j);
    } catch (e) {
      // Show fallback content instead of error screen
      setAiItinerary(fallback);
    }
    setLoadingAI(false);
  }

  async function runBlind() {
    const parsedBudget = parseInt(String(blindBudget).replace(/\D/g, ""), 10) || 1500;
    setBlindLoading(true); setBlindResult(null); setBlindRevealed(false); setEnvelopeOpen(false);
    recordSearch();

    // Pull avoid preferences from user profile
    const avoidDests = user?.avoidDestinations ? user.avoidDestinations.split(",").map(s => s.trim().toLowerCase()).filter(Boolean) : [];
    const avoidAirlinesList = user?.avoidAirlines ? user.avoidAirlines.split(",").map(s => s.trim()).filter(Boolean) : [];
    const avoidHotelsList = user?.avoidHotels ? user.avoidHotels.split(",").map(s => s.trim()).filter(Boolean) : [];

    try {
      let pool = filterPool(parsedBudget, blindRegion, blindSub, blindCountry, blindState, undefined, undefined, undefined, undefined, undefined, undefined, blindLayoverOk)
        .filter(d => blindVibes.length === 0 || blindVibes.some(v => d.tags.includes(v)))
        // Filter out avoided destinations and countries
        .filter(d => {
          const destLower = d.name.toLowerCase();
          const countryLower = (d.country || "").toLowerCase();
          return !avoidDests.some(a => destLower.includes(a) || countryLower.includes(a));
        });
      if (!pool.length) {
        // Same pricing model as filterPool — checks real trip cost (flight
        // + hotel + ground) not just on-ground minBudget. Otherwise the
        // fallback pool would happily return Paris for a $1,000 budget.
        const fallbackAirport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
        pool = DESTINATIONS.filter(d => {
          const baselineCost = getBaselineTripCost(d, fallbackAirport, 5);
          return parsedBudget >= baselineCost /* upper-bound ceiling removed: high budgets must never return zero */;
        }).filter(d => {
          const destLower = d.name.toLowerCase();
          const countryLower = (d.country || "").toLowerCase();
          return !avoidDests.some(a => destLower.includes(a) || countryLower.includes(a));
        });
      }
      // Final fallback — if all destinations are excluded, use full pool
      if (!pool.length) {
        const fallbackAirport2 = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
        pool = DESTINATIONS.filter(d => {
          const baselineCost = getBaselineTripCost(d, fallbackAirport2, 5);
          return parsedBudget >= baselineCost /* upper-bound ceiling removed: high budgets must never return zero */;
        });
      }

      // Last-ditch safety — if even the final fallback is empty (e.g.
      // user's budget is below every single destination's real cost),
      // surface a clear error rather than crashing on undefined dest.
      // The pre-flight check at the "Book My Surprise Trip" button
      // should catch most cases, but this guards against avoidDests +
      // narrow filters wiping out everything.
      if (!pool.length) {
        setBlindLoading(false);
        setBlindResult({ error: true, message: `Your filters and $${parsedBudget.toLocaleString()} budget didn't match any destinations. Try widening your region or removing some avoid filters.` });
        return;
      }

      const dest = pool[Math.floor(Math.random() * pool.length)];
      const month = blindDateFrom ? blindDateFrom : blindMonth !== null ? MONTHS[blindMonth] : "summer";
      const dateInfo = blindDateFrom && blindDateTo ? `Exact dates: ${blindDateFrom} to ${blindDateTo}.` : blindDateFrom ? `Departing around ${blindDateFrom}.` : blindMonth !== null ? `Month: ${MONTHS[blindMonth]}.` : "Dates flexible.";
      const loc = dest.state ? `${dest.name}, ${dest.state}, ${dest.country}` : `${dest.name}, ${dest.country}`;

      // Build avoid strings for AI prompt
      const avoidAirlinesStr = avoidAirlinesList.length ? `Avoid these airlines: ${avoidAirlinesList.join(", ")}.` : "";
      const avoidHotelsStr = avoidHotelsList.length ? `Avoid these hotel chains: ${avoidHotelsList.join(", ")}.` : "";

      // Build a fallback result in case AI fails
      const fallback = {
        emoji: dest.emoji,
        destination: dest.name,
        state: dest.state || null,
        country: dest.country,
        tagline: `An incredible ${blindDuration}-night adventure awaits you in ${dest.name}.`,
        flightEstimate: `$${Math.round(parsedBudget * 0.4)}–$${Math.round(parsedBudget * 0.5)} per person`,
        hotelName: `A curated hotel in ${dest.name}`,
        hotelStars: 4,
        hotelEstimate: `$${Math.round(parsedBudget * 0.07)} per night`,
        totalEstimate: `$${parsedBudget}`,
        confirmationCode: `TMA-${dest.name.slice(0,3).toUpperCase()}${Math.random().toString(36).slice(2,5).toUpperCase()}`,
        checkIn: `June 2026`,
        checkOut: `${blindDuration} nights later`,
        days: [
          { day: 1, title: "Arrive & Explore", highlight: "Settle in and discover the neighbourhood" },
          { day: 2, title: "Local Highlights", highlight: "Visit the top sights and local favourites" },
          { day: 3, title: "Hidden Gems", highlight: "Off the beaten path experiences" },
        ],
        packingTip: "Pack light and leave room for souvenirs!",
        surpriseFact: `${dest.name} is one of the most exciting destinations we could have picked for you.`,
      };

      try {
        const j = await callAI(`You are a travel booking AI. A traveler gave you full authority to book a surprise trip.
Destination: ${loc}. Duration: ${blindDuration} nights. Travelers: ${travelers}. Budget: $${parsedBudget}. ${dateInfo} Vibes: ${blindVibes.join(", ") || "open"}. Age range: ${blindAgeRange || "open"}. Included in budget: ${blindIncludes.join(", ") || "nothing specified"}.
Flight layovers: ${blindLayoverOk.length ? blindLayoverOk.join(", ") : "no preference"}.
Accommodation type: ${hotelType.length ? hotelType.join(", ") : "4-star or higher preferred"}.
Transport preference: ${transportType.length ? transportType.join(", ") : "no preference"}.
Activity interests: ${activityTypes.length ? activityTypes.join(", ") : "no preference"}.
${travelingWithPet ? "IMPORTANT: Traveling with a pet — recommend pet-friendly hotels and outdoor activities." : ""}
${travelingWithChildren ? "IMPORTANT: Traveling with children — prioritise family-friendly hotels, child-safe activities, and kid-friendly dining." : ""}
${avoidAirlinesStr} ${avoidHotelsStr}
Return ONLY this JSON (no markdown):
{"emoji":"${dest.emoji}","destination":"${dest.name}","state":${dest.state ? `"${dest.state}"` : "null"},"country":"${dest.country}","tagline":"one exciting sentence","flightEstimate":"$X–$Y per person","hotelName":"realistic hotel name","hotelStars":4,"hotelEstimate":"$X per night","totalEstimate":"$X","confirmationCode":"TMA-XXXXXX","checkIn":"realistic date in ${month} 2026","checkOut":"${blindDuration} nights after check-in","days":[{"day":1,"title":"short title","highlight":"best activity"},{"day":2,"title":"short title","highlight":"best activity"},{"day":3,"title":"short title","highlight":"best activity"}],"packingTip":"one sentence","surpriseFact":"one wild fun fact"}`);
        setBlindResult(j);
        if (typeof window.jgaTrack === "function") window.jgaTrack("surprise_me_complete", { destination: dest.name, country: dest.country, budget: parsedBudget, duration: blindDuration });
      } catch {
        setBlindResult(fallback);
        if (typeof window.jgaTrack === "function") window.jgaTrack("surprise_me_complete", { destination: dest.name, country: dest.country, budget: parsedBudget, duration: blindDuration, fallback: true });
      }
    } catch (e) { console.error("runBlind outer error:", e); setBlindResult({ error: true }); }
    setBlindLoading(false);
  }

  function handleRegisterSuccess(userData) {
    if (typeof window.jgaTrack === "function") {
      window.jgaTrack("sign_up", { method: "email", tier: "free" });
      window.jgaTrack("registered", { method: "email" });
    }
    try {
      const referralCode = "TMA-" + ((userData?.firstName || "USR").slice(0,3).toUpperCase()) + Math.random().toString(36).slice(2,5).toUpperCase();
      const safeUser = {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        email: userData?.email || "",
        dob: userData?.dob || "",
        address: userData?.address || "",
        hasPassport: userData?.hasPassport || false,
        ageRange: userData?.ageRange || null,
        subscriptionTier: "free",
        referralCode,
        referralCount: 0,
        ktn: "",
        departureAirport: "",
        avoidDestinations: "",
        avoidAirlines: "",
        avoidHotels: "",
        ffPrograms: {},
        // Email verification — pulled from backend response. Defaults to false (fail-closed)
        // so the banner shows on fresh registrations until the user clicks the email link.
        emailVerified: userData?.emailVerified === true,
      };
      setUser(safeUser);
      setRegistered(true);
      _resetSessionExpiredFlag(); // future expirations can re-signal
      _clearAnonymousState("register-success"); // clear pre-signup quiz/identity ghost data
      setShowRegister(false);
      try { localStorage.setItem("jga-user", JSON.stringify(safeUser)); } catch(e) {}
      // ── Identity restore: backend is source of truth ──
      // For brand new registrations, the backend may have just stored the identity
      // sent in the register payload. Prefer that over localStorage.
      try {
        const backendIdentity = userData.travelIdentity;
        // Only trust localStorage identity if a sessionStorage flag confirms it
        // was set THIS session (i.e., the user took the quiz on this device
        // before signing up). Without this guard, a second account registered
        // on the same browser would inherit the first account's identity.
        let sessionScopedIdentity = null;
        try {
          const sessionFlag = sessionStorage.getItem("jga-quiz-completed-this-session");
          const localIdentity = localStorage.getItem("jga-traveler-type");
          if (sessionFlag && localIdentity && sessionFlag === localIdentity) {
            sessionScopedIdentity = localIdentity;
          }
        } catch {}

        let chosen = null;
        if (backendIdentity && TRAVEL_IDENTITIES[backendIdentity]) {
          chosen = backendIdentity;
          console.log("[JGA Register] Using identity from backend:", chosen);
        } else if (sessionScopedIdentity && TRAVEL_IDENTITIES[sessionScopedIdentity]) {
          chosen = sessionScopedIdentity;
          console.log("[JGA Register] Backend has no identity; using session-scoped localStorage:", chosen);
        } else {
          // No backend identity, no session-scoped local identity. Clear any
          // stale localStorage identity from a previous account/session on
          // this browser so the new user sees a clean "take the quiz" prompt.
          try {
            const stale = localStorage.getItem("jga-traveler-type");
            if (stale) {
              console.warn("[JGA Register] Clearing stale localStorage identity from previous session:", stale);
              localStorage.removeItem("jga-traveler-type");
            }
          } catch {}
        }
        if (chosen) {
          setTravelerType(chosen);
          setExpVibes(TRAVEL_IDENTITIES[chosen].vibes || []);
          setBlindVibes(TRAVEL_IDENTITIES[chosen].vibes || []);
          try { localStorage.setItem("jga-traveler-type", chosen); } catch(e) {}
        } else {
          // Make sure local React state is also cleared for the new account
          setTravelerType(null);
        }
      } catch(e) {}
      // ── Post-register routing: booking-code arrival path ──
      // Same as handleLoginSuccess — if a booking code is pending (user came
      // via the approval email or used the "sign up instead" fallback in
      // LoginModal), route to the Trips tab so they immediately see their
      // pending trip.
      try {
        const pendingCode = localStorage.getItem("jga_pending_booking_code");
        if (pendingCode) {
          console.log("[JGA Register] Pending booking code detected, routing to Trips tab:", pendingCode);
          setAccountDefaultTab("trips");
          setTimeout(() => { reset(); setMode("account"); }, 0);
        }
      } catch {}
    } catch(err) {
      console.error("Register error:", err);
      setShowRegister(false);
    }
  }

  function handleLoginSuccess(userData) {
    // Returning user just logged in — don't auto-prompt them with the quiz
    setShowOnboarding(false);
    try { localStorage.setItem("jga-onboarded", "true"); } catch(e) {}
    // ── Identity restore: backend is sole source of truth ──
    // On login, the backend's identity (or absence of it) is authoritative.
    // We do NOT fall back to localStorage — if a previous account on this
    // browser had an identity, that's THEIR identity, not the current user's.
    // Cross-account leak: previously, if user A had "Digital Nomad" in
    // localStorage and user B (no identity yet) logged in on the same
    // browser, user B inherited Digital Nomad. Fixed by hard-clearing
    // localStorage when backend says no identity.
    try {
      const backendIdentity = userData.travelIdentity;
      if (backendIdentity && TRAVEL_IDENTITIES[backendIdentity]) {
        if (backendIdentity !== travelerType) {
          console.log("[JGA Login] Identity changed:", travelerType, "→", backendIdentity);
        }
        setTravelerType(backendIdentity);
        setExpVibes(TRAVEL_IDENTITIES[backendIdentity].vibes || []);
        setBlindVibes(TRAVEL_IDENTITIES[backendIdentity].vibes || []);
        // Mirror backend → localStorage for next session consistency
        try { localStorage.setItem("jga-traveler-type", backendIdentity); } catch(e) {}
      } else {
        // Backend says this user has no identity. Clear any stale localStorage
        // identity (which would be from a previous user on this browser).
        // Dashboard will prompt them to take the quiz.
        try {
          const stale = localStorage.getItem("jga-traveler-type");
          if (stale) {
            console.warn("[JGA Login] Clearing stale localStorage identity from previous user:", stale);
            localStorage.removeItem("jga-traveler-type");
          }
        } catch {}
        setTravelerType(null);
        setExpVibes([]);
        setBlindVibes([]);
      }
    } catch(e) {}

    // ── Hydrate travelCompanion + Phase 2 tracking from backend ──
    // Backend is source of truth. If user changed Style on another device,
    // we pick up the new value here. Same for the 2-day nudge tracking —
    // companionChangedAt + companionNudgeDismissedAt come from server and
    // overwrite localStorage for cross-device consistency.
    try {
      // Companion (the value, not the change tracking)
      if (userData.travelCompanion) {
        setTravelCompanion(userData.travelCompanion);
        try { localStorage.setItem("jga-travel-companion", userData.travelCompanion); } catch {}
      }

      // Change tracking
      if (userData.companionChangedAt && userData.companionChangedFrom) {
        // Backend has an active change tracked — mirror to localStorage
        try {
          localStorage.setItem("jga-companion-changed-at", JSON.stringify({
            from: userData.companionChangedFrom,
            to: userData.travelCompanion,
            at: new Date(userData.companionChangedAt).getTime(),
          }));
        } catch {}
      } else {
        // Backend has no active change (or it was reverted server-side).
        // Clear localStorage to match — orphan tracking from a previous
        // session shouldn't trigger a nudge for a change the backend
        // already considers resolved.
        try { localStorage.removeItem("jga-companion-changed-at"); } catch {}
      }

      // Nudge dismissal
      if (userData.companionNudgeDismissedAt) {
        try {
          localStorage.setItem("jga-companion-nudge-dismissed-at",
            String(new Date(userData.companionNudgeDismissedAt).getTime()));
        } catch {}
      } else {
        try { localStorage.removeItem("jga-companion-nudge-dismissed-at"); } catch {}
      }
    } catch(e) {
      console.warn("[JGA Login] Companion hydration error", e);
    }
    if (typeof window.jgaTrack === "function") window.jgaTrack("login", { method: "email" });
    // Only generate referral code if not already provided
    const referralCode = userData.referralCode || ("JGA-" + (userData.firstName.slice(0,3).toUpperCase()) + Math.random().toString(36).slice(2,6).toUpperCase());
    const pastUnsurveyed = [
      { dest: "Paris", emoji: "🇫🇷", date: "Mar 3–8, 2026", code: "TMA-PAR3K7", returnDate: "2026-03-08" },
    ].filter(t => !completedSurveys.includes(t.code));
    setUser({
      ...userData,
      referralCode,
      referralCount: userData.referralCount ?? 0,
      pendingSurvey: userData.pendingSurvey || pastUnsurveyed[0] || null,
    });
    setRegistered(true);
    _resetSessionExpiredFlag(); // future expirations can re-signal
    _clearAnonymousState("login-success"); // clear pre-signup quiz/identity ghost data
    setShowLogin(false);
    // ── Post-login routing: booking-code arrival path ──
    // If the user arrived via the "you're cleared to book" approval email
    // (URL had ?code=TMA-XXX), open the Trips tab so they immediately see
    // their pending trip with the booking code, ready to book. Detected
    // via the localStorage stash set by the URL handler. The trip itself
    // shows up via GET /api/trips — backend should auto-resolve pending
    // trips matching the user's email (see handoff doc).
    try {
      const pendingCode = localStorage.getItem("jga_pending_booking_code");
      if (pendingCode) {
        console.log("[JGA Login] Pending booking code detected, routing to Trips tab:", pendingCode);
        setAccountDefaultTab("trips");
        // Defer the mode change until after the dashboard mounts so we
        // don't race with handleLoginSuccess's other state updates.
        setTimeout(() => { reset(); setMode("account"); }, 0);
      }
    } catch {}
  }

  const TOP_TRAVELED = [
    { name: "Bangkok", country: "Thailand", emoji: "🇹🇭", stat: "#1 most visited city worldwide", travelers: "22M+ visitors/year" },
    { name: "Paris", country: "France", emoji: "🇫🇷", stat: "Most iconic city in Europe", travelers: "19M+ visitors/year" },
    { name: "Dubai", country: "UAE", emoji: "🇦🇪", stat: "Fastest growing destination", travelers: "16M+ visitors/year" },
    { name: "Tokyo", country: "Japan", emoji: "🇯🇵", stat: "Top-rated city in Asia", travelers: "15M+ visitors/year" },
    { name: "New York City", country: "USA", emoji: "🇺🇸", stat: "Most visited US city", travelers: "14M+ visitors/year" },
    { name: "Barcelona", country: "Spain", emoji: "🇪🇸", stat: "Top beach & culture combo", travelers: "12M+ visitors/year" },
    { name: "Marrakech", country: "Morocco", emoji: "🇲🇦", stat: "Africa's most visited destination", travelers: "11M+ visitors/year" },
    { name: "Cairo", country: "Egypt", emoji: "🇪🇬", stat: "Gateway to the ancient world", travelers: "10M+ visitors/year" },
    { name: "Cape Town", country: "South Africa", emoji: "🇿🇦", stat: "Africa's most scenic city", travelers: "5M+ visitors/year" },
    { name: "Abu Dhabi", country: "UAE", emoji: "🇦🇪", stat: "Middle East's cultural capital", travelers: "8M+ visitors/year" },
    { name: "Amman", country: "Jordan", emoji: "🇯🇴", stat: "Gateway to Petra & the Dead Sea", travelers: "6M+ visitors/year" },
    { name: "Nairobi", country: "Kenya", emoji: "🇰🇪", stat: "East Africa's safari hub", travelers: "4M+ visitors/year" },
  ];

  const TOP_AFFORDABLE = [
    { name: "Chiang Mai", country: "Thailand", emoji: "🇹🇭", stat: "From $35/day all-in", budget: "$400–$800 / week" },
    { name: "Hoi An", country: "Vietnam", emoji: "🇻🇳", stat: "Most affordable coastal gem", budget: "$350–$700 / week" },
    { name: "Marrakech", country: "Morocco", emoji: "🇲🇦", stat: "Culture & color for less", budget: "$400–$900 / week" },
    { name: "Budapest", country: "Hungary", emoji: "🇭🇺", stat: "Cheapest European capital", budget: "$450–$950 / week" },
    { name: "Cartagena", country: "Colombia", emoji: "🇨🇴", stat: "Caribbean luxury on a budget", budget: "$500–$1,000 / week" },
    { name: "Bali", country: "Indonesia", emoji: "🇮🇩", stat: "Luxury feel, budget price", budget: "$500–$1,100 / week" },
    { name: "Cairo", country: "Egypt", emoji: "🇪🇬", stat: "Ancient wonders at low cost", budget: "$350–$750 / week" },
    { name: "Nairobi", country: "Kenya", emoji: "🇰🇪", stat: "Safari base without the splurge", budget: "$500–$1,000 / week" },
    { name: "Amman", country: "Jordan", emoji: "🇯🇴", stat: "Middle East's most affordable gateway", budget: "$450–$900 / week" },
    { name: "Accra", country: "Ghana", emoji: "🇬🇭", stat: "West Africa's rising star", budget: "$400–$800 / week" },
  ];

  function reset() {
    setMode(null);
    setBottomTab(null);
    window.scrollTo({ top: 0, behavior: "smooth" }); setStep(0); setResults(null); setSelected(null); setAiItinerary(null);
    setBudget("2000"); setExpIncludes(["hotel","air"]); setExpAgeRange(null); setExpVibes([]); setExpHotelType([]); setExpTransportType([]); setExpActivityTypes([]); setExpDateMode("dates"); setExpMonth(null); setDepartDate(""); setReturnDate("");
    setBlindResult(null); setBlindRevealed(false); setBlindVibes([]);
    setBlindRegion("Anywhere"); setBlindSub(null); setBlindCountry(null); setBlindState(null);
    setBlindMonth(null); setEnvelopeOpen(false); setBlindIncludes(["hotel","air"]); setBlindAgeRange(null); setBlindDateFrom(""); setBlindDateTo(""); setBlindDateMode("dates");
    setBlindLayoverOk([]); setHotelType([]); setTransportType([]); setActivityTypes([]);
    setExpRegion("Anywhere"); setExpSub(null); setExpCountry(null); setExpState(null);
    setLockedTripDest(null);
  }

  const spinsLeft = FREE_SPINS - spinCount;
  const canSpin = registered || spinCount < FREE_SPINS;

  // ── Listen for modal triggers from child components ─────────
  useEffect(() => {
    const loginHandler = () => setShowLogin(true);
    const registerHandler = () => setShowRegister(true);
    const forgotHandler = () => setShowForgotPassword(true);
    document.addEventListener("jga:openLogin", loginHandler);
    document.addEventListener("jga:openRegister", registerHandler);
    document.addEventListener("jga:openForgotPassword", forgotHandler);
    return () => {
      document.removeEventListener("jga:openLogin", loginHandler);
      document.removeEventListener("jga:openRegister", registerHandler);
      document.removeEventListener("jga:openForgotPassword", forgotHandler);
    };
  }, []);
  useEffect(() => {
    if (mode) {
      setShowIdleBanner(false);
      setShowDestPreview(false);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (destPreviewTimer.current) clearTimeout(destPreviewTimer.current);
      if (destCycleTimer.current) clearInterval(destCycleTimer.current);
      return;
    }
    // Show destination preview at 10s
    destPreviewTimer.current = setTimeout(() => setShowDestPreview(true), 10000);
    // Cycle through destinations every 2.5s
    destCycleTimer.current = setInterval(() => setDestPreviewIdx(i => (i + 1) % DESTINATIONS.length), 2500);
    // Show idle banner at 25 minutes (only after extended inactivity)
    idleTimer.current = setTimeout(() => setShowIdleBanner(true), 25 * 60 * 1000);
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (destPreviewTimer.current) clearTimeout(destPreviewTimer.current);
      if (destCycleTimer.current) clearInterval(destCycleTimer.current);
    };
  }, [mode]);

  // ── Render ──────────────────────────────────────────────────
  // ── Theme tokens (Phase 3 of editorial sweep) ───────────────
  // The editorial direction is committed: cream parchment surfaces, burgundy
  // ink, gold accents, serif italic headlines. darkMode is locked to false
  // (see Phase 1 above), so only the SECOND branch of each `darkMode ? X : Y`
  // ternary resolves. We've updated those second branches to editorial values.
  // The ternaries themselves will be collapsed in Phase 4 as a cleanup pass.
  // Touching these tokens cascades through ~487 sites across this file.
  const bg         = darkMode
    ? "linear-gradient(135deg,#0e1620 0%,#15202b 40%,#1a1f30 100%)"           // dead branch (darkMode locked false)
    : "linear-gradient(180deg,#F4F9FD 0%,#EAF3FA 60%,#E0EDF7 100%)";          // warm white, subtle top→bottom drift
  const cardBg      = darkMode ? "rgba(28,42,58,0.95)"   : "#FAFAF5";          // cream parchment for all cards
  const textPrimary = darkMode ? "#ffffff"               : "#1B3A5C";          // sky-deep for headlines/body (was #1B3A5C ink-dark)
  const textMuted   = darkMode ? "#4A6B8A"               : "#4A6B8A";          // sky-secondary for body text (was #4A6B8A ink-mid)
  const textDim     = darkMode ? "#4A6B8A"               : "#94A3B8";          // sky-tertiary for captions/refs (was #94A3B8 dim ink)
  const borderColor = darkMode ? "rgba(27,58,92,0.14)": "rgba(27,58,92,0.18)";  // burgundy hairline
  const surfaceBg   = darkMode ? "rgba(27,58,92,0.096)": "rgba(27,58,92,0.06)";  // cream-tint surface (warm wash)
  // Mode-aware gold/amber/cream TEXT colors — the light branches are deep
  // editorial ink shades suitable for cream parchment backgrounds. The brand
  // constant `gold` (#4A8CC4) is unchanged — it remains the decorative gradient
  // color, while these tokens are READING colors that need real contrast.
  const goldText   = darkMode ? "#4A8CC4"               : "#1B3A5C";   // pale gold → deep tobacco
  const amberText  = darkMode ? "#4A8CC4"               : "#1B3A5C";   // amber → deep tobacco
  const creamText  = darkMode ? "#FAFAF5"               : "#1B3A5C";   // cream → editorial dark ink

  // ── Editorial direction tokens (FIXED, not mode-aware) ──
  // The new visual language: cream parchment for collection surfaces,
  // burgundy ink for postmarks and accent text, serif italic for headlines.
  // These tokens are FIXED — they don't depend on darkMode. Surfaces that
  // commit to the editorial direction read from these regardless of toggle.
  // Mirrors the Stamps spec palette so the system is consistent across
  // Profile, Trips, Boards, Stamps Wall, and any future collection surface.
  const editorialCream      = "#FAFAF5";   // primary cream parchment background
  const editorialCreamDeep  = "#F0EDE3";   // deeper cream for sub-surfaces
  const editorialBorder     = "#B8C9D4";   // hairline border between cream surfaces
  const editorialInkBurgundy= "#1B3A5C";   // postmarks, country lines, state pills
  const editorialInkDark    = "#1B3A5C";   // titles, primary text on cream
  const editorialInkMid     = "#4A6B8A";   // taglines, italic labels, secondary text on cream

  // ── Canonical editorial palette (Phase 0 of full editorial sweep) ──
  // After the sweep, every surface reads from these tokens. They're the FINAL
  // names; the editorial* aliases above remain only for backward compat with
  // Profile + Surprise Me (already shipped) until the sweep finishes converting
  // them. New code uses these canonical names.
  const bgCream      = "#FAFAF5";   // page-level cream parchment background
  const bgCreamDeep  = "#F0EDE3";   // deeper cream for sub-surfaces / cards on cream pages
  const bgDarkAccent = "#15202b";   // rare dark surface (booking confirm, delete confirm)
  const inkDark      = "#1B3A5C";   // primary text on cream
  const inkMid       = "#4A6B8A";   // secondary / italic text on cream
  const inkBurgundy  = "#1B3A5C";   // primary accent (CTAs, postmarks, active states)
  const inkGold      = "#4A8CC4";   // secondary accent (eyebrow uppercase, dividers)
  const inkOnDark    = "#FAFAF5";   // text color on rare bgDarkAccent surfaces
  const borderCream  = "#B8C9D4";   // hairline border on cream
  // Bottom nav tokens — nav goes burgundy/gold per editorial direction
  const navBg     = "#1B3A5C";
  const navInk    = "#FAFAF5";
  const navAccent = "#4A8CC4";
  const dynCard     = { ...cardStyle, background: cardBg,
    border: darkMode ? "1px solid rgba(74,140,196,0.18)" : "1px solid rgba(27,58,92,0.2)",
    boxShadow: darkMode
      ? "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(27,58,92,0.060)"
      : "0 2px 20px rgba(27,58,92,0.1), 0 1px 4px rgba(27,58,92,0.06)",
    borderRadius: 20,
  };

  return (
    <JGAErrorBoundary>
    <ThemeContext.Provider value={{ darkMode, textPrimary, textMuted, textDim, borderColor, surfaceBg, cardBg, goldText, amberText, creamText, editorialCream, editorialCreamDeep, editorialBorder, editorialInkBurgundy, editorialInkDark, editorialInkMid, bgCream, bgCreamDeep, bgDarkAccent, inkDark, inkMid, inkBurgundy, inkGold, inkOnDark, borderCream, navBg, navInk, navAccent }}>
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: textPrimary, transition: "background 0.4s, color 0.4s" }}>
      {/* Toast banner — surfaces silent failures (backend DELETE, sync errors)
         to the user. Critical on mobile where DevTools isn't available. Fixed
         bottom-center, above bottom nav, auto-dismiss after 5s. Tappable to
         dismiss early. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          onClick={() => setToast(null)}
          style={{
            position: "fixed",
            bottom: 80, // Above bottom nav
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            maxWidth: "92%",
            minWidth: 280,
            padding: "12px 16px",
            borderRadius: 12,
            background: toast.type === "error"
              ? (darkMode ? "rgba(180, 60, 60, 0.95)" : "rgba(220, 80, 80, 0.98)")
              : (darkMode ? "rgba(40, 60, 90, 0.95)" : "rgba(60, 80, 110, 0.98)"),
            border: `1px solid ${toast.type === "error" ? "rgba(255,200,200,0.3)" : "rgba(27,58,92,0.14)"}`,
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            cursor: "pointer",
            animation: "fadeIn 0.25s ease-out",
            textAlign: "center",
            lineHeight: 1.4,
          }}>
          {toast.message}
          <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.75, marginTop: 4 }}>tap to dismiss</div>
        </div>
      )}      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 0, background: darkMode
        ? "radial-gradient(ellipse at 15% 15%,rgba(255,200,80,0.04) 0%,transparent 55%), radial-gradient(ellipse at 85% 85%,rgba(27,58,92,0.05) 0%,transparent 55%)"
        : "radial-gradient(ellipse at 20% 10%,rgba(27,58,92,0.12) 0%,transparent 55%), radial-gradient(ellipse at 80% 90%,rgba(27,58,92,0.08) 0%,transparent 50%)" }} />
      {/* ── Film grain texture layer ─────────────────────────────────
         Subtle monochromatic noise across the viewport to add depth and
         "premium print" feel. Single SVG <feTurbulence> filter rendered
         once per viewport, then GPU-cached. `overlay` blend mode is more
         visible than `screen`/`multiply` across browsers — adjust opacity
         down once confirmed visible.
         pointer-events: none so clicks pass through. fixed position so
         it stays put while scrolling (atmosphere, not content).        */}
      <svg aria-hidden="true" focusable="false" style={{
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
        pointerEvents: "none", zIndex: 1,
        opacity: darkMode ? 0.08 : 0.07,
        mixBlendMode: "overlay",
      }}>
        <filter id="jga-film-grain">
          {/* baseFrequency 0.85 = fine grain (premium photo) vs lower values (rough) */}
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" seed="3"/>
          <feColorMatrix type="saturate" values="0"/>
        </filter>
        <rect width="100%" height="100%" filter="url(#jga-film-grain)"/>
      </svg>
      {/* Session-expired banner — appears when 401 detected on any API call */}
      {sessionExpiredMsg && (
        <div role="alert" aria-live="polite"
          onClick={() => setSessionExpiredMsg(null)}
          style={{
            position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
            zIndex: 999, maxWidth: 420, width: "calc(100% - 32px)",
            padding: "12px 18px",
            borderRadius: 12,
            background: darkMode ? "rgba(255,166,109,0.15)" : "rgba(255,166,109,0.18)",
            border: "1px solid rgba(255,166,109,0.4)",
            color: darkMode ? "#ffa66d" : "#a04420",
            fontSize: 13, fontWeight: 600,
            WebkitBackdropFilter: "blur(12px)", backdropFilter: "blur(12px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            cursor: "pointer",
            display: "flex", alignItems: "center", gap: 10,
            animation: "jga-toast-in 0.3s ease-out",
          }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M12 7 L12 13 M12 16 L12 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <span style={{ flex: 1 }}>{sessionExpiredMsg}</span>
          <span style={{ fontSize: 11, opacity: 0.7 }}>tap to dismiss</span>
        </div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes spinAnim{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
        /* ── Desktop editorial side rails ──
           On wide viewports the centered 680px content has hundreds of px of
           empty space on each side, which reads "designed for mobile, dropped
           on desktop." These rails fill that space with subtle editorial
           decoration that disappears below 1024px (no mobile interference).
           Left rail: vertical "JUST GO ANYWHERE · NO. 1" label, like a
              magazine margin slug.
           Right rail: editorial coordinate stamp + small compass glyph.
           Both faded to ~25% opacity — they should read as page margin,
           not as content. ─────────────────────────────────────────────────── */
        .jga-dashboard-frame { position: relative; }
        @media (min-width: 1024px) {
          .jga-dashboard-frame::before,
          .jga-dashboard-frame::after {
            content: "";
            position: absolute;
            top: 40px;
            bottom: 40px;
            width: 220px;
            pointer-events: none;
            font-family: "Cormorant Garamond", Georgia, serif;
            color: rgba(27,58,92, 0.32);
            background-repeat: no-repeat;
          }
          .jga-dashboard-frame::before {
            left: -260px;
            background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 220 800' fill='none'%3E%3C!-- Vertical rule --%3E%3Cline x1='10' y1='40' x2='10' y2='760' stroke='%231B3A5C' stroke-width='0.5' opacity='0.25'/%3E%3C!-- Top: VOL. I · ED. 2026 marker --%3E%3Cg transform='translate(40 70)'%3E%3Cline x1='-10' y1='0' x2='40' y2='0' stroke='%231B3A5C' stroke-width='0.6' opacity='0.4'/%3E%3Ctext x='-10' y='14' font-family='Cormorant Garamond, Georgia, serif' font-style='italic' font-size='10' fill='%231B3A5C' opacity='0.38' letter-spacing='1.5'%3EVOL. I %C2%B7 ED. 2026%3C/text%3E%3Cline x1='-10' y1='20' x2='80' y2='20' stroke='%231B3A5C' stroke-width='0.4' opacity='0.3'/%3E%3C/g%3E%3C!-- Middle: rotated headline + dot marker --%3E%3Ctext x='40' y='400' font-family='Cormorant Garamond, Georgia, serif' font-size='13' fill='%231B3A5C' opacity='0.32' letter-spacing='6' transform='rotate(-90 40 400)'%3EJUST GO ANYWHERE %C2%B7 NO. 1 %C2%B7 EDITORIAL%3C/text%3E%3Ccircle cx='10' cy='400' r='3' fill='%234A8CC4' opacity='0.6'/%3E%3C!-- Bottom: page-number ornament --%3E%3Cg transform='translate(35 740)'%3E%3Cline x1='-5' y1='0' x2='15' y2='0' stroke='%231B3A5C' stroke-width='0.5' opacity='0.35'/%3E%3Ctext x='5' y='14' font-family='Cormorant Garamond, Georgia, serif' font-style='italic' font-size='12' fill='%231B3A5C' opacity='0.4' text-anchor='middle' letter-spacing='3'%3E%E2%80%94 I %E2%80%94%3C/text%3E%3Cline x1='-5' y1='22' x2='15' y2='22' stroke='%231B3A5C' stroke-width='0.5' opacity='0.35'/%3E%3C/g%3E%3C/svg%3E");
            background-position: left top;
            background-size: 220px 100%;
          }
          .jga-dashboard-frame::after {
            right: -260px;
            background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 220 800' fill='none'%3E%3C!-- Vertical rule --%3E%3Cline x1='210' y1='40' x2='210' y2='760' stroke='%231B3A5C' stroke-width='0.5' opacity='0.25'/%3E%3C!-- Top: ESTABLISHED wordmark --%3E%3Cg transform='translate(180 80)'%3E%3Cline x1='-50' y1='0' x2='30' y2='0' stroke='%231B3A5C' stroke-width='0.5' opacity='0.35'/%3E%3Ctext x='-10' y='14' font-family='Cormorant Garamond, Georgia, serif' font-style='italic' font-size='10' fill='%231B3A5C' opacity='0.38' text-anchor='middle' letter-spacing='3'%3EESTABLISHED 2026%3C/text%3E%3Cline x1='-50' y1='22' x2='30' y2='22' stroke='%231B3A5C' stroke-width='0.4' opacity='0.3'/%3E%3C/g%3E%3C!-- Upper-middle: rotated coordinates --%3E%3Ctext x='180' y='200' font-family='Cormorant Garamond, Georgia, serif' font-style='italic' font-size='11' fill='%231B3A5C' opacity='0.32' letter-spacing='2' transform='rotate(90 180 200)'%3E35.5%C2%B0 N %C2%B7 80.8%C2%B0 W %C2%B7 ALL DIRECTIONS%3C/text%3E%3C!-- Middle: compass --%3E%3Cg transform='translate(180 460)'%3E%3Ccircle cx='0' cy='0' r='14' fill='none' stroke='%231B3A5C' stroke-width='0.8' opacity='0.4'/%3E%3Ccircle cx='0' cy='0' r='2' fill='%231B3A5C' opacity='0.45'/%3E%3Cline x1='0' y1='-14' x2='0' y2='-8' stroke='%231B3A5C' stroke-width='1' opacity='0.6'/%3E%3Cline x1='0' y1='8' x2='0' y2='14' stroke='%231B3A5C' stroke-width='0.5' opacity='0.4'/%3E%3Cline x1='-14' y1='0' x2='-8' y2='0' stroke='%231B3A5C' stroke-width='0.5' opacity='0.4'/%3E%3Cline x1='8' y1='0' x2='14' y2='0' stroke='%231B3A5C' stroke-width='0.5' opacity='0.4'/%3E%3Ctext x='0' y='30' font-family='Cormorant Garamond, Georgia, serif' font-size='9' font-style='italic' fill='%231B3A5C' opacity='0.4' text-anchor='middle' letter-spacing='2'%3EN%3C/text%3E%3C/g%3E%3C!-- Lower: cross-hair coordinate stamp --%3E%3Cg transform='translate(180 640)'%3E%3Cline x1='-12' y1='0' x2='12' y2='0' stroke='%231B3A5C' stroke-width='0.5' opacity='0.35'/%3E%3Cline x1='0' y1='-12' x2='0' y2='12' stroke='%231B3A5C' stroke-width='0.5' opacity='0.35'/%3E%3Ccircle cx='0' cy='0' r='2' fill='none' stroke='%234A8CC4' stroke-width='0.6' opacity='0.55'/%3E%3Ctext x='0' y='28' font-family='Cormorant Garamond, Georgia, serif' font-size='8.5' font-style='italic' fill='%231B3A5C' opacity='0.4' text-anchor='middle' letter-spacing='1.5'%3Eyou are here%3C/text%3E%3C/g%3E%3C!-- Bottom: continued mark --%3E%3Cg transform='translate(180 740)'%3E%3Ctext x='0' y='0' font-family='Cormorant Garamond, Georgia, serif' font-style='italic' font-size='11' fill='%231B3A5C' opacity='0.35' text-anchor='middle' letter-spacing='2'%3E%E2%9C%A6 %E2%80%94 ad infinitum%3C/text%3E%3C/g%3E%3C/svg%3E");
            background-position: right top;
            background-size: 220px 100%;
          }
        }
        @media (min-width: 1280px) {
          .jga-dashboard-frame::before { left: -300px; width: 250px; }
          .jga-dashboard-frame::after { right: -300px; width: 250px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .jga-wheel, [style*="pulse"], [style*="spinAnim"], [style*="shimmer"], [style*="flagGlow"] {
            animation: none !important;
          }
        }
        @keyframes titleSlide{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes emojiPop{0%{transform:scale(0.5);opacity:0}70%{transform:scale(1.2)}100%{transform:scale(1);opacity:1}}
        @keyframes revealBurst{0%{transform:scale(0.3) rotate(-10deg);opacity:0}60%{transform:scale(1.15) rotate(3deg);opacity:1}80%{transform:scale(0.95) rotate(-1deg)}100%{transform:scale(1) rotate(0deg);opacity:1}}
        @keyframes confettiFall{0%{transform:translateY(-20px) rotate(0deg);opacity:1}100%{transform:translateY(80px) rotate(720deg);opacity:0}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        /* Hand-wave for "Welcome back" greetings — waves once, pauses, repeats */
        @keyframes jga-wave{0%,55%,100%{transform:rotate(0deg)}10%{transform:rotate(14deg)}20%{transform:rotate(-8deg)}30%{transform:rotate(14deg)}40%{transform:rotate(-4deg)}50%{transform:rotate(0deg)}}
        /* Status LED pulse for trip status pills (BOARDING, DEPARTED, DELAYED) */
        @keyframes jga-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.45;transform:scale(0.85)}}
        /* Slide-down + fade-in for toast banners (e.g. session-expired) */
        @keyframes jga-toast-in{from{transform:translate(-50%,-20px);opacity:0}to{transform:translate(-50%,0);opacity:1}}
        /* Constellation reveal — stars settle in with a slight overshoot, lines draw via stroke-dashoffset */
        @keyframes jga-star-in { from { opacity: 0; transform: scale(0.3); } to { opacity: 1; transform: scale(1); } }
        @keyframes jga-line-draw { 0% { opacity: 0; } 25% { opacity: 0.65; } 100% { opacity: 0.65; stroke-dashoffset: 0; } }
        @keyframes jga-bg-star { from { opacity: 0; } to { opacity: 1; } }
        @keyframes jga-chart-text-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes jga-reveal-overlay { 0% { opacity: 0; } 15% { opacity: 1; } 75% { opacity: 1; } 100% { opacity: 0; } }
        /* Top-nav Trips icon: gentle "carried" bob — 1px vertical drift, ~3.4s cycle.
           Imperceptible at rest, conveys "this is your stuff in motion." */
        @keyframes jga-trips-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-1px)}}
        .jga-trips-icon{animation:jga-trips-bob 3.4s ease-in-out infinite;transform-origin:center;transform-box:fill-box}
        /* iOS Safari auto-zoom prevention on focused inputs. iOS zooms in
           when a focused input has font-size < 16px and never zooms back
           out, leaving the user stuck on a magnified partial page. Forcing
           the floor on mobile viewports prevents this without altering the
           desktop typography. !important needed to override inline styles
           on inputs/textareas scattered across the app. */
        @media (max-width: 768px) {
          input, textarea, select {
            font-size: 16px !important;
          }
        }
        /* Top-nav Boards icon: the two pinned cards stir slightly. Alternating
           rotation creates the feeling of cards softly hanging on a corkboard.
           Phase offsets prevent the two from moving in lockstep. */
        @keyframes jga-board-card-1{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(-7.5deg)}}
        @keyframes jga-board-card-2{0%,100%{transform:rotate(5deg)}50%{transform:rotate(6.5deg)}}
        .jga-board-card-1{animation:jga-board-card-1 5.2s ease-in-out infinite;transform-origin:9.5px 9.7px;transform-box:fill-box}
        .jga-board-card-2{animation:jga-board-card-2 5.2s ease-in-out 1.3s infinite;transform-origin:15px 14px;transform-box:fill-box}
        @media (prefers-reduced-motion: reduce) {
          .jga-trips-icon, .jga-board-card-1, .jga-board-card-2 { animation: none !important; }
        }
        /* ── Date picker — gold-themed icon across all date inputs ── */
        input[type="date"] { color-scheme: dark; }
        input[type="date"]::-webkit-calendar-picker-indicator {
          /* Tints the default calendar icon to gold (#4A8CC4) */
          filter: invert(75%) sepia(25%) saturate(580%) hue-rotate(2deg) brightness(95%) contrast(88%);
          cursor: pointer;
          opacity: 0.85;
          transition: opacity 0.15s, transform 0.15s;
        }
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
          transform: scale(1.1);
        }
        /* Firefox calendar icon — softer styling fallback */
        input[type="date"]::-moz-calendar-picker-indicator {
          filter: invert(75%) sepia(25%) saturate(580%) hue-rotate(2deg);
          cursor: pointer;
        }
        * { transition: background-color 0.4s, border-color 0.4s, color 0.4s; }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
        /* ── ADA: Focus rings ── */
        *:focus-visible {
          outline: 2.5px solid #4A8CC4 !important;
          outline-offset: 3px !important;
          border-radius: 4px;
        }
        button:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible {
          outline: 2.5px solid #4A8CC4 !important;
          outline-offset: 3px !important;
        }
        /* ── ADA: Minimum touch targets ── */
        button { min-height: 44px; min-width: 44px; }
        /* ── ADA: Improve muted text contrast ── */
        /* textDim #556 → #8a9ab0, textMuted #7a8a9a → #9aaabb */
        body {
          background: ${bg};
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        ${!darkMode ? `
          /* ── Teal-forward fresh light mode ── */
          body {
            font-family: 'Inter', sans-serif;
            background-image: none;
          }
          h1, h2, h3 {
            font-family: 'Inter', sans-serif !important;
          }
          input, textarea, select {
            color: #1B3A5C !important;
            background: #FBFAF7 !important;
            border: 1px solid rgba(27,58,92,0.25) !important;
            border-radius: 10px;
            font-family: 'Inter', sans-serif;
          }
          input::placeholder, textarea::placeholder { color: #94A3B8 !important; }
          button { font-family: 'Inter', sans-serif !important; }
        ` : `
          input, textarea, select {
            color: #1B3A5C !important;
            background: rgba(27,58,92,0.060) !important;
            border: 1px solid rgba(27,58,92,0.120) !important;
            border-radius: 10px;
          }
          input::placeholder, textarea::placeholder { color: #445 !important; }
        `}
      `}</style>

      {showRegister && <RegisterModal initialEmail={prefillRegisterEmail} initialBookingCode={prefillBookingCode} onClose={() => { setShowRegister(false); setPrefillBookingCode(""); }} onSuccess={handleRegisterSuccess} onOpenLogin={(email) => { setLoginInitialEmail(email || ""); setShowRegister(false); setShowLogin(true); }} />}
      {/* Mode Comparison modal — first-time experience for users who haven't
          yet tried Surprise Me or Take Me Anywhere. Helps them understand the
          difference between the two main discovery modes. After they've used
          either, the identity CTA on the dashboard jumps straight to Surprise
          Me without showing this popup. */}
      {showModeComparison && (
        <div role="dialog" aria-modal="true" aria-label="Choose how to start"
          onClick={() => setShowModeComparison(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
            background: "rgba(0,0,0,0.78)",
            WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
            animation: "fadeIn 0.2s ease",
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              background: darkMode ? "#1B3A5C" : "#FAFAF5",
              border: `1px solid rgba(27,58,92,0.30)`,
              borderRadius: 16,
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
            }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "22px 24px 16px", borderBottom: `1px solid rgba(27,58,92,0.18)` }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#1B3A5C", fontWeight: 700, marginBottom: 6 }}>HOW DO YOU WANT TO START?</div>
                <div style={{ fontSize: 14, color: textMuted, fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: 1.5 }}>
                  Two ways to discover your next trip. Pick the one that fits your mood today — you can always switch.
                </div>
              </div>
              <button onClick={() => setShowModeComparison(false)} aria-label="Close"
                style={{ width: 32, height: 32, borderRadius: 16, border: `1px solid rgba(27,58,92,0.30)`, background: "transparent", color: textPrimary, cursor: "pointer", fontFamily: "inherit", fontSize: 16, fontWeight: 700, lineHeight: 1, flexShrink: 0, marginLeft: 14 }}>×</button>
            </div>

            {/* Side-by-side cards */}
            <div style={{ padding: "20px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {/* Surprise Me card */}
              <div style={{ padding: "20px 18px", borderRadius: 14, border: `1px solid rgba(27,58,92,0.25)`, background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(255,255,255,0.6)", display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontWeight: 800, color: "#1B3A5C", marginBottom: 4 }}>Surprise Me</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: textPrimary, fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: 1.2 }}>Trust us with one trip</div>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    "Answer your vibe, budget and dates",
                    "We pick one destination that matches",
                    "You book, then we reveal",
                    "Best for: \u201CJust send me anywhere\u201D",
                  ].map(item => (
                    <li key={item} style={{ display: "flex", gap: 8, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                      <span style={{ color: "#1B3A5C", fontWeight: 800, flexShrink: 0 }}>·</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "auto", paddingTop: 8 }}>
                  <button
                    onClick={() => { reset(); setStep(0); setMode("blind"); setShowModeComparison(false); }}
                    style={{
                      width: "100%", padding: "11px 18px", borderRadius: 10,
                      border: "none", cursor: "pointer", fontFamily: "inherit",
                      background: "linear-gradient(135deg, #1B3A5C, #1B3A5Ccc)",
                      color: "#FAFAF5", fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                      boxShadow: "0 2px 12px rgba(27,58,92,0.40)",
                      whiteSpace: "pre-line", lineHeight: 1.3,
                      minHeight: 60,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                    {"Surprise\nMe →"}
                  </button>
                </div>
              </div>

              {/* Take Me Anywhere card */}
              <div style={{ padding: "20px 18px", borderRadius: 14, border: `1px solid rgba(74,140,196,0.45)`, background: darkMode ? "rgba(74,140,196,0.08)" : "rgba(255,255,255,0.6)", display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontWeight: 800, color: goldText, marginBottom: 4 }}>Take Me Anywhere</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: textPrimary, fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: 1.2 }}>Browse what fits you</div>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    "Set your region, dates, vibe, and must-haves",
                    "We show every destination that matches",
                    "Compare and pick what speaks to you",
                    "Best for: \u201CShow me what\u2019s possible\u201D",
                  ].map(item => (
                    <li key={item} style={{ display: "flex", gap: 8, fontSize: 13, color: textMuted, lineHeight: 1.5 }}>
                      <span style={{ color: goldText, fontWeight: 800, flexShrink: 0 }}>·</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "auto", paddingTop: 8 }}>
                  <button
                    onClick={() => { reset(); setStep(0); setMode("explore"); setShowModeComparison(false); }}
                    style={{
                      width: "100%", padding: "11px 18px", borderRadius: 10,
                      border: "none", cursor: "pointer", fontFamily: "inherit",
                      background: `linear-gradient(135deg, ${gold}, ${gold}cc)`,
                      color: "#FAFAF5", fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                      boxShadow: `0 2px 12px ${gold}66`,
                      lineHeight: 1.3,
                      minHeight: 60,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                    Take Me Anywhere →
                  </button>
                </div>
              </div>
            </div>

            {/* Footer hint */}
            <div style={{ padding: "12px 24px 22px", fontSize: 11.5, color: textDim, fontStyle: "italic", textAlign: "center", lineHeight: 1.5 }}>
              No wrong answer — pick whichever fits your mood. You can switch flows anytime.
            </div>
          </div>
        </div>
      )}
      {/* Worth Knowing modal — opened via nav link. Same 5 items as the
          anonymous-homepage Worth Knowing section (intentional content
          duplication for now; can extract to a constant when backend RSS
          aggregator lands per deferred-ideas doc). */}
      {showWorthKnowing && (
        <div role="dialog" aria-modal="true" aria-label="Travel updates"
          onClick={() => setShowWorthKnowing(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
            background: "rgba(0,0,0,0.75)",
            WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)",
            animation: "fadeIn 0.2s ease",
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 600, maxHeight: "85vh", overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              background: darkMode ? "#1B3A5C" : "#FAFAF5",
              border: `1px solid ${darkMode ? "rgba(27,58,92,0.30)" : "rgba(27,58,92,0.20)"}`,
              borderRadius: 16,
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(27,58,92,0.14)"}` }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, letterSpacing: 3, color: "#1B3A5C", fontWeight: 700, marginBottom: 4 }}>WORTH KNOWING</div>
                <div style={{ fontSize: 13, color: textMuted, fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif" }}>Travel updates & alerts</div>
              </div>
              <button onClick={() => setShowWorthKnowing(false)} aria-label="Close"
                style={{ width: 32, height: 32, borderRadius: 16, border: `1px solid ${darkMode ? "rgba(27,58,92,0.30)" : "rgba(0,0,0,0.12)"}`, background: "transparent", color: textPrimary, cursor: "pointer", fontFamily: "inherit", fontSize: 16, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
            <div style={{ padding: "4px 24px 24px" }}>
              <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
                {[
                  { tag: "VISA",      title: "EU Entry/Exit System (EES) launches Nov 10, 2026 — biometric registration required at first crossing", source: "European Commission" },
                  { tag: "ADVISORY",  title: "Japan requires minimum 6 months passport validity at time of entry — check before booking", source: "U.S. State Dept" },
                  { tag: "LOGISTICS", title: "U.S. passport processing returns to 6–8 week standard timeline after summer backlog", source: "travel.state.gov" },
                  { tag: "VISA",      title: "UK eTA now required for U.S. travelers — £10, valid 2 years, apply before departure", source: "GOV.UK" },
                  { tag: "EVENTS",    title: "Iceland's Reykjanes Peninsula reopens to visitors — Blue Lagoon and Keflavík unaffected", source: "Icelandic Met Office" },
                ].map((item, i, arr) => (
                  <div key={item.title} style={{ display: "flex", gap: 14, padding: "14px 0", borderBottom: i < arr.length - 1 ? `0.5px solid ${darkMode ? "rgba(27,58,92,0.22)" : "rgba(27,58,92,0.18)"}` : "none" }}>
                    <div style={{ flex: "0 0 76px", fontSize: 10, letterSpacing: 2, color: "#1B3A5C", fontWeight: 700, paddingTop: 4 }}>{item.tag}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, lineHeight: 1.45, color: textPrimary, fontFamily: "'Source Serif 4', Georgia, serif", marginBottom: 4 }}>{item.title}</div>
                      <div style={{ fontSize: 11.5, color: textMuted, fontStyle: "italic" }}>{item.source}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `0.5px solid ${darkMode ? "rgba(27,58,92,0.22)" : "rgba(27,58,92,0.18)"}`, fontSize: 10.5, color: textDim, fontStyle: "italic", lineHeight: 1.5 }}>
                JGA aggregates from trusted public sources. Always verify with your embassy or carrier before travel.
              </div>
            </div>
          </div>
        </div>
      )}
      {showAlertModal && (
        <TripAlertModal
          dest={showAlertModal}
          existingAlert={tripAlerts[showAlertModal.name]}
          onClose={() => setShowAlertModal(null)}
          onSave={(alertData) => {
            setTripAlerts(prev => ({ ...prev, [showAlertModal.name]: alertData }));
            showToast(`Alert saved — we'll watch ${showAlertModal.name} for matches`, "info");
            setShowAlertModal(null);
          }}
          onDelete={() => {
            setTripAlerts(prev => {
              const next = { ...prev };
              delete next[showAlertModal.name];
              return next;
            });
            setShowAlertModal(null);
          }}
        />
      )}

      {/* ── Mood Quiz Modal ── */}
      {showMoodQuiz && (() => {
        const MOOD_QUESTIONS = [
          { id: "energy", q: "What's the mood?", options: [
            { label: "Energized", vibes: ["adventure","nightlife"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2 C 8 8, 6 12, 6 15 C 6 18.5 8.7 21 12 21 C 15.3 21 18 18.5 18 15 C 18 12 16 8 12 2 Z" stroke="#ff8a4a" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(255,138,74,0.12)"/>
                <path d="M12 9 C 10 12, 9 13.5, 9 15.5 C 9 17 10.3 18 12 18 C 13.7 18 15 17 15 15.5 C 15 13.5 14 12 12 9 Z" fill="#ff8a4a" opacity="0.55"/>
              </svg>
            )},
            { label: "Restless", vibes: ["adventure","nightlife"], icon: (
              // Three jittery zigzag lines — pent-up energy / can't sit still
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 4 L 8 8 L 6 12 L 8 16 L 6 20" stroke="#c14d4d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 4 L 14 8 L 12 12 L 14 16 L 12 20" stroke="#c14d4d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 4 L 16 8 L 18 12 L 16 16 L 18 20" stroke="#c14d4d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )},
            { label: "Calm", vibes: ["wellness","nature"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3 C 14 7, 18 9, 18 14 C 18 18 15.5 21 12 21 C 8.5 21 6 18 6 14 C 6 9 10 7 12 3 Z" stroke="#6dbd6d" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(109,189,109,0.1)"/>
                <path d="M12 8 L12 18" stroke="#6dbd6d" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
              </svg>
            )},
            { label: "Burned out", vibes: ["wellness","nature","beach"], icon: (
              // Low battery — universally understood "depleted" symbol
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="9" width="16" height="8" rx="1.5" stroke="#b08868" strokeWidth="1.5" fill="rgba(176,136,104,0.08)"/>
                <rect x="19.5" y="11" width="2" height="4" rx="0.5" fill="#b08868"/>
                <rect x="5" y="11" width="3" height="4" fill="#b08868" opacity="0.7"/>
              </svg>
            )},
            { label: "Romantic", vibes: ["romance","food"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 21 C 4 14, 4 7, 8 5 C 10 4, 11.5 5, 12 7 C 12.5 5, 14 4, 16 5 C 20 7, 20 14, 12 21 Z" stroke="#ff6b8a" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(255,107,138,0.12)"/>
              </svg>
            )},
            { label: "Curious", vibes: ["culture","art"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="#4A8CC4" strokeWidth="1.6" fill="rgba(74,140,196,0.1)"/>
                <path d="M9 9 C 9 7 10.5 6 12 6 C 13.5 6 15 7 15 9 C 15 11 12 11 12 14" stroke="#4A8CC4" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                <circle cx="12" cy="17.5" r="0.9" fill="#4A8CC4"/>
              </svg>
            )},
          ]},
          { id: "scene", q: "Where do you want to be right now?", options: [
            { label: "Near water", vibes: ["beach"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M2 9 Q 5 6, 8 9 T 14 9 T 22 9" stroke="#1B3A5C" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                <path d="M2 14 Q 5 11, 8 14 T 14 14 T 22 14" stroke="#1B3A5C" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                <path d="M2 19 Q 5 16, 8 19 T 14 19 T 22 19" stroke="#1B3A5C" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
              </svg>
            )},
            { label: "In the mountains", vibes: ["nature","adventure"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M2 20 L 8 9 L 12 14 L 16 6 L 22 20 Z" stroke="#a8916a" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(168,145,106,0.15)"/>
                <path d="M14.5 9 L 17.5 9" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
              </svg>
            )},
            { label: "In a city", vibes: ["culture","nightlife"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="10" width="5" height="11" stroke="#4A8CC4" strokeWidth="1.5" fill="rgba(74,140,196,0.12)"/>
                <rect x="9.5" y="5" width="5" height="16" stroke="#4A8CC4" strokeWidth="1.5" fill="rgba(74,140,196,0.18)"/>
                <rect x="16" y="13" width="5" height="8" stroke="#4A8CC4" strokeWidth="1.5" fill="rgba(74,140,196,0.12)"/>
                <line x1="11" y1="9" x2="13" y2="9" stroke="#4A8CC4" strokeWidth="1" opacity="0.6"/>
                <line x1="11" y1="13" x2="13" y2="13" stroke="#4A8CC4" strokeWidth="1" opacity="0.6"/>
              </svg>
            )},
            { label: "Somewhere quiet", vibes: ["nature","wellness"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="18" cy="7" r="3" fill="rgba(255,200,100,0.5)" stroke="#4A8CC4" strokeWidth="1.5"/>
                <path d="M2 18 L 22 18" stroke="#a8916a" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M2 18 Q 7 14, 12 17 T 22 18" stroke="#a8916a" strokeWidth="1.4" fill="rgba(168,145,106,0.18)" strokeLinejoin="round"/>
              </svg>
            )},
            { label: "Somewhere warm", vibes: ["beach","luxury"], icon: (
              // Sun with rays — warmth / sunshine
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="4.5" stroke="#ff8a4a" strokeWidth="1.6" fill="rgba(255,138,74,0.18)"/>
                <path d="M12 2 L12 4.5 M12 19.5 L12 22 M2 12 L4.5 12 M19.5 12 L22 12 M5 5 L6.8 6.8 M17.2 17.2 L19 19 M19 5 L17.2 6.8 M6.8 17.2 L5 19" stroke="#ff8a4a" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            )},
            { label: "Somewhere far & new", vibes: ["adventure","culture"], icon: (
              // Compass — wanderlust / exploration
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="#4A8CC4" strokeWidth="1.6" fill="rgba(74,140,196,0.1)"/>
                <path d="M12 12 L 15.5 8.5 L 13 13 L 9 16 Z" fill="#4A8CC4" opacity="0.8"/>
                <circle cx="12" cy="12" r="1" fill="#4A8CC4"/>
              </svg>
            )},
          ]},
          { id: "vibe", q: "What sounds best?", options: [
            { label: "Crowds & energy", vibes: ["nightlife"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="2" fill="#ff8a4a"/>
                <path d="M12 4 L 12 7 M12 17 L 12 20 M4 12 L 7 12 M17 12 L 20 12" stroke="#ff8a4a" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M6.3 6.3 L 8.5 8.5 M15.5 15.5 L 17.7 17.7 M6.3 17.7 L 8.5 15.5 M15.5 8.5 L 17.7 6.3" stroke="#ff8a4a" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
              </svg>
            )},
            { label: "Slow meals", vibes: ["food","romance"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 3 L 9 8 C 9 9.5, 10 11, 12 11 C 14 11, 15 9.5, 15 8 L 15 3" stroke="#ff6b8a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M12 11 L 12 21" stroke="#ff6b8a" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M8 21 L 16 21" stroke="#ff6b8a" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            )},
            { label: "Going off-grid", vibes: ["adventure","nature"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M8 6 L 8 4 C 8 3 9 2 10 2 L 14 2 C 15 2 16 3 16 4 L 16 6" stroke="#6dbd6d" strokeWidth="1.5" strokeLinecap="round"/>
                <rect x="5" y="6" width="14" height="15" rx="2" stroke="#6dbd6d" strokeWidth="1.6" fill="rgba(109,189,109,0.1)"/>
                <line x1="9" y1="11" x2="15" y2="11" stroke="#6dbd6d" strokeWidth="1.4" strokeLinecap="round"/>
                <line x1="9" y1="14" x2="15" y2="14" stroke="#6dbd6d" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
              </svg>
            )},
            { label: "Pure relaxation", vibes: ["wellness","luxury"], icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 4 C 10 8, 8 10, 8 13 C 8 15 9.8 16.5 12 16.5 C 14.2 16.5 16 15 16 13 C 16 10, 14 8, 12 4 Z" stroke="#1B3A5C" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(27,58,92,0.15)"/>
                <path d="M5 20 Q 9 18, 12 20 T 19 20" stroke="#1B3A5C" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.7"/>
              </svg>
            )},
            { label: "Drinks till sunrise", vibes: ["nightlife","food"], icon: (
              // Cocktail glass with crescent moon — late-night escapism
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 4 L 15 4 L 12 11 C 12 13 11 13.5 10 13.5 C 9 13.5 8 13 8 11 L 5 4 Z" stroke="#722f37" strokeWidth="1.5" strokeLinejoin="round" fill="rgba(114,47,55,0.22)"/>
                <line x1="10" y1="13.5" x2="10" y2="20" stroke="#722f37" strokeWidth="1.5"/>
                <line x1="7" y1="20" x2="13" y2="20" stroke="#722f37" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M19 5 A 3 3 0 1 0 19 11 A 2.4 2.4 0 1 1 19 5 Z" fill="#722f37" opacity="0.7"/>
              </svg>
            )},
            { label: "Disappearing for a few days", vibes: ["nature","wellness"], icon: (
              // Fading footprints walking away — solo retreat / vanishing act
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <ellipse cx="6" cy="19" rx="2.2" ry="1.4" fill="#6a7a8a" opacity="1"/>
                <ellipse cx="10" cy="15" rx="2" ry="1.3" fill="#6a7a8a" opacity="0.7"/>
                <ellipse cx="14" cy="11" rx="2" ry="1.2" fill="#6a7a8a" opacity="0.45"/>
                <ellipse cx="18" cy="7" rx="1.8" ry="1" fill="#6a7a8a" opacity="0.25"/>
              </svg>
            )},
          ]},
        ];
        return (
          <MoodQuizModal
            questions={MOOD_QUESTIONS}
            onClose={() => setShowMoodQuiz(false)}
            onComplete={(answers) => {
              const allVibes = answers.flatMap(a => a.vibes);
              const vibeCounts = {};
              allVibes.forEach(v => { vibeCounts[v] = (vibeCounts[v] || 0) + 1; });
              const topVibes = Object.entries(vibeCounts).sort((a,b) => b[1]-a[1]).slice(0,3).map(([v]) => v);
              setUserMood({ vibes: topVibes, answeredAt: new Date().toISOString() });
              setMoodNudgeDismissedAt(null); // fresh mood = fresh nudge cycle
              setShowMoodQuiz(false);
              // After modal closes and dashboard re-renders with the new mood-matched
              // shortlist, scroll the Mood Match section into view so the user
              // actually sees the payoff. Without this scroll, the result appears
              // somewhere on the page but the user is still scrolled to wherever
              // they tapped the quiz CTA — feels like "nothing happened."
              setTimeout(() => {
                const el = document.getElementById("jga-mood-match");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 60);
            }}
          />
        );
      })()}
      {/* Stat Breakdown Modal — shows when user clicks Trips/Countries/States/Days */}
      {/* Quick Book Modal — shown when user taps a dashboard discovery card */}
      {quickBookDest && (
        <QuickBookModal
          dest={quickBookDest.dest}
          accentColor={quickBookDest.accent}
          labelTag={quickBookDest.label}
          defaultDateRange={
            quickBookDest.tripInfo && (quickBookDest.tripInfo.departDate || quickBookDest.tripInfo.days)
              ? quickBookDest.tripInfo
              : (quickBookDest.defaultDateRange || null)
          }
          departureAirport={expDepartureAirport || blindDepartureAirport || user?.departureAirport || ""}
          flightPrice={flightPrices[quickBookDest.dest.name] || getBaselineFlightCost(quickBookDest.dest, expDepartureAirport || blindDepartureAirport || user?.departureAirport || null)}
          userBudget={resolveUserBudget().value}
          defaultTravelers={({ "solo": 1, "couple": 2, "friends": 3, "family": 4 })[travelCompanion] || 1}
          saveItinerary={saveItineraryFlow}
          autoPreview={!!quickBookDest.autoPreview}
          onClose={() => setQuickBookDest(null)}
          onPreview={async (d, dys) => {
            const parsedBudget = parseInt(String(dashboardBudget || user?.budgetPref || budget || "1500").replace(/\D/g, ""), 10) || 1500;
            const fallback = {
              days: Array.from({ length: Math.min(dys, 5) }, (_, i) => ({
                day: i + 1,
                title: ["Arrive & Explore", "Local Highlights", "Hidden Gems", "Culture & Food", "Farewell Day"][i] || `Day ${i + 1}`,
                morning: "Start with a local breakfast and explore the neighbourhood",
                afternoon: "Visit the main attractions and soak in the local culture",
                evening: "Dinner at a recommended local restaurant",
                tip: "Ask locals for their favourite hidden spots — they always know best",
              })),
              packingTip: `Pack layers and comfortable walking shoes — perfect for exploring ${d.name}.`,
            };
            try {
              const loc = d.state ? `${d.name}, ${d.state}, ${d.country}` : `${d.name}, ${d.country}`;
              const j = await callAI(`Create a brief ${dys}-day sample itinerary preview for ${loc}, budget around $${parsedBudget}. Keep it concise and high-level — this is a preview to entice booking. Return ONLY JSON:
{"days":[{"day":1,"title":"string","morning":"string","afternoon":"string","evening":"string","tip":"string"}],"packingTip":"string"}`);
              return j;
            } catch (e) {
              console.error("[JGA Preview] AI failed, using fallback:", e);
              return fallback;
            }
          }}
          onConfirm={({ departDate: dd, returnDate: rd, travelers: tv, days: dys, estTotal, userBudget: chosenBudget }) => {
            // ── Price consistency: BookingModal shows the trip's estimated
            // cost, NOT the user's max budget ceiling ──
            // Previously this fell back to chosenBudget (= user's $4K cap)
            // when computing the BookingModal's total, causing a visible
            // price jump from QBM's estimate to the user's full budget +
            // fees on the next screen. Prefer estTotal so the price the
            // user already saw in QBM stays consistent through to booking.
            const finalAmount = estTotal
              || chosenBudget
              || parseInt(String(dashboardBudget || user?.budgetPref || "1500").replace(/[^0-9]/g,""), 10)
              || 1500;
            setSelected(quickBookDest.dest);
            setDepartDate(dd);
            setReturnDate(rd);
            setDays(dys);
            setBudget(String(finalAmount));
            setQuickBookDest(null);
            setShowBooking(true);
          }}
        />
      )}

      {/* ── Over-budget interstitial ── 
          When user taps an Explore card that costs more than their current
          budget, instead of dropping them into TMA where the filter would say
          '0 matches' (the jarring 'Iceland was right there, now it's gone'
          moment), surface a clear choice. */}
      {exploreOverBudget && (() => {
        const { dest, neededBudget, currentBudget, days: cardDays } = exploreOverBudget;
        const overBy = neededBudget - currentBudget;
        return (
          <div onClick={() => setExploreOverBudget(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(15,9,8,0.55)",
              zIndex: 9990, display: "flex", alignItems: "center", justifyContent: "center", padding: 18,
            }}>
            <div onClick={e => e.stopPropagation()}
              style={{
                background: darkMode ? "#1a1010" : "#FAFAF5",
                border: `1px solid ${darkMode ? "rgba(27,58,92,0.30)" : "rgba(27,58,92,0.22)"}`,
                borderRadius: 18, padding: "24px 22px 20px", maxWidth: 420, width: "100%",
                boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              }}>
              <div style={{ fontSize: 10, letterSpacing: 2.5, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 800, marginBottom: 8 }}>Budget check</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: textPrimary, marginBottom: 10, lineHeight: 1.2 }}>
                {dest.name} needs about ${neededBudget.toLocaleString()} to do honestly.
              </div>
              <div style={{ fontSize: 13, color: textMuted, marginBottom: 18, lineHeight: 1.4 }}>
                You're set at ${currentBudget.toLocaleString()} — that's ${overBy.toLocaleString()} short for {cardDays} days (flight + hotel + on-ground, solo).
                Three options:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={() => {
                  // Bump budget to the needed amount + a small cushion, set dashboard,
                  // then route to TMA pre-filled FOR THIS DESTINATION (not filtered
                  // by region/country/state). Previously this set expRegion/expSub/
                  // expCountry/expState which then ran a TMA search — but if the
                  // catalog had only one destination matching those filters at the
                  // new budget tier (e.g. Lombok being the only Indonesia card),
                  // the TMA search yielded 0 matches even though the user just
                  // tapped this exact card. Now we set prefilledDest so the
                  // existing prefilledDest useEffect routes directly to step=2
                  // with this destination selected and its itinerary generating.
                  // User lands on Lombok's itinerary at the new budget. Same
                  // pattern as the blog-pick deep-link.
                  // ORDER MATTERS: reset() internally calls setBudget("2000") to
                  // clear session state. If reset() runs AFTER setBudget(newBudget),
                  // the reset's "2000" wins (last-write-wins on batched state) and
                  // the user sees "$2,000" displayed despite agreeing to the bump.
                  // Call reset FIRST, then write the bumped values on top.
                  const newBudget = Math.ceil(neededBudget / 100) * 100;
                  setExploreOverBudget(null);
                  setSearchQuery("");
                  setBottomTab(null);
                  reset();
                  // Now write the bumped values — these are the last writes so they win
                  setDashboardBudget(String(newBudget));
                  setBudget(String(newBudget));
                  try { localStorage.setItem("jga-dashboard-budget", String(newBudget)); } catch {}
                  setDays(cardDays);
                  setPrefilledDest(dest);
                  setMode("explore");
                }}
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 12,
                    border: "none", background: teal, color: "#fff",
                    fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                    textAlign: "left",
                  }}>
                  Bump budget to ${(Math.ceil(neededBudget / 100) * 100).toLocaleString()}
                </button>
                {(() => {
                  // Mirror the card stepper's logic — short-haul (≤4h) allows
                  // 2-day trips; longer requires 3-day floor.
                  const flightHrs = estimateFlightDurationHours(dest, expDepartureAirport || blindDepartureAirport || user?.departureAirport || null);
                  const destMinDays = (flightHrs !== null && flightHrs <= 4) ? 2 : 3;
                  return cardDays > destMinDays ? (
                    <button onClick={() => {
                      // Shorten to the destination's minimum trip length
                      setExplorePerCardDays(prev => ({ ...prev, [dest.name]: destMinDays }));
                      setExploreOverBudget(null);
                    }}
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        border: `1px solid ${darkMode ? "rgba(27,58,92,0.30)" : "rgba(27,58,92,0.22)"}`,
                        background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.05)",
                        color: textPrimary, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                        textAlign: "left",
                      }}>
                      Try a shorter trip ({destMinDays} days)
                    </button>
                  ) : null;
                })()}
                <button onClick={() => setExploreOverBudget(null)}
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 12,
                    border: "none", background: "transparent",
                    color: textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    textAlign: "left",
                  }}>
                  Pick a different destination
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showStatBreakdown && (() => {
        // Resilient status filtering: trips without status are treated as completed (legacy data)
        const isCompletedStatus = (s) => !s || s === "completed" || s === "past";
        const isOngoingStatus = (s) => s === "current" || s === "ongoing";
        const isUpcomingStatus = (s) => s === "upcoming";
        const completed = pastTrips.filter(t => isCompletedStatus(t.status));
        const ongoing = pastTrips.filter(t => isOngoingStatus(t.status));
        const upcoming = pastTrips.filter(t => isUpcomingStatus(t.status));
        const allTrips = [...upcoming, ...ongoing, ...completed];
        // Group helpers
        const byCountry = {};
        completed.forEach(t => {
          const dest = DESTINATIONS.find(d => d.name === t.dest);
          const country = dest?.country || "Unknown";
          if (!byCountry[country]) byCountry[country] = { trips: [], emoji: dest?.emoji || "🌍", days: 0 };
          byCountry[country].trips.push(t);
          byCountry[country].days += (t.days || 0);
        });
        // Cities = unique destinations. Better metric than states (which stayed
        // empty for most international travel). Groups trips by destination name
        // so users can see "Paris × 2" if they've been twice.
        const byCity = {};
        completed.forEach(t => {
          const dest = DESTINATIONS.find(d => d.name === t.dest);
          const cityKey = t.dest || "Unknown";
          if (!byCity[cityKey]) byCity[cityKey] = { city: cityKey, country: dest?.country || "", emoji: dest?.emoji || t.emoji || "🌍", trips: [] };
          byCity[cityKey].trips.push(t);
        });
        // Days fallback chain: prefer t.days, else compute from dateFrom/dateTo,
        // else journal meta dates. Same chain as dashboard stat for consistency.
        const computeTripDays = (t) => {
          if (t.days) return t.days;
          const datesFromTrip = (t.dateFrom && t.dateTo) ? [t.dateFrom, t.dateTo] : null;
          const meta = journalMeta[t.code];
          const datesFromMeta = (meta?.dateFrom && meta?.dateTo) ? [meta.dateFrom, meta.dateTo] : null;
          const dates = datesFromTrip || datesFromMeta;
          if (!dates) return 0;
          const ms = new Date(dates[1]) - new Date(dates[0]);
          if (!isFinite(ms) || ms < 0) return 0;
          return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
        };
        const totalDays = completed.reduce((s, t) => s + computeTripDays(t), 0);
        const config = {
          trips: { color: teal, title: "Your Trips", subtitle: `${allTrips.length} total · ${completed.length} completed · ${upcoming.length} upcoming` },
          countries: { color: goldText, title: "Countries Visited", subtitle: `${Object.keys(byCountry).length} ${Object.keys(byCountry).length === 1 ? "country" : "countries"} explored` },
          cities: { color: "#4A8CC4", title: "Cities Visited", subtitle: `${Object.keys(byCity).length} ${Object.keys(byCity).length === 1 ? "city" : "cities"} explored` },
          days: { color: coral, title: "Days Traveled", subtitle: `${totalDays} days across ${completed.length} trips` },
        }[showStatBreakdown];
        return (
          <div onClick={() => setShowStatBreakdown(null)}
            style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#fff", borderRadius: 16, padding: 22, width: "100%", maxWidth: 480, maxHeight: "82vh", overflowY: "auto", border: `1.5px solid ${config.color}40`, boxShadow: `0 20px 60px ${config.color}20` }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: config.color, textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Breakdown</div>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: config.color, lineHeight: 1.1 }}>{config.title}</h2>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>{config.subtitle}</div>
                </div>
                <button onClick={() => setShowStatBreakdown(null)} aria-label="Close"
                  style={{ background: "rgba(27,58,92,0.072)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, color: textMuted, fontSize: 18, padding: "4px 11px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit", lineHeight: 1, flexShrink: 0 }}>✕</button>
              </div>

              {/* Body */}
              {showStatBreakdown === "trips" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {allTrips.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "30px 0", color: textMuted, fontSize: 13, fontStyle: "italic" }}>No trips yet — book your first to start filling this in.</div>
                  ) : allTrips.map((t, i) => {
                    const dest = DESTINATIONS.find(d => d.name === t.dest);
                    const isOngoing = isOngoingStatus(t.status);
                    const isUpcoming = isUpcomingStatus(t.status);
                    const statusLabel = isOngoing ? "On trip now" : isUpcoming ? "Upcoming" : "Completed";
                    const statusColor = isOngoing ? "#6dbd6d" : isUpcoming ? teal : gold;
                    return (
                      <div key={i} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}`, background: darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)", display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 22 }}>{dest?.emoji || t.emoji || "🌍"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>{t.dest}</div>
                          <div style={{ fontSize: 11, color: textDim }}>{dest?.country}{dest?.state ? ` · ${dest.state}` : ""} · {t.days || "?"} days</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, padding: "3px 8px", borderRadius: 12, background: `${statusColor}20`, border: `1px solid ${statusColor}40`, whiteSpace: "nowrap", flexShrink: 0 }}>{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {showStatBreakdown === "countries" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {Object.keys(byCountry).length === 0 ? (
                    <div style={{ textAlign: "center", padding: "30px 0", color: textMuted, fontSize: 13, fontStyle: "italic" }}>Complete a trip to start collecting countries.</div>
                  ) : Object.entries(byCountry).sort((a,b) => b[1].trips.length - a[1].trips.length).map(([country, data]) => (
                    <div key={country} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${gold}25`, background: `${gold}08`, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{data.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>{country}</div>
                        <div style={{ fontSize: 11, color: textDim }}>{data.trips.length} {data.trips.length === 1 ? "trip" : "trips"} · {data.days} days</div>
                      </div>
                      <span style={{ fontSize: 10, color: textDim }}>{data.trips.map(t => t.dest).join(", ")}</span>
                    </div>
                  ))}
                </div>
              )}

              {showStatBreakdown === "cities" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {Object.keys(byCity).length === 0 ? (
                    <div style={{ textAlign: "center", padding: "30px 0", color: textMuted, fontSize: 13, fontStyle: "italic" }}>Cities will appear here once you've completed trips.</div>
                  ) : Object.values(byCity).map(c => (
                    <div key={c.city} style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(74,140,196,0.25)", background: "rgba(74,140,196,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{c.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>{c.city}{c.trips.length > 1 ? ` · ×${c.trips.length}` : ""}</div>
                        {c.country && (
                          <div style={{ fontSize: 11, color: textDim }}>{c.country}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showStatBreakdown === "days" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {completed.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "30px 0", color: textMuted, fontSize: 13, fontStyle: "italic" }}>No completed trips yet — days will tally up as you travel.</div>
                  ) : (
                    <>
                      <div style={{ padding: "14px 16px", borderRadius: 12, background: `${coral}10`, border: `1px solid ${coral}30`, textAlign: "center" }}>
                        <div style={{ fontSize: 32, fontWeight: 900, color: coral, lineHeight: 1 }}>{totalDays}</div>
                        <div style={{ fontSize: 11, color: textDim, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>Total days traveled</div>
                        <div style={{ fontSize: 11, color: textMuted, marginTop: 6 }}>Avg {completed.length > 0 ? Math.round(totalDays / completed.length) : 0} days per trip</div>
                      </div>
                      {completed.sort((a,b) => computeTripDays(b) - computeTripDays(a)).map((t, i) => {
                        const dest = DESTINATIONS.find(d => d.name === t.dest);
                        const tripDays = computeTripDays(t);
                        return (
                          <div key={i} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}`, display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 20 }}>{dest?.emoji || t.emoji || "🌍"}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>{t.dest}</div>
                              <div style={{ fontSize: 11, color: textDim }}>{dest?.country}</div>
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 900, color: coral }}>{tripDays}<span style={{ fontSize: 10, color: textDim, marginLeft: 3, fontWeight: 600 }}>days</span></div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {showLogin && <LoginModal
        initialEmail={loginInitialEmail}
        initialBookingCode={loginInitialBookingCode}
        onClose={() => { setShowLogin(false); setLoginInitialEmail(""); setLoginInitialBookingCode(""); }}
        onSuccess={handleLoginSuccess}
        onOpenRegister={(email, code) => {
          // "Sign up instead" fallback for users without accounts. Close
          // Login, open Register with email + code pre-filled. The code
          // is also kept in localStorage so handleRegisterSuccess can route
          // to Trips tab after successful registration.
          if (email) setPrefillRegisterEmail(email);
          if (code) setPrefillBookingCode(code);
          setShowLogin(false);
          setShowRegister(true);
        }}
      />}
      {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} onBackToLogin={() => setShowLogin(true)} />}
      {resetPasswordToken && <ResetPasswordModal token={resetPasswordToken} onClose={() => setResetPasswordToken(null)} onSuccess={(userData) => { handleLoginSuccess(userData); setResetPasswordToken(null); }} />}

      {/* ── Minor Block Modal — hard gate for under-18 users at booking step ── */}
      {showMinorBlock && (
        <div role="dialog" aria-modal="true" aria-labelledby="minor-block-title" style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", animation: "fadeIn 0.25s ease" }}>
          <div style={{ width: "100%", maxWidth: 440, padding: "28px 24px", borderRadius: 18, background: darkMode ? "#0d1521" : "#fff", border: "1px solid rgba(74,140,196,0.4)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", animation: "fadeIn 0.3s ease 0.05s both" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
              <div style={{ width: 64, height: 64, borderRadius: 32, background: "rgba(74,140,196,0.15)", border: "2px solid rgba(74,140,196,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="9" r="3.5" stroke="#4A8CC4" strokeWidth="1.7" fill="rgba(74,140,196,0.15)"/>
                  <path d="M5 20 C 5 16, 8 14, 12 14 C 16 14, 19 16, 19 20" stroke="#4A8CC4" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                  <circle cx="18" cy="6" r="3.5" fill="#4A8CC4" stroke={darkMode ? "#0d1521" : "#fff"} strokeWidth="1.5"/>
                  <path d="M16.5 6 L 17.5 7 L 19.5 5" stroke={darkMode ? "#0d1521" : "#fff"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
            </div>
            <h2 id="minor-block-title" style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 900, color: textPrimary, textAlign: "center" }}>You'll be able to book when you turn 18</h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: textMuted, textAlign: "center", lineHeight: 1.55 }}>
              For now, you can keep exploring — take the identity quiz, save destinations to your wishlist, plan trips, and search itineraries. Booking will be unlocked the day you turn 18.
            </p>
            <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", marginBottom: 16, fontSize: 11, color: textMuted, lineHeight: 1.5 }}>
              <strong style={{ color: "#4A8CC4" }}>Want to travel sooner?</strong> Have a parent or guardian register their own account — they can book for the family.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => setShowMinorBlock(false)}
                style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #4A8CC4, #1B3A5C)", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Keep exploring
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Verify Email Block Modal — hard gate before booking ── */}
      {showVerifyBlock && (
        <div role="dialog" aria-modal="true" aria-labelledby="verify-block-title" style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", animation: "fadeIn 0.25s ease" }}>
          <div style={{ width: "100%", maxWidth: 440, padding: "28px 24px", borderRadius: 18, background: darkMode ? "#0d1521" : "#fff", border: "1px solid rgba(245,180,80,0.4)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", animation: "fadeIn 0.3s ease 0.05s both" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
              <div style={{ width: 64, height: 64, borderRadius: 32, background: "rgba(245,180,80,0.15)", border: "2px solid rgba(245,180,80,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="6" width="18" height="13" rx="2" stroke="#f5b450" strokeWidth="1.7" fill="rgba(245,180,80,0.2)"/>
                  <path d="M3 7 L 12 13 L 21 7" stroke="#f5b450" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <circle cx="19" cy="6" r="3.5" fill="#f5b450" stroke={darkMode ? "#0d1521" : "#fff"} strokeWidth="1.5"/>
                  <text x="19" y="9" textAnchor="middle" fontSize="6" fontWeight="900" fill={darkMode ? "#0d1521" : "#fff"}>!</text>
                </svg>
              </div>
            </div>
            <h2 id="verify-block-title" style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 900, color: textPrimary, textAlign: "center" }}>Verify your email first</h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: textMuted, textAlign: "center", lineHeight: 1.55 }}>
              We need to confirm your email before you can book a trip. We sent a link to <strong style={{ color: textPrimary, wordBreak: "break-all" }}>{user?.email}</strong> — click it to continue.
            </p>
            <div style={{ padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", marginBottom: 16, fontSize: 11, color: textMuted, lineHeight: 1.5 }}>
              <strong style={{ color: textPrimary }}>Don't see it?</strong> Check your spam folder, or hit Resend below. The link expires in 24 hours.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={resendVerificationEmail} disabled={resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited"}
                style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #f5b450, #e8a040)", color: "#1a0f00", fontSize: 14, fontWeight: 800, cursor: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited") ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited") ? 0.7 : 1 }}>
                {resendStatus === "sending" ? "Sending…" : resendStatus === "sent" ? "✓ Email sent — check your inbox" : resendStatus === "rate-limited" ? "Wait 30 seconds before trying again" : resendStatus === "error" ? "Couldn't send — try again" : "Resend verification email"}
              </button>
              <button onClick={() => setShowVerifyBlock(false)}
                style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                I'll verify later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Verify Email Result Modal — shows after URL token is processed ── */}
      {verifyStatus && (
        <div role="dialog" aria-modal="true" aria-labelledby="verify-status-title" style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", animation: "fadeIn 0.25s ease" }}>
          <div style={{ width: "100%", maxWidth: 420, padding: "28px 24px", borderRadius: 18, background: darkMode ? "#0d1521" : "#fff", border: `1px solid ${verifyStatus === "success" ? "rgba(109,189,109,0.4)" : "rgba(245,180,80,0.4)"}`, boxShadow: "0 20px 60px rgba(0,0,0,0.5)", animation: "fadeIn 0.3s ease 0.05s both", textAlign: "center" }}>
            {verifyStatus === "verifying" && (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 28, border: "3px solid rgba(27,58,92,0.3)", borderTopColor: teal, animation: "spin 1s linear infinite" }}/>
                </div>
                <h2 id="verify-status-title" style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 900, color: textPrimary }}>Verifying your email…</h2>
                <p style={{ margin: 0, fontSize: 12, color: textMuted }}>This will just take a second.</p>
              </>
            )}
            {verifyStatus === "success" && (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                  <div style={{ width: 64, height: 64, borderRadius: 32, background: "rgba(109,189,109,0.15)", border: "2px solid rgba(109,189,109,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.15)"/>
                      <path d="M8 12 L 11 15 L 16.5 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </div>
                </div>
                <h2 id="verify-status-title" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900, color: textPrimary }}>Email verified ✓</h2>
                <p style={{ margin: "0 0 20px", fontSize: 13, color: textMuted, lineHeight: 1.55 }}>You're all set. You can now book trips, reset your password, and access all features.</p>
                <button onClick={() => setVerifyStatus(null)}
                  style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #6dbd6d, #4a9d65)", color: "#0a1a0a", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                  Continue
                </button>
              </>
            )}
            {(verifyStatus === "expired" || verifyStatus === "invalid" || verifyStatus === "error") && (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 28, background: "rgba(245,180,80,0.15)", border: "2px solid rgba(245,180,80,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" stroke="#f5b450" strokeWidth="1.8" fill="rgba(245,180,80,0.15)"/>
                      <path d="M12 7 L 12 13" stroke="#f5b450" strokeWidth="2.2" strokeLinecap="round"/>
                      <circle cx="12" cy="16.5" r="1.2" fill="#f5b450"/>
                    </svg>
                  </div>
                </div>
                <h2 id="verify-status-title" style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 900, color: textPrimary }}>
                  {verifyStatus === "expired" ? "This link expired" : verifyStatus === "invalid" ? "Invalid link" : "Something went wrong"}
                </h2>
                <p style={{ margin: "0 0 18px", fontSize: 13, color: textMuted, lineHeight: 1.55 }}>
                  {verifyStatus === "expired" ? "Verification links expire after 24 hours. We can send you a fresh one." : verifyStatus === "invalid" ? "This link isn't valid. It may have been used already, or copied incorrectly." : "We couldn't verify your email right now. Try resending or contact support."}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {registered && (
                    <button onClick={() => { setVerifyStatus(null); resendVerificationEmail(); }}
                      style={{ width: "100%", padding: "11px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #f5b450, #e8a040)", color: "#1a0f00", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                      Send a new verification email
                    </button>
                  )}
                  <button onClick={() => setVerifyStatus(null)}
                    style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`, background: "transparent", color: textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showBooking && selected && (isMinor ? (() => { setShowBooking(false); setShowMinorBlock(true); return null; })() : isUnverified ? (() => { setShowBooking(false); setShowVerifyBlock(true); return null; })() : (() => { const fee = feeForDest(selected); return <BookingModal dest={selected} budget={budget} days={days} departDate={departDate} returnDate={returnDate} expIncludes={expIncludes} serviceFee={fee.serviceFee} feeLabel={fee.feeLabel} onClose={() => setShowBooking(false)} user={user} aiItinerary={aiItinerary} saveItinerary={saveItineraryFlow} onPostcardEarn={() => setPostcardsData(getPostcards())} onViewItineraries={() => { setAccountDefaultTab("itineraries"); setMode("trips"); }} />; })())}
      {affordableBookingDest && (isMinor ? (() => { setAffordableBookingDest(null); setShowMinorBlock(true); return null; })() : isUnverified ? (() => { setAffordableBookingDest(null); setShowVerifyBlock(true); return null; })() : (() => { const fee = feeForDest(affordableBookingDest); return <BookingModal dest={affordableBookingDest} budget={affordableBookingBudget || 1200} days={7} departDate="" returnDate="" expIncludes={["air","hotel"]} serviceFee={fee.serviceFee} feeLabel={fee.feeLabel} onClose={() => setAffordableBookingDest(null)} user={user} aiItinerary={null} saveItinerary={saveItineraryFlow} onPostcardEarn={() => setPostcardsData(getPostcards())} onViewItineraries={() => { setAccountDefaultTab("itineraries"); setMode("trips"); }} />; })())}
      {showBlindPayment && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" }}>
          <div style={{ ...dynCard, width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", animation: "fadeIn 0.3s ease", border: "1px solid rgba(27,58,92,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <button onClick={() => setShowBlindPayment(false)} style={{ background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: teal }}>Complete Your Surprise Booking</h2>
            </div>

            {/* Summary */}
            <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 14, padding: "16px", marginBottom: 20, display: "flex", gap: 14, alignItems: "center" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <circle cx="22" cy="22" r="19" fill={`${teal}22`} stroke={teal} strokeWidth="2"/>
                <ellipse cx="22" cy="22" rx="9" ry="19" fill={`${teal}10`} stroke={teal} strokeWidth="1.6"/>
                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
              </svg>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: teal }}>Surprise Me</div>
                <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>Destination revealed after payment · {blindDuration} nights · {formatTravelers(parseInt(travelers, 10) || 1, "full")}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: goldText, marginTop: 6 }}>${parseInt(String(blindBudget).replace(/\D/g,""),10).toLocaleString()}</div>
              </div>
            </div>

            {/* Similar trips teaser */}
            <div style={{ background: "rgba(27,58,92,0.04)", border: "1px solid rgba(27,58,92,0.15)", borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: teal, marginBottom: 8 }}>🎯 Trips similar to yours have included:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {["🇵🇹 Lisbon, Portugal", "🇯🇵 Kyoto, Japan", "🇨🇴 Cartagena, Colombia", "🇪🇸 Barcelona, Spain"].map(d => (
                  <div key={d} style={{ fontSize: 13, color: "#4A8CC4", fontWeight: 600 }}>{d}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: textDim, lineHeight: 1.6, fontStyle: "italic" }}>Every destination is selected based on your preferences, budget, and timing.</div>
            </div>

            {/* Preferences review */}
            <div style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: "1px solid rgba(27,58,92,0.096)", borderRadius: 14, padding: "16px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: textMuted, textTransform: "uppercase", marginBottom: 12 }}>Your Preferences</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { label: "Region", value: Array.isArray(blindRegion) ? blindRegion.join(", ") : (blindRegion || "Anywhere") },
                  { label: "Duration", value: `${blindDuration} nights` },
                  { label: "Travelers", value: formatTravelers(parseInt(travelers, 10) || 1, "full") },
                  { label: "Budget", value: `$${parseInt(String(blindBudget).replace(/\D/g,""),10).toLocaleString()} per person` },
                  { label: "Vibes", value: blindVibes.length ? blindVibes.join(", ") : "Open to anything" },
                  { label: "Dates", value: blindDateFrom && blindDateTo ? `${blindDateFrom} → ${blindDateTo}` : blindMonth !== null ? MONTHS[blindMonth] : "Flexible" },
                  { label: "Includes", value: blindIncludes.length ? blindIncludes.map(i => i === "air" ? "Flights" : i === "hotel" ? "Hotel" : i === "transport" ? "Transport" : "Activities").join(", ") : "Not specified" },
                  ...(blindLayoverOk.length ? [{ label: "Flights", value: blindLayoverOk.join(", ") }] : []),
                  ...(hotelType.length ? [{ label: "Hotel style", value: hotelType.join(", ") }] : []),
                  ...(activityTypes.length ? [{ label: "Activities", value: activityTypes.join(", ") }] : []),
                  ...(user?.avoidDestinations ? [{ label: "⚠️ Avoiding", value: user.avoidDestinations, warn: true }] : []),
                  ...(user?.avoidAirlines ? [{ label: "⚠️ Avoid airlines", value: user.avoidAirlines, warn: true }] : []),
                  ...(user?.avoidHotels ? [{ label: "⚠️ Avoid hotels", value: user.avoidHotels, warn: true }] : []),
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, ...(row.warn ? { background: "rgba(255,107,107,0.05)", borderRadius: 8, padding: "6px 8px", margin: "2px -8px", border: "1px solid rgba(255,107,107,0.15)" } : {}) }}>
                    <span style={{ fontSize: 12, color: row.warn ? "#ff6b6b" : "#4A6B8A", flexShrink: 0, fontWeight: row.warn ? 700 : 400 }}>{row.label}</span>
                    <span style={{ fontSize: 12, color: row.warn ? coral : "#4A8CC4", textAlign: "right", fontWeight: 600 }}>{row.value}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowBlindPayment(false)} style={{ marginTop: 12, background: "none", border: "none", color: teal, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 }}>
                ← Edit preferences
              </button>
              {(user?.avoidDestinations || user?.avoidAirlines || user?.avoidHotels) && (
                <div style={{ marginTop: 10, fontSize: 11, color: "#1B3A5C", fontStyle: "italic" }}>
                  Your avoid preferences from your profile are applied to this booking.
                </div>
              )}
            </div>
            <button onClick={() => setBlindAddInsurance(a => !a)} style={{ width: "100%", marginBottom: 16, padding: "12px 16px", borderRadius: 12, border: `1px solid ${blindAddInsurance ? "rgba(74,140,196,0.45)" : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"}`, background: blindAddInsurance ? "rgba(74,140,196,0.08)" : "rgba(27,58,92,0.030)", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: blindAddInsurance ? "#1B3A5C" : "#4A6B8A" }}>Travel Protection <span style={{ fontWeight: 400, color: textDim, fontSize: 11 }}>· Recommended for international trips</span></div>
                <div style={{ fontSize: 11, color: textDim, marginTop: 2 }}>Trip cancellation · Medical up to $50K · Lost baggage · +$49</div>
              </div>
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${blindAddInsurance ? "#4A8CC4" : "rgba(27,58,92,0.18)"}`, background: blindAddInsurance ? "#4A8CC4" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#FAFAF5", fontWeight: 900, flexShrink: 0 }}>
                {blindAddInsurance ? "✓" : ""}
              </div>
            </button>

            {/* ── Date requirement check ── */}
            {(blindDateFrom === "" && blindMonth === null) && (
              <div style={{ background: "rgba(255,107,107,0.07)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#ff6b6b", marginBottom: 6 }}>📅 Dates required before payment</div>
                <div style={{ fontSize: 12, color: "#ff9090", lineHeight: 1.6, marginBottom: 12 }}>Please go back and select either specific travel dates or a travel month so we can confirm availability for your trip.</div>
                <button onClick={() => {
                  setShowBlindPayment(false);
                  // Allow modal dismissal + page repaint before scrolling
                  setTimeout(() => {
                    const el = document.getElementById("sm-date-section");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 120);
                }} style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "#ff6b6b", color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  ← Add dates
                </button>
              </div>
            )}

            {/* ── Phase 1 booking gate (SM) ────────────────────────────
               Same pattern as BookingModal: approved users with saved codes
               auto-validate to "cleared to book" badge; everyone else sees
               waitlist signup. Phase 2 reintroduces the explicit code field. */}
            {blindCodeStatus === "valid" && blindCodeRecord ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "rgba(109,189,109,0.10)", border: "1px solid rgba(109,189,109,0.4)", marginBottom: 16 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.18)"/>
                  <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C" }}>You're cleared to book</div>
                  <div style={{ fontSize: 11, color: "#4A6B8A" }}>Welcome back{blindCodeRecord.customerName ? `, ${blindCodeRecord.customerName}` : ""}. Continue to confirm your trip.</div>
                </div>
              </div>
            ) : blindCodeStatus === "checking" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.20)", marginBottom: 16 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${teal}`, borderTopColor: "transparent", animation: "jgaSpin 0.7s linear infinite", flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: "#4A6B8A" }}>Verifying your booking access…</div>
              </div>
            ) : (
              <div style={{ background: `linear-gradient(135deg, rgba(74,140,196,0.10) 0%, rgba(74,140,196,0.04) 100%)`, border: `1px solid rgba(74,140,196,0.35)`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                {blindWaitlistStatus === "joined" ? (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
                      <circle cx="12" cy="12" r="10" stroke="#6dbd6d" strokeWidth="1.8" fill="rgba(109,189,109,0.18)"/>
                      <path d="M8 12 L 11 15 L 16 9" stroke="#6dbd6d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1B3A5C", marginBottom: 4 }}>You're on the waitlist!</div>
                      <div style={{ fontSize: 12, color: "#4A6B8A", lineHeight: 1.5 }}>
                        We'll email you at <strong>{user?.email || "your email"}</strong> when bookings open. Your Surprise Me preferences are saved.
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#4A8CC4", fontWeight: 800, marginBottom: 4 }}>JOIN THE WAITLIST TO BOOK</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, lineHeight: 1.35, marginBottom: 6 }}>
                      Ready for your Surprise Me trip?
                    </div>
                    <div style={{ fontSize: 12, color: "#4A6B8A", marginBottom: 12, lineHeight: 1.5 }}>
                      We're rolling out booking access in waves — we'll email you when you're cleared to book a surprise adventure.
                    </div>
                    <textarea value={blindWaitlistNotes}
                      onChange={e => setBlindWaitlistNotes(e.target.value)}
                      placeholder="Anything we should know? (Optional — special occasion, dream regions, etc.)"
                      rows={3}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: "rgba(27,58,92,0.060)", border: "1px solid rgba(27,58,92,0.144)", color: "#1B3A5C", outline: "none", boxSizing: "border-box", resize: "vertical", marginBottom: 10 }}
                    />
                    <button onClick={async () => {
                        if (!user?.email) {
                          alert("Please sign in or register first so we know where to email you when you're cleared to book.");
                          return;
                        }
                        setBlindWaitlistStatus("sending");
                        const result = await joinWaitlist({
                          customerName: user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "",
                          email: user.email,
                          departureAirport: blindDepartureAirport || user?.departureAirport,
                          budget: blindBudget ? String(blindBudget) : undefined,
                          travelDates: blindDateFrom || (blindMonth !== null ? MONTHS[blindMonth] : "flexible"),
                          duration: blindDuration ? `${blindDuration} nights` : undefined,
                          vibes: Array.isArray(blindVibes) && blindVibes.length ? blindVibes.join(", ") : undefined,
                          mode: "surprise",
                          notes: [
                            blindRegion && blindRegion !== "Anywhere" ? `Preferred region: ${Array.isArray(blindRegion) ? blindRegion.join(", ") : blindRegion}` : null,
                            blindWaitlistNotes.trim() || null,
                          ].filter(Boolean).join(" · ") || undefined,
                        });
                        if (result.ok) setBlindWaitlistStatus("joined");
                        else setBlindWaitlistStatus("error");
                      }}
                      disabled={blindWaitlistStatus === "sending"}
                      style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: blindWaitlistStatus === "sending" ? "rgba(74,140,196,0.5)" : `linear-gradient(135deg, #4A8CC4, #8B5F70)`, color: "#fff", fontWeight: 800, fontSize: 14, cursor: blindWaitlistStatus === "sending" ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                      {blindWaitlistStatus === "sending" ? "Joining…" : blindWaitlistStatus === "error" ? "Try again" : "Join the waitlist →"}
                    </button>
                    {blindWaitlistStatus === "error" && (
                      <div style={{ fontSize: 11, color: coral, marginTop: 8, textAlign: "center" }}>Something went wrong. Please try again or email support@justgoanywhere.com</div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Card form */}
            <Label>Payment Details</Label>
            {(() => {
              const blindInputStyle = (field) => ({
                width: "100%", padding: "13px 14px", borderRadius: 12, fontSize: 15, fontFamily: "inherit",
                background: blindCard[field] ? "rgba(27,58,92,0.07)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)",
                border: `1px solid ${blindCardErrors[field] ? "rgba(255,107,107,0.5)" : blindCard[field] ? "rgba(27,58,92,0.4)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`,
                color: textPrimary, outline: "none", boxSizing: "border-box", marginBottom: blindCardErrors[field] ? 4 : 14,
              });

              function validateBlindCard() {
                const e = {};
                if (!blindCard.name.trim()) e.name = "Required";
                if (blindCard.number.replace(/\s/g,"").length < 16) e.number = "Enter a valid 16-digit card number";
                if (!blindCard.expiry.match(/^\d{2}\/\d{2}$/)) e.expiry = "Format: MM/YY";
                if (blindCard.cvv.length < 3) e.cvv = "3 or 4 digits required";
                setBlindCardErrors(e);
                return Object.keys(e).length === 0;
              }

              async function handleBlindPay() {
                if (blindDateFrom === "" && blindMonth === null) { alert("Please go back and add travel dates or a month before completing your booking."); setShowBlindPayment(false); return; }
                if (!blindBookingCode.trim()) { setBlindBookingCodeError("Please enter your booking reference code to continue."); return; }
                if (blindCodeStatus !== "valid") { setBlindBookingCodeError("Please validate your code before continuing."); return; }
                if (!validateBlindCard()) return;
                setBlindProcessing(true);
                const parsedBudget = parseInt(String(blindBudget).replace(/\D/g,""),10) || 1500;
                const confCode = `TMA-${Math.random().toString(36).slice(2,8).toUpperCase()}`;
                const total = parsedBudget + (blindAddInsurance ? 49 : 0) + 49;
                await new Promise(r => setTimeout(r, 1500));
                setBlindProcessing(false);
                setShowBlindPayment(false);
                setBlindCard({ name: "", number: "", expiry: "", cvv: "" });
                setBlindCardErrors({});
                // Show curating modal and track purchase — also reset blind flow so no black screen
                setShowCuratingModal(true);
                setMode(null);
                setStep(0);
                setBlindResult(null);
                setBlindRevealed(false);
                setEnvelopeOpen(false);
                if (typeof window.jgaTrack === "function") window.jgaTrack("purchase", { mode: "surprise_me", value: total, currency: "USD" });

                // Generate AI itinerary silently in background (for admin reference only, no UI changes)
                let aiResult = null;
                try {
                  const aiAirport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
                  const pool = DESTINATIONS.filter(d => {
                    const baselineCost = getBaselineTripCost(d, aiAirport, 5);
                    return parsedBudget >= baselineCost /* upper-bound ceiling removed: high budgets must never return zero */;
                  });
                  const dest = pool[Math.floor(Math.random() * pool.length)];
                  if (dest) {
                    const loc = dest.state ? `${dest.name}, ${dest.state}, ${dest.country}` : `${dest.name}, ${dest.country}`;
                    const blindCurrencyNote = fahrenheit ? "Use USD ($) for prices." : `Use local currency for ${dest.country} (€ for Europe, £ for UK, ¥ for Japan, etc.).`;
                    const blindTempNote = fahrenheit ? "Use Fahrenheit (°F)." : "Use Celsius (°C).";
                    const j = await callAI(`You are a travel booking AI. Destination: ${loc}. Duration: ${blindDuration} nights. Budget: $${parsedBudget}. ${blindCurrencyNote} ${blindTempNote} Return ONLY JSON: {"destination":"${dest.name}","country":"${dest.country}","emoji":"${dest.emoji}","tagline":"one sentence","days":[{"day":1,"title":"string","highlight":"string"}],"packingTip":"string"}`);
                    aiResult = j;
                  }
                } catch(e) { console.log("Background itinerary generation failed:", e); }

                // Post pending booking to backend
                try {
                  const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
                  const token = localStorage.getItem("jga_token");
                  await fetch(`${API}/api/bookings/pending`, {
                    // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                      destination: aiResult?.destination || "Surprise Me",
                      destinationEmoji: aiResult?.emoji || "🌍",
                      mode: "Surprise Me",
                      budget: parsedBudget,
                      travelers,
                      duration: blindDuration,
                      vibes: blindVibes.join(", ") || "Open",
                      region: Array.isArray(blindRegion) ? blindRegion.join(", ") : blindRegion,
                      hotelPreference: hotelType.join(", ") || "No preference",
                      flightPreference: Array.isArray(blindLayoverOk) ? blindLayoverOk.join(", ") : "No preference",
                      activityPreferences: activityTypes.join(", ") || "No preference",
                      departureAirport: blindDepartureAirport || user?.departureAirport || "Not specified",
                      includes: blindIncludes.join(", "),
                      checkIn: blindDateFrom || "TBD",
                      checkOut: blindDateTo || "TBD",
                      travelingWithPet,
                      travelingWithChildren,
                      travelerAgeRanges: travelerAges.filter(Boolean).join(", ") || "Not specified",
                      avoidDestinations: user?.avoidDestinations || "",
                      avoidAirlines: user?.avoidAirlines || "",
                      avoidHotels: user?.avoidHotels || "",
                      userEmail: user?.email || "Guest",
                      userName: user ? `${user.firstName} ${user.lastName || ""}`.trim() : "Guest",
                      aiItinerary: aiResult,
                      confirmationCode: confCode,
                      totalCost: total,
                      includesInsurance: blindAddInsurance,
                      bookedAt: new Date().toISOString(),
                    }),
                  });
                } catch(e) { console.error("Pending booking failed:", e); }
              }

              return (
                <div>
                  {(blindCodeStatus === "valid" || !isPreLaunch()) ? (
                    <>
                      <input value={blindCard.name} onChange={e => setBlindCard(c => ({...c, name: e.target.value}))} placeholder="Name on card" aria-label="Cardholder name" style={blindInputStyle("name")} />
                      {blindCardErrors.name && <div style={{ color: coral, fontSize: 11, marginBottom: 10 }}>{blindCardErrors.name}</div>}
                      <input value={blindCard.number} onChange={e => setBlindCard(c => ({...c, number: e.target.value.replace(/[^\d\s]/g,"").slice(0,19)}))} placeholder="1234 5678 9012 3456" maxLength={19} aria-label="Card number" style={blindInputStyle("number")} />
                      {blindCardErrors.number && <div style={{ color: coral, fontSize: 11, marginBottom: 10 }}>{blindCardErrors.number}</div>}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
                        <div>
                          <input value={blindCard.expiry} onChange={e => { let v = e.target.value.replace(/\D/g,""); if (v.length >= 2) v = v.slice(0,2) + "/" + v.slice(2,4); setBlindCard(c => ({...c, expiry: v})); }} placeholder="MM/YY" maxLength={5} style={{...blindInputStyle("expiry"), marginBottom: 0}} />
                          {blindCardErrors.expiry && <div style={{ color: coral, fontSize: 11, marginTop: 4 }}>{blindCardErrors.expiry}</div>}
                        </div>
                        <div>
                          <input value={blindCard.cvv} onChange={e => setBlindCard(c => ({...c, cvv: e.target.value.replace(/\D/g,"").slice(0,4)}))} placeholder="CVV" maxLength={4} style={{...blindInputStyle("cvv"), marginBottom: 0}} />
                          {blindCardErrors.cvv && <div style={{ color: coral, fontSize: 11, marginTop: 4 }}>{blindCardErrors.cvv}</div>}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Pre-launch w/o valid code — show a notice instead of CC inputs */
                    <div style={{ borderRadius: 12, border: "1px solid rgba(27,58,92,0.22)", background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", padding: "16px 18px", marginBottom: 18, textAlign: "center" }}>
                      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Coming Soon</div>
                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color: "#4A6B8A", fontStyle: "italic", lineHeight: 1.5 }}>Join the waitlist above to get an early-access invite.</div>
                    </div>
                  )}
                  <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 10, padding: "12px 14px", marginTop: 16, marginBottom: 20 }}>
                    {blindAddInsurance && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#1B3A5C", marginBottom: 6 }}>
                        <span>Travel Protection</span><span>+$49</span>
                      </div>
                    )}
                    {blindCurationFee > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: textMuted, marginBottom: 6 }}>
                        <span>{blindFeeLabel}</span><span>+${blindCurationFee}</span>
                      </div>
                    )}
                    <div style={{ borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 14, color: "#4A8CC4", fontWeight: 700 }}>Total today</span>
                      <span style={{ fontSize: 22, fontWeight: 900, color: goldText }}>${(parseInt(String(blindBudget).replace(/\D/g,""),10) + (blindAddInsurance ? 49 : 0) + blindCurationFee).toLocaleString()}</span>
                    </div>
                    {/* Price promise breakdown */}
                    <div style={{ marginTop: 10, background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "10px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: textMuted, marginBottom: 4 }}>
                        <span>Trip budget</span><span style={{ color: goldText }}>${parseInt(String(blindBudget).replace(/\D/g,""),10)?.toLocaleString()}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: textMuted, marginBottom: 4 }}>
                        <span>Actual cost</span><span style={{ color: textDim, fontStyle: "italic" }}>Calculated after booking</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#1B3A5C", fontWeight: 700, marginTop: 2 }}>If lower than budget — you get the difference back</div>
                    </div>

                    {/* Sealed destination teaser — editorial postmark aesthetic
                        instead of the blurred-text placeholder, which read
                        tacky. Clean maroon ink-on-cream with a wax-seal
                        feeling that matches the "reveal" narrative. */}
                    <div style={{ marginTop: 10, position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid rgba(27,58,92,0.25)", background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                      {/* Wax-seal icon */}
                      <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, #A0463C, #0F2747)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(111,45,36,0.35), inset 0 1px 2px rgba(255,255,255,0.18)" }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M5 8 L12 13 L19 8 M5 8 L5 17 Q5 18 6 18 L18 18 Q19 18 19 17 L19 8 M5 8 L12 3 L19 8" stroke="#FAFAF5" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Destination Sealed</div>
                        <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 13, color: "#4A6B8A", fontStyle: "italic", lineHeight: 1.45 }}>Your trip will be revealed the moment you're ready to look.</div>
                      </div>
                    </div>

                    {blindCurationFee > 0 && (
                      <div style={{ fontSize: 11, color: textDim, marginTop: 8 }}>Includes ${blindCurationFee} {isBlindDomestic ? "domestic" : "international"} curation fee · our team personally books your trip</div>
                    )}
                  </div>
                  <TrustBand variant="compact" darkMode={darkMode} style={{ marginBottom: 10 }} />
                  {blindCodeStatus === "valid" ? (
                    <button onClick={handleBlindPay} disabled={blindProcessing} style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: blindProcessing ? darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)" : `linear-gradient(135deg,${teal},#0F2747)`, color: blindProcessing ? "#4A6B8A" : "#FAFAF5", fontWeight: 900, fontSize: 16, cursor: blindProcessing ? "not-allowed" : "pointer", fontFamily: "inherit", letterSpacing: 0.3 }}>
                      {blindProcessing ? "Processing…" : "Book My Surprise Trip →"}
                    </button>
                  ) : (
                    // Phase 1: user hasn't been cleared to book yet — pay
                    // button is hidden, they join waitlist above instead
                    <button onClick={() => setShowBlindPayment(false)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: "rgba(27,58,92,0.045)", color: "#4A6B8A", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {blindWaitlistStatus === "joined" ? "Done — back to browsing" : "Close"}
                    </button>
                  )}
                  {(blindCodeStatus === "valid" || !isPreLaunch()) && (
                    <div style={{ fontSize: 11, color: textDim, textAlign: "center", marginTop: 10 }}>🔒 Secured by Stripe · 100% refund if cancelled</div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {showBlindGate && <BlindGateModal onClose={() => setShowBlindGate(false)} onSuccess={(userData) => { handleRegisterSuccess(userData); setShowBlindGate(false); const userIsMinor = userData?.ageRange === "under18" || (userData?.dob && (Date.now() - new Date(userData.dob).getTime()) / (365.25*24*60*60*1000) < 18); if (userIsMinor) { setShowMinorBlock(true); } else if (userData?.emailVerified === false) { setShowVerifyBlock(true); } else { setShowBlindPayment(true); } }} onLogin={(userData) => { handleLoginSuccess(userData); setShowBlindGate(false); const userIsMinor = userData?.ageRange === "under18" || (userData?.dob && (Date.now() - new Date(userData.dob).getTime()) / (365.25*24*60*60*1000) < 18); if (userIsMinor) { setShowMinorBlock(true); } else if (userData?.emailVerified === false) { setShowVerifyBlock(true); } else { setShowBlindPayment(true); } }} />}
      {showWhyPopup && !showBlindPayment && (
        <WhySurprisePopup
          onBook={() => { setShowWhyPopup(false); if (isMinor) { setShowMinorBlock(true); } else if (isUnverified) { setShowVerifyBlock(true); } else { setShowBlindPayment(true); } }}
          onClose={() => setShowWhyPopup(false)}
        />
      )}
      {showExitQuestion && (
        <ExitQuestionModal
          onClose={() => { setShowExitQuestion(false); reset(); }}
          onAdjustBudget={() => { setShowExitQuestion(false); setBlindBudget("800"); }}
          onSeeTrips={() => { setShowExitQuestion(false); setMode("explore"); }}
          onSeeGuarantee={() => { setShowExitQuestion(false); setShowCancellationPolicy(true); }}
        />
      )}
      {showNotBookingModal && (
        <NotBookingModal
          onClose={() => setShowNotBookingModal(false)}
          onGoHome={() => { setShowNotBookingModal(false); reset(); }}
          onAdjustBudget={() => { setShowNotBookingModal(false); setBlindBudget("800"); }}
          onSeeTrips={() => { setShowNotBookingModal(false); setMode("explore"); }}
          onSeeGuarantee={() => { setShowNotBookingModal(false); setShowCancellationPolicy(true); }}
        />
      )}
      {showIdentityCard && travelerType && TRAVEL_IDENTITIES[travelerType] && (() => {
        const id = TRAVEL_IDENTITIES[travelerType];
        const sky = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
        // Split the identity name into "The X" + last word italicized in accent.
        // For two-word names like "Digital Nomad", first line = "The Digital",
        // second line = "Nomad". Three-word names: first line = "The {first}",
        // second line = "{second} {third}".
        const nameWords = travelerType.split(" ");
        const firstLine = nameWords.length === 1
          ? `The ${nameWords[0]}`
          : `The ${nameWords[0]}`;
        const secondLine = nameWords.slice(1).join(" ");
        // Mode label — capitalize first recommended mode
        const modeNames = {
          surprise: "Surprise Me", explore: "Take Me Anywhere",
          mood: "Mood Match", trending: "Trending", affordable: "Most Affordable",
        };
        const firstMode = Array.isArray(id.modes) && id.modes[0] ? (modeNames[id.modes[0]] || id.modes[0]) : "Mood Match";
        // Top 2 vibes joined
        const vibeStr = Array.isArray(id.vibes) ? id.vibes.slice(0, 2).map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(" · ") : "";

        return (
          <div role="dialog" aria-modal="true" aria-label="Your travel identity"
            style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(16px)", backdropFilter: "blur(16px)", animation: "fadeIn 0.3s ease" }}>
            <div style={{ width: "100%", maxWidth: 440, maxHeight: "92vh", overflowY: "auto", background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", border: "1px solid rgba(27,58,92,0.12)", borderRadius: 14, padding: 22, boxShadow: "0 4px 24px rgba(0,0,0,0.18)", animation: "fadeIn 0.4s cubic-bezier(0.34,1.2,0.64,1)" }}>

              {/* Header — chart number + close */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "#94A3B8", textTransform: "uppercase" }}>
                  Travel Identity
                </div>
                <button onClick={() => setShowIdentityCard(false)} aria-label="Close"
                  style={{ background: "rgba(27,58,92,0.08)", border: "none", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#4A6B8A", fontSize: 14, fontFamily: "inherit" }}>✕</button>
              </div>

              {/* Constellation chart on identity-specific night sky */}
              <div style={{
                position: "relative",
                width: "100%",
                aspectRatio: "200 / 140",
                borderRadius: 10,
                overflow: "hidden",
                border: `1px solid ${sky.accent}22`,
                marginBottom: 18,
                boxShadow: "0 2px 12px rgba(20,18,24,0.15)",
              }}>
                <ConstellationChart identity={id} identityName={travelerType} variant="inApp" animate={identityCardFreshReveal} />
                {/* Reveal overlay — "charting your sky..." text fades in before
                    constellation builds, then fades out as the name appears. */}
                {identityCardFreshReveal && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pointerEvents: "none",
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontStyle: "italic",
                    fontSize: 16,
                    color: "rgba(250,250,245,0.85)",
                    letterSpacing: 0.5,
                    opacity: 0,
                    animation: "jga-reveal-overlay 2.5s ease-in-out 0s forwards",
                  }}>
                    charting your sky…
                  </div>
                )}
              </div>

              {/* Identity name — two lines, italic accent on second */}
              <div style={{
                textAlign: "center",
                marginBottom: 12,
                animation: identityCardFreshReveal ? "jga-chart-text-in 0.4s ease-out 2.8s both" : undefined,
              }}>
                <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 28, fontWeight: 500, color: "#1B3A5C", lineHeight: 1.1, letterSpacing: "-0.4px" }}>
                  {firstLine}
                </div>
                <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: "italic", fontSize: 28, fontWeight: 500, color: sky.accent, lineHeight: 1.1, letterSpacing: "-0.4px" }}>
                  {secondLine}
                </div>
              </div>

              {/* Description quote */}
              <p style={{
                margin: "0 0 18px",
                textAlign: "center",
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontStyle: "italic",
                fontSize: 14,
                color: "#4A6B8A",
                lineHeight: 1.55,
                animation: identityCardFreshReveal ? "jga-chart-text-in 0.4s ease-out 3.0s both" : undefined,
              }}>
                "{id.desc}"
              </p>

              {/* Dashed divider */}
              <div style={{ borderTop: "1px dashed rgba(27,58,92,0.25)", marginBottom: 14 }} />

              {/* Attribute grid — maps each star on the chart to its value.
                  The chart labels are just ENERGY/PACE/etc; the actual values
                  live here in readable form. */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14,
                animation: identityCardFreshReveal ? "jga-chart-text-in 0.4s ease-out 3.2s both" : undefined,
              }}>
                {STAR_ORDER.map(attr => (
                  <div key={attr} style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: sky.accent, textTransform: "uppercase", marginBottom: 4, fontWeight: 600 }}>
                      {attr}
                    </div>
                    <div style={{ fontSize: 11, color: "#1B3A5C", lineHeight: 1.3 }}>
                      {id[attr] || "—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Dashed divider */}
              <div style={{ borderTop: "1px dashed rgba(27,58,92,0.25)", marginBottom: 14 }} />

              {/* 3-column info strip: TRIP | SUITS | VIBES */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14,
                animation: identityCardFreshReveal ? "jga-chart-text-in 0.4s ease-out 3.4s both" : undefined,
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Trip</div>
                  <div style={{ fontSize: 11, color: "#1B3A5C", fontWeight: 600 }}>{id.tripLength || "—"}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Suits</div>
                  <div style={{ fontSize: 11, color: "#1B3A5C", fontWeight: 600 }}>{firstMode}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9, letterSpacing: 1.4, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Vibes</div>
                  <div style={{ fontSize: 11, color: "#1B3A5C", fontWeight: 600 }}>{vibeStr || "—"}</div>
                </div>
              </div>

              {/* Dashed divider */}
              <div style={{ borderTop: "1px dashed rgba(27,58,92,0.25)", marginBottom: 14 }} />

              {/* Primary CTA — per-identity 'see trips' button. Uses the identity's
                  greetingCta from IDENTITY_COPY so the label feels personalized
                  ('Find your next sunset' for Sunset Chaser, etc.). On tap, closes
                  the modal and opens the mode comparison popup — same path as the
                  dashboard identity CTA. */}
              <button
                onClick={() => {
                  // Route directly to TMA with identity vibes pre-filled — user
                  // tapped the identity CTA from inside their chart, so they're
                  // already in identity-exploration mode. The mode comparison
                  // would be redundant friction here. Identity vibes seed TMA's
                  // filtering so the user lands on a list curated to their identity.
                  setShowIdentityCard(false);
                  reset();
                  if (Array.isArray(id.vibes)) setExpVibes(id.vibes);
                  setStep(0);
                  setMode("explore");
                }}
                style={{
                  width: "100%", padding: "13px 16px", borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${id.color}, ${id.color}cc)`,
                  color: "#FAFAF5",
                  fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  letterSpacing: 0.3,
                  marginBottom: 10,
                  boxShadow: `0 2px 12px ${id.color}40`,
                }}>
                {getIdentityCopy(travelerType).greetingCta} →
              </button>

              {/* Actions */}
              <div style={{
                display: "flex", gap: 10,
                animation: identityCardFreshReveal ? "jga-chart-text-in 0.4s ease-out 3.5s both" : undefined,
              }}>
                <button onClick={() => shareIdentityCard(id, travelerType)}
                  disabled={identitySharing}
                  style={{
                    flex: 1, padding: "12px 14px", borderRadius: 10,
                    border: "none",
                    background: id.color,
                    color: "#FAFAF5",
                    fontSize: 13, fontWeight: 700, cursor: identitySharing ? "wait" : "pointer", fontFamily: "inherit",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                    opacity: identitySharing ? 0.7 : 1,
                  }}>
                  {identitySharing ? "Generating…" : "Share my Travel Identity"}
                </button>
                <button onClick={() => { setShowIdentityCard(false); setOnboardingRetake(true); setShowOnboarding(true); }}
                  style={{
                    flex: 1, padding: "12px 14px", borderRadius: 10,
                    border: "1px solid rgba(27,58,92,0.3)",
                    background: "transparent",
                    color: "#4A6B8A",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}>
                  Retake quiz
                </button>
              </div>
              {identityShareError && (
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,107,107,0.12)", color: "#1B3A5C", fontSize: 12, textAlign: "center" }}>
                  {identityShareError}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Companion-identity update nudge ──
         Soft prompt shown when the user changed their Style chip 2+ days ago
         and the change was dramatic (e.g., Solo→Family). Lets them either
         retake the quiz to update identity, or keep current and silence the
         nudge for 30 days. */}
      {showCompanionIdentityNudge && pendingCompanionChange && (
        <div onClick={() => setShowCompanionIdentityNudge(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
            WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", zIndex: 9000,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, animation: "fadeIn 0.25s ease",
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              maxWidth: 420, width: "100%", borderRadius: 18,
              background: darkMode ? "#1a2235" : "#fff",
              border: `1px solid ${teal}40`,
              padding: "28px 24px", textAlign: "center",
              boxShadow: `0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px ${teal}20`,
            }}>
            <div style={{ width: 56, height: 56, borderRadius: 28, background: `${teal}18`, border: `1.5px solid ${teal}50`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="6" cy="9" r="2.3" stroke={teal} strokeWidth="1.6" fill="none"/>
                <circle cx="12" cy="7" r="2.3" stroke={teal} strokeWidth="1.6" fill="none"/>
                <circle cx="18" cy="9" r="2.3" stroke={teal} strokeWidth="1.6" fill="none"/>
              </svg>
            </div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: teal, fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>Travel style update</div>
            <h2 style={{ margin: "0 0 10px", fontSize: 19, fontWeight: 900, color: textPrimary, lineHeight: 1.3 }}>
              Want to update your travel identity?
            </h2>
            <p style={{ margin: "0 0 22px", fontSize: 13, color: textMuted, lineHeight: 1.55 }}>
              You've been planning <strong style={{ color: textPrimary, textTransform: "capitalize" }}>{pendingCompanionChange.to}</strong> trips for a few days now. Retaking the quiz can refresh your identity to better match how you're traveling lately.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => {
                // Accept — open quiz in retake mode, clear the change record
                try { localStorage.removeItem("jga-companion-changed-at"); } catch {}
                setShowCompanionIdentityNudge(false);
                setPendingCompanionChange(null);
                setOnboardingRetake(true);
                setShowOnboarding(true);
              }}
                style={{
                  width: "100%", padding: "12px 16px", borderRadius: 12,
                  border: "none", background: teal, color: "#FAFAF5",
                  fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                }}>
                Update identity →
              </button>
              <button onClick={() => {
                // Dismiss — silence for 30 days (local + backend), clear the
                // change record so we don't keep checking the same stale event.
                try {
                  localStorage.setItem("jga-companion-nudge-dismissed-at", String(Date.now()));
                  localStorage.removeItem("jga-companion-changed-at");
                } catch {}
                syncNudgeDismissedToBackend();
                setShowCompanionIdentityNudge(false);
                setPendingCompanionChange(null);
              }}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: 12,
                  border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`,
                  background: "transparent", color: textMuted,
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                Keep current identity
              </button>
            </div>
            <div style={{ marginTop: 14, fontSize: 10, color: textDim, fontStyle: "italic" }}>
              We'll ask again in 30 days if your style stays different
            </div>
          </div>
        </div>
      )}

      {showOnboarding && (
        <OnboardingModal
          registered={registered}
          forceRetake={onboardingRetake}
          shareIdentityCard={shareIdentityCard}
          onOpenLogin={(email) => { setLoginInitialEmail(email || ""); setShowOnboarding(false); setOnboardingRetake(false); setShowLogin(true); }}
          onOpenRegister={(email, identityName) => {
            setShowOnboarding(false);
            setOnboardingRetake(false);
            // Pre-populate the register email with whatever they entered at the
            // quiz-save step, so they don't retype it.
            if (email) setPrefillRegisterEmail(email);
            // Set the identity now so the just-taken quiz result survives
            // registration. It's also in localStorage, and handleRegisterSuccess
            // syncs it to the backend — this fixes the "quiz didn't populate
            // after I registered" bug.
            if (identityName) setTravelerType(identityName);
            setShowRegister(true);
          }}
          onSelect={(identityName, companion) => {
          setShowOnboarding(false);
          setOnboardingRetake(false);
          setTravelerType(identityName);
          if (companion) setTravelCompanion(companion);
          // Quiz was just completed — clear any pending companion-change nudge state
          // since the identity is now fresh either way.
          try {
            localStorage.removeItem("jga-companion-changed-at");
            localStorage.removeItem("jga-companion-nudge-dismissed-at");
          } catch {}
          if (!identityName) return;
          const id = TRAVEL_IDENTITIES[identityName];
          if (id) {
            // Pre-populate vibes for both modes
            setExpVibes(id.vibes || []);
            setBlindVibes(id.vibes || []);
            // Pre-populate hotel type
            const hotelMap = {"Luxury Escapist": "luxury", "Romantic Wanderer": "boutique", "Soft-Life Traveler": "boutique", "Cultural Nomad": "boutique", "Culinary Explorer": "boutique", "Sunset Chaser": "boutique", "Controlled Chaos Traveler": "economical", "Adrenaline Collector": "economical", "Spontaneous Minimalist": "budget", "Off-Grid Dreamer": "budget", "Digital Nomad": "economical", "Heritage Seeker": "boutique", "Eco Wanderer": "economical", "Festival Hunter": "economical", "Photo Pilgrim": "boutique", "Wellness Pilgrim": "luxury", "Bookworm Wanderer": "boutique", "Family Architect": "boutique", "Solo Soul-Searcher": "economical", "Night Owl Nomad": "boutique"};
            const hotelPref = hotelMap[identityName];
            if (hotelPref) { setExpHotelType([hotelPref]); setHotelType([hotelPref]); }
            // Pre-populate trip duration
            const durationMap = {"Romantic Wanderer": 8, "Controlled Chaos Traveler": 7, "Culinary Explorer": 6, "Luxury Escapist": 7, "Adrenaline Collector": 10, "Soft-Life Traveler": 6, "Cultural Nomad": 14, "Spontaneous Minimalist": 7, "Off-Grid Dreamer": 10, "Sunset Chaser": 6, "Digital Nomad": 21, "Heritage Seeker": 12, "Eco Wanderer": 10, "Festival Hunter": 5, "Photo Pilgrim": 8, "Wellness Pilgrim": 7, "Bookworm Wanderer": 8, "Family Architect": 7, "Solo Soul-Searcher": 14, "Night Owl Nomad": 5};
            const dur = durationMap[identityName];
            if (dur) { setDays(dur); setBlindDuration(dur); }
            // Pre-populate traveler count based on companion type
            const companionTravelers = { "solo": 1, "couple": 2, "friends": 3, "family": 4 };
            if (companion && companionTravelers[companion]) {
              setTravelers(companionTravelers[companion]);
            }
            // Route to the dashboard. The user just saw the constellation
            // inside OnboardingModal's reveal phase, so no second modal needed —
            // they can tap the identity nav pill to view the card again later.
            setMode(null);
            setBottomTab(null);
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }, 250);
            // First-time quiz completion (not retake) → show stamps seeding
            // flow per Jun 2026 onboarding spec. Skip is always available.
            // Also skip for registered users entirely — they completed any
            // first-time onboarding steps at registration. Even if their
            // identity failed to hydrate from the backend (cross-browser
            // verify scenario) and the system mistakenly treats them as
            // first-time, they should NOT see the seeding modal again.
            // Added Jun 25, 2026 (Cas's "retake routed to quick start" bug).
            if (!onboardingRetake && !registered) {
              setStampsSeedingState("visited");
            }
            // Surface the "Almost there" reminder to unregistered users who
            // just finished the quiz. The state init only runs at App mount,
            // so we need to flip it explicitly here for the new-user path.
            if (!registered) {
              try { sessionStorage.removeItem("jga-almost-there-dismissed"); } catch {}
              setShowAlmostThereBanner(true);
            }
          }
        }} />
      )}
      {/* Stamps history seeding — post-quiz, pre-dashboard. Logs an
         analytics event on completion so backend can trigger identity
         recalc when that engine ships. */}
      {stampsSeedingState && (
        <StampsSeedingModal
          catalog={SUGGESTED_STAMPS}
          identity={travelerType && TRAVEL_IDENTITIES[travelerType]}
          travelerType={travelerType}
          addState={stampsSeedingState}
          existingStampCount={(() => { try { return Object.keys(getStamps()).length; } catch (e) { return 0; } })()}
          existingStampIds={(() => { try { return Object.keys(getStamps()); } catch (e) { return []; } })()}
          onSkip={() => {
            setStampsSeedingState(null);
            try {
              console.log("[JGA Analytics] stamps_seeding_skipped", { travelerType });
            } catch (e) {}
          }}
          onComplete={(seededStamps) => {
            // Write each seeded stamp using the addState the modal was opened
            // in ("someday" or "visited"). Visited stamps carry yearVisited
            // and visitedAt; someday stamps don't.
            try {
              const current = JSON.parse(localStorage.getItem(STAMP_STORAGE_KEY) || "{}");
              const now = Date.now();
              const isVisited = stampsSeedingState === "visited";
              for (const s of seededStamps) {
                current[s.id] = {
                  state: stampsSeedingState,
                  source: isVisited ? "seeded_during_onboarding" : "added_someday",
                  createdAt: now,
                  updatedAt: now,
                  visitedAt: isVisited ? now : null,
                  yearVisited: isVisited ? s.yearVisited : null,
                  visitedNote: isVisited ? s.note : null,
                  plannedFor: null,
                };
              }
              localStorage.setItem(STAMP_STORAGE_KEY, JSON.stringify(current));
            } catch (e) {
              console.error("[JGA Seeding] persist failed", e);
            }
            setStampsSeedingState(null);
            setJournalRefreshTick(t => t + 1); // tell AccountModal to re-read
            try {
              console.log("[JGA Analytics] stamps_seeding_completed", {
                travelerType,
                addState: stampsSeedingState,
                stampCount: seededStamps.length,
                countryCount: new Set(seededStamps.map(s => s.country)).size,
              });
            } catch (e) {}
          }}
        />
      )}
      {postcardsSeedingState && (
        <PostcardsSeedingModal
          catalog={POSTCARDS_CATALOG}
          addState={postcardsSeedingState}
          existingPostcardCount={(() => { try { return Object.keys(getPostcards()).length; } catch (e) { return 0; } })()}
          existingPostcardIds={(() => { try { return Object.keys(getPostcards()); } catch (e) { return []; } })()}
          onSkip={() => {
            setPostcardsSeedingState(null);
            try {
              console.log("[JGA Analytics] postcards_seeding_skipped", {});
            } catch (e) {}
          }}
          onComplete={(seededPostcards) => {
            try {
              const current = JSON.parse(localStorage.getItem(POSTCARD_STORAGE_KEY) || "{}");
              const now = Date.now();
              const isVisited = postcardsSeedingState === "visited";
              for (const p of seededPostcards) {
                current[p.id] = {
                  state: postcardsSeedingState,
                  source: isVisited ? "added_visited" : "added_from_journal",
                  createdAt: now,
                  updatedAt: now,
                  visitedAt: isVisited ? now : null,
                };
              }
              localStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(current));
            } catch (e) {
              console.error("[JGA Postcards Seeding] persist failed", e);
            }
            setPostcardsSeedingState(null);
            setJournalRefreshTick(t => t + 1); // tell AccountModal to re-read
            try {
              console.log("[JGA Analytics] postcards_seeding_completed", {
                postcardCount: seededPostcards.length,
              });
            } catch (e) {}
          }}
        />
      )}
      {showAgentWaitlist && <AgentWaitlistModal onClose={() => setShowAgentWaitlist(false)} />}
      {tmaWaitlistContext && <TMAWaitlistModal context={tmaWaitlistContext} user={user} onClose={() => setTmaWaitlistContext(null)} />}
      {boardPickerDest && (
        <BoardPickerModal
          destName={boardPickerDest}
          boards={boards}
          setBoards={setBoards}
          boardsApi={boardsApi}
          setPostcardsData={setPostcardsData}
          hidePostcardSection={boardPickerHidePostcard}
          onClose={() => { setBoardPickerDest(null); setBoardPickerHidePostcard(false); }}
        />
      )}
      {swipeInfoDest && (
        <SwipeInfoModal
          destination={swipeInfoDest}
          userBudget={resolveUserBudget().value}
          userAirport={expDepartureAirport || blindDepartureAirport || user?.departureAirport || null}
          flightPrice={flightPrices?.[swipeInfoDest.name] || getBaselineFlightCost(swipeInfoDest, expDepartureAirport || blindDepartureAirport || user?.departureAirport || null)}
          fahrenheit={fahrenheit}
          travelers={paxFromCompanion(travelCompanion, travelers)}
          onClose={() => setSwipeInfoDest(null)}
          onSave={() => { setBoardPickerDest(swipeInfoDest.name); setSwipeInfoDest(null); }}
          onSkip={() => { setSwipeSkips(prev => ({ ...prev, [swipeInfoDest.name]: Date.now() })); setSwipeInfoDest(null); }}
        />
      )}

      {/* ── About Us Modal ── */}
      {showAboutUs && (
        <div role="dialog" aria-modal="true" aria-label="About Just Go Anywhere"
          style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 16px 40px", background: "rgba(0,0,0,0.78)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}
          onClick={() => setShowAboutUs(false)}>
          <div onClick={e => e.stopPropagation()} style={{ ...lightCard(darkMode), width: "100%", maxWidth: 600, margin: "auto 0", border: `1px solid ${gold}40` }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
              <div style={{ flex: 1 }}>
                {/* Brand lockup: globe + wordmark — leads the About page so
                   the user sees the brand before the manifesto. */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <JGAGlobe size={36} />
                  <JGAWordmark size={20} showTM />
                </div>
                <div style={{ fontSize: 11, letterSpacing: 2.5, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>About Us</div>
                <h2 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: textPrimary, lineHeight: 1.15, letterSpacing: "-0.5px" }}>An idea worth waiting fifteen years for.</h2>
              </div>
              <button onClick={() => setShowAboutUs(false)} aria-label="Close"
                style={{ background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 18, width: 32, height: 32, color: textMuted, fontSize: 18, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, marginLeft: 12 }}>✕</button>
            </div>

            {/* Hero manifesto */}
            <div style={{ padding: "18px 20px", borderRadius: 14, marginBottom: 22, background: `linear-gradient(135deg, ${gold}10, ${teal}06)`, border: `1px solid ${gold}25` }}>
              <p style={{ margin: 0, fontSize: 15, color: textPrimary, lineHeight: 1.65, fontStyle: "italic" }}>
                Most travel sites assume you already know where you want to go. We don't think that's how travel actually works. The best trips of your life weren't on a list — someone showed them to you, or you stumbled onto them by accident.
              </p>
              <p style={{ margin: "12px 0 0", fontSize: 14, color: textMuted, lineHeight: 1.6 }}>
                Just Go Anywhere<sup style={{ fontSize: 9, color: goldText }}>™</sup> exists to do that on purpose.
              </p>
            </div>

            {/* The origin story */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: goldText, textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>The origin</div>
              <p style={{ margin: "0 0 12px", fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                Fifteen years ago, before kids, I had a recurring thought: I wanted to go somewhere, and I didn't care where. Every booking site demanded I pick the destination first, and the idea got stuck there, and I shelved it.
              </p>
              <p style={{ margin: "0 0 12px", fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                In the meantime, I lived it. I planned my own honeymoon and extended a Dubai layover into part of the trip. I judge baton competitions across the country and turn every assignment into something more — a solo day at Universal, a zoo in Madison, WI, visiting friends in California. I never needed a travel agent. I needed a tool that worked the way I already traveled.
              </p>
              <p style={{ margin: "0 0 12px", fontSize: 14, color: textPrimary, lineHeight: 1.7, fontStyle: "italic" }}>
                I'm not a travel industry person. I'm the person travel industry tools were never built for.
              </p>
              <p style={{ margin: 0, fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                Years in precision manufacturing taught me operations. Running a non-profit taught me how to build something from nothing. Affiliate marketing taught me the business model. Selling my parents' house without a realtor — three offers over asking — taught me that the gatekeepers aren't always the experts. And a screenplay that landed in the top 1% of 10,000 submissions taught me that ideas executed with care beat ideas executed with credentials.
              </p>
            </div>

            {/* The turning point */}
            <div style={{ padding: "16px 18px", borderRadius: 12, marginBottom: 22, background: `${teal}08`, border: `1px solid ${teal}25` }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: teal, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>The turning point</div>
              <p style={{ margin: 0, fontSize: 14, color: textPrimary, lineHeight: 1.7, fontStyle: "italic" }}>
                Then AI happened, and the 15-year-old itch became buildable. So I built it.
              </p>
            </div>

            {/* What makes it different */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: goldText, textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>What makes JGA different</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, marginBottom: 3 }}>Matchmaking, not searching</div>
                  <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.55 }}>You answer a few honest questions about who you are. We propose where you should go. Twenty traveler identities, real personality matching — not just budget filters.</div>
                </div>
                <div style={{ padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, marginBottom: 3 }}>Built for the time-strapped</div>
                  <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.55 }}>Most people don't have three weekends to spend planning a trip. We collapse the overwhelm into a few thoughtful prompts, then deliver something bookable.</div>
                </div>
                <div style={{ padding: "12px 14px", borderRadius: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.02)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, marginBottom: 3 }}>A travel companion, not a booking engine</div>
                  <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.55 }}>Most platforms wave goodbye the moment your card is charged. We stay with you — through the trip, after the trip, into the next one.</div>
                </div>
              </div>
            </div>

            {/* The journaling story */}
            <div style={{ padding: "18px 20px", borderRadius: 14, marginBottom: 22, background: `${"#4A8CC4"}08`, border: `1px solid ${"#4A8CC4"}25` }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#4A8CC4", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Why we journal</div>
              <p style={{ margin: "0 0 10px", fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                Years ago I took a long trip through Europe. I remember some of it — but a lot of the texture is gone. The names of the streets I walked. The waiter who taught me a phrase. The exact reason a small detail in a small town made me cry.
              </p>
              <p style={{ margin: "0 0 10px", fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                I wish I'd written it down.
              </p>
              <p style={{ margin: 0, fontSize: 14, color: textPrimary, lineHeight: 1.7 }}>
                That's why JGA is also a place to log your trips, not just plan them. Travel is one of the few things that actually changes who you are — but only if you remember it. We want you to remember it.
              </p>
            </div>

            {/* The promise */}
            <div style={{ padding: "14px 16px", borderRadius: 12, background: `${teal}08`, border: `1px solid ${teal}25`, marginBottom: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: teal, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Our promise</div>
              <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.6 }}>
                Real trips for real travelers. Identity matched to destinations. Itineraries you'll actually use. Memories you'll actually keep. And support that knows your name.
              </div>
            </div>

            {/* The vision */}
            <div style={{ padding: "14px 16px", borderRadius: 12, background: `${gold}06`, border: `1px solid ${gold}25`, marginBottom: 22 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: goldText, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>What we're building toward</div>
              <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.6 }}>
                A place where the next great trip of your life is sitting somewhere on a map, waiting for someone to show you. And a place where, years from now, you can come back and remember exactly how it felt.
              </div>
            </div>

            {/* Sign-off */}
            <div style={{ textAlign: "center", padding: "12px 0 18px", fontSize: 13, fontStyle: "italic", color: textMuted, lineHeight: 1.6 }}>
              Built by one person, for the kind of traveler who knows there's something out there worth finding.
              <div style={{ marginTop: 6, fontSize: 12, color: goldText, fontStyle: "normal", fontWeight: 700, letterSpacing: 1 }}>— Let the destination find you.</div>
            </div>

            {/* CTAs */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <a href="mailto:support@justgoanywhere.com" style={{ flex: 1, minWidth: 140, padding: "10px 14px", borderRadius: 10, border: `1px solid ${teal}50`, background: "transparent", color: teal, fontSize: 13, fontWeight: 700, textAlign: "center", textDecoration: "none", fontFamily: "inherit" }}>
                ✉️ Get in touch
              </a>
              <button onClick={() => { setShowAboutUs(false); setShowAgentWaitlist(true); }}
                style={{ flex: 1, minWidth: 140, padding: "10px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${gold}, ${amber})`, color: "#FAFAF5", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                For travel agents →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Match Quality Feedback ──
         Was originally "Booking Abandonment Feedback" — reframed Jun 25, 2026
         (Cas's affiliate-pivot copy cleanup) to ask about the recommendation
         itself rather than presupposing the user was trying to book. Still
         gathers useful signal (was the match good? wrong vibe? wrong place?)
         but matches the affiliate-only product model. Backend submissions
         retagged "[Match Feedback - X]" so the data category is correct. */}
      {showAbandonFeedback && (() => {
        const REASONS = [
          { id: "loved", label: "✨ Loved this match", color: "#6dbd6d" },
          { id: "vibe", label: "🤔 Not quite my vibe", color: goldText },
          { id: "destination", label: "📍 Wrong destination for me", color: teal },
          { id: "timing", label: "📅 Right place, wrong timing", color: "#4A8CC4" },
          { id: "info", label: "💭 Need more info before deciding", color: coral },
          { id: "browsing", label: "👀 Just exploring", color: textMuted },
        ];
        return (
          <div role="dialog" aria-modal="true" aria-label="Quick feedback"
            style={{ position: "fixed", inset: 0, zIndex: 250, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 0, background: "rgba(0,0,0,0.55)", WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)", animation: "fadeIn 0.25s ease" }}
            onClick={() => { setShowAbandonFeedback(false); }}>
            <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, background: darkMode ? "rgba(13,21,33,0.98)" : "#fff", borderTopLeftRadius: 20, borderTopRightRadius: 20, border: `1px solid ${darkMode ? "rgba(74,140,196,0.2)" : "rgba(27,58,92,0.25)"}`, borderBottom: "none", padding: "20px 20px 32px", animation: "slideUp 0.3s ease" }}>
              <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
              {/* Drag handle */}
              <div style={{ width: 40, height: 4, borderRadius: 2, background: darkMode ? "rgba(27,58,92,0.14)" : "rgba(0,0,0,0.15)", margin: "0 auto 14px" }} />

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: goldText, textTransform: "uppercase", fontWeight: 700 }}>Quick question</div>
                <button onClick={() => setShowAbandonFeedback(false)} aria-label="Close"
                  style={{ background: "none", border: "none", color: textMuted, fontSize: 20, cursor: "pointer", fontFamily: "inherit", padding: 0, marginRight: -4 }}>✕</button>
              </div>
              <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: textPrimary, lineHeight: 1.3 }}>What's your take on this recommendation?</h3>
              <p style={{ margin: "0 0 16px", fontSize: 12, color: textMuted, lineHeight: 1.5 }}>Helps us tune matches for you. Takes 5 seconds.</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {REASONS.map(r => (
                  <button key={r.id} onClick={async () => {
                    // Send feedback to backend
                    try {
                      const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
                      await fetch(`${API}/api/suggestions`, {
                        // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ suggestion: `[Match Feedback - ${abandonContext.from}] ${r.label}`, email: user?.email || null }),
                      });
                    } catch {}
                    // Dismiss this destination from home-page recommendations
                    // so the next render rotates to a different match. Lasts
                    // 7 days — long enough that re-engagement on a different
                    // session feels fresh. The user can still find this dest
                    // via Explore / search; this only affects the auto-pick.
                    // Added Jun 25, 2026.
                    if (abandonContext.destName) {
                      try {
                        const dismissed = JSON.parse(localStorage.getItem("jga-zone1-dismissed-v1") || "{}");
                        dismissed[`rec_${abandonContext.destName}`] = Date.now() + (7 * 24 * 60 * 60 * 1000);
                        localStorage.setItem("jga-zone1-dismissed-v1", JSON.stringify(dismissed));
                      } catch {}
                    }
                    setShowAbandonFeedback(false);
                  }}
                    style={{ padding: "11px 14px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`, background: darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)", cursor: "pointer", fontFamily: "inherit", textAlign: "left", fontSize: 13, color: textPrimary, transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${r.color}80`; e.currentTarget.style.background = `${r.color}10`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.02)"; }}>
                    {r.label}
                  </button>
                ))}
              </div>

              <button onClick={() => setShowAbandonFeedback(false)}
                style={{ width: "100%", marginTop: 10, padding: "10px", background: "none", border: "none", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Skip
              </button>
            </div>
          </div>
        );
      })()}
      {showPrivacyPolicy && <PrivacyPolicyModal onClose={() => setShowPrivacyPolicy(false)} />}

      {/* Curating modal */}
      {showCuratingModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(0,0,0,0.85)", WebkitBackdropFilter: "blur(12px)", backdropFilter: "blur(12px)" }}>
          <div style={{ background: darkMode ? "rgba(13,21,33,0.98)" : "#ffffff", border: `1px solid rgba(27,58,92,0.3)`, borderRadius: 24, padding: "40px 32px", maxWidth: 420, width: "100%", textAlign: "center", animation: "fadeIn 0.4s ease" }}>
            <div style={{ fontSize: 56, marginBottom: 16, animation: "pulse 2s ease-in-out infinite" }}>✈️</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: teal, marginBottom: 12 }}>We're Curating Your Trip!</h2>
            <p style={{ fontSize: 14, color: textMuted, lineHeight: 1.7, marginBottom: 20 }}>
              We're personally sourcing the best flights, accommodation, and experiences for your trip. You'll receive a confirmation email with your full booking details shortly.
            </p>
            <div style={{ background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: textMuted, marginBottom: 4 }}>What happens next</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  "✓ Your payment has been processed",
                  "✓ Our team is sourcing your trip",
                  "📧 Confirmation email on the way",
                  "📱 Your trip will appear in My Account",
                ].map(s => (
                  <div key={s} style={{ fontSize: 13, color: s.startsWith("✓") ? "#6dbd6d" : textPrimary, textAlign: "left" }}>{s}</div>
                ))}
              </div>
            </div>
            <button onClick={() => setShowCuratingModal(false)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${teal},#0F2747)`, color: "#FAFAF5", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
              Got It — I'm Excited! 🎉
            </button>
            <p style={{ fontSize: 11, color: textDim, marginTop: 12 }}>Questions? Email support@justgoanywhere.com</p>
          </div>
        </div>
      )}
      {showCustomerService && <CustomerServiceModal onClose={() => setShowCustomerService(false)} />}
      {showCancellationPolicy && <CancellationPolicyModal onClose={() => setShowCancellationPolicy(false)} />}
      {showTermsOfService && <TermsOfServiceModal onClose={() => setShowTermsOfService(false)} />}
      {showAffiliateDisclosure && <AffiliateDisclosureModal onClose={() => setShowAffiliateDisclosure(false)} />}
      {showSurvey && surveyTrip && <TripSurveyModal trip={surveyTrip} onClose={() => { setShowSurvey(false); setCompletedSurveys(s => [...s, surveyTrip.code]); setUser(u => ({ ...u, pendingSurvey: null })); setAccountDefaultTab("trips"); setMode("account"); }} />}

      <a href="#main-content" style={{ position: "absolute", top: -100, left: 0, background: teal, color: "#FAFAF5", padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, zIndex: 9999, textDecoration: "none" }}
        onFocus={e => e.currentTarget.style.top = "8px"}
        onBlur={e => e.currentTarget.style.top = "-100px"}>
        Skip to main content
      </a>
      <main id="main-content" style={{ position: "relative", zIndex: 1, maxWidth: 680, margin: "0 auto", padding: "28px 20px 100px", color: textPrimary, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

        {/* ── TOP NAV V3 (mockup-accurate) ── */}
        <style>{`
          @keyframes jgaWordmarkShimmer {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          /* BASE — universal, always-renders plain maroon. No gradients,
             no animations, no background-clip. This is the guaranteed-
             visible state on every browser including Safari. */
          .jga-title {
            font-size: 1.45rem;
            font-weight: 900;
            margin: 0;
            line-height: 1;
            letter-spacing: -0.5px;
            white-space: nowrap;
            color: #1B3A5C;
          }
          .jga-title-dark {
            color: #4A8CC4;
          }
          /* PROGRESSIVE ENHANCEMENT — only browsers that are NOT Safari get
             the shimmer gradient. @supports not (-webkit-hyphens: none)
             matches every browser except Safari/WebKit. This way the
             default state (which Safari uses) is the simple, guaranteed-
             visible one, and gradient is purely additive. */
          @supports not (-webkit-hyphens: none) {
            .jga-title {
              background: linear-gradient(90deg, #5c1f1a 0%, #1B3A5C 25%, #4A8CC4 50%, #1B3A5C 75%, #5c1f1a 100%);
              background-size: 200% 200%;
              background-position: 0% 50%;
              -webkit-background-clip: text;
              background-clip: text;
              -webkit-text-fill-color: transparent;
              will-change: background-position;
              animation: jgaWordmarkShimmer 6s ease-in-out infinite;
            }
            .jga-title-dark {
              background: linear-gradient(90deg, #4A8CC4 0%, #4A8CC4 25%, #FAFAF5 50%, #4A8CC4 75%, #4A8CC4 100%);
              background-size: 200% 200%;
              -webkit-background-clip: text;
              background-clip: text;
              -webkit-text-fill-color: transparent;
            }
          }
          /* (Legacy .jga-title-dark / .jga-title-light rules removed — they
             applied background-image + filter unconditionally, which broke
             the Safari fallback. Variants are now handled inside the
             progressive-enhancement @supports block above.) */
          .jga-tab-btn {
            display: flex; flex-direction: column; align-items: center;
            gap: 4px; padding: 8px 4px 0; border: none; background: transparent;
            cursor: pointer; font-family: inherit; flex: 1; position: relative;
            transition: all 0.18s ease;
          }
          .jga-tab-btn:hover { transform: translateY(-1px); }
          .jga-tab-icon { font-size: 22px; line-height: 1; display: flex; align-items: center; justify-content: center; }
          .jga-tab-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.5px; padding-bottom: 9px; text-transform: uppercase; text-align: center; line-height: 1.3; }
          .jga-tab-underline {
            position: absolute; bottom: 0; left: 10%; right: 10%;
            height: 3px; border-radius: 3px 3px 0 0;
          }
          .jga-tab-icon-wrap {
            width: 44px; height: 44px; border-radius: 13px;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.18s ease;
          }
          /* TOP NAV TABS — Home / Journal / Trips / Boards / Explore / Account.
             Defaults mirror the old inline values; media queries shrink them
             on mobile. Static dimensions live here; dynamic styles (color,
             glow, transform) stay inline. */
          .jga-topnav-tab {
            padding: 10px 0 8px;
            gap: 4px;
          }
          .jga-topnav-icon-wrap {
            width: 44px; height: 44px; border-radius: 13px;
          }
          .jga-topnav-label {
            font-size: 12px;
            letter-spacing: 0.8px;
          }

          /* Mobile-responsive top nav */
          @media (max-width: 720px) {
            .jga-title { font-size: 1rem; letter-spacing: -0.3px; }
            .jga-nav-pill-name { display: none; }
            .jga-nav-identity-text { display: none; }
            .jga-nav-signout-text { font-size: 10px !important; }
            /* Tighter tab spacing on tablet/large phones */
            .jga-tab-btn { padding: 6px 2px 0; gap: 2px; }
            .jga-tab-icon-wrap { width: 36px; height: 36px; border-radius: 10px; }
            .jga-tab-icon-wrap svg { width: 18px !important; height: 18px !important; }
            .jga-tab-label { font-size: 9.5px; padding-bottom: 7px; letter-spacing: 0.3px; }
            /* Top nav tab tightening */
            .jga-topnav-tab { padding: 6px 0 5px; gap: 2px; }
            .jga-topnav-icon-wrap { width: 32px; height: 32px; border-radius: 9px; }
            .jga-topnav-icon-wrap svg { width: 18px !important; height: 18px !important; }
            .jga-topnav-label { font-size: 9px; letter-spacing: 0.3px; }
          }
          @media (max-width: 560px) {
            .jga-title { font-size: 0.85rem; }
            .jga-auth-pill { padding: 5px 10px !important; font-size: 11px !important; }
            .jga-auth-pill-beta { padding: 5px 8px !important; }
            /* Even tighter on small phones */
            .jga-tab-btn { padding: 5px 1px 0; }
            .jga-tab-icon-wrap { width: 32px; height: 32px; border-radius: 9px; }
            .jga-tab-icon-wrap svg { width: 16px !important; height: 16px !important; }
            .jga-tab-label { font-size: 9px; padding-bottom: 6px; letter-spacing: 0.2px; }
            /* Top nav even tighter */
            .jga-topnav-tab { padding: 4px 0 3px; gap: 1px; }
            .jga-topnav-icon-wrap { width: 28px; height: 28px; border-radius: 8px; }
            .jga-topnav-icon-wrap svg { width: 16px !important; height: 16px !important; }
            .jga-topnav-label { font-size: 8.5px; letter-spacing: 0.2px; }
          }
          @media (max-width: 480px) {
            /* Keep wordmark visible on Safari mobile per Cas's Day 28 fix —
               previous rule hid it which broke the brand presence on the
               page. Only Beta button gets hidden on tight screens. */
            .jga-auth-pill-beta { display: none; }
            /* Tightest top nav state */
            .jga-topnav-tab { padding: 4px 0 3px; }
            .jga-topnav-icon-wrap { width: 26px; height: 26px; border-radius: 7px; }
            .jga-topnav-icon-wrap svg { width: 14px !important; height: 14px !important; }
            .jga-topnav-label { font-size: 8px; letter-spacing: 0.15px; }
          }
          @media (max-width: 360px) {
            .jga-auth-pill { padding: 5px 8px !important; font-size: 10px !important; }
          }
        `}</style>

        {/* Row 1: hamburger + wordmark + auth + toggle */}
        <div style={{
          position: "sticky", top: 0, zIndex: 50,
          margin: "-28px -20px 0",
          borderBottomLeftRadius: 22,
          borderBottomRightRadius: 22,
          overflow: "hidden",
          WebkitBackdropFilter: "blur(20px)", backdropFilter: "blur(20px)",
          background: darkMode ? "rgba(8,14,28,0.97)" : "linear-gradient(135deg, #2C3E50 0%, #34495E 100%)",
          borderBottom: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(20,32,46,0.5)"}`,
          boxShadow: darkMode ? "0 1px 0 rgba(27,58,92,0.12), 0 4px 32px rgba(0,0,0,0.7)" : "0 2px 20px rgba(20,32,46,0.18)",
        }}>
          {/* Top row */}
          <div className="jga-top-row" style={{ padding: "0 10px", display: "flex", alignItems: "center", gap: 6, height: 52, flexWrap: "nowrap", overflow: "hidden", background: darkMode ? "transparent" : "#FAFAF5", borderBottom: darkMode ? "none" : "1px solid rgba(27,58,92,0.10)" }}>
            {/* Globe home button */}
            <button onClick={reset} title="Home" aria-label="Go to home" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, justifyContent: "center", opacity: 0.9, transition: "opacity 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "1"}
              onMouseLeave={e => e.currentTarget.style.opacity = "0.9"}>
              <svg width="28" height="28" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="22" cy="22" r="19" fill={`${teal}22`} stroke={teal} strokeWidth="2"/>
                <ellipse cx="22" cy="22" rx="9" ry="19" fill={`${teal}10`} stroke={teal} strokeWidth="1.6"/>
                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
              </svg>
            </button>

            {/* Wordmark */}
            <button onClick={reset} className="jga-title-wrap" style={{ background: "none", border: "none", cursor: "pointer", padding: "0 12px 0 0", position: "relative", minWidth: 0, flexShrink: 1 }}>
              <h1 className={`jga-title ${darkMode ? "jga-title-dark" : "jga-title-light"}`} style={{ display: "inline" }}>Just Go Anywhere</h1>
              <span style={{ position: "absolute", top: 0, right: 0, fontSize: 9, color: darkMode ? "#4A8CC4" : teal, lineHeight: 1, fontWeight: 700 }}>TM</span>
            </button>

            <div style={{ flex: 1 }} />

            {/* Auth — only show when logged out */}
            {!registered && (
              <>
                <button onClick={() => setShowLogin(true)} className="jga-auth-pill" style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${darkMode ? "rgba(27,58,92,0.5)" : "rgba(27,58,92,0.6)"}`, background: "transparent", color: teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>Sign In</button>
                <button onClick={() => setShowRegister(true)} className="jga-auth-pill" style={{ padding: "6px 14px", borderRadius: 20, border: "none", background: darkMode ? `linear-gradient(135deg,#f5d78e,#c97832)` : `linear-gradient(135deg,${teal},#0F2747)`, color: darkMode ? "#FAFAF5" : "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>Register</button>
              </>
            )}
            {/* Auth — show pills when logged in.
                Travel Identity pill + Account/name pill hidden pre-launch per
                Cas: identity is accessible via dashboard tile, account via
                bottom-nav Account tab. Flip SHOW_TOP_HEADER_PILLS to true to
                restore both pills. */}
            {registered && (() => {
              const SHOW_TOP_HEADER_PILLS = false;
              return SHOW_TOP_HEADER_PILLS ? (
              <>
                {travelerType && TRAVEL_IDENTITIES[travelerType] ? (() => {
                  const ident = TRAVEL_IDENTITIES[travelerType];
                  const skyEntry = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
                  const accent = skyEntry.accent;
                  // First-word label, with explicit handling for compound first
                  // parts ("Night Owl" → NIGHT OWL, "Off-Grid" → OFF-GRID).
                  const labelForPill = (name) => {
                    const compound = ["Night Owl", "Off-Grid", "Soft-Life"];
                    for (const c of compound) {
                      if (name.startsWith(c)) return c.toUpperCase();
                    }
                    return name.split(" ")[0].toUpperCase();
                  };
                  return (
                    <button onClick={() => setShowIdentityCard(true)} title="View your travel chart"
                      aria-label="View your travel chart"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 6px", borderRadius: 16, border: `1px solid ${ident.color}30`, background: `${ident.color}1F`, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, height: 24, lineHeight: 1 }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${ident.color}33`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${ident.color}1F`; }}>
                      <span style={{ display: "inline-flex", width: 20, height: 14, flexShrink: 0 }}>
                        <ConstellationChart identity={ident} identityName={travelerType} variant="pill" />
                      </span>
                      <span className="jga-nav-identity-text" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11, fontWeight: 700, color: ident.color, letterSpacing: 1 }}>{labelForPill(travelerType)}</span>
                    </button>
                  );
                })() : (
                  <button onClick={() => setShowOnboarding(true)} title="Take the travel identity quiz"
                    aria-label="Set your travel identity"
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 20, border: `1px dashed ${gold}66`, background: `${gold}10`, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                    <span style={{ fontSize: 13 }}>🧭</span>
                    <span className="jga-nav-identity-text" style={{ fontSize: 11, fontWeight: 700, color: goldText }}>Set Identity</span>
                  </button>
                )}
                <button onClick={() => { setAccountDefaultTab("settings"); if (mode !== "account") { reset(); setMode("account"); } }}
                  title="Account settings"
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 20, border: `1px solid ${darkMode ? "rgba(27,58,92,0.4)" : "rgba(27,58,92,0.6)"}`, background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.1)", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                  <span className="jga-nav-pill-name" style={{ fontSize: 12, fontWeight: 700, color: teal }}>{user?.firstName}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="8" r="3.5" stroke={teal} strokeWidth="1.7"/>
                    <path d="M5 20c0-3.5 3.1-6.5 7-6.5s7 3 7 6.5" stroke={teal} strokeWidth="1.7" strokeLinecap="round"/>
                  </svg>
                </button>
                <button onClick={() => setShowWorthKnowing(true)}
                  className="jga-nav-worth-knowing"
                  title="Travel updates & advisories"
                  style={{ background: "none", border: "none", color: textDim, fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: "0 4px", whiteSpace: "nowrap", flexShrink: 0 }}>
                  Worth Knowing
                </button>
                <button onClick={() => {
                  setRegistered(false);
                  setUser(null);
                  setSpinCount(0);
                  // Reset navigation to home so user doesn't land on a
                  // gated surface (Journal/Trips/Boards/Account) that
                  // renders empty when unauthenticated. Added Jun 25, 2026.
                  setMode(null);
                  setBottomTab(null);
                  // Centralized: clear ALL jga-* keys (auth, synced data, anonymous,
                  // legacy) except cosmetic preferences. Keeps the next user / next
                  // login on this browser truly clean.
                  _clearAllJgaState("user-logout");
                  try { sessionStorage.removeItem("jga-quiz-completed-this-session"); } catch {}
                }}
                  className="jga-nav-signout-text"
                  style={{ background: "none", border: "none", color: textDim, fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: "0 4px", whiteSpace: "nowrap", flexShrink: 0 }}>
                  Sign out
                </button>
              </>
              ) : (
                /* Pills hidden — Sign out still visible on its own */
                <button onClick={() => {
                  setRegistered(false);
                  setUser(null);
                  setSpinCount(0);
                  // Reset navigation to home so user doesn't land on a
                  // gated surface that renders empty when unauthenticated.
                  setMode(null);
                  setBottomTab(null);
                  _clearAllJgaState("user-logout");
                  try { sessionStorage.removeItem("jga-quiz-completed-this-session"); } catch {}
                }}
                  className="jga-nav-signout-text"
                  style={{ background: "none", border: "none", color: textDim, fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: "0 4px", whiteSpace: "nowrap", flexShrink: 0 }}>
                  Sign out
                </button>
              );
            })()}
            {/* Theme toggle retired — editorial direction is a single curated aesthetic */}
          </div>

          {/* Tab row — Home / Journal / Trips / Boards / Account.
             Persistent section nav, visible on every screen. Journal sits next
             to Home (Jun 2026) since Journal is where the user is meant to be
             most active — Stamps, Postcards, Trip Entries, and the unified
             Dashboard all live there. */}
          <div style={{ display: "flex", borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)"}` }}>
            {[
              { id: "home", label: "HOME", color: teal, action: () => {
                  reset();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                },
                svg: (active) => (
                  // House icon — roof + body + door. Distinct from the globe at
                  // the top of the header (also a homepage link) and the magnifying
                  // glass that used to be here for EXPLORE (now in bottom nav).
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M3 10 L12 3 L21 10 L21 20 Q21 21 20 21 L4 21 Q3 21 3 20 Z"
                      stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" strokeLinejoin="round"
                      fill={active ? "rgba(253,246,232,0.18)" : "none"}/>
                    <path d="M10 21 L10 14 L14 14 L14 21" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
                  </svg>
                )
              },
              { id: "journal", label: "JOURNAL", color: teal, action: () => {
                  if (registered) {
                    if (mode !== "journal") { reset(); setMode("journal"); }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else setShowLogin(true);
                },
                svg: (active) => {
                  const stroke = active ? "#FAFAF5" : "#c4c8d1";
                  const fill = active ? "rgba(253,246,232,0.18)" : "none";
                  return (
                    // Open book — two facing pages with a binding crease and 3 lines of text on each
                    <svg width="22" height="20" viewBox="0 0 26 22" fill="none">
                      <path d="M2 4 L12 6 L12 19 L2 17 Z" stroke={stroke} strokeWidth="1.6" fill={fill} strokeLinejoin="round"/>
                      <path d="M24 4 L14 6 L14 19 L24 17 Z" stroke={stroke} strokeWidth="1.6" fill={fill} strokeLinejoin="round"/>
                      <path d="M13 6 L13 19" stroke={stroke} strokeWidth="1.2" opacity="0.7"/>
                      {/* Page lines */}
                      <path d="M4.5 9 L10 9.8" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                      <path d="M4.5 11.5 L10 12.3" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                      <path d="M4.5 14 L9 14.7" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                      <path d="M16 9.8 L21.5 9" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                      <path d="M16 12.3 L21.5 11.5" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                      <path d="M17 14.7 L21.5 14" stroke={stroke} strokeWidth="0.9" opacity="0.6"/>
                    </svg>
                  );
                }
              },
              { id: "trips", label: "TRIPS", color: teal, action: () => {
                  if (registered) {
                    if (mode !== "trips") { reset(); setMode("trips"); }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else setShowLogin(true);
                },
                svg: (active) => (
                  <svg width="22" height="18" viewBox="0 0 26 20" fill="none">
                    <g className="jga-trips-icon">
                      <rect x="1" y="3" width="24" height="14" rx="2" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" fill={active ? "rgba(253,246,232,0.18)" : "none"}/>
                      <path d="M1 7 L13 12 L25 7" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.5" fill="none"/>
                      <path d="M7 3 L7 1" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M19 3 L19 1" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" strokeLinecap="round"/>
                    </g>
                  </svg>
                )
              },
              { id: "boards", label: "BOARDS", color: teal, action: () => {
                  if (registered) {
                    if (mode !== "wishlist") { reset(); setMode("wishlist"); }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else setShowLogin(true);
                },
                svg: (active) => {
                  const stroke = active ? "#FAFAF5" : "#c4c8d1";
                  const fill = active ? "rgba(253,246,232,0.18)" : "none";
                  return (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      {/* Corkboard frame — slightly inset rounded square */}
                      <rect x="3" y="4" width="18" height="16" rx="2.4" stroke={stroke} strokeWidth="1.7" fill={fill}/>
                      {/* Two pinned cards, each gently stirring on its own pin */}
                      <rect className="jga-board-card-1" x="6.5" y="7.2" width="6" height="5" rx="0.8" stroke={stroke} strokeWidth="1.3" fill={active ? "rgba(253,246,232,0.22)" : "none"}/>
                      <rect className="jga-board-card-2" x="12" y="11" width="6" height="6" rx="0.8" stroke={stroke} strokeWidth="1.3" fill={active ? "rgba(253,246,232,0.22)" : "none"}/>
                      {/* Pushpin — a defining detail */}
                      <circle cx="9" cy="8.6" r="1.1" fill={stroke} stroke="none"/>
                    </svg>
                  );
                }
              },
              { id: "search", label: "EXPLORE", color: teal, action: () => {
                  if (mode !== "search") { reset(); setMode("search"); }
                  window.scrollTo({ top: 0, behavior: "smooth" });
                },
                svg: (active) => (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <circle cx="10.5" cy="10.5" r="7"
                      stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8"
                      fill={active ? "rgba(253,246,232,0.18)" : "none"}/>
                    <path d="M16 16 L21 21"
                      stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                )
              },
              { id: "profile", label: "ACCOUNT", color: teal, action: () => {
                  if (registered) {
                    if (mode !== "profile") { reset(); setMode("profile"); }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else setShowLogin(true);
                },
                svg: (active) => (
                  <svg width="20" height="22" viewBox="0 0 22 24" fill="none">
                    <circle cx="11" cy="7" r="5" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" fill={active ? "rgba(253,246,232,0.18)" : "none"}/>
                    <path d="M2 22 C2 17 5 14 11 14 C17 14 20 17 20 22" stroke={active ? "#FAFAF5" : "#c4c8d1"} strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                  </svg>
                )
              },
            ].map(item => {
              const isActive = bottomTab === item.id;
              const c = item.color;
              // Cream-unified active state. Previously the active tab had a
              // maroon glow + maroon underline while text was cream — clash.
              // Now glow, border, and underline all read cream against the
              // slate background, with the icon picking up cream too.
              const glowBg = darkMode ? "rgba(27,58,92,0.18)" : "rgba(253,246,232,0.20)";
              const glowShadow = darkMode
                ? "0 0 22px rgba(27,58,92,0.55), 0 0 8px rgba(27,58,92,0.3)"
                : "0 0 22px rgba(253,246,232,0.55), 0 0 8px rgba(253,246,232,0.3)";
              const activeBarColor = darkMode ? c : "#FAFAF5";
              const activeBorderColor = darkMode ? `${c}60` : "rgba(253,246,232,0.6)";
              return (
                <button key={item.id} onClick={() => { item.action(); setBottomTab(item.id); }}
                aria-label={item.label} aria-current={bottomTab === item.id ? "page" : undefined}
                className="jga-topnav-tab"
                style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  border: "none", background: "transparent", cursor: "pointer",
                  fontFamily: "inherit", transition: "all 0.22s",
                  position: "relative",
                  opacity: 1,
                }}>
                  <div className="jga-topnav-icon-wrap" style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: isActive ? glowBg : "transparent",
                    boxShadow: isActive ? glowShadow : "none",
                    border: isActive ? `1.5px solid ${activeBorderColor}` : "1px solid transparent",
                    transition: "all 0.22s",
                    transform: isActive ? "translateY(-2px)" : "translateY(0)",
                  }}>
                    {item.svg(isActive)}
                  </div>
                  <span className="jga-topnav-label" style={{ fontWeight: isActive ? 800 : 600, color: isActive ? (darkMode ? c : "#FAFAF5") : (darkMode ? "#4A6B8A" : "#c4c8d1"), transition: "all 0.22s" }}>
                    {item.label}
                  </span>
                  {isActive && <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: 28, height: 3, borderRadius: "3px 3px 0 0", background: activeBarColor, boxShadow: darkMode ? `0 0 8px ${c}80` : "0 0 8px rgba(253,246,232,0.6)" }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-nav chip row (Escape Mode / Mood Match / Detour) removed.
           Dashboard sections are findable by scrolling — editorial restraint over
           navigational utility. Sticky-anchor jump-to-section utility kept its
           own quiet death. */}


        {/* Spacer below sticky nav */}
        <div style={{ height: 12 }} />

        {/* ── "Almost there" banner ──
           Shows on homepage for unregistered-but-onboarded users. They took
           the quiz, saw their identity, and clicked Continue browsing instead
           of registering. This banner is their persistent path back to the
           register flow — without it, they'd have to remember to tap Sign Up
           in the top nav (and many won't). Sun-gold tint = warm invitation,
           not nag. Dismissible per session. */}
        {/* The banner now requires user to have done something concrete that
           gives them context for WHY they'd want to sign up — taken the quiz,
           spun TMA, or saved a destination. Cold-arrival visitors don't see
           it (the new 4-zone unregistered home does soft conversion itself). */}
        {showAlmostThereBanner && !registered && !mode && (travelerType || spinCount > 0 || starredAlerts.length > 0) && (
          <div style={{
            marginBottom: 14, padding: "12px 14px", borderRadius: 14,
            background: "#FAFAF5",
            border: "0.5px solid rgba(245,215,138,0.55)",
            display: "flex", alignItems: "center", gap: 12,
            animation: "idleFade 0.4s ease",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.3 }}>
                Almost there{user?.firstName ? `, ${user.firstName}` : ""} — save what you've built.
              </div>
              <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 4, lineHeight: 1.4 }}>
                30 seconds. Then it's yours forever.
              </div>
              {/* "Already have an account?" moved to its own line for mobile
                 layout clarity (Jun 25, 2026 per Cas) — the inline placement
                 caused awkward wrapping on "Then it's yours forever." */}
              <div style={{ marginTop: 6 }}>
                <button onClick={() => { setShowLogin(true); }}
                  style={{ background: "none", border: "none", color: "#1B3A5C", fontWeight: 700, textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: 11, fontFamily: "inherit" }}>
                  Already have an account?
                </button>
              </div>
            </div>
            <button onClick={() => {
                // NOTE: previously this referenced quizLeadEmail which lives
                // in OnboardingModal scope — referencing it here threw a
                // ReferenceError when clicked, silently breaking the CTA.
                // prefillRegisterEmail is set elsewhere (URL ?email=, etc)
                // when relevant. Fix Jun 25, 2026.
                setShowRegister(true);
              }}
              style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0, boxShadow: "0 2px 8px rgba(27,58,92,0.18)" }}>
              Sign Up →
            </button>
            <button onClick={() => {
                setShowAlmostThereBanner(false);
                try { sessionStorage.setItem("jga-almost-there-dismissed", "true"); } catch {}
              }}
              aria-label="Dismiss reminder"
              style={{ background: "transparent", border: "none", color: "#4A6B8A", fontSize: 18, cursor: "pointer", padding: "4px 6px", fontFamily: "inherit", lineHeight: 1, flexShrink: 0 }}>
              ×
            </button>
          </div>
        )}

        {/* ── Verify email banner ──
           Shown for registered users whose email isn't verified yet.
           Same shape as Almost There. Dismissible per session — but
           comes back next session until they actually verify. */}
        {isUnverified && !mode && (() => {
          let dismissed = false;
          try { dismissed = sessionStorage.getItem("jga-verify-banner-dismissed") === "true"; } catch (e) {}
          if (dismissed) return null;
          const statusLabel = resendStatus === "sending" ? "Sending…"
            : resendStatus === "sent" ? "Email sent — check your inbox"
            : resendStatus === "rate-limited" ? "Try again in a few minutes"
            : resendStatus === "error" ? "Couldn't send — try again"
            : null;
          return (
            <div style={{
              marginBottom: 14, padding: "12px 14px", borderRadius: 14,
              background: "#FAFAF5",
              border: "0.5px solid rgba(232,154,60,0.55)",
              display: "flex", alignItems: "center", gap: 12,
              animation: "idleFade 0.4s ease",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.3 }}>
                  Verify your email to save your collection.
                </div>
                <div style={{ fontSize: 11, color: "#4A6B8A", marginTop: 2, lineHeight: 1.4 }}>
                  {statusLabel || <>We sent a link to <strong>{user?.email || "your email"}</strong>. Tap it to verify, or resend.</>}
                </div>
              </div>
              <button
                onClick={resendVerificationEmail}
                disabled={resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited"}
                style={{
                  padding: "8px 14px", borderRadius: 10, border: "none",
                  background: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited")
                    ? "rgba(27,58,92,0.12)"
                    : "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                  color: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited") ? "#4A6B8A" : "#FAFAF5",
                  fontSize: 11, fontWeight: 800,
                  cursor: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited") ? "not-allowed" : "pointer",
                  fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0,
                  boxShadow: (resendStatus === "sending" || resendStatus === "sent" || resendStatus === "rate-limited") ? "none" : "0 2px 8px rgba(27,58,92,0.18)",
                }}>
                {resendStatus === "sent" ? "Sent ✓" : "Resend"}
              </button>
              <button onClick={() => {
                  try { sessionStorage.setItem("jga-verify-banner-dismissed", "true"); } catch {}
                  // Force re-render — the dismissal is read on next render
                  setHomeRefreshTick(t => t + 1);
                }}
                aria-label="Dismiss reminder"
                style={{ background: "transparent", border: "none", color: "#4A6B8A", fontSize: 18, cursor: "pointer", padding: "4px 6px", fontFamily: "inherit", lineHeight: 1, flexShrink: 0 }}>
                ×
              </button>
            </div>
          );
        })()}

        {/* ── Idle nudge banner ── */}
        {showIdleBanner && !mode && (
          <div style={{ marginBottom: 18, borderRadius: 16, overflow: "hidden", position: "relative", cursor: "pointer" }}
            onClick={() => { setShowIdleBanner(false); setMode("explore"); }}>
            <style>{`
              @keyframes waveShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
              @keyframes waveBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
              @keyframes idleFade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
            `}</style>
            <div style={{ animation: "waveShift 4s ease infinite, idleFade 0.5s ease", backgroundSize: "300% 300%", background: darkMode ? `linear-gradient(135deg, #0d2a1a, #1a3a0d, #0a1a2e, #1a0a2e, #2a0d1a, #0d2a2a)` : `linear-gradient(135deg, #e8f4f0, #f0f4e8, #e8eef8, #f0e8f4, #f4e8ee, #e8f4f4)`, padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              {/* Wavy SVG decoration */}
              <div style={{ position: "absolute", inset: 0, opacity: darkMode ? 0.18 : 0.3, pointerEvents: "none", overflow: "hidden" }}>
                <svg width="100%" height="100%" viewBox="0 0 600 80" preserveAspectRatio="none">
                  <path d="M0 40 Q75 10 150 40 Q225 70 300 40 Q375 10 450 40 Q525 70 600 40 L600 80 L0 80 Z" fill="#1B3A5C"/>
                  <path d="M0 50 Q75 20 150 50 Q225 80 300 50 Q375 20 450 50 Q525 80 600 50 L600 80 L0 80 Z" fill="#4A8CC4" opacity="0.5"/>
                </svg>
              </div>
              <div style={{ animation: "waveBob 2.5s ease-in-out infinite", position: "relative", zIndex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: textPrimary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>
                  Stop Deciding. Start Going.
                </div>
                <div style={{ fontSize: 12, color: textMuted }}>
                  The world is waiting — pick a destination or let us surprise you.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0, position: "relative", zIndex: 1 }}>
                <button onClick={e => { e.stopPropagation(); setShowIdleBanner(false); setMode("explore"); }} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: darkMode ? `linear-gradient(135deg,${gold},${amber})` : `linear-gradient(135deg,${teal},#0F2747)`, color: darkMode ? "#FAFAF5" : "#ffffff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                  Let's Go →
                </button>
                <button onClick={e => { e.stopPropagation(); setShowIdleBanner(false); }} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${borderColor}`, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ACCOUNT PAGE & DEDICATED FULL-PAGE TABS ── */}
        {/* Profile/Trips/Journal/Identity each get their own mode but share AccountModal as render shell */}
        {(mode === "account" || mode === "profile" || mode === "trips" || mode === "identity" || mode === "wishlist" || mode === "journal") && user && (() => {
          // Map mode → AccountModal tab
          const modeTabMap = {
            "account": accountDefaultTab,
            "profile": "profile",
            "trips": "trips",
            "identity": "identity",
            "wishlist": "watchlist",
            "journal": "journal",
          };
          const resolvedTab = modeTabMap[mode] || "profile";
          return (
            <AccountModal user={user} starredAlerts={starredAlerts} setStarredAlerts={setStarredAlerts}
              boards={boards} setBoards={setBoards} boardsApi={boardsApi} onOpenBoardPicker={(name) => setBoardPickerDest(name)}
              tripsApi={tripsApi} journalApi={journalApi}
              userMood={userMood}
              defaultProfileSubTab={accountProfileSubTab}
              defaultJournalSubTab={accountJournalSubTab}
              tripAlerts={tripAlerts} setTripAlerts={setTripAlerts}
              onOpenAlertModal={(dest) => setShowAlertModal(dest)}
              onOpenStampsSeeding={(state) => setStampsSeedingState(state || "visited")}
              onOpenPostcardsSeeding={(state) => setPostcardsSeedingState(state || "dreaming")}
              journalRefreshTick={journalRefreshTick}
              onClose={() => { setMode(null); setBottomTab(null); }}
              onUpdate={async (u) => {
                // Optimistic local update first
                setUser(u);
                // Persist to backend
                try {
                  const API = (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "https://api.justgoanywhere.com";
                  let token = null;
                  try { token = localStorage.getItem("jga_token"); } catch {}
                  if (!token) {
                    console.warn("[JGA Profile] No auth token — local update only");
                    return { ok: false, reason: "no_token" };
                  }
                  // Send only patchable fields (exclude photoURL which can be large; that gets its own endpoint).
                  // Passport fields are sensitive and routed through encryption on the backend. We only include
                  // them when the user has actually entered something — sending empty passport strings on every
                  // save (e.g., when user just edits hotel programs) was causing the backend to reject the entire
                  // save with "Passport storage is not configured" because it tries to encrypt empty strings.
                  const payload = {
                    firstName: u.firstName,
                    lastName: u.lastName,
                    dob: u.dob,
                    address: u.address,
                    ageRange: u.ageRange,
                    ktn: u.ktn,
                    departureAirport: u.departureAirport,
                    avoidDestinations: u.avoidDestinations,
                    avoidAirlines: u.avoidAirlines,
                    avoidHotels: u.avoidHotels,
                    ffPrograms: u.ffPrograms,
                    hotelPrograms: u.hotelPrograms,
                    visibleAirlines: u.visibleAirlines,
                    visibleHotels: u.visibleHotels,
                    budgetPref: u.budgetPref,
                    vibes: u.vibes,
                  };
                  // Only include passport fields if user has actually filled them in.
                  // The original passport on the server stays intact since we're not sending
                  // these keys at all (PATCH semantics: missing key = leave alone).
                  if (u.passportNumber && u.passportNumber.trim()) {
                    payload.passportNumber = u.passportNumber;
                    payload.passportCountry = u.passportCountry || "";
                    payload.passportExpiry = u.passportExpiry || "";
                  }
                  const res = await fetch(`${API}/api/users/profile`, {
                    method: "PATCH",
                    headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Bearer ${token}`,
                    },
                    // credentials removed for Safari ITP compatibility (we use Bearer tokens, not cookies)
                    body: JSON.stringify(payload),
                  });
                  if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    console.error("[JGA Profile] Save failed", res.status, body);
                    return { ok: false, status: res.status, error: body?.error || `HTTP ${res.status}` };
                  }
                  console.log("[JGA Profile] Saved to backend");
                  return { ok: true };
                } catch (err) {
                  console.error("[JGA Profile] Network error", err);
                  return { ok: false, error: "Network error" };
                }
              }}
              defaultTab={resolvedTab}
              standalone={mode !== "account"}
              savedItineraries={savedItineraries}
              onDeleteItinerary={deleteItineraryFlow}
              travelerType={travelerType}
              onRetakeQuiz={() => { setMode(null); setBottomTab(null); setOnboardingRetake(true); setShowOnboarding(true); }}
              onOpenIdentityCard={() => setShowIdentityCard(true)}
              onIdentityUpdate={(id) => { setTravelerType(id); setExpVibes(TRAVEL_IDENTITIES[id]?.vibes || []); setBlindVibes(TRAVEL_IDENTITIES[id]?.vibes || []); }}
              onNavigate={(targetMode) => { reset(); setMode(targetMode); setBottomTab(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onBookDest={(dest, accent, tripInfo) => {
                // Defensively resolve string dest names to the full DESTINATIONS
                // object. Saved itineraries store `dest` as a string (the
                // destination name), but QuickBookModal / BookingModal expect
                // an object with .name / .country / .tags / .region. Without
                // this, BookingModal's `confirmCode = TMA-${dest.name.slice(0,3)...}`
                // crashes with "undefined is not an object (evaluating
                // 't.name.slice')". Centralizing the resolution here means
                // every entry point gets the right shape — Boards, saved
                // itineraries, Mood Match, anywhere.
                const resolved = typeof dest === "string"
                  ? DESTINATIONS.find(d => d.name === dest)
                  : dest;
                if (!resolved || !resolved.name) {
                  console.warn("[JGA] onBookDest received unresolvable dest:", dest);
                  return;
                }
                setQuickBookDest({ dest: resolved, accent: accent || "#1B3A5C", label: "From your board", tripInfo: tripInfo || null });
              }}
              pastTrips={pastTrips} setPastTrips={setPastTrips}
              journalEntries={journalEntries} setJournalEntries={setJournalEntries}
              journalEditing={journalEditing} setJournalEditing={setJournalEditing}
              journalDraft={journalDraft} setJournalDraft={setJournalDraft}
              journalPhotos={journalPhotos} setJournalPhotos={setJournalPhotos}
              journalRatings={journalRatings} setJournalRatings={setJournalRatings}
              journalMeta={journalMeta} setJournalMeta={setJournalMeta}
              journalRatingDraft={journalRatingDraft} setJournalRatingDraft={setJournalRatingDraft}
              journalMetaDraft={journalMetaDraft} setJournalMetaDraft={setJournalMetaDraft}
            />
          );
        })()}

        {/* ── TRENDING TAB ── */}
        {mode === "trending" && (() => {
          const TRENDING = [
            { rank: 1, name: "Bangkok", country: "Thailand", emoji: "🇹🇭", visitors: "22M+", tagline: "#1 most visited city worldwide" },
            { rank: 2, name: "Paris", country: "France", emoji: "🇫🇷", visitors: "19M+", tagline: "Most iconic city in Europe" },
            { rank: 3, name: "Dubai", country: "UAE", emoji: "🇦🇪", visitors: "16M+", tagline: "Fastest growing destination" },
            { rank: 4, name: "Tokyo", country: "Japan", emoji: "🇯🇵", visitors: "15M+", tagline: "Top-rated city in Asia" },
            { rank: 5, name: "New York City", country: "USA", emoji: "🇺🇸", visitors: "14M+", tagline: "Most visited US city" },
            { rank: 6, name: "Barcelona", country: "Spain", emoji: "🇪🇸", visitors: "12M+", tagline: "Top beach & culture combo" },
            { rank: 7, name: "Marrakech", country: "Morocco", emoji: "🇲🇦", visitors: "11M+", tagline: "Africa's most visited destination" },
            { rank: 8, name: "Cape Town", country: "South Africa", emoji: "🇿🇦", visitors: "10M+", tagline: "World's most scenic city" },
            { rank: 9, name: "Bali", country: "Indonesia", emoji: "🇮🇩", visitors: "9M+", tagline: "Most instagrammed island" },
            { rank: 10, name: "Rome", country: "Italy", emoji: "🇮🇹", visitors: "8M+", tagline: "Eternal city, eternal appeal" },
          ];

          // Helper: estimate when a destination might fit user's budget.
          // Uses real trip cost (flight + 5-night hotel + on-ground floor)
          // not just dest.minBudget — earlier version compared budget to
          // on-ground costs only and ignored the flight, which caused
          // "within your budget" to fire for trips that actually cost
          // 2x the user's budget once the flight was included.
          function forecastAffordability(destName, userBudget, originAirport) {
            const d = DESTINATIONS.find(x => x.name === destName);
            if (!d || !userBudget) return null;
            const realCost = getBaselineTripCost(d, originAirport, 5);
            // Already affordable now
            if (userBudget >= realCost) return { now: true, realCost };
            // Seasonal discount estimates by region — typical % off in shoulder/low season.
            // Applied to the full trip cost (flight + hotel + ground) since seasonal
            // pricing affects both flights and hotels in tandem.
            const region = d.subRegion || d.region || "";
            const lowSeason = (() => {
              if (region.includes("Europe")) return { months: ["Jan","Feb","Nov"], discount: 0.25 };
              if (region.includes("Caribbean")) return { months: ["May","Sep","Oct"], discount: 0.30 };
              if (region.includes("Asia") && region.includes("Southeast")) return { months: ["May","Jun","Sep"], discount: 0.20 };
              if (region.includes("Asia")) return { months: ["May","Jun"], discount: 0.18 };
              if (region.includes("Africa")) return { months: ["Apr","May","Oct","Nov"], discount: 0.20 };
              if (region.includes("North America")) return { months: ["Jan","Feb","Nov"], discount: 0.22 };
              if (region.includes("Oceania")) return { months: ["May","Jun","Aug"], discount: 0.25 };
              return { months: ["Apr","Sep","Oct"], discount: 0.20 };
            })();
            const discountedCost = Math.round(realCost * (1 - lowSeason.discount));
            if (userBudget >= discountedCost) {
              return { now: false, months: lowSeason.months, discountedMin: discountedCost, realCost };
            }
            return { now: false, gap: realCost - userBudget, neverInRange: true, realCost };
          }

          // Get user's effective budget for forecasting (from any input)
          const userBudgetForForecast = resolveUserBudget().value;
          // User's home airport for distance-based flight estimates
          const airportForForecast = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
          // Build alert summary: which alerts are matched right now?
          const alertList = Object.entries(tripAlerts).map(([name, alert]) => {
            const dest = DESTINATIONS.find(d => d.name === name);
            if (!dest) return null;
            const types = alert.types || [];
            // Budget match: alert fires when alert.budget >= real estimated
            // trip cost (flight + hotel + ground). Previously compared only
            // to dest.minBudget which ignored flight cost — caused $1,000
            // alerts to "match" Paris even though real trip is $2,090.
            const realCost = getBaselineTripCost(dest, airportForForecast, 5);
            const budgetMatch = types.includes("budget") && alert.budget >= realCost;
            // Season match: any alert month within next 3 months
            const now = new Date();
            const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const next3 = [now.getMonth(), (now.getMonth()+1)%12, (now.getMonth()+2)%12].map(m => M[m]);
            const seasonMatch = types.includes("season") && (alert.months || []).some(m => next3.includes(m));
            return { name, dest, alert, matched: budgetMatch || seasonMatch, budgetMatch, seasonMatch, realCost };
          }).filter(Boolean);
          const matched = alertList.filter(a => a.matched);

          return (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              {/* Hero header — left-aligned with animated icon on the right */}
              <div style={{ marginBottom: 26 }}>
                {/* Trending Trips animations: outer circle pulses (1.8s);
                   trend line "draws on" using stroke-dasharray (3.5s loop —
                   feels like a chart updating in real time). Honors
                   prefers-reduced-motion. */}
                <style>{`
                  @keyframes jga-trend-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: 0.92; } }
                  @keyframes jga-trend-draw { 0% { stroke-dashoffset: 60; } 70% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; } }
                  .jga-trend-pulse { animation: jga-trend-pulse 1.8s ease-in-out infinite; transform-origin: 22px 22px; transform-box: fill-box; }
                  .jga-trend-line { stroke-dasharray: 60; stroke-dashoffset: 60; animation: jga-trend-draw 3.5s ease-out infinite; }
                  @media (prefers-reduced-motion: reduce) {
                    .jga-trend-pulse, .jga-trend-line { animation: none; stroke-dashoffset: 0; }
                  }
                `}</style>
                <button onClick={() => { reset(); setMode("search"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "4px 0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  ← Back to Explore
                </button>
                <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>where the world is going right now</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: teal, letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase", flex: 1, minWidth: 0 }}>Trending Trips</h2>
                  <svg width="72" height="72" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
                    <circle className="jga-trend-pulse" cx="22" cy="22" r="19" fill="rgba(27,58,92,0.15)" stroke={teal} strokeWidth="1.8"/>
                    <polyline className="jga-trend-line" points="9,32 17,22 23,27 35,12" fill="none" stroke={teal} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path className="jga-trend-line" d="M30,9 L35,12 L31,16" fill="none" stroke={teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9,34 L35,34" stroke={teal} strokeWidth="1.2" strokeLinecap="round" opacity="0.35"/>
                  </svg>
                </div>
              </div>

              {/* Trip Alerts Summary — logged-in users with alerts.
                 Background was previously teal-tinted (`${teal}08`) which
                 read as tan against the cream page. Switched to mauve for
                 stronger visual identity — alerts feel like notifications
                 not background chrome. */}
              {registered && alertList.length > 0 && (
                <div style={{ marginBottom: 22, padding: "16px 18px", borderRadius: 16, border: "1px solid rgba(74,140,196,0.45)", background: "linear-gradient(135deg, rgba(74,140,196,0.18) 0%, rgba(74,140,196,0.08) 100%)", boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset, 0 2px 8px rgba(27,58,92,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, letterSpacing: 2, color: "#1B3A5C", textTransform: "uppercase", fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                      Your Trip Alerts
                    </div>
                    <div style={{ fontSize: 12, color: textMuted, fontWeight: 600 }}>{alertList.length} active</div>
                  </div>

                  {/* Matched (celebration) banners */}
                  {matched.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {matched.slice(0, 2).map(m => (
                        <div key={m.name} style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(74,140,196,0.18)", border: "1px solid rgba(74,140,196,0.55)", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 22 }}>{m.dest.emoji}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {/* SVG sparkle — replaces ✨ emoji so visual
                                 weight matches our other inline icons */}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                                <path d="M12 2 L13.5 9 L20.5 10.5 L13.5 12 L12 19 L10.5 12 L3.5 10.5 L10.5 9 Z" fill="#4A8CC4" stroke="#1B3A5C" strokeWidth="0.8" strokeLinejoin="round"/>
                                <circle cx="19" cy="5" r="1.2" fill="#4A8CC4"/>
                                <circle cx="5" cy="19" r="0.9" fill="#4A8CC4"/>
                              </svg>
                              <span>{m.name} matches your alert!</span>
                            </div>
                            <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                              {m.budgetMatch && `Est. ~$${m.realCost.toLocaleString()} · within your $${m.alert.budget.toLocaleString()} alert`}
                              {m.budgetMatch && m.seasonMatch && " · "}
                              {m.seasonMatch && "Best season approaching"}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            {/* Edit alert — smaller ghost button so Book stays the primary action */}
                            <button onClick={() => setShowAlertModal(m.dest)}
                              aria-label={`Edit alert for ${m.name}`}
                              title={`Edit alert for ${m.name}`}
                              style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(27,58,92,0.3)", background: "rgba(27,58,92,0.08)", color: "#1B3A5C", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, transition: "all 0.15s" }}
                              onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.18)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.08)"; }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M16.5 3.5 L20.5 7.5 L8 20 L3 21 L4 16 Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
                                <path d="M14 6 L18 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                            <button onClick={() => {
                            // Auto-detect airport if not set
                            const hasAirport = expDepartureAirport || blindDepartureAirport || user?.departureAirport;
                            if (!hasAirport && navigator.geolocation) {
                              console.log("[JGA Detect] Trip alert booking - auto-detecting airport");
                              navigator.geolocation.getCurrentPosition(pos => {
                                const nearest = getNearestAirport(pos.coords.latitude, pos.coords.longitude);
                                if (nearest) {
                                  console.log("[JGA Detect] Auto-set:", nearest.code);
                                  setExpDepartureAirport(nearest.code);
                                  setBlindDepartureAirport(nearest.code);
                                  try { localStorage.setItem("jga-airport", nearest.code); } catch(e) {}
                                  // Also persist to user profile so it appears in Account section
                                  if (registered && user && !user.departureAirport) {
                                    setUser(prev => prev ? { ...prev, departureAirport: nearest.code } : prev);
                                  }
                                }
                              }, () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
                            }
                            // Open Surprise Me / Book flow with destination prefilled
                            setSelected(m.dest);
                            setBudget(String(m.alert?.budget || m.dest.minBudget || 1500));
                            setExpRegion(m.dest.region || "Anywhere");
                            setExpSub(m.dest.subRegion || null);
                            setExpCountry(m.dest.country || null);
                            setPrefilledDest(m.dest);
                            setShowBooking(true);
                          }}
                            style={{ padding: "7px 14px", borderRadius: 18, border: "none", background: teal, color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M5 8 L5 18 C 5 19 6 20 7 20 L 17 20 C 18 20 19 19 19 18 L 19 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                              <path d="M3 8 L21 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
                              <path d="M9 12 L15 12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
                              <path d="M9 4 L 9 8 M15 4 L 15 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                            Book
                          </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Forecast list — alerts that aren't matched yet */}
                  {alertList.filter(a => !a.matched).length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: textDim, marginBottom: 8, fontWeight: 600 }}>Tracking — we'll notify you when conditions match:</div>
                      {alertList.filter(a => !a.matched).slice(0, 3).map(a => {
                        const fc = forecastAffordability(a.name, a.alert.budget);
                        return (
                          <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", fontSize: 12, color: textMuted }}>
                            <span style={{ fontSize: 18 }}>{a.dest.emoji}</span>
                            <span style={{ flex: 1, color: textPrimary, fontWeight: 600 }}>{a.name}</span>
                            <span style={{ fontSize: 11, color: textDim }}>
                              {fc?.months ? `Likely affordable in ${fc.months.slice(0, 3).join(", ")}` : a.alert.types?.includes("season") ? `Watching ${(a.alert.months || []).slice(0, 3).join(", ")}` : `$${a.alert.budget.toLocaleString()} target`}
                            </span>
                            {/* Edit alert — opens the alert modal in edit mode for this destination */}
                            <button onClick={() => setShowAlertModal(a.dest)}
                              aria-label={`Edit alert for ${a.name}`}
                              title={`Edit alert for ${a.name}`}
                              style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 7, border: "1px solid rgba(27,58,92,0.25)", background: "rgba(27,58,92,0.06)", color: "#1B3A5C", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, transition: "all 0.15s" }}
                              onMouseEnter={e => { e.currentTarget.style.background = "rgba(27,58,92,0.14)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.45)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "rgba(27,58,92,0.06)"; e.currentTarget.style.borderColor = "rgba(27,58,92,0.25)"; }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M16.5 3.5 L20.5 7.5 L8 20 L3 21 L4 16 Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
                                <path d="M14 6 L18 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Non-registered upsell */}
              {!registered && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 12, border: `1px solid ${teal}30`, background: `${teal}08`, marginBottom: 20, gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: teal, marginBottom: 2 }}>🔔 Set trip alerts</div>
                    <div style={{ fontSize: 12, color: textMuted }}>Get notified when prices drop or your budget fits.</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setShowLogin(true)} style={{ padding: "7px 14px", borderRadius: 18, border: `1px solid ${teal}50`, background: "transparent", color: teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Sign In</button>
                    <button onClick={() => setShowRegister(true)} style={{ padding: "7px 14px", borderRadius: 18, border: "none", background: `linear-gradient(135deg,${teal},#1B3A5C)`, color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Sign Up</button>
                  </div>
                </div>
              )}

              {/* Ranked list */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {TRENDING.map((dest, i) => {
                  const fullDest = DESTINATIONS.find(x => x.name === dest.name);
                  const hasAlert = !!tripAlerts[dest.name];
                  const isStarred = starredAlerts.includes(dest.name);
                  const fc = registered && fullDest ? forecastAffordability(dest.name, userBudgetForForecast, airportForForecast) : null;
                  return (
                    <div key={dest.rank} style={{
                      padding: "14px 16px",
                      borderRadius: 12,
                      border: `1px solid rgba(27,58,92,0.18)`,
                      background: darkMode ? "rgba(255,255,255,0.04)" : "#ffffff",
                      transition: "all 0.18s ease",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.4)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.10)" : "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.18)"; e.currentTarget.style.background = darkMode ? "rgba(255,255,255,0.04)" : "#ffffff"; e.currentTarget.style.transform = "translateY(0)"; }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: teal, minWidth: 28, textAlign: "right" }}>#{dest.rank}</span>
                        <span style={{ fontSize: 28 }}>{dest.emoji}</span>
                        <div style={{ flex: 1, cursor: "pointer" }}
                          onClick={() => {
                            if (!registered) { setShowRegister(true); return; }
                            if (!fullDest) return;
                            // Open QuickBookModal directly with the destination. Inherits
                            // dashboard preferences (airport, budget, companion) via app
                            // state. Previously this routed to TMA (setMode('explore')) which
                            // triggered AI itinerary generation — per Cas, the user should
                            // just see the booking modal with their preferences applied.
                            setQuickBookDest({ dest: fullDest, accent: "#1B3A5C", label: "Trending" });
                          }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>{dest.name}</div>
                          <div style={{ fontSize: 12, color: textMuted }}>{dest.country} · {dest.visitors}/yr</div>
                          <div style={{ fontSize: 11, color: textDim, fontStyle: "italic" }}>{dest.tagline}</div>
                          {/* Flight estimate — scaled to traveler count via travelCompanion */}
                          {fullDest && flightPrices[dest.name] && (() => {
                            const _ttCompanionMap = { solo: 1, couple: 2, friends: 3, family: 4 };
                            const ttPax = _ttCompanionMap[travelCompanion] || 1;
                            const est = estimateTripCost(fullDest, flightPrices[dest.name], ttPax);
                            if (!est) return null;
                            return (
                              <div style={{ fontSize: 11, marginTop: 3, fontWeight: 700, color: teal }}>
                                From ${est.from.toLocaleString()} from {expDepartureAirport || blindDepartureAirport}{ttPax > 1 ? ` · ${ttPax} travelers` : ""}
                              </div>
                            );
                          })()}
                          {/* Forecast hint */}
                          {fc && (
                            <div style={{ fontSize: 11, marginTop: 4, fontWeight: 700, color: fc.now ? "#1B3A5C" : fc.months ? coral : textDim }}>
                              {fc.now ? "✓ Within your budget now" : fc.months ? `📅 Best in ${fc.months.slice(0, 3).join(", ")}` : `Budget gap: $${fc.gap?.toLocaleString()}`}
                            </div>
                          )}
                        </div>

                        {/* ⭐ Star + 🔔 Bell — stacked column for consistency with
                              Most Affordable. Both buttons shown to anonymous users;
                              tapping either prompts register/login. */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                          {/* ⭐ Star — opens board picker (postcard section suppressed
                             since intent here is "save to wishlist", not "collect postcard").
                             Anonymous users get the register prompt. */}
                          <button onClick={e => {
                              e.stopPropagation();
                              if (!registered) { setShowRegister(true); return; }
                              setBoardPickerHidePostcard(true);
                              setBoardPickerDest(dest.name);
                            }}
                            aria-label={registered ? (isStarred ? "Manage boards for this destination" : "Save to a board") : "Sign up to save to a board"}
                            title={registered ? undefined : "Sign up free to save destinations to boards"}
                            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${isStarred ? "rgba(74,140,196,0.55)" : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: isStarred ? "rgba(74,140,196,0.12)" : "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={isStarred ? gold : "none"} stroke={isStarred ? gold : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round">
                              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                            </svg>
                          </button>

                          {/* 🔔 Alert button */}
                          <button onClick={e => {
                              e.stopPropagation();
                              if (!registered) { setShowRegister(true); return; }
                              setShowAlertModal(fullDest || { ...dest, minBudget: 800, maxBudget: 2000, region: "Asia" });
                            }}
                            aria-label={hasAlert ? `Edit alert for ${dest.name}` : `Set alert for ${dest.name}`}
                            title={hasAlert ? "Alert active — tap to edit" : "Set price alert"}
                            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${hasAlert ? `${teal}80` : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: hasAlert ? `${teal}20` : "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={hasAlert ? coral : "none"} stroke={hasAlert ? coral : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
                              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── AFFORDABLE TAB ── */}
        {mode === "affordable" && (() => {
          // Budget priority: stable sources first (user.budgetPref, dashboardBudget), then mutable
          // (budget, blindBudget), then default. The global `budget` state can be mutated by booking-flow
          // side effects (e.g. QuickBookModal's onConfirm calls setBudget to a destination's price).
          // Reading from stable sources first keeps Most Affordable's matching set consistent across
          // the booking flow — otherwise Quick Getaways disappears after a card is tapped.
          // MA deliberately prefers the STABLE user.budgetPref over the mutable
          // `budget` state (booking side-effects can mutate `budget`), so it
          // passes budgetPref as the explicit value — short-circuits first,
          // then falls back to the shared cascade if pref is blank.
          // maBudgetOverride takes precedence — it's the MA-local override
          // set by the inline budget pills in the context bar. When null,
          // we fall back to the cascade so dashboard changes still flow in.
          const { value: _resolvedBudget } = resolveUserBudget({ explicit: user?.budgetPref });
          const userBudget = maBudgetOverride || _resolvedBudget;
          const userAirport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;

          // Mid-range hotel cost per night by region — sourced from the
          // module-level HOTEL_NIGHTLY_BY_SUBREGION (shared with Trending
          // tab so "within budget" math is consistent across surfaces).
          const HOTEL_NIGHTLY = HOTEL_NIGHTLY_BY_SUBREGION;

          // Flight cost fallback — sourced from module-level constants.
          // For users with a departure airport set, the distance-based
          // estimateFlightPrice() handles this far more accurately.
          const FLIGHT_FROM_US = FLIGHT_FROM_US_BY_SUBREGION;

          // Generate date windows for this month and next.
          // The user-chosen night count (maTripNights, default 7) drives the
          // standard windows. A 3-day "Weekend getaway" stays as a separate
          // option so short trips remain visible when the chosen length
          // doesn't fit the budget — gives MA a flexible escape hatch.
          //
          // If the user has picked a specific MONTH (maTripMonthOffset !== null),
          // collapse to a single window in that month so the seasonal flight
          // multiplier applies. Otherwise use the rotating default windows.
          const now = new Date();
          const _maNights = maTripNights;
          const DATE_WINDOWS = (maTripMonthOffset !== null) ? (() => {
            const start = new Date(now.getFullYear(), now.getMonth() + maTripMonthOffset, 12); // mid-month
            const monthName = start.toLocaleDateString("en-US", { month: "long" });
            return [
              { label: `${monthName} · ${_maNights} nights`, days: _maNights, start },
            ];
          })() : [
            { label: `This month · ${_maNights} nights`, days: _maNights, start: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7) },
            { label: `Next month · ${_maNights} nights`, days: _maNights, start: new Date(now.getFullYear(), now.getMonth() + 1, 5) },
            { label: "Weekend getaway · 3 nights", days: 3, start: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 4) },
            { label: `Two weeks out · ${_maNights} nights`, days: _maNights, start: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14) },
          ];

          function formatDateRange(start, days) {
            const end = new Date(start); end.setDate(end.getDate() + days);
            const fmt = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            return `${fmt(start)} – ${fmt(end)}`;
          }

          // ── Group size ──
          // Derive pax from MULTIPLE sources to match filterPool's robust
          // resolution: travelCompanion first (dashboard Style cell), then
          // the numeric `travelers` state (any picker that sets it), then 1.
          // Previously this only checked travelCompanion, so a user who set
          // travelers=2 via a numeric picker without picking 'couple' on the
          // dashboard would still see single-traveler MA prices.
          const _maCompanionMap = { solo: 1, couple: 2, friends: 3, family: 4 };
          const maPax = _maCompanionMap[travelCompanion]
                     || (travelers > 1 ? travelers : 0)
                     || 1;
          const maRoomsFor = (days) => Math.ceil(maPax / 2);

          // For each destination, compute estimated total cost
          const matches = DESTINATIONS.map(dest => {
            const hotelNightly = dest.hotelNightlyOverride || HOTEL_NIGHTLY[dest.subRegion] || 110;
            // Fallback flight cost — used when distance-based estimate
            // isn't available (anonymous user, no home airport, or
            // destination coords missing from DEST_COORDS)
            const fallbackFlightCost = FLIGHT_FROM_US[dest.subRegion] || 750;
            // Pick the best date window that fits the budget. We compute
            // flight price PER WINDOW because the distance-based estimator
            // applies seasonal multipliers (peak summer/Dec = +25%, off-peak
            // winter/Nov = -15%) — same destination at different dates can
            // swing $300+ for a long-haul trip.
            const bestWindow = DATE_WINDOWS.map(w => {
              const startISO = w.start.toISOString().split("T")[0];
              const distanceEstimate = userAirport
                ? estimateFlightPrice(userAirport, dest.name, startISO)
                : null;
              const flightPerSeat = distanceEstimate || fallbackFlightCost;
              // Group totals — flight scales by pax (each person needs a seat),
              // hotel scales by rooms (Math.ceil(pax/2)).
              const flightCost = flightPerSeat * maPax;
              const hotelTotal = hotelNightly * w.days * maRoomsFor(w.days);
              const total = flightCost + hotelTotal;
              return { ...w, hotelTotal, flightCost, total };
            }).filter(w => w.total <= userBudget * 0.95)
              .sort((a, b) => b.days - a.days)[0]; // prefer longer trips that fit
            return { dest, bestWindow, cheapestTotal: (bestWindow?.flightCost || (fallbackFlightCost * maPax)) + hotelNightly * 5 * maRoomsFor(5) };
          })
          .filter(m => m.bestWindow) // only show where budget works
          .sort((a, b) => {
            // Sort by best value: most days for the money
            const scoreA = (a.bestWindow.days / a.bestWindow.total) * 1000;
            const scoreB = (b.bestWindow.days / b.bestWindow.total) * 1000;
            return scoreB - scoreA;
          })
          .slice(0, 12);

          return (
            <div style={{ animation: "fadeIn 0.3s ease" }}>

              {/* Hero header — left-aligned with animated icon on the right */}
              <div style={{ marginBottom: 20 }}>
                {/* Most Affordable animations: outer rays rotate slowly (8s);
                   $ pulses with a subtle scale (1.6s ease in/out). Honors
                   prefers-reduced-motion. */}
                <style>{`
                  @keyframes jga-aff-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                  @keyframes jga-aff-pulse { 0%, 100% { transform: scale(1); opacity: 0.95; } 50% { transform: scale(1.18); opacity: 1; } }
                  .jga-aff-rotate { animation: jga-aff-rotate 8s linear infinite; transform-origin: 22px 22px; }
                  .jga-aff-pulse { animation: jga-aff-pulse 1.6s ease-in-out infinite; transform-origin: 22px 22px; transform-box: fill-box; }
                  @media (prefers-reduced-motion: reduce) {
                    .jga-aff-rotate, .jga-aff-pulse { animation: none; }
                  }
                `}</style>
                <button onClick={() => { reset(); setMode("search"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "4px 0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  ← Back to Explore
                </button>
                <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>where your budget flies this month</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: "#1B3A5C", letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase", flex: 1, minWidth: 0 }}>Within Your Budget</h2>
                  <svg width="72" height="72" viewBox="0 0 44 44" fill="none" style={{ display: "block", flexShrink: 0 }}>
                    {/* Outer ring stays anchored. Rays rotate around it. */}
                    <circle cx="22" cy="22" r="19" fill="rgba(27,58,92,0.15)" stroke="#1B3A5C" strokeWidth="1.8"/>
                    <g className="jga-aff-rotate">
                      <circle cx="22" cy="22" r="10" fill="rgba(27,58,92,0.12)" stroke="#1B3A5C" strokeWidth="1.6"/>
                      <path d="M22 3 L22 9" stroke="#1B3A5C" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M22 35 L22 41" stroke="#1B3A5C" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M3 22 L9 22" stroke="#1B3A5C" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M35 22 L41 22" stroke="#1B3A5C" strokeWidth="1.8" strokeLinecap="round"/>
                    </g>
                    {/* $ pulses independently — sits on top of rotating rays */}
                    <text className="jga-aff-pulse" x="22" y="27.5" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1B3A5C" fontFamily="Inter,sans-serif">$</text>
                  </svg>
                </div>
                <div style={{ fontSize: 13, color: textMuted, marginTop: 6 }}>Estimated flight + 3★ hotel · <span style={{ color: "#1B3A5C", fontWeight: 700 }}>live fares at booking</span></div>
              </div>

              {/* ── Quick Getaways — destinations under 2 hours from user's airport AND within budget ──
                 Moved here from the dashboard. The "cheap weekend trip" mental model
                 belongs in budget-pressure mode. Budget-fit is computed per US candidate
                 here (not intersected with the page's main `matches` array, since that's
                 sliced to top-12 and sorted by best-value — US destinations often don't
                 make that cut even though they fit the budget). */}
              {(() => {
                const originAirport = userAirport ? MAJOR_AIRPORTS.find(a => a.code === userAirport) : null;

                if (!originAirport) return null; // Airport-less users — skip silently here (hero handles airport prompt)

                // 2-hour flight ≈ ~1000 miles ground distance
                const QUICK_GETAWAY_MAX_MILES = 1000;
                const cityToAirport = {};
                MAJOR_AIRPORTS.forEach(a => {
                  const city = a.city.toLowerCase();
                  if (!cityToAirport[city]) cityToAirport[city] = { lat: a.lat, lng: a.lng };
                });
                cityToAirport["honolulu"]      = { lat: 21.3245, lng: -157.9251 };
                cityToAirport["nashville"]     = { lat: 36.1245, lng: -86.6782 };
                cityToAirport["austin"]        = { lat: 30.1944, lng: -97.6699 };
                cityToAirport["new orleans"]   = { lat: 29.9934, lng: -90.2580 };
                cityToAirport["las vegas"]     = { lat: 36.0840, lng: -115.1537 };
                cityToAirport["aspen"]         = { lat: 39.2232, lng: -106.8687 };
                cityToAirport["santa fe"]      = { lat: 35.6171, lng: -106.0890 };
                cityToAirport["asheville"]     = { lat: 35.4362, lng: -82.5418 };
                cityToAirport["charleston"]    = { lat: 32.8986, lng: -80.0405 };
                cityToAirport["savannah"]      = { lat: 32.1276, lng: -81.2021 };
                cityToAirport["key west"]      = { lat: 24.5561, lng: -81.7596 };
                cityToAirport["jackson hole"]  = { lat: 43.6073, lng: -110.7377 };
                cityToAirport["sedona"]        = { lat: 34.8697, lng: -111.7610 };

                // Compute budget-fit per US destination using the same formula as the
                // main matches array. A North American 3-day weekend = $180 flight + $360 hotel = $540.
                // So most reasonable budgets ($700+) will easily fit at least one quick getaway.
                const NA_HOTEL_NIGHTLY = HOTEL_NIGHTLY["North America"] || 120;
                const NA_FLIGHT = FLIGHT_FROM_US["North America"] || 180;
                const minWeekendCost = NA_FLIGHT + NA_HOTEL_NIGHTLY * 3; // 3-night weekend baseline
                const fitsBudget = minWeekendCost <= userBudget * 0.95;

                if (!fitsBudget) return null; // Budget genuinely can't accommodate any quick getaway

                const usDestinations = DESTINATIONS.filter(d => d.country === "USA");
                const quickGetaways = usDestinations
                  .map(d => {
                    const destCoords = cityToAirport[d.name.toLowerCase()];
                    if (!destCoords) return null;
                    const miles = distanceMiles(originAirport.lat, originAirport.lng, destCoords.lat, destCoords.lng);
                    return { ...d, miles };
                  })
                  // Filter: 1-2.5 hour flights only (250-1000 miles per the
                  // miles/500 + 0.5 formula in flightTimeLabel). Anything under
                  // 1hr is too local to feel like a "getaway"; anything over
                  // 2.5hr defeats the "quick" framing.
                  .filter(d => d && d.miles > 250 && d.miles <= QUICK_GETAWAY_MAX_MILES)
                  .sort((a, b) => a.miles - b.miles)
                  .slice(0, 4);

                if (quickGetaways.length === 0) return null;

                const flightTimeLabel = (miles) => {
                  const hoursDecimal = miles / 500 + 0.5;
                  const h = Math.floor(hoursDecimal);
                  const m = Math.round((hoursDecimal - h) * 60);
                  if (h === 0) return `${m}m flight`;
                  return m === 0 ? `${h}h flight` : `${h}h ${m}m flight`;
                };

                return (
                  <div style={{ padding: "16px 18px", borderRadius: 14, border: "1px solid rgba(74,140,196,0.55)", background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset, 0 2px 8px rgba(27,58,92,0.04)", marginBottom: 22 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M12 2 L 15 9 L 22 9 L 16 14 L 19 21 L 12 17 L 5 21 L 8 14 L 2 9 L 9 9 Z" stroke="#1B3A5C" strokeWidth="1.7" strokeLinejoin="round" fill="rgba(27,58,92,0.2)"/>
                        </svg>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: textPrimary }}>Quick Getaways</h3>
                      </div>
                      <div style={{ fontSize: 10, color: "#1B3A5C", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 }}>From {userAirport} · Fits budget</div>
                    </div>
                    <p style={{ margin: "6px 0 14px", fontSize: 12, color: textMuted, fontStyle: "italic", lineHeight: 1.5 }}>Close to home, fits your ${userBudget.toLocaleString()} budget — pack light, leave Friday, back Sunday.</p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                      {quickGetaways.map(d => (
                        <button key={d.name}
                          onClick={() => setQuickBookDest({ dest: d, accent: "#1B3A5C", label: "Quick getaway", defaultDateRange: "fri-mon" })}
                          style={{
                            textAlign: "left", padding: "12px 13px", borderRadius: 12,
                            border: `1px solid rgba(27,58,92,0.18)`,
                            background: darkMode ? "rgba(255,255,255,0.04)" : "#ffffff",
                            cursor: "pointer", fontFamily: "inherit",
                            transition: "all 0.18s ease",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.4)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.10)" : "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.18)"; e.currentTarget.style.background = darkMode ? "rgba(255,255,255,0.04)" : "#ffffff"; e.currentTarget.style.transform = "translateY(0)"; }}>
                          <div style={{ fontSize: 22, marginBottom: 4 }}>{d.emoji}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, lineHeight: 1.2, marginBottom: 2 }}>{d.name}</div>
                          <div style={{ fontSize: 10, color: textMuted, marginBottom: 5 }}>{d.state}</div>
                          <div style={{ display: "inline-block", fontSize: 9, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 8, background: "rgba(27,58,92,0.15)", color: "#1B3A5C", fontWeight: 700 }}>
                            {flightTimeLabel(d.miles)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Budget + airport context bar */}
              <div style={{ background: darkMode ? "rgba(27,58,92,0.06)" : "rgba(27,58,92,0.08)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 14, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                      <span>YOUR BUDGET</span>
                      {maBudgetOverride !== null && (
                        <button onClick={() => setMaBudgetOverride(null)}
                          aria-label="Reset to dashboard budget"
                          title="Reset to dashboard budget"
                          style={{ border: "none", background: "transparent", color: "#4A8CC4", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline" }}>
                          ↺ reset
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#1B3A5C", marginBottom: 6 }}>${userBudget.toLocaleString()}</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {[
                        { val: 1000, label: "$1K" },
                        { val: 2000, label: "$2K" },
                        { val: 3000, label: "$3K" },
                        { val: 5000, label: "$5K" },
                        { val: 7500, label: "$7.5K" },
                        { val: 10000, label: "$10K" },
                      ].map(b => {
                        // Active when EITHER the override matches the pill OR
                        // (no override is set and dashboard budget rounds to this pill).
                        // The pill picker is a fast-set tool; the big number above is truth.
                        const active = userBudget === b.val;
                        return (
                          <button key={b.val} onClick={() => setMaBudgetOverride(b.val)}
                            aria-label={`Set MA budget to ${b.label}`}
                            aria-pressed={active}
                            style={{
                              padding: "3px 8px", borderRadius: 8,
                              border: `1px solid ${active ? "rgba(27,58,92,0.9)" : "rgba(27,58,92,0.25)"}`,
                              background: active ? "#1B3A5C" : "transparent",
                              color: active ? "#FAFAF5" : "#1B3A5C",
                              fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit",
                              transition: "all 0.15s ease",
                              minWidth: 36,
                            }}>{b.label}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ borderLeft: `1px solid rgba(27,58,92,0.2)`, paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 2 }}>FLYING FROM</div>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#1B3A5C", letterSpacing: 2 }}>{userAirport || "—"}</div>
                  </div>
                  <div style={{ borderLeft: `1px solid rgba(27,58,92,0.2)`, paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>TRAVELERS</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[1, 2, 3, 4].map(n => {
                        const active = maPax === n;
                        return (
                          <button key={n} onClick={() => {
                            setTravelers(n);
                            // Set default vibe for this count (couple/friends/family).
                            // The fuller TMA / Surprise Me pickers let users
                            // override via the vibe sub-row; this compact dashboard
                            // cell uses the default since there's no room for a
                            // sub-toggle here.
                            const c = getDefaultVibeForCount(n);
                            if (c) setTravelCompanion(c);
                          }}
                            aria-label={`Set to ${n} traveler${n === 1 ? "" : "s"}`}
                            aria-pressed={active}
                            style={{
                              padding: "3px 8px", borderRadius: 8,
                              border: `1px solid ${active ? "rgba(27,58,92,0.9)" : "rgba(27,58,92,0.25)"}`,
                              background: active ? "#1B3A5C" : "transparent",
                              color: active ? "#FAFAF5" : "#1B3A5C",
                              fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit",
                              transition: "all 0.15s ease",
                              minWidth: 26,
                            }}>{n}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ borderLeft: `1px solid rgba(27,58,92,0.2)`, paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>WHEN</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(() => {
                        const pills = [{ key: null, label: "Any" }];
                        const today = new Date();
                        for (let i = 0; i < 6; i++) {
                          const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
                          pills.push({ key: i, label: d.toLocaleDateString("en-US", { month: "short" }) });
                        }
                        return pills.map(p => {
                          const active = maTripMonthOffset === p.key;
                          return (
                            <button key={p.label} onClick={() => setMaTripMonthOffset(p.key)}
                              aria-label={p.key === null ? "Trip month: any" : `Trip month: ${p.label}`}
                              aria-pressed={active}
                              style={{
                                padding: "3px 8px", borderRadius: 8,
                                border: `1px solid ${active ? "rgba(27,58,92,0.9)" : "rgba(27,58,92,0.25)"}`,
                                background: active ? "#1B3A5C" : "transparent",
                                color: active ? "#FAFAF5" : "#1B3A5C",
                                fontSize: 11, fontWeight: 800,
                                cursor: "pointer", fontFamily: "inherit",
                                transition: "all 0.15s ease",
                                minWidth: 30,
                              }}>{p.label}</button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  <div style={{ borderLeft: `1px solid rgba(27,58,92,0.2)`, paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 4 }}>NIGHTS</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[3, 5, 7, 10, 14].map(n => {
                        const active = maTripNights === n;
                        return (
                          <button key={n} onClick={() => setMaTripNights(n)}
                            aria-label={`Set trip length to ${n} nights`}
                            aria-pressed={active}
                            style={{
                              padding: "3px 8px", borderRadius: 8,
                              border: `1px solid ${active ? "rgba(27,58,92,0.9)" : "rgba(27,58,92,0.25)"}`,
                              background: active ? "#1B3A5C" : "transparent",
                              color: active ? "#FAFAF5" : "#1B3A5C",
                              fontSize: 11, fontWeight: 800,
                              cursor: "pointer", fontFamily: "inherit",
                              transition: "all 0.15s ease",
                              minWidth: 26,
                            }}>{n}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => {
                    detectAirport((code) => {
                      setExpDepartureAirport(code);
                      setBlindDepartureAirport(code);
                      // Persist to user profile so it shows in Account section
                      if (registered && user && !user.departureAirport) {
                        setUser(prev => prev ? { ...prev, departureAirport: code } : prev);
                      }
                    });
                  }}
                    style={{ padding: "7px 14px", borderRadius: 20, border: userAirport ? "1px solid rgba(27,58,92,0.4)" : "none", background: userAirport ? "transparent" : "#1B3A5C", color: userAirport ? "#1B3A5C" : "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <svg width="11" height="13" viewBox="0 0 38 48" fill="none" aria-hidden="true">
                      <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="none" stroke={userAirport ? "#1B3A5C" : "#FAFAF5"} strokeWidth="2.8"/>
                      <circle cx="19" cy="18" r="3.5" fill={userAirport ? "#1B3A5C" : "#FAFAF5"}/>
                    </svg>
                    {userAirport ? "Re-detect" : "Detect"}
                  </button>
                </div>
              </div>

              {matches.length === 0 ? (() => {
                // Figure out the cheapest trip available from this airport,
                // so we can suggest a specific budget that would unlock at
                // least one match. Mirrors the matches computation above but
                // without the budget filter — picks the absolute cheapest
                // destination + cheapest date window combination.
                //
                // CRITICAL: applies the same traveler scaling as the main matches
                // block — flight × maPax (each seat) and hotel × Math.ceil(maPax/2)
                // rooms. Without this, the empty state would suggest single-traveler
                // budgets to multi-traveler users (e.g. '$1,700 for Cartagena' to
                // a family of 4, when the actual cost is ~$4,700).
                const cheapestCandidates = DESTINATIONS.map(dest => {
                  const hotelNightly = dest.hotelNightlyOverride || HOTEL_NIGHTLY[dest.subRegion] || 110;
                  const fallbackFlightCost = FLIGHT_FROM_US[dest.subRegion] || 750;
                  const cheapestWindow = DATE_WINDOWS.map(w => {
                    const startISO = w.start.toISOString().split("T")[0];
                    const distanceEstimate = userAirport ? estimateFlightPrice(userAirport, dest.name, startISO) : null;
                    const flightPerSeat = distanceEstimate || fallbackFlightCost;
                    const flightCost = flightPerSeat * maPax;
                    const hotelTotal = hotelNightly * w.days * maRoomsFor(w.days);
                    const total = flightCost + hotelTotal;
                    return { ...w, total };
                  }).sort((a, b) => a.total - b.total)[0];
                  return { dest, total: cheapestWindow.total };
                }).sort((a, b) => a.total - b.total);
                const cheapestMatch = cheapestCandidates[0];
                const suggestedBudget = cheapestMatch ? Math.ceil(cheapestMatch.total / 50) * 50 : null; // round up to next $50
                const bumpedBudget = Math.round(userBudget * 1.25);

                return (
                  <div style={{
                    textAlign: "center",
                    padding: "32px 20px 28px",
                    background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                    borderRadius: 16,
                    border: "1px solid rgba(27,58,92,0.18)",
                  }}>
                    <div style={{
                      fontSize: 10,
                      letterSpacing: 3,
                      color: "#1B3A5C",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      marginBottom: 14,
                    }}>
                      No matches yet
                    </div>
                    <h3 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 900, color: textPrimary, letterSpacing: -0.3 }}>
                      Nothing fits a{" "}
                      <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: "italic", fontWeight: 500, color: "#1B3A5C" }}>
                        ${userBudget.toLocaleString()}
                      </span>
                      {" "}budget
                    </h3>
                    <p style={{ margin: "0 auto 22px", fontSize: 13, color: textMuted, lineHeight: 1.6, maxWidth: 340 }}>
                      {suggestedBudget
                        ? <>The cheapest trip from {userAirport || "your area"} right now is around <strong style={{ color: "#1B3A5C" }}>${suggestedBudget.toLocaleString()}</strong> for {cheapestMatch.dest.name}.</>
                        : <>Try raising your budget to see where you can go.</>}
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "stretch" }}>
                      {suggestedBudget && (
                        <button onClick={() => {
                          // MA-local override only — per Cas, budget changes inside
                          // MA should NOT propagate back to dashboard or user.budgetPref.
                          setMaBudgetOverride(suggestedBudget);
                        }} style={{
                          padding: "13px 20px",
                          borderRadius: 12,
                          border: "none",
                          background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                          color: "#FAFAF5",
                          fontWeight: 800,
                          fontSize: 14,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          boxShadow: "0 4px 14px rgba(27,58,92,0.22)",
                        }}>
                          Set budget to ${suggestedBudget.toLocaleString()} →
                        </button>
                      )}
                      <button onClick={() => {
                        // MA-local override only — see comment above.
                        setMaBudgetOverride(bumpedBudget);
                      }} style={{
                        padding: "11px 20px",
                        borderRadius: 12,
                        border: "1px solid rgba(27,58,92,0.35)",
                        background: "transparent",
                        color: "#1B3A5C",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}>
                        Or bump by 25% → ${bumpedBudget.toLocaleString()}
                      </button>
                      <button onClick={() => { reset(); setMode("explore"); setStep(0); }} style={{
                        background: "none",
                        border: "none",
                        color: textDim,
                        fontSize: 12,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textDecoration: "underline",
                        marginTop: 2,
                      }}>
                        Change budget manually
                      </button>
                    </div>
                  </div>
                );
              })() : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {matches.map(({ dest, bestWindow }, i) => (
                    <button key={dest.name} aria-label={`Explore ${dest.name}`}
                      onClick={() => {
                        const d = DESTINATIONS.find(x => x.name === dest.name);
                        if (d) { setAffordableBookingDest(d); setAffordableBookingBudget(bestWindow.total); }
                      }}
                      style={{ display: "flex", alignItems: "stretch", gap: 0, borderRadius: 16, overflow: "hidden", border: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(27,58,92,0.18)"}`, background: darkMode ? "rgba(27,58,92,0.030)" : "#ffffff", cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.18s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.4)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.10)" : "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.18)" : "rgba(27,58,92,0.18)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.030)" : "#ffffff"; e.currentTarget.style.transform = "translateY(0)"; }}>

                      {/* Rank + emoji column */}
                      <div style={{ width: 60, background: darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.07)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "14px 0", flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: "#1B3A5C" }}>#{i + 1}</span>
                        <span style={{ fontSize: 28 }}>{dest.emoji}</span>
                      </div>

                      {/* Main content */}
                      <div style={{ flex: 1, padding: "12px 14px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{dest.name}</div>
                            <div style={{ fontSize: 11, color: textMuted }}>{dest.country} · {fmtTemp(dest.temp, fahrenheit)}</div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 10 }}>
                            <div style={{ fontSize: 9, color: textDim, letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 700, marginBottom: 1 }}>Est. trip total</div>
                            <div style={{ fontSize: 16, fontWeight: 900, color: "#1B3A5C" }}>~${bestWindow.total.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: textDim }}>{bestWindow.days} nights</div>
                          </div>
                        </div>

                        {/* Date range chip */}
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: "rgba(27,58,92,0.1)", border: "1px solid rgba(27,58,92,0.2)", marginBottom: 8 }}>
                          <span style={{ fontSize: 10 }}>📅</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1B3A5C" }}>{bestWindow.label}</span>
                          <span style={{ fontSize: 10, color: textMuted }}>· {formatDateRange(bestWindow.start, bestWindow.days)}</span>
                        </div>

                        {/* Cost breakdown — ONLY actual trip costs (flight + hotel
                            + everything-else). Spending money moved to its own line
                            below so it can't read as "added to the trip cost". */}
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: bestWindow.total < userBudget ? 6 : 0 }}>
                          <div style={{ fontSize: 11, color: textDim }}>✈️ Flight ~${bestWindow.flightCost.toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: textDim }}>🏨 Hotel ~${bestWindow.hotelTotal.toLocaleString()} · 3★</div>
                        </div>
                        {/* Budget-fit indicator — clearly separated from trip costs,
                            framed as a positive "under budget" signal in success green. */}
                        {bestWindow.total < userBudget && (
                          <div style={{ fontSize: 11, color: "#3a8a3a", fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12l5 5L20 7" stroke="#3a8a3a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            ${(userBudget - bestWindow.total).toLocaleString()} under your ${userBudget.toLocaleString()} budget
                          </div>
                        )}
                      </div>

                      {/* Star + Bell + arrow */}
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "10px 10px", gap: 6, flexShrink: 0 }}>
                        {/* ⭐ Star — board picker for registered; register prompt for anonymous.
                              Same visual either way so anonymous users see the affordance and
                              know what's behind the prompt. */}
                        {(() => {
                          const isStarred = registered && starredAlerts.includes(dest.name);
                          return (
                            <button aria-label={registered ? `${isStarred ? "Manage boards" : "Save"} ${dest.name}` : "Sign up to save to a board"}
                              title={registered ? undefined : "Sign up free to save destinations to boards"}
                              onClick={e => {
                                e.stopPropagation();
                                if (!registered) { setShowRegister(true); return; }
                                setBoardPickerHidePostcard(true);
                                setBoardPickerDest(dest.name);
                              }}
                              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${isStarred ? "rgba(74,140,196,0.55)" : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`, background: isStarred ? "rgba(74,140,196,0.12)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill={isStarred ? "#4A8CC4" : "none"} stroke={isStarred ? "#4A8CC4" : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                              </svg>
                            </button>
                          );
                        })()}

                        {/* 🔔 Alert button — mirrors Trending Trips pattern.
                              Registered users open the alert modal; anonymous
                              users get nudged to register. The hasAlert state
                              comes from tripAlerts which is the canonical
                              source for the user's per-destination alerts. */}
                        {(() => {
                          const hasAlert = !!(tripAlerts && tripAlerts[dest.name]);
                          return (
                            <button onClick={e => {
                                e.stopPropagation();
                                if (!registered) { setShowRegister(true); return; }
                                setShowAlertModal({ ...dest, region: dest.region || "Anywhere" });
                              }}
                              aria-label={hasAlert ? `Edit alert for ${dest.name}` : `Set alert for ${dest.name}`}
                              title={hasAlert ? "Alert active — tap to edit" : "Set price alert"}
                              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${hasAlert ? `${teal}80` : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: hasAlert ? `${teal}20` : "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill={hasAlert ? "#FF6B6B" : "none"} stroke={hasAlert ? "#FF6B6B" : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                              </svg>
                            </button>
                          );
                        })()}

                        <div style={{ fontSize: 16, color: "#1B3A5C" }}>→</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(27,58,92,0.05)", border: "1px solid rgba(27,58,92,0.15)", fontSize: 11, color: textMuted, lineHeight: 1.55 }}>
                <span style={{ fontWeight: 800, color: "#1B3A5C" }}>About these prices · </span>
                Trip totals are estimates — flights modeled from {userAirport ? `${userAirport} `: ""}distance, season, and demand; hotels averaged for 3★ stays in each region. Live fares load when you tap to book, and they can run higher or lower than what's shown here.
              </div>
            </div>
          );
        })()}

        {/* ── HOME SUMMARY ── */}
        {!mode && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20, animation: "fadeIn 0.4s ease" }}>

            {/* ─────────────────────────────────────────────────────── */}
            {/* TRAVEL DASHBOARD — logged-in users only                  */}
            {/* ─────────────────────────────────────────────────────── */}
            {registered && (() => {
              // ════════════════════════════════════════════════════════════
              //   HOME PAGE — 4-ZONE STRUCTURE (Jun 2026 redesign handoff)
              // ════════════════════════════════════════════════════════════
              // Replaces the previous accordion dashboard. Per spec:
              //   Zone 1: Welcome + Invitation Card (decision-tree driven)
              //   Zone 2: You — recent saves grid
              //   Zone 3: Where to next? — TMA + SM + Just Browse
              //   Zone 4: Story — rotating brand line + sub-context
              //
              // States 1, 2, 3, 7 shipped for launch. States 4, 5, 6 (trip-
              // related: recent completed / active / upcoming) deferred to
              // Week 2 per Cas's call — doc carves them out as P1.
              //
              // Note: no Aura ring on home (lives in MoodBoard now), no stats
              // row (in Journal Dashboard), no Worth Knowing accordion (in
              // nav modal), no What is JGA carousel (Zone 4 does this job).
              const identity = travelerType && TRAVEL_IDENTITIES[travelerType] ? TRAVEL_IDENTITIES[travelerType] : null;
              const firstName = user?.firstName || null;

              // ── Zone 1 eyebrow: day + time of day (no weather/geo) ──
              const now = new Date();
              const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
              const hour = now.getHours();
              const timeOfDay = hour < 5 ? "night" : hour < 12 ? "morning" : hour < 17 ? "afternoon" : hour < 21 ? "evening" : "night";
              const eyebrowText = `${dayName} ${timeOfDay}`;

              // ── State derivation for decision tree ──
              const allBoardItems = boards.flatMap(b => (b.items || []).map(name => ({ name, boardId: b.id, boardName: b.name })));
              const tripCount = (pastTrips || []).length;

              // Read recent-saves sidecar (items added since the sidecar
              // shipped — legacy items skip State 3 detection per Cas's call)
              let recentSaves = [];
              try {
                const log = JSON.parse(localStorage.getItem("jga-recent-saves-v1") || "[]");
                const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                recentSaves = log.filter(e => new Date(e.addedAt).getTime() > sevenDaysAgo);
              } catch (e) {}

              // Dismissed states tracker (State 7 for now; State 4 wired in later)
              let dismissedStates = {};
              try {
                dismissedStates = JSON.parse(localStorage.getItem("jga-zone1-dismissed-v1") || "{}");
              } catch (e) {}
              const isDismissed = (key) => dismissedStates[key] && dismissedStates[key] > Date.now();
              const dismissState = (key, ttlMs) => {
                try {
                  const d = JSON.parse(localStorage.getItem("jga-zone1-dismissed-v1") || "{}");
                  d[key] = Date.now() + ttlMs;
                  localStorage.setItem("jga-zone1-dismissed-v1", JSON.stringify(d));
                  // Force a re-render via the always-changing tick counter.
                  // Previously used setBottomTab(null) which deduplicates when
                  // already null, leaving the dismissed dest visible.
                  setHomeRefreshTick(t => t + 1);
                } catch (e) {}
              };

              // Decision tree — first match wins
              let primaryState;
              if (!travelerType) {
                primaryState = "no_quiz";
              } else if (allBoardItems.length === 0 && tripCount === 0) {
                primaryState = "no_saves_no_trips";
              } else if (recentSaves.length > 0) {
                primaryState = "recent_saves";
              } else {
                primaryState = "default";
              }
              // (States 4, 5, 6 — trip-related — deferred to Week 2.)

              // Most-recent board info for State 3
              const mostRecentSaveBoard = recentSaves[0]?.boardName || null;
              const mostRecentSaveBoardId = recentSaves[0]?.boardId || null;
              const recentSaveCount = recentSaves.length;

              // Recommendation for State 7 (default) — pick a destination
              // matching identity vibes that the user hasn't saved yet
              let recommendedDest = null;
              let recommendedMatchCount = 0;
              let recommendedMatchedVibes = [];
              if (primaryState === "default" && identity?.vibes?.length) {
                // savedNames includes BOTH explicit board saves AND any
                // destination the user has generated/saved an itinerary for.
                // The itinerary check matters because a user might generate
                // an itinerary for Chiang Mai, save it, and return to home —
                // without this, the home dashboard keeps recommending Chiang
                // Mai even though they've already engaged with it.
                // Added Jun 25, 2026 per Cas.
                const savedNames = new Set([
                  ...allBoardItems.map(i => i.name),
                  ...((savedItineraries || []).map(i => i.dest).filter(Boolean)),
                ]);
                const identityVibesSet = new Set(identity.vibes);
                let bestScore = 0;
                for (const d of DESTINATIONS) {
                  if (savedNames.has(d.name)) continue;
                  if (isDismissed(`rec_${d.name}`)) continue;
                  const destTags = d.tags || [];
                  const matchedTags = destTags.filter(t => identityVibesSet.has(t));
                  if (matchedTags.length > bestScore) {
                    bestScore = matchedTags.length;
                    recommendedDest = d;
                    recommendedMatchCount = matchedTags.length;
                    recommendedMatchedVibes = matchedTags;
                  }
                }
              }

              // ── Zone 2 content: 4 most recent items ──
              // Cas's pick (Boards Path B): emoji + name on colored tile,
              // not bespoke illustrations (those don't exist yet — Q3 work).
              // Mix four sources per doc: recent saves, stamps (visited/someday),
              // postcards (dreaming/visited), and untimed board items as fallback.
              // Sort newest-first by timestamp, take 4.
              // ── Zone 2 builders ────────────────────────────────────────
              // Stamps + postcards split into separate sub-sections per Cas's
              // call (Jun 19, 2026). Each capped at 3. Saves dropped from this
              // zone — recent saves live on Boards. Journal entries
              // intentionally NOT surfaced here — too personal for the home page.
              const buildStampsZone = () => {
                let stamps = {};
                try { stamps = getStamps(); } catch (e) {}
                const items = [];
                for (const [id, s] of Object.entries(stamps)) {
                  const catEntry = SUGGESTED_STAMPS.find(c => c.id === id);
                  if (!catEntry) continue;
                  const ts = s.updatedAt || s.visitedAt || s.createdAt || 0;
                  items.push({
                    type: "stamp",
                    id,
                    name: catEntry.title,
                    country: catEntry.country,
                    emoji: catEntry.emoji,
                    canon: catEntry.canon || null,
                    state: s.state,
                    sortKey: ts,
                  });
                }
                items.sort((a, b) => b.sortKey - a.sortKey);
                return { all: items, top: items.slice(0, 3) };
              };
              const buildPostcardsZone = () => {
                let postcards = {};
                try { postcards = getPostcards(); } catch (e) {}
                const items = [];
                for (const [id, p] of Object.entries(postcards)) {
                  const catEntry = POSTCARDS_CATALOG.find(c => c.id === id);
                  if (!catEntry) continue;
                  const ts = p.updatedAt || p.visitedAt || p.createdAt || 0;
                  items.push({
                    type: "postcard",
                    id,
                    name: catEntry.destName || catEntry.title,
                    country: catEntry.country,
                    emoji: DESTINATIONS.find(d => d.name === (catEntry.destName || catEntry.title))?.emoji || "📮",
                    state: p.state,
                    sortKey: ts,
                  });
                }
                items.sort((a, b) => b.sortKey - a.sortKey);
                return { all: items, top: items.slice(0, 3) };
              };
              const zone2Stamps = buildStampsZone();
              const zone2Postcards = buildPostcardsZone();
              // Keep zone2Items alive as a lightweight presence check elsewhere
              // (e.g. invitation card state). Length = stamps + postcards.
              const zone2Items = [...zone2Stamps.all, ...zone2Postcards.all];

              // ── Zone 4 rotating line ──
              const STORY_LINES = [
                "What if 'where to go' wasn't a problem you had to solve?",
                "The trips you take become the trips you remember. We keep the receipts.",
                "Why go anywhere when we can help you find the right somewhere?",
                "You might only get one trip a year. Let's make it the right one.",
                "The window seat is the best seat. You're not driving — you're arriving.",
                "It's not how you travel. It's that you do — somewhere, anywhere.",
              ];
              const storyLine = (() => {
                // Lock for the session — IIFE runs on every render, so without
                // sessionStorage the line would flicker on every state change.
                let session = null;
                try { session = sessionStorage.getItem("jga-story-session-line-v1"); } catch (e) {}
                if (session && STORY_LINES.includes(session)) return session;
                // New session: pick excluding the last-shown line (per-user)
                let lastShown = null;
                try { lastShown = localStorage.getItem("jga-story-last-line-v1"); } catch (e) {}
                const available = STORY_LINES.filter(l => l !== lastShown);
                const picked = available[Math.floor(Math.random() * available.length)] || STORY_LINES[0];
                try {
                  localStorage.setItem("jga-story-last-line-v1", picked);
                  sessionStorage.setItem("jga-story-session-line-v1", picked);
                } catch (e) {}
                return picked;
              })();

              // Welcome line copy
              const welcomeLine = firstName
                ? `Welcome, ${firstName}.`
                : "Welcome, traveler.";

              // Identity reflection
              const identityReflection = identity
                ? <>You're a <button onClick={() => setShowIdentityCard(true)} style={{ background: "none", border: "none", padding: 0, color: "#4A8CC4", fontWeight: 600, borderBottom: "1px dashed rgba(74,140,196,0.4)", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit" }}>{travelerType}</button> — let's keep building the picture.</>
                : <>We don't know you yet. Let's start there.</>;

              // Invitation card content per state
              let invitation = null;
              if (primaryState === "no_quiz") {
                invitation = {
                  eyebrow: "START HERE",
                  headline: <>We don't know you yet.</>,
                  body: "Take the 60-second quiz. We'll match you to one of 20 traveler identities — and everything else gets smarter from there.",
                  primary: { label: "Take the quiz →", onClick: () => { setOnboardingRetake(false); setShowOnboarding(true); } },
                  secondary: null,
                };
              } else if (primaryState === "no_saves_no_trips") {
                invitation = {
                  eyebrow: "NEXT UP",
                  headline: <>You're a <span style={{ color: "#E89A3C" }}>{travelerType}</span>. Let's find your first place.</>,
                  body: "Save somewhere to a Board, or let us pick a destination for you. No commitments — just exploring.",
                  primary: { label: "Show me places →", onClick: () => { reset(); setMode("explore"); } },
                  secondary: { label: "Surprise me", onClick: () => { reset(); setMode("blind"); } },
                };
              } else if (primaryState === "recent_saves") {
                invitation = {
                  eyebrow: "PICK UP WHERE YOU LEFT OFF",
                  headline: <>You saved {recentSaveCount} place{recentSaveCount !== 1 ? "s" : ""} to <span style={{ color: "#E89A3C" }}>{mostRecentSaveBoard}</span> last week.</>,
                  body: "Want to start planning, or keep dreaming?",
                  primary: { label: "Start planning →", onClick: () => { reset(); setMode("explore"); } },
                  secondary: { label: "Keep dreaming", onClick: () => { reset(); setMode("wishlist"); } },
                };
              } else {
                // Default — show a recommendation if we have one, else generic
                if (recommendedDest) {
                  const vibesLine = recommendedMatchedVibes.length > 0
                    ? `Matches: ${recommendedMatchedVibes.slice(0, 4).join(" · ")}`
                    : null;
                  invitation = {
                    eyebrow: "SOMETHING NEW",
                    headline: <>We found a place that fits your <span style={{ color: "#E89A3C" }}>{travelerType}</span> side.</>,
                    body: `${recommendedDest.name} matched ${recommendedMatchCount} of your saved vibes.`,
                    extra: vibesLine,
                    primary: {
                      label: `Show me ${recommendedDest.name} →`,
                      onClick: () => {
                        reset();
                        setPrefilledDest(recommendedDest);
                        setMode("explore");
                      }
                    },
                    secondary: { label: "Not today", onClick: () => dismissState(`rec_${recommendedDest.name}`, 14 * 24 * 60 * 60 * 1000) },
                  };
                } else {
                  // No vibes data or no eligible dest — show a neutral fallback
                  invitation = {
                    eyebrow: "WHERE TO?",
                    headline: <>Pick up where you left off.</>,
                    body: "Browse the catalog, or let Take Me Anywhere give you three matches.",
                    primary: { label: "Take Me Anywhere →", onClick: () => { reset(); setMode("explore"); } },
                    secondary: { label: "Just browse", onClick: () => { reset(); setMode("search"); } },
                  };
                }
              }

              // Hide Zone 2 entirely if 0 items + no quiz
              const showZone2 = travelerType !== null || zone2Items.length > 0;

              // ════════════════════════════════════════════════════════════
              //                       RENDER
              // ════════════════════════════════════════════════════════════
              return (
                <div style={{
                  position: "relative",
                  // Page atmosphere — window-seat gradient + sun glow.
                  // Matches Boards + Journal surfaces for visual coherence.
                  background: "linear-gradient(180deg, #F4F9FD 0%, #E8F0F7 60%, #F0E8DC 100%)",
                  borderRadius: 18,
                  margin: "-4px -2px 0",
                  overflow: "hidden",
                }}>
                  {/* Sun glow — top-right radial in golden hour */}
                  <div aria-hidden="true" style={{
                    position: "absolute",
                    top: -60, right: -60,
                    width: 280, height: 280,
                    background: "radial-gradient(circle, rgba(245,215,138,0.35) 0%, transparent 70%)",
                    pointerEvents: "none",
                    zIndex: 0,
                  }} />
                  <div style={{ position: "relative", zIndex: 1 }}>

                    {/* ═══════════ ZONE 1: Welcome + Invitation ═══════════ */}
                    <div style={{ padding: "28px 22px 24px" }}>
                      <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.16em", color: textMuted, marginBottom: 12 }}>
                        {eyebrowText} <span style={{ color: goldText, fontWeight: 600 }}>at Just Go Anywhere&trade;</span>
                      </div>
                      {/* Small reminder — moved from old Zone 4 (Jun 19, 2026).
                         Tells the "why JGA exists" story at the top so users see
                         it before they scroll. Same storyLine library, dialed
                         down from 18px to 13px so it whispers, not shouts. */}
                      <div style={{ fontSize: 13, fontStyle: "italic", color: textMuted, lineHeight: 1.5, marginBottom: 14, letterSpacing: "-0.003em" }}>
                        "{storyLine}"
                      </div>
                      <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                        {welcomeLine}
                      </h1>
                      {!identity ? (
                        <div style={{ marginTop: 10, fontSize: 16, color: textMuted, lineHeight: 1.5 }}>
                          We don't know you yet. Let's start there.
                        </div>
                      ) : (() => {
                        /* Travel identity banner — registered version. Color +
                           vibe matches Mood Board's identity hero (Jun 19, 2026
                           realignment). The previous olive was a one-off that
                           drifted from the rest of the identity surfaces. Sky
                           gradient + per-identity accent now unify them. */
                        const skyEntry = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
                        const chart = generateConstellation(identity);
                        return (
                          <div style={{
                            marginTop: 16, position: "relative",
                            background: getIdentitySky(travelerType),
                            border: `1px solid ${skyEntry.accent}33`,
                            borderRadius: 16, padding: "20px 20px 20px",
                            overflow: "hidden",
                            boxShadow: `0 4px 24px rgba(20,18,24,0.18), 0 1px 0 ${skyEntry.accent}22 inset`,
                          }}>
                            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                              {/* Mini constellation — same SVG pattern as Mood Board hero */}
                              <div style={{ flexShrink: 0, width: 80, height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <svg viewBox="0 0 200 140" width="80" height="56" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                                  {chart.lines.map((l, i) => (
                                    <line key={`l-${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                                      stroke={skyEntry.accent} strokeWidth="2" opacity="0.7" strokeLinecap="round" />
                                  ))}
                                  {Object.entries(chart.stars).map(([attr, s]) => (
                                    <g key={attr}>
                                      <circle cx={s.x} cy={s.y} r={s.r * 2.5} fill="#FAFAF5" opacity="0.18" />
                                      <circle cx={s.x} cy={s.y} r={s.r * 1.5} fill="#FAFAF5" />
                                    </g>
                                  ))}
                                </svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>
                                  Travel Identity
                                </div>
                                <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 22, fontWeight: 500, color: "#FAFAF5", lineHeight: 1.1, marginBottom: 6, letterSpacing: "-0.3px" }}>
                                  <span>The {travelerType.split(" ")[0]}</span>{travelerType.split(" ").length > 1 && <span style={{ fontStyle: "italic", color: skyEntry.accent }}> {travelerType.split(" ").slice(1).join(" ")}</span>}
                                </div>
                                {(identity.tagline || identity.desc || identity.description) && (
                                  <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 12, color: "rgba(250,250,245,0.78)", lineHeight: 1.45, fontStyle: "italic", marginBottom: 14 }}>
                                    "{identity.tagline || identity.desc || identity.description}"
                                  </div>
                                )}
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button onClick={() => setShowIdentityCard(true)}
                                    style={{
                                      padding: "9px 14px", borderRadius: 10, border: "none",
                                      background: skyEntry.accent,
                                      color: "#1B3A5C", fontSize: 12, fontWeight: 800, cursor: "pointer",
                                      fontFamily: "inherit", letterSpacing: 0.3,
                                      boxShadow: `0 2px 10px ${skyEntry.accent}55`,
                                    }}>
                                    View identity card →
                                  </button>
                                  <button onClick={() => {
                                      reset();
                                      // Apply identity-aligned defaults AFTER reset (reset clears them).
                                      if (identity?.vibes) setExpVibes(identity.vibes.slice(0, 4));
                                      setBudget(String(getIdentityBudgetGuess(travelerType)));
                                      setMode("explore");
                                    }}
                                    style={{
                                      padding: "9px 14px", borderRadius: 10,
                                      border: "0.5px solid rgba(250,250,245,0.4)",
                                      background: "transparent", color: "#FAFAF5",
                                      fontSize: 12, fontWeight: 700, cursor: "pointer",
                                      fontFamily: "inherit", letterSpacing: 0.3,
                                    }}>
                                    Browse my matches
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Invitation Card — the one next thing */}
                      {invitation && (
                        <div style={{
                          marginTop: 22,
                          position: "relative",
                          background: "rgba(250,250,245,0.85)",
                          border: "0.5px solid rgba(27,58,92,0.1)",
                          borderRadius: 14,
                          padding: "18px 20px",
                          overflow: "hidden",
                        }}>
                          {/* Subtle internal golden glow top-right */}
                          <div aria-hidden="true" style={{
                            position: "absolute",
                            top: -40, right: -40,
                            width: 120, height: 120,
                            background: "radial-gradient(circle, rgba(232,154,60,0.18) 0%, transparent 70%)",
                            pointerEvents: "none",
                          }} />
                          <div style={{ position: "relative", zIndex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#4A8CC4", textTransform: "uppercase", marginBottom: 8 }}>
                              {invitation.eyebrow}
                            </div>
                            <div style={{ fontSize: 19, fontWeight: 600, color: "#1B3A5C", lineHeight: 1.3, letterSpacing: "-0.01em", marginBottom: 10 }}>
                              {invitation.headline}
                            </div>
                            <div style={{ fontSize: 14, color: textMuted, lineHeight: 1.5, marginBottom: invitation.extra ? 6 : 14 }}>
                              {invitation.body}
                            </div>
                            {invitation.extra && (
                              <div style={{ fontSize: 12, color: "#4A8CC4", lineHeight: 1.5, marginBottom: 14, fontWeight: 600, letterSpacing: 0.2 }}>
                                {invitation.extra}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <button onClick={invitation.primary.onClick}
                                style={{
                                  padding: "11px 18px", borderRadius: 10, border: "none",
                                  background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                                  color: "#FAFAF5", fontSize: 13, fontWeight: 800, cursor: "pointer",
                                  fontFamily: "inherit", letterSpacing: 0.3,
                                  boxShadow: "0 2px 8px rgba(232,154,60,0.3)",
                                }}>
                                {invitation.primary.label}
                              </button>
                              {invitation.secondary && (
                                <button onClick={invitation.secondary.onClick}
                                  style={{
                                    padding: "11px 18px", borderRadius: 10,
                                    border: "1px solid rgba(27,58,92,0.25)",
                                    background: "transparent", color: "#1B3A5C",
                                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                                    fontFamily: "inherit", letterSpacing: 0.3,
                                  }}>
                                  {invitation.secondary.label}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ═══════════ ZONE 2: You (belonging) ═══════════ */}
                    {/* Split into Stamps + Postcards sub-sections per Cas's
                       Jun 19, 2026 call. Saves no longer render here — they
                       live on Boards. Journal entries intentionally NOT shown
                       (too personal for the public dashboard). */}
                    {showZone2 && (() => {
                      // Tile renderer — shared by both sub-sections
                      const palette = ["#A8C5DA", "#C9A87B", "#9CB89C", "#B8A0BD", "#D4A574"];
                      const renderTile = (item, idx) => {
                        const colorIdx = (item.name.charCodeAt(0) + item.name.length) % palette.length;
                        const tileBg = palette[colorIdx];
                        const onTileClick = () => {
                          reset();
                          setAccountDefaultTab("journal");
                          setAccountJournalSubTab(item.type === "stamp" ? "stamps" : "postcards");
                          setMode("journal");
                        };
                        const isVisited = item.state === "visited";
                        const stateLabel = isVisited ? "✓ VISITED"
                          : item.type === "postcard" ? "○ DREAMING"
                          : "○ SOMEDAY";
                        const stateColor = isVisited ? "#2D9E5C" : "#E89A3C";
                        return (
                          <button key={`${item.type}:${item.id}-${idx}`}
                            onClick={onTileClick}
                            style={{
                              padding: 0, borderRadius: 12,
                              border: "0.5px solid rgba(27,58,92,0.1)",
                              background: "#FAFAF5", cursor: "pointer",
                              fontFamily: "inherit", textAlign: "left",
                              overflow: "hidden",
                            }}>
                            <div style={{
                              aspectRatio: "7 / 5",
                              overflow: "hidden",
                              background: tileBg,
                              position: "relative",
                            }}>
                              {item.type === "stamp"
                                ? <StampIllustration id={item.id} />
                                : <PostcardIllustration id={item.id} />}
                              {item.type === "stamp" && item.canon === "wonder" && (
                                <div style={{ position: "absolute", top: 4, right: 4, pointerEvents: "none" }}>
                                  <WonderSeal size={24} />
                                </div>
                              )}
                            </div>
                            <div style={{ padding: "8px 10px" }}>
                              <div style={{
                                fontSize: 12, fontWeight: 700, color: "#1B3A5C", lineHeight: 1.25,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                wordBreak: "break-word",
                              }}>
                                {item.name}
                              </div>
                              <div style={{ fontSize: 9, color: stateColor, fontWeight: 700, marginTop: 4, letterSpacing: "0.05em" }}>
                                {stateLabel}
                              </div>
                            </div>
                          </button>
                        );
                      };
                      // Empty-state prompt — single button that routes to the
                      // seeding modal in Visited mode (per Cas's call).
                      const renderEmptyPrompt = (kind) => {
                        const onClick = () => {
                          if (kind === "stamps") setStampsSeedingState("visited");
                          else setPostcardsSeedingState("visited");
                        };
                        return (
                          <button onClick={onClick}
                            style={{
                              width: "100%",
                              padding: "16px 18px",
                              borderRadius: 12,
                              border: "1px dashed rgba(74,140,196,0.4)",
                              background: "rgba(74,140,196,0.04)",
                              cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#1B3A5C", marginBottom: 2 }}>
                                Add {kind} for places you've been
                              </div>
                              <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.4 }}>
                                Mark trips you've already taken to start your collection.
                              </div>
                            </div>
                            <span style={{ color: "#4A8CC4", fontSize: 18, flexShrink: 0 }}>→</span>
                          </button>
                        );
                      };
                      // Sub-section block — used for both Stamps and Postcards
                      const renderSubSection = (kind, zoneData) => {
                        const total = zoneData.all.length;
                        const title = kind === "stamps" ? "Stamps" : "Postcards";
                        return (
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: "#1B3A5C", letterSpacing: 1.6, textTransform: "uppercase" }}>
                                {title} <span style={{ color: textDim, fontWeight: 600 }}>· {total}</span>
                              </div>
                              {total > 0 && (
                                <button onClick={() => {
                                  reset();
                                  setAccountDefaultTab("journal");
                                  setAccountJournalSubTab(kind);
                                  setMode("journal");
                                }}
                                  style={{ background: "none", border: "none", color: "#4A8CC4", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0, flexShrink: 0 }}>
                                  See all →
                                </button>
                              )}
                            </div>
                            {total === 0 ? renderEmptyPrompt(kind) : (
                              <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 10,
                              }}>
                                {zoneData.top.map((item, idx) => renderTile(item, idx))}
                                {/* Fill remaining slots with "+ Add more" placeholder
                                   tiles when the user has fewer than 3 items. Solves
                                   two problems: keeps the grid visually balanced (no
                                   oversized single-column tile when only 1 saved) AND
                                   gives a direct affordance to add more without going
                                   to Journal first. Added Jun 25, 2026 per Cas. */}
                                {Array.from({ length: Math.max(0, 3 - zoneData.top.length) }).map((_, idx) => (
                                  <button
                                    key={`add-slot-${idx}`}
                                    onClick={() => {
                                      if (kind === "stamps") setStampsSeedingState("visited");
                                      else setPostcardsSeedingState("dreaming");
                                    }}
                                    style={{
                                      padding: 0, borderRadius: 12,
                                      border: "1px dashed rgba(74,140,196,0.4)",
                                      background: "rgba(74,140,196,0.04)",
                                      cursor: "pointer", fontFamily: "inherit",
                                      display: "flex", flexDirection: "column",
                                      alignItems: "center", justifyContent: "center",
                                      aspectRatio: "7 / 5",
                                      color: "#4A8CC4",
                                      transition: "all 0.15s",
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,140,196,0.08)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.6)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(74,140,196,0.04)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.4)"; }}
                                  >
                                    <div style={{ fontSize: 22, fontWeight: 300, lineHeight: 1, marginBottom: 4 }}>+</div>
                                    <div style={{ fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700 }}>Add</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      };
                      return (
                        <div style={{ padding: "12px 22px 28px" }}>
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.14em", color: textMuted, marginBottom: 4 }}>
                              what you've been collecting
                            </div>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                              Your travel life so far
                            </h2>
                          </div>
                          {renderSubSection("stamps", zone2Stamps)}
                          {renderSubSection("postcards", zone2Postcards)}
                        </div>
                      );
                    })()}

                    {/* ═══════════ ZONE 3: Where to next? ═══════════ */}
                    <div style={{ padding: "12px 22px 28px" }}>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.14em", color: textMuted, marginBottom: 4 }}>
                          when you're ready
                        </div>
                        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                          Where to next?
                        </h2>
                      </div>

                      {/* Two hero cards side-by-side */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        {/* Take Me Anywhere — navy */}
                        <button onClick={() => { reset(); setMode("explore"); }}
                          style={{
                            position: "relative",
                            padding: "14px 14px", borderRadius: 14, border: "none",
                            background: "linear-gradient(135deg, #1B3A5C 0%, #2E5478 100%)",
                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                            overflow: "hidden", color: "#FAFAF5",
                          }}>
                          <div aria-hidden="true" style={{
                            position: "absolute", top: -30, right: -30, width: 100, height: 100,
                            background: "radial-gradient(circle, rgba(245,215,138,0.3) 0%, transparent 70%)",
                            pointerEvents: "none",
                          }} />
                          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
                            {/* Icon puck — subtle cream-tinted rounded square */}
                            <div style={{
                              flexShrink: 0,
                              width: 42, height: 42, borderRadius: 11,
                              background: "rgba(250,250,245,0.12)",
                              border: "1px solid rgba(250,250,245,0.18)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <svg aria-hidden="true" width="22" height="26" viewBox="0 0 38 48" fill="none">
                                <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="rgba(250,250,245,0.15)" stroke="#FAFAF5" strokeWidth="2"/>
                                <circle cx="19" cy="18" r="7" fill="none" stroke="#FAFAF5" strokeWidth="1.6"/>
                                <circle cx="19" cy="18" r="2.5" fill="#FAFAF5" opacity="0.9"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Take Me Anywhere</div>
                              <div style={{ fontSize: 11, color: "rgba(250,250,245,0.78)", lineHeight: 1.35 }}>
                                Set a budget. Pick a vibe. See your matches.
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Surprise Me — golden hour */}
                        <button onClick={() => { reset(); setMode("blind"); }}
                          style={{
                            position: "relative",
                            padding: "14px 14px", borderRadius: 14, border: "none",
                            background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                            overflow: "hidden", color: "#FAFAF5",
                          }}>
                          <div aria-hidden="true" style={{
                            position: "absolute", top: -30, right: -30, width: 100, height: 100,
                            background: "radial-gradient(circle, rgba(250,250,245,0.2) 0%, transparent 70%)",
                            pointerEvents: "none",
                          }} />
                          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              flexShrink: 0,
                              width: 42, height: 42, borderRadius: 11,
                              background: "rgba(250,250,245,0.15)",
                              border: "1px solid rgba(250,250,245,0.22)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <svg aria-hidden="true" width="24" height="24" viewBox="0 0 44 44" fill="none">
                                <circle cx="22" cy="22" r="19" fill="rgba(250,250,245,0.15)" stroke="#FAFAF5" strokeWidth="2"/>
                                <ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke="#FAFAF5" strokeWidth="1.5"/>
                                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke="#FAFAF5" strokeWidth="1.5" fill="none"/>
                                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke="#FAFAF5" strokeWidth="1.2" fill="none" opacity="0.7"/>
                                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke="#FAFAF5" strokeWidth="1.2" fill="none" opacity="0.7"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Surprise Me</div>
                              <div style={{ fontSize: 11, color: "rgba(250,250,245,0.85)", lineHeight: 1.35 }}>
                                Book the trip. We reveal where you're going.
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>

                      {/* Just browse — quiet card below */}
                      <button onClick={() => { reset(); setMode("search"); }}
                        style={{
                          width: "100%", padding: "14px 16px", borderRadius: 12,
                          border: "0.5px solid rgba(27,58,92,0.1)",
                          background: "rgba(250,250,245,0.5)",
                          cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                        }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3A5C" }}>Just browse</div>
                          <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                            Explore the catalog without committing to anything
                          </div>
                        </div>
                        <div style={{ fontSize: 16, color: "#4A8CC4", flexShrink: 0 }}>→</div>
                      </button>
                    </div>

                    {/* ═══════════ ZONE 4: Story (closing) ═══════════ */}
                    {/* ═══════════ ZONE 4 removed Jun 19, 2026 ═══════════ */}
                    {/* The storyLine + descriptor used to live here. They moved
                       up to Zone 1 (between the eyebrow and welcome line) so
                       users see them sooner. No closing block needed on
                       registered home — the dashboard ends with Zone 3. */}
                  </div>
                </div>
              );
            })()}

            {/* ─────────────────────────────────────────────────────── */}
            {/* UNREGISTERED DASHBOARD — aspirational, drives sign-up    */}
            {/* ─────────────────────────────────────────────────────── */}
            {!registered && (() => {
              // ════════════════════════════════════════════════════════════
              //   UNREGISTERED HOME — 4-ZONE STRUCTURE (Jun 2026)
              // ════════════════════════════════════════════════════════════
              // Companion to the registered 4-zone home. Same atmosphere,
              // same voice, same zones — different fill because the user
              // has no personal data yet.
              //
              // Two states based on quiz completion:
              //   State A (no quiz):  Zone 1 = "Take the quiz" invitation
              //   State B (quiz taken): Zone 1 = navy night-sky constellation
              //     card with "Sign up to save it" CTA
              //
              // Zone 2 (You / Travelers like you) is HIDDEN pre-launch —
              // we only have one curated trip (Sarah's Lisbon) and don't
              // ship fake/composite trips per Cas's call. Zone 2 returns
              // post-launch when more trips exist.
              //
              // Zone 3 gating:
              //   TMA: preview-then-gate via existing spinCount limit
              //   Surprise Me: immediate sign-up gate
              //   Just Browse: free, no gate
              //
              // Zone 4: identical rotating Story library as registered home.
              const identity = travelerType && TRAVEL_IDENTITIES[travelerType] ? TRAVEL_IDENTITIES[travelerType] : null;
              const now = new Date();
              const dayName = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
              const hour = now.getHours();
              const timeOfDay = hour < 5 ? "night" : hour < 12 ? "morning" : hour < 17 ? "afternoon" : hour < 21 ? "evening" : "night";
              const eyebrowText = `${dayName} ${timeOfDay}`;

              // Zone 4 rotating line (reuses the same library + session lock)
              const STORY_LINES = [
                "What if 'where to go' wasn't a problem you had to solve?",
                "The trips you take become the trips you remember. We keep the receipts.",
                "Why go anywhere when we can help you find the right somewhere?",
                "You might only get one trip a year. Let's make it the right one.",
                "The window seat is the best seat. You're not driving — you're arriving.",
                "It's not how you travel. It's that you do — somewhere, anywhere.",
              ];
              const storyLine = (() => {
                let session = null;
                try { session = sessionStorage.getItem("jga-story-session-line-v1"); } catch (e) {}
                if (session && STORY_LINES.includes(session)) return session;
                let lastShown = null;
                try { lastShown = localStorage.getItem("jga-story-last-line-v1"); } catch (e) {}
                const available = STORY_LINES.filter(l => l !== lastShown);
                const picked = available[Math.floor(Math.random() * available.length)] || STORY_LINES[0];
                try {
                  localStorage.setItem("jga-story-last-line-v1", picked);
                  sessionStorage.setItem("jga-story-session-line-v1", picked);
                } catch (e) {}
                return picked;
              })();

              // Identity quote — pull from the catalog if we have an identity
              const identityQuote = identity?.tagline || identity?.desc || identity?.description || null;

              return (
                <div style={{
                  position: "relative",
                  // Page atmosphere — same window-seat gradient as registered
                  background: "linear-gradient(180deg, #F4F9FD 0%, #E8F0F7 60%, #F0E8DC 100%)",
                  borderRadius: 18,
                  margin: "-4px -2px 0",
                  overflow: "hidden",
                }}>
                  {/* Sun glow — top-right radial in golden hour */}
                  <div aria-hidden="true" style={{
                    position: "absolute",
                    top: -60, right: -60,
                    width: 280, height: 280,
                    background: "radial-gradient(circle, rgba(245,215,138,0.35) 0%, transparent 70%)",
                    pointerEvents: "none",
                    zIndex: 0,
                  }} />
                  <div style={{ position: "relative", zIndex: 1 }}>

                    {/* ═══════════ ZONE 1: Welcome + Invitation ═══════════ */}
                    <div style={{ padding: "28px 22px 24px" }}>
                      <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.16em", color: textMuted, marginBottom: 12 }}>
                        {eyebrowText} <span style={{ color: goldText, fontWeight: 600 }}>at Just Go Anywhere&trade;</span>
                      </div>
                      {/* Small reminder — same treatment as registered Zone 1.
                         Moved here from Zone 4 (Jun 19, 2026). */}
                      <div style={{ fontSize: 13, fontStyle: "italic", color: textMuted, lineHeight: 1.5, marginBottom: 14, letterSpacing: "-0.003em" }}>
                        "{storyLine}"
                      </div>

                      {!travelerType ? (
                        /* State A: no quiz — cold arrival */
                        <>
                          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                            Welcome to JGA.
                          </h1>
                          <div style={{ marginTop: 10, fontSize: 16, color: textMuted, lineHeight: 1.5 }}>
                            Let's start with who you travel as. Two minutes.
                          </div>
                          {/* Invitation card — same as registered State 1 */}
                          <div style={{
                            marginTop: 22, position: "relative",
                            background: "rgba(250,250,245,0.85)",
                            border: "0.5px solid rgba(27,58,92,0.1)",
                            borderRadius: 14, padding: "18px 20px",
                            overflow: "hidden",
                          }}>
                            <div aria-hidden="true" style={{
                              position: "absolute", top: -40, right: -40, width: 120, height: 120,
                              background: "radial-gradient(circle, rgba(232,154,60,0.18) 0%, transparent 70%)",
                              pointerEvents: "none",
                            }} />
                            <div style={{ position: "relative", zIndex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#4A8CC4", textTransform: "uppercase", marginBottom: 8 }}>
                                START HERE
                              </div>
                              <div style={{ fontSize: 19, fontWeight: 600, color: "#1B3A5C", lineHeight: 1.3, letterSpacing: "-0.01em", marginBottom: 10 }}>
                                We don't know you yet.
                              </div>
                              <div style={{ fontSize: 14, color: textMuted, lineHeight: 1.5, marginBottom: 14 }}>
                                Take the 60-second quiz. We'll match you to one of 20 traveler identities — and everything else gets smarter from there.
                              </div>
                              <button onClick={() => { setOnboardingRetake(false); setShowOnboarding(true); }}
                                style={{
                                  padding: "11px 18px", borderRadius: 10, border: "none",
                                  background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                                  color: "#FAFAF5", fontSize: 13, fontWeight: 800, cursor: "pointer",
                                  fontFamily: "inherit", letterSpacing: 0.3,
                                  boxShadow: "0 2px 8px rgba(232,154,60,0.3)",
                                }}>
                                Take the quiz →
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* State B: quiz taken, not registered — sky-themed
                           identity card matching Mood Board (Jun 19, 2026).
                           Left/right: mini constellation + identity text/CTAs. */
                        <>
                          {(() => {
                            const skyEntry = IDENTITY_SKY[travelerType] || IDENTITY_SKY["Romantic Wanderer"];
                            const chart = identity ? generateConstellation(identity) : null;
                            return (
                              <div style={{
                                marginTop: 4, position: "relative",
                                background: getIdentitySky(travelerType),
                                border: `1px solid ${skyEntry.accent}33`,
                                borderRadius: 16, padding: "22px 22px 22px",
                                overflow: "hidden",
                                boxShadow: `0 4px 24px rgba(20,18,24,0.18), 0 1px 0 ${skyEntry.accent}22 inset`,
                              }}>
                                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                                  {/* Mini constellation — same SVG pattern as Mood Board hero */}
                                  {chart && (
                                    <div style={{ flexShrink: 0, width: 80, height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <svg viewBox="0 0 200 140" width="80" height="56" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                                        {chart.lines.map((l, i) => (
                                          <line key={`l-${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                                            stroke={skyEntry.accent} strokeWidth="2" opacity="0.7" strokeLinecap="round" />
                                        ))}
                                        {Object.entries(chart.stars).map(([attr, s]) => (
                                          <g key={attr}>
                                            <circle cx={s.x} cy={s.y} r={s.r * 2.5} fill="#FAFAF5" opacity="0.18" />
                                            <circle cx={s.x} cy={s.y} r={s.r * 1.5} fill="#FAFAF5" />
                                          </g>
                                        ))}
                                      </svg>
                                    </div>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, letterSpacing: 2, color: "rgba(250,250,245,0.55)", textTransform: "uppercase", marginBottom: 4 }}>
                                      You took the quiz · Travel Identity
                                    </div>
                                    <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 22, fontWeight: 500, color: "#FAFAF5", lineHeight: 1.1, marginBottom: 6, letterSpacing: "-0.3px" }}>
                                      <span>The {travelerType.split(" ")[0]}</span>{travelerType.split(" ").length > 1 && <span style={{ fontStyle: "italic", color: skyEntry.accent }}> {travelerType.split(" ").slice(1).join(" ")}</span>}
                                    </div>
                                    {identityQuote && (
                                      <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 12, color: "rgba(250,250,245,0.78)", lineHeight: 1.45, fontStyle: "italic", marginBottom: 10 }}>
                                        "{identityQuote}"
                                      </div>
                                    )}
                                    <div style={{ fontSize: 12, color: "rgba(250,250,245,0.78)", lineHeight: 1.55, marginBottom: 14 }}>
                                      Save your identity so it's here next time. Build mood boards, journal your trips, and get destinations matched to who you are.
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                      <button onClick={() => setShowRegister(true)}
                                        style={{
                                          padding: "9px 14px", borderRadius: 10, border: "none",
                                          background: skyEntry.accent,
                                          color: "#1B3A5C", fontSize: 12, fontWeight: 800, cursor: "pointer",
                                          fontFamily: "inherit", letterSpacing: 0.3,
                                          boxShadow: `0 2px 10px ${skyEntry.accent}55`,
                                        }}>
                                        Sign up to save it →
                                      </button>
                                      <button onClick={() => {
                                          reset();
                                          // Apply identity-aligned defaults for the un-registered banner too.
                                          // identity may be null here (un-registered + retake quiz states),
                                          // so fall back to TRAVEL_IDENTITIES lookup by travelerType.
                                          const ident = identity || TRAVEL_IDENTITIES[travelerType];
                                          if (ident?.vibes) setExpVibes(ident.vibes.slice(0, 4));
                                          setBudget(String(getIdentityBudgetGuess(travelerType)));
                                          setMode("explore");
                                        }}
                                        style={{
                                          padding: "9px 14px", borderRadius: 10,
                                          border: "0.5px solid rgba(250,250,245,0.4)",
                                          background: "transparent", color: "#FAFAF5",
                                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                                          fontFamily: "inherit", letterSpacing: 0.3,
                                        }}>
                                        Browse my matches
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>

                    {/* ═══════════ ZONE 2: hidden pre-launch ═══════════ */}
                    {/* Per Cas's call: only one curated trip (Sarah's Lisbon) exists
                       and we don't ship fake/composite trips. Zone 2 returns
                       post-launch when 3 more real trips are sourced. */}

                    {/* ═══════════ ZONE 3: Where to next? ═══════════ */}
                    <div style={{ padding: "12px 22px 28px" }}>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontStyle: "italic", letterSpacing: "0.14em", color: textMuted, marginBottom: 4 }}>
                          when you're ready
                        </div>
                        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1B3A5C", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                          Where to next?
                        </h2>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        {/* Take Me Anywhere — preview-then-gate via existing spin count */}
                        <button onClick={() => { reset(); setMode("explore"); }}
                          style={{
                            position: "relative", padding: "14px 14px", borderRadius: 14, border: "none",
                            background: "linear-gradient(135deg, #1B3A5C 0%, #2E5478 100%)",
                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                            overflow: "hidden", color: "#FAFAF5",
                          }}>
                          <div aria-hidden="true" style={{
                            position: "absolute", top: -30, right: -30, width: 100, height: 100,
                            background: "radial-gradient(circle, rgba(245,215,138,0.3) 0%, transparent 70%)",
                            pointerEvents: "none",
                          }} />
                          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              flexShrink: 0,
                              width: 42, height: 42, borderRadius: 11,
                              background: "rgba(250,250,245,0.12)",
                              border: "1px solid rgba(250,250,245,0.18)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <svg aria-hidden="true" width="22" height="26" viewBox="0 0 38 48" fill="none">
                                <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="rgba(250,250,245,0.15)" stroke="#FAFAF5" strokeWidth="2"/>
                                <circle cx="19" cy="18" r="7" fill="none" stroke="#FAFAF5" strokeWidth="1.6"/>
                                <circle cx="19" cy="18" r="2.5" fill="#FAFAF5" opacity="0.9"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Take Me Anywhere</div>
                              <div style={{ fontSize: 11, color: "rgba(250,250,245,0.78)", lineHeight: 1.35 }}>
                                Set a budget. Pick a vibe. See your matches.
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Surprise Me — immediate gate per doc */}
                        <button onClick={() => setShowRegister(true)}
                          style={{
                            position: "relative", padding: "14px 14px", borderRadius: 14, border: "none",
                            background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                            overflow: "hidden", color: "#FAFAF5",
                          }}>
                          <div aria-hidden="true" style={{
                            position: "absolute", top: -30, right: -30, width: 100, height: 100,
                            background: "radial-gradient(circle, rgba(250,250,245,0.2) 0%, transparent 70%)",
                            pointerEvents: "none",
                          }} />
                          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              flexShrink: 0,
                              width: 42, height: 42, borderRadius: 11,
                              background: "rgba(250,250,245,0.15)",
                              border: "1px solid rgba(250,250,245,0.22)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <svg aria-hidden="true" width="24" height="24" viewBox="0 0 44 44" fill="none">
                                <circle cx="22" cy="22" r="19" fill="rgba(250,250,245,0.15)" stroke="#FAFAF5" strokeWidth="2"/>
                                <ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke="#FAFAF5" strokeWidth="1.5"/>
                                <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke="#FAFAF5" strokeWidth="1.5" fill="none"/>
                                <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke="#FAFAF5" strokeWidth="1.2" fill="none" opacity="0.7"/>
                                <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke="#FAFAF5" strokeWidth="1.2" fill="none" opacity="0.7"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Surprise Me</div>
                              <div style={{ fontSize: 11, color: "rgba(250,250,245,0.85)", lineHeight: 1.35 }}>
                                Book the trip. We reveal where you're going.
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>

                      {/* Just browse — ungated */}
                      <button onClick={() => { reset(); setMode("search"); }}
                        style={{
                          width: "100%", padding: "14px 16px", borderRadius: 12,
                          border: "0.5px solid rgba(27,58,92,0.1)",
                          background: "rgba(250,250,245,0.5)",
                          cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                        }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3A5C" }}>Just browse</div>
                          <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                            Explore the catalog without committing to anything
                          </div>
                        </div>
                        <div style={{ fontSize: 16, color: "#4A8CC4", flexShrink: 0 }}>→</div>
                      </button>
                    </div>

                    {/* ═══════════ ZONE 4: Closing sign-up nudge ═══════════ */}
                    {/* storyLine moved to Zone 1 (Jun 19, 2026). Closing just
                       carries the low-emphasis sign-up nudge for visitors who
                       scrolled all the way down without signing up yet. */}
                    <div style={{
                      padding: "24px 22px 28px",
                      background: "linear-gradient(180deg, transparent 0%, rgba(245,215,138,0.12) 100%)",
                    }}>
                      <div style={{ maxWidth: 380, margin: "0 auto", textAlign: "center" }}>
                        <div aria-hidden="true" style={{ width: 40, height: 1, background: "rgba(27,58,92,0.2)", margin: "0 auto 16px" }} />
                        <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
                          Ready to make this yours?{" "}
                          <button onClick={() => setShowRegister(true)}
                            style={{ background: "none", border: "none", color: "#4A8CC4", fontWeight: 600, textDecoration: "underline", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 12 }}>
                            Sign up — 30 seconds.
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* ── Swipeable info carousel — DISABLED Jun 2026 ──
               Both 4-zone home redesigns (registered + unregistered) explicitly
               exclude this carousel — Zone 4's Story closer does the
               "what is JGA" job more quietly. Kept here under `false &&`
               in case any slide is wanted as a standalone surface later. */}
            {false && (() => {
              const slides = [
                {
                  id: "what",
                  tag: "What is Just Go Anywhere™?",
                  content: (
                    <div>
                      <p style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: textPrimary, lineHeight: 1.6 }}>
                        Find your travel identity. Discover where to go. Remember every trip.
                      </p>
                      <p style={{ margin: "0 0 16px", fontSize: 13, color: textMuted, lineHeight: 1.7 }}>
                        Take the quiz to meet your traveler type — one of twenty identities matched to who you actually are. Save destinations you love, build mood boards, and journal every trip <strong style={{ color: darkMode ? textPrimary : "#1B3A5C" }}>so they stay with you for years.</strong>
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        {[
                          {
                            icon: () => (
                              // Quiz / identity icon — speech-bubble with sparkle
                              <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
                                <circle cx="22" cy="22" r="19" fill={`${gold}20`} stroke={gold} strokeWidth="1.8"/>
                                <path d="M14 16 Q14 14 16 14 L28 14 Q30 14 30 16 L30 24 Q30 26 28 26 L20 26 L16 30 L16 26 Q14 26 14 24 Z" fill="none" stroke={gold} strokeWidth="1.6" strokeLinejoin="round"/>
                                <path d="M22 18 L23 20 L25 21 L23 22 L22 24 L21 22 L19 21 L21 20 Z" fill={gold}/>
                              </svg>
                            ),
                            title: "Take the quiz", desc: "Meet your type"
                          },
                          {
                            icon: () => (
                              // Destination match — pin + heart
                              <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
                                <circle cx="22" cy="22" r="19" fill={`${teal}20`} stroke={teal} strokeWidth="1.8"/>
                                <path d="M22 12 C 18 12 15 15 15 19 C 15 23 22 32 22 32 C 22 32 29 23 29 19 C 29 15 26 12 22 12 Z" fill="none" stroke={teal} strokeWidth="1.6" strokeLinejoin="round"/>
                                <circle cx="22" cy="19" r="2.5" fill={teal}/>
                              </svg>
                            ),
                            title: "Discover matches", desc: "Curated for you"
                          },
                          {
                            icon: () => (
                              // Save / journal — open book with bookmark
                              <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
                                <circle cx="22" cy="22" r="19" fill={`${teal}20`} stroke={teal} strokeWidth="1.8"/>
                                <path d="M12 16 L22 18 L22 32 L12 30 Z" fill="none" stroke={teal} strokeWidth="1.5" strokeLinejoin="round"/>
                                <path d="M32 16 L22 18 L22 32 L32 30 Z" fill="none" stroke={teal} strokeWidth="1.5" strokeLinejoin="round"/>
                                <path d="M15 21 L19 21.6" stroke={teal} strokeWidth="1" opacity="0.7"/>
                                <path d="M15 24 L19 24.6" stroke={teal} strokeWidth="1" opacity="0.7"/>
                                <path d="M25 21.6 L29 21" stroke={teal} strokeWidth="1" opacity="0.7"/>
                                <path d="M25 24.6 L29 24" stroke={teal} strokeWidth="1" opacity="0.7"/>
                              </svg>
                            ),
                            title: "Save & journal", desc: "Keep what matters"
                          },
                        ].map(s => (
                          <div key={s.title} style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "#FAFAF5", border: `1px solid ${darkMode ? "rgba(27,58,92,0.084)" : "rgba(27,58,92,0.15)"}`, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                            <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>{s.icon()}</div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: teal, marginBottom: 2 }}>{s.title}</div>
                            <div style={{ fontSize: 11, color: textDim, lineHeight: 1.4 }}>{s.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
                {
                  id: "why",
                  tag: "Why Just Go Anywhere™?",
                  content: (
                    <div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                        {[
                          { stat: "8–20 hrs", label: "avg planning time per trip" },
                          { stat: "38+",      label: "websites visited before booking" },
                          { stat: "53%",      label: "travelers feel overwhelmed" },
                          { stat: "~5 min",   label: "to set up your trip with us" },
                        ].map(s => (
                          <div key={s.stat} style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "#F0EDE3", borderRadius: 12, padding: "10px 12px" }}>
                            <div style={{ fontSize: 20, fontWeight: 900, color: darkMode ? gold : amber, lineHeight: 1, marginBottom: 3 }}>{s.stat}</div>
                            <div style={{ fontSize: 10, color: textMuted, lineHeight: 1.4 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                      <p style={{ margin: 0, fontSize: 12, color: textMuted, lineHeight: 1.65, fontStyle: "italic" }}>
                        Dozens of sites, hours comparing, still second-guessing. We collapse all of that into one screen.
                      </p>
                    </div>
                  ),
                },
                {
                  id: "proof",
                  tag: "What travelers love",
                  content: (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {[
                        { name: "Sarah M.", location: "Identity: Romantic Wanderer", quote: "The quiz nailed me. I'd been planning all the wrong trips for years. Now I have 8 destinations saved that actually feel like me.", rating: 5, color: teal },
                        { name: "James & Priya", location: "12 entries · 4 trips", quote: "We journal every night on the road. Voice notes from Lisbon, photos from Porto — it's like a real travelogue now, not just camera roll chaos.", rating: 5, color: goldText },
                        { name: "Marcus T.", location: "3 Boards · 22 destinations saved", quote: "My Mood Boards changed how I dream. I'm not just bookmarking — I'm building a real plan. Patagonia is next.", rating: 5, color: teal },
                      ].map(t => (
                        <div key={t.name} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg,${t.color}60,${t.color}30)`, border: `1.5px solid ${t.color}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, color: t.color, flexShrink: 0 }}>
                            {t.name.split(" ").map(n => n[0]).join("").slice(0,2)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: textPrimary }}>{t.name}</span>
                              <span style={{ fontSize: 10, color: textDim }}>{t.location}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: 12, color: textMuted, lineHeight: 1.5, fontStyle: "italic" }}>"{t.quote}"</p>
                            <div style={{ display: "flex", gap: 1, marginTop: 4 }}>
                              {Array.from({ length: t.rating }).map((_,i) => <span key={i} style={{ fontSize: 10, color: goldText }}>★</span>)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
              ];

              return (
                <div>
                  <div
                    onTouchStart={e => setCarouselTouchStart(e.touches[0].clientX)}
                    onTouchEnd={e => {
                      if (carouselTouchStart === null) return;
                      const diff = carouselTouchStart - e.changedTouches[0].clientX;
                      if (diff > 50) setCarouselSlide(s => (s + 1) % slides.length);
                      else if (diff < -50) setCarouselSlide(s => (s - 1 + slides.length) % slides.length);
                      setCarouselTouchStart(null);
                    }}
                    style={{ borderRadius: 20, padding: "20px 18px", background: darkMode ? "linear-gradient(135deg,rgba(13,21,33,0.98),rgba(8,18,28,0.98))" : "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.15)" : "rgba(74,140,196,0.55)"}`, boxShadow: darkMode ? "0 4px 32px rgba(0,0,0,0.3)" : "0 1px 0 rgba(255,255,255,0.4) inset, 0 2px 8px rgba(27,58,92,0.04)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ fontSize: 10, letterSpacing: 2.5, color: darkMode ? "#4A8CC4" : "#1B3A5C", textTransform: "uppercase", fontWeight: 700 }}>{slides[carouselSlide].tag}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setCarouselSlide(s => (s - 1 + slides.length) % slides.length)} aria-label="Previous slide" style={{ width:28,height:28,borderRadius:"50%",border:"1px solid rgba(27,58,92,0.45)",background:"transparent",color:teal,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit" }}>‹</button>
                        <button onClick={() => setCarouselSlide(s => (s + 1) % slides.length)} aria-label="Next slide" style={{ width:28,height:28,borderRadius:"50%",border:"1px solid rgba(27,58,92,0.45)",background:"transparent",color:teal,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit" }}>›</button>
                      </div>
                    </div>
                    <div key={carouselSlide} aria-live="polite" aria-atomic="true" style={{ animation: "fadeIn 0.2s ease" }}>
                      {slides[carouselSlide].content}
                    </div>
                  </div>
                  <div style={{ display:"flex",justifyContent:"center",gap:6,marginTop:10 }}>
                    {slides.map((_,i) => (
                      <button key={i} onClick={() => setCarouselSlide(i)} style={{ width:i===carouselSlide?22:7,height:7,borderRadius:4,border:"none",background:i===carouselSlide?teal:(darkMode?"rgba(27,58,92,0.14)":"rgba(0,0,0,0.15)"),cursor:"pointer",padding:0,transition:"all 0.2s" }} />
                    ))}
                  </div>
                </div>
              );
            })()}


            {/* ── Matched Destinations — shown after first search ── */}
            {registered && spinCount > 0 && (() => {
              const identityData = travelerType && TRAVEL_IDENTITIES[travelerType];
              const userVibes = expVibes.length > 0 ? expVibes : blindVibes.length > 0 ? blindVibes : identityData?.vibes || (user?.vibes || []);
              // Use whichever mode the user just engaged as the explicit budget;
              // resolver fills the fallback cascade if that mode has none.
              const contextBudget = expVibes.length > 0 ? budget : blindVibes.length > 0 ? blindBudget : null;
              const { value: userBudget } = resolveUserBudget({ explicit: contextBudget });
              // Use the same flight-inclusive cost basis the card display uses,
              // so the filter agrees with the displayed prices. Earlier the
              // filter compared against bare minBudget (on-ground only), which
              // let Iceland pass at $2000 while its displayed cost was $2400+.
              const airportForFilter = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
              const matched = DESTINATIONS.filter(d => {
                const baseline = getBaselineTripCost(d, airportForFilter, 5, 1);
                const budgetOk = userBudget >= baseline;
                const vibeOk = userVibes.length === 0 || userVibes.some(v => d.tags.includes(v));
                return budgetOk && vibeOk;
              }).sort(() => Math.random() - 0.5).slice(0, 6);
              if (!matched.length) return null;
              return (
                <div style={{ animation: "fadeIn 0.4s ease" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, letterSpacing: 2.5, color: teal, textTransform: "uppercase", fontWeight: 700, marginBottom: 2 }}>✨ Matched For You</div>
                      <div style={{ fontSize: 13, color: textMuted }}>
                        {identityData ? <>{identityData.emoji} <strong style={{ color: identityData.color }}>{travelerType}</strong> picks</> : `Based on your${userVibes.length > 0 ? ` ${userVibes.slice(0,2).join(" & ")} vibe` : " preferences"}`} · ${userBudget.toLocaleString()} budget
                      </div>
                    </div>
                    <button onClick={() => { reset(); setMode("explore"); }} aria-label="See all matched destinations"
                      style={{ fontSize: 11, fontWeight: 700, color: teal, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      See all →
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                    {matched.map(dest => (
                      <button key={dest.name} aria-label={`Explore ${dest.name}, ${dest.country}`}
                        onClick={() => { setPrefilledDest(dest); setMode("explore"); setStep(2); setResults([dest]); setSelected(dest); generateItinerary(dest); }}
                        style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "#FAFAF5", border: `1px solid ${darkMode ? "rgba(27,58,92,0.15)" : "rgba(27,58,92,0.25)"}`, borderRadius: 14, padding: "12px 14px", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.45)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.06)" : "rgba(27,58,92,0.08)"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.15)" : "rgba(27,58,92,0.25)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "#FAFAF5"; }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }} role="img" aria-label={dest.country}>{dest.emoji}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: textPrimary, marginBottom: 2 }}>{dest.name}</div>
                        <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>{dest.country}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <BudgetDisplay dest={dest} departureAirport={expDepartureAirport || blindDepartureAirport} flightPrice={flightPrices[dest.name]} userBudget={userBudget} travelers={paxFromCompanion(travelCompanion, travelers)} />
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(27,58,92,0.1)", color: teal }}>{dest.tags[0]}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Blog removed — now lives in Worth Knowing → Blog dropdown
                (registered dashboard) to avoid duplication. */}

            {/* ── Travel Aura This Month — moved up to sit under the Style/Budget/Airport/Passport row ── */}

          </div>
        )}

        {/* ══════════ TRENDING ══════════ */}
        {mode === "top-traveled" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={reset} style={{ background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
                <div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: darkMode ? coral : "#1B3A5C" }}>Trending Destinations</h2>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>Where the world is going right now</div>
                </div>
              </div>
              {!registered && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setShowLogin(true)} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid rgba(27,58,92,0.35)`, background: "rgba(27,58,92,0.07)", color: teal, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Sign In</button>
                  <button onClick={() => setShowRegister(true)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Register</button>
                </div>
              )}
            </div>
            {registered && starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length > 0 && (
              <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.18)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#4A8CC4" }}>
                ⭐ Watching {starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length} destination{starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length !== 1 ? "s" : ""} — we'll alert you when trips fit your budget
              </div>
            )}
            {!registered && (
              <div style={{ background: "rgba(74,140,196,0.04)", border: "1px solid rgba(74,140,196,0.12)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#7a6040", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span>🔒 Price alerts are a members-only feature</span>
                <button onClick={() => setShowRegister(true)} style={{ background: "none", border: "none", color: goldText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}>Join free →</button>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TOP_TRAVELED.map((d, i) => {
                const starred = starredAlerts.includes(d.name);
                return (
                  <div key={d.name} style={{ ...dynCard, padding: "14px 16px", border: `1px solid ${starred ? "rgba(74,140,196,0.3)" : "rgba(255,107,107,0.15)"}`, display: "flex", alignItems: "center", gap: 12, transition: "border-color 0.2s" }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "rgba(255,107,107,0.4)", minWidth: 26, textAlign: "center" }}>#{i + 1}</div>
                    <div style={{ fontSize: 28 }}>{d.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: coral, fontWeight: 700 }}>{d.travelers}</div>
                      </div>
                      <div style={{ fontSize: 12, color: textMuted, marginTop: 1 }}>{d.country}</div>
                      <div style={{ fontSize: 11, color: textDim, marginTop: 3, fontStyle: "italic" }}>{d.stat}</div>
                    </div>
                    {registered ? (
                      <button onClick={() => setBoardPickerDest(d.name)}
                        title={starred ? "Manage boards for this destination" : "Save to a board"}
                        style={{ flexShrink: 0, background: starred ? "rgba(74,140,196,0.15)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)", border: `1px solid ${starred ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s" }} aria-label={starred ? "Manage boards for this destination" : "Save to a board"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={starred ? "#4A8CC4" : "none"} stroke={starred ? "#4A8CC4" : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                        </svg>
                      </button>
                    ) : (
                      <button onClick={() => setShowRegister(true)} title="Members can set price alerts"
                        style={{ flexShrink: 0, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: "1px solid rgba(27,58,92,0.096)", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14, opacity: 0.45 }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.3)"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "0.45"; e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"; }}>
                        🔒
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: textDim, textAlign: "center", fontStyle: "italic" }}>
              {registered ? "☆ Star a destination to get alerted when trips are within your budget" : "🔒 Register a free account to set price alerts"}
            </div>
            {!registered && (
              <button onClick={() => setShowRegister(true)} style={{ display: "block", margin: "8px auto 0", background: "none", border: "none", color: goldText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                Join free →
              </button>
            )}

            {/* Starred destinations budget planner */}
            {registered && starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length > 0 && !showStarredPlan && (
              <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "14px 16px", marginTop: 12 }}>
                <div style={{ fontSize: 12, color: "#4A8CC4", marginBottom: 10 }}>
                  ⭐ You've starred {starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length} destination{starredAlerts.filter(a => TOP_TRAVELED.some(d => d.name === a)).length !== 1 ? "s" : ""} — set budgets and save to your profile
                </div>
                <button onClick={() => setShowStarredPlan(true)}
                  style={{ width: "100%", padding: "11px", borderRadius: 10, border: `1px solid rgba(74,140,196,0.4)`, background: "rgba(74,140,196,0.08)", color: goldText, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Plan My Starred Trips →
                </button>
              </div>
            )}
            {registered && showStarredPlan && (
              <StarredPlanPanel
                starredAlerts={starredAlerts}
                topList={TOP_TRAVELED}
                accentColor={coral}
                onClose={() => setShowStarredPlan(false)}
                onExplore={() => { setShowStarredPlan(false); setMode("explore"); }}
              />
            )}

            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <Btn onClick={() => setMode("explore")} style={{ flex: 1 }}>Book this trip →</Btn>
              <Btn variant="ghost" onClick={reset} style={{ flex: 1 }}>← Home</Btn>
            </div>
          </div>
        )}

        {/* ══════════ MOST AFFORDABLE ══════════ */}
        {mode === "top-affordable" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={reset} style={{ background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: darkMode ? "#6dbd6d" : "#2a7a4a" }}>Most Affordable Destinations</h2>
                <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>Best value trips you can book right now</div>
              </div>
            </div>
            {registered && starredAlerts.filter(a => TOP_AFFORDABLE.some(d => d.name === a)).length > 0 && (
              <div style={{ background: "rgba(109,189,109,0.06)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#6dbd6d" }}>
                ⭐ Watching {starredAlerts.filter(a => TOP_AFFORDABLE.some(d => d.name === a)).length} destination{starredAlerts.filter(a => TOP_AFFORDABLE.some(d => d.name === a)).length !== 1 ? "s" : ""} — we'll alert you when trips are within your budget
              </div>
            )}
            {!registered && (
              <div style={{ background: "rgba(109,189,109,0.04)", border: "1px solid rgba(109,189,109,0.1)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#4a7a4a", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span>🔒 Price alerts are a members-only feature</span>
                <button onClick={() => setShowRegister(true)} style={{ background: "none", border: "none", color: goldText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}>Join free →</button>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TOP_AFFORDABLE.map((d, i) => {
                const starred = starredAlerts.includes(d.name);
                return (
                  <div key={d.name} style={{ ...dynCard, padding: "14px 16px", border: `1px solid ${starred ? "rgba(74,140,196,0.3)" : "rgba(109,189,109,0.15)"}`, display: "flex", alignItems: "center", gap: 12, transition: "border-color 0.2s" }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "rgba(109,189,109,0.4)", minWidth: 26, textAlign: "center" }}>#{i + 1}</div>
                    <div style={{ fontSize: 28 }}>{d.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: "#6dbd6d", fontWeight: 700 }}>{d.budget}</div>
                      </div>
                      <div style={{ fontSize: 12, color: textMuted, marginTop: 1 }}>{d.country}</div>
                      <div style={{ fontSize: 11, color: textDim, marginTop: 3, fontStyle: "italic" }}>{d.stat}</div>
                    </div>
                    {registered ? (
                      <button onClick={() => setBoardPickerDest(d.name)}
                        title={starred ? "Manage boards for this destination" : "Save to a board"}
                        style={{ flexShrink: 0, background: starred ? "rgba(74,140,196,0.15)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)", border: `1px solid ${starred ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s" }} aria-label={starred ? "Manage boards for this destination" : "Save to a board"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={starred ? "#4A8CC4" : "none"} stroke={starred ? "#4A8CC4" : (darkMode ? "#556" : "#aaa")} strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                        </svg>
                      </button>
                    ) : (
                      <button onClick={() => setShowRegister(true)} title="Members can set price alerts"
                        style={{ flexShrink: 0, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: "1px solid rgba(27,58,92,0.096)", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14, opacity: 0.45 }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.borderColor = "rgba(109,189,109,0.3)"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "0.45"; e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"; }}>
                        🔒
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: textDim, textAlign: "center", fontStyle: "italic" }}>
              {registered ? "☆ Star a destination to get alerted when trips are within your budget" : "🔒 Register a free account to set price alerts"}
            </div>
            {!registered && (
              <button onClick={() => setShowRegister(true)} style={{ display: "block", margin: "8px auto 0", background: "none", border: "none", color: goldText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                Join free →
              </button>
            )}
            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <Btn onClick={() => setMode("explore")} style={{ flex: 1 }}>Book this trip →</Btn>
              <Btn variant="ghost" onClick={reset} style={{ flex: 1 }}>← Home</Btn>
            </div>
          </div>
        )}

        {/* Travel Agents CTA removed — now lives in Worth Knowing →
            For Travel Agents dropdown (registered dashboard). */}

        {/* ══════════ SEARCH PAGE (inline, renders between top & bottom nav) ══════════ */}
        {mode === "search" && (
          <div style={{
            animation: "fadeIn 0.2s ease",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          }}>
            {/* Page header — matches Trips/Boards/Journal exactly. The
               outer wrapper has NO padding because <main> already supplies
               page-level padding (28px 20px 100px). Adding more here was
               making Explore sit 16px lower and 20px more inset than the
               other surfaces — fixed Jun 19, 2026. */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>find your next place to go</div>
              <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: "#1B3A5C", letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase" }}>Explore</h2>
            </div>
            {/* ── Sub-nav: discovery categories — moved ABOVE the search bar
                per Cas's Day 28 call. Pills are the primary navigation;
                search is secondary refinement. */}
            {/* Row 1: Surprise Me + Take Me Anywhere — booking entry points,
                same brand SVGs as bottom nav + Zone 3 on home. */}
            <div style={{ display: "flex", gap: 10, padding: 0, flexShrink: 0 }}>
              <button onClick={() => { reset(); setMode("blind"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  padding: "11px 12px", borderRadius: 12,
                  border: `1px solid ${darkMode ? "rgba(232,154,60,0.3)" : "rgba(232,154,60,0.4)"}`,
                  background: darkMode ? "rgba(232,154,60,0.08)" : "rgba(232,154,60,0.1)",
                  color: "#C8741D", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 44 44" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="22" cy="22" r="19" fill="none" stroke="#C8741D" strokeWidth="1.8"/>
                  <ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke="#C8741D" strokeWidth="1.4"/>
                  <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke="#C8741D" strokeWidth="1.4" fill="none"/>
                </svg>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>Surprise Me</span>
                  <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.78, fontStyle: "italic", marginTop: 2 }}>blind booking</span>
                </div>
              </button>
              <button onClick={() => { reset(); setMode("explore"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  padding: "11px 12px", borderRadius: 12,
                  border: `1px solid ${darkMode ? "rgba(27,58,92,0.3)" : "rgba(27,58,92,0.4)"}`,
                  background: darkMode ? "rgba(27,58,92,0.06)" : "rgba(27,58,92,0.08)",
                  color: "#1B3A5C", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                <svg aria-hidden="true" width="16" height="18" viewBox="0 0 38 48" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="none" stroke="#1B3A5C" strokeWidth="2"/>
                  <circle cx="19" cy="18" r="7" fill="none" stroke="#1B3A5C" strokeWidth="1.6"/>
                  <circle cx="19" cy="18" r="2.5" fill="#1B3A5C"/>
                </svg>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>Take Me Anywhere</span>
                  <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.78, fontStyle: "italic", marginTop: 2 }}>match finder</span>
                </div>
              </button>
            </div>

            {/* Row 2: Most Affordable + Trending Trips */}
            <div style={{ display: "flex", gap: 10, padding: "6px 0 12px", flexShrink: 0 }}>
              <button onClick={() => { reset(); setMode("affordable"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  padding: "11px 12px", borderRadius: 12,
                  border: `1px solid ${darkMode ? "rgba(109,189,109,0.25)" : "rgba(109,189,109,0.35)"}`,
                  background: darkMode ? "rgba(109,189,109,0.06)" : "rgba(109,189,109,0.08)",
                  color: "#3a8a3a", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                <svg width="18" height="18" viewBox="0 0 44 44" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="22" cy="22" r="19" fill="none" stroke="#3a8a3a" strokeWidth="1.8"/>
                  <circle cx="22" cy="22" r="10" fill="none" stroke="#3a8a3a" strokeWidth="1.3"/>
                  <text x="22" y="27" textAnchor="middle" fontSize="12" fontWeight="800" fill="#3a8a3a" fontFamily="Inter,sans-serif">$</text>
                </svg>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>Most Affordable</span>
                  <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.78, fontStyle: "italic", marginTop: 2 }}>within your budget</span>
                </div>
              </button>
              <button onClick={() => { reset(); setMode("trending"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  padding: "11px 12px", borderRadius: 12,
                  border: `1px solid ${darkMode ? "rgba(255,107,107,0.25)" : "rgba(255,107,107,0.35)"}`,
                  background: darkMode ? "rgba(255,107,107,0.06)" : "rgba(255,107,107,0.08)",
                  color: "#c44545", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                }}>
                <svg width="18" height="18" viewBox="0 0 44 44" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="22" cy="22" r="19" fill="none" stroke="#c44545" strokeWidth="1.8"/>
                  <polyline points="10,30 17,22 23,26 34,13" fill="none" stroke="#c44545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M30 11 L34 13 L31 16" fill="none" stroke="#c44545" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>Trending Trips</span>
                  <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.78, fontStyle: "italic", marginTop: 2 }}>most popular</span>
                </div>
              </button>
            </div>

            {/* Descriptor — explains what MA and TT do. Links to Account
                → Alerts and Boards. Italic-lowercase styling matches the
                eyebrow lines on other tab pages. */}
            <div style={{
              padding: "8px 0 12px",
              fontSize: 12,
              lineHeight: 1.5,
              color: textMuted,
              fontStyle: "italic",
              textAlign: "center",
              flexShrink: 0,
            }}>
              browse most affordable or trending trips to add{" "}
              <button
                onClick={() => {
                  if (registered) {
                    setAccountDefaultTab("alerts");
                    reset();
                    setMode("trips");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    setShowLogin(true);
                  }
                }}
                style={{
                  background: "none", border: "none", padding: 0,
                  color: teal, fontStyle: "italic", fontWeight: 700,
                  fontSize: "inherit", fontFamily: "inherit",
                  textDecoration: "underline", textUnderlineOffset: 2,
                  cursor: "pointer",
                }}>
                alerts
              </button>
              {" "}to your account or save locations to your{" "}
              <button
                onClick={() => {
                  if (registered) {
                    reset();
                    setMode("wishlist");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    setShowLogin(true);
                  }
                }}
                style={{
                  background: "none", border: "none", padding: 0,
                  color: teal, fontStyle: "italic", fontWeight: 700,
                  fontSize: "inherit", fontFamily: "inherit",
                  textDecoration: "underline", textUnderlineOffset: 2,
                  cursor: "pointer",
                }}>
                board
              </button>
              .
            </div>

            {/* ── Header bar (search input) ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "16px 0 12px",
              borderBottom: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.07)"}`,
              background: darkMode ? "rgba(6,10,20,0.95)" : "#FAFAF5",
              WebkitBackdropFilter: "blur(12px)", backdropFilter: "blur(12px)",
              flexShrink: 0,
            }}>
              {/* Search input */}
              <div style={{ position: "relative", flex: 1 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", opacity: 0.45 }}>
                  <circle cx="10.5" cy="10.5" r="7" stroke={teal} strokeWidth="2"/>
                  <path d="M16 16 L21 21" stroke={teal} strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="City, country, region or vibe…"
                  aria-label="Search destinations by city, country, region or vibe"
                  role="searchbox"
                  style={{
                    width: "100%", padding: "14px 44px 14px 46px",
                    borderRadius: 16, fontSize: 16, fontFamily: "inherit",
                    background: darkMode ? "rgba(27,58,92,0.084)" : "rgba(0,0,0,0.06)",
                    border: `1.5px solid ${searchQuery ? teal : (darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)")}`,
                    color: textPrimary, outline: "none", boxSizing: "border-box",
                    transition: "border-color 0.2s",
                  }}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} style={{
                    position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                    background: darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.08)",
                    border: "none", borderRadius: "50%", width: 22, height: 22,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", color: textMuted, fontSize: 12, lineHeight: 1,
                  }}>✕</button>
                )}
              </div>
              {/* Cancel — progressive: clears active filter first, exits second */}
              <button onClick={() => {
                if (searchQuery) {
                  // First Cancel: just clear the filter, stay on Explore main
                  setSearchQuery("");
                } else {
                  // No active filter: exit Explore entirely
                  setMode(null);
                  setBottomTab(null);
                }
              }} style={{
                background: "none", border: "none", color: teal, fontSize: 15,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, padding: "4px 0",
              }}>Cancel</button>
            </div>

            {/* ── Scrollable body ── */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 0 120px" }}>
              {(() => {
                const q = searchQuery.trim().toLowerCase();
                const flagMap = {
                  "Austria":"🇦🇹","Belgium":"🇧🇪","Croatia":"🇭🇷","Czech Republic":"🇨🇿","Denmark":"🇩🇰","Finland":"🇫🇮","France":"🇫🇷","Germany":"🇩🇪","Greece":"🇬🇷","Hungary":"🇭🇺","Iceland":"🇮🇸","Ireland":"🇮🇪","Italy":"🇮🇹","Malta":"🇲🇹","Netherlands":"🇳🇱","Norway":"🇳🇴","Poland":"🇵🇱","Portugal":"🇵🇹","Romania":"🇷🇴","Spain":"🇪🇸","Sweden":"🇸🇪","Switzerland":"🇨🇭","United Kingdom":"🇬🇧","Cambodia":"🇰🇭","China":"🇨🇳","India":"🇮🇳","Indonesia":"🇮🇩","Japan":"🇯🇵","Malaysia":"🇲🇾","Maldives":"🇲🇻","Nepal":"🇳🇵","Philippines":"🇵🇭","Singapore":"🇸🇬","South Korea":"🇰🇷","Sri Lanka":"🇱🇰","Taiwan":"🇹🇼","Thailand":"🇹🇭","Vietnam":"🇻🇳","Botswana":"🇧🇼","Egypt":"🇪🇬","Ethiopia":"🇪🇹","Ghana":"🇬🇭","Kenya":"🇰🇪","Morocco":"🇲🇦","Namibia":"🇳🇦","Nigeria":"🇳🇬","Rwanda":"🇷🇼","Senegal":"🇸🇳","South Africa":"🇿🇦","Tanzania":"🇹🇿","Tunisia":"🇹🇳","Zimbabwe":"🇿🇼","Bahrain":"🇧🇭","Israel":"🇮🇱","Jordan":"🇯🇴","Kuwait":"🇰🇼","Lebanon":"🇱🇧","Oman":"🇴🇲","Qatar":"🇶🇦","Saudi Arabia":"🇸🇦","UAE":"🇦🇪","Australia":"🇦🇺","Fiji":"🇫🇯","New Zealand":"🇳🇿","USA":"🇺🇸","Canada":"🇨🇦","Mexico":"🇲🇽","Argentina":"🇦🇷","Brazil":"🇧🇷","Chile":"🇨🇱","Colombia":"🇨🇴","Peru":"🇵🇪",
                };
                const seen = new Set(DESTINATIONS.map(d => d.name));
                const countryPool = Object.entries(flagMap).filter(([c]) => !seen.has(c)).map(([name, emoji]) => ({ name, emoji, country: name, state: null, region: "Anywhere", subRegion: null, minBudget: null, maxBudget: null, tags: [], vibe: "", isCountry: true }));
                const allSearchable = [...DESTINATIONS, ...countryPool];
                const airport = expDepartureAirport || blindDepartureAirport || user?.departureAirport || null;
                // Cost for FILTERING + SORTING — stable 5-day baseline. This keeps
                // the visible destination list put when the user adjusts days on
                // an individual card. Without this stability, raising days on a
                // card would push its cost above the pill cap and the card would
                // vanish mid-edit (disorienting).
                const getBaselineFilterCost = (d) => {
                  if (!d.minBudget) return null; // country-only entries
                  return getBaselineTripCost(d, airport, 5, 1);
                };
                // Cost for per-card DISPLAY — respects the user's chosen days
                // (explorePerCardDays). The over-budget indicator on each card
                // uses this so the user sees real cost feedback as they adjust,
                // without losing the card from view.
                const getCardCost = (d) => {
                  if (!d.minBudget) return null; // country-only entries
                  const days = explorePerCardDays[d.name] || 5;
                  return getBaselineTripCost(d, airport, days, 1);
                };
                // Filter by vibe-or-search text first, then by budget pill (if set).
                const textMatched = q.length >= 1 ? allSearchable.filter(d =>
                  d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q) ||
                  (d.state || "").toLowerCase().includes(q) || (d.region || "").toLowerCase().includes(q) ||
                  d.tags.some(t => t.includes(q)) || (d.vibe || "").toLowerCase().includes(q)
                ) : [];
                // Apply budget pill filter with auto-loosen. If the user's
                // chosen pill yields zero results but vibe-matched text results
                // exist, escalate to the next pill until results appear (or
                // we hit '3K+'). Track which pill we landed on so we can both
                // sync state and show an explanation.
                const pillOrder = ["<1K", "1-2K", "2-3K", "3K+"];
                const computeResultsForPill = (pill) => {
                  // Helper: sort filtered destinations by cost ascending.
                  // Country-only entries (no minBudget → null cost) sort last
                  // so they don't clutter the top of the list. Uses STABLE
                  // baseline cost so reordering doesn't happen when user
                  // adjusts days on individual cards.
                  const sortByCost = (arr) => [...arr].sort((a, b) => {
                    const ca = getBaselineFilterCost(a);
                    const cb = getBaselineFilterCost(b);
                    if (ca === null && cb === null) return 0;
                    if (ca === null) return 1;
                    if (cb === null) return -1;
                    return ca - cb;
                  });
                  if (pill === null) return sortByCost(textMatched).slice(0, 24);
                  const cap = pill === "<1K" ? 1000
                             : pill === "1-2K" ? 2000
                             : pill === "2-3K" ? 3000
                             : Infinity;
                  if (pill === "3K+") {
                    return sortByCost(textMatched.filter(d => {
                      if (!d.minBudget) return true;
                      const cost = getBaselineFilterCost(d);
                      return cost === null || cost >= 3000;
                    })).slice(0, 24);
                  }
                  return sortByCost(textMatched.filter(d => {
                    if (!d.minBudget) return true;
                    const cost = getBaselineFilterCost(d);
                    return cost === null || cost <= cap;
                  })).slice(0, 24);
                };
                let effectivePill = exploreBudgetPill;
                let results = computeResultsForPill(effectivePill);
                // Track empty-fallback state: when the user's pill yields zero
                // matches but vibe-matched destinations exist at higher prices,
                // show the closest 12 sorted by cost ascending. Each card's
                // verdict pill already tells the user how much over budget
                // they are. The pill state is NOT silently changed — what
                // the user picked stays picked.
                let showingClosestOver = false;
                if (results.length === 0 && textMatched.length > 0 && effectivePill !== null) {
                  // Sort all vibe matches by cost (cheapest first), cap at 12.
                  // Uses baseline cost so this list is stable when user adjusts days.
                  const sortedByCost = [...textMatched]
                    .filter(d => d.minBudget) // skip country-only entries
                    .map(d => ({ d, cost: getBaselineFilterCost(d) }))
                    .filter(x => x.cost !== null)
                    .sort((a, b) => a.cost - b.cost)
                    .slice(0, 12)
                    .map(x => x.d);
                  if (sortedByCost.length > 0) {
                    results = sortedByCost;
                    showingClosestOver = true;
                  }
                }

                const vibes = [
                  { label: "Adventure", q: "adventure", color: VIBE_COLORS.adventure, icon: VIBE_ICONS.adventure },
                  { label: "Beach",     q: "beach",     color: VIBE_COLORS.beach,     icon: VIBE_ICONS.beach },
                  { label: "Culture",   q: "culture",   color: VIBE_COLORS.culture,   icon: VIBE_ICONS.culture },
                  { label: "Food",      q: "food",      color: VIBE_COLORS.food,      icon: VIBE_ICONS.food },
                  { label: "Luxury",    q: "luxury",    color: VIBE_COLORS.luxury,    icon: VIBE_ICONS.luxury },
                  { label: "Nature",    q: "nature",    color: VIBE_COLORS.nature,    icon: VIBE_ICONS.nature },
                  { label: "Romance",   q: "romance",   color: VIBE_COLORS.romance,   icon: VIBE_ICONS.romance },
                  { label: "Wellness",  q: "wellness",  color: VIBE_COLORS.wellness,  icon: VIBE_ICONS.wellness },
                ];

                const regions = ["Africa", "Americas", "Asia", "Europe", "Middle East", "Oceania"];

                if (q.length >= 1 && results.length === 0) return (
                  <div style={{ textAlign: "center", paddingTop: 60 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🌍</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>No results for "{searchQuery}"</div>
                    <div style={{ fontSize: 14, color: textMuted }}>Try a city, country, or vibe like "beach" or "culture"</div>
                  </div>
                );

                if (results.length > 0) return (
                  <div>
                    {/* Budget pills row — let users narrow without leaving Explore */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 8 }}>Filter by budget</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {[
                          { id: null,    label: "Any" },
                          { id: "<1K",   label: "Under $1K" },
                          { id: "1-2K",  label: "$1-2K" },
                          { id: "2-3K",  label: "$2-3K" },
                          { id: "3K+",   label: "$3K+" },
                        ].map(p => {
                          const active = exploreBudgetPill === p.id;
                          return (
                            <button key={p.label} onClick={() => setExploreBudgetPill(p.id)}
                              style={{
                                padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                                border: `1px solid ${active ? teal : (darkMode ? "rgba(27,58,92,0.18)" : "rgba(0,0,0,0.14)")}`,
                                background: active ? teal : (darkMode ? "rgba(27,58,92,0.06)" : "rgba(0,0,0,0.03)"),
                                color: active ? "#fff" : textMuted,
                                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                              }}>
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 12 }}>
                      {results.length} result{results.length !== 1 ? "s" : ""}
                    </div>
                    {showingClosestOver && (
                      <div style={{
                        background: "rgba(27,58,92,0.07)",
                        border: "1px solid rgba(27,58,92,0.18)",
                        borderRadius: 10, padding: "10px 12px", marginBottom: 10,
                        fontSize: 12, color: textMuted, lineHeight: 1.4,
                      }}>
                        Unfortunately there are no matches {effectivePill === "<1K" ? "under $1K" : effectivePill === "1-2K" ? "between $1-2K" : effectivePill === "2-3K" ? "between $2-3K" : "for this filter"}{airport ? ` from ${airport}` : ""}. <span style={{ fontStyle: "italic" }}>Below are some close options.</span>
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {results.map(d => {
                        // Per-destination day floor — short-haul (≤4h flight)
                        // allows 2-day weekend trips; longer flights require
                        // 3-day minimum so users don't book trips where most of
                        // the time is in transit.
                        const flightHrs = estimateFlightDurationHours(d, airport);
                        const minDays = (flightHrs !== null && flightHrs <= 4) ? 2 : 3;
                        const days = explorePerCardDays[d.name] || Math.max(5, minDays);
                        const cost = getCardCost(d);
                        const userBudgetInt = parseInt(String(budget).replace(/[^0-9]/g,""),10) || parseInt(String(dashboardBudget || "").replace(/[^0-9]/g,""),10) || null;
                        const overBy = (cost !== null && userBudgetInt !== null && cost > userBudgetInt) ? cost - userBudgetInt : 0;
                        const fits = cost !== null && userBudgetInt !== null && cost <= userBudgetInt;
                        const adjustDays = (delta) => {
                          const newDays = Math.max(minDays, Math.min(14, days + delta));
                          setExplorePerCardDays(prev => ({ ...prev, [d.name]: newDays }));
                        };
                        return (
                          <div key={d.name} style={{
                            background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                            border: `1px solid ${darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.06)"}`,
                            borderRadius: 14, padding: "14px 16px", fontFamily: "inherit",
                          }}>
                            {/* Top row — clickable area opens destination */}
                            <button onClick={() => {
                              const _region = d.region || "Anywhere";
                              const _sub = d.subRegion || null;
                              const _country = d.isCountry ? d.country : (d.country || null);
                              const _state = d.state || null;
                              // Over-budget interstitial: if destination needs more money
                              // than user's current budget, surface the choice instead of
                              // dropping into TMA where it would say '0 matches'.
                              if (overBy > 0 && d.minBudget) {
                                setExploreOverBudget({ dest: d, neededBudget: cost, currentBudget: userBudgetInt, days });
                                return;
                              }
                              setSearchQuery(""); setBottomTab(null); reset();
                              setExpRegion(_region); setExpSub(_sub); setExpCountry(_country); setExpState(_state);
                              // If user adjusted days on the card, carry that into TMA.
                              if (explorePerCardDays[d.name]) setDays(explorePerCardDays[d.name]);
                              setMode("explore");
                            }} style={{
                              width: "100%", display: "flex", alignItems: "center", gap: 16,
                              background: "transparent", border: "none", padding: 0,
                              cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                            }}>
                              <span style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{d.emoji}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>{d.name}</div>
                                <div style={{ fontSize: 13, color: textMuted, marginTop: 1 }}>
                                  {d.state ? `${d.state} · ` : ""}{d.isCountry ? d.region : d.country}
                                  {d.vibe ? ` · ${d.vibe}` : ""}
                                  {(() => {
                                    const hrs = estimateFlightDurationHours(d, airport);
                                    return (hrs !== null && hrs >= 8) ? (
                                      <span style={{ color: "#1B3A5C", fontStyle: "italic" }}>, ~{hrs}h flight</span>
                                    ) : null;
                                  })()}
                                </div>
                              </div>
                              <div style={{ flexShrink: 0, textAlign: "right" }}>
                                {d.minBudget ? (
                                  <>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1B3A5C" }}>${cost?.toLocaleString()}</div>
                                    <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{days}d · flight+stay+food</div>
                                  </>
                                ) : (
                                  <div style={{ fontSize: 12, color: textDim }}>Explore →</div>
                                )}
                              </div>
                            </button>

                            {/* Bottom row — day stepper + verdict (only for full destinations with budgets) */}
                            {d.minBudget && (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.08)" : "rgba(0,0,0,0.05)"}` }}>
                                {/* Day stepper */}
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <button onClick={() => adjustDays(-1)} disabled={days <= minDays} aria-label="Decrease days"
                                    style={{
                                      width: 28, height: 28, borderRadius: 8,
                                      border: `1px solid ${darkMode ? "rgba(27,58,92,0.2)" : "rgba(0,0,0,0.12)"}`,
                                      background: darkMode ? "rgba(27,58,92,0.06)" : "#fff",
                                      color: days <= minDays ? textDim : textPrimary,
                                      fontSize: 16, fontWeight: 800, cursor: days <= minDays ? "not-allowed" : "pointer",
                                      fontFamily: "inherit", lineHeight: 1, padding: 0,
                                    }}>−</button>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, minWidth: 56, textAlign: "center" }}>{days} days</div>
                                  <button onClick={() => adjustDays(1)} disabled={days >= 14} aria-label="Increase days"
                                    style={{
                                      width: 28, height: 28, borderRadius: 8,
                                      border: `1px solid ${darkMode ? "rgba(27,58,92,0.2)" : "rgba(0,0,0,0.12)"}`,
                                      background: darkMode ? "rgba(27,58,92,0.06)" : "#fff",
                                      color: days >= 14 ? textDim : textPrimary,
                                      fontSize: 16, fontWeight: 800, cursor: days >= 14 ? "not-allowed" : "pointer",
                                      fontFamily: "inherit", lineHeight: 1, padding: 0,
                                    }}>+</button>
                                </div>
                                {/* Budget verdict */}
                                {userBudgetInt && cost !== null && (
                                  fits ? (
                                    <div style={{ fontSize: 11, fontWeight: 800, color: "#1B3A5C", background: "rgba(42,106,42,0.10)", padding: "4px 10px", borderRadius: 14 }}>
                                      ✓ Fits your budget
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: 11, fontWeight: 800, color: "#1B3A5C", background: "rgba(27,58,92,0.10)", padding: "4px 10px", borderRadius: 14 }}>
                                      ${overBy.toLocaleString()} over
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );

                // Empty state — vibe pills + region shortcuts
                return (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 14 }}>Browse by vibe</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 32 }}>
                      {vibes.map(v => (
                        <button key={v.q} onClick={() => setSearchQuery(v.q)} style={{
                          padding: "16px 14px", borderRadius: 16, border: `1px solid ${darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"}`,
                          background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                          color: textPrimary, fontSize: 15, fontWeight: 600, cursor: "pointer",
                          fontFamily: "inherit", textAlign: "left", transition: "all 0.15s",
                          display: "flex", alignItems: "center", gap: 10,
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = `${v.color}99`; e.currentTarget.style.background = `${v.color}10`; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"; }}>
                          <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{v.icon(v.color)}</span>
                          <span>{v.label}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 14 }}>Browse by region</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {regions.map(r => (
                        <button key={r} onClick={() => {
                          setBottomTab(null); reset();
                          setExpRegion(r); setExpSub(null); setExpCountry(null); setExpState(null);
                          setMode("explore");
                        }} style={{
                          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "15px 16px", borderRadius: 14, border: "none",
                          background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                          color: textPrimary, fontSize: 15, fontWeight: 600, cursor: "pointer",
                          fontFamily: "inherit", transition: "background 0.15s",
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.08)" : "rgba(27,58,92,0.06)"}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"}>
                          <span>{r}</span>
                          <span style={{ color: textDim, fontSize: 16 }}>›</span>
                        </button>
                      ))}
                    </div>

                    {/* Travel facts — restored Jun 19, 2026. Lives here under
                       Browse by Region per Cas's call. Surfaces the funFact
                       fields that have been sitting in POSTCARDS_CATALOG. */}
                    <TravelFactsDropdown catalog={POSTCARDS_CATALOG} />

                    {/* ── Train Trips Across America ── */}
                    <div style={{ marginTop: 32 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: textPrimary, letterSpacing: -0.2 }}>Train Trips Across America</h3>
                        <span style={{ fontSize: 18 }} aria-hidden="true">🚂</span>
                      </div>
                      <p style={{ fontSize: 13, color: textMuted, fontStyle: "italic", marginBottom: 18, lineHeight: 1.5 }}>
                        Slow travel, no airports, no security lines. All prices shown are <strong style={{ color: textPrimary, fontStyle: "normal" }}>one-way starting fares</strong> — double for a round-trip estimate. Book on Amtrak.com.
                      </p>

                      {/* Great Routes */}
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 12 }}>
                        The Great Routes
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                        {TRAIN_TRIPS.filter(t => t.tier === "great_route").map(t => (
                          <div key={t.id} style={{
                            background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                            border: `1px solid ${darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.06)"}`,
                            borderRadius: 14, padding: "14px 16px",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                              <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>{t.name}</div>
                              <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", flexShrink: 0 }}>{t.duration}</div>
                            </div>
                            <div style={{ fontSize: 13, color: textMuted, marginBottom: 10 }}>{t.route}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                              {t.highlights.map((h, i) => (
                                <div key={i} style={{ fontSize: 12, color: textPrimary, lineHeight: 1.45, paddingLeft: 12, position: "relative" }}>
                                  <span style={{ position: "absolute", left: 0, color: "#1B3A5C" }}>·</span>
                                  {h}
                                </div>
                              ))}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 11, color: textMuted, paddingTop: 8, borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.08)" : "rgba(0,0,0,0.05)"}` }}>
                              <span><strong style={{ color: textPrimary }}>Coach</strong> from ${t.coachFrom} <span style={{ color: textDim, fontSize: 10 }}>one-way</span></span>
                              {t.roometteFrom && <span><strong style={{ color: textPrimary }}>Roomette</strong> from ${t.roometteFrom} <span style={{ color: textDim, fontSize: 10 }}>one-way</span></span>}
                            </div>
                            <div style={{ fontSize: 10, color: textDim, marginTop: 8, fontStyle: "italic" }}>
                              {t.stations}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Easy Connections */}
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: textDim, textTransform: "uppercase", marginBottom: 12 }}>
                        Easy Connections
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                        {TRAIN_TRIPS.filter(t => t.tier === "easy_connection").map(t => (
                          <div key={t.id} style={{
                            background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                            border: `1px solid ${darkMode ? "rgba(27,58,92,0.12)" : "rgba(0,0,0,0.06)"}`,
                            borderRadius: 14, padding: "14px 16px",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                              <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>{t.name}</div>
                              <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", flexShrink: 0 }}>{t.duration}</div>
                            </div>
                            <div style={{ fontSize: 13, color: textMuted, marginBottom: 10 }}>{t.route}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                              {t.highlights.map((h, i) => (
                                <div key={i} style={{ fontSize: 12, color: textPrimary, lineHeight: 1.45, paddingLeft: 12, position: "relative" }}>
                                  <span style={{ position: "absolute", left: 0, color: "#1B3A5C" }}>·</span>
                                  {h}
                                </div>
                              ))}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 11, color: textMuted, paddingTop: 8, borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.08)" : "rgba(0,0,0,0.05)"}` }}>
                              <span><strong style={{ color: textPrimary }}>Coach</strong> from ${t.coachFrom} <span style={{ color: textDim, fontSize: 10 }}>one-way</span></span>
                              {t.businessFrom && <span><strong style={{ color: textPrimary }}>Business</strong> from ${t.businessFrom} <span style={{ color: textDim, fontSize: 10 }}>one-way</span></span>}
                            </div>
                            <div style={{ fontSize: 10, color: textDim, marginTop: 8, fontStyle: "italic" }}>
                              {t.stations}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", lineHeight: 1.5, paddingTop: 12, borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.10)" : "rgba(0,0,0,0.06)"}` }}>
                        Prices shown are typical baseline fares — actual cost varies on{" "}
                        <span style={{ color: "#1B3A5C", fontWeight: 700 }}>Amtrak.com</span>{" "}
                        by date, demand, and how far ahead you book.
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ══════════ EXPLORE MODE ══════════ */}
        {mode === "explore" && (
          <div>
            {/* NOTE: This wrapper used to have `animation: fadeIn 0.3s ease`,
                but the keyframe's translateY transform created a containing
                block that broke position: sticky on the match-counter pill
                inside. Removed entirely — the cosmetic loss is minimal. */}
            {/* Hero header — left-aligned with animated icon on the right.
                Matches the unified page-header pattern (My Trips, Trending Trips,
                Most Affordable). */}
            <div style={{ marginBottom: 26 }}>
              <button onClick={() => { reset(); setMode("search"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "4px 0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                ← Back to Explore
              </button>
              {/* Spinning wheel */}
              <style>{`
                @keyframes spinWheel { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .jga-wheel { animation: spinWheel 8s linear infinite; transform-origin: 50% 50%; }
        @media (prefers-reduced-motion: reduce) { .jga-wheel { animation: none; } }
                .jga-wheel:hover { animation-duration: 1.2s; }
              `}</style>
              <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>set your budget, pick your vibe, we find the match</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: goldText, letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase", flex: 1, minWidth: 0 }}>Take Me Anywhere</h2>
                <div style={{ width: 72, height: 72, position: "relative", flexShrink: 0 }}>
                  <svg className="jga-wheel" width="72" height="72" viewBox="0 0 90 90" fill="none">
                    {/* Outer ring */}
                    <circle cx="45" cy="45" r="42" fill={`${amber}15`} stroke={gold} strokeWidth="2"/>
                    {/* Wheel segments — 8 slices */}
                    {[0,1,2,3,4,5,6,7].map(i => {
                      const angle = (i * 45) * Math.PI / 180;
                      const nextAngle = ((i + 1) * 45) * Math.PI / 180;
                      const r = 38;
                      const x1 = 45 + r * Math.cos(angle);
                      const y1 = 45 + r * Math.sin(angle);
                      const x2 = 45 + r * Math.cos(nextAngle);
                      const y2 = 45 + r * Math.sin(nextAngle);
                      const colors = ["#4A8CC4","#1B3A5C","#4A8CC4","#1B3A5C","#4A8CC4","#1B3A5C","#4A8CC4","#1B3A5C"];
                      return (
                        <g key={i}>
                          <path d={`M45,45 L${x1},${y1} A${r},${r} 0 0,1 ${x2},${y2} Z`} fill={colors[i]} opacity="0.22" stroke={gold} strokeWidth="0.8"/>
                          {/* Destination flags on alternating segments */}
                          {i % 2 === 0 && (
                            <text
                              x={45 + 26 * Math.cos((i * 45 + 22.5) * Math.PI / 180)}
                              y={45 + 26 * Math.sin((i * 45 + 22.5) * Math.PI / 180)}
                              textAnchor="middle" dominantBaseline="middle" fontSize="13">
                              {["🗺️","✈️","🌴","🏛️"][i/2]}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    {/* Divider lines */}
                    {[0,1,2,3,4,5,6,7].map(i => {
                      const angle = (i * 45) * Math.PI / 180;
                      return <line key={i} x1="45" y1="45" x2={45 + 42 * Math.cos(angle)} y2={45 + 42 * Math.sin(angle)} stroke={gold} strokeWidth="0.8" opacity="0.4"/>;
                    })}
                    {/* Center hub */}
                    <circle cx="45" cy="45" r="9" fill={`${amber}30`} stroke={gold} strokeWidth="1.8"/>
                    <circle cx="45" cy="45" r="3.5" fill={gold}/>
                  </svg>
                  {/* Pointer arrow */}
                  <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)", fontSize: 14, lineHeight: 1 }}>▼</div>
                </div>
              </div>
            </div>
            {/* ── Airport row (always shown) ── */}
            {(() => {
              const currentAirport = expDepartureAirport;
              if (!currentAirport) {
                return (
                  <div id="exp-airport-banner" style={{ background: darkMode ? "rgba(27,58,92,0.04)" : "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 18, display: "flex", alignItems: "center", gap: 14 }}>
                    <svg width="28" height="28" viewBox="0 0 38 48" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="rgba(27,58,92,0.10)" stroke="#1B3A5C" strokeWidth="2"/>
                      <circle cx="19" cy="18" r="7" fill="none" stroke="#1B3A5C" strokeWidth="1.8"/>
                      <circle cx="19" cy="18" r="2.5" fill="#1B3A5C" opacity="0.9"/>
                    </svg>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A5C", marginBottom: 3 }}>Add your departure airport for accurate pricing</div>
                      <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.5 }}>Without it, budget estimates are averages and won't reflect actual flight costs from your city.</div>
                    </div>
                    <button onClick={() => detectAirport((code) => { setExpDepartureAirport(code); setBlindDepartureAirport(code); })}
                      style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#E89A3C,#C8741D)", color: "#FAFAF5", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <svg width="11" height="13" viewBox="0 0 38 48" fill="none" aria-hidden="true">
                        <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="none" stroke="#FAFAF5" strokeWidth="2.8"/>
                        <circle cx="19" cy="18" r="3.5" fill="#FAFAF5"/>
                      </svg>
                      Detect
                    </button>
                  </div>
                );
              }
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px", borderRadius: 12, border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}`, background: darkMode ? "rgba(27,58,92,0.04)" : "rgba(27,58,92,0.06)", marginBottom: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <svg width="14" height="16" viewBox="0 0 38 48" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="none" stroke="#1B3A5C" strokeWidth="2.5"/>
                      <circle cx="19" cy="18" r="3.5" fill="#1B3A5C"/>
                    </svg>
                    <span style={{ fontSize: 11, color: textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>From</span>
                    <span style={{ fontSize: 14, fontWeight: 900, color: "#1B3A5C", letterSpacing: 1.5 }}>{String(currentAirport).slice(0, 3).toUpperCase()}</span>
                  </div>
                  <button onClick={() => detectAirport((code) => { setExpDepartureAirport(code); setBlindDepartureAirport(code); })}
                    style={{ padding: "5px 11px", borderRadius: 14, border: "1px solid rgba(27,58,92,0.4)", background: "transparent", color: "#1B3A5C", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <svg width="9" height="11" viewBox="0 0 38 48" fill="none" aria-hidden="true">
                      <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill="none" stroke="#1B3A5C" strokeWidth="2.8"/>
                      <circle cx="19" cy="18" r="3.5" fill="#1B3A5C"/>
                    </svg>
                    Re-detect
                  </button>
                </div>
              );
            })()}

            {/* Step pills */}
            <div id="exp-step-pills" style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 22 }}>
              {["Budget & Location","Dates","Spin"].map((s, i) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: step === i ? `linear-gradient(135deg,${gold},${amber})` : step > i ? darkMode ? "#1e3a1e" : "#d4f0d4" : darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.07)", color: step === i ? "#1a0a00" : step > i ? "#2a7a2a" : textDim, border: step > i ? `1px solid ${darkMode ? "#3a7a3a" : "#6dbd6d"}` : `1px solid ${borderColor}` }}>{step > i ? "✓" : i + 1}</div>
                  <span style={{ fontSize: 10, color: step >= i ? (darkMode ? "#4A8CC4" : "#4A8CC4") : textDim, letterSpacing: 1, textTransform: "uppercase" }}>{s}</span>
                  {i < 2 && <div style={{ width: 16, height: 1, background: darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)" }} />}
                </div>
              ))}
            </div>

            <div style={dynCard}>
              {/* Step 0 — Budget + Location */}
              {step === 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <svg width="26" height="32" viewBox="0 0 38 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z" fill={`${amber}22`} stroke={gold} strokeWidth="2"/>
                      <circle cx="19" cy="18" r="7" fill={`${amber}30`} stroke={gold} strokeWidth="1.8"/>
                      <ellipse cx="19" cy="18" rx="4" ry="7" fill="none" stroke={gold} strokeWidth="1" opacity="0.5"/>
                      <path d="M12 18 Q15.5 16 19 18 Q22.5 20 26 18" stroke={gold} strokeWidth="1" fill="none" opacity="0.5"/>
                      <circle cx="19" cy="18" r="2.5" fill={gold} opacity="0.8"/>
                    </svg>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Let's Go Somewhere!</h2>
                  </div>
                  <p style={{ color: textMuted, fontSize: 13, marginBottom: 22, fontStyle: "italic" }}>Per person — tell us what's included</p>

                  {/* Locked-trip banner — shown when user picked a specific
                      destination (e.g. from home State 7) and wants to set
                      budget/dates before generation. Skips the matching flow
                      entirely; the button at the bottom becomes "Generate
                      itinerary for {dest}". */}
                  {lockedTripDest && (
                    <div style={{
                      background: "linear-gradient(135deg, rgba(232,154,60,0.12) 0%, rgba(232,154,60,0.06) 100%)",
                      border: "1px solid rgba(232,154,60,0.4)",
                      borderRadius: 12,
                      padding: "12px 16px",
                      marginBottom: 16,
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <div style={{ fontSize: 26, flexShrink: 0 }}>{lockedTripDest.emoji || "📍"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C8741D", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>
                          PLANNING YOUR TRIP TO
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.2 }}>
                          {lockedTripDest.name}
                          {lockedTripDest.country && <span style={{ fontSize: 12, fontWeight: 500, color: textMuted, marginLeft: 6 }}>· {lockedTripDest.country}</span>}
                        </div>
                      </div>
                      <button onClick={() => setLockedTripDest(null)}
                        aria-label="Cancel locked destination"
                        style={{ background: "transparent", border: "none", color: textMuted, fontSize: 18, cursor: "pointer", padding: "4px 6px", lineHeight: 1, flexShrink: 0 }}>×</button>
                    </div>
                  )}

                  {/* Match count — inline render. Floating/sticky/portal
                       approaches all caused issues (see deferred-ideas).
                       Per-section counts at Transport and Activities are
                       the user's primary signal while toggling deeper.
                       HIDDEN when a locked destination is set — matching
                       is irrelevant when the user already picked a place. */}
                  {!lockedTripDest && (() => {
                    const matchCount = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                    return (
                      <div style={{
                        background: matchCount === 0
                          ? "linear-gradient(135deg, rgba(250,245,232,0.97) 0%, rgba(240,229,207,0.97) 100%)"
                          : "rgba(74,140,196,0.10)",
                        borderRadius: 10,
                        padding: "10px 14px",
                        marginBottom: 16,
                        border: `1px solid ${matchCount === 0 ? "rgba(27,58,92,0.22)" : "rgba(74,140,196,0.3)"}`,
                        fontSize: 13,
                        color: matchCount === 0 ? "#1B3A5C" : "#1B3A5C",
                        textAlign: "center",
                        fontWeight: 700,
                      }}>
                        {matchCount === 0 ? "0 destinations match — adjust your filters below" : (
                          <>
                            {matchCount} destinations match
                            {matchDelta !== null && matchDelta !== 0 && (
                              <span style={{
                                marginLeft: 8,
                                fontSize: 12,
                                fontWeight: 700,
                                opacity: 0.85,
                                color: matchDelta > 0 ? "#1B3A5C" : "#1B3A5C",
                                animation: "fadeIn 0.25s ease",
                              }}>
                                ({matchDelta > 0 ? "+" : "−"}{Math.abs(matchDelta)})
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Rooming-assumption disclaimer — keeps the estimate honest
                      without cluttering the surface. Only shown when there are
                      matches (it explains the prices being displayed). The real
                      rooming choice happens at booking. */}
                  {(() => {
                    const mc = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                    if (mc === 0) return null;
                    return (
                      <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", textAlign: "center", marginTop: -8, marginBottom: 16, lineHeight: 1.5 }}>
                        Estimates assume about 2 guests per room. You'll set exact rooming at booking.
                      </div>
                    );
                  })()}

                  {/* How it works — TMA primer */}
                  <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.18)", borderRadius: 12, padding: "12px 16px", marginBottom: 22, fontSize: 13, color: "#1B3A5C", lineHeight: 1.6 }}>
                    <strong style={{ color: teal }}>How it works:</strong> Tell us your budget, region, and vibe — we'll match you with destinations that fit. You choose the one that calls to you, then we help you book it.
                  </div>

                  <Label>Number of Travelers</Label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
                    {[
                      { n: 1, svg: JGA_ICONS.traveller_solo,   label: "Solo" },
                      { n: 2, svg: JGA_ICONS.traveller_couple, label: "2" },
                      { n: 3, svg: JGA_ICONS.traveller_three,  label: "3" },
                      { n: 4, svg: JGA_ICONS.traveller_four,   label: "4" },
                    ].map(({ n, svg, label }) => (
                      <Pill key={n} active={travelers === n} color={teal} onClick={() => {
                        setTravelers(n);
                        setTravelerAges([]);
                        setTravelingWithChildren(false);
                        // Set sensible default vibe for the count so matching has
                        // something to score against. User can override via the
                        // vibe sub-row that appears below.
                        const defaultVibe = getDefaultVibeForCount(n);
                        if (defaultVibe) setTravelCompanion(defaultVibe);
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{svg(travelers === n ? teal : "#4A6B8A", 16)}</span>
                          {label}
                        </span>
                      </Pill>
                    ))}
                  </div>
                  {/* Vibe sub-row — appears for n=2 (Romantic/Friends) or n=3-4
                      (Friends/Family). Drives companionFit scoring so a friends
                      pair doesn't get romance-coded matches and vice versa. */}
                  {travelers >= 2 && getVibeOptionsForCount(travelers).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 16, paddingLeft: 2 }}>
                      <span style={{ fontSize: 11, color: textDim, alignSelf: "center", marginRight: 2, fontStyle: "italic" }}>Vibe:</span>
                      {getVibeOptionsForCount(travelers).map(opt => (
                        <Pill key={opt.id} active={travelCompanion === opt.id} color={teal} onClick={() => setTravelCompanion(opt.id)} small>
                          {opt.label}
                        </Pill>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                    <button onClick={() => setTravelingWithPet(p => !p)} style={{ display: "flex", alignItems: "center", gap: 8, background: travelingWithPet ? "rgba(27,58,92,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: `1px solid ${travelingWithPet ? "rgba(27,58,92,0.4)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", width: "fit-content" }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${travelingWithPet ? teal : "#F0EDE3"}`, background: travelingWithPet ? teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {travelingWithPet && <span style={{ color: "#FAFAF5", fontSize: 10, fontWeight: 900 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 13, color: travelingWithPet ? teal : "#4A6B8A" }}>🐾 Traveling with a pet</span>
                    </button>
                    {travelers >= 3 && (
                      <button onClick={() => setTravelingWithChildren(c => !c)} style={{ display: "flex", alignItems: "center", gap: 8, background: travelingWithChildren ? "rgba(74,140,196,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: `1px solid ${travelingWithChildren ? "rgba(74,140,196,0.4)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", width: "fit-content" }}>
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${travelingWithChildren ? gold : "#F0EDE3"}`, background: travelingWithChildren ? gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {travelingWithChildren && <span style={{ color: "#FAFAF5", fontSize: 10, fontWeight: 900 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 13, color: travelingWithChildren ? gold : "#4A6B8A" }}>👶 Traveling with children?</span>
                      </button>
                    )}
                  </div>

                  <Label>Your Budget</Label>
                  <div style={{ position: "relative", marginBottom: 10 }}>
                    <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontSize: 20, fontWeight: 700, color: amber, pointerEvents: "none" }}>$</span>
                    <input type="text" inputMode="numeric" value={budget}
                      onChange={e => setBudget(e.target.value.replace(/[^\d]/g, ""))}
                      placeholder="e.g. 1000"
                      name="jga-tma-budget"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      style={{ width: "100%", padding: "13px 14px 13px 36px", borderRadius: 12, fontSize: 22, fontWeight: 800, fontFamily: "inherit", background: "rgba(74,140,196,0.07)", border: `1px solid ${budget ? "rgba(74,140,196,0.45)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: goldText, outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 24 }}>
                    {[600, 1000, 1500, 2000, 2500].map(b => <Pill key={b} active={budget === String(b)} onClick={() => setBudget(String(b))}>${b >= 1000 ? b / 1000 + "k" : b}</Pill>)}
                  </div>

                  <Label>What's the Vibe?</Label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 24 }}>
                    {VIBES.map(v => {
                      const on = expVibes.includes(v.id);
                      return (
                        <Pill key={v.id} active={on} color={v.color} onClick={() => {
                          const newVibes = on ? expVibes.filter(x => x !== v.id) : [...expVibes, v.id];
                          setExpVibes(newVibes);
                          setTmaActivePreset(null); // manual vibe change → exit preset mode
                          if (expIncludes.includes("activities")) {
                            const suggested = [...new Set(newVibes.flatMap(vid => VIBE_TO_ACTIVITIES[vid] || []))];
                            setExpActivityTypes(suggested);
                          }
                        }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{v.svg(v.color)}</span>
                            {v.label}
                          </span>
                        </Pill>
                      );
                    })}
                  </div>

                  {/* Date range — moved inline from former step 1 so users
                      see how dates interact with budget in a single view. */}
                  <Label>When are you going?</Label>
                  {/* Three-tab selector */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
                    {[
                      { id: "dates",    label: "📅 Exact Dates" },
                      { id: "month",    label: "🗓️ Pick Month" },
                      { id: "duration", label: "⏱️ Duration" },
                    ].map(opt => (
                      <button key={opt.id} onClick={() => { setExpDateMode(opt.id); setDepartDate(""); setReturnDate(""); setExpMonth(null); setDays(7); }}
                        style={{ flex: 1, padding: "9px 4px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: expDateMode === opt.id ? 700 : 400, border: `1px solid ${expDateMode === opt.id ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: expDateMode === opt.id ? "rgba(74,140,196,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: expDateMode === opt.id ? gold : "#4A6B8A", transition: "all 0.15s" }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Exact dates */}
                  {expDateMode === "dates" && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <Label>Departure</Label>
                          <input type="date" value={departDate}
                            min={getMinTravelDate()}
                            onChange={e => applyDepartChange(e.target.value, departDate, returnDate, setDepartDate, setReturnDate)}
                            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, fontSize: 14, fontFamily: "inherit", background: "rgba(74,140,196,0.07)", border: `1px solid ${departDate ? "rgba(74,140,196,0.45)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: departDate ? gold : "#4A6B8A", outline: "none", boxSizing: "border-box", cursor: "pointer", colorScheme: "dark" }} />
                        </div>
                        <div>
                          <Label>Return</Label>
                          <input type="date" value={returnDate}
                            min={departDate || getMinTravelDate()}
                            max={departDate ? addDaysISO(departDate, 21) : undefined}
                            onChange={e => {
                              // TMA imposes a 21-day trip ceiling — validate first, then delegate
                              // floor/snap to the shared helper for consistent behavior.
                              const v = e.target.value;
                              if (v && departDate && daysBetweenISO(departDate, v) > 21) return;
                              applyReturnChange(v, departDate, setReturnDate);
                            }}
                            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, fontSize: 14, fontFamily: "inherit", background: "rgba(27,58,92,0.07)", border: `1px solid ${returnDate ? "rgba(27,58,92,0.45)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: returnDate ? teal : "#4A6B8A", outline: "none", boxSizing: "border-box", cursor: "pointer", colorScheme: "dark" }} />
                        </div>
                      </div>
                      {departDate && returnDate && (() => {
                        const d = Math.round((new Date(returnDate) - new Date(departDate)) / 86400000);
                        if (d > 0) { if (days !== d) setDays(d); }
                        return d > 0 ? (
                          <div style={{ background: "rgba(27,58,92,0.07)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 12, padding: "12px 18px", textAlign: "center" }}>
                            <span style={{ fontSize: 26, fontWeight: 900, ...gradientTextStyle("linear-gradient(135deg,#a8d8ea,#4A8CC4)", "#4A8CC4") }}>{d}</span>
                            <span style={{ color: textMuted, fontSize: 14, marginLeft: 8 }}>{d === 1 ? "day" : "days"} ✈️</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Pick month */}
                  {expDateMode === "month" && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 6, marginBottom: 10 }}>
                        {MONTHS.map((m, i) => {
                          const isPast = i < new Date().getMonth();
                          const openYear = parseInt(BOOKING_OPEN_DATE.slice(0, 4), 10);
                          const openMonth = parseInt(BOOKING_OPEN_DATE.slice(5, 7), 10) - 1;
                          const currentYear = new Date().getFullYear();
                          const isPreLaunchMonth = currentYear === openYear && i < openMonth;
                          const isBlocked = isPast || isPreLaunchMonth;
                          return (
                            <button key={m}
                              onClick={() => { if (!isBlocked) setExpMonth(expMonth === i ? null : i); }}
                              disabled={isBlocked}
                              title={isPast ? "This month has passed" : isPreLaunchMonth ? "Coming soon" : undefined}
                              style={{ padding: "8px 4px", borderRadius: 8, fontSize: 12, textAlign: "center", fontFamily: "inherit",
                                cursor: isBlocked ? "not-allowed" : "pointer",
                                border: `1px solid ${expMonth === i ? gold : isBlocked ? "transparent" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                                background: expMonth === i ? "rgba(74,140,196,0.15)" : "transparent",
                                color: isBlocked ? (darkMode ? "rgba(27,58,92,0.16)" : "rgba(0,0,0,0.18)") : expMonth === i ? gold : "#4A6B8A",
                                textDecoration: isBlocked ? "line-through" : "none",
                                opacity: isBlocked ? 0.4 : 1,
                              }}>{m}</button>
                          );
                        })}
                      </div>
                      {expMonth !== null && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(74,140,196,0.12)" }}>
                          <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>How long?</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                            <input type="range" min={1} max={21} step={1} value={days} onChange={e => setDays(+e.target.value)} style={{ flex: 1, accentColor: gold, cursor: "pointer" }} />
                            <span style={{ color: goldText, fontWeight: 800, fontSize: 15, minWidth: 60 }}>{days} {days === 1 ? "day" : "days"}</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {[3, 5, 7, 10, 14, 21].map(d => (
                              <button key={d} onClick={() => setDays(d)}
                                style={{ padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 12, border: `1px solid ${days === d ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: days === d ? "rgba(74,140,196,0.12)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: days === d ? gold : "#4A6B8A" }}>
                                {d} days
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: 12, color: goldText, fontStyle: "italic", marginTop: 10 }}>
                            ✓ {MONTHS[expMonth]} · {days} {days === 1 ? "day" : "days"}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Duration */}
                  {expDateMode === "duration" && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                        <input type="range" min={1} max={21} step={1} value={days} onChange={e => setDays(+e.target.value)} style={{ flex: 1, accentColor: gold, cursor: "pointer" }} />
                        <span style={{ color: goldText, fontWeight: 800, fontSize: 16, minWidth: 60 }}>{days} {days === 1 ? "day" : "days"}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {[3, 5, 7, 10, 14, 21].map(d => (
                          <Pill key={d} active={days === d} onClick={() => setDays(d)}>{d} days</Pill>
                        ))}
                      </div>
                    </div>
                  )}

                  <LocationPicker
                    region={expRegion} setRegion={setExpRegion}
                    subRegion={expSub} setSubRegion={setExpSub}
                    country={expCountry} setCountry={setExpCountry}
                    stateVal={expState} setStateVal={setExpState}
                  />

                  {/* ── Quick Start — Travel identity presets ──
                       Shows the user's own travel identity + 4 most similar
                       (by shared vibes). 'View all 20' expands the row.
                       Tapping a chip applies that identity's vibes (filtered
                       to TMA-valid ones), transport defaults, and activities
                       derived from VIBE_TO_ACTIVITIES. Manual changes to any
                       sub-option clear the highlight. */}
                  <Label>Quick start — travel like an identity</Label>
                  <p style={{ fontSize: 12, color: textMuted, fontStyle: "italic", marginTop: -4, marginBottom: 12, lineHeight: 1.5 }}>
                    {travelerType
                      ? `Your identity: ${travelerType}. Tap to apply, or browse identities close to yours.`
                      : "Pick an identity to skip the granular toggles — or take the identity quiz to personalize this row."}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
                    {(() => {
                      const validVibes = new Set(VIBES.map(v => v.id));
                      const idsToShow = tmaShowAllIdentities
                        ? Object.keys(TRAVEL_IDENTITIES)
                        : getTopIdentitiesForUser(travelerType, 5);
                      return idsToShow.map(idName => {
                        const id = TRAVEL_IDENTITIES[idName];
                        if (!id) return null;
                        const active = tmaActivePreset === idName;
                        const isUserIdentity = travelerType === idName;
                        return (
                          <button key={idName}
                            onClick={() => {
                              if (active) {
                                setTmaActivePreset(null);
                                return;
                              }
                              // Apply identity bundle:
                              // 1. Vibes — intersect identity vibes with TMA's valid vibes
                              const compatibleVibes = (id.vibes || []).filter(v => validVibes.has(v));
                              setExpVibes(compatibleVibes);
                              // 2. Transport — from our hand-derived defaults
                              const transports = IDENTITY_TRANSPORT_DEFAULTS[idName] || [];
                              setExpTransportType(transports);
                              // 3. Activities — derive from VIBE_TO_ACTIVITIES of compatible vibes
                              const derivedActs = [...new Set(compatibleVibes.flatMap(v => VIBE_TO_ACTIVITIES[v] || []))];
                              setExpActivityTypes(derivedActs);
                              // 4. Auto-check Transport + Activities in Includes
                              setExpIncludes(ei => {
                                const next = new Set(ei);
                                next.add("transport");
                                next.add("activities");
                                return Array.from(next);
                              });
                              setTmaActivePreset(idName);
                            }}
                            style={{
                              padding: "10px 14px",
                              borderRadius: 22,
                              border: `1px solid ${active ? `${teal}80` : isUserIdentity ? `${gold}55` : darkMode ? "rgba(27,58,92,0.18)" : "rgba(0,0,0,0.12)"}`,
                              background: active ? `${teal}15` : isUserIdentity ? `${gold}10` : darkMode ? "rgba(27,58,92,0.04)" : "rgba(0,0,0,0.02)",
                              color: active ? teal : isUserIdentity ? goldText : "#4A6B8A",
                              fontSize: 13,
                              fontWeight: active || isUserIdentity ? 800 : 600,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              transition: "all 0.15s",
                            }}>
                            <span aria-hidden="true">{id.emoji}</span>
                            <span>{idName}</span>
                            {isUserIdentity && !active && <span style={{ fontSize: 10, color: goldText, fontStyle: "italic", marginLeft: 2 }}>· yours</span>}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  <button
                    onClick={() => setTmaShowAllIdentities(s => !s)}
                    style={{
                      fontSize: 11, color: teal, background: "none", border: "none",
                      cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
                      padding: 0, marginBottom: 24, textDecoration: "underline", textUnderlineOffset: 2,
                    }}>
                    {tmaShowAllIdentities ? "Show top 5 only" : "Browse all 20 identities"}
                  </button>

                  <Label>What's Included in Your Budget?</Label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                    {[
                      { id: "air",        svg: JGA_ICONS.inc_flights,    label: "Flights",    desc: "Return airfare" },
                      { id: "hotel",      svg: JGA_ICONS.inc_hotel,      label: "Hotel",      desc: "Accommodation" },
                      // ── Transport + Activities temporarily hidden (June 2026) ──
                      // Too many edge cases between vibe→activity defaults, identity
                      // transport defaults, and the filterPool match counts when both
                      // were enabled. Hiding the picker chips disables them entirely
                      // (sub-sections below stay gated on expIncludes.includes(...)
                      // so they never render). Re-enable by un-commenting these two.
                      // { id: "transport",  svg: JGA_ICONS.inc_transport,  label: "Transport",  desc: "Local getting around" },
                      // { id: "activities", svg: JGA_ICONS.inc_activities, label: "Activities", desc: "Tours & experiences" },
                    ].map(item => {
                      const on = expIncludes.includes(item.id);
                      return (
                        <button key={item.id}
                          onClick={() => { setExpIncludes(ei => on ? ei.filter(x => x !== item.id) : [...ei, item.id]); setTmaActivePreset(null); }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                            border: `1px solid ${on ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                            background: on ? "rgba(74,140,196,0.08)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{item.svg(on ? gold : "#4A6B8A", 22)}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: on ? gold : "#4A6B8A" }}>{item.label}</div>
                            <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{item.desc}</div>
                          </div>
                          <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${on ? gold : "rgba(27,58,92,0.18)"}`, background: on ? gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: "#FAFAF5", fontWeight: 900 }}>
                            {on ? "✓" : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Flight sub-options — restored. Layover preference drives
                       the getLayoverMultiplier math via filterPool's 12th arg,
                       same as it does for SM via blindLayoverOk. Gated on air
                       being included; identical visual pattern to Hotel/
                       Transport/Activity sub-options below. */}
                  {expIncludes.includes("air") && (
                    <div style={{ marginBottom: 18, paddingLeft: 8, borderLeft: `2px solid rgba(74,140,196,0.2)` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase" }}>Flight preferences</div>
                        {(() => {
                          const c = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                          return (
                            <div style={{ fontSize: 10, fontWeight: 700, color: c === 0 ? "#1B3A5C" : "#4A8CC4", letterSpacing: 1.5 }}>
                              {c === 0 ? "NO MATCHES" : `${c} ${c === 1 ? "match" : "matches"}`}
                            </div>
                          );
                        })()}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7 }}>
                        {[
                          { id: "no",    svg: JGA_ICONS.flight_direct, label: "Direct only",   desc: "Non-stop" },
                          { id: "short", svg: JGA_ICONS.flight_short,  label: "Short layover", desc: "Under 3h" },
                          { id: "yes",   svg: JGA_ICONS.flight_any,    label: "Any layover",   desc: "Best price" },
                        ].map(opt => {
                          const on = expLayoverOk.includes(opt.id);
                          return (
                            <button key={opt.id} onClick={() => { setExpLayoverOk(prev => on ? prev.filter(x => x !== opt.id) : [...prev, opt.id]); setTmaActivePreset(null); }}
                              style={{
                                padding: "10px 6px", borderRadius: 10, cursor: "pointer", textAlign: "center", fontFamily: "inherit", transition: "all 0.2s",
                                border: `1px solid ${on ? `${teal}80` : darkMode ? "rgba(27,58,92,0.10)" : "rgba(0,0,0,0.1)"}`,
                                background: on ? `${teal}15` : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                              }}>
                              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }} aria-hidden="true">{opt.svg(on ? teal : "#4A6B8A", 20)}</div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: on ? teal : "#4A6B8A" }}>{opt.label}</div>
                              <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{opt.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Hotel sub-options */}
                  {expIncludes.includes("hotel") && (
                    <div style={{ marginBottom: 18, paddingLeft: 8, borderLeft: `2px solid rgba(74,140,196,0.2)` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase" }}>Accommodation type</div>
                        {(() => {
                          const c = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                          return (
                            <div style={{ fontSize: 10, fontWeight: 700, color: c === 0 ? "#1B3A5C" : "#4A8CC4", letterSpacing: 1.5 }}>
                              {c === 0 ? "NO MATCHES" : `${c} ${c === 1 ? "match" : "matches"}`}
                            </div>
                          );
                        })()}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 7 }}>
                        {[
                          { id: "luxury",     svg: JGA_ICONS.hotel_luxury,     label: "Luxury",     desc: "5-star & resorts" },
                          { id: "boutique",   svg: JGA_ICONS.hotel_boutique,   label: "Boutique",   desc: "Stylish & characterful" },
                          { id: "economical", svg: JGA_ICONS.hotel_economical, label: "Economical", desc: "Comfortable & good value" },
                          { id: "budget",     svg: JGA_ICONS.hotel_budget,     label: "Budget",     desc: "Affordable, no-frills" },
                        ].map(opt => {
                          const on = expHotelType.includes(opt.id);
                          return (
                            <button key={opt.id} onClick={() => { setExpHotelType(prev => on ? prev.filter(x => x !== opt.id) : [...prev, opt.id]); setTmaActivePreset(null); }}
                              style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                                border: `1px solid ${on ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"}`,
                                background: on ? "rgba(74,140,196,0.08)" : "rgba(27,58,92,0.030)" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{opt.svg(on ? gold : "#4A6B8A", 20)}</span>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: on ? gold : "#4A6B8A" }}>{opt.label}</div>
                                <div style={{ fontSize: 10, color: textDim }}>{opt.desc}</div>
                              </div>
                              {on && <div style={{ marginLeft: "auto", color: goldText, fontSize: 12 }}>✓</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Transport sub-options */}
                  {expIncludes.includes("transport") && (
                    <div style={{ marginBottom: 18, paddingLeft: 8, borderLeft: `2px solid rgba(74,140,196,0.2)` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase" }}>Transport preferences</div>
                        {(() => {
                          const c = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                          return (
                            <div style={{ fontSize: 10, fontWeight: 700, color: c === 0 ? "#1B3A5C" : "#4A8CC4", letterSpacing: 1.5 }}>
                              {c === 0 ? "NO MATCHES" : `${c} ${c === 1 ? "match" : "matches"}`}
                            </div>
                          );
                        })()}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {[
                          { id: "bike",       svg: JGA_ICONS.trans_bike,      label: "Bike Rental" },
                          { id: "bus",        svg: JGA_ICONS.trans_bus,       label: "Bus / Coach" },
                          { id: "metro",      svg: JGA_ICONS.trans_metro,     label: "Metro" },
                          { id: "rental_car", svg: JGA_ICONS.trans_car,       label: "Rental Car" },
                          { id: "rideshare",  svg: JGA_ICONS.trans_rideshare, label: "Ride Shares" },
                          { id: "taxi",       svg: JGA_ICONS.trans_taxi,      label: "Taxis" },
                          { id: "train",      svg: JGA_ICONS.trans_train,     label: "Trains" },
                          { id: "walking",    svg: JGA_ICONS.trans_walking,   label: "Walking" },
                        ].map(opt => {
                          const on = expTransportType.includes(opt.id);
                          return (
                            <button key={opt.id} onClick={() => { setExpTransportType(tt => on ? tt.filter(x => x !== opt.id) : [...tt, opt.id]); setTmaActivePreset(null); }}
                              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 30, cursor: "pointer", fontFamily: "inherit", fontSize: 12, transition: "all 0.2s",
                                border: `1px solid ${on ? "rgba(74,140,196,0.5)" : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"}`,
                                background: on ? "rgba(74,140,196,0.08)" : "rgba(27,58,92,0.030)",
                                color: on ? gold : "#4A6B8A" }}>
                              <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{opt.svg(on ? gold : "#4A6B8A", 14)}</span>
                              <span>{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                      {expTransportType.length === 0 && (
                        <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", marginTop: 8, lineHeight: 1.4 }}>
                          No transport picked — filter uses a low-end estimate (~$20/day). Pick what you'll actually use for a sharper match.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activity sub-options */}
                  {expIncludes.includes("activities") && (
                    <div style={{ marginBottom: 18, paddingLeft: 8, borderLeft: `2px solid rgba(74,140,196,0.2)` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10 }}>
                        <div style={{ fontSize: 11, color: "#4A8CC4", letterSpacing: 2, textTransform: "uppercase" }}>Activity interests</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {expVibes.length > 0 && (
                            <div style={{ fontSize: 10, color: "#6dbd6d" }}>✓ suggested from your vibe</div>
                          )}
                          {(() => {
                            const c = filterPool(parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk).length;
                            return (
                              <div style={{ fontSize: 10, fontWeight: 700, color: c === 0 ? "#1B3A5C" : "#4A8CC4", letterSpacing: 1.5 }}>
                                {c === 0 ? "NO MATCHES" : `${c} ${c === 1 ? "match" : "matches"}`}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {[
                          { id: "art",           svg: JGA_ICONS.act_art,       label: "Art & Galleries" },
                          { id: "concerts",      svg: JGA_ICONS.act_concerts,  label: "Concerts & Shows" },
                          { id: "cooking",       svg: JGA_ICONS.act_cooking,   label: "Cooking Classes" },
                          { id: "food_tours",    svg: JGA_ICONS.act_food,      label: "Food Tours" },
                          { id: "hiking",        svg: JGA_ICONS.act_hiking,    label: "Hiking & Trails" },
                          { id: "museums",       svg: JGA_ICONS.act_museums,   label: "Museums" },
                          { id: "nightlife",     svg: JGA_ICONS.act_nightlife, label: "Nightlife" },
                          { id: "shopping",      svg: JGA_ICONS.act_shopping,  label: "Shopping" },
                          { id: "spa",           svg: JGA_ICONS.act_spa,       label: "Spa & Wellness" },
                          { id: "sports",        svg: JGA_ICONS.act_sports,    label: "Sports & Matches" },
                          { id: "walking_tours", svg: JGA_ICONS.act_walking,   label: "Walking Tours" },
                          { id: "water",         svg: JGA_ICONS.act_water,     label: "Water Activities" },
                          { id: "wildlife",      svg: JGA_ICONS.act_wildlife,  label: "Wildlife & Nature" },
                        ].map(opt => {
                          const on = expActivityTypes.includes(opt.id);
                          const suggested = expVibes.length > 0 && expVibes.some(vid => (VIBE_TO_ACTIVITIES[vid] || []).includes(opt.id));
                          const activeColor = on ? gold : suggested ? "#6dbd6d" : "#4A6B8A";
                          return (
                            <button key={opt.id} onClick={() => { setExpActivityTypes(at => on ? at.filter(x => x !== opt.id) : [...at, opt.id]); setTmaActivePreset(null); }}
                              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 30, cursor: "pointer", fontFamily: "inherit", fontSize: 12, transition: "all 0.2s",
                                border: `1px solid ${on ? "rgba(74,140,196,0.5)" : suggested ? "rgba(109,189,109,0.35)" : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"}`,
                                background: on ? "rgba(74,140,196,0.08)" : suggested ? "rgba(109,189,109,0.06)" : "rgba(27,58,92,0.030)",
                                color: activeColor }}>
                              <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{opt.svg(activeColor, 14)}</span>
                              <span>{opt.label}</span>
                              {suggested && !on && <span style={{ fontSize: 11, opacity: 0.7 }}>✦</span>}
                            </button>
                          );
                        })}
                      </div>
                      {expActivityTypes.length === 0 && (
                        <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", marginTop: 8, lineHeight: 1.4 }}>
                          No activities picked — filter uses a low-end estimate (~$30/day). Pick what interests you for a sharper match.
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Preferences disclosure ──
                        Sets honest expectations: prefs steer the match pool
                        and the trip cost estimate, but specific flights, hotel
                        rooms, and tour slots depend on what's actually
                        available at booking time. Same disclosure lives in SM
                        for parity. */}
                  <div style={{
                    marginTop: -4, marginBottom: 18,
                    padding: "10px 12px", borderRadius: 8,
                    background: "rgba(27,58,92,0.04)",
                    border: "1px solid rgba(27,58,92,0.12)",
                    fontSize: 11, lineHeight: 1.55, color: textMuted, fontStyle: "italic",
                  }}>
                    We'll do our best to honor your preferences — specific flights, hotel rooms, and activity slots depend on what's available at booking time.
                  </div>

                  {(() => {
                    const parsedB = parseInt(String(budget).replace(/[^0-9]/g,""),10)||1000;
                    const pool = filterPool(parsedB, expRegion, expSub, expCountry, expState, undefined, expIncludes, days, expTransportType, expActivityTypes, expHotelType, expLayoverOk);
                    const matchCount = pool.length;
                    // Diagnostic: when no matches come back, log everything that
                    // went into the filter so we can see why. Pre-launch only;
                    // remove once filter logic is trusted in production.
                    if (matchCount === 0 && typeof window !== "undefined" && !window.__JGA_TMA_FILTER_LOGGED) {
                      window.__JGA_TMA_FILTER_LOGGED = true;
                      console.warn("[JGA TMA Filter] 0 matches — inputs:", {
                        rawBudget: budget,
                        parsedBudget: parsedB,
                        expRegion, expSub, expCountry, expState,
                        expIncludes, days,
                        expTransportType, expActivityTypes,
                        travelCompanion, travelers,
                        departureAirport: expDepartureAirport || user?.departureAirport || "(unset)",
                      });
                      setTimeout(() => { delete window.__JGA_TMA_FILTER_LOGGED; }, 4000);
                    }
                    return (
                      <>
                        {matchCount === 0 && !lockedTripDest && (
                          <div style={{
                            background: "linear-gradient(135deg, #FAFAF5 0%, #F0EDE3 100%)",
                            border: "1px solid rgba(27,58,92,0.22)",
                            borderRadius: 14,
                            padding: "16px 18px",
                            margin: "10px 0 20px",
                            fontSize: 13,
                            color: "#4A6B8A",
                            lineHeight: 1.6,
                          }}>
                            <div style={{
                              fontSize: 10,
                              letterSpacing: 2.5,
                              color: "#1B3A5C",
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                              textTransform: "uppercase",
                              fontWeight: 700,
                              marginBottom: 8,
                            }}>
                              No matches
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              Nothing fits your current budget and region combination. Try:
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              <button onClick={() => setBudget(b => String(Math.round((parseInt(b)||1000) * 1.25)))}
                                style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.35)", background: "transparent", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                                Raise budget 25%
                              </button>
                              <button onClick={() => { setExpRegion("Anywhere"); setExpSub(null); setExpCountry(null); setExpState(null); }}
                                style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(27,58,92,0.35)", background: "transparent", color: "#1B3A5C", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                                Open up region
                              </button>
                            </div>
                          </div>
                        )}
                        {(() => {
                          const needsDates = expDateMode === "dates" && (!departDate || !returnDate);
                          const needsMonth = expDateMode === "month" && expMonth === null;
                          const missingDates = needsDates || needsMonth;
                          // matchCount is 0 when lockedTripDest is set (we hide the match counter
                          // entirely above), so we must NOT block on matchCount in that case.
                          const blocked = lockedTripDest ? missingDates : (matchCount === 0 || missingDates);
                          return (
                            <>
                              {missingDates && (
                                <div style={{ background: "rgba(255,107,107,0.07)", border: "1px solid rgba(255,107,107,0.25)", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#ff9090" }}>
                                  {needsDates ? "⚠️ Please select both departure and return dates." : "⚠️ Please select a travel month."}
                                </div>
                              )}
                              {lockedTripDest ? (
                                <Btn
                                  variant="teal"
                                  onClick={() => {
                                    if (blocked) return;
                                    const dest = lockedTripDest;
                                    setLockedTripDest(null);
                                    setSpinError(null);
                                    setResults([dest]);
                                    setSelected(dest);
                                    setStep(2);
                                    generateItinerary(dest);
                                  }}
                                  disabled={blocked}>
                                  Generate itinerary for {lockedTripDest.name} →
                                </Btn>
                              ) : (
                                <Btn variant="teal" onClick={() => { if (blocked) return; setSpinError(null); trySpinExplore(); setStep(2); }} disabled={blocked}>
                                  Spin & Find My Match
                                </Btn>
                              )}
                            </>
                          );
                        })()}
                      </>
                    );
                  })()}
                  <Btn variant="ghost" onClick={reset} style={{ marginTop: 8 }}>← Back</Btn>
                </div>
              )}


              {/* Step 2 — Spin (auto-triggers, no intermediate screen) */}
              {step === 2 && (
                <div>
                  {!results && !spinning && spinError && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ borderRadius: 14, border: "1px solid rgba(255,107,107,0.35)", background: "rgba(255,107,107,0.07)", padding: "14px 16px", marginBottom: 14, textAlign: "left" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#ff6b6b", marginBottom: 6 }}>😕 No destinations found</div>
                        <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.65, marginBottom: 12 }}>{spinError}</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button onClick={() => { setBudget(b => String(Math.round((parseInt(b)||1000) * 1.25))); setSpinError(null); }} style={{ padding: "7px 13px", borderRadius: 8, border: "1px solid rgba(255,107,107,0.3)", background: "transparent", color: "#ff8888", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>↑ Raise budget 25%</button>
                          <button onClick={() => { setExpRegion("Anywhere"); setExpSub(null); setExpCountry(null); setExpState(null); setSpinError(null); }} style={{ padding: "7px 13px", borderRadius: 8, border: "1px solid rgba(255,107,107,0.3)", background: "transparent", color: "#ff8888", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🌍 Open up region</button>
                          <button onClick={() => { setExpVibes([]); setSpinError(null); }} style={{ padding: "7px 13px", borderRadius: 8, border: "1px solid rgba(255,107,107,0.3)", background: "transparent", color: "#ff8888", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✕ Clear vibes</button>
                        </div>
                      </div>
                      <Btn onClick={() => { setSpinError(null); trySpinExplore(); }} disabled={!canSpin}>Try Again</Btn>
                      <Btn variant="ghost" onClick={() => setStep(0)} style={{ marginTop: 8 }}>← Adjust preferences</Btn>
                    </div>
                  )}
                  {!results && !spinning && !spinError && null /* auto-spun from step 0 CTA */}

                  {spinning && (() => {
                    // Globe spinner colors per mode. Pale gold disappears on
                    // cream in light mode — switch to a deeper amber for
                    // visibility while keeping the gold-toned brand feel.
                    const globeStroke = darkMode ? "#4A8CC4" : "#1B3A5C";
                    const globeFill = darkMode ? "rgba(74,140,196,0.15)" : "rgba(201,120,50,0.12)";
                    const globeDot = darkMode ? "#4A8CC4" : "#1B3A5C";
                    return (
                    <div style={{ textAlign: "center", padding: "24px 0" }}>
                      <svg style={{animation:"spinAnim 0.6s linear infinite",display:"inline-block",marginBottom:8}} width="46" height="46" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="22" cy="22" r="19" fill={globeFill} stroke={globeStroke} strokeWidth="2"/><ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke={globeStroke} strokeWidth="1.5"/><path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={globeStroke} strokeWidth="1.4" fill="none"/><path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={globeStroke} strokeWidth="1.1" fill="none" opacity="0.7"/><path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={globeStroke} strokeWidth="1.1" fill="none" opacity="0.7"/><circle cx="22" cy="22" r="3" fill={globeDot} opacity="0.5"/></svg>
                      <div style={{ fontSize: 24, fontWeight: 800, color: goldText, minHeight: 40, marginTop: 12 }}>{spinText}</div>
                      <p style={{ color: textMuted, fontSize: 13, marginTop: 10 }}>Searching the globe…</p>
                    </div>
                    );
                  })()}

                  {results && !selected && (
                    <div>
                      <h2 style={{ fontSize: 19, fontWeight: 700, marginBottom: 4, textAlign: "center" }}>View Your {results.length} {results.length === 1 ? "Match" : "Matches"}</h2>
                      {(() => {
                        const bVal = parseInt(String(budget).replace(/[^0-9]/g,""),10) || 0;
                        if (!bVal) return null;
                        const companionMap = { solo: 1, couple: 2, friends: 3, family: 4 };
                        const pax = companionMap[travelCompanion] || (travelers > 1 ? travelers : 0) || 1;
                        return (
                          <div style={{ textAlign: "center", marginBottom: 12, fontSize: 11, color: textDim, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: 0.5 }}>
                            Within your <strong style={{ color: "#1B3A5C" }}>${bVal.toLocaleString()}</strong> budget{pax > 1 ? ` · ${pax} travelers` : ""}
                          </div>
                        );
                      })()}
                      {/* How-to-book helper — added Jun 11 2026. Users were
                          uncertain whether tapping a destination card or
                          tapping its price was the way to proceed; some
                          didn't realize the whole row was clickable. This
                          inline note makes the next step explicit AND
                          surfaces the refine-by-vibe/region affordance
                          (which lives behind the back arrow). */}
                      <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.22)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#1B3A5C", lineHeight: 1.55 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                          <span style={{ fontSize: 14, lineHeight: 1.3, flexShrink: 0 }}>👆</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ color: "#1B3A5C" }}>Tap any destination</strong> to see flights, hotels, and start booking your trip.
                            <div style={{ fontSize: 11, color: textMuted, marginTop: 3 }}>
                              Want different results? Use <strong style={{ color: "#1B3A5C" }}>←&nbsp;Change&nbsp;dates&nbsp;/&nbsp;preferences</strong> below to refine by vibe or region.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {results.map(dest => (
                          <button key={dest.name} onClick={() => { setSelected(dest); if (typeof window.jgaTrack === "function") window.jgaTrack("select_destination", { destination: dest.name, country: dest.country, region: dest.region, budget: budget }); }}
                            style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: "1px solid rgba(74,140,196,0.18)", borderRadius: 14, padding: "14px 16px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit", transition: "all 0.2s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,140,196,0.08)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.45)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "rgba(74,140,196,0.18)"; }}>
                            <div style={{ fontSize: 34 }}>{dest.emoji}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={{ fontSize: 17, fontWeight: 800, color: textPrimary }}>{dest.name}</span>
                                <span style={{ fontSize: 11, color: textMuted }}>{fmtTemp(dest.temp, fahrenheit)}</span>
                              </div>
                              <div style={{ fontSize: 12, color: textMuted }}>{dest.state ? `${dest.state}, ` : ""}{dest.country}</div>
                              <div style={{ fontSize: 11, color: "#4A8CC4", fontStyle: "italic", marginTop: 2 }}>{dest.vibe}</div>
                              <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                                {dest.tags.map(t => <span key={t} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.07)", color: "#889", letterSpacing: 0.5, textTransform: "uppercase" }}>{t}</span>)}
                              </div>
                              <div style={{ fontSize: 10, color: "#6dbd6d", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                                <span>🔥</span>
                                <span>{Math.floor((dest.name.charCodeAt(0) + dest.name.charCodeAt(1) + dest.name.length) % 180 + 47)} people booked this trip this month</span>
                              </div>
                              <div style={{ marginTop: 8 }}>
                                <BudgetDisplay dest={dest} departureAirport={expDepartureAirport} flightPrice={flightPrices[dest.name]} userBudget={parseInt(String(budget).replace(/[^0-9]/g,""),10) || null} travelers={paxFromCompanion(travelCompanion, travelers)} />
                              </div>
                            </div>
                            <div style={{ fontSize: 16, color: amber }}>→</div>
                          </button>
                        ))}
                      </div>
                      <Btn variant="ghost" onClick={spinMoreExplore} style={{ marginTop: 10 }} disabled={!canSpin}>
                        {canSpin ? "Spin to see 10 more" : "🔒 Register to spin again"}
                      </Btn>
                      {prevResults && prevResults.length > 0 && (
                        <button onClick={() => { setResults(prevResults); setPrevResults(null); setSelected(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          style={{ display: "block", width: "100%", marginTop: 8, background: "none", border: "none", color: teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3 }}>
                          ← See your previous {prevResults.length} {prevResults.length === 1 ? "pick" : "picks"}
                        </button>
                      )}
                      <Btn variant="ghost" onClick={() => { setResults(null); setStep(0); }} style={{ marginTop: 8 }}>← Change dates / preferences</Btn>
                    </div>
                  )}

                  {selected && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                        <button onClick={() => { setSelected(null); setAiItinerary(null); }} style={{ background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
                        <div>
                          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{selected.emoji} {selected.name}</h2>
                          <div style={{ fontSize: 11, color: textMuted }}>{selected.state ? `${selected.state}, ` : ""}{selected.country} · {days}d · ${parseInt(String(budget).replace(/\D/g,""),10)?.toLocaleString() || budget}</div>
                        </div>
                      </div>

                      {/* ── Guest budget disclaimer — signed-out users (esp. blog
                         deep-links) land here without ever setting a budget, so
                         trips are priced against the $2k default. Make that explicit
                         and offer a one-tap path to register & set their own. ── */}
                      {!registered && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.3)", borderRadius: 12, padding: "10px 14px", marginBottom: 14 }}>
                          <div style={{ flex: 1, minWidth: 150 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: goldText, marginBottom: 2 }}>You're browsing as a guest</div>
                            <div style={{ fontSize: 11.5, color: textMuted, lineHeight: 1.45 }}>
                              These trips are priced for a default ${parseInt(String(budget).replace(/\D/g,""),10)?.toLocaleString() || budget} budget. Sign up to set your own and save your trips.
                            </div>
                          </div>
                          <button onClick={() => setShowRegister(true)} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${teal}, #0F2747)`, color: "#FAFAF5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Set my budget →</button>
                        </div>
                      )}

                      {/* ── Milestone — capture trip occasion BEFORE the choice row.
                         Inline card per spec. Per-destination persistence keyed
                         by destId. Passes through to AI itinerary prompt + waitlist
                         payload + journal entry when those commitments happen.
                         "Just because" is the default real answer; skip preserves
                         that silently and marks the answer as "skipped" for backend. */}
                      {!aiItinerary && !loadingAI && !isMinor && (() => {
                        const destId = `${selected.name}|${selected.country}`;
                        const stored = tmaMilestones[destId];
                        const value = stored || { type: "just-because", otherText: null };
                        return (
                          <MilestoneCard
                            value={value}
                            destName={selected.name}
                            onChange={(next) => {
                              const updated = { ...tmaMilestones, [destId]: { ...next, updatedAt: Date.now() } };
                              setTmaMilestonesState(updated);
                              setTmaMilestone(destId, next);
                              setTmaMilestoneStatus(s => ({ ...s, [destId]: "answered" }));
                            }}
                            onSkip={() => {
                              setTmaMilestoneStatus(s => ({ ...s, [destId]: "skipped" }));
                            }}
                          />
                        );
                      })()}

                      {/* ── Choice row — See Itinerary (primary) / Get on the waitlist (secondary)
                         See Itinerary is the strong CTA: AI generates a day-by-day plan, which
                         is the bookable-feeling outcome at v1. "Get on the waitlist" is the
                         secondary path for users who want to be notified when JGA opens
                         bookings (affiliate handoff to Booking.com is still being tested).
                         Once See Itinerary is clicked (itinerary loading or loaded), this
                         row hides — the itinerary below carries the moment from there. */}
                      {!aiItinerary && !loadingAI && !isMinor && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, letterSpacing: 1.8, color: textDim, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>What's next?</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <button onClick={() => {
                              const destId = `${selected.name}|${selected.country}`;
                              const m = tmaMilestones[destId] || { type: "just-because", otherText: null };
                              const status = tmaMilestoneStatus[destId] || "answered";
                              generateItinerary(selected, { milestone: m, milestoneStatus: status });
                              if (typeof window.jgaTrack === "function") window.jgaTrack("view_itinerary", { destination: selected.name, milestone: m.type, milestone_status: status });
                            }}
                              style={{
                                padding: "16px 12px", borderRadius: 12,
                                border: "1px solid rgba(27,58,92,0.3)",
                                background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)",
                                cursor: "pointer", fontFamily: "inherit",
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                                transition: "all 0.18s ease",
                                boxShadow: "0 2px 12px rgba(27,58,92,0.25)",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(27,58,92,0.35)"; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(27,58,92,0.25)"; }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <rect x="4" y="3" width="16" height="18" rx="1.8" stroke="#FAFAF5" strokeWidth="1.6" fill="rgba(250,250,245,0.10)"/>
                                <path d="M7 8 L17 8" stroke="#FAFAF5" strokeWidth="1.4" strokeLinecap="round"/>
                                <path d="M7 12 L17 12" stroke="#FAFAF5" strokeWidth="1.4" strokeLinecap="round"/>
                                <path d="M7 16 L13 16" stroke="#FAFAF5" strokeWidth="1.4" strokeLinecap="round"/>
                              </svg>
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#FAFAF5" }}>See Itinerary</div>
                              <div style={{ fontSize: 10, color: "rgba(250,250,245,0.75)", textAlign: "center", lineHeight: 1.35 }}>AI-crafted day-by-day plan</div>
                            </button>
                            <button onClick={() => {
                                const destId = `${selected.name}|${selected.country}`;
                                const m = tmaMilestones[destId] || { type: "just-because", otherText: null };
                                const status = tmaMilestoneStatus[destId] || "answered";
                                setTmaWaitlistContext({ dest: selected, departDate: departDate || null, returnDate: returnDate || null, milestone: m, milestoneStatus: status });
                              }}
                              style={{
                                padding: "16px 12px", borderRadius: 12,
                                border: "1px solid rgba(74,140,196,0.45)",
                                background: "linear-gradient(135deg, rgba(74,140,196,0.12) 0%, rgba(74,140,196,0.04) 100%)",
                                cursor: "pointer", fontFamily: "inherit",
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                                transition: "all 0.18s ease",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(74,140,196,0.75)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(74,140,196,0.45)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M12 3 L 13.5 8.5 L 19 9 L 14.5 12.5 L 16 18 L 12 15 L 8 18 L 9.5 12.5 L 5 9 L 10.5 8.5 Z" stroke="#4A8CC4" strokeWidth="1.4" fill="rgba(74,140,196,0.12)" strokeLinejoin="round"/>
                              </svg>
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#4A8CC4" }}>Get on the waitlist</div>
                              <div style={{ fontSize: 10, color: textDim, textAlign: "center", lineHeight: 1.35 }}>We'll notify you when it's bookable</div>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ── Book Now CTA — replaced with friendly card for minors ── */}
                      {selected && !loadingAI && isMinor && (
                        <div style={{ background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 14 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <circle cx="12" cy="9" r="3" stroke="#4A8CC4" strokeWidth="1.7" fill="rgba(74,140,196,0.2)"/>
                              <path d="M5 20 C 5 16, 8 14, 12 14 C 16 14, 19 16, 19 20" stroke="#4A8CC4" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                            </svg>
                            <div style={{ fontSize: 13, fontWeight: 800, color: "#4A8CC4" }}>Booking unlocks at 18</div>
                          </div>
                          <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>For now, save this destination to your wishlist and explore the itinerary. You'll be able to book the day you turn 18 — and we'll have everything ready.</div>
                        </div>
                      )}
                      {selected && !loadingAI && !isMinor && (() => {
                        const hasDates = (departDate && returnDate) || (expMonth !== null);
                        // When dates are set, the upper side-by-side row already
                        // provides the strong Book Now CTA — skip this card to
                        // avoid the duplicate "Ready to book?" prompt.
                        if (hasDates) return null;
                        return (
                          <div id="jga-tma-book-card" style={{ background: "rgba(74,140,196,0.05)", border: "1px solid rgba(74,140,196,0.3)", borderRadius: 14, padding: "16px 18px", marginBottom: 14 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: goldText, marginBottom: 10 }}>Add dates for {selected.emoji} {selected.name}</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 4 }}>DEPARTURE</div>
                                  <input type="date" value={departDate}
                                    onChange={e => applyDepartChange(e.target.value, departDate, returnDate, setDepartDate, setReturnDate)}
                                    min={getMinTravelDate()}
                                    style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: `1px solid ${departDate ? "rgba(27,58,92,0.4)" : "rgba(74,140,196,0.3)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: textDim, letterSpacing: 0.5, marginBottom: 4 }}>RETURN</div>
                                  <input type="date" value={returnDate}
                                    onChange={e => applyReturnChange(e.target.value, departDate, setReturnDate)}
                                    min={departDate || getMinTravelDate()}
                                    style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: `1px solid ${returnDate ? "rgba(27,58,92,0.4)" : "rgba(74,140,196,0.3)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                                </div>
                              </div>
                              <button onClick={() => {
                                const destId = `${selected.name}|${selected.country}`;
                                const m = tmaMilestones[destId] || { type: "just-because", otherText: null };
                                const status = tmaMilestoneStatus[destId] || "answered";
                                setTmaWaitlistContext({ dest: selected, departDate: departDate || null, returnDate: returnDate || null, milestone: m, milestoneStatus: status });
                              }} disabled={!departDate || !returnDate}
                                style={{ width: "100%", padding: "11px", borderRadius: 12, border: "none", background: departDate && returnDate ? `linear-gradient(135deg,${teal},#0F2747)` : "rgba(27,58,92,0.096)", color: departDate && returnDate ? "#FAFAF5" : textDim, fontWeight: 900, fontSize: 14, cursor: departDate && returnDate ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "all 0.2s" }}>
                                {departDate && returnDate ? "Get on the waitlist →" : "Select both dates to continue"}
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {loadingAI && <RotatingItineraryLoader accentColor="#4A8CC4" />}
                      {aiError && (
                        <div style={{ textAlign: "center", padding: "24px 0" }}>
                          <div style={{ fontSize: 40, marginBottom: 10 }}>🛫</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: goldText, marginBottom: 6 }}>Waiting for take-off…</div>
                          <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>Our travel planners are momentarily in the clouds. Give us a second to land.</div>
                          <button onClick={() => { setAiError(false); generateItinerary(selected); }} style={{ padding: "10px 20px", borderRadius: 10, border: `1px solid rgba(74,140,196,0.4)`, background: "rgba(74,140,196,0.08)", color: goldText, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Try Again →</button>
                        </div>
                      )}
                      {aiItinerary && (
                        <>
                          <ItineraryView data={aiItinerary} />
                          {registered && (
                            <div style={{ marginTop: 10, marginBottom: 4 }}>
                              {savedItineraries.some(i => i.dest === selected.name && (`${i.departDate || ""}|${i.returnDate || ""}`) === `${departDate || ""}|${returnDate || ""}`) ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(109,189,109,0.08)", border: "1px solid rgba(109,189,109,0.25)", fontSize: 13, color: "#6dbd6d", fontWeight: 700 }}>
                                  ✓ Saved to your Itineraries
                                </div>
                              ) : (
                                <button onClick={async () => {
                                  await saveItineraryFlow({
                                    destName: selected.name,
                                    destEmoji: selected.emoji,
                                    destCountry: selected.country || "",
                                    days,
                                    data: aiItinerary,
                                    departDate: departDate || null,
                                    returnDate: returnDate || null,
                                  });
                                }} style={{ width: "100%", padding: "11px", borderRadius: 10, border: "1px solid rgba(74,140,196,0.4)", background: "rgba(74,140,196,0.07)", color: goldText, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  🗺️ Save to My Itineraries
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {aiItinerary && !isMinor && (() => {
                        const hasDates = (departDate && returnDate) || (expMonth !== null);
                        return (
                          <button onClick={() => {
                              if (hasDates) { setShowBooking(true); }
                              else {
                                const el = document.getElementById("jga-tma-book-card");
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                              }
                            }}
                            style={{ width: "100%", marginTop: 12, padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #E89A3C 0%, #C8741D 100%)", color: "#FAFAF5", fontWeight: 900, fontSize: 14.5, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 12px rgba(27,58,92,0.25)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all 0.18s ease" }}
                            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(27,58,92,0.35)"; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(27,58,92,0.25)"; }}>
                            {hasDates ? `Book ${selected.emoji} ${selected.name} →` : `📅 Pick dates & book ${selected.name} →`}
                          </button>
                        );
                      })()}
                      <Btn variant="ghost" onClick={() => { setSelected(null); setAiItinerary(null); trySpinExplore(); }} style={{ marginTop: 10 }} disabled={!canSpin}>
                        {canSpin ? "Spin for new destinations" : "🔒 Register to spin again"}
                      </Btn>
                      {selected && (
                        <div style={{ marginTop: 12, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)", border: `1px solid ${borderColor}`, borderRadius: 12, padding: "12px 14px" }}>
                          <div style={{ fontSize: 11, color: textMuted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Share My Match</div>
                          <div style={{ fontSize: 12, color: textMuted, marginBottom: 10, fontStyle: "italic" }}>
                            "{selected.emoji} I just matched to {selected.name} on Just Go Anywhere!"
                          </div>
                          {(() => {
                            // Single share template — used across SMS, email, FB, and native share
                            // so the wording stays consistent. Brand name + travel-identity emphasis
                            // (vs the prior AI-focused copy).
                            const shareMessage = `${selected.emoji} I just matched to ${selected.name} on Just Go Anywhere!

Based on my travel identity, vibe and budget, this trip was selected for me.

Learn more at www.justgoanywhere.com`;
                            return (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {[
                              { label: "Text", key: "sms", url: `sms:?body=${encodeURIComponent(shareMessage)}`, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg> },
                              { label: "Email", key: "email", url: `mailto:?subject=${encodeURIComponent(`My trip match: ${selected.name}!`)}&body=${encodeURIComponent(shareMessage)}`, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg> },
                              { label: "Instagram", key: "ig", share: true, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg> },
                              { label: "TikTok", key: "tt", share: true, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg> },
                              { label: "Facebook", key: "fb", url: `https://www.facebook.com/sharer/sharer.php?u=https://justgoanywhere.com&quote=${encodeURIComponent(shareMessage)}`, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg> },
                              { label: "Share", key: "native", share: true, native: true, svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M16 6l-4-4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 2v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg> },
                            ].map(s => {
                              const sharedStyle = {
                                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
                                padding: "8px 14px",
                                width: 116,
                                height: 36,
                                boxSizing: "border-box",
                                borderRadius: 20,
                                border: `1px solid ${borderColor}`,
                                background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.04)",
                                fontSize: 12,
                                color: textMuted,
                                fontWeight: 600,
                                textDecoration: "none",
                                transition: "all 0.15s",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                whiteSpace: "nowrap",
                              };
                              const handleShareClick = () => {
                                if (navigator.share) { navigator.share({ title: "My trip match!", text: shareMessage, url: "https://justgoanywhere.com" }); }
                                else { navigator.clipboard?.writeText(shareMessage); }
                              };
                              const content = <>{s.svg}<span>{s.label}</span></>;
                              return s.share ? (
                                <button key={s.label} onClick={handleShareClick} aria-label={`Share to ${s.label}`} style={sharedStyle}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#1B3A5C"; e.currentTarget.style.color = "#1B3A5C"; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = textMuted; }}>
                                  {content}
                                </button>
                              ) : (
                                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" style={sharedStyle}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#1B3A5C"; e.currentTarget.style.color = "#1B3A5C"; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = textMuted; }}>
                                  {content}
                                </a>
                              );
                            })}
                          </div>
                            );
                          })()}
                        </div>
                      )}
                      <button onClick={() => { setSelected(null); setAiItinerary(null); }} style={{ display: "block", margin: "10px auto 0", background: "none", border: "none", color: textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Back to matches</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════ BLIND BOOKING ══════════ */}
        {mode === "blind" && !blindResult && !blindLoading && !blindPreviewStep && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            {/* Surprise Me: now uses the same bare container as TMA/Trending/
               Affordable — no dynCard rectangle. Globe animation: latitude
               lines slide horizontally inside a circular clip mask, which
               reads as "the globe surface rotating across the sphere" rather
               than the previous full-group rotation that made the ellipse
               warp visibly. Honors prefers-reduced-motion. */}
            <style>{`
              @keyframes jga-globe-drift { from { transform: translateX(0); } to { transform: translateX(-19px); } }
              .jga-globe-drift { animation: jga-globe-drift 6s linear infinite; }
              @media (prefers-reduced-motion: reduce) { .jga-globe-drift { animation: none; } }
            `}</style>
            {/* Hero header — left-aligned with animated icon on the right.
                Matches the unified mode-page pattern (TMA, Trending, Most Affordable). */}
            <div style={{ marginBottom: 26 }}>
              <button onClick={() => { reset(); setMode("search"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{ background: "none", border: "none", color: textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "4px 0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                ← Back to Explore
              </button>
              <div style={{ fontSize: 13, letterSpacing: 2, color: textMuted, fontStyle: "italic", marginBottom: 6, textTransform: "lowercase" }}>a destination chosen for you, revealed when you're ready</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <h2 style={{ margin: 0, fontSize: 36, fontWeight: 900, color: textPrimary, letterSpacing: 0, lineHeight: 1.05, textTransform: "uppercase", flex: 1, minWidth: 0 }}>Surprise me</h2>
                <svg width="72" height="72" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
                  <defs>
                    {/* Clip the surface to the sphere's outline so latitude lines
                       don't visibly bleed past the globe's edge as they slide. */}
                    <clipPath id="jga-globe-clip">
                      <circle cx="22" cy="22" r="19"/>
                    </clipPath>
                  </defs>
                  {/* Sphere silhouette + front meridian — STATIC. */}
                  <circle cx="22" cy="22" r="19" fill={`${teal}22`} stroke={teal} strokeWidth="2"/>
                  <ellipse cx="22" cy="22" rx="9" ry="19" fill={`${teal}10`} stroke={teal} strokeWidth="1.6"/>
                  {/* Latitude lines drift left across the sphere face. */}
                  <g clipPath="url(#jga-globe-clip)">
                    <g className="jga-globe-drift">
                      <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                      <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                      <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                      {/* Second copy, offset by +19px so the loop is seamless */}
                      <path d="M22 22 Q31 18 41 22 Q51 26 60 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                      <path d="M24 13 Q32 10 41 13 Q50 16 58 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                      <path d="M24 31 Q32 28 41 31 Q50 34 58 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                    </g>
                  </g>
                </svg>
              </div>
            </div>


            {/* How it works — explains the surprise mechanic up front */}
            <div style={{ background: "rgba(27,58,92,0.06)", border: "1px solid rgba(27,58,92,0.18)", borderRadius: 12, padding: "12px 16px", marginBottom: 22, fontSize: 13, color: "#1B3A5C", lineHeight: 1.6 }}>
              <strong style={{ color: teal }}>How it works:</strong> We pick your destination, find estimated hotel, air, transport, activities, and lock it in. The destination stays sealed until you're ready to look and book.
            </div>

            {/* ── Group 1 eyebrow ── */}

            <Label>Number of Travelers</Label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {[
                { n: 1, svg: JGA_ICONS.traveller_solo,   label: "Solo" },
                { n: 2, svg: JGA_ICONS.traveller_couple, label: "2" },
                { n: 3, svg: JGA_ICONS.traveller_three,  label: "3" },
                { n: 4, svg: JGA_ICONS.traveller_four,   label: "4" },
              ].map(({ n, svg, label }) => (
                <Pill key={n} active={travelers === n} color={teal} onClick={() => {
                  setTravelers(n);
                  setTravelerAges([]);
                  setTravelingWithChildren(false);
                  // Sensible default vibe per count; user can override via sub-row.
                  const defaultVibe = getDefaultVibeForCount(n);
                  if (defaultVibe) setTravelCompanion(defaultVibe);
                }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{svg(travelers === n ? teal : "#4A6B8A", 16)}</span>
                    {label}
                  </span>
                </Pill>
              ))}
            </div>
            {/* Vibe sub-row — Romantic/Friends for n=2, Friends/Family for n=3-4 */}
            {travelers >= 2 && getVibeOptionsForCount(travelers).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12, paddingLeft: 2 }}>
                <span style={{ fontSize: 11, color: textDim, alignSelf: "center", marginRight: 2, fontStyle: "italic" }}>Vibe:</span>
                {getVibeOptionsForCount(travelers).map(opt => (
                  <Pill key={opt.id} active={travelCompanion === opt.id} color={teal} onClick={() => setTravelCompanion(opt.id)} small>
                    {opt.label}
                  </Pill>
                ))}
              </div>
            )}
            {/* Pet checkbox */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: travelers >= 3 ? 8 : 24 }}>
              <button onClick={() => setTravelingWithPet(p => !p)} style={{ display: "flex", alignItems: "center", gap: 8, background: travelingWithPet ? "rgba(27,58,92,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: `1px solid ${travelingWithPet ? "rgba(27,58,92,0.4)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", width: "fit-content" }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${travelingWithPet ? teal : "#F0EDE3"}`, background: travelingWithPet ? teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {travelingWithPet && <span style={{ color: "#FAFAF5", fontSize: 10, fontWeight: 900 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, color: travelingWithPet ? teal : textMuted }}>🐾 Traveling with a pet</span>
              </button>
              {/* Children — only shows for 3+ */}
              {travelers >= 3 && (
                <div>
                  <button onClick={() => setTravelingWithChildren(c => !c)} style={{ display: "flex", alignItems: "center", gap: 8, background: travelingWithChildren ? "rgba(74,140,196,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: `1px solid ${travelingWithChildren ? "rgba(74,140,196,0.4)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", width: "fit-content", marginBottom: 8 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${travelingWithChildren ? gold : "#F0EDE3"}`, background: travelingWithChildren ? gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {travelingWithChildren && <span style={{ color: "#FAFAF5", fontSize: 10, fontWeight: 900 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 13, color: travelingWithChildren ? gold : textMuted }}>👶 Traveling with children?</span>
                  </button>
                  {travelingWithChildren && (
                    <div style={{ padding: "10px 14px", background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 10, fontSize: 12, color: "#4A8CC4", lineHeight: 1.6 }}>
                      ✓ We'll prioritise family-friendly hotels, child-safe activities, and kid-friendly dining.
                    </div>
                  )}
                </div>
              )}
            </div>
            {travelers > 1 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, color: textMuted, marginBottom: 10 }}>Age ranges in your group (optional)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {Array.from({ length: travelers }).map((_, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 12, color: textMuted, minWidth: 64 }}>Traveler {i + 1}</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, flex: 1 }}>
                        {AGE_RANGES.map(a => {
                          const active = travelerAges[i] === a.id;
                          return (
                            <button key={a.id} onClick={() => setTravelerAges(prev => { const arr = [...prev]; arr[i] = active ? null : a.id; return arr; })}
                              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: active ? 700 : 400, border: `1px solid ${active ? `${a.color}80` : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: active ? `${a.color}18` : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: active ? a.color : "#4A6B8A", transition: "all 0.15s" }}>
                              <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{a.svg(active ? a.color : "#4A6B8A", 13)}</span>
                              <span>{a.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Label>Budget Per Person <span style={{ fontSize: 10, color: textDim, fontWeight: 400 }}>— flights + hotel</span></Label>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontSize: 20, fontWeight: 700, color: teal, pointerEvents: "none" }}>$</span>
              <input type="text" inputMode="numeric" value={blindBudget}
                onChange={e => setBlindBudget(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="e.g. 1500"
                style={{ width: "100%", padding: "13px 14px 13px 36px", borderRadius: 12, fontSize: 22, fontWeight: 800, fontFamily: "inherit", background: "rgba(27,58,92,0.07)", border: `1px solid ${blindBudget ? "rgba(27,58,92,0.45)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: teal, outline: "none", boxSizing: "border-box" }} />
            </div>
            {/* Group total preview — shows the actual trip total when traveling with 2+ */}
            {blindBudget && travelers > 1 && (
              <div style={{ fontSize: 11, color: textMuted, fontStyle: "italic", marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
                For {travelers} travelers, trip total budget: <strong style={{ color: "#1B3A5C", fontStyle: "normal" }}>${(parseInt(blindBudget,10) * travelers).toLocaleString()}</strong>
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 24 }}>
              {[800, 1200, 1500, 2000, 2500, 4000].map(b => (
                <Pill key={b} active={blindBudget === String(b)} color={teal} onClick={() => setBlindBudget(String(b))}>
                  ${b >= 1000 ? b / 1000 + "k" : b}
                </Pill>
              ))}
            </div>

            <Label>Travel Vibe (pick any)</Label>
            <div style={{ fontSize: 11, color: textDim, fontStyle: "italic", marginTop: -8, marginBottom: 10, lineHeight: 1.5 }}>
              We'll pre-select matching activity preferences below — adjust them however you like.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 24 }}>
              {VIBES.map(v => (
                <Pill key={v.id} active={blindVibes.includes(v.id)} color={v.color}
                  onClick={() => {
                    const newVibes = blindVibes.includes(v.id)
                      ? blindVibes.filter(x => x !== v.id)
                      : [...blindVibes, v.id];
                    setBlindVibes(newVibes);
                    // Auto-suggest activities when Activities is included
                    if (blindIncludes.includes("activities")) {
                      const suggested = [...new Set(newVibes.flatMap(vid => VIBE_TO_ACTIVITIES[vid] || []))];
                      setActivityTypes(suggested);
                    }
                  }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{v.svg(v.color)}</span>
                    {v.label}
                  </span>
                </Pill>
              ))}
            </div>

            <div id="sm-date-section" />
            <Label>When are you going?</Label>
            {/* Three-tab date mode selector */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {[
                { id: "dates", label: "📅 Departure Dates" },
                { id: "month", label: "🗓️ Pick Month" },
                { id: "duration", label: "⏱️ Trip Duration" },
              ].map(opt => (
                <button key={opt.id} onClick={() => setBlindDateMode(opt.id)}
                  style={{ flex: 1, padding: "9px 4px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: blindDateMode === opt.id ? 700 : 400, border: `1px solid ${blindDateMode === opt.id ? "rgba(27,58,92,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: blindDateMode === opt.id ? "rgba(27,58,92,0.12)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: blindDateMode === opt.id ? teal : "#4A6B8A", transition: "all 0.15s" }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {blindDateMode === "dates" && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: textMuted, marginBottom: 5 }}>Departure from</div>
                    <input type="date" value={blindDateFrom}
                      min={getMinTravelDate()}
                      onChange={e => { setBlindDateFrom(e.target.value); if (blindDateTo && e.target.value > blindDateTo) setBlindDateTo(""); }}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: blindDateFrom ? "rgba(27,58,92,0.07)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: `1px solid ${blindDateFrom ? "rgba(27,58,92,0.4)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: blindDateFrom ? teal : "#4A6B8A", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: textMuted, marginBottom: 5 }}>Return by</div>
                    <input type="date" value={blindDateTo}
                      min={blindDateFrom || getMinTravelDate()}
                      max={blindDateFrom ? (() => { const d = new Date(blindDateFrom); d.setDate(d.getDate() + 21); return d.toISOString().split("T")[0]; })() : undefined}
                      onChange={e => {
                        const diff = blindDateFrom ? Math.round((new Date(e.target.value) - new Date(blindDateFrom)) / 86400000) : 0;
                        if (diff > 21) return;
                        setBlindDateTo(e.target.value);
                      }}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", background: blindDateTo ? "rgba(27,58,92,0.07)" : darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: `1px solid ${blindDateTo ? "rgba(27,58,92,0.4)" : darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.12)"}`, color: blindDateTo ? teal : "#4A6B8A", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
                  </div>
                </div>
                {blindDateFrom && blindDateTo && (
                  <div style={{ fontSize: 12, color: teal, fontStyle: "italic" }}>
                    ✓ {Math.ceil((new Date(blindDateTo) - new Date(blindDateFrom)) / 86400000)} days · {new Date(blindDateFrom).toLocaleDateString("en-US",{month:"short",day:"numeric"})} – {new Date(blindDateTo).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                  </div>
                )}
              </div>
            )}

            {blindDateMode === "month" && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 6, marginBottom: 10 }}>
                  {MONTHS.map((m, i) => {
                    const isPast = i < new Date().getMonth();
                    const openYear = parseInt(BOOKING_OPEN_DATE.slice(0, 4), 10);
                    const openMonth = parseInt(BOOKING_OPEN_DATE.slice(5, 7), 10) - 1;
                    const currentYear = new Date().getFullYear();
                    const isPreLaunchMonth = currentYear === openYear && i < openMonth;
                    const isBlocked = isPast || isPreLaunchMonth;
                    return (
                      <button key={m}
                        onClick={() => { if (!isBlocked) setBlindMonth(blindMonth === i ? null : i); }}
                        disabled={isBlocked}
                        title={isPast ? "This month has passed" : isPreLaunchMonth ? "Coming soon" : undefined}
                        style={{ padding: "8px 4px", borderRadius: 8, fontSize: 12, textAlign: "center", fontFamily: "inherit",
                          cursor: isBlocked ? "not-allowed" : "pointer",
                          border: `1px solid ${blindMonth === i ? teal : isBlocked ? "transparent" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                          background: blindMonth === i ? "rgba(27,58,92,0.18)" : "transparent",
                          color: isBlocked ? (darkMode ? "rgba(27,58,92,0.16)" : "rgba(0,0,0,0.18)") : blindMonth === i ? teal : "#4A6B8A",
                          textDecoration: isBlocked ? "line-through" : "none",
                          opacity: isBlocked ? 0.4 : 1,
                        }}>{m}</button>
                    );
                  })}
                </div>
                {blindMonth !== null && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(27,58,92,0.12)" }}>
                    <div style={{ fontSize: 11, color: teal, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>How long?</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                      <input type="range" min={3} max={21} step={1} value={blindDuration} onChange={e => setBlindDuration(+e.target.value)} style={{ flex: 1, accentColor: teal, cursor: "pointer" }} />
                      <span style={{ color: teal, fontWeight: 800, fontSize: 15, minWidth: 60 }}>{blindDuration} nights</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {[3,5,7,10,14,21].map(n => (
                        <button key={n} onClick={() => setBlindDuration(n)}
                          style={{ padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 12, border: `1px solid ${blindDuration === n ? "rgba(27,58,92,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: blindDuration === n ? "rgba(27,58,92,0.12)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: blindDuration === n ? teal : "#4A6B8A" }}>
                          {n} nights
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: teal, fontStyle: "italic", marginTop: 10 }}>
                      ✓ {MONTHS[blindMonth]} · {blindDuration} nights
                    </div>
                  </div>
                )}
              </div>
            )}

            {blindDateMode === "duration" && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <input type="range" min={3} max={21} step={1} value={blindDuration} onChange={e => setBlindDuration(+e.target.value)} style={{ flex: 1, accentColor: teal, cursor: "pointer" }} />
                  <span style={{ color: teal, fontWeight: 800, fontSize: 16, minWidth: 52 }}>{blindDuration} nights</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[3,5,7,10,14,21].map(n => (
                    <button key={n} onClick={() => setBlindDuration(n)}
                      style={{ padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontFamily: "inherit", fontSize: 12, border: `1px solid ${blindDuration === n ? "rgba(27,58,92,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`, background: blindDuration === n ? "rgba(27,58,92,0.12)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: blindDuration === n ? teal : "#4A6B8A" }}>
                      {n} nights
                    </button>
                  ))}
                </div>
              </div>
            )}

            <LocationPicker
              region={blindRegion} setRegion={setBlindRegion}
              subRegion={blindSub} setSubRegion={setBlindSub}
              country={blindCountry} setCountry={setBlindCountry}
              stateVal={blindState} setStateVal={setBlindState}
              color={teal}
            />

            <Label>What's Included in Your Budget?</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 22 }}>
              {[
                { id: "air",        svg: JGA_ICONS.inc_flights,    label: "Flights",    desc: "Return airfare" },
                { id: "hotel",      svg: JGA_ICONS.inc_hotel,      label: "Hotel",      desc: "Accommodation" },
                { id: "transport",  svg: JGA_ICONS.inc_transport,  label: "Transport",  desc: "Local getting around" },
                { id: "activities", svg: JGA_ICONS.inc_activities, label: "Activities", desc: "Tours & experiences" },
              ].map(item => {
                const on = blindIncludes.includes(item.id);
                return (
                  <button key={item.id}
                    onClick={() => setBlindIncludes(bi => on ? bi.filter(x => x !== item.id) : [...bi, item.id])}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                      border: `1px solid ${on ? "rgba(27,58,92,0.5)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                      background: on ? "rgba(27,58,92,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{item.svg(on ? teal : "#4A6B8A", 24)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: on ? teal : "#4A6B8A" }}>{item.label}</div>
                      <div style={{ fontSize: 11, color: textDim, marginTop: 1 }}>{item.desc}</div>
                    </div>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${on ? teal : "rgba(27,58,92,0.18)"}`, background: on ? teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: "#FAFAF5", fontWeight: 900 }}>
                      {on ? "✓" : ""}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── Group 2 eyebrow ── */}

            {/* Departure Airport — trust banner (detect + manual change, with
                plain-language framing about what prices are based on) */}
            <AirportTrustBanner
              airport={blindDepartureAirport}
              accent={teal}
              onChange={(code) => {
                setBlindDepartureAirport(code);
                setExpDepartureAirport(code);
                if (registered && user && !user.departureAirport) {
                  setUser(prev => prev ? { ...prev, departureAirport: code } : prev);
                }
              }}
            />

            {blindIncludes.includes("air") && (
              <>
              {/* Flight Preferences — affects flight cost estimate which
                    affects which destinations match the budget. Direct ~+25%,
                    short layover neutral, any layover ~-8%. */}
              <Label color={teal}>Flight preferences</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, marginBottom: 16 }}>
                {[
                  { id: "no",    svg: JGA_ICONS.flight_direct, label: "Direct only",   desc: "Non-stop" },
                  { id: "short", svg: JGA_ICONS.flight_short,  label: "Short layover", desc: "Under 3h" },
                  { id: "yes",   svg: JGA_ICONS.flight_any,    label: "Any layover",   desc: "Best price" },
                ].map(opt => {
                  const on = blindLayoverOk.includes(opt.id);
                  return (
                    <button key={opt.id} onClick={() => setBlindLayoverOk(prev => on ? prev.filter(x => x !== opt.id) : [...prev, opt.id])}
                      style={{
                        padding: "10px 6px", borderRadius: 10, cursor: "pointer", textAlign: "center", fontFamily: "inherit", transition: "all 0.2s",
                        border: `1px solid ${on ? `${teal}80` : darkMode ? "rgba(27,58,92,0.10)" : "rgba(0,0,0,0.1)"}`,
                        background: on ? `${teal}15` : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)",
                      }}>
                      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }} aria-hidden="true">{opt.svg(on ? teal : "#4A6B8A", 20)}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: on ? teal : "#4A6B8A" }}>{opt.label}</div>
                      <div style={{ fontSize: 10, color: textDim, marginTop: 1 }}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              </>
            )}

            {blindIncludes.includes("hotel") && (
              <div style={{ marginBottom: 22 }}>
                <Label>Accommodation Type</Label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
                  {[
                    { id: "luxury",     svg: JGA_ICONS.hotel_luxury,     label: "Luxury",     desc: "5-star hotels & resorts" },
                    { id: "boutique",   svg: JGA_ICONS.hotel_boutique,   label: "Boutique",   desc: "Stylish, characterful stays" },
                    { id: "economical", svg: JGA_ICONS.hotel_economical, label: "Economical", desc: "Comfortable & good value" },
                    { id: "budget",     svg: JGA_ICONS.hotel_budget,     label: "Budget",     desc: "Affordable, no-frills" },
                  ].map(opt => {
                    const on = Array.isArray(hotelType) ? hotelType.includes(opt.id) : hotelType === opt.id;
                    return (
                      <button key={opt.id} onClick={() => setHotelType(prev => {
                        const arr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
                        return arr.includes(opt.id) ? arr.filter(x => x !== opt.id) : [...arr, opt.id];
                      })}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 12, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s",
                          border: `1px solid ${on ? "rgba(74,140,196,0.55)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                          background: on ? "rgba(74,140,196,0.08)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }} aria-hidden="true">{opt.svg(on ? gold : "#4A6B8A", 22)}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: on ? gold : "#4A6B8A" }}>{opt.label}</div>
                          <div style={{ fontSize: 10, color: textDim }}>{opt.desc}</div>
                        </div>
                        {on && <div style={{ marginLeft: "auto", color: goldText, fontSize: 13 }}>✓</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {blindIncludes.includes("transport") && (
              <div style={{ marginBottom: 22 }}>
                <Label>Transport Preferences (pick any)</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {[
                    { id: "bike",       svg: JGA_ICONS.trans_bike,      label: "Bike Rental" },
                    { id: "bus",        svg: JGA_ICONS.trans_bus,       label: "Bus / Coach" },
                    { id: "metro",      svg: JGA_ICONS.trans_metro,     label: "Metro / Subway" },
                    { id: "rental_car", svg: JGA_ICONS.trans_car,       label: "Rental Car" },
                    { id: "rideshare",  svg: JGA_ICONS.trans_rideshare, label: "Ride Shares" },
                    { id: "taxi",       svg: JGA_ICONS.trans_taxi,      label: "Taxis" },
                    { id: "train",      svg: JGA_ICONS.trans_train,     label: "Trains" },
                    { id: "walking",    svg: JGA_ICONS.trans_walking,   label: "Walking" },
                  ].map(opt => {
                    const on = transportType.includes(opt.id);
                    return (
                      <button key={opt.id} onClick={() => setTransportType(tt => on ? tt.filter(x => x !== opt.id) : [...tt, opt.id])}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 30, cursor: "pointer", fontFamily: "inherit", fontSize: 13, transition: "all 0.2s",
                          border: `1px solid ${on ? "rgba(74,140,196,0.55)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                          background: on ? "rgba(74,140,196,0.1)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)",
                          color: on ? gold : "#4A6B8A" }}>
                        <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{opt.svg(on ? gold : "#4A6B8A", 15)}</span>
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {blindIncludes.includes("activities") && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <Label>Activity Interests (pick any)</Label>
                  {blindVibes.length > 0 && <div style={{ fontSize: 10, color: "#6dbd6d" }}>✓ suggested from your vibe</div>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {[
                    { id: "art",           svg: JGA_ICONS.act_art,       label: "Art & Galleries" },
                    { id: "concerts",      svg: JGA_ICONS.act_concerts,  label: "Concerts & Shows" },
                    { id: "cooking",       svg: JGA_ICONS.act_cooking,   label: "Cooking Classes" },
                    { id: "food_tours",    svg: JGA_ICONS.act_food,      label: "Food Tours" },
                    { id: "hiking",        svg: JGA_ICONS.act_hiking,    label: "Hiking & Trails" },
                    { id: "museums",       svg: JGA_ICONS.act_museums,   label: "Museums" },
                    { id: "nightlife",     svg: JGA_ICONS.act_nightlife, label: "Nightlife" },
                    { id: "shopping",      svg: JGA_ICONS.act_shopping,  label: "Shopping" },
                    { id: "spa",           svg: JGA_ICONS.act_spa,       label: "Spa & Wellness" },
                    { id: "sports",        svg: JGA_ICONS.act_sports,    label: "Sports & Matches" },
                    { id: "walking_tours", svg: JGA_ICONS.act_walking,   label: "Walking Tours" },
                    { id: "water",         svg: JGA_ICONS.act_water,     label: "Water Activities" },
                    { id: "wildlife",      svg: JGA_ICONS.act_wildlife,  label: "Wildlife & Nature" },
                  ].map(opt => {
                    const on = activityTypes.includes(opt.id);
                    const suggested = blindVibes.length > 0 && blindVibes.some(vid => (VIBE_TO_ACTIVITIES[vid] || []).includes(opt.id));
                    const activeColor = on ? teal : suggested ? "#6dbd6d" : "#4A6B8A";
                    return (
                      <button key={opt.id} onClick={() => setActivityTypes(at => on ? at.filter(x => x !== opt.id) : [...at, opt.id])}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 30, cursor: "pointer", fontFamily: "inherit", fontSize: 13, transition: "all 0.2s",
                          border: `1px solid ${on ? "rgba(27,58,92,0.55)" : suggested ? "rgba(109,189,109,0.35)" : darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}`,
                          background: on ? "rgba(27,58,92,0.1)" : suggested ? "rgba(109,189,109,0.06)" : darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)",
                          color: activeColor }}>
                        <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden="true">{opt.svg(activeColor, 15)}</span>
                        <span>{opt.label}</span>
                        {suggested && !on && <span style={{ fontSize: 11, opacity: 0.7 }}>✦</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Milestone — capture occasion BEFORE matching runs.
               Per spec, milestone affects the SM match itself (boost romance
               weights for honeymoon, family-friendly for reunion, etc.).
               Session-only — resets when user changes mode or restarts SM. */}
            <MilestoneCard
              compact
              value={blindMilestone}
              onChange={(next) => { setBlindMilestone(next); setBlindMilestoneStatus("answered"); }}
              onSkip={() => { setBlindMilestoneStatus("skipped"); }}
            />

            {/* ── Preferences disclosure ──
                  Same honest expectations note as TMA: prefs steer the match
                  pool and cost estimate, but specific bookings depend on real
                  availability at the booking moment. */}
            <div style={{
              marginBottom: 18,
              padding: "10px 12px", borderRadius: 8,
              background: "rgba(27,58,92,0.04)",
              border: "1px solid rgba(27,58,92,0.12)",
              fontSize: 11, lineHeight: 1.55, color: textMuted, fontStyle: "italic",
            }}>
              We'll do our best to honor your preferences — specific flights, hotel rooms, and activity slots depend on what's available at booking time.
            </div>

            {isMinor ? (
              <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(74,140,196,0.08)", border: "1px solid rgba(74,140,196,0.25)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="9" r="3" stroke="#4A8CC4" strokeWidth="1.7" fill="rgba(74,140,196,0.2)"/>
                    <path d="M5 20 C 5 16, 8 14, 12 14 C 16 14, 19 16, 19 20" stroke="#4A8CC4" strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                  </svg>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#4A8CC4" }}>Booking unlocks at 18</div>
                </div>
                <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>You're all set up — keep exploring destinations, save trips to your wishlist, and search itineraries. Booking will be available the day you turn 18.</div>
              </div>
            ) : (
              <Btn variant="teal" onClick={() => {
                // v1: preview-only flow. Was: opened payment modal.
                // Now: filters destinations, shows spinning globe ~2.4s,
                // reveals 3 sample destinations, then waitlist CTA.
                const parsedBudget = parseInt(String(blindBudget).replace(/\D/g,""), 10) || 1500;
                const blindAirport = blindDepartureAirport || expDepartureAirport || user?.departureAirport || null;
                const pool = DESTINATIONS.filter(d => {
                  const baselineCost = getBaselineTripCost(d, blindAirport, 5);
                  return parsedBudget >= baselineCost;
                });
                if (!pool.length) {
                  const minOption = minBudgetForFilters(blindRegion, blindSub, blindCountry, blindState);
                  if (minOption) {
                    const rounded = Math.ceil(minOption.cost / 100) * 100;
                    setBlindResult({ error: true, message: `No destinations fit a $${parsedBudget.toLocaleString()} budget right now. The cheapest match in this region is ${minOption.dest.name} at about $${rounded.toLocaleString()}.` });
                  } else {
                    setBlindResult({ error: true, message: `No destinations fit a $${parsedBudget.toLocaleString()} budget. International trips from the US typically start around $1,800; domestic around $1,500.` });
                  }
                  return;
                }
                // Milestone-aware scoring helpers. Per spec, milestone reweights
                // identity scoring subtly — bonus points for destinations whose
                // tags align with the occasion. Range 0-3, additive to vibe score.
                // "just-because" or skipped contributes 0 (no reweighting).
                const milestoneBoostTags = {
                  "honeymoon":          ["romance", "luxury", "beach", "wellness"],
                  "anniversary":        ["romance", "luxury", "scenic", "wellness"],
                  "milestone-birthday": ["luxury", "scenic", "city", "festival"],
                  "graduation":         ["adventure", "festival", "city", "nightlife"],
                  "family-reunion":     ["family-friendly", "scenic", "cultural", "outdoor"],
                  "reset-moment":       ["wellness", "scenic", "outdoor", "spiritual"],
                  "gift-for-someone":   ["luxury", "romance", "scenic", "wellness"],
                };
                const milestoneType = blindMilestone?.type;
                const boostTags = (milestoneType && milestoneType !== "just-because" && milestoneType !== "other")
                  ? (milestoneBoostTags[milestoneType] || [])
                  : [];
                // Apply vibe-based scoring if user picked vibes — better samples match better.
                // Layer milestone tag matches on top: each matching tag adds 1 (cap at 3).
                const scored = pool.map(d => {
                  const vibeScore = (blindVibes && blindVibes.length > 0)
                    ? (d.tags || []).filter(t => blindVibes.includes(t)).length
                    : 0;
                  const milestoneScore = boostTags.length
                    ? Math.min(3, (d.tags || []).filter(t => boostTags.includes(t)).length)
                    : 0;
                  return { d, score: vibeScore + milestoneScore };
                }).sort((a, b) => b.score - a.score);
                // Take the top scored, then shuffle slightly for variety
                const topPool = scored.slice(0, Math.min(12, scored.length)).map(s => s.d);
                const shuffled = [...topPool].sort(() => Math.random() - 0.5);
                setBlindPreviewSamples(shuffled.slice(0, 3));
                setBlindPreviewStep("spinning");
                // Track preview start for funnel analytics
                if (typeof window.jgaTrack === "function") window.jgaTrack("surprise_me_preview_start", { budget: parsedBudget, vibes: blindVibes, milestone: blindMilestone?.type, milestone_status: blindMilestoneStatus });
                // Auto-advance to reveal after the spinning animation
                setTimeout(() => {
                  setBlindPreviewStep("revealed");
                  if (typeof window.jgaTrack === "function") window.jgaTrack("surprise_me_preview_revealed");
                }, 2400);
              }}>Show Me a Preview ✨</Btn>
            )}
            <Btn variant="ghost" onClick={() => setShowNotBookingModal(true)} style={{ marginTop: 8, fontSize: 13, color: textDim }}>← Not ready to book yet?</Btn>
          </div>
        )}

        {/* ── Surprise Me v1 Preview + Waitlist ──
            Replaces the old payment / booking flow. Two phases driven by
            blindPreviewStep: "spinning" (wheel animation) → "revealed"
            (samples + waitlist CTA). Bypasses BlindGateModal (the auth
            gate) so anonymous users can experience the preview — the
            waitlist signup itself captures the lead, registration is
            optional. */}
        {mode === "blind" && blindPreviewStep && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <style>{`
              @keyframes jga-wheel-spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(720deg); }
              }
              @keyframes jga-flag-stream {
                0% { transform: translateX(120%); opacity: 0; }
                15% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translateX(-120%); opacity: 0; }
              }
              @keyframes jga-card-rise {
                0% { transform: translateY(20px); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
              }
              .jga-spin-wheel { animation: jga-wheel-spin 2.4s cubic-bezier(0.5, 0, 0.2, 1) forwards; }
              .jga-flag-1 { animation: jga-flag-stream 1.2s ease-in-out 0s 2; }
              .jga-flag-2 { animation: jga-flag-stream 1.2s ease-in-out 0.2s 2; }
              .jga-flag-3 { animation: jga-flag-stream 1.2s ease-in-out 0.4s 2; }
              .jga-flag-4 { animation: jga-flag-stream 1.2s ease-in-out 0.6s 2; }
              .jga-flag-5 { animation: jga-flag-stream 1.2s ease-in-out 0.8s 2; }
              .jga-sample-1 { animation: jga-card-rise 0.5s ease 0s both; }
              .jga-sample-2 { animation: jga-card-rise 0.5s ease 0.15s both; }
              .jga-sample-3 { animation: jga-card-rise 0.5s ease 0.3s both; }
              @media (prefers-reduced-motion: reduce) {
                .jga-spin-wheel, [class^="jga-flag-"], [class^="jga-sample-"] { animation: none !important; }
              }
            `}</style>

            {/* Spinning phase — wheel + streaming flags */}
            {blindPreviewStep === "spinning" && (
              <div style={{ textAlign: "center", padding: "48px 20px" }}>
                <div style={{ position: "relative", height: 200, marginBottom: 24, overflow: "hidden" }}>
                  {/* Streaming flag emojis behind the wheel */}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <div style={{ display: "flex", gap: 30, fontSize: 28, opacity: 0.4 }}>
                      <span className="jga-flag-1">🇫🇷</span>
                      <span className="jga-flag-2">🇯🇵</span>
                      <span className="jga-flag-3">🇲🇽</span>
                      <span className="jga-flag-4">🇮🇹</span>
                      <span className="jga-flag-5">🇿🇦</span>
                    </div>
                  </div>
                  {/* Spinning wheel — uses the JGA globe SVG */}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg className="jga-spin-wheel" width="140" height="140" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                      <circle cx="22" cy="22" r="19" fill={`${teal}18`} stroke={teal} strokeWidth="2"/>
                      <ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke={teal} strokeWidth="1.6"/>
                      <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={teal} strokeWidth="1.6" fill="none"/>
                      <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                      <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={teal} strokeWidth="1.3" fill="none" opacity="0.7"/>
                    </svg>
                  </div>
                </div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: teal, marginBottom: 6, letterSpacing: -0.3 }}>Spinning the globe…</h2>
                <div style={{ fontSize: 13, color: textMuted, fontStyle: "italic" }}>Matching your vibe to the world</div>
              </div>
            )}

            {/* Revealed phase — sample destinations + waitlist CTA */}
            {blindPreviewStep === "revealed" && (
              <div>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 28 }}>
                  <div style={{ fontSize: 10, color: textDim, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>A taste of what's possible</div>
                  <h2 style={{ margin: 0, fontSize: 28, fontWeight: 900, color: textPrimary, letterSpacing: -0.5, lineHeight: 1.15, marginBottom: 8 }}>Your surprise could be one of these</h2>
                  <div style={{ fontSize: 13, color: textMuted, lineHeight: 1.5, maxWidth: 380, margin: "0 auto" }}>
                    When Surprise Me launches, you'll get a single mystery destination handpicked for your vibe — revealed when you're ready.
                  </div>
                </div>

                {/* Sample destination cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                  {blindPreviewSamples.map((d, i) => (
                    <div key={d.name} className={`jga-sample-${i + 1}`} style={{ background: `${teal}08`, border: `1px solid ${teal}25`, borderRadius: 14, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                      <span style={{ fontSize: 36, flexShrink: 0, lineHeight: 1 }}>{d.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>{d.country}{d.bestTime ? ` · Best: ${d.bestTime.split(" or ")[0]}` : ""}</div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {(d.tags || []).slice(0, 3).map(t => (
                            <span key={t} style={{ fontSize: 9, color: teal, background: `${teal}15`, padding: "2px 7px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Try again nudge — added Jun 19, 2026. Sits between the
                   preview cards and the waitlist CTA so users who don't see
                   themselves in the 3 picks have an obvious off-ramp back to
                   the input form. Resets blindPreviewStep → null which routes
                   to the input page; previous selections persist so they
                   only have to tweak. */}
                <div style={{ textAlign: "center", marginBottom: 24, padding: "0 8px" }}>
                  <div style={{ fontSize: 13, color: textMuted, lineHeight: 1.5, fontStyle: "italic", marginBottom: 12 }}>
                    Not quite what you were thinking? Change your preferences and spin again.
                  </div>
                  <button
                    onClick={() => {
                      setBlindPreviewStep(null);
                      setBlindPreviewSamples([]);
                      if (typeof window.jgaTrack === "function") window.jgaTrack("surprise_me_preview_try_again");
                    }}
                    style={{
                      padding: "10px 22px", borderRadius: 12, border: `1px solid ${teal}40`,
                      background: "transparent", color: teal,
                      fontSize: 13, fontWeight: 800, cursor: "pointer",
                      fontFamily: "inherit", letterSpacing: 0.3,
                    }}>
                    Try again
                  </button>
                </div>

                {/* Waitlist CTA */}
                <div style={{ background: `linear-gradient(135deg, ${teal}10, ${gold}08)`, border: `1px solid ${teal}30`, borderRadius: 16, padding: "20px 18px", marginBottom: 16 }}>
                  {previewWaitlistStatus === "ok" || previewWaitlistStatus === "already" ? (
                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: teal, marginBottom: 6 }}>
                        {previewWaitlistStatus === "already" ? "You're already on the list!" : "You're on the list!"}
                      </div>
                      <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
                        We'll email you the moment Surprise Me bookings open. In the meantime — keep exploring, saving boards, and building your travel identity.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ textAlign: "center", marginBottom: 14 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: textPrimary, marginBottom: 4 }}>Surprise Me bookings open soon</div>
                        <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>Be first in line — get notified the day it launches.</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
                        <input
                          type="email"
                          value={previewWaitlistEmail}
                          onChange={e => { setPreviewWaitlistEmail(e.target.value); setPreviewWaitlistError(""); }}
                          placeholder={user?.email || "you@email.com"}
                          autoComplete="email"
                          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(255,255,255,0.7)", border: `1px solid ${teal}30`, color: textPrimary, outline: "none", boxSizing: "border-box" }}
                        />
                        {previewWaitlistError && (
                          <div style={{ fontSize: 11, color: "#c44", marginTop: -2 }}>{previewWaitlistError}</div>
                        )}
                        <button
                          disabled={previewWaitlistSubmitting}
                          onClick={async () => {
                            const email = (previewWaitlistEmail || user?.email || "").trim();
                            if (!email || !email.includes("@")) {
                              setPreviewWaitlistError("Please enter a valid email.");
                              return;
                            }
                            setPreviewWaitlistSubmitting(true);
                            setPreviewWaitlistError("");
                            try {
                              const parsedBudget = parseInt(String(blindBudget).replace(/\D/g, ""), 10) || null;
                              const result = await joinWaitlist({
                                email,
                                mode: "surprise",
                                customerName: user?.firstName || user?.name || undefined,
                                departureAirport: blindDepartureAirport || expDepartureAirport || user?.departureAirport || undefined,
                                duration: blindDuration || undefined,
                                budget: parsedBudget || undefined,
                                travelers: parseInt(travelers, 10) || undefined,
                                vibes: blindVibes && blindVibes.length ? blindVibes : undefined,
                                includes: blindIncludes && blindIncludes.length ? blindIncludes : undefined,
                                milestoneType: blindMilestone?.type || undefined,
                                milestoneOtherText: blindMilestone?.type === "other" ? (blindMilestone.otherText || undefined) : undefined,
                                milestoneStatus: blindMilestoneStatus || undefined,
                              });
                              setPreviewWaitlistSubmitting(false);
                              if (result.ok) {
                                setPreviewWaitlistStatus(result.alreadyOnWaitlist ? "already" : "ok");
                                if (typeof window.jgaTrack === "function") {
                                  window.jgaTrack("surprise_me_waitlist_join", {
                                    already: !!result.alreadyOnWaitlist,
                                    milestone: blindMilestone?.type,
                                    milestone_status: blindMilestoneStatus,
                                  });
                                }
                              } else {
                                setPreviewWaitlistStatus("error");
                                setPreviewWaitlistError(result.error || "Couldn't join the waitlist. Try again?");
                              }
                            } catch (err) {
                              setPreviewWaitlistSubmitting(false);
                              setPreviewWaitlistStatus("error");
                              setPreviewWaitlistError("Couldn't join the waitlist. Try again?");
                            }
                          }}
                          style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${teal}, ${goldText})`, color: "#FAFAF5", fontWeight: 800, fontSize: 14, cursor: previewWaitlistSubmitting ? "wait" : "pointer", fontFamily: "inherit", opacity: previewWaitlistSubmitting ? 0.6 : 1 }}>
                          {previewWaitlistSubmitting ? "Adding you…" : "Join the Waitlist →"}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Back to preferences */}
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={() => {
                      setBlindPreviewStep(null);
                      setBlindPreviewSamples([]);
                      setPreviewWaitlistEmail("");
                      setPreviewWaitlistStatus(null);
                      setPreviewWaitlistError("");
                    }}
                    style={{ background: "none", border: "none", color: textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: "8px 0" }}>
                    ← Change my preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Blind loading */}
        {mode === "blind" && blindLoading && (
          <div style={{ ...dynCard, textAlign: "center", padding: "48px 24px", animation: "fadeIn 0.3s ease" }}>
            <div style={{ fontSize: 40, marginBottom: 20, animation: "pulse 1.5s ease-in-out infinite" }}>🔐</div>
            <h2 style={{ color: teal, fontWeight: 800, marginBottom: 10, fontSize: 20 }}>Unlocking your trip…</h2>
            <RotatingSubtitle lines={["Finding the perfect match for you…", "Checking flights and stays…", "This one's special…"]} />
          </div>
        )}

        {/* Blind sealed envelope */}
        {mode === "blind" && blindResult && !blindResult.error && !blindRevealed && (
          <div style={{ ...dynCard, textAlign: "center", padding: "40px 28px", animation: "fadeIn 0.4s ease" }}>
            <div style={{ fontSize: 11, letterSpacing: 4, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 16 }}>Your booking is confirmed</div>
            <div style={{ fontSize: 72, marginBottom: 16, display: "inline-block", filter: "drop-shadow(0 0 20px rgba(27,58,92,0.4))", transition: "transform 0.5s", transform: envelopeOpen ? "scale(1.2)" : "scale(1)" }}>
              {envelopeOpen ? "📭" : "📩"}
            </div>
            <h2 style={{ color: teal, fontWeight: 900, fontSize: 22, marginBottom: 8 }}>Your Mystery Trip is Locked In!</h2>
            <p style={{ color: textMuted, fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>
              {travelers} traveler{travelers > 1 ? "s" : ""} · {blindDuration} nights · {blindMonth !== null ? MONTHS[blindMonth] : "Flexible dates"}
            </p>
            <p style={{ color: "#4A8CC4", fontSize: 13, marginBottom: 8 }}>Confirmation: <strong style={{ color: textPrimary }}>{blindResult.confirmationCode}</strong></p>
            <div style={{ background: "rgba(27,58,92,0.07)", border: "1px solid rgba(27,58,92,0.15)", borderRadius: 12, padding: "12px 16px", marginBottom: 8, fontSize: 13, color: "#1B3A5C" }}>
              🗓️ <strong style={{ color: textPrimary }}>{blindResult.checkIn}</strong> → <strong style={{ color: textPrimary }}>{blindResult.checkOut}</strong>
            </div>
            <div style={{ background: "rgba(74,140,196,0.07)", border: "1px solid rgba(74,140,196,0.15)", borderRadius: 12, padding: "12px 16px", marginBottom: 24, fontSize: 14, color: goldText, fontWeight: 700 }}>
              💰 Estimated total: {blindResult.totalEstimate}
            </div>
            {/* Reroll — 1 free — above reveal */}
            <button onClick={() => {
              if (blindRerollsUsed >= 1) return;
              setBlindRerollsUsed(r => r + 1);
              setBlindResult(null);
              runBlind();
            }} disabled={blindRerollsUsed >= 1}
              style={{ width: "100%", marginBottom: 10, padding: "11px", borderRadius: 12, border: `1px solid ${blindRerollsUsed >= 1 ? "rgba(27,58,92,0.072)" : "rgba(27,58,92,0.3)"}`, background: "transparent", color: blindRerollsUsed >= 1 ? textDim : teal, fontWeight: 700, fontSize: 13, cursor: blindRerollsUsed >= 1 ? "not-allowed" : "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              {blindRerollsUsed >= 1 ? "Reroll used" : "Free Reroll — pick a different destination"}
            </button>

            <Btn variant="teal" onClick={() => { setEnvelopeOpen(true); setTimeout(() => setBlindRevealed(true), 2500); }}>🎁 Reveal My Trip</Btn>
            <p style={{ fontSize: 11, color: textDim, textAlign: "center", marginTop: 10, lineHeight: 1.6, fontStyle: "italic" }}>
              Details shown are AI-generated estimates.{travelers > 1 ? " Group estimates assume about 2 guests per room." : ""} Final flights, accommodation, and pricing may be subject to change based on booking availability. Your confirmed itinerary will be sent by email.
            </p>
            <Btn variant="ghost" onClick={() => { setBlindResult(null); setBlindRerollsUsed(0); setShowSwapConfirm(false); }} style={{ marginTop: 8 }}>↩ Change preferences</Btn>
          </div>
        )}

        {/* Blind reveal */}
        {mode === "blind" && blindResult && !blindResult.error && blindRevealed && (
          <div style={{ animation: "revealDrop 0.6s cubic-bezier(0.34,1.56,0.64,1)" }}>
            <style>{`
              @keyframes revealDrop { from { opacity:0; transform:scale(0.88) translateY(20px); } to { opacity:1; transform:scale(1) translateY(0); } }
              @keyframes emojiPop   { 0%{transform:scale(0) rotate(-15deg)} 60%{transform:scale(1.25) rotate(4deg)} 100%{transform:scale(1) rotate(0)} }
              @keyframes titleSlide { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
              @keyframes shimmer    { 0%,100%{opacity:0.6} 50%{opacity:1} }
              @keyframes confettiFall {
                0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
                100% { transform: translateY(120px) rotate(720deg); opacity: 0; }
              }
            `}</style>

            {/* Full-bleed hero banner */}
            <div style={{ borderRadius: 20, overflow: "hidden", marginBottom: 16, position: "relative", background: "linear-gradient(160deg,#0a1a2e 0%,#0d2a1a 40%,#1a0a2e 100%)", border: "1px solid rgba(74,140,196,0.3)", boxShadow: `0 0 60px rgba(74,140,196,0.12), 0 0 120px rgba(27,58,92,0.08)` }}>

              {/* Confetti burst — CSS only */}
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                {["#4A8CC4","#4A8CC4","#ff6b6b","#6dbd6d","#1B3A5C","#a8d8ea"].flatMap((color, ci) =>
                  [0,1,2,3].map(i => (
                    <div key={`${ci}-${i}`} style={{
                      position: "absolute",
                      left: `${10 + ci * 14 + i * 3}%`,
                      top: "-10px",
                      width: i % 2 === 0 ? 6 : 4,
                      height: i % 2 === 0 ? 12 : 8,
                      borderRadius: i % 3 === 0 ? "50%" : 2,
                      background: color,
                      opacity: 0.8,
                      animation: `confettiFall ${1.2 + ci * 0.15 + i * 0.1}s ease-in ${ci * 0.08 + i * 0.05}s both`,
                    }} />
                  ))
                )}
              </div>

              {/* Confetti burst on reveal */}
              {blindRevealed && (
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 120, pointerEvents: "none", overflow: "hidden", zIndex: 2 }}>
                  {["🎊","✨","🌟","🎉","⭐","💫","🎈","🏖️"].map((e, i) => (
                    <div key={i} style={{ position: "absolute", left: `${10 + i * 11}%`, top: 0, fontSize: 18, animation: `confettiFall ${0.8 + i * 0.15}s ease-out ${i * 0.08}s both` }}>{e}</div>
                  ))}
                </div>
              )}

              {/* Ambient glow rings */}
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(74,140,196,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

              <div style={{ padding: "36px 28px 28px", textAlign: "center", position: "relative", zIndex: 1 }}>
                <div style={{ fontSize: 11, letterSpacing: 5, color: "#4A8CC4", textTransform: "uppercase", marginBottom: 16, animation: "shimmer 2s ease-in-out infinite" }}>
                  ✦ &nbsp; Your destination awaits &nbsp; ✦
                </div>

                {/* Giant emoji */}
                <div style={{ fontSize: 88, lineHeight: 1, marginBottom: 16, display: "inline-block", animation: "revealBurst 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.1s both", filter: `drop-shadow(0 0 32px rgba(74,140,196,0.6)) drop-shadow(0 0 60px rgba(74,140,196,0.2))` }}>
                  {blindResult.emoji}
                </div>

                {/* Destination name */}
                <h2 style={{ fontSize: "clamp(2.4rem,9vw,3.8rem)", fontWeight: 900, margin: "0 0 6px", lineHeight: 1, ...gradientTextStyle(`linear-gradient(135deg,#fff8e0 0%,${gold} 40%,${amber} 100%)`, gold), letterSpacing: "-1px", animation: "titleSlide 0.5s ease 0.3s both" }}>
                  {blindResult.destination}
                </h2>

                {blindResult.state && (
                  <div style={{ color: textMuted, fontSize: 14, marginBottom: 2, animation: "titleSlide 0.5s ease 0.4s both" }}>{blindResult.state}</div>
                )}
                <div style={{ color: textMuted, fontSize: 15, marginBottom: 14, animation: "titleSlide 0.5s ease 0.45s both" }}>{blindResult.country}</div>

                {/* Tagline */}
                <div style={{ color: "#4A8CC4", fontSize: 15, fontStyle: "italic", lineHeight: 1.6, maxWidth: 340, margin: "0 auto 20px", animation: "titleSlide 0.5s ease 0.5s both" }}>
                  "{blindResult.tagline}"
                </div>

                {/* Quick stats strip */}
                <div style={{ display: "flex", justifyContent: "center", gap: 20, animation: "titleSlide 0.5s ease 0.6s both" }}>
                  {[
                    ["✈️", blindResult.flightEstimate],
                    ["🏨", `${blindResult.hotelStars}★`],
                    ["💰", blindResult.totalEstimate],
                  ].map(([icon, val]) => (
                    <div key={icon} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 18, marginBottom: 3 }}>{icon}</div>
                      <div style={{ fontSize: 11, color: "#4A8CC4", fontWeight: 700 }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Share buttons */}
                <div style={{ marginTop: 20, animation: "titleSlide 0.5s ease 0.7s both" }}>
                  <div style={{ fontSize: 11, color: textDim, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Share your trip</div>
                  <div style={{ fontSize: 13, color: textMuted, marginBottom: 12, fontStyle: "italic" }}>
                    `I just booked a surprise trip to ${blindResult.destination} for ${blindResult.totalEstimate} 🌍✈️ at www.justgoanywhere.com`
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                    {[
                      { label: "TikTok", icon: "🎵", color: "#010101", share: true },
                      { label: "Instagram", icon: "📸", color: "#E1306C", share: true },
                      { label: "Facebook", icon: "👥", color: "#1877f2", url: `https://www.facebook.com/sharer/sharer.php?u=https://justgoanywhere.com&quote=I just booked a surprise trip to ${blindResult.destination} for ${blindResult.totalEstimate} 🌍✈️ at www.justgoanywhere.com` },
                      { label: "Text/iMessage", icon: "💬", color: "#6dbd6d", url: `sms:?body=I just booked a surprise trip to ${encodeURIComponent(blindResult.destination)} for ${encodeURIComponent(blindResult.totalEstimate || "")} 🌍✈️ at www.justgoanywhere.com` },
                    ].map(s => {
                      const sharedStyle = { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, border: "1px solid rgba(27,58,92,0.120)", background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)", textDecoration: "none", fontSize: 12, color: "#4A8CC4", fontWeight: 600, transition: "all 0.15s", cursor: "pointer", fontFamily: "inherit" };
                      return s.share ? (
                        <button key={s.label} onClick={() => {
                          const text = `I just booked a surprise trip to ${blindResult.destination} for ${blindResult.totalEstimate} 🌍✈️ at www.justgoanywhere.com`;
                          if (navigator.share) { navigator.share({ title: "Just Go Anywhere", text, url: "https://justgoanywhere.com" }); }
                          else { navigator.clipboard?.writeText(text); }
                        }} aria-label={`Share to ${s.label}`} style={sharedStyle}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)"}>
                          <span>{s.icon}</span> {s.label}
                        </button>
                      ) : (
                        <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" style={sharedStyle}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.1)"}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.05)"}>
                          <span>{s.icon}</span> {s.label}
                        </a>
                      );
                    })}
                    <button onClick={() => { navigator.share ? navigator.share({ title: "Just Go Anywhere", text: `I just booked a surprise trip to ${blindResult.destination} for ${blindResult.totalEstimate} 🌍✈️ at www.justgoanywhere.com`, url: "https://justgoanywhere.com" }) : navigator.clipboard.writeText(`I just booked a surprise trip to ${blindResult.destination} for ${blindResult.totalEstimate} 🌍✈️ at www.justgoanywhere.com`); }}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, border: "1px solid rgba(27,58,92,0.3)", background: "rgba(27,58,92,0.08)", fontSize: 12, color: teal, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      📤 Share
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking details — collapsed by default look */}
            <div style={{ ...dynCard, padding: 16, marginBottom: 14, border: "1px solid rgba(27,58,92,0.18)", animation: "titleSlide 0.4s ease 0.7s both" }}>
              <Label>Booking Details</Label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[["✈️ Flights", blindResult.flightEstimate], ["🏨 Hotel", `${blindResult.hotelName} (${blindResult.hotelStars}★)`], ["🗓️ Check-in", blindResult.checkIn], ["🗓️ Check-out", blindResult.checkOut], ["👤 Travelers", `${travelers} person${travelers > 1 ? "s" : ""}`], ["💰 Total", blindResult.totalEstimate]].map(([k, v]) => (
                  <div key={k} style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: textDim, marginBottom: 3 }}>{k}</div>
                    <div style={{ color: textPrimary, fontWeight: 700, fontSize: 12 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: teal, textAlign: "center" }}>Confirmation: <strong>{blindResult.confirmationCode}</strong></div>
            </div>

            {blindResult.surpriseFact && (
              <div style={{ background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.12)", borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontSize: 13, color: "#b0903a", fontStyle: "italic", animation: "titleSlide 0.4s ease 0.8s both" }}>
                🌟 {blindResult.surpriseFact}
              </div>
            )}

            {blindResult.days?.length > 0 && (
              <div style={{ marginBottom: 14, animation: "titleSlide 0.4s ease 0.85s both" }}>
                <Label>Trip Highlights</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {blindResult.days.map(d => (
                    <div key={d.day} style={{ display: "flex", gap: 10, background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: "10px 12px", border: "1px solid rgba(27,58,92,0.072)" }}>
                      <span style={{ color: goldText, fontWeight: 800, fontSize: 12, minWidth: 36 }}>Day {d.day}</span>
                      <div>
                        <div style={{ fontSize: 12, color: textPrimary, fontWeight: 700 }}>{d.title}</div>
                        <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{d.highlight}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {blindResult.packingTip && (
              <div style={{ background: "rgba(100,175,207,0.07)", borderRadius: 10, padding: "10px 14px", marginBottom: 20, border: "1px solid rgba(100,175,207,0.15)", fontSize: 12, color: "#7aafcf", fontStyle: "italic" }}>
                🎒 {blindResult.packingTip}
              </div>
            )}

            {/* ── Cancellation flow ── */}
            {blindCancelStep === 0 && (
              <>
                <Btn variant="teal" onClick={() => { setBlindResult(null); setBlindRevealed(false); setEnvelopeOpen(false); setBlindCancelStep(0); setBlindCancelReason(""); }}>🎭 Book Another Surprise</Btn>

                {/* Destination swap — after reveal */}
                {!showSwapConfirm ? (
                  <button onClick={() => setShowSwapConfirm(true)}
                    style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 12, border: "1px solid rgba(74,140,196,0.3)", background: "rgba(74,140,196,0.05)", color: goldText, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                    🔄 Request Destination Swap
                  </button>
                ) : (
                  <div style={{ marginTop: 10, background: "rgba(74,140,196,0.06)", border: "1px solid rgba(74,140,196,0.2)", borderRadius: 12, padding: "14px", textAlign: "left" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: goldText, marginBottom: 6 }}>Destination Swap</div>
                    <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.6, marginBottom: 12 }}>We'll select a new destination for you. Your booking terms and dates stay the same. This can only be used once.</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setBlindRevealed(false); setEnvelopeOpen(false); setShowSwapConfirm(false); setBlindResult(null); runBlind(); }}
                        style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${gold},${amber})`, color: "#FAFAF5", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                        Yes, Swap It →
                      </button>
                      <button onClick={() => setShowSwapConfirm(false)}
                        style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${borderColor}`, background: "transparent", color: textDim, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                        Keep It
                      </button>
                    </div>
                  </div>
                )}

                <button onClick={() => setBlindCancelStep(1)} style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 12, border: "1px solid rgba(255,107,107,0.3)", background: "rgba(255,107,107,0.06)", color: coral, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel This Trip
                </button>
                <Btn variant="ghost" onClick={reset} style={{ marginTop: 8 }}>← Back to home</Btn>
              </>
            )}

            {blindCancelStep === 1 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <button onClick={() => setBlindCancelStep(0)} style={{ background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.06)", border: "1px solid rgba(27,58,92,0.120)", color: textMuted, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} aria-label="Go back">← Back</button>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: coral }}>Reason for Cancellation</h2>
                </div>
                <p style={{ color: textMuted, fontSize: 13, marginBottom: 16, fontStyle: "italic" }}>This helps us improve future Surprise Me recommendations.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {[
                    { id: "dates", label: "Dates don't work anymore" },
                    { id: "destination", label: "Not happy with the destination" },
                    { id: "budget", label: "Budget constraints" },
                    { id: "personal", label: "Personal circumstances changed" },
                    { id: "other", label: "Other reason" },
                  ].map(r => (
                    <button key={r.id} onClick={() => setBlindCancelReason(r.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s",
                        border: `1px solid ${blindCancelReason === r.id ? "rgba(255,107,107,0.5)" : darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)"}`,
                        background: blindCancelReason === r.id ? "rgba(255,107,107,0.08)" : "rgba(27,58,92,0.030)" }}>
                      <span style={{ fontSize: 13, color: blindCancelReason === r.id ? "#f0c0c0" : "#4A6B8A" }}>{r.label}</span>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${blindCancelReason === r.id ? coral : "rgba(27,58,92,0.18)"}`, background: blindCancelReason === r.id ? coral : "transparent" }} />
                    </button>
                  ))}
                </div>
                <div style={{ background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: textMuted }}>Amount paid</span>
                    <span style={{ fontSize: 13, color: textPrimary }}>${parseInt(String(blindBudget).replace(/\D/g,""),10).toLocaleString()}</span>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,107,107,0.15)", paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: textMuted }}>Refund (100%)</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: darkMode ? "#6dbd6d" : "#2a7a4a" }}>${parseInt(String(blindBudget).replace(/\D/g,""),10).toLocaleString()}</span>
                  </div>
                </div>
                <button onClick={() => setBlindCancelStep(2)} disabled={!blindCancelReason}
                  style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: !blindCancelReason ? darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.1)" : `linear-gradient(135deg,#FF6B6B,${coral})`, color: !blindCancelReason ? "#4A6B8A" : "#fff", fontWeight: 900, fontSize: 15, cursor: !blindCancelReason ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  Confirm Cancellation & Full Refund
                </button>
                <button onClick={() => setBlindCancelStep(0)} style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, border: "1px solid rgba(27,58,92,0.120)", background: darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.04)", color: textMuted, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  ← Keep My Trip
                </button>
              </div>
            )}

            {blindCancelStep === 2 && (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                  <svg width="72" height="72" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="22" cy="22" r="19" fill="rgba(109,189,109,0.12)" stroke="#6dbd6d" strokeWidth="2"/>
                    <circle cx="22" cy="22" r="13" fill="rgba(109,189,109,0.08)" stroke="#6dbd6d" strokeWidth="1.2" opacity="0.6"/>
                    <path d="M13 22 L19 28 L31 16" stroke="#6dbd6d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 900, color: "#6dbd6d", marginBottom: 8 }}>Trip Cancelled</h2>
                <p style={{ color: textMuted, fontSize: 13, marginBottom: 22, lineHeight: 1.6 }}>
                  Your Surprise Me trip to {blindResult.destination} has been cancelled. A confirmation has been sent to your email.
                </p>
                <div style={{ background: "rgba(109,189,109,0.07)", border: "1px solid rgba(109,189,109,0.2)", borderRadius: 14, padding: "18px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#6dbd6d", textTransform: "uppercase", marginBottom: 10 }}>Refund Details</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: textMuted }}>Refund amount</span>
                    <span style={{ fontSize: 22, fontWeight: 900, color: "#6dbd6d" }}>${parseInt(String(blindBudget).replace(/\D/g,""),10).toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: 12, color: textDim, lineHeight: 1.6 }}>Returned to your original payment method · Allow 5–10 business days</div>
                </div>
                <Btn variant="ghost" onClick={() => { setBlindResult(null); setBlindRevealed(false); setEnvelopeOpen(false); setBlindCancelStep(0); setBlindCancelReason(""); reset(); }}>Back to Just Go Anywhere</Btn>
              </div>
            )}
          </div>
        )}

        {blindResult?.error && (
          <div style={{ ...dynCard, textAlign: "center", padding: "36px 24px" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>✈️</div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 900, color: goldText }}>In-flight, please hold</h2>
            <p style={{ color: textMuted, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
              Our booking crew hit a spot of turbulence. We're circling back around — give it a moment and try again.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={() => setBlindResult(null)} style={{ flex: 1 }}>Try Again →</Btn>
              <Btn variant="ghost" onClick={reset} style={{ flex: 1 }}>← Home</Btn>
            </div>
          </div>
        )}

        {/* ── FOOTER V3 ── */}
        <div style={{ marginTop: 32 }}>
          {/* Top rule with globe motif */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: darkMode ? "linear-gradient(to right, transparent, rgba(74,140,196,0.45), transparent)" : "linear-gradient(to right, transparent, rgba(27,58,92,0.4), transparent)" }} />
            <svg width="22" height="22" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.85 }}>
              <circle cx="22" cy="22" r="19" fill="none" stroke={darkMode ? gold : teal} strokeWidth="1.5"/>
              <ellipse cx="22" cy="22" rx="8" ry="19" fill="none" stroke={darkMode ? gold : teal} strokeWidth="1.2"/>
              <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={darkMode ? gold : teal} strokeWidth="1.2" fill="none"/>
            </svg>
            <div style={{ flex: 1, height: 1, background: darkMode ? "linear-gradient(to right, transparent, rgba(74,140,196,0.45), transparent)" : "linear-gradient(to right, transparent, rgba(27,58,92,0.4), transparent)" }} />
          </div>

          {/* Footer grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 16 }}>
            {/* Brand column */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 3, color: darkMode ? "#4A8CC4" : teal, textTransform: "uppercase", marginBottom: 6 }}>
                Just Go Anywhere™
              </div>
              <p style={{ fontSize: 11, color: darkMode ? "#4A6B8A" : textDim, lineHeight: 1.6, margin: 0, marginBottom: 8 }}>
                Find your travel identity. Discover where to go. Remember every trip.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                {/* Instagram */}
                <a href="https://www.instagram.com/justgoanywhere.travel" target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", border: `1px solid ${darkMode ? "rgba(27,58,92,0.16)" : "rgba(0,0,0,0.08)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", transition: "all 0.2s", textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(74,140,196,0.4)"; e.currentTarget.style.background = "rgba(74,140,196,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"; }}
                  title="@justgoanywhere.travel">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="2" width="20" height="20" rx="5" stroke="#9aaabb" strokeWidth="1.8" fill="none"/>
                    <circle cx="12" cy="12" r="4.5" stroke="#9aaabb" strokeWidth="1.8" fill="none"/>
                    <circle cx="17.5" cy="6.5" r="1" fill="#9aaabb"/>
                  </svg>
                </a>
                {/* TikTok */}
                <a href="https://www.tiktok.com/@Just.Go.Anywhere" target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", border: `1px solid ${darkMode ? "rgba(27,58,92,0.16)" : "rgba(0,0,0,0.08)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", transition: "all 0.2s", textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(27,58,92,0.4)"; e.currentTarget.style.background = "rgba(27,58,92,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"; }}
                  title="@Just.Go.Anywhere">
                  <svg width="13" height="14" viewBox="0 0 24 27" fill="none">
                    <path d="M17 1C17 1 17.5 5 22 6V10C22 10 19 10 17 8.5V18C17 22.5 13.5 26 9 26C4.5 26 1 22.5 1 18C1 13.5 4.5 10 9 10C9.5 10 10 10.05 10.5 10.15V14.3C10 14.1 9.5 14 9 14C6.8 14 5 15.8 5 18C5 20.2 6.8 22 9 22C11.2 22 13 20.2 13 18V1H17Z" stroke="#9aaabb" strokeWidth="1.8" fill="none" strokeLinejoin="round"/>
                  </svg>
                </a>
                {/* Email */}
                <a href="mailto:support@justgoanywhere.com"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", border: `1px solid ${darkMode ? "rgba(27,58,92,0.16)" : "rgba(0,0,0,0.08)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(0,0,0,0.03)", transition: "all 0.2s", textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(74,140,196,0.4)"; e.currentTarget.style.background = "rgba(74,140,196,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "rgba(27,58,92,0.096)" : "rgba(0,0,0,0.08)"; e.currentTarget.style.background = darkMode ? "rgba(27,58,92,0.045)" : "rgba(0,0,0,0.03)"; }}
                  title="support@justgoanywhere.com">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="#9aaabb" strokeWidth="1.8" fill="none"/>
                    <path d="M3 7 L12 13 L21 7" stroke="#9aaabb" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>

              {/* TrustedSite static trustmark — Trustico script (loaded in
                 index.html) finds this div and hydrates it into a verified
                 badge. The floating version is suppressed via global CSS so
                 it doesn't overlap the mobile bottom nav. */}
              <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-start" }}>
                <div className="trustedsite-trustmark"
                  data-type="205"
                  data-width="120"
                  data-height="50"
                  style={{ opacity: darkMode ? 0.85 : 1 }}
                  aria-label="TrustedSite verified — Just Go Anywhere"
                />
              </div>

              {/* Compact FAQ — collapsible, under social links */}
              {(() => {
                const FAQS = [
                  { q: "Why use Just Go Anywhere™?", a: "The average traveler spends 8–20 hours planning a single trip — visiting 38+ websites, comparing hundreds of flights and hotels, and still second-guessing the booking. We collapse all of that into one screen. Set a budget, tell us your vibe, and we handle flights, hotel, and activities. You show up. We do the rest." },
                  { q: "How is JGA different from booking myself?", a: "We do everything — research, price comparison, booking, and itinerary planning. You get a fully planned trip in minutes instead of hours. Surprise Me destinations are curated by real people, not random — they genuinely match your vibe, budget, and travel style. And if the trip costs less than your budget, you get the difference back." },
                  { q: "Do I need an account to book?", a: "You can browse and explore without an account. Creating a free account gives you full access to booking trips, saving itineraries, building your wish list, and writing in your travel journal." },
                  { q: "What's included in my budget?", a: "You choose what's included: return flights, hotel, local transport, and/or activities. We build the package around exactly what you select — nothing more, nothing hidden." },
                  { q: "What's the Price Match Guarantee?", a: "If your confirmed trip package costs less than your stated budget, we refund you the difference. No questions asked. Every booking includes this promise." },
                  { q: "How does Surprise Me work?", a: "You set a budget and any preferences — travel vibe, dates, how many travelers. Our team curates a complete trip (flights + hotel + activities) to a destination you won't know until after you book." },
                  { q: "What if I hate the destination?", a: "Surprise Me trips come with a destination swap — if we reveal the destination and it really doesn't work for you, we'll find an alternative at no extra cost. Take Me Anywhere lets you choose your region and vibe upfront." },
                  { q: "Can I cancel or change my trip?", a: "Yes. You have 24 hours after booking to cancel for a full refund. After that, our standard cancellation policy applies — see the Cancellation Policy in the footer for full details." },
                  { q: "Can I book for 5 or more travelers?", a: "Our self-serve booking is capped at 4 travelers per trip — this keeps the experience fast and the matches accurate. For larger groups (think families with extended members, friend trips of 5+, or group celebrations), email hello@justgoanywhere.com and we'll handle it manually. We'll match the same Price Match Guarantee on custom group bookings." },
                ];
                return (
                  <div style={{ marginTop: 14 }}>
                    <button onClick={() => setShowFooterFaq(!showFooterFaq)}
                      aria-expanded={showFooterFaq}
                      aria-controls="footer-faq-list"
                      style={{
                        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "4px 0", background: "transparent", border: "none",
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                      }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" stroke={darkMode ? "#4A6B8A" : "#8aacb0"} strokeWidth="1.7" fill="none"/>
                          <path d="M9 9 C 9 7.5 10.3 7 12 7 C 13.7 7 15 7.7 15 9.3 C 15 10.5 14 11 13 11.5 C 12 12 12 13 12 13.5 M12 16 L12 16.2" stroke={darkMode ? "#4A6B8A" : "#8aacb0"} strokeWidth="1.7" strokeLinecap="round" fill="none"/>
                        </svg>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, color: darkMode ? "#4A6B8A" : "#8aacb0", textTransform: "uppercase" }}>FAQ</span>
                      </span>
                      <span style={{ fontSize: 14, color: darkMode ? "#4A6B8A" : "#8aacb0", transition: "transform 0.2s", transform: showFooterFaq ? "rotate(180deg)" : "rotate(0deg)", lineHeight: 1 }}>⌄</span>
                    </button>

                    {showFooterFaq && (
                      <div id="footer-faq-list" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4, animation: "fadeIn 0.25s ease" }}>
                        {FAQS.map((faq, i) => (
                          <div key={i} style={{ borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)"}`, overflow: "hidden" }}>
                            <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                              aria-expanded={openFaq === i}
                              aria-controls={`footer-faq-answer-${i}`}
                              style={{
                                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "10px 12px",
                                background: openFaq === i ? (darkMode ? "rgba(27,58,92,0.07)" : "rgba(27,58,92,0.05)") : "transparent",
                                border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                              }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: openFaq === i ? teal : textPrimary, flex: 1, paddingRight: 10 }}>{faq.q}</span>
                              <span style={{ fontSize: 14, color: teal, flexShrink: 0, transition: "transform 0.2s", transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
                            </button>
                            {openFaq === i && (
                              <div id={`footer-faq-answer-${i}`} role="region" style={{ padding: "0 12px 12px", background: darkMode ? "rgba(27,58,92,0.03)" : "rgba(27,58,92,0.025)" }}>
                                <p style={{ margin: 0, fontSize: 12, color: textMuted, lineHeight: 1.7 }}>{faq.a}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Links column */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, color: darkMode ? "#4A6B8A" : "#8aacb0", textTransform: "uppercase", marginBottom: 8 }}>
                Legal & Support
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {[
                  { label: "About Us", action: () => setShowAboutUs(true), soon: false },
                  { label: "Travel Agents", action: () => setShowAgentWaitlist(true), soon: false },
                  { label: "Customer Service & Help", action: () => window.open("mailto:support@justgoanywhere.com","_blank"), soon: false },
                  { label: "Cancellation Policy", action: () => setShowCancellationPolicy(true), soon: false },
                  { label: "Privacy Policy", action: () => setShowPrivacyPolicy(true), soon: false },
                  { label: "Terms of Service", action: () => setShowTermsOfService(true), soon: true },
                  { label: "Affiliate Disclosure", action: () => setShowAffiliateDisclosure(true), soon: true },
                ].map(link => (
                  <span key={link.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1.1 }}>
                    <button onClick={link.action} style={{ background: "none", border: "none", color: link.soon ? textDim : (darkMode ? "#9aaabb" : "#3a6a72"), fontSize: 11, cursor: link.soon ? "default" : "pointer", fontFamily: "inherit", textDecoration: "none", padding: 0, textAlign: "left", opacity: link.soon ? 0.5 : 1, transition: "color 0.15s", lineHeight: 1.3 }}
                      onMouseEnter={e => { if (!link.soon) e.currentTarget.style.color = darkMode ? gold : teal; }}
                      onMouseLeave={e => { e.currentTarget.style.color = link.soon ? textDim : (darkMode ? "#9aaabb" : "#3a6a72"); }}>
                      {link.label}
                    </button>
                    {link.soon && (
                      <span style={{ fontSize: 9, color: "#4A8CC4", animation: "pulse 1.5s ease-in-out infinite" }}>●</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Suggestion box — moved from home to footer (under Affiliate
              Disclosure list, above Trust band per Cas's call). Renders on
              every page since it lives in the global footer. ── */}
          <div style={{ marginTop: 10, marginBottom: 14, background: darkMode ? "rgba(27,58,92,0.030)" : "rgba(0,0,0,0.03)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.084)" : "rgba(0,0,0,0.08)"}`, borderRadius: 14, padding: "16px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, marginBottom: 4 }}>Don't see a location or activity you're interested in?</div>
            <div style={{ fontSize: 12, color: textMuted, marginBottom: 10 }}>Let us know — we add new destinations and experiences regularly.</div>
            {!registered ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: darkMode ? "rgba(27,58,92,0.06)" : "rgba(27,58,92,0.08)", border: "1px solid rgba(27,58,92,0.2)", borderRadius: 10, padding: "12px 14px" }}>
                <span style={{ fontSize: 13, color: textMuted, flex: 1 }}>Sign in to submit a suggestion.</span>
                <button onClick={() => setShowLogin(true)} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: teal, color: "#FAFAF5", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Sign In</button>
                <button onClick={() => setShowRegister(true)} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid rgba(74,140,196,0.3)`, background: "transparent", color: goldText, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Register</button>
              </div>
            ) : blindSuggestionSent ? (
              <div style={{ fontSize: 13, color: "#6dbd6d", fontWeight: 600 }}>✓ Thanks! We'll look into adding that.</div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={blindSuggestion}
                  onChange={e => setBlindSuggestion(e.target.value)}
                  placeholder="e.g. Patagonia, scuba diving, ski resorts…"
                  style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: `1px solid ${darkMode ? "rgba(27,58,92,0.120)" : "rgba(0,0,0,0.12)"}`, background: darkMode ? "rgba(27,58,92,0.060)" : "rgba(250,250,245,0.6)", color: textPrimary, fontSize: 13, fontFamily: "inherit", outline: "none" }}
                  onKeyDown={e => {
                    if (e.key === "Enter" && blindSuggestion.trim()) {
                      fetch(`${process.env.REACT_APP_API_URL || "https://api.justgoanywhere.com"}/api/suggestions`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ suggestion: blindSuggestion, email: user?.email || null })
                      }).then(() => { setBlindSuggestionSent(true); setBlindSuggestion(""); if (typeof window.jgaTrack === "function") window.jgaTrack("suggestion_submitted", {}); }).catch(() => { setBlindSuggestionSent(true); setBlindSuggestion(""); });
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (!blindSuggestion.trim()) return;
                    fetch(`${process.env.REACT_APP_API_URL || "https://api.justgoanywhere.com"}/api/suggestions`, {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ suggestion: blindSuggestion, email: user?.email || null })
                    }).then(() => { setBlindSuggestionSent(true); setBlindSuggestion(""); if (typeof window.jgaTrack === "function") window.jgaTrack("suggestion_submitted", {}); }).catch(() => { setBlindSuggestionSent(true); setBlindSuggestion(""); });
                  }}
                  disabled={!blindSuggestion.trim()}
                  style={{ padding: "9px 14px", borderRadius: 10, border: "none", background: blindSuggestion.trim() ? teal : darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.06)", color: blindSuggestion.trim() ? "#FAFAF5" : textDim, fontWeight: 700, fontSize: 13, cursor: blindSuggestion.trim() ? "pointer" : "not-allowed", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s" }}>
                  Send →
                </button>
              </div>
            )}
          </div>

          {/* Trust band — moved from top to footer */}
          <div style={{ paddingTop: 12, paddingBottom: 10, borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)"}` }}>
            <TrustBand variant="full" darkMode={darkMode} />
          </div>

          {/* Bottom bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
            paddingTop: 12,
            borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.06)"}`,
          }}>
            <p style={{ margin: 0, fontSize: 10, letterSpacing: 1.5, color: darkMode ? "#4A6B8A" : "#aabfc4", textTransform: "uppercase" }}>
              © 2026 Just Go Anywhere · The World Is Yours
            </p>

          </div>
        </div>

        {/* ── BOTTOM NAV BAR (V3) — HIDDEN per Cas's Day 28 nav consolidation ──
            EXPLORE moved to the top nav (between BOARDS and ACCOUNT). Surprise
            Me + Take Me Anywhere now live in (1) Explore's pill sub-nav, and
            (2) Zone 3 on the home page. No more bottom nav. Keeping the code
            wrapped in `false &&` so we can revert quickly if needed without
            losing the SVG icons and conditional logic. */}
        {false && !["trips", "journal", "wishlist", "account", "profile"].includes(mode) && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 60,
          transform: navHidden ? "translateY(110%)" : "translateY(0)",
          transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          willChange: "transform",
          pointerEvents: "none",
        }}>
          <div style={{
            maxWidth: 680, margin: "0 auto", display: "flex",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            WebkitBackdropFilter: "blur(20px)", backdropFilter: "blur(20px)",
            background: darkMode ? "rgba(8,14,28,0.97)" : "linear-gradient(135deg, #2C3E50 0%, #34495E 100%)",
            borderTop: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(20,32,46,0.5)"}`,
            borderLeft: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(20,32,46,0.35)"}`,
            borderRight: `1px solid ${darkMode ? "rgba(27,58,92,0.18)" : "rgba(20,32,46,0.35)"}`,
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            boxShadow: darkMode ? "0 -1px 0 rgba(27,58,92,0.12), 0 -4px 32px rgba(0,0,0,0.7)" : "0 -4px 20px rgba(20,32,46,0.22)",
            pointerEvents: "auto",
          }}>
            {(() => {
              // Bottom nav v3 (Jun 11 2026): three discovery surfaces —
              // Surprise Me (teaser + waitlist), Take Me Anywhere (primary
              // affiliate handoff), and Explore (search + Most Affordable +
              // Trending Trips sub-nav). Top nav's EXPLORE moved here per
              // Cas's request; a HOME icon replaced it in the top nav.
              const tabs = [
                { id: "blind",      label: ["Surprise", "Me"],    color: teal,      svgIcon: (active) => (
                  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="19" fill={active ? "rgba(253,246,232,0.22)" : `${teal}15`} stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="2"/>
                    <ellipse cx="22" cy="22" rx="9" ry="19" fill="none" stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="1.5"/>
                    <path d="M3 22 Q12 18 22 22 Q32 26 41 22" stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="1.5" fill="none"/>
                    <path d="M5 13 Q13 10 22 13 Q31 16 39 13" stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="1.2" fill="none" opacity="0.7"/>
                    <path d="M5 31 Q13 28 22 31 Q31 34 39 31" stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="1.2" fill="none" opacity="0.7"/>
                  </svg>
                )},
                { id: "explore",    label: ["Take Me", "Anywhere"], color: goldText,     svgIcon: (active) => (
                  <svg width="20" height="22" viewBox="0 0 38 48" fill="none">
                    <path d="M19 2C10.163 2 3 9.163 3 18c0 11.5 16 28 16 28s16-16.5 16-28C35 9.163 27.837 2 19 2z"
                      fill={active ? "rgba(253,246,232,0.22)" : (darkMode ? "rgba(74,140,196,0.08)" : "rgba(27,58,92,0.08)")}
                      stroke={active ? "#FAFAF5" : (darkMode ? "#7a9ab8" : "#c4c8d1")} strokeWidth="2"/>
                    <circle cx="19" cy="18" r="7" fill="none" stroke={active ? "#FAFAF5" : (darkMode ? "#7a9ab8" : "#c4c8d1")} strokeWidth="1.6"/>
                    <circle cx="19" cy="18" r="2.5" fill={active ? "#FAFAF5" : (darkMode ? "#7a9ab8" : "#c4c8d1")} opacity="0.9"/>
                  </svg>
                )},
                { id: "search",     label: ["Explore"],            color: teal,         svgIcon: (active) => (
                  // Magnifying glass — was the EXPLORE icon in the top nav.
                  // Moved here per Cas's Jun 11 nav reorg.
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <circle cx="10.5" cy="10.5" r="7"
                      stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="2"
                      fill={active ? "rgba(253,246,232,0.22)" : `${teal}10`}/>
                    <path d="M16 16 L21 21"
                      stroke={active ? "#FAFAF5" : (darkMode ? "#3a5a6a" : "#c4c8d1")} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )},
              ];
              return tabs.map(tab => {
                const isActive = mode === tab.id;
                // Cream-unified active state. Glow, border, underline, label
                // all read cream against the slate background — matches the
                // top nav treatment, replaces the old burgundy active color.
                const activeColor = "#FAFAF5";
                const glowColor = "rgba(253,246,232,0.20)";
                const glowShadow = "0 0 20px rgba(253,246,232,0.55), 0 0 8px rgba(253,246,232,0.3)";
                return (
                  <button key={tab.id} className="jga-tab-btn"
                    onClick={() => { setBottomTab(null); if (mode !== tab.id) { reset(); setMode(tab.id); } }}
                    aria-current={isActive ? "page" : undefined}
                    aria-label={tab.id === "blind" ? "Surprise Me" : tab.id === "explore" ? "Take Me Anywhere" : tab.id === "search" ? "Explore" : tab.id}
                    tabIndex={0}
                    style={{ color: isActive ? activeColor : "#c4c8d1", opacity: mode && !isActive ? 0.55 : 1, transition: "opacity 0.22s, transform 0.18s" }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 13,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isActive ? glowColor : "transparent",
                      boxShadow: isActive ? glowShadow : "none",
                      border: isActive ? `1.5px solid rgba(253,246,232,0.5)` : "1px solid transparent",
                      transition: "all 0.22s ease",
                      transform: isActive ? "translateY(-1px)" : "translateY(0)",
                    }}>
                      <span className="jga-tab-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{tab.svgIcon(isActive)}</span>
                    </div>
                    <span className="jga-tab-label" style={{ fontWeight: isActive ? 800 : 500, color: isActive ? activeColor : "#c4c8d1", letterSpacing: isActive ? 0.5 : 0.3 }}>{Array.isArray(tab.label) ? tab.label.join("\n").split("\n").map((line, i) => <span key={i} style={{ display: "block" }}>{line}</span>) : tab.label}</span>
                    {isActive && <div className="jga-tab-underline" style={{ background: activeColor, boxShadow: `0 0 10px rgba(253,246,232,0.6)` }} />}
                  </button>
                );
              });
            })()}
          </div>
        </div>
        )}

      </main>
    </div>
    </ThemeContext.Provider>
    </JGAErrorBoundary>
  );
}
